/* ERP V2.5 : ACHATS + INTERFACE RESPONSIVE ET ÉPURÉE */
/*
  AZONGBIO v2 — Base unifiée
  Thème : Interface existante + PDF sobres (charbon + vert)
  Principe : refonte visuelle non invasive, conservation des actions,
  permissions, appels Supabase et générateurs PDF existants.
*/
import { useState, useEffect, useRef, Fragment } from "react";
import { createClient } from '@supabase/supabase-js';
import { jsPDF } from "jspdf";
import {
  Eye, EyeOff, User, Printer, Tag, Trash2, FileText, Mail, MapPin, Phone, Search,
  Receipt, Users, BarChart3, Package, Send, Warehouse, FileEdit, Target,
  MessageCircle, Briefcase, CreditCard, Truck, PackageCheck, LogOut, Menu, X,
  TrendingUp, TrendingDown, AlertTriangle, Clock, Wallet, RefreshCw, Bell,
  Download, LayoutDashboard, Activity, ShoppingCart, Boxes, Settings, Compass, Calculator, ShieldCheck, Landmark, AlertOctagon, Layers, ChevronRight, ClipboardCheck, Banknote
} from "lucide-react";
import qrcode from "./qr";
// L'application ne parle plus directement à l'adresse technique du serveur
// (152-67-64-119.nip.io) : certains réseaux mobiles/box en Afrique bloquent ce
// type d'adresse générée automatiquement, ce qui empêchait certains collaborateurs
// de se connecter. On passe désormais par le même nom de domaine que l'application
// elle-même (via une règle de renvoi "rewrites" définie dans vercel.json) : le
// navigateur ne contacte jamais l'adresse bloquée, c'est Vercel qui relaie la
// demande vers le serveur en interne.
const SUPABASE_PROXY_URL =
  (typeof window !== "undefined" && window.location && window.location.origin)
    ? window.location.origin + "/supabase"
    : "https://152-67-64-119.nip.io";
const supabase = createClient(
  SUPABASE_PROXY_URL,
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlzcyI6InN1cGFiYXNlIiwiaWF0IjoxNzg3MjQzMTc3LCJleHAiOjIxMDI2MDMxNzd9.4nMQ_CgTZ00FoS5uIErsQ7nrdTT1kOaleab8LZFi9Iw"
);

// Les liens signés renvoyés par le stockage (preuves de paiement, documents RH...)
// contiennent en dur l'adresse technique du serveur, puisque c'est le serveur
// lui-même qui les fabrique — cette fonction les réécrit pour qu'ils passent eux
// aussi par le même relais Vercel que le reste de l'application, plutôt que
// d'exposer à nouveau l'adresse bloquée sur certains réseaux.
const toProxyUrl = (url) => {
  if (!url) return url;
  try {
    return url.replace("https://152-67-64-119.nip.io", SUPABASE_PROXY_URL);
  } catch {
    return url;
  }
};

const LOGO_URI = "/logo.jpg";
const LOGO_UI = "/logo-ui.png";
if (typeof document!=="undefined" && !document.getElementById("az-base")) { const sb=document.createElement("style"); sb.id="az-base"; sb.textContent="html,body{overscroll-behavior:none;-webkit-text-size-adjust:100%;text-size-adjust:100%;margin:0;max-width:100%}html{overflow-x:hidden}body{overflow-x:clip;touch-action:pan-y}@media (max-width:768px){html,body{height:100%;overflow:hidden}#root{position:fixed;top:0;left:0;right:0;bottom:0;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;overscroll-behavior-y:none}#root>div{min-height:100%}}"; document.head.appendChild(sb); }
if (typeof window!=="undefined" && !window.__azNoBounce) {
  window.__azNoBounce = true;
  let ty0 = 0;
  document.addEventListener("touchstart", e=>{ if (e.touches.length===1) ty0 = e.touches[0].clientY; }, {passive:true});
  document.addEventListener("touchmove", e=>{
    if (e.touches.length!==1 || !window.matchMedia("(max-width:768px)").matches) return;
    const dy = e.touches[0].clientY - ty0;
    if (dy===0) return;
    let el = e.target;
    while (el && el.nodeType===1) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy==="auto"||oy==="scroll") && el.scrollHeight>el.clientHeight+1) {
        if ((dy>0 && el.scrollTop>0) || (dy<0 && el.scrollTop+el.clientHeight<el.scrollHeight-1)) return;
      }
      el = el.parentElement;
    }
    if (e.cancelable) e.preventDefault();
  }, {passive:false});
}
if (typeof window!=="undefined" && window.matchMedia && window.matchMedia("(max-width:768px)").matches) {
  const mv=document.querySelector('meta[name="viewport"]');
  const vc="width=device-width, initial-scale=0.92, maximum-scale=1, viewport-fit=cover";
  if (mv) mv.setAttribute("content",vc);
  else { const m=document.createElement("meta"); m.name="viewport"; m.content=vc; document.head.appendChild(m); }
}
if (typeof document!=="undefined" && !document.getElementById("az-fonts")) {
  const st=document.createElement("style"); st.id="az-fonts";
  st.textContent=`@font-face{font-family:"Inter";font-style:normal;font-weight:100 900;font-display:swap;src:url(/fonts/inter-latin-ext-wght-normal.woff2) format("woff2");unicode-range:U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF}@font-face{font-family:"Inter";font-style:normal;font-weight:100 900;font-display:swap;src:url(/fonts/inter-latin-wght-normal.woff2) format("woff2");unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD}:root{--az-font:"Inter","Inter Display",system-ui,-apple-system,"Segoe UI",Roboto,sans-serif}html,body{background:#F5F7F6;font-family:var(--az-font)}`;
  document.head.appendChild(st);
}

const payInfo = (e={}) => {
  const g = (k) => String((e||{})[k]||"").trim();
  const o = { momo:g("paiement_momo_numero"), momoNom:g("paiement_momo_nom"), banque:g("paiement_banque"), compte:g("paiement_compte"), titulaire:g("paiement_titulaire") };
  o.any = !!(o.momo || o.banque || o.compte);
  return o;
};
const buildPayText = (facture, entreprise, amount) => {
  const PI = payInfo(entreprise);
  const L = ["FACTURE "+(facture.numero||"")+" - "+((entreprise||{}).entreprise_nom||"")];
  if (amount>0) L.push("Montant a payer : "+Math.round(amount)+" FCFA");
  if (PI.momo) L.push("MTN Mobile Money : "+PI.momo+(PI.momoNom?" ("+PI.momoNom+")":""));
  if (PI.banque||PI.compte) L.push("Virement : "+[PI.banque,PI.compte].filter(Boolean).join(" - ")+(PI.titulaire?" ("+PI.titulaire+")":""));
  L.push("Reference a indiquer : "+(facture.numero||""));
  return L.join("\n").normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^\x20-\x7E\n]/g,"-");
};

// Page publique de paiement : le QR code de chaque facture pointe vers /payer/<code secret de la facture>.
const PAYER_BASE = "https://azongbio-app.braliebranding.com";
const payerUrl = (facture) => (facture && facture.lien_actif!==false && /^[a-f0-9]{32}$/.test(String(facture.lien_token||""))) ? PAYER_BASE+"/payer/"+facture.lien_token : "";
const payQrText = (facture, entreprise, amount) => payerUrl(facture) || buildPayText(facture, entreprise, amount);
const payerCodeFromUrl = () => {
  try {
    const m = String(window.location.pathname||"").match(/^\/payer\/([^/]*)\/?$/);
    return m ? m[1] : null;
  } catch(_) { return null; }
};
// ── Signature par téléphone (QR code) : page publique /signer/<code>, vérification /verifier/<code> ──
const sigCodeFromUrl = (nom) => {
  try {
    const m = String(window.location.pathname||"").match(new RegExp("^/"+nom+"/([^/]*)/?$"));
    return m ? m[1] : null;
  } catch(_) { return null; }
};
const sigSignUrl = (token) => PAYER_BASE+"/signer/"+token;
const sigVerifUrl = (token) => PAYER_BASE+"/verifier/"+token;
// Empreinte courte du texte signé (aide à repérer une modification ; pas une protection cryptographique)
const empreinteDoc = (d) => {
  const str = [d&&d.type,d&&d.clientNom,d&&d.objet,d&&d.date,d&&d.corps].map(x=>String(x||"").replace(/\s+/g," ").trim()).join("|");
  let h1=0xdeadbeef, h2=0x41c6ce57;
  for (let i=0;i<str.length;i++){ const ch=str.charCodeAt(i); h1=Math.imul(h1^ch,2654435761); h2=Math.imul(h2^ch,1597334677); }
  h1=Math.imul(h1^(h1>>>16),2246822507)^Math.imul(h2^(h2>>>13),3266489909);
  h2=Math.imul(h2^(h2>>>16),2246822507)^Math.imul(h1^(h1>>>13),3266489909);
  return ((h2>>>0).toString(16).padStart(8,"0")+(h1>>>0).toString(16).padStart(8,"0")).toUpperCase().slice(0,12);
};
// La signature n'est utilisée que si le texte n'a pas changé depuis qu'elle a été apposée
const sigValide = (d) => !!(d && d.signature && /^data:image\/png;base64,/.test(String(d.signature.png||"")) && /^[a-f0-9]{32}$/.test(String(d.signature.token||"")) && d.signature.empreinte===empreinteDoc(d));
const sigHeure = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR",{timeZone:"Africa/Douala",day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"}).replace(/,?[\s  ]+(?=\d{2}:\d{2}$)/," à ");
  } catch(_) { return ""; }
};
const sigHeureCourte = (iso) => { try { return new Date(iso).toLocaleTimeString("fr-FR",{timeZone:"Africa/Douala",hour:"2-digit",minute:"2-digit"}); } catch(_) { return ""; } };
const pngDims = (u) => {
  try {
    const b = atob(String(u).split(",")[1].slice(0,40));
    const g = (i) => ((b.charCodeAt(i)<<24)|(b.charCodeAt(i+1)<<16)|(b.charCodeAt(i+2)<<8)|b.charCodeAt(i+3))>>>0;
    const w=g(16), h=g(20);
    return (w>0&&h>0&&w<20000&&h<20000) ? {w,h} : {w:250,h:100};
  } catch(_) { return {w:250,h:100}; }
};
const makeQrMatrix = (text) => {
  try {
    const qr = qrcode(0,"M"); qr.addData(text); qr.make();
    const n = qr.getModuleCount(); const rows=[];
    for (let r=0;r<n;r++){ const row=[]; for(let c=0;c<n;c++) row.push(qr.isDark(r,c)); rows.push(row); }
    return { n, rows };
  } catch(e) { console.warn("QR non genere", e); return null; }
};
const qrSvgPath = (qm) => { let d=""; for(let r=0;r<qm.n;r++) for(let c=0;c<qm.n;c++) if(qm.rows[r][c]) d+="M"+(c+2)+" "+(r+2)+"h1v1h-1z"; return d; };

const waPhone = (raw) => {
  const first = String(raw||"").split(/[\/;,]| ou /i)[0];
  let d = first.replace(/[^0-9]/g,"");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length===9 && /^[62]/.test(d)) d = "237"+d;
  return d.length>=10 ? d : "";
};
const buildRelanceMsg = (facture, client, entreprise, reste, ttc) => {
  const PI = payInfo(entreprise);
  const ech = endOfLocalDay(facture.date_echeance);
  const jours = ech && ech < new Date() ? Math.floor((Date.now()-ech.getTime())/86400000) : 0;
  const L = [];
  L.push("Bonjour "+(facture.client_nom||client?.nom||"")+",");
  L.push("");
  L.push("Sauf erreur de notre part, la facture "+(facture.numero||"")+" du "+fmtDate(facture.date_emission)+" (total "+FCFA(ttc)+") reste à régler"+(jours>0?", son échéance du "+fmtDate(facture.date_echeance)+" étant dépassée de "+jours+" jour"+(jours>1?"s":""):" (échéance le "+fmtDate(facture.date_echeance)+")")+".");
  L.push("Montant restant à payer : "+FCFA(reste));
  if (PI.any) {
    L.push("");
    L.push("Pour régler :");
    if (PI.momo) L.push("- MTN Mobile Money : "+PI.momo+(PI.momoNom?" ("+PI.momoNom+")":""));
    if (PI.banque||PI.compte) L.push("- Virement : "+[PI.banque,PI.compte].filter(Boolean).join(" - ")+(PI.titulaire?" ("+PI.titulaire+")":""));
  }
  L.push("");
  if (payerUrl(facture)) { L.push("Payer ou envoyer votre preuve en ligne : "+payerUrl(facture)); L.push(""); }
  L.push("Merci d'indiquer la référence "+(facture.numero||"")+" lors du règlement. Si le paiement a déjà été effectué, merci de nous envoyer la preuve.");
  L.push("");
  L.push("Cordialement,");
  L.push((entreprise?.entreprise_nom||"")+((entreprise?.entreprise_tel)?" — "+entreprise.entreprise_tel:""));
  return L.join("\n").replace(/[\u202f\u00a0]/g," ");
};
const openRelanceWhatsApp = (facture, client, entreprise, reste, ttc) => {
  const ph = waPhone(client?.contact||client?.contact_tel);
  if (!ph) return false;
  window.open("https://wa.me/"+ph+"?text="+encodeURIComponent(buildRelanceMsg(facture, client, entreprise, reste, ttc)),"_blank");
  return true;
};

const GENERAL_CHAT_KEY = "__general__";
const REALTIME_BUSINESS_TABLES = [
  "employes", "produits", "clients", "fournisseurs", "ventes", "depenses",
  "lots", "factures", "proformas", "depots", "livraisons", "paiements",
  "depot_stocks", "avaries",
  "departements", "conges", "documents_employes", "historique_employes",
  "evaluations_employes", "contrats_employes", "soldes_conges",
  "immobilisations", "mouvements_immobilisations", "entretiens_immobilisations",
  "achats_commandes", "achats_receptions", "achats_factures", "achats_paiements",
  "parametres", "jours_feries", "receptions_bouteilles", "ingredients", "recettes"
];
const salutationHeure = (d=new Date()) => {
  const h = d.getHours();
  return h>=5 && h<12 ? "Bonjour" : h>=12 && h<18 ? "Bon après-midi" : "Bonsoir";
};
const PRODUITS_CODES = {};
const normNomProduit = (n) => String(n||"").trim().toLowerCase().replace(/\s+/g," ");
const codeProduitDe = (designation) => PRODUITS_CODES[normNomProduit(designation)] || "";
const libelleLigne = (designation) => { const c = codeProduitDe(designation); return c ? c+" · "+(designation||"") : (designation||""); };
const FCFA = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0)) + " FCFA";
const NUM  = (n) => new Intl.NumberFormat("fr-FR").format(n || 0);
// Versions "sûres pour PDF" : la police standard du PDF ne sait pas afficher
// l'espace fine insécable utilisée par défaut par le formatage français des nombres
// (elle apparaissait comme une barre verticale) — on la remplace par un espace normal.
const pdfSafe = (s) => String(s)
  .replace(/[\u202F\u00A0\u2009]/g, " ")
  // Le jeu de caracteres des polices standard du PDF (helvetica non incorporee) ne
  // couvre que le Latin-1/WinAnsi : les symboles hors de cette plage (<=, ->, etc.)
  // ne s'affichent pas correctement (glyphe aleatoire a la place) -- on les remplace
  // par un equivalent ASCII lisible avant tout rendu.
  .replace(/\u2264/g, "<=").replace(/\u2265/g, ">=")
  .replace(/\u2192/g, "->").replace(/\u2190/g, "<-");
const pdfFCFA = (n) => pdfSafe(FCFA(n));
const pdfNUM  = (n) => pdfSafe(NUM(n));

// Utilitaires sûrs et centralisés
const safeJsonParse = (value, fallback) => {
  if (value == null || value === "") return fallback;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return fallback; }
};
const toFiniteNumber = (value, fallback=0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};
const localISODate = (date=new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const shifted = new Date(d.getTime() - d.getTimezoneOffset()*60000);
  return shifted.toISOString().slice(0,10);
};
const today = () => localISODate(new Date());
const addDaysISO = (dateOrIso, days) => {
  const base = typeof dateOrIso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateOrIso)
    ? new Date(dateOrIso+"T12:00:00")
    : new Date(dateOrIso || Date.now());
  base.setDate(base.getDate()+Number(days||0));
  return localISODate(base);
};
const parseDateSafe = (value) => {
  if (!value) return null;
  const d = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(value+"T12:00:00")
    : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};
const endOfLocalDay = (value) => {
  const d = parseDateSafe(value);
  if (!d) return null;
  d.setHours(23,59,59,999);
  return d;
};
const fmtDate = (value) => {
  const d = parseDateSafe(value);
  return d ? d.toLocaleDateString("fr-FR", { day:"2-digit", month:"long", year:"numeric" }) : (value||"");
};

// ── Amortissement des immobilisations ──
// Les achats de matériel courant (bouteilles, matière première, etc.) restent dans les
// "Dépenses" comme avant. Les immobilisations, elles, ne sont JAMAIS comptées dans les
// dépenses : leur coût est étalé dans le temps via un plan d'amortissement linéaire,
// avec une durée de vie par défaut selon la catégorie (modifiable fiche par fiche).
const DUREE_AMORTISSEMENT_CAT = {
  "Véhicule":5, "Ordinateur":3, "Téléphone":2, "Machine":5, "Groupe électrogène":5,
  "Équipement réseau":3, "Imprimante":3, "Mobilier":5, "Bâtiment":20, "Autre":5
};
const dureeAmortissementDefaut = (categorie) => DUREE_AMORTISSEMENT_CAT[categorie] || 5;

// Nombre de mois sur lesquels l'immobilisation est amortie (durée × 12, arrondie).
const moisAmortissement = (item={}) => {
  const duree = toFiniteNumber(item.duree_amortissement) || dureeAmortissementDefaut(item.categorie);
  return Math.max(1, Math.round(duree*12));
};
// Montant amorti chaque mois : constant sur toute la durée de vie, de façon à totaliser
// exactement la valeur d'acquisition au bout de `moisAmortissement(item)` mois.
const amortissementMensuel = (item={}) => {
  const valeur = toFiniteNumber(item.valeur_acquisition);
  const n = moisAmortissement(item);
  return valeur>0 ? valeur/n : 0;
};
// Montant d'amortissement imputable à un mois calendaire précis (annee, moisIndex 0-11).
// Nul avant l'acquisition et une fois la durée de vie entièrement écoulée.
const amortissementDuMois = (item={}, annee, moisIndex) => {
  const dateAcq = parseDateSafe(item.date_acquisition);
  if (!dateAcq) return 0;
  const idx = (annee-dateAcq.getFullYear())*12 + (moisIndex-dateAcq.getMonth());
  if (idx<0 || idx>=moisAmortissement(item)) return 0;
  return amortissementMensuel(item);
};
// Somme des amortissements imputables aux mois recouverts par l'intervalle [y0,y1].
const amortissementSurPeriode = (item={}, y0, y1) => {
  if (!y0 || !y1) return 0;
  let total=0;
  const cursor=new Date(y0.getFullYear(),y0.getMonth(),1);
  const fin=new Date(y1.getFullYear(),y1.getMonth(),1);
  while (cursor<=fin) {
    total += amortissementDuMois(item, cursor.getFullYear(), cursor.getMonth());
    cursor.setMonth(cursor.getMonth()+1);
  }
  return total;
};
// Cumul amorti à une date de référence (pour l'affichage de la fiche immobilisation).
const cumulAmortiADate = (item={}, dateRef=new Date()) => {
  const dateAcq = parseDateSafe(item.date_acquisition);
  if (!dateAcq || !dateRef) return 0;
  const idx=(dateRef.getFullYear()-dateAcq.getFullYear())*12+(dateRef.getMonth()-dateAcq.getMonth());
  const moisEcoules=Math.max(0,Math.min(idx+1,moisAmortissement(item)));
  return amortissementMensuel(item)*moisEcoules;
};
// Valeur nette comptable (VNC) = valeur d'acquisition − cumul amorti, jamais négative.
const valeurNetteComptable = (item={}, dateRef=new Date()) => {
  const valeur=toFiniteNumber(item.valeur_acquisition);
  return Math.max(0, valeur-cumulAmortiADate(item,dateRef));
};
const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, ch => ({
  "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#039;"
}[ch]));
// Libellé de statut d'un employé, toujours cohérent avec la couleur du badge (rouge/vert).
// Le champ "actif" (compte applicatif) et le champ "statut_rh" peuvent être modifiés
// séparément dans l'appli : si l'un dit "actif" et l'autre "inactif", on donne toujours
// la priorité au champ booléen "actif" pour ne jamais afficher un badge rouge marqué
// "Actif", ou l'inverse — le mot affiché doit toujours correspondre à la couleur.
const employeStatutLabel = (e={}) => {
  const actif = e.actif !== false;
  const raw = String(e.statut_rh||"").trim();
  if (!raw) return actif ? "Actif" : "Inactif";
  const rawLower = raw.toLowerCase();
  if (!actif && rawLower==="actif") return "Inactif";
  if (actif && rawLower==="inactif") return "Actif";
  return raw;
};
const getTvaRate = (entreprise={}) => {
  const raw = toFiniteNumber(entreprise.tva_taux, 19.25);
  return Math.max(0, raw)/100;
};
const calculateInvoiceTotals = (facture={}, entreprise={}) => {
  const lignes = Array.isArray(facture.lignes) ? facture.lignes : safeJsonParse(facture.lignes, []);
  const ht = lignes.reduce((sum,line)=>{
    const calculated=toFiniteNumber(line?.qte)*toFiniteNumber(line?.prixUnit);
    const amount=line?.montant==null||line?.montant===""?calculated:toFiniteNumber(line.montant,calculated);
    return sum+amount;
  },0);
  const remisePct = Math.max(0, toFiniteNumber(facture.remise_pct, 0));
  const remise = remisePct ? Math.round(ht*remisePct/100) : 0;
  const hn = Math.max(0, ht-remise);
  const invoiceRate=toFiniteNumber(facture.tva_taux,NaN);
  const tvaRate=Number.isFinite(invoiceRate)?Math.max(0,invoiceRate)/100:getTvaRate(entreprise);
  const tva = facture.appliquer_tva !== false ? Math.round(hn*tvaRate) : 0;
  return { ht, remise, hn, tva, ttc:hn+tva, remisePct, tvaRate, tvaLabel:new Intl.NumberFormat("fr-FR",{maximumFractionDigits:2}).format(tvaRate*100) };
};

// Charge une image (même origine) et la convertit en data URL pour l'intégrer dans le PDF
const loadImageAsDataURL = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL("image/png"), ratio: img.naturalHeight / img.naturalWidth });
    } catch (e) { reject(e); }
  };
  img.onerror = reject;
  img.src = url;
});


// Palette commune à tous les PDF générés par l'application.
// Nouvelle ligne graphique : sobre, claire et commune à tous les documents.
// On conserve la clé "gold" pour compatibilité avec les générateurs existants,
// mais elle représente désormais l'unique couleur d'accent verte de la marque.
const PDF_BRAND = {
  dark:[34,42,46],
  gold:[46,125,107],
  accent:[46,125,107],
  ink:[32,36,39],
  muted:[105,113,118],
  pale:[247,249,248],
  line:[224,229,227],
  white:[255,255,255]
};
const enterpriseValue = (entreprise={}, key, fallback="") => entreprise?.[key] || fallback;
const enterpriseSlogan = (entreprise={}) => enterpriseValue(
  entreprise,
  "entreprise_slogan",
  "Thés glacés artisanaux — l'âme du terroir camerounais"
);
const getPdfLogoAsset = async () => {
  try { return await loadImageAsDataURL(LOGO_UI); }
  catch { try { return await loadImageAsDataURL(LOGO_URI); } catch { return null; } }
};

// Adresse publique de l'ERP, utilisée pour le lien direct et le QR code sur la fiche
// d'accès remise à chaque nouvel employé.
const ERP_LOGIN_URL = "https://azongbio-app.braliebranding.com/";
// Récupère un QR code (image PNG) pointant vers l'URL donnée, via un service public gratuit,
// et le renvoie en data URL exploitable par jsPDF (doc.addImage). Ne bloque jamais la
// génération du reste du document si le réseau n'est pas disponible au moment de l'appel :
// dans ce cas on renvoie simplement null et l'appelant affiche l'adresse en texte à la place.
const fetchQrCodeDataUrl = async (data, size=260) => {
  try {
    const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(data)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("QR code non généré :", e);
    return null;
  }
};

// En-tête PDF minimal : logo + identité à gauche, informations du document à droite,
// puis un simple filet de séparation. Aucun cartouche sombre ni bandeau massif.
const drawPdfLetterhead = (doc, entreprise={}, options={}, logoAsset=null) => {
  const {
    title="DOCUMENT",
    reference="",
    date=fmtDate(today()),
    subtitle=""
  } = options;
  const pageW = doc.internal.pageSize.getWidth();
  const { dark, accent, ink, muted, line, white } = PDF_BRAND;
  const left = 14;
  const right = 196;
  const metaX = 136;
  const metaW = right-metaX;
  const titleSize = 7.2;
  const refSize = 10.4;
  const subSize = 6.8;
  const lh = (sz) => sz*0.3528*1.2;

  doc.setFont("helvetica","bold");
  doc.setFontSize(titleSize);
  const titleLines = doc.splitTextToSize(pdfSafe(String(title).toUpperCase()),metaW);
  doc.setFont("helvetica","bold");
  doc.setFontSize(refSize);
  const refLines = reference ? doc.splitTextToSize(pdfSafe(String(reference)),metaW) : [];
  const metaLine = subtitle || date || "";
  doc.setFont("helvetica","normal");
  doc.setFontSize(subSize);
  const subLines = metaLine ? doc.splitTextToSize(pdfSafe(String(metaLine)),metaW) : [];

  let metaBottom = 11 + titleLines.length*lh(titleSize);
  if (refLines.length) metaBottom += 1.7 + refLines.length*lh(refSize);
  if (subLines.length) metaBottom += 1.5 + subLines.length*lh(subSize);
  const dividerY = Math.max(31.5, metaBottom+4.2);
  const headerH = dividerY+3;

  doc.setFillColor(...white);
  doc.rect(0,0,pageW,headerH,"F");

  let textX = left;
  if (logoAsset?.dataUrl) {
    const logoW = 30;
    const logoH = Math.min(logoW*(logoAsset.ratio||1),15);
    doc.addImage(logoAsset.dataUrl,"PNG",left,9+(Math.max(0,8-logoH)/2),logoW,logoH);
    textX = left+35;
  }

  doc.setTextColor(...ink);
  doc.setFont("helvetica","bold");
  doc.setFontSize(13.6);
  doc.text(
    pdfSafe(enterpriseValue(entreprise,"entreprise_nom","Bralie Branding Food SARL")),
    textX,13.2,{maxWidth:94}
  );
  doc.setFont("helvetica","normal");
  doc.setFontSize(7.2);
  doc.setTextColor(...accent);
  doc.text(pdfSafe(enterpriseSlogan(entreprise)),textX,18.6,{maxWidth:94});

  const contact = [
    enterpriseValue(entreprise,"entreprise_tel"),
    enterpriseValue(entreprise,"entreprise_email"),
    enterpriseValue(entreprise,"entreprise_site")
  ].filter(Boolean).join("  ·  ");
  doc.setTextColor(...muted);
  doc.setFontSize(6.9);
  doc.text(pdfSafe(contact),textX,23.6,{maxWidth:94});

  let ty = 10.8;
  doc.setTextColor(...accent);
  doc.setFont("helvetica","bold");
  doc.setFontSize(titleSize);
  titleLines.forEach(lineText=>{
    doc.text(lineText,right,ty,{align:"right"});
    ty += lh(titleSize);
  });

  if (refLines.length) {
    ty += 1.7;
    doc.setTextColor(...dark);
    doc.setFont("helvetica","bold");
    doc.setFontSize(refSize);
    refLines.forEach(lineText=>{
      doc.text(lineText,right,ty,{align:"right"});
      ty += lh(refSize);
    });
  }
  if (subLines.length) {
    ty += 1.5;
    doc.setTextColor(...muted);
    doc.setFont("helvetica","normal");
    doc.setFontSize(subSize);
    subLines.forEach(lineText=>{
      doc.text(lineText,right,ty,{align:"right"});
      ty += lh(subSize);
    });
  }

  doc.setLineWidth(0.35);
  doc.setDrawColor(...line);
  doc.line(left,dividerY,right,dividerY);
  doc.setLineWidth(0.75);
  doc.setDrawColor(...accent);
  doc.line(left,dividerY,left+24,dividerY);
  return dividerY+6.5;
};

// Pied de page discret et uniforme pour tous les documents.
const addPdfPageFooters = (doc, entreprise={}, documentLabel="", extraDroite="") => {
  const pageCount = doc.getNumberOfPages();
  const { muted, line } = PDF_BRAND;
  const company = enterpriseValue(entreprise,"entreprise_nom","Bralie Branding Food SARL");
  const legal = [
    enterpriseValue(entreprise,"entreprise_niu") ? "NIU : "+enterpriseValue(entreprise,"entreprise_niu") : "",
    enterpriseValue(entreprise,"entreprise_rc") ? "RC : "+enterpriseValue(entreprise,"entreprise_rc") : "",
    enterpriseValue(entreprise,"entreprise_rccm") && enterpriseValue(entreprise,"entreprise_rccm")!=="A compléter" ? "RCCM : "+enterpriseValue(entreprise,"entreprise_rccm") : ""
  ].filter(Boolean).join("  ·  ");
  const location = [
    enterpriseValue(entreprise,"entreprise_adresse"),
    enterpriseValue(entreprise,"entreprise_ville")
  ].filter(Boolean).join(" — ");

  for (let page=1; page<=pageCount; page++) {
    doc.setPage(page);
    doc.setDrawColor(...line);
    doc.setLineWidth(0.3);
    doc.line(14,282,196,282);
    doc.setTextColor(...muted);
    doc.setFont("helvetica","normal");
    doc.setFontSize(6.3);
    doc.text(pdfSafe([company,legal].filter(Boolean).join("  ·  ")),14,287,{maxWidth:145});
    if (location) doc.text(pdfSafe(location),14,291,{maxWidth:145});
    if (extraDroite) { doc.setFont("helvetica","normal"); doc.text(pdfSafe(extraDroite),196,287,{align:"right",maxWidth:60}); }
    doc.setFont("helvetica","bold");
    doc.text(
      pdfSafe((documentLabel?documentLabel+"  ·  ":"")+`Page ${page}/${pageCount}`),
      196,291,{align:"right",maxWidth:45}
    );
  }
};

// Titre de section sans cartouche : repère d'accent + filet léger.
const drawPdfSectionTitle = (doc, title, y) => {
  const { dark, accent, line } = PDF_BRAND;
  doc.setDrawColor(...accent);
  doc.setLineWidth(1.1);
  doc.line(14,y-3.2,14,y+1.8);
  doc.setTextColor(...dark);
  doc.setFont("helvetica","bold");
  doc.setFontSize(10);
  doc.text(pdfSafe(title),18,y+0.5);
  doc.setDrawColor(...line);
  doc.setLineWidth(0.3);
  doc.line(14,y+4.2,196,y+4.2);
  return y+10;
};

// Système de tableaux PDF commun à toute l'application.
// Chaque cellule possède maintenant une vraie bordure et le texte est centré
// verticalement dans la ligne, y compris quand une valeur se replie sur 2 lignes.
const preparePdfTableColumns = (columns=[], left=14, totalWidth=182) => {
  if (!columns.length) return [];
  const explicit = columns.every(c=>Number.isFinite(Number(c.width)) && Number(c.width)>0);
  let widths;
  if (explicit) {
    const raw=columns.map(c=>Number(c.width));
    const sum=raw.reduce((a,b)=>a+b,0)||totalWidth;
    widths=raw.map(w=>w*totalWidth/sum);
  } else {
    // Compatibilité avec d'anciens appels basés sur des points d'ancrage x.
    const anchors=columns.map((c,i)=>Number.isFinite(Number(c.x))?Number(c.x):(left+i*totalWidth/columns.length));
    const bounds=[left];
    for(let i=1;i<columns.length;i++){
      const prev=anchors[i-1], cur=anchors[i];
      bounds.push(Math.max(bounds[bounds.length-1]+8,(prev+cur)/2));
    }
    bounds.push(left+totalWidth);
    widths=bounds.slice(1).map((b,i)=>Math.max(8,b-bounds[i]));
    const sum=widths.reduce((a,b)=>a+b,0)||totalWidth;
    widths=widths.map(w=>w*totalWidth/sum);
  }
  let x=left;
  return columns.map((c,i)=>{
    const out={...c,x,width:widths[i]};
    x+=widths[i];
    return out;
  }).map((c,i,arr)=>({
    ...c,
    x:left+arr.slice(0,i).reduce((sum,v)=>sum+v.width,0)
  }));
};

const pdfTableCellLines = (doc, text, width, fontSize=7.6, style="normal", padding=2.2) => {
  doc.setFont("helvetica",style);
  doc.setFontSize(fontSize);
  const safe=pdfSafe(text==null||text===""?"—":String(text));
  return doc.splitTextToSize(safe,Math.max(3,width-padding*2));
};

const getPdfTableRowHeight = (doc, columns=[], cells=[], options={}) => {
  const cols=preparePdfTableColumns(columns,options.left??14,options.totalWidth??182);
  const minHeight=options.minHeight??9;
  const lineHeightFactor=options.lineHeightFactor??1.18;
  const paddingY=options.paddingY??2.1;
  let maxTextH=0;
  cols.forEach((col,i)=>{
    const cell=(cells[i]&&typeof cells[i]==="object"&&!Array.isArray(cells[i]))?cells[i]:{text:cells[i]};
    const fontSize=cell.fontSize??options.fontSize??7.6;
    const style=cell.bold?"bold":(cell.italic?"italic":(cell.style||options.style||"normal"));
    const lines=pdfTableCellLines(doc,cell.text,col.width,fontSize,style,options.paddingX??2.2);
    const em=fontSize*0.352778;
    const lineH=em*lineHeightFactor;
    const blockH=em+Math.max(0,lines.length-1)*lineH;
    maxTextH=Math.max(maxTextH,blockH);
  });
  return Math.max(minHeight,maxTextH+paddingY*2);
};

const drawPdfTableRow = (doc, y, columns=[], cells=[], options={}) => {
  const { ink, dark, pale, line, white }=PDF_BRAND;
  const left=options.left??14,totalWidth=options.totalWidth??182;
  const cols=preparePdfTableColumns(columns,left,totalWidth);
  const rowH=options.height??getPdfTableRowHeight(doc,cols,cells,{...options,left,totalWidth});
  const fill=options.fill || (options.header?pale:white);
  const border=options.border || line;
  const lineHeightFactor=options.lineHeightFactor??1.18;
  const paddingX=options.paddingX??2.2;

  doc.setFillColor(...fill);
  doc.setDrawColor(...border);
  doc.setLineWidth(options.lineWidth??0.28);
  doc.rect(left,y,totalWidth,rowH,"FD");

  let x=left;
  cols.forEach((col,i)=>{
    if(i>0) doc.line(x,y,x,y+rowH);
    const raw=cells[i];
    const cell=(raw&&typeof raw==="object"&&!Array.isArray(raw))?raw:{text:raw};
    const fontSize=cell.fontSize??options.fontSize??7.6;
    const style=cell.bold?"bold":(cell.italic?"italic":(cell.style||options.style||(options.header?"bold":"normal")));
    const align=cell.align||col.align||"left";
    const color=cell.color||options.textColor||(options.header?dark:ink);
    const lines=pdfTableCellLines(doc,cell.text,col.width,fontSize,style,paddingX);
    const em=fontSize*0.352778;
    const lineH=em*lineHeightFactor;
    const blockH=em+Math.max(0,lines.length-1)*lineH;
    const firstBaseline=y+(rowH-blockH)/2+em*0.78;
    let tx=x+paddingX;
    if(align==="right") tx=x+col.width-paddingX;
    else if(align==="center") tx=x+col.width/2;

    doc.setTextColor(...color);
    doc.setFont("helvetica",style);
    doc.setFontSize(fontSize);
    doc.text(lines,tx,firstBaseline,{align,lineHeightFactor,maxWidth:Math.max(3,col.width-paddingX*2)});
    x+=col.width;
  });
  return y+rowH;
};

// En-tête de tableau : vraie ligne de tableau, mêmes colonnes et mêmes bordures
// que les données. Le texte est centré verticalement et peut se replier proprement.
const drawPdfTableHeader = (doc, y, columns=[], options={}) => {
  return drawPdfTableRow(
    doc,
    y,
    columns,
    columns.map(c=>({text:c.label,align:c.align||"left",bold:true,fontSize:c.fontSize||7.1})),
    { ...options, header:true, minHeight:options.minHeight??9.5, paddingY:2.2 }
  );
};

// Bloc de signature réutilisable pour les PDF générés par l'application.
// Si dataUrl (image PNG de la signature tracée à l'écran, doigt/stylet) est
// fournie, elle est insérée juste au-dessus du trait et la légende devient
// "Signé électroniquement". Sinon le trait reste vide comme avant : le
// document est alors à signer à la main après impression, exactement comme
// le faisait chaque générateur PDF avant l'ajout de la signature à l'écran.
const drawPdfSignatureBlock = (doc, x, y, sigW, opts={}) => {
  const { line, muted } = PDF_BRAND;
  const { lineY = y+22, caption = "Nom, date et signature", dataUrl } = opts;
  if (dataUrl) {
    try {
      // La signature était auparavant forcée dans un cadre de largeur/hauteur fixes,
      // ce qui l'étirait ou l'écrasait selon la forme du tracé d'origine. On respecte
      // maintenant ses proportions réelles (limitées à la largeur et à une hauteur max
      // disponibles), centrée dans l'espace prévu au-dessus de la ligne.
      const props = doc.getImageProperties(dataUrl);
      const maxH = 17;
      let w = sigW, h = w * (props.height/props.width);
      if (h > maxH) { h = maxH; w = h * (props.width/props.height); }
      doc.addImage(dataUrl,"PNG", x+(sigW-w)/2, lineY-h-2, w, h);
    }
    catch(imgErr) { console.warn("Signature non insérée dans le PDF :", imgErr); }
  }
  doc.setDrawColor(...line);
  doc.line(x,lineY,x+sigW,lineY);
  doc.setFont("helvetica","normal"); doc.setTextColor(...muted); doc.setFontSize(7.2);
  doc.text(dataUrl ? "Signé électroniquement le "+fmtDate((opts.signedAt?String(opts.signedAt).slice(0,10):"")||today()) : caption, x+sigW/2, lineY+4, {align:"center", maxWidth:sigW});
};

// ══ Génère un vrai PDF de facture/proforma (pour pièce jointe email) ══
// Rattachement des ventes à leur fiche client : par le CODE client d'abord (stable même si le nom
// change), sinon par le nom (sans tenir compte des majuscules, accents et espaces en trop) quand
// ce nom n'appartient qu'à UNE seule fiche.
const normNomClient = (s) => String(s||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/\s+/g," ").trim().toLowerCase();
const construireRattachementClients = (clients, factures) => {
  const nomsCount = {};
  (clients||[]).forEach(c=>{ const k=normNomClient(c.nom); nomsCount[k]=(nomsCount[k]||0)+1; });
  const codeParFacture = {};
  (factures||[]).forEach(f=>{ if (f.client_code) codeParFacture[String(f.id)] = f.client_code; });
  return (v, c) => {
    const code = v.client_code || (v.facture_id ? codeParFacture[String(v.facture_id)] : "") || "";
    if (code) return code===c.code;
    const k = normNomClient(v.client);
    return !!k && k===normNomClient(c.nom) && nomsCount[k]===1;
  };
};

// Noms de clients qui existent en plusieurs fiches (même nom, adresses différentes) :
// renvoie { nom_normalisé: [fiches...] } uniquement pour les noms en double.
const nomsClientsEnDouble = (clients) => {
  const m = {};
  (clients||[]).forEach(c=>{ const k=normNomClient(c.nom); if(!k) return; (m[k]=m[k]||[]).push(c); });
  Object.keys(m).forEach(k=>{ if (m[k].length<2) delete m[k]; });
  return m;
};

const generateInvoicePdfBase64 = async (facture, client, entreprise, isProforma=false, avaries=[], paiements=[]) => {
  const doc = new jsPDF({ unit:"mm", format:"a4" });
  const totals = calculateInvoiceTotals(facture, entreprise);
  const paidTotal = (paiements||[]).reduce((s,p)=>s+toFiniteNumber(p.montant),0);
  const resteDu = isProforma ? totals.ttc : (String(facture.statut||"")==="Payée" ? 0 : Math.max(0,totals.ttc-paidTotal));
  const echeanceD = endOfLocalDay(facture.date_echeance);
  const lateNow = !isProforma && resteDu>0 && echeanceD && echeanceD < new Date();
  const lateDays = lateNow ? Math.floor((Date.now()-echeanceD.getTime())/86400000) : 0;
  const kind = isProforma ? "pf" : resteDu<=0 ? "paid" : lateNow ? "late" : paidTotal>0 ? "part" : "open";
  const kindLabel = { pf:"Proforma", paid:"Payée", late:"En retard", part:"Partiellement payée", open:"En attente" }[kind];
  const dueLabel = isProforma ? "Montant du devis" : kind==="paid" ? "Facture soldée" : "Montant à payer";
  const dueAmount = (isProforma||kind==="paid") ? totals.ttc : resteDu;
  const lastPay = (paiements||[]).map(p=>p.date_versement||p.date_validation||"").filter(Boolean).sort().slice(-1)[0];
  const dueRight = isProforma ? "Valable jusqu'au "+fmtDate(facture.date_echeance)
    : kind==="paid" ? (lastPay ? "Réglée le "+fmtDate(lastPay) : "Merci pour votre règlement")
    : kind==="late" ? "Échéance dépassée de "+lateDays+" jour"+(lateDays>1?"s":"")
    : "Échéance le "+fmtDate(facture.date_echeance);
  const logoAsset = await getPdfLogoAsset();
  const headerOptions = {
    title:isProforma ? "PROFORMA / DEVIS" : "FACTURE",
    reference:facture.numero || "",
    subtitle:isProforma ? "Document commercial" : kindLabel
  };
  const newPage = () => {
    doc.addPage();
    return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
  };
  let y = drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
  const { ink, muted, pale, line, gold, dark } = PDF_BRAND;
  const E = (k) => enterpriseValue(entreprise,k);
  const MD = ["classique","sobre"].includes(String(entreprise?.facture_modele)) ? entreprise.facture_modele : "moderne";
  const accentC = MD==="moderne" ? gold : MD==="classique" ? ink : [90,100,95];
  const drawBox = (bx,by,bw,bh,r=2) => {
    if (MD==="moderne") { doc.setFillColor(...pale); doc.roundedRect(bx,by,bw,bh,r,r,"F"); }
    else if (MD==="classique") { doc.setFillColor(255,255,255); doc.setDrawColor(174,184,179); doc.setLineWidth(0.3); doc.roundedRect(bx,by,bw,bh,1,1,"FD"); }
    else { doc.setDrawColor(...line); doc.setLineWidth(0.3); doc.line(bx,by,bx+bw,by); }
  };
  const HDR = MD==="classique" ? {fill:[32,42,46],textColor:[255,255,255],border:[32,42,46]} : MD==="sobre" ? {fill:[255,255,255],textColor:muted} : {};

  // Émetteur et destinataire
  drawBox(14,y,86,33); drawBox(110,y,86,33);
  doc.setTextColor(...accentC);
  doc.setFont("helvetica","bold");
  doc.setFontSize(7.5);
  doc.text("ÉMETTEUR",18,y+6);
  doc.text(isProforma?"DESTINATAIRE":"FACTURÉ À",114,y+6);
  doc.setTextColor(...ink);
  doc.setFontSize(9.5);
  doc.text(pdfSafe(E("entreprise_nom")),18,y+12,{maxWidth:77});
  doc.text(pdfSafe(client?.nom || facture.client_nom || ""),114,y+12,{maxWidth:77});
  doc.setFont("helvetica","normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...muted);
  const issuerLines = [
    [E("entreprise_adresse"),E("entreprise_ville")].filter(Boolean).join(", "),
    E("entreprise_tel") ? "Tél : "+E("entreprise_tel") : "",
    E("entreprise_email"),
    E("entreprise_niu") ? "NIU : "+E("entreprise_niu") : ""
  ].filter(Boolean);
  const clientLines = [
    [client?.adresse,client?.ville].filter(Boolean).join(", "),
    client?.niu ? "NIU : "+client.niu : "",
    client?.contact || "",
    client?.email || ""
  ].filter(Boolean);
  doc.text(issuerLines.map(pdfSafe),18,y+17,{lineHeightFactor:1.35,maxWidth:77});
  doc.text(clientLines.map(pdfSafe),114,y+17,{lineHeightFactor:1.35,maxWidth:77});
  y += 39;

  // Dates
  doc.setDrawColor(...line);
  doc.setLineWidth(0.3);
  doc.line(14,y,196,y);
  y += 7;
  const dateBlocks = [
    ["Date d'émission",fmtDate(facture.date_emission)],
    [isProforma?"Validité":"Échéance",fmtDate(facture.date_echeance)],
    ["Mode de règlement",facture.reglement || "À convenir"]
  ];
  dateBlocks.forEach(([label,value],idx)=>{
    const x = 14 + idx*61;
    doc.setFont("helvetica","bold");
    doc.setFontSize(6.8);
    doc.setTextColor(...muted);
    doc.text(label.toUpperCase(),x,y);
    doc.setFontSize(8.5);
    doc.setTextColor(...ink);
    doc.text(pdfSafe(value||"—"),x,y+5,{maxWidth:54});
  });
  y += 14;

  {
    const tint = { paid:[232,245,234], part:[233,240,253], open:[255,244,221], late:[253,236,234], pf:[240,235,251] }[kind];
    const strong = { paid:[30,122,45], part:[47,111,222], open:[168,104,10], late:[214,69,61], pf:[122,79,214] }[kind];
    if (MD==="moderne") { doc.setFillColor(...tint); doc.roundedRect(14,y,182,19,2.5,2.5,"F"); }
    else if (MD==="classique") { doc.setFillColor(243,244,243); doc.rect(14,y,182,19,"F"); doc.setFillColor(...strong); doc.rect(14,y,2.2,19,"F"); }
    else { doc.setDrawColor(...ink); doc.setLineWidth(0.3); doc.line(14,y,196,y); doc.line(14,y+19,196,y+19); }
    doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(...muted);
    doc.text(pdfSafe(dueLabel.toUpperCase()),19,y+6.5);
    doc.setFontSize(15); doc.setTextColor(...ink);
    doc.text(pdfFCFA(dueAmount),19,y+14.8);
    doc.setFontSize(8); doc.setTextColor(...strong);
    doc.text(pdfSafe(dueRight),191,y+8,{align:"right",maxWidth:95});
    if (!isProforma && kind!=="paid" && paidTotal>0) {
      doc.setFont("helvetica","normal"); doc.setFontSize(7.4); doc.setTextColor(...muted);
      doc.text(pdfSafe("Déjà réglé : "+pdfFCFA(paidTotal)+" sur "+pdfFCFA(totals.ttc)),191,y+14,{align:"right",maxWidth:95});
    }
    y += 25;
  }

  const columns = [
    {label:"DÉSIGNATION",width:102},

    {label:"QTÉ",width:20,align:"right"},
    {label:"PRIX UNIT.",width:28,align:"right"},
    {label:"TOTAL HT",width:32,align:"right"}
  ];
  const drawItemsHeader = () => { y = drawPdfTableHeader(doc,y,columns,HDR); };
  drawItemsHeader();
  (Array.isArray(facture.lignes)?facture.lignes:[]).forEach(lineItem=>{
    const lineTotal = lineItem.montant==null ? toFiniteNumber(lineItem.qte)*toFiniteNumber(lineItem.prixUnit) : lineItem.montant;
    const cells=[
      {text:libelleLigne(lineItem.designation),fontSize:8.1},
      {text:pdfNUM(lineItem.qte)+" btl",align:"right",fontSize:8.1},
      {text:pdfNUM(lineItem.prixUnit),align:"right",fontSize:8.1},
      {text:pdfNUM(lineTotal),align:"right",fontSize:8.1,bold:true}
    ];
    const rowH=getPdfTableRowHeight(doc,columns,cells,{minHeight:9});
    if (y+rowH>273) { y=newPage(); drawItemsHeader(); }
    y=drawPdfTableRow(doc,y,columns,cells,{height:rowH,minHeight:9});
  });

  if (y>236) y=newPage();
  y += 4;
  const totalX=116, totalW=80;
  const totalBoxH = 31 + (totals.remise>0?6:0) + (facture.appliquer_tva!==false?6:0);
  drawBox(totalX,y,totalW,totalBoxH);
  let ty=y+7;
  const totalRow=(label,value,bold=false,color=ink)=>{
    doc.setFont("helvetica",bold?"bold":"normal");
    doc.setFontSize(bold?10:8.2);
    doc.setTextColor(...color);
    doc.text(pdfSafe(label),totalX+5,ty);
    doc.text(pdfSafe(value),totalX+totalW-5,ty,{align:"right"});
    ty += bold?8:6;
  };
  totalRow("Sous-total HT",pdfFCFA(totals.ht));
  if (totals.remise>0) totalRow(`Remise ${totals.remisePct}%`,"-"+pdfFCFA(totals.remise),false,[180,60,60]);
  totalRow("Base imposable",pdfFCFA(totals.hn));
  if (facture.appliquer_tva!==false) totalRow(`TVA ${totals.tvaLabel} %`,pdfFCFA(totals.tva));
  ty += 2;
  doc.setDrawColor(...accentC);
  doc.line(totalX+5,ty-2,totalX+totalW-5,ty-2);
  ty += 4;
  totalRow("TOTAL TTC",pdfFCFA(totals.ttc),true,dark);
  y += totalBoxH+5;

  if (!isProforma && (paiements||[]).length) {
    const rows = paiements.slice().sort((a,b)=>String(a.date_versement||"").localeCompare(String(b.date_versement||"")));
    const boxH = 13 + rows.length*6 + 10;
    if (y+boxH>272) y=newPage();
    drawBox(14,y,182,boxH);
    doc.setFont("helvetica","bold"); doc.setFontSize(7.2); doc.setTextColor(...muted);
    doc.text("RÈGLEMENTS REÇUS",19,y+6.5);
    let ry=y+13.5;
    rows.forEach(p=>{
      doc.setFont("helvetica","normal"); doc.setFontSize(8); doc.setTextColor(...ink);
      doc.text(pdfSafe([fmtDate(p.date_versement||p.date_validation),p.moyen,p.banque].filter(Boolean).join("  ·  ")),19,ry,{maxWidth:120});
      doc.setFont("helvetica","bold"); doc.setTextColor(30,122,45);
      doc.text(pdfFCFA(p.montant),191,ry,{align:"right"});
      ry += 6;
    });
    doc.setDrawColor(...ink); doc.setLineWidth(0.4); doc.line(19,ry-2.8,191,ry-2.8);
    doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(...ink);
    doc.text(resteDu>0?"Reste à payer":"Solde",19,ry+3.4);
    doc.setTextColor(...(resteDu>0?[214,69,61]:[30,122,45]));
    doc.text(resteDu>0?pdfFCFA(resteDu):"0 FCFA",191,ry+3.4,{align:"right"});
    y += boxH+4;
  }

  if (!isProforma && kind!=="paid") {
    const PI = payInfo(entreprise);
    const qm = (PI.any||payerUrl(facture)) ? makeQrMatrix(payQrText(facture, entreprise, dueAmount)) : null;
    const lines = [];
    if (PI.momo) lines.push("MTN Mobile Money : "+PI.momo+(PI.momoNom?" ("+PI.momoNom+")":""));
    if (PI.banque||PI.compte) lines.push("Virement : "+[PI.banque,PI.compte].filter(Boolean).join(" - ")+(PI.titulaire?" ("+PI.titulaire+")":""));
    if (!PI.any) lines.push("Modes acceptés : "+(facture.reglement||"Virement · Mobile Money MTN/Orange · Espèces"));
    lines.push("Merci d'indiquer la référence "+(facture.numero||"")+" lors du règlement.");
    const qrSize = qm ? 30 : 0;
    const textW = 172;
    doc.setFont("helvetica","normal"); doc.setFontSize(8);
    const wrapped = lines.map(l=>doc.splitTextToSize(pdfSafe(l),textW));
    const nLines = wrapped.reduce((s,w)=>s+w.length,0);
    const boxH = 10.5 + nLines*4.2 + 1.5 + (qm ? qrSize+8 : 0);
    if (y+boxH>279) y=newPage();
    drawBox(14,y,182,boxH);
    doc.setFont("helvetica","bold"); doc.setFontSize(7.2); doc.setTextColor(...muted);
    doc.text("COMMENT PAYER",19,y+6);
    doc.setFont("helvetica","normal"); doc.setFontSize(8); doc.setTextColor(...ink);
    let ty = y+10.5;
    wrapped.forEach(w=>{ doc.text(w,19,ty); ty += w.length*4.2+0.8; });
    if (qm) {
      const qx = 105-qrSize/2, qy = ty+1;
      doc.setFillColor(255,255,255); doc.setDrawColor(227,233,230); doc.setLineWidth(0.2);
      doc.roundedRect(qx-1.5,qy-1.5,qrSize+3,qrSize+3,1.5,1.5,"FD");
      const cell = qrSize/(qm.n+4);
      doc.setFillColor(15,31,23);
      for (let r=0;r<qm.n;r++) for (let c=0;c<qm.n;c++) if (qm.rows[r][c]) doc.rect(qx+(c+2)*cell, qy+(r+2)*cell, cell+0.03, cell+0.03, "F");
      doc.setFont("helvetica","normal"); doc.setFontSize(6.8); doc.setTextColor(...muted);
      doc.text(payerUrl(facture)?"Scannez pour payer ou envoyer votre preuve":"Scannez pour lire les détails du paiement",105,qy+qrSize+4.6,{align:"center"});
    }
    y += boxH+6;
  }

  if (facture.note || isProforma) {

    if (y>262) y=newPage();
    const noteText = isProforma
      ? [facture.note, "Document non contractuel. Valable selon la date indiquée ci-dessus."].filter(Boolean).join("\n")
      : facture.note;
    const noteLines=doc.splitTextToSize(pdfSafe(noteText||""),172);
    const noteH=Math.max(12,noteLines.length*4.2+8);
    doc.setFillColor(...pale);
    doc.roundedRect(14,y,182,noteH,2,2,"F");
    doc.setFillColor(...gold);
    doc.rect(14,y,2.2,noteH,"F");
    doc.setTextColor(...muted);
    doc.setFont("helvetica","normal");
    doc.setFontSize(7.8);
    doc.text(noteLines,20,y+6,{lineHeightFactor:1.25});
    y += noteH+6;
  }

  // Avaries signalées sur cette facture : produit, quantité, raison, lieu / adresse / ville, remplacement.
  const avs = Array.isArray(avaries) ? avaries : [];
  if (avs.length && !isProforma) {
    if (y>222) y=newPage();
    doc.setFont("helvetica","bold");doc.setFontSize(8);doc.setTextColor(...gold);
    doc.text("AVARIES SIGNALÉES SUR CETTE FACTURE",14,y+3);
    y += 7;
    const avCols=[
      {label:"DATE",width:20},
      {label:"PRODUIT",width:38},
      {label:"QTÉ",width:12,align:"right"},
      {label:"RAISON",width:30},
      {label:"LIEU / ADRESSE / VILLE",width:56},
      {label:"REMPLACEMENT",width:26}
    ];
    y=drawPdfTableHeader(doc,y,avCols);
    avs.forEach(a=>{
      const lieu=[a.lieu,a.adresse,a.lieu_ville||a.ville].filter(Boolean).join(" — ");
      const rempl=(a.remplacement||"")+(a.remplacement==="Remplacée"&&a.remplace_le?" le "+new Date(a.remplace_le).toLocaleDateString("fr-FR"):"");
      const cells=[
        {text:new Date(a.date).toLocaleDateString("fr-FR"),fontSize:7.2},
        {text:a.produit||"",fontSize:7.4},
        {text:pdfNUM(a.quantite),align:"right",fontSize:7.6,bold:true},
        {text:a.raison||"—",fontSize:7.2},
        {text:lieu||"—",fontSize:7.2},
        {text:rempl||"—",fontSize:7.2}
      ];
      const h=getPdfTableRowHeight(doc,avCols,cells,{minHeight:8.5});
      if(y+h>273){y=newPage();y=drawPdfTableHeader(doc,y,avCols);}
      y=drawPdfTableRow(doc,y,avCols,cells,{height:h,minHeight:8.5});
    });
  }

  addPdfPageFooters(doc,entreprise,isProforma?"Proforma":"Facture");
  const datauri = doc.output("datauristring");
  return { base64:datauri.split(",").pop(), filename:(isProforma?"Proforma_":"Facture_")+(facture.numero||today())+".pdf" };
};

// ══ Génère un PDF pour un document (lettre, offre de prix...) — pour pièce jointe email ══
const generateDocumentPdfBase64 = async (docForm, entreprise, session) => {
  const doc = new jsPDF({ unit:"mm", format:"a4" });
  const logoAsset = await getPdfLogoAsset();
  const headerOptions = {
    title:docForm.type || "DOCUMENT",
    reference:docForm.reference || "",
    date:fmtDate(docForm.date || today()),
    subtitle:docForm.reference ? fmtDate(docForm.date || today()) : "Correspondance officielle"
  };
  const newPage=()=>{ doc.addPage(); return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset); };
  let y=drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
  const { ink, muted, pale, gold }=PDF_BRAND;

  if (docForm.clientNom) {
    doc.setFillColor(...pale);
    doc.roundedRect(110,y,86,18,2,2,"F");
    doc.setTextColor(...muted);
    doc.setFont("helvetica","bold");
    doc.setFontSize(6.8);
    doc.text("À L'ATTENTION DE",114,y+6);
    doc.setTextColor(...ink);
    doc.setFontSize(9.5);
    doc.text(pdfSafe(docForm.clientNom),114,y+12,{maxWidth:76});
    y += 24;
  }

  if (docForm.objet) {
    doc.setFillColor(...pale);
    const objectLines=doc.splitTextToSize(pdfSafe(docForm.objet),164);
    const objectH=Math.max(13,objectLines.length*4.8+8);
    doc.roundedRect(14,y,182,objectH,2,2,"F");
    doc.setFillColor(...gold);
    doc.rect(14,y,2.2,objectH,"F");
    doc.setFont("helvetica","bold");
    doc.setFontSize(8.7);
    doc.setTextColor(...ink);
    doc.text("Objet :",20,y+7);
    doc.setFont("helvetica","normal");
    doc.text(objectLines,35,y+7,{lineHeightFactor:1.2});
    y += objectH+9;
  }

  doc.setTextColor(...ink);
  doc.setFont("helvetica","normal");
  doc.setFontSize(10);
  const paragraphs=String(docForm.corps||"").split(/\n+/);
  paragraphs.forEach((paragraph,index)=>{
    const lines=doc.splitTextToSize(pdfSafe(paragraph),176);
    if (!lines.length) { y+=5; return; }
    lines.forEach(line=>{
      if (y>270) y=newPage();
      doc.text(line,20,y);
      y+=5.7;
    });
    if (index<paragraphs.length-1) y+=3.2;
  });

  const _sg = sigValide(docForm) ? docForm.signature : null;
  if (y>(_sg?226:235)) y=newPage();
  y+=16;
  doc.setTextColor(...muted);
  doc.setFontSize(8);
  doc.text(_sg&&!_sg.moi?"Signature":"Pour "+pdfSafe(enterpriseValue(entreprise,"entreprise_nom","l'entreprise")),196,y,{align:"right"});
  y+=20;
  doc.setDrawColor(...gold);
  if (_sg) {
    try {
      const dm = pngDims(_sg.png); const k = Math.min(50/dm.w, 20/dm.h); const dw = dm.w*k, dh = dm.h*k;
      doc.addImage(_sg.png,"PNG",171-dw/2,y-dh-0.6,dw,dh);
    } catch(_) {}
    const qv = makeQrMatrix(sigVerifUrl(_sg.token));
    if (qv) {
      const qs = 24, qx = 20, qy = y-22, cell = qs/(qv.n+4);
      doc.setFillColor(255,255,255); doc.setDrawColor(227,233,230); doc.setLineWidth(0.2);
      doc.roundedRect(qx-1.2,qy-1.2,qs+2.4,qs+2.4,1.5,1.5,"FD");
      doc.setFillColor(15,31,23);
      for (let r=0;r<qv.n;r++) for (let c=0;c<qv.n;c++) if (qv.rows[r][c]) doc.rect(qx+(c+2)*cell, qy+(r+2)*cell, cell+0.03, cell+0.03, "F");
      doc.setFont("helvetica","bold"); doc.setFontSize(7.5); doc.setTextColor(...ink);
      doc.text("Document signé électroniquement",qx+qs+4,y-16);
      doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(...muted);
      doc.text("Scannez le QR code pour vérifier la signature",qx+qs+4,y-11.8);
      doc.text("Empreinte : "+_sg.empreinte,qx+qs+4,y-8);
    }
  }
  doc.setDrawColor(...gold);
  doc.line(145,y,196,y);
  y+=6;
  doc.setTextColor(...ink);
  doc.setFont("helvetica","bold");
  doc.setFontSize(9.5);
  doc.text(pdfSafe(_sg?_sg.nom:(session?.nom||"Direction")),196,y,{align:"right"});
  y+=5;
  doc.setFont("helvetica","normal");
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text(pdfSafe(_sg?(_sg.fonction||""):(session?.poste||"Direction")),196,y,{align:"right"});
  if (_sg) {
    doc.setFontSize(7); doc.text(pdfSafe("Signé électroniquement le "+sigHeure(_sg.signe_le)),196,y+4.6,{align:"right"});
  }

  addPdfPageFooters(doc,entreprise,docForm.type||"Document");
  const datauri=doc.output("datauristring");
  return { base64:datauri.split(",").pop(), filename:(docForm.type||"Document").replace(/\s+/g,"_")+".pdf" };
};


const base64PdfToBlob = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i=0; i<binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type:"application/pdf" });
};

const deliverPdfOnDevice = async ({ base64, filename, title="Document Azongbio", onOpenInApp, direct=false }) => {
  const blob = base64PdfToBlob(base64);
  const safeFilename = String(filename || "document.pdf").replace(/[\\/:*?"<>|]+/g, "_");
  const file = new File([blob], safeFilename, { type:"application/pdf" });
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;

  // Partage natif prioritaire : WhatsApp, Gmail, impression, fichiers...
  if ((!onOpenInApp || direct) && navigator.share && navigator.canShare?.({ files:[file] })) {
    try {
      await navigator.share({ title, files:[file] });
      return { mode:"share" };
    } catch (error) {
      if (error?.name === "AbortError") return { mode:"cancelled" };
      console.warn("Partage PDF indisponible :", error);
    }
  }

  const url = URL.createObjectURL(blob);

  // Le partage natif n'est autorisé par le téléphone que juste après un appui sur un bouton :
  // un PDF fabriqué quelques secondes après l'appui (fiche d'accès, rapports...) est refusé.
  // On affiche alors le document DANS l'application, avec les boutons Partager (appui frais,
  // donc accepté) / Télécharger / Fermer — pour tout document, installé ou non.
  if (onOpenInApp) {
    try { window.__azPdfFiles = window.__azPdfFiles || {}; window.__azPdfFiles[url] = file; } catch(_) {}
    onOpenInApp(url, safeFilename, title);
    return { mode:"in-app" };
  }
  if (isStandalone) {
    window.location.assign(url);
    setTimeout(()=>URL.revokeObjectURL(url), 60000);
    return { mode:"open" };
  }

  // Navigateur classique : téléchargement direct.
  const link = document.createElement("a");
  link.href = url;
  link.download = safeFilename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(()=>URL.revokeObjectURL(url), 5000);
  return { mode:"download" };
};

// Correctif du 30/09/2026 : quand deliverPdfOnDevice ci-dessus retombe sur l'aperçu "dans
// l'app" (PWA installée sur téléphone sans partage natif disponible — notamment le cas
// signalé pour la fiche d'accès ERP/mot de passe), cet aperçu n'offrait jusqu'ici qu'un
// bouton "Fermer" : aucun moyen d'en faire quoi que ce soit (l'envoyer par WhatsApp, l'email,
// l'enregistrer). Cette fonction retente un partage natif depuis l'aperçu lui-même.
const sharePdfPreview = async ({ url, filename, title }) => {
  try {
    // Le fichier est déjà prêt (gardé à l'ouverture) : le partage part tout de suite, sans attente.
    let file = null;
    try { file = window.__azPdfFiles && window.__azPdfFiles[url]; } catch(_) {}
    if (!file) {
      const blob = await (await fetch(url)).blob();
      file = new File([blob], filename || "document.pdf", { type:"application/pdf" });
    }
    if (navigator.share && navigator.canShare?.({ files:[file] })) {
      await navigator.share({ title, files:[file] });
      return true;
    }
  } catch (error) {
    if (error?.name === "AbortError") return true;
    console.warn("Partage PDF (aperçu) indisponible :", error);
  }
  return false;
};

// ── Reprise des anciennes factures (avant l'ERP) : liste figée des 94 factures « à cocher » ──
const AC_BASE = new Set(["F00008", "F00017", "F00018", "F00019", "F00020", "F00021", "F00022", "F00023", "F00024", "F00025", "F00026", "F00027", "F00028", "F00029", "F00030", "F00031", "F00032", "F00036", "F00039", "F00048", "F00053", "F00054", "F00055", "F00058", "F00059", "F00061", "F00062", "F00063", "F00064", "F00069", "F00071", "F00072", "F00073", "F00074", "F00075", "F00076", "F00077", "F00078", "F00079", "F00080", "F00081", "F00083", "F00084", "F00085", "F00086", "F00087", "F00088", "F00089", "F00091", "F00092", "F00093", "F00094", "F00095", "F00096", "F00097", "F00098", "F00099", "F00100", "F00101", "F00102", "F00103", "F00104", "F00105", "F00106", "F00107", "F00108", "F00109", "F00110", "F00111", "F00112", "F00113", "F00114", "F00115", "F00116", "F00117", "F00118", "F00119", "F00122", "F00123", "F00125", "F00127", "F00129", "F00132", "F00138", "F00146", "F00148", "F00149", "F00151", "F00153", "F00154", "F00155", "F00156", "F00160", "F00161"]);
// Mode de paiement connu d'après la liste papier (Mobile Money : MTN ou Orange à préciser).
const AC_PRESET = {"F00106":"","F00107":"","F00108":"","F00058":"","F00116":"","F00103":"","F00114":"Virement bancaire","F00115":"Virement bancaire","F00098":"Virement bancaire","F00100":"Virement bancaire","F00101":"Virement bancaire","F00099":"Virement bancaire","F00119":"Virement bancaire","F00102":"Virement bancaire","F00104":"Virement bancaire","F00095":"Virement bancaire","F00151":"Virement bancaire","F00094":"Virement bancaire","F00125":"Virement bancaire","F00153":"Virement bancaire","F00146":"Virement bancaire"};

const MOYENS_REGLEMENT = [
  "", "Mobile Money MTN", "Mobile Money Orange",
  "Virement bancaire", "Carte bancaire", "Espèces", "Chèque"
];

// ── Contrôle qualité par lot : mesures relevées et points de conformité vérifiés
// par le producteur en fin de production (flux en 2 étapes, voir page "controle_qualite").
const QC_MESURES = [
  ["ph_avant", "pH avant ajustement", "", true],
  ["ph_apres", "pH après ajustement", "cible 3,2-3,8 (max 4,0)", true],
  ["temp_max_pasteur", "Température max. produit (°C)", "92-95 °C", true],
  ["duree_maintien", "Durée de maintien", "15-30 sec", false],
  ["temp_remplissage", "Température de remplissage (°C)", "85-88 °C", true],
  ["temp_refroidissement", "Température fin refroidissement (°C)", "35-40 °C", true],
  ["heure_debut", "Heure début pasteurisation", "", false],
  ["heure_fin", "Heure fin pasteurisation", "", false],
  ["delai_embouteillage", "Délai préparation avant embouteillage", "au minimum", false],
];
const QC_CHECKS = {
  "Matières premières & formulation (pour 5 L)": [
    ["eau", "Eau potable et contrôlée"],
    ["the", "Thé propre, bien stocké"],
    ["aromates", "Aromates lavés, sains"],
    ["sucre", "Sucre — 300 g / 5 L"],
    ["acide_citrique", "Acide citrique — 2,5 g / 5 L"],
    ["sorbate", "Sorbate de potassium — 1,0 g / 5 L"],
    ["benzoate", "Benzoate de sodium — 0,5 g / 5 L"],
  ],
  "Conditionnement & anti-recontamination": [
    ["bouteilles_desinf", "Bouteilles nettoyées, rincées et désinfectées"],
    ["bouchons_desinf", "Bouchons désinfectés, intérieur non touché"],
    ["remplisseuse", "Remplisseuse nettoyée avant production"],
    ["bouchage", "Bouchage immédiat"],
    ["retournement", "Retournement des bouteilles"],
    ["etancheite", "Étanchéité / couple de serrage contrôlés"],
    ["separation_zones", "Séparation zones avant / après pasteurisation"],
  ],
  "Hygiène du personnel (BPH)": [
    ["mains", "Mains lavées et désinfectées"],
    ["tenue", "Tenue propre (charlotte, masque, gants)"],
    ["equipements", "Équipements nettoyés avant/après"],
    ["zones", "Zones propres et ordonnées"],
    ["dechets", "Déchets évacués rapidement"],
  ],
  "Contrôle final": [
    ["visuel", "Contrôle visuel : limpidité, absence de dépôt/gaz"],
    ["ph_final_ok", "pH final vérifié et enregistré"],
  ],
};
const QC_EMPTY_FORM = {
  volume_litres: "", nombre_bouteilles: "",
  ph_avant: "", ph_apres: "", temp_max_pasteur: "", duree_maintien: "",
  temp_remplissage: "", temp_refroidissement: "", heure_debut: "", heure_fin: "",
  delai_embouteillage: "", decision: "", observations: "", visa_responsable: "",
};

// Composant select moyen de règlement réutilisable — défini ici, en dehors du
// composant App(), pour que son identité reste stable entre les rendus. Il était
// auparavant défini à l'intérieur de App(), ce qui recréait une fonction différente
// à chaque rendu : React démontait/remontait alors le <select> à chaque frappe,
// provoquant un clignotement qui empêchait de choisir une option.
const SelectReglement = ({value, onChange}) => (
  <select className="inp" value={value} onChange={onChange}>
    {MOYENS_REGLEMENT.map(m=>(
      <option key={m} value={m}>{m||"-- Non spécifié --"}</option>
    ))}
  </select>
);

const ALL_MODULES = [
  { id:"tableau",       label:"Tableau de bord",    cat:"principal" },
  { id:"ventes",        label:"Ventes",              cat:"commercial" },
  { id:"factures",      label:"Factures",            cat:"commercial" },
  { id:"proformas",     label:"Proformas",           cat:"commercial" },
  { id:"clients",       label:"Clients",             cat:"commercial" },
  { id:"objectifs",     label:"Objectifs"          },
  { id:"paiements",     label:"Preuves de paiement" },
  { id:"achats",        label:"Achats",              cat:"stock" },
  { id:"stock",         label:"Stock & Produits",    cat:"stock" },
  { id:"production",    label:"Production / Lots",   cat:"stock" },
  { id:"controle_qualite", label:"Contrôle qualité",  cat:"stock" },
  { id:"fournisseurs",  label:"Fournisseurs",        cat:"finance" },
  { id:"livraisons",    label:"Livraisons",          cat:"stock" },
  { id:"stock-villes",  label:"Parcours du stock",   cat:"stock" },
  { id:"avaries",       label:"Avaries",             cat:"stock" },
  { id:"prospection",   label:"Prospection",         cat:"commercial" },
  { id:"comptabilite",  label:"Comptabilité",        cat:"finance" },
  { id:"caisse",        label:"Caisse",              cat:"finance" },
  { id:"rentabilite",   label:"Rentabilité",         cat:"finance" },
  { id:"rapports",      label:"Rapports",            cat:"finance" },
  { id:"documents",     label:"Documents",           cat:"communication" },
  { id:"messagerie",    label:"Boîte mail",          cat:"communication" },
  { id:"employes",      label:"Employés",            cat:"admin" },
  { id:"immobilisations", label:"Immobilisations",    cat:"admin" },
  { id:"procurations",  label:"Procurations",        cat:"admin" },
  { id:"salaires",      label:"Fiches de salaire",   cat:"admin" },
  { id:"parametres",    label:"Paramètres",          cat:"admin" },
  { id:"peut_modifier", label:"Peut modifier",       cat:"permission_only" }, // permission seule, jamais un onglet
  { id:"valider_paiements", label:"Valider les paiements", cat:"permission_only" },
  { id:"supprimer_factures", label:"Supprimer les factures", cat:"permission_only" },
  { id:"voir_toutes_villes", label:"Voir toutes les villes", cat:"permission_only" },
  { id:"transferer_villes", label:"Transférer du stock entre villes (dépannage)", cat:"permission_only" },
  { id:"voir_tous_chiffres", label:"Voir les chiffres de tous (CA, factures, créances des autres employés)", cat:"permission_only" },
  { id:"rh_donnees_sensibles", label:"Voir les données RH sensibles (salaire, CNI, banque, adresse, notes...)", cat:"permission_only" },
  { id:"chat",          label:"Chat d'équipe",       cat:"header_only" },     // affiché dans le header, pas le menu
];

// Fonctions ERP qu'un employé peut cumuler (ex: Commercial + Comptable en même temps),
// choisies via des cases à cocher sur sa fiche (auparavant une liste déroulante à choix
// unique). Le champ `role` (texte, une seule valeur) continue d'exister en base et reste
// la source de vérité pour tous les contrôles d'accès déjà en place dans l'appli et côté
// serveur (isDirecteur, visibilité multi-villes, création de compte...) — on le recalcule
// automatiquement à partir des fonctions cochées plutôt que de réécrire ces contrôles.
const FONCTIONS_OPTIONS = [
  ["employe","Employé"], ["responsable_ville","Responsable de ville"], ["production","Production"],
  ["commercial","Commercial"], ["comptable","Comptable"], ["administrateur","Administrateur"], ["directeur","Directeur"]
];
const FONCTIONS_PRIORITE = ["directeur","administrateur","responsable_ville","comptable","commercial","production","employe"];
const rolePrincipalDeFonctions = fonctions => {
  const list = Array.isArray(fonctions) ? fonctions : [];
  return FONCTIONS_PRIORITE.find(r=>list.includes(r)) || "employe";
};
// Pour une fiche existante créée avant l'ajout des cases à cocher (donc sans `fonctions`
// enregistré), on reconstruit la liste à partir de l'ancien champ `role` unique, pour que
// rien ne semble décoché à l'ouverture.
const fonctionsDepuisEmploye = e => {
  const stored = safeJsonParse(e?.fonctions, null);
  if (Array.isArray(stored) && stored.length) return stored;
  const legacyRole = String(e?.role||"").trim().toLowerCase();
  return legacyRole ? [legacyRole] : ["employe"];
};

// Correctif du 30/09/2026 (audit — vague 7) : « est-ce un directeur ? » était réécrite
// indépendamment à 4 endroits dans ce fichier (isDirecteur() pour la session courante,
// le calcul de globalAccess dans load() pour une fiche fraîchement relue, et deux filtres
// isDirecteurRow/isDirRow pour une fiche employé quelconque) — la même formule copiée-collée
// plutôt qu'une seule règle centrale. Extrait ici en une fonction pure (role/poste déjà en
// minuscules ou pas, peu importe) : chaque appelant continue de fournir sa propre source
// (session, fiche fraîchement relue, ou fiche d'un autre employé), mais la RÈGLE elle-même
// n'existe plus qu'une fois — un futur changement de critère se fait à un seul endroit.
const estRoleDirecteur = (role, poste) => {
  const r = String(role||"").trim().toLowerCase();
  const p = String(poste||"").trim().toLowerCase();
  return r.includes("directeur") || ["direction","administrateur","admin"].includes(r) || p.includes("directeur");
};

/* ══ LOGIN ══ */
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Authentification via Supabase Auth uniquement. L'ancien repli bcrypt (/api/login)
  // a été retiré du parcours de connexion le 23/08/2026, une fois vérifié que tous les
  // employés actifs avaient bien un compte Supabase Auth synchronisé. Le fichier
  // api/login.js reste sur le serveur comme outil de dépannage manuel (non appelé
  // automatiquement), au cas où le conteneur Auth aurait un incident.
  // Important : depuis ce changement, un nouvel employé doit avoir son compte créé via
  // api/save-employee.js (création automatique intégrée) — sinon il ne pourra pas se
  // connecter, faute de compte Supabase Auth.
  const login = async () => {
    if (!username||!password) return setErr("Remplissez tous les champs");
    setLoading(true);
    const uname = username.trim().toLowerCase();

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: uname + "@braliebranding.internal",
        password
      });
      if (!authError && authData?.user) {
        const employeId = authData.user.app_metadata?.employe_id;
        const { data: emp, error: empErr } = await supabase.from("employes").select("*").eq("id", employeId).maybeSingle();
        if (!empErr && emp) {
          // Le mot de passe est correct, mais le compte a été désactivé (collaborateur
          // parti) : on referme immédiatement la session Supabase Auth qui vient d'être
          // ouverte, pour ne laisser aucun accès valide traîner, et on refuse l'entrée.
          if (emp.actif === false) {
            try { await supabase.auth.signOut({ scope: "local" }); } catch {}
            setLoading(false);
            setErr("Ce compte a été désactivé"); setTimeout(()=>setErr(""),3000);
            return;
          }
          // Historique des connexions — remis en place ici car il n'était plus alimenté
          // depuis que la connexion passe directement par Supabase Auth (le 23/08/2026,
          // l'ancien repli /api/login.js, seul endroit qui l'écrivait jusque-là, a été
          // retiré du parcours normal). Ne doit jamais empêcher la connexion : erreur avalée.
          try { await supabase.from("connexions").insert({ employe_nom:emp.nom, employe_matricule:emp.matricule }); } catch {}
          try { await supabase.from("employes").update({ derniere_connexion:new Date().toISOString() }).eq("id", emp.id); } catch {}
          setLoading(false); onLogin(emp); return;
        }
      }
      setLoading(false);
      setErr("Identifiant ou mot de passe incorrect"); setTimeout(()=>setErr(""),3000);
    } catch (e) {
      setLoading(false);
      setErr("Erreur de connexion — réessayez"); setTimeout(()=>setErr(""),3000);
    }
  };

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, overflowY:"auto", overflowX:"hidden", overscrollBehavior:"none", WebkitOverflowScrolling:"touch", background:"#F5F7F6", display:"flex", fontFamily:"var(--az-font)" }}>
      <div style={{ width:"100%", maxWidth:420, padding:20, margin:"auto", boxSizing:"border-box" }}>
        <div style={{ textAlign:"center", marginBottom:40 }}>
          <img src={LOGO_UI} alt="Azongbio" style={{ height:64, marginBottom:16 }} />
          <div style={{ fontSize:11, letterSpacing:3, textTransform:"uppercase", color:"#5B6B62" }}>Bralie Branding Food SARL</div>
          <div style={{ fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"#1E8A32", marginTop:4 }}>Système de Gestion Interne</div>
        </div>
        <div style={{ background:"#FFFFFF", border:"1px solid rgba(47,158,63,0.27)", borderRadius:8, padding:32 }}>
          <div style={{ fontSize:22, color:"#0F1F17", marginBottom:24, textAlign:"center" }}>Connexion</div>
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1.5, color:"#5B6B62", display:"block", marginBottom:5 }}>Nom d'utilisateur</label>
            <input placeholder="Votre identifiant" autoCapitalize="none" autoCorrect="off" autoComplete="username" value={username} onChange={e=>setUsername(e.target.value)}
              style={{ width:"100%", background:"#F1F5F3", border:"1px solid #E3E9E6", color:"#0F1F17", padding:"10px 14px", borderRadius:4, fontSize:(typeof window!=="undefined"&&window.innerWidth<=768)?16:14, fontFamily:"inherit", boxSizing:"border-box" }} />
          </div>
          <div style={{ marginBottom:24 }}>
            <label style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1.5, color:"#5B6B62", display:"block", marginBottom:5 }}>Mot de passe</label>
            <div style={{ position:"relative" }}>
              <input type={showPwd?"text":"password"} placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&login()}
                style={{ width:"100%", background:"#F1F5F3", border:"1px solid #E3E9E6", color:"#0F1F17", padding:"10px 42px 10px 14px", borderRadius:4, fontSize:(typeof window!=="undefined"&&window.innerWidth<=768)?16:14, fontFamily:"inherit", boxSizing:"border-box" }} />
              <button onClick={()=>setShowPwd(!showPwd)} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"#5B6B62", cursor:"pointer", fontSize:16, display:"flex", alignItems:"center" }}>
                {showPwd?<EyeOff size={16}/>:<Eye size={16}/>}
              </button>
            </div>
          </div>
          {err && <div style={{ background:"rgba(192,57,43,.2)", border:"1px solid #E9A29D", borderRadius:4, padding:"8px 12px", color:"#D6453D", fontSize:13, marginBottom:16 }}>{err}</div>}
          <button onClick={login} disabled={loading}
            style={{ width:"100%", padding:"12px", background:"#2F9E3F", color:"#FFFFFF", border:"none", borderRadius:4, cursor:"pointer", fontWeight:700, fontSize:15, fontFamily:"inherit" }}>
            {loading ? "Vérification..." : "Se connecter"}
          </button>
          <div style={{ marginTop:20, borderTop:"1px solid #E3E9E6", paddingTop:16, textAlign:"center" }}>
            <div style={{ fontSize:11, letterSpacing:2, textTransform:"uppercase", color:"#1E8A32", fontStyle:"italic" }}>
              L'âme du terroir camerounais
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══ FACTURE PRINT — VERSION CORRIGÉE ══ */
/* ══ SCANNER DE DOCUMENT — détection des bords, cadre bleu, redressement, PDF ══ */
// Le scan par caméra est réservé aux téléphones et tablettes (jamais la webcam d'un PC).
// Petit « bip » pour une nouvelle notification (ignoré si le navigateur ne l'autorise pas encore).
// ── Notifications sur téléphone (Web Push) ──
function pushCleVersOctets(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const brut = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(brut.length);
  for (let i = 0; i < brut.length; i++) out[i] = brut.charCodeAt(i);
  return out;
}
function pushSupporte() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}
function estIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
function estAppInstallee() {
  return (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;
}

function bipNotification() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.4);
    setTimeout(()=>{ try{ ctx.close(); }catch(_){} }, 700);
  } catch(_) {}
}
// « il y a 5 min », « hier », etc.
function notifIlYa(dateStr) {
  const t = new Date(dateStr).getTime();
  if (!Number.isFinite(t)) return "";
  const m = Math.max(0, Math.round((Date.now()-t)/60000));
  if (m < 1) return "à l'instant";
  if (m < 60) return "il y a "+m+" min";
  const h = Math.round(m/60);
  if (h < 24) return "il y a "+h+" h";
  const d = Math.round(h/24);
  if (d === 1) return "hier";
  if (d < 7) return "il y a "+d+" jours";
  return new Date(t).toLocaleDateString("fr-FR");
}
const estAppareilMobile = () => {
  try {
    const ua = navigator.userAgent || "";
    if (/Android|iPhone|iPad|iPod|Mobile|Tablet|Silk|Kindle/i.test(ua)) return true;
    // iPadOS se présente comme un Mac : on le reconnaît à son écran tactile.
    return navigator.platform === "MacIntel" && (navigator.maxTouchPoints||0) > 1;
  } catch(_) { return false; }
};

// Flou 3x3 pour calmer le bruit de l'image avant de chercher le document.
function scanBoxBlur(gray, w, h) {
  const out = new Uint8Array(w*h);
  for (let y=0;y<h;y++) for (let x=0;x<w;x++) {
    let s=0, c=0;
    for (let dy=-1;dy<=1;dy++) { const yy=y+dy; if (yy<0||yy>=h) continue;
      for (let dx=-1;dx<=1;dx++) { const xx=x+dx; if (xx<0||xx>=w) continue; s+=gray[yy*w+xx]; c++; } }
    out[y*w+x] = s/c;
  }
  return out;
}

// Seuil d'Otsu : sépare au mieux « clair » (papier) et « sombre » (fond).
function scanOtsu(gray) {
  const hist = new Array(256).fill(0);
  for (let i=0;i<gray.length;i++) hist[gray[i]]++;
  const total = gray.length;
  let sum=0; for (let i=0;i<256;i++) sum += i*hist[i];
  let sumB=0, wB=0, best=0, thr=128;
  for (let t=0;t<256;t++) {
    wB += hist[t]; if (!wB) continue;
    const wF = total-wB; if (!wF) break;
    sumB += t*hist[t];
    const mB=sumB/wB, mF=(sum-sumB)/wF;
    const between = wB*wF*(mB-mF)*(mB-mF);
    if (between>best) { best=between; thr=t; }
  }
  return thr;
}

// Cherche le plus grand « rectangle » (papier clair sur fond sombre, ou l'inverse) :
// renvoie ses 4 coins [haut-gauche, haut-droite, bas-droite, bas-gauche] ou null.
function scanFindQuad(gray, w, h) {
  const g = scanBoxBlur(gray, w, h);
  const t = scanOtsu(g);
  const total = w*h;
  let best = null;
  for (const bright of [true,false]) {
    const mask = new Uint8Array(total);
    for (let i=0;i<total;i++) mask[i] = (bright ? g[i]>t : g[i]<=t) ? 1 : 0;
    const seen = new Uint8Array(total);
    const stack = new Int32Array(total);
    for (let s=0;s<total;s++) {
      if (!mask[s] || seen[s]) continue;
      let sp=0; stack[sp++]=s; seen[s]=1;
      let area=0, L=false, R=false, T=false, B=false;
      let minSum=1e9,maxSum=-1e9,minDiff=1e9,maxDiff=-1e9;
      let tl=[0,0],br=[0,0],tr=[0,0],bl=[0,0];
      while (sp>0) {
        const p = stack[--sp]; const x = p%w, y = (p/w)|0; area++;
        if (x===0) L=true; if (x===w-1) R=true; if (y===0) T=true; if (y===h-1) B=true;
        const sm=x+y, df=x-y;
        if (sm<minSum) { minSum=sm; tl=[x,y]; }
        if (sm>maxSum) { maxSum=sm; br=[x,y]; }
        if (df>maxDiff) { maxDiff=df; tr=[x,y]; }
        if (df<minDiff) { minDiff=df; bl=[x,y]; }
        if (x>0)   { const n=p-1; if (mask[n]&&!seen[n]) { seen[n]=1; stack[sp++]=n; } }
        if (x<w-1) { const n=p+1; if (mask[n]&&!seen[n]) { seen[n]=1; stack[sp++]=n; } }
        if (y>0)   { const n=p-w; if (mask[n]&&!seen[n]) { seen[n]=1; stack[sp++]=n; } }
        if (y<h-1) { const n=p+w; if (mask[n]&&!seen[n]) { seen[n]=1; stack[sp++]=n; } }
      }
      if (area < total*0.12 || area > total*0.92) continue;
      const touches = (L?1:0)+(R?1:0)+(T?1:0)+(B?1:0);
      if (touches >= 3) continue; // c'est le fond, pas le document
      const pts = [tl,tr,br,bl];
      let qa = 0;
      for (let i=0;i<4;i++) { const a=pts[i], b=pts[(i+1)%4]; qa += a[0]*b[1]-b[0]*a[1]; }
      qa = Math.abs(qa)/2;
      if (qa <= 0) continue;
      const rect = area/qa;
      if (rect < 0.72 || rect > 1.12) continue;
      let minSide = 1e9;
      for (let i=0;i<4;i++) { const a=pts[i], b=pts[(i+1)%4]; minSide = Math.min(minSide, Math.hypot(a[0]-b[0],a[1]-b[1])); }
      if (minSide < w*0.2) continue;
      const score = area*(1-Math.abs(1-rect));
      if (!best || score>best.score) best = { pts, score, area:area/total, rect };
    }
  }
  return best;
}

// Homographie : pour 4 points (u,v) de destination -> (x,y) de la source.
function scanHomography(dst, src) {
  const A = [], b = [];
  for (let i=0;i<4;i++) {
    const [u,v]=dst[i], [x,y]=src[i];
    A.push([u,v,1,0,0,0,-u*x,-v*x]); b.push(x);
    A.push([0,0,0,u,v,1,-u*y,-v*y]); b.push(y);
  }
  const n=8;
  for (let i=0;i<n;i++) A[i].push(b[i]);
  for (let c=0;c<n;c++) {
    let piv=c;
    for (let r=c+1;r<n;r++) if (Math.abs(A[r][c])>Math.abs(A[piv][c])) piv=r;
    if (Math.abs(A[piv][c])<1e-10) return null;
    [A[c],A[piv]]=[A[piv],A[c]];
    for (let r=0;r<n;r++) {
      if (r===c) continue;
      const f=A[r][c]/A[c][c];
      for (let k=c;k<=n;k++) A[r][k]-=f*A[c][k];
    }
  }
  return A.map((row,i)=>row[n]/row[i]);
}

// Redresse le document : renvoie un ImageData-like {data,width,height} de taille ow x oh.
function scanWarp(srcData, sw, sh, quad, ow, oh) {
  const H = scanHomography([[0,0],[ow-1,0],[ow-1,oh-1],[0,oh-1]], quad);
  if (!H) return null;
  const out = new Uint8ClampedArray(ow*oh*4);
  for (let v=0;v<oh;v++) for (let u=0;u<ow;u++) {
    const den = H[6]*u + H[7]*v + 1;
    let x = (H[0]*u + H[1]*v + H[2])/den;
    let y = (H[3]*u + H[4]*v + H[5])/den;
    if (x<0) x=0; if (y<0) y=0; if (x>sw-1.001) x=sw-1.001; if (y>sh-1.001) y=sh-1.001;
    const x0=x|0, y0=y|0, fx=x-x0, fy=y-y0;
    const i00=(y0*sw+x0)*4, i10=i00+4, i01=i00+sw*4, i11=i01+4;
    const o=(v*ow+u)*4;
    for (let c=0;c<3;c++) {
      out[o+c] = srcData[i00+c]*(1-fx)*(1-fy)+srcData[i10+c]*fx*(1-fy)+srcData[i01+c]*(1-fx)*fy+srcData[i11+c]*fx*fy;
    }
    out[o+3]=255;
  }
  return { data:out, width:ow, height:oh };
}

// Étire les contrastes (2e-98e centile de luminance) pour un rendu « scan » plus lisible.
function scanEnhance(img) {
  const d = img.data, n = img.width*img.height;
  const hist = new Array(256).fill(0);
  for (let i=0;i<n;i++) { const o=i*4; hist[(0.3*d[o]+0.59*d[o+1]+0.11*d[o+2])|0]++; }
  let acc=0, lo=0, hi=255;
  for (let i=0;i<256;i++) { acc+=hist[i]; if (acc>=n*0.02) { lo=i; break; } }
  acc=0;
  for (let i=255;i>=0;i--) { acc+=hist[i]; if (acc>=n*0.02) { hi=i; break; } }
  if (hi-lo < 40) return img;
  const k = 255/(hi-lo);
  for (let i=0;i<n;i++) { const o=i*4; for (let c=0;c<3;c++) { const v=(d[o+c]-lo)*k; d[o+c]=v<0?0:v>255?255:v; } }
  return img;
}

// Transforme un canvas en fichier PDF A4 (portrait ou paysage selon la forme du document).
function canvasToPdfFile(canvas, baseName) {
  const landscape = canvas.width > canvas.height;
  const pdf = new jsPDF({ unit:"mm", format:"a4", orientation: landscape?"landscape":"portrait" });
  const pw = landscape?297:210, ph = landscape?210:297, m = 5;
  const ratio = Math.min((pw-2*m)/canvas.width, (ph-2*m)/canvas.height);
  const w = canvas.width*ratio, h = canvas.height*ratio;
  pdf.addImage(canvas.toDataURL("image/jpeg",0.85), "JPEG", (pw-w)/2, (ph-h)/2, w, h);
  return new File([pdf.output("blob")], baseName+"_"+Date.now()+".pdf", { type:"application/pdf" });
}

// Photo classique ou image importée : convertie en PDF avant l'envoi (un PDF reste tel quel).
async function imageFileToPdf(file) {
  if (!file || file.type==="application/pdf" || !/^image\//.test(file.type)) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res,rej)=>{ const i=new Image(); i.onload=()=>res(i); i.onerror=()=>rej(new Error("Image illisible")); i.src=url; });
    const k = Math.min(1, 1800/Math.max(img.naturalWidth,img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.max(1,Math.round(img.naturalWidth*k)); c.height = Math.max(1,Math.round(img.naturalHeight*k));
    c.getContext("2d").drawImage(img,0,0,c.width,c.height);
    return canvasToPdfFile(c, "preuve");
  } finally { URL.revokeObjectURL(url); }
}

// Photo de profil : redimensionnée (max 480 px) et compressée en JPEG léger, stockée directement dans la fiche.
async function photoFileToDataUrl(file) {
  if (!file) throw new Error("Aucune photo reçue.");
  // Lecture immédiate du fichier (plus fiable sur iPhone que de relire le fichier plus tard).
  const brut = await new Promise((res,rej)=>{ const r=new FileReader(); r.onload=()=>res(r.result); r.onerror=()=>rej(new Error("Impossible de lire la photo.")); r.readAsDataURL(file); });
  const img = await new Promise((res,rej)=>{ const i=new Image(); i.onload=()=>res(i); i.onerror=()=>rej(new Error("Format de photo non pris en charge. Prends la photo avec l'appareil, ou choisis un JPG/PNG.")); i.src=brut; });
  const k = Math.min(1, 480/Math.max(img.naturalWidth,img.naturalHeight));
  const c = document.createElement("canvas");
  c.width = Math.max(1,Math.round(img.naturalWidth*k)); c.height = Math.max(1,Math.round(img.naturalHeight*k));
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0,0,c.width,c.height);
  ctx.drawImage(img,0,0,c.width,c.height);
  return c.toDataURL("image/jpeg",0.82);
}

function AutoScanCamera({ onCapture, onCancel }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const workRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastRunRef = useRef(0);
  const startedAtRef = useRef(0);
  const capturedRef = useRef(false);
  const cancelledRef = useRef(false);
  const quadRef = useRef(null);      // coins lissés, en pixels de l'image de travail
  const missRef = useRef(0);
  const stableRef = useRef(0);
  const resultCanvasRef = useRef(null);
  const [status, setStatus] = useState("init"); // init | scanning | review | error
  const [found, setFound] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const STABLE_NEEDED = 9;   // ≈ 1 s de document immobile avant la capture automatique
  const WORK_W = 200;

  const stopStream = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach(t=>t.stop());
    streamRef.current = null;
  };

  const drawOverlay = (quad, sw, sh) => {
    const ov = overlayRef.current, box = containerRef.current, video = videoRef.current;
    if (!ov || !box || !video) return;
    const dpr = window.devicePixelRatio || 1;
    const cw = box.clientWidth, ch = box.clientHeight;
    if (ov.width !== Math.round(cw*dpr) || ov.height !== Math.round(ch*dpr)) { ov.width = Math.round(cw*dpr); ov.height = Math.round(ch*dpr); }
    const ctx = ov.getContext("2d");
    ctx.setTransform(dpr,0,0,dpr,0,0);
    ctx.clearRect(0,0,cw,ch);
    if (!quad) return;
    const vw = video.videoWidth || 1, vh = video.videoHeight || 1;
    const k = Math.min(cw/vw, ch/vh);
    const ox = (cw-vw*k)/2, oy = (ch-vh*k)/2;
    const P = quad.map(([x,y]) => [ox + x*(vw/sw)*k, oy + y*(vh/sh)*k]);
    ctx.beginPath();
    P.forEach(([x,y],i)=> i?ctx.lineTo(x,y):ctx.moveTo(x,y));
    ctx.closePath();
    ctx.fillStyle = "rgba(37,99,235,0.32)";   // zone du document recouverte en bleu
    ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = "#2563EB"; ctx.stroke();
    ctx.fillStyle = "#fff";
    P.forEach(([x,y])=>{ ctx.beginPath(); ctx.arc(x,y,5,0,Math.PI*2); ctx.fill(); ctx.stroke(); });
  };

  const analyse = () => {
    const video = videoRef.current, work = workRef.current;
    if (!video || !work || video.readyState < 2 || !video.videoWidth) return;
    const sw = WORK_W, sh = Math.max(1, Math.round(WORK_W*video.videoHeight/video.videoWidth));
    if (work.width !== sw || work.height !== sh) { work.width = sw; work.height = sh; }
    const ctx = work.getContext("2d", { willReadFrequently:true });
    ctx.drawImage(video, 0, 0, sw, sh);
    const px = ctx.getImageData(0,0,sw,sh).data;
    const gray = new Uint8Array(sw*sh);
    for (let i=0,j=0;i<px.length;i+=4,j++) gray[j] = 0.3*px[i]+0.59*px[i+1]+0.11*px[i+2];
    const res = scanFindQuad(gray, sw, sh);
    if (res) {
      missRef.current = 0;
      const prev = quadRef.current;
      let moved = 1;
      if (prev) {
        moved = res.pts.reduce((s,p,i)=>s+Math.hypot(p[0]-prev[i][0],p[1]-prev[i][1]),0)/4/sw;
      }
      // Lissage : le cadre bleu suit le document sans trembler.
      quadRef.current = prev ? res.pts.map((p,i)=>[prev[i][0]*0.5+p[0]*0.5, prev[i][1]*0.5+p[1]*0.5]) : res.pts;
      stableRef.current = moved < 0.015 ? stableRef.current+1 : 0;
      setFound(true);
      setProgress(Math.min(100, Math.round(stableRef.current/STABLE_NEEDED*100)));
      drawOverlay(quadRef.current, sw, sh);
      if (stableRef.current >= STABLE_NEEDED && Date.now()-startedAtRef.current > 800) doCapture();
    } else {
      missRef.current += 1;
      if (missRef.current > 4) { quadRef.current = null; stableRef.current = 0; setFound(false); setProgress(0); drawOverlay(null, sw, sh); }
      else if (quadRef.current) drawOverlay(quadRef.current, sw, sh);
    }
  };

  const startCamera = async () => {
    try {
      if (!estAppareilMobile()) throw new Error("Le scan par caméra est réservé aux téléphones et tablettes.");
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Caméra non disponible sur cet appareil ou ce navigateur.");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode:{ ideal:"environment" }, width:{ ideal:1920 }, height:{ ideal:1080 } }, audio:false
      });
      if (cancelledRef.current) { stream.getTracks().forEach(t=>t.stop()); return; }
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      capturedRef.current = false; quadRef.current = null; missRef.current = 0; stableRef.current = 0;
      startedAtRef.current = Date.now();
      setFound(false); setProgress(0); setStatus("scanning");
      const loop = (ts) => {
        if (capturedRef.current || cancelledRef.current) return;
        rafRef.current = requestAnimationFrame(loop);
        if (ts - lastRunRef.current < 110) return;
        lastRunRef.current = ts;
        analyse();
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch (err) {
      if (!cancelledRef.current) { setStatus("error"); setErrMsg(err?.message || "Caméra indisponible"); }
    }
  };

  function doCapture() {
    if (capturedRef.current) return;
    capturedRef.current = true;
    const video = videoRef.current, work = workRef.current;
    const vw = video.videoWidth, vh = video.videoHeight;
    const full = document.createElement("canvas");
    full.width = vw; full.height = vh;
    const fctx = full.getContext("2d");
    fctx.drawImage(video, 0, 0, vw, vh);
    let out = full;
    const q = quadRef.current;
    if (q && work) {
      const kx = vw/work.width, ky = vh/work.height;
      // Léger retrait vers le centre (1,5 %) pour ne pas garder le fond sur les bords du scan.
      const cxq = q.reduce((a,p)=>a+p[0],0)/4, cyq = q.reduce((a,p)=>a+p[1],0)/4;
      const quad = q.map(([x,y])=>[(cxq+(x-cxq)*0.985)*kx, (cyq+(y-cyq)*0.985)*ky]);
      const d = (a,b)=>Math.hypot(a[0]-b[0],a[1]-b[1]);
      let ow = Math.max(d(quad[0],quad[1]), d(quad[3],quad[2]));
      let oh = Math.max(d(quad[0],quad[3]), d(quad[1],quad[2]));
      const k = Math.min(1, 1700/Math.max(ow,oh));
      ow = Math.max(300, Math.round(ow*k)); oh = Math.max(300, Math.round(oh*k));
      const src = fctx.getImageData(0,0,vw,vh);
      const warped = scanWarp(src.data, vw, vh, quad, ow, oh);
      if (warped) {
        scanEnhance(warped);
        const c = document.createElement("canvas");
        c.width = ow; c.height = oh;
        c.getContext("2d").putImageData(new ImageData(warped.data, ow, oh), 0, 0);
        out = c;
      }
    }
    resultCanvasRef.current = out;
    setPreviewUrl(out.toDataURL("image/jpeg", 0.8));
    stopStream();
    setStatus("review");
  }

  const validate = () => {
    try {
      const file = canvasToPdfFile(resultCanvasRef.current, "preuve");
      onCapture(file);
    } catch (e) { setStatus("error"); setErrMsg("Création du PDF impossible : "+(e?.message||e)); }
  };

  const retake = () => { capturedRef.current = false; setPreviewUrl(""); setStatus("init"); startCamera(); };

  useEffect(() => {
    cancelledRef.current = false;
    startCamera();
    return () => { cancelledRef.current = true; stopStream(); };
    // eslint-disable-next-line
  }, []);

  return (
    <div ref={containerRef} style={{position:"fixed",inset:0,background:"#000",zIndex:500,display:"flex",flexDirection:"column"}}>
      <video ref={videoRef} playsInline muted style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"contain",display:status==="review"?"none":"block"}} />
      <canvas ref={workRef} style={{display:"none"}} />
      <canvas ref={overlayRef} style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none",display:status==="scanning"?"block":"none"}} />
      {status==="scanning" && (
        <div style={{position:"absolute",left:0,right:0,bottom:"max(22px,env(safe-area-inset-bottom))",display:"flex",flexDirection:"column",alignItems:"center",pointerEvents:"none"}}>
          <div style={{width:"70%",maxWidth:300,height:6,background:"rgba(255,255,255,.25)",borderRadius:3,overflow:"hidden"}}>
            <div style={{height:"100%",width:progress+"%",background:"#2563EB",transition:"width .1s linear"}} />
          </div>
          <div style={{marginTop:12,color:"#fff",fontSize:13,textAlign:"center",padding:"0 20px",textShadow:"0 1px 3px #000"}}>
            {!found ? "Placez le document entier dans l'image, sur un fond contrasté" : progress<100 ? "Document détecté — ne bougez plus…" : "Capture…"}
          </div>
        </div>
      )}
      {status==="init" && (
        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:14}}>Ouverture de la caméra…</div>
      )}
      {status==="review" && (
        <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",background:"#0b1626"}}>
          <div style={{flex:1,minHeight:0,display:"flex",alignItems:"center",justifyContent:"center",padding:"64px 12px 8px"}}>
            <img src={previewUrl} alt="Document scanné" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain",background:"#fff",boxShadow:"0 2px 14px rgba(0,0,0,.6)"}} />
          </div>
          <div style={{padding:"10px 14px max(16px,env(safe-area-inset-bottom))",display:"flex",gap:10}}>
            <button onClick={retake} style={{flex:1,padding:"12px",background:"transparent",color:"#fff",border:"1px solid rgba(255,255,255,.4)",borderRadius:8,fontSize:14,cursor:"pointer"}}>Reprendre</button>
            <button onClick={validate} style={{flex:2,padding:"12px",background:"#2563EB",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:700,cursor:"pointer"}}>Valider (enregistrer en PDF)</button>
          </div>
        </div>
      )}
      {status==="error" && (
        <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:13,padding:24,textAlign:"center",gap:14}}>
          <div>{errMsg}</div>
          <div style={{color:"#A8B6C4",fontSize:12}}>Vous pouvez aussi importer un fichier existant avec le bouton « Importer un fichier ».</div>
        </div>
      )}
      <div style={{position:"absolute",top:"max(14px,env(safe-area-inset-top))",left:0,right:0,display:"flex",justifyContent:"space-between",padding:"0 14px"}}>
        <button onClick={()=>{cancelledRef.current=true;stopStream();onCancel();}} style={{padding:"9px 16px",background:"rgba(0,0,0,.55)",color:"#fff",border:"1px solid rgba(255,255,255,.3)",borderRadius:20,cursor:"pointer",fontSize:13}}>✕ Annuler</button>
        {status==="scanning" && <button onClick={doCapture} style={{padding:"9px 16px",background:"#2563EB",color:"#fff",border:"none",borderRadius:20,cursor:"pointer",fontSize:13,fontWeight:700}}>Capturer maintenant</button>}
      </div>
    </div>
  );
}

// Capture d'une ou plusieurs signatures à l'écran (doigt ou stylet), utilisée
// avant de générer un document qui comporte une ligne "Signature" (fiche de
// remise de matériel, contrat de travail, feuille de transport, bulletin de
// paie...). `signers` est une liste [{key,label}] : un écran de signature est
// affiché pour chacun, dans l'ordre. "Passer" laisse la ligne vierge (à signer
// à la main plus tard, comportement identique à avant cette fonctionnalité) ;
// "Valider" enregistre le tracé et passe au signataire suivant. `onComplete`
// reçoit toujours un objet {clé: dataURL PNG, ...} — jamais null — pour que
// l'appelant puisse toujours générer le document, signé ou non.
function SignatureFlow({ documentTitle, signers, onComplete }) {
  const [step, setStep] = useState(0);
  const [captured, setCaptured] = useState({});
  const [hasDrawn, setHasDrawn] = useState(false);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const hasDrawnRef = useRef(false);
  const signer = signers[step];

  useEffect(() => {
    const canvas = canvasRef.current, container = containerRef.current;
    if (!canvas || !container) return;
    const setup = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1,Math.round(rect.width*dpr));
      canvas.height = Math.max(1,Math.round(rect.height*dpr));
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr,0,0,dpr,0,0);
      ctx.fillStyle = "#fff";
      ctx.fillRect(0,0,rect.width,rect.height);
    };
    setup();
    hasDrawnRef.current = false; setHasDrawn(false);
    window.addEventListener("resize", setup);
    return () => window.removeEventListener("resize", setup);
  }, [step]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX-rect.left, y: e.clientY-rect.top };
  };
  const handleDown = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = getPos(e);
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch(_) {}
  };
  const handleMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.strokeStyle = "#16233A"; ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(lastPtRef.current.x,lastPtRef.current.y); ctx.lineTo(p.x,p.y); ctx.stroke();
    lastPtRef.current = p;
    if (!hasDrawnRef.current) { hasDrawnRef.current = true; setHasDrawn(true); }
  };
  const handleUp = () => { drawingRef.current = false; };

  const clearCanvas = () => {
    const rect = containerRef.current.getBoundingClientRect();
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0,0,rect.width,rect.height);
    hasDrawnRef.current = false; setHasDrawn(false);
  };
  const finishWith = (updated) => {
    const next = step+1;
    if (next < signers.length) { setCaptured(updated); setStep(next); }
    else onComplete(updated);
  };
  const skipSigner = () => finishWith(captured);
  const validateStep = () => finishWith({ ...captured, [signer.key]: canvasRef.current.toDataURL("image/png") });
  const stopHere = () => onComplete(captured);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(7,26,46,.92)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{width:"100%",maxWidth:480,background:"#0F2740",border:"1px solid #28506F",borderRadius:10,padding:18}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
          <div style={{color:"#A8B6C4",fontSize:11}}>{documentTitle}</div>
          <button onClick={stopHere} title="Arrêter ici" style={{background:"transparent",border:"none",color:"#7890A5",cursor:"pointer",padding:2}}><X size={16}/></button>
        </div>
        <div style={{color:"#D4A84B",fontSize:15,fontWeight:700,marginBottom:2}}>Signature {step+1}/{signers.length} — {signer.label}</div>
        <div style={{color:"#7890A5",fontSize:11,marginBottom:12}}>Signez avec le doigt ou le stylet dans le cadre ci-dessous.</div>
        <div ref={containerRef} style={{background:"#fff",borderRadius:6,overflow:"hidden",height:190,touchAction:"none"}}>
          <canvas
            ref={canvasRef}
            style={{width:"100%",height:"100%",display:"block",touchAction:"none"}}
            onPointerDown={handleDown} onPointerMove={handleMove} onPointerUp={handleUp} onPointerLeave={handleUp} onPointerCancel={handleUp}
          />
        </div>
        <div style={{display:"flex",gap:8,marginTop:14,flexWrap:"wrap"}}>
          <button className="btn btn-ghost btn-sm" onClick={clearCanvas}>Effacer</button>
          <button className="btn btn-ghost btn-sm" onClick={skipSigner}>Signer plus tard</button>
          <button className="btn btn-gold" style={{flex:1}} disabled={!hasDrawn} onClick={validateStep}>{step+1<signers.length?"Valider et signature suivante":"Valider et générer le document"}</button>
        </div>
      </div>
    </div>
  );
}


const CSS_FX = `
.fx-overlay{position:fixed;inset:0;z-index:300;background:rgba(15,31,23,.58);backdrop-filter:blur(3px);overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;display:flex;flex-direction:column;align-items:center;padding:max(10px,env(safe-area-inset-top)) 12px max(18px,env(safe-area-inset-bottom));font-family:var(--az-font,Inter,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif);box-sizing:border-box}
.fx-overlay *{box-sizing:border-box}
.fx-bar,.fx-paper{flex-shrink:0}
.fx-bar{position:sticky;top:0;z-index:5;width:100%;max-width:840px;display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;background:#fff;border:1px solid #E6EBE8;border-radius:16px;padding:10px 10px 10px 14px;box-shadow:0 8px 24px rgba(15,31,23,.22);margin-bottom:12px}
.fx-bar-l{display:flex;align-items:center;gap:10px;min-width:0}
.fx-bar-ic{width:38px;height:38px;border-radius:11px;background:#E8F5EA;color:#1E7A2D;display:grid;place-items:center;flex-shrink:0}
.fx-bar-t{font-size:15px;font-weight:700;color:#0F1F17;line-height:1.2}
.fx-bar-s{font-size:12px;color:#5B6B62;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:260px}
.fx-bar-r{display:flex;gap:8px;align-items:center}
.fx-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;border:none;border-radius:11px;padding:10px 16px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;min-height:42px}
.fx-btn-p{background:#2F9E3F;color:#fff;box-shadow:0 2px 6px rgba(47,158,63,.3)}.fx-btn-p:hover{background:#278a36}
.fx-btn-g{background:#fff;color:#2B3A32;border:1px solid #D5DDD9}.fx-btn-g:hover{border-color:#2F9E3F;color:#1E7A2D}
.fx-btn-wa{color:#1E7A2D;border-color:#BFE3C6;background:#F1FAF2}
.fx-paper{position:relative;width:100%;max-width:840px;background:#fff;border-radius:20px;box-shadow:0 24px 60px rgba(0,0,0,.35);overflow:hidden;color:#0F1F17}
.fx-in{position:relative;padding:clamp(18px,4.5vw,46px)}
.fx-wm{position:absolute;inset:0;display:grid;place-items:center;pointer-events:none;font-size:clamp(54px,15vw,120px);font-weight:800;letter-spacing:.08em;color:rgba(122,79,214,.06);transform:rotate(-24deg);z-index:0}
.fx-in>*{position:relative;z-index:1}
.fx-head{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding-bottom:22px;border-bottom:1px solid #E6EBE8}
.fx-brand{display:flex;gap:16px;align-items:center;min-width:0}
.fx-brand img{height:34px;width:auto;flex-shrink:0}
.fx-co{font-size:17px;font-weight:700;letter-spacing:-.01em;line-height:1.25}
.fx-sl{font-size:12px;color:#2E7D6B;margin-top:3px;line-height:1.4}
.fx-ct{font-size:11.5px;color:#5B6B62;margin-top:4px;line-height:1.5;overflow-wrap:anywhere}
.fx-doc{text-align:right;flex-shrink:0}
.fx-kind{font-size:11px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#2E7D6B}
.fx-num{font-size:28px;font-weight:800;letter-spacing:-.03em;line-height:1.1;margin-top:2px}
.fx-pill{display:inline-flex;align-items:center;gap:6px;padding:4px 11px;border-radius:999px;font-size:12px;font-weight:700;margin-top:8px}
.s-paid{background:#E8F5EA;color:#1E7A2D}.s-part{background:#E9F0FD;color:#2F6FDE}.s-open{background:#FFF4DD;color:#A8680A}.s-late{background:#FDECEA;color:#D6453D}.s-pf{background:#F0EBFB;color:#7A4FD6}
.fx-due{display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap;margin:22px 0 4px;padding:18px 22px;border-radius:16px}
.fx-due.s-paid{background:#E8F5EA}.fx-due.s-part{background:#E9F0FD}.fx-due.s-open{background:#FFF4DD}.fx-due.s-late{background:#FDECEA}.fx-due.s-pf{background:#F0EBFB}
.fx-due-l{font-size:13px;font-weight:600;color:#4B5A52}
.fx-due-a{font-size:clamp(26px,6vw,36px);font-weight:800;letter-spacing:-.03em;line-height:1.1;margin-top:3px;color:#0F1F17}
.fx-due-a small{font-size:.42em;font-weight:700;color:#5B6B62;margin-left:6px;letter-spacing:0}
.fx-due-r{font-size:13px;font-weight:600;text-align:right;line-height:1.5}
.fx-parties{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:20px}
.fx-p{background:#F7F9F8;border-radius:14px;padding:14px 16px;min-width:0}
.fx-pl{font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#8A978F;margin-bottom:6px}
.fx-pn{font-size:15px;font-weight:700;margin-bottom:3px;overflow-wrap:anywhere}
.fx-pt{font-size:12.5px;color:#4B5A52;line-height:1.7;overflow-wrap:anywhere}
.fx-meta{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:14px;margin:20px 0 6px}
.fx-ml{font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#8A978F;margin-bottom:3px}
.fx-mv{font-size:14px;font-weight:600}
.fx-t{width:100%;border-collapse:collapse;margin-top:18px}
.fx-t th{text-align:left;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#8A978F;padding:10px 8px;border-bottom:1.5px solid #0F1F17}
.fx-t th:not(:first-child),.fx-t td:not(:first-child){text-align:right;white-space:nowrap}
.fx-t td{padding:13px 8px;font-size:14px;border-bottom:1px solid #EDF1EF}
.fx-t td:first-child{font-weight:600}
.fx-t td.b{font-weight:700}
.fx-mob{display:none;margin-top:14px;border-top:1.5px solid #0F1F17}
.fx-li{padding:13px 0;border-bottom:1px solid #EDF1EF}
.fx-li-a{font-size:14px;font-weight:600}
.fx-li-b{display:flex;justify-content:space-between;gap:10px;margin-top:4px;font-size:13px;color:#5B6B62}
.fx-li-b b{color:#0F1F17;font-size:14px}
.fx-tot{margin:16px 0 0 auto;width:min(100%,360px)}
.fx-tr{display:flex;justify-content:space-between;gap:12px;padding:7px 0;font-size:13.5px;color:#4B5A52}
.fx-tr.red{color:#D6453D}
.fx-tr.g{border-top:2px solid #0F1F17;margin-top:6px;padding-top:12px;font-size:17px;font-weight:800;color:#0F1F17}
.fx-tr.g span:last-child{color:#2E7D6B}
.fx-box{background:#F7F9F8;border-radius:14px;padding:14px 16px;margin-top:18px}
.fx-bt{font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#5B6B62;margin-bottom:8px}
.fx-pr{display:flex;justify-content:space-between;gap:12px;font-size:13.5px;padding:6px 0;border-bottom:1px dashed #DDE4E0}
.fx-pr:last-child{border-bottom:none}
.fx-pr span:first-child{color:#4B5A52}.fx-pr b{color:#1E7A2D;white-space:nowrap}
.fx-rest{display:flex;justify-content:space-between;gap:12px;padding-top:10px;margin-top:4px;border-top:2px solid #0F1F17;font-size:15px;font-weight:800}
.fx-how{font-size:13px;color:#4B5A52;line-height:1.7}
.fx-how b{color:#0F1F17}
.fx-note{border-left:3px solid #2F9E3F;background:#F7F9F8;border-radius:0 12px 12px 0;padding:11px 14px;font-size:12.5px;color:#4B5A52;margin-top:14px;line-height:1.6;overflow-wrap:anywhere}
.fx-note.pf{border-color:#7A4FD6}
.fx-foot{margin-top:28px;padding-top:14px;border-top:1px solid #E6EBE8;font-size:11px;color:#8A978F;text-align:center;line-height:1.8;overflow-wrap:anywhere}
.fx-payrow{display:flex;flex-direction:column;gap:14px;align-items:stretch}
.fx-line{margin-bottom:5px}
.fx-qr{flex-shrink:0;text-align:center;align-self:center}
.fx-qr svg{display:block;width:176px;height:176px;border-radius:10px;border:1px solid #E3E9E6;margin:0 auto}
.fx-qrc{font-size:11px;color:#5B6B62;margin-top:6px;line-height:1.35}
/* Modèle CLASSIQUE */
.fx-m-classique{border-radius:6px}
.fx-m-classique .fx-head{border-bottom:3px solid #0F1F17}
.fx-m-classique .fx-kind,.fx-m-classique .fx-sl,.fx-m-classique .fx-tr.g span:last-child{color:#0F1F17}
.fx-m-classique .fx-p,.fx-m-classique .fx-box{background:#fff;border:1px solid #AEB8B3;border-radius:4px}
.fx-m-classique .fx-due{border-radius:4px;background:#F3F4F3!important;border-left:6px solid #5B6B62}
.fx-m-classique .fx-due.s-paid{border-left-color:#1E7A2D}.fx-m-classique .fx-due.s-part{border-left-color:#2F6FDE}.fx-m-classique .fx-due.s-open{border-left-color:#A8680A}.fx-m-classique .fx-due.s-late{border-left-color:#D6453D}.fx-m-classique .fx-due.s-pf{border-left-color:#7A4FD6}
.fx-m-classique .fx-t th{background:#0F1F17;color:#fff;border-bottom:none;padding:11px 10px}
.fx-m-classique .fx-t td{border-bottom:1px solid #D5DCD8}
.fx-m-classique .fx-mob{border-top:3px solid #0F1F17}
/* Modèle SOBRE */
.fx-m-sobre{border-radius:6px}
.fx-m-sobre .fx-head{border-bottom:1px solid #0F1F17}
.fx-m-sobre .fx-kind,.fx-m-sobre .fx-sl,.fx-m-sobre .fx-tr.g span:last-child{color:#0F1F17}
.fx-m-sobre .fx-num{font-weight:600}
.fx-m-sobre .fx-p,.fx-m-sobre .fx-box{background:transparent;border-radius:0;border-top:1px solid #C9D1CD;padding:12px 0 4px}
.fx-m-sobre .fx-due{background:transparent!important;border-radius:0;border-top:1px solid #0F1F17;border-bottom:1px solid #0F1F17;padding:16px 0}
.fx-m-sobre .fx-pill{background:transparent!important;border:1px solid currentColor}
.fx-m-sobre .fx-t th{color:#5B6B62;border-bottom:1px solid #0F1F17}
.fx-m-sobre .fx-tr.g{border-top:1px solid #0F1F17}
/* Modèle MODERNE (par défaut) */
.fx-m-moderne{border-top:7px solid #2F9E3F}
.fx-m-moderne .fx-num{color:#1E7A2D}
.fx-m-moderne .fx-t th{background:#EAF5EC;color:#1E7A2D;border-bottom:none}
.fx-m-moderne .fx-tr.g{background:#1E7A2D;color:#fff;border-top:none;border-radius:12px;padding:12px 16px}
.fx-m-moderne .fx-tr.g span:last-child{color:#fff}
.fx-m-moderne .fx-box,.fx-m-moderne .fx-p{background:#F2F8F3}
@media(max-width:620px){
  .fx-payrow{flex-direction:column;align-items:stretch}
  .fx-qr{align-self:center}
  .fx-head{flex-direction:column;gap:14px}
  .fx-brand{flex-direction:column;align-items:flex-start;gap:10px}
  .fx-brand img{height:30px}
  .fx-doc{text-align:left}
  .fx-parties{grid-template-columns:1fr}
  .fx-t{display:none}.fx-mob{display:block}
  .fx-due-r{text-align:left}
  .fx-bar{padding:10px}
  .fx-x{display:none}
  .fx-bar-r{width:100%}.fx-bar-r .fx-btn-p{flex:1}
  .fx-bar-s{max-width:60vw}
}
`;

const CSS_FA = `
.fa-cred{margin:22px 0 4px;padding:18px 20px;border-radius:16px;background:#E8F5EA;display:flex;flex-direction:column;gap:14px}
.fa-row{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
.fa-lbl{font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#1E7A2D;margin-bottom:3px}
.fa-val{font-size:clamp(20px,6vw,28px);font-weight:800;letter-spacing:.02em;line-height:1.2;color:#0F1F17;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;overflow-wrap:anywhere;user-select:all;-webkit-user-select:all}
.fa-cp{border:1px solid #BFE3C6;background:#fff;color:#1E7A2D;border-radius:10px;padding:7px 12px;font-family:inherit;font-size:12.5px;font-weight:700;cursor:pointer;min-height:34px}
.fa-hello{font-size:14px;line-height:1.6;color:#2B3A32;margin-top:20px}
.fa-warn{margin-top:12px;font-size:12.5px;line-height:1.6;color:#A8680A;background:#FFF4DD;border-radius:12px;padding:10px 14px}
.fa-st{display:flex;gap:11px;align-items:flex-start;margin-bottom:9px;font-size:13.5px;line-height:1.55;color:#2B3A32}
.fa-st:last-child{margin-bottom:0}
.fa-n{flex-shrink:0;width:22px;height:22px;border-radius:50%;background:#2F9E3F;color:#fff;font-size:12px;font-weight:800;display:grid;place-items:center;margin-top:1px}
.fa-url{font-weight:700;color:#1E7A2D;overflow-wrap:anywhere}
.fa-two{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:18px}
.fa-two .fx-box{margin-top:0}
.fa-sub{font-size:13px;font-weight:700;color:#0F1F17;margin-bottom:8px}
.fa-qrw{display:flex;gap:18px;align-items:center;flex-wrap:wrap}
.fa-qrw svg{width:150px;height:150px;border-radius:10px;border:1px solid #E3E9E6;flex-shrink:0}
.fa-qrt{font-size:13px;color:#4B5A52;line-height:1.6;min-width:0;flex:1 1 160px}
@media(max-width:640px){.fa-two{grid-template-columns:1fr}.fa-qrw{justify-content:center;text-align:center}}
`;

function FicheAcces({ employe, credentials, entreprise, onClose, onPdf }) {
  const [busy, setBusy] = useState(false);
  const [copie, setCopie] = useState("");
  const E = (k) => (entreprise && entreprise[k]) || "";
  const nom = [employe?.prenom, employe?.nom].filter(Boolean).join(" ").trim() || employe?.nom || "—";
  const prenom = nom.split(" ")[0] || nom;
  const qm = makeQrMatrix(ERP_LOGIN_URL);
  const copier = async (cle, txt) => {
    try { await navigator.clipboard.writeText(String(txt||"")); setCopie(cle); setTimeout(()=>setCopie(""),1800); } catch(_) {}
  };
  const telecharger = async () => {
    if (busy) return;
    setBusy(true);
    try { await onPdf(); } finally { setBusy(false); }
  };
  return (
    <div className="fx-overlay" role="dialog" aria-modal="true" aria-label="Fiche d'accès à l'ERP">
      <style>{CSS_FX+CSS_FA}</style>
      <div className="fx-bar">
        <div className="fx-bar-l">
          <span className="fx-bar-ic"><ShieldCheck size={19}/></span>
          <div style={{minWidth:0}}>
            <div className="fx-bar-t">Accès à l'ERP</div>
            <div className="fx-bar-s">{nom}</div>
          </div>
        </div>
        <div className="fx-bar-r">
          <button type="button" className="fx-btn fx-btn-p" disabled={busy} onClick={telecharger}><Download size={16}/> <span>{busy?"Préparation…":<>Télécharger<span className="fx-x"> / Partager</span> PDF</>}</span></button>
          <button type="button" className="fx-btn fx-btn-g" onClick={onClose} aria-label="Fermer la fiche"><X size={18}/></button>
        </div>
      </div>

      <div className="fx-paper fx-m-moderne">
        <div className="fx-in">
          <div className="fx-head">
            <div className="fx-brand">
              <img src={LOGO_UI} alt="Azongbio" />
              <div style={{minWidth:0}}>
                <div className="fx-co">{E("entreprise_nom")}</div>
                <div className="fx-sl">{enterpriseSlogan(entreprise||{})}</div>
                <div className="fx-ct">{[E("entreprise_tel"),E("entreprise_email"),E("entreprise_site")].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
            <div className="fx-doc">
              <div className="fx-kind">Fiche d'accès</div>
              <div className="fx-num" style={{fontSize:"clamp(20px,5vw,28px)"}}>ERP</div>
              <span className="fx-pill s-paid">Édité le {fmtDate(today())}</span>
            </div>
          </div>

          <div className="fa-hello">Bonjour <b>{prenom}</b>, voici vos accès au système de gestion interne AZONGBIO ERP, depuis votre ordinateur ou votre téléphone.</div>

          <div className="fa-cred">
            <div className="fa-row">
              <div style={{minWidth:0}}><div className="fa-lbl">Identifiant</div><div className="fa-val">{credentials?.username||"—"}</div></div>
              <button type="button" className="fa-cp" onClick={()=>copier("u",credentials?.username)}>{copie==="u"?"Copié ✓":"Copier"}</button>
            </div>
            <div className="fa-row">
              <div style={{minWidth:0}}><div className="fa-lbl">Mot de passe temporaire</div><div className="fa-val">{credentials?.password||"—"}</div></div>
              <button type="button" className="fa-cp" onClick={()=>copier("p",credentials?.password)}>{copie==="p"?"Copié ✓":"Copier"}</button>
            </div>
          </div>
          <div className="fa-warn">Ce mot de passe est temporaire : il vous sera demandé d'en choisir un nouveau, que vous seul(e) connaissez, dès votre première connexion.</div>

          <div className="fx-box" style={{marginTop:20}}>
            <div className="fx-bt">1. Se connecter à l'ERP</div>
            <div className="fa-st"><span className="fa-n">1</span><div>Ouvrez votre navigateur (Chrome, Safari…) et allez sur <span className="fa-url">{ERP_LOGIN_URL}</span></div></div>
            <div className="fa-st"><span className="fa-n">2</span><div>Entrez votre identifiant et votre mot de passe temporaire ci-dessus.</div></div>
            <div className="fa-st"><span className="fa-n">3</span><div>Choisissez votre nouveau mot de passe quand l'ERP vous le demande.</div></div>
          </div>

          <div className="fa-two">
            <div className="fx-box">
              <div className="fx-bt">2. Écran d'accueil — iPhone</div>
              <div className="fa-sub">Avec Safari</div>
              <div className="fa-st"><span className="fa-n">1</span><div>Ouvrez l'adresse de l'ERP dans Safari.</div></div>
              <div className="fa-st"><span className="fa-n">2</span><div>Appuyez sur l'icône Partager (le carré avec une flèche vers le haut).</div></div>
              <div className="fa-st"><span className="fa-n">3</span><div>Choisissez « Sur l'écran d'accueil ».</div></div>
              <div className="fa-st"><span className="fa-n">4</span><div>Appuyez sur « Ajouter ».</div></div>
            </div>
            <div className="fx-box">
              <div className="fx-bt">2. Écran d'accueil — Android</div>
              <div className="fa-sub">Avec Chrome</div>
              <div className="fa-st"><span className="fa-n">1</span><div>Ouvrez l'adresse de l'ERP dans Chrome.</div></div>
              <div className="fa-st"><span className="fa-n">2</span><div>Appuyez sur le menu ⋮ en haut à droite.</div></div>
              <div className="fa-st"><span className="fa-n">3</span><div>Choisissez « Ajouter à l'écran d'accueil » (ou « Installer l'application »).</div></div>
              <div className="fa-st"><span className="fa-n">4</span><div>Confirmez.</div></div>
            </div>
          </div>
          <div className="fx-note">Une icône AZONGBIO apparaît alors sur l'écran d'accueil, comme une application normale.</div>

          <div className="fx-box">
            <div className="fx-bt">3. Ou scannez ce code pour ouvrir l'ERP directement</div>
            <div className="fa-qrw">
              {qm&&<svg viewBox={"0 0 "+(qm.n+4)+" "+(qm.n+4)} shapeRendering="crispEdges" role="img" aria-label="QR code de l'ERP"><rect width={qm.n+4} height={qm.n+4} fill="#fff"/><path d={qrSvgPath(qm)} fill="#0F1F17"/></svg>}
              <div className="fa-qrt">Scannez avec l'appareil photo du téléphone.<br/><span className="fa-url">{ERP_LOGIN_URL}</span></div>
            </div>
          </div>

          <div className="fx-foot">
            {E("entreprise_nom")} · NIU : {E("entreprise_niu")} · RC : {E("entreprise_rc")}<br/>
            {[E("entreprise_adresse"),E("entreprise_ville")].filter(Boolean).join(" · ")}{E("entreprise_site")?" · "+E("entreprise_site"):""}
          </div>
        </div>
      </div>
    </div>
  );
}


const CSS_PV = `
.pv-host{display:flex;flex-direction:column;gap:14px;margin-top:18px}
.pv-page{width:100%;height:auto;display:block;border:1px solid #E3E9E6;border-radius:6px;background:#fff;box-shadow:0 1px 5px rgba(15,31,23,.08)}
.pv-load{margin-top:18px;text-align:center;font-size:13px;color:#5B6B62;padding:18px 0}
.pv-if{width:100%;height:78vh;border:1px solid #E3E9E6;border-radius:8px;background:#fff;margin-top:18px}
`;
const loadPdfJs = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("pas de navigateur"));
  if (window.pdfjsLib) { try { window.pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js"; } catch(_) {} return Promise.resolve(window.pdfjsLib); }
  if (window.__azPdfJs) return window.__azPdfJs;
  window.__azPdfJs = new Promise((res, rej) => {
    const sc = document.createElement("script");
    sc.src = "/pdfjs/pdf.min.js";
    sc.onload = () => { try { window.pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js"; res(window.pdfjsLib); } catch(e) { window.__azPdfJs = null; rej(e); } };
    sc.onerror = () => { window.__azPdfJs = null; rej(new Error("lecteur de PDF indisponible")); };
    document.head.appendChild(sc);
  });
  return window.__azPdfJs;
};

function PdfSheet({ preview, onClose }) {
  const hostRef = useRef(null);
  const [nb, setNb] = useState(0);
  const [fini, setFini] = useState(false);
  const [echec, setEchec] = useState(false);
  useEffect(() => {
    let annule = false, doc = null;
    (async () => {
      try {
        const lib = await loadPdfJs();
        const buf = await (await fetch(preview.url)).arrayBuffer();
        if (annule) return;
        doc = await lib.getDocument({ data: new Uint8Array(buf) }).promise;
        if (annule) return;
        setNb(doc.numPages);
        const host = hostRef.current; if (!host) return;
        const larg = Math.max(280, host.clientWidth || 360);
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          if (annule) return;
          const base = page.getViewport({ scale: 1 });
          const vp = page.getViewport({ scale: Math.min(larg * ratio, 1500) / base.width });
          const cv = document.createElement("canvas");
          cv.width = Math.floor(vp.width); cv.height = Math.floor(vp.height);
          cv.className = "pv-page"; cv.setAttribute("role", "img"); cv.setAttribute("aria-label", "Page " + i);
          host.appendChild(cv);
          await page.render({ canvasContext: cv.getContext("2d"), viewport: vp }).promise;
        }
        if (!annule) setFini(true);
      } catch (e) {
        if (!annule) { console.warn("Affichage du document :", e); setEchec(true); }
      }
    })();
    return () => { annule = true; try { doc && doc.destroy(); } catch(_) {} };
  }, [preview.url]);
  const titre = (!preview.title || preview.title === "Document Azongbio") && preview.filename
    ? String(preview.filename).replace(/\.pdf$/i, "").replace(/_/g, " ") : (preview.title || "Document");
  const enregistrer = () => {
    const a = document.createElement("a");
    a.href = preview.url; a.download = preview.filename || "document.pdf"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const partager = async () => {
    if (navigator.share && navigator.canShare) { const ok = await sharePdfPreview(preview); if (!ok) enregistrer(); }
    else enregistrer();
  };
  const fermer = () => {
    try { URL.revokeObjectURL(preview.url); if (window.__azPdfFiles) delete window.__azPdfFiles[preview.url]; } catch(_) {}
    onClose();
  };
  return (
    <div className="fx-overlay" style={{zIndex:400}} role="dialog" aria-modal="true" aria-label={"Aperçu : "+titre}>
      <style>{CSS_FX+CSS_PV}</style>
      <div className="fx-bar">
        <div className="fx-bar-l">
          <span className="fx-bar-ic"><FileText size={19}/></span>
          <div style={{minWidth:0}}>
            <div className="fx-bar-t">{titre}</div>
            <div className="fx-bar-s">{preview.filename||"document.pdf"}</div>
          </div>
        </div>
        <div className="fx-bar-r">
          <button type="button" className="fx-btn fx-btn-p" onClick={partager}><Download size={16}/> <span>Télécharger<span className="fx-x"> / Partager</span> PDF</span></button>
          <button type="button" className="fx-btn fx-btn-g" onClick={fermer} aria-label="Fermer l'aperçu"><X size={18}/></button>
        </div>
      </div>
      <div className="fx-paper fx-m-moderne">
        <div className="fx-in">
          <div className="fx-head" style={{paddingBottom:16}}>
            <div style={{minWidth:0}}>
              <div className="fx-kind">Document</div>
              <div className="fx-num" style={{fontSize:"clamp(18px,4.6vw,24px)",overflowWrap:"anywhere"}}>{titre}</div>
            </div>
            <div className="fx-doc"><span className="fx-pill s-paid" style={{marginTop:0}}>{nb?nb+" page"+(nb>1?"s":""):(echec?"PDF":"Chargement…")}</span></div>
          </div>
          {echec
            ? <iframe title={titre} src={preview.url} className="pv-if" />
            : <>
                <div ref={hostRef} className="pv-host" />
                {!fini&&<div className="pv-load">Préparation du document…</div>}
              </>}
        </div>
      </div>
    </div>
  );
}

function FacturePrint({ facture, client, entreprise, onClose, isProforma=false, avaries=[], paiements=[], onToggleLien }) {
  const ref = useRef();
  const [pdfPreview, setPdfPreview] = useState(null);
  const { ht, remise, hn, tva, ttc, tvaLabel } = calculateInvoiceTotals(facture, entreprise);
  const E = (k) => entreprise[k] || "";

  const css = `
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Georgia,serif;color:#1a1a1a;background:#fff}
    .hband{background:#fff;border-top:5px solid #071A2E;border-bottom:2px solid #D4A84B;color:#1f2733;padding:18px 48px 16px;display:flex;justify-content:space-between;align-items:center;margin-bottom:28px}
    .brandwrap{display:flex;align-items:center;gap:14px;min-width:0}
    .brandname{font-size:19px;font-weight:700;letter-spacing:1px;color:#202733}
    .brandslogan{font-size:9px;letter-spacing:1.7px;text-transform:uppercase;color:#b28b48;margin-top:4px}
    .brandcontact{font-family:Arial,sans-serif;font-size:9px;color:#78808c;margin-top:5px}
    .docbox{min-width:210px;background:#071A2E;color:#fff;border-radius:7px;padding:12px 15px;text-align:right}
    .body{padding:0 48px 38px}
    .parties{display:flex;justify-content:space-between;margin-bottom:24px}
    .plbl{font-size:9px;text-transform:uppercase;letter-spacing:2px;color:#888;margin-bottom:5px}
    .pname{font-size:15px;font-weight:700;margin-bottom:3px}
    .pline{font-size:12px;color:#555;line-height:1.8}
    .gold{height:2px;background:linear-gradient(90deg,#D4A84B,transparent);margin:18px 0 22px}
    .dates{display:flex;gap:36px;margin-bottom:22px}
    .dlbl{font-size:9px;text-transform:uppercase;letter-spacing:2px;color:#888;margin-bottom:2px}
    .dval{font-size:13px;font-weight:600}
    table{width:100%;border-collapse:collapse;margin-bottom:22px}
    thead tr{border-bottom:2px solid #071A2E}
    th{font-size:9px;text-transform:uppercase;letter-spacing:2px;color:#888;padding:7px 8px;font-weight:normal;text-align:left}
    th:not(:first-child),td:not(:first-child){text-align:right}
    td{padding:9px 8px;font-size:13px;border-bottom:1px solid #f0ece4}
    .tots{margin-left:auto;width:290px}
    .tr{display:flex;justify-content:space-between;padding:6px 0;font-size:13px;color:#555;border-bottom:1px solid #f0ece4}
    .trg{border-top:2px solid #071A2E;border-bottom:none;padding-top:10px;margin-top:4px}
    .trg span:first-child{font-size:14px;font-weight:700}
    .trg span:last-child{font-size:17px;font-weight:700;color:#D4A84B}
    .regl{display:flex;justify-content:space-between;align-items:center;padding:10px 14px;margin-top:14px;background:#f8f4ec;border-radius:4px;border:1px solid #F3D98E;font-size:13px}
    .note{background:#faf8f4;border-left:3px solid #D4A84B;padding:9px 13px;font-size:11px;color:#555;margin-top:12px}
    .foot{margin-top:36px;border-top:1px solid #e8e0d0;padding-top:12px;font-size:10px;color:#aaa;text-align:center;line-height:1.9}
    .sbadge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:9px;letter-spacing:1px;text-transform:uppercase;margin-top:6px}
    .sp{background:#e8f5ec;color:#2d5a3d;border:1px solid #2d5a3d}
    .sa{background:#fef9ec;color:#7a5a00;border:1px solid #D4A84B}
    .spf{background:#f0eaff;color:#5a3d8c;border:1px solid #a880ee}
    @media print{
      body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .no-print{display:none!important}
    }
  `;

  // Génère le HTML statique de la facture (pour l'overlay impression).
  // Toutes les données venant de la base sont échappées avant document.write.
  const buildHTML = () => {
    const H = escapeHtml;
    const rows = (facture.lignes||[]).map(l=>
      `<tr>
        <td>${H(libelleLigne(l.designation))}</td>
        <td>${H(NUM(l.qte))} btl</td>
        <td>${H(NUM(l.prixUnit))}</td>
        <td style="font-weight:600">${H(NUM(l.montant))}</td>
      </tr>`
    ).join("");
    const reglHTML = facture.reglement
      ? `<div class="regl"><span>Mode de règlement</span><strong>${H(facture.reglement)}</strong></div>` : "";
    const noteHTML = facture.note
      ? `<div class="note"><strong>Note :</strong> ${H(facture.note)}</div>` : "";
    const avList = (!isProforma && Array.isArray(avaries)) ? avaries : [];
    const avariesHTML = avList.length ? `
      <div style="margin-top:16px">
        <div style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#D4A84B;margin-bottom:6px">Avaries signalées sur cette facture</div>
        <table>
          <thead><tr><th>Date</th><th>Produit</th><th>Qté</th><th>Raison</th><th>Lieu / adresse / ville</th><th>Remplacement</th></tr></thead>
          <tbody>${avList.map(a=>`<tr>
            <td>${H(new Date(a.date).toLocaleDateString("fr-FR"))}</td>
            <td>${H(a.produit)}</td>
            <td style="font-weight:600">${H(NUM(a.quantite))}</td>
            <td>${H(a.raison||"—")}</td>
            <td>${H([a.lieu,a.adresse,a.lieu_ville||a.ville].filter(Boolean).join(" — ")||"—")}</td>
            <td>${H((a.remplacement||"—")+(a.remplacement==="Remplacée"&&a.remplace_le?" le "+new Date(a.remplace_le).toLocaleDateString("fr-FR"):""))}</td>
          </tr>`).join("")}</tbody>
        </table>
      </div>` : "";
    const proformaNote = isProforma
      ? `<div class="note" style="border-color:#a880ee;margin-top:8px"><strong>Document non contractuel.</strong> Valable 30 jours à compter de la date d'émission.</div>` : "";
    const clientLines = [
      client?.code,
      client?.niu ? "NIU : "+client.niu : "",
      client?.adresse,
      client?.ville,
      client?.contact,
      client?.email
    ].filter(Boolean).map(H).join("<br/>");

    return `
      <div class="hband">
        <div class="brandwrap">
          <img src="${H(LOGO_URI)}" alt="Azongbio" style="height:48px;display:block" />
          <div>
            <div class="brandname">${H(E("entreprise_nom"))}</div>
            <div class="brandslogan">${H(enterpriseSlogan(entreprise))}</div>
            <div class="brandcontact">${H([E("entreprise_tel"),E("entreprise_email"),E("entreprise_site")].filter(Boolean).join(" · "))}</div>
          </div>
        </div>
        <div class="docbox">
          <div style="font-size:10px;letter-spacing:4px;text-transform:uppercase;color:#D4A84B">${isProforma?"Proforma / Devis":"Facture"}</div>
          <div style="font-size:20px;color:#fff;margin-top:4px">${H(facture.numero)}</div>
          <span class="sbadge ${isProforma?"spf":facture.statut==="Payée"?"sp":"sa"}">${H(isProforma?"Proforma":facture.statut)}</span>
        </div>
      </div>
      <div class="body">
        <div class="parties">
          <div>
            <div class="plbl">Émetteur</div>
            <div class="pname">${H(E("entreprise_nom"))}</div>
            <div class="pline">
              ${H(E("entreprise_adresse"))}<br/>
              ${H(E("entreprise_ville"))}<br/>
              Tél : ${H(E("entreprise_tel"))}<br/>
              Email : ${H(E("entreprise_email"))}<br/>
              NIU : ${H(E("entreprise_niu"))}<br/>
              RC : ${H(E("entreprise_rc"))}
            </div>
          </div>
          <div style="text-align:right">
            <div class="plbl">Facturé à</div>
            <div class="pname">${H(client?.nom||facture.client_nom)}</div>
            <div class="pline">${clientLines}</div>
          </div>
        </div>
        <div class="dates">
          <div><div class="dlbl">Date d'émission</div><div class="dval">${H(fmtDate(facture.date_emission))}</div></div>
          <div><div class="dlbl">${isProforma?"Date de validité":"Date d'échéance"}</div><div class="dval">${H(fmtDate(facture.date_echeance))}</div></div>
        </div>
        <div class="gold"></div>
        <table>
          <thead><tr><th>Désignation</th><th>Qté</th><th>Prix unit. FCFA</th><th>Total HT FCFA</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="tots">
          <div class="tr"><span>Sous-total HT</span><span>${H(NUM(ht))} FCFA</span></div>
          ${remise>0?`<div class="tr" style="color:#c0392b"><span>Remise ${H(facture.remise_pct)}%</span><span>− ${H(NUM(remise))} FCFA</span></div>`:""}
          <div class="tr"><span>Base imposable</span><span>${H(NUM(hn))} FCFA</span></div>
          ${facture.appliquer_tva!==false?`<div class="tr"><span>TVA ${H(tvaLabel)} %</span><span>${H(NUM(tva))} FCFA</span></div>`:""}
          <div class="tr trg"><span>Total TTC</span><span>${H(NUM(ttc))} FCFA</span></div>
        </div>
        ${reglHTML}${noteHTML}${proformaNote}${avariesHTML}
        <div class="foot">
          ${H(E("entreprise_nom"))} · NIU : ${H(E("entreprise_niu"))} · RC : ${H(E("entreprise_rc"))}<br/>
          ${H(E("entreprise_adresse"))} · ${H(E("entreprise_ville"))}<br/>
          Règlement : ${H(facture.reglement||"Virement · Mobile Money MTN/Orange · Espèces")} · ${H(E("entreprise_site"))}
        </div>
      </div>
    `;
  };

  useEffect(()=>{
    const previousOverflow=document.body.style.overflow;
    document.body.style.overflow="hidden";
    const closeOnEscape=(event)=>{ if(event.key==="Escape") onClose(); };
    window.addEventListener("keydown",closeOnEscape);
    return ()=>{
      document.body.style.overflow=previousOverflow;
      window.removeEventListener("keydown",closeOnEscape);
    };
  },[onClose]);

  // PDF mobile sans popup : partage natif ou téléchargement direct.
  const handlePrint = async () => {
    try {
      const pdf = await generateInvoicePdfBase64(
        facture,
        client,
        entreprise,
        isProforma,
        avaries,
        paiements
      );
      const result = await deliverPdfOnDevice({
        ...pdf,
        title:(isProforma ? "Proforma " : "Facture ") + (facture.numero || ""),
        direct:true,
        onOpenInApp: (url, filename, title) => setPdfPreview({ url, filename, title })
      });
      if (result.mode !== "cancelled" && result.mode !== "in-app") onClose();
    } catch (error) {
      console.error("Génération PDF :", error);
      alert("Impossible de générer le PDF : " + (error?.message || "erreur inconnue"));
    }
  };

  const totalPaye = (paiements||[]).reduce((s,p)=>s+(Number(p.montant)||0),0);
  const reste = isProforma ? ttc : (String(facture.statut||"")==="Payée" ? 0 : Math.max(0, ttc-totalPaye));
  const echeanceDate = endOfLocalDay(facture.date_echeance);
  const enRetard = !isProforma && reste>0 && echeanceDate && echeanceDate < new Date();
  const joursRetard = enRetard ? Math.floor((Date.now()-echeanceDate.getTime())/86400000) : 0;
  const kind = isProforma ? "pf" : reste<=0 ? "paid" : enRetard ? "late" : totalPaye>0 ? "part" : "open";
  const kindLabel = { pf:"Proforma", paid:"Payée", late:"En retard", part:"Partiellement payée", open:"En attente" }[kind];
  const dueLabel = isProforma ? "Montant du devis" : kind==="paid" ? "Facture soldée" : "Montant à payer";
  const dueAmount = isProforma ? ttc : kind==="paid" ? ttc : reste;
  const derniereDatePaiement = (paiements||[]).map(p=>p.date_versement||p.date_validation||"").filter(Boolean).sort().slice(-1)[0];
  const dueRight = isProforma
    ? ("Valable jusqu'au "+fmtDate(facture.date_echeance))
    : kind==="paid"
      ? (derniereDatePaiement ? "Réglée le "+fmtDate(derniereDatePaiement) : "Merci pour votre règlement")
      : kind==="late"
        ? ("Échéance dépassée de "+joursRetard+" jour"+(joursRetard>1?"s":"")+" ("+fmtDate(facture.date_echeance)+")")
        : ("Échéance le "+fmtDate(facture.date_echeance));
  const methodes = "Virement · Mobile Money MTN/Orange · Espèces";
  return (
    <div className="fx-overlay" role="dialog" aria-modal="true" aria-label={isProforma?"Aperçu de la proforma":"Aperçu de la facture"}>
      <style>{CSS_FX}</style>
      <div className="fx-bar">
        <div className="fx-bar-l">
          <span className="fx-bar-ic"><FileText size={19}/></span>
          <div style={{minWidth:0}}>
            <div className="fx-bar-t">{isProforma?"Proforma":"Facture"} {facture.numero}</div>
            <div className="fx-bar-s">{client?.nom||facture.client_nom}</div>
          </div>
        </div>
        <div className="fx-bar-r">
          <button type="button" className="fx-btn fx-btn-p" onClick={handlePrint}><Download size={16}/> <span>Télécharger<span className="fx-x"> / Partager</span> PDF</span></button>
          {!isProforma&&reste>0&&waPhone(client?.contact||client?.contact_tel)&&<button type="button" className="fx-btn fx-btn-g fx-btn-wa" title="Relancer le client par WhatsApp" aria-label="Relancer le client par WhatsApp" onClick={()=>openRelanceWhatsApp(facture,client,entreprise,reste,ttc)}><MessageCircle size={18}/></button>}
          {!isProforma&&onToggleLien&&facture.lien_token&&<button type="button" className="fx-btn fx-btn-g" aria-label={facture.lien_actif===false?"Réactiver le lien de paiement":"Désactiver le lien de paiement"} title={facture.lien_actif===false?"Lien de paiement désactivé — toucher pour réactiver":"Lien de paiement actif — toucher pour désactiver"} onClick={()=>onToggleLien(facture)}>{facture.lien_actif===false?<EyeOff size={16}/>:<Eye size={16}/>}<span className="fx-x"> {facture.lien_actif===false?"Lien désactivé":"Lien actif"}</span></button>}
          <button type="button" className="fx-btn fx-btn-g" onClick={onClose} aria-label="Fermer l'aperçu"><X size={18}/></button>
        </div>
      </div>

      <div ref={ref} className={"fx-paper fx-m-"+(["classique","sobre"].includes(String(E("facture_modele")))?E("facture_modele"):"moderne")}>
        {isProforma&&<div className="fx-wm" aria-hidden="true">PROFORMA</div>}
        <div className="fx-in">
          <div className="fx-head">
            <div className="fx-brand">
              <img src={LOGO_UI} alt="Azongbio" />
              <div style={{minWidth:0}}>
                <div className="fx-co">{E("entreprise_nom")}</div>
                <div className="fx-sl">{enterpriseSlogan(entreprise)}</div>
                <div className="fx-ct">{[E("entreprise_tel"),E("entreprise_email"),E("entreprise_site")].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
            <div className="fx-doc">
              <div className="fx-kind">{isProforma?"Proforma / Devis":"Facture"}</div>
              <div className="fx-num">{facture.numero}</div>
              <span className={"fx-pill s-"+kind}>{kind==="paid"?"✓ ":""}{kindLabel}</span>
            </div>
          </div>

          <div className={"fx-due s-"+kind}>
            <div>
              <div className="fx-due-l">{dueLabel}</div>
              <div className="fx-due-a">{NUM(dueAmount)}<small>FCFA</small></div>
            </div>
            <div className="fx-due-r">
              {dueRight}
              {!isProforma&&kind!=="paid"&&totalPaye>0&&<div style={{fontWeight:500,color:"#4B5A52"}}>Déjà réglé : {NUM(totalPaye)} FCFA sur {NUM(ttc)} FCFA</div>}
            </div>
          </div>

          <div className="fx-parties">
            <div className="fx-p">
              <div className="fx-pl">Émetteur</div>
              <div className="fx-pn">{E("entreprise_nom")}</div>
              <div className="fx-pt">
                {E("entreprise_adresse")}{E("entreprise_ville")&&<><br/>{E("entreprise_ville")}</>}
                {E("entreprise_tel")&&<><br/>Tél : {E("entreprise_tel")}</>}
                {E("entreprise_email")&&<><br/>{E("entreprise_email")}</>}
                {E("entreprise_niu")&&<><br/>NIU : {E("entreprise_niu")}</>}
                {E("entreprise_rc")&&<><br/>RC : {E("entreprise_rc")}</>}
              </div>
            </div>
            <div className="fx-p">
              <div className="fx-pl">Facturé à</div>
              <div className="fx-pn">{client?.nom||facture.client_nom}</div>
              <div className="fx-pt">
                {client?.code&&<>{client.code}<br/></>}
                {client?.niu&&<>NIU : {client.niu}<br/></>}
                {client?.adresse&&<>{client.adresse}<br/></>}
                {client?.ville&&<>{client.ville}<br/></>}
                {client?.contact&&<>{client.contact}<br/></>}
                {client?.email&&<>{client.email}</>}
              </div>
            </div>
          </div>

          <div className="fx-meta">
            <div><div className="fx-ml">Date d'émission</div><div className="fx-mv">{fmtDate(facture.date_emission)}</div></div>
            <div><div className="fx-ml">{isProforma?"Date de validité":"Date d'échéance"}</div><div className="fx-mv">{fmtDate(facture.date_echeance)}</div></div>
            <div><div className="fx-ml">Mode de règlement</div><div className="fx-mv">{facture.reglement||"À convenir"}</div></div>
          </div>

          <table className="fx-t">
            <thead><tr><th>Désignation</th><th>Qté</th><th>Prix unit.</th><th>Total HT</th></tr></thead>
            <tbody>
              {facture.lignes.map((l,i)=>(
                <tr key={i}><td>{libelleLigne(l.designation)}</td><td>{NUM(l.qte)} btl</td><td>{NUM(l.prixUnit)}</td><td className="b">{NUM(l.montant)}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="fx-mob">
            {facture.lignes.map((l,i)=>(
              <div className="fx-li" key={i}>
                <div className="fx-li-a">{libelleLigne(l.designation)}</div>
                <div className="fx-li-b"><span>{NUM(l.qte)} btl × {NUM(l.prixUnit)} FCFA</span><b>{NUM(l.montant)} FCFA</b></div>
              </div>
            ))}
          </div>

          <div className="fx-tot">
            <div className="fx-tr"><span>Sous-total HT</span><span>{NUM(ht)} FCFA</span></div>
            {remise>0&&<div className="fx-tr red"><span>Remise {facture.remise_pct}%</span><span>− {NUM(remise)} FCFA</span></div>}
            <div className="fx-tr"><span>Base imposable</span><span>{NUM(hn)} FCFA</span></div>
            {facture.appliquer_tva!==false&&<div className="fx-tr"><span>TVA {tvaLabel} %</span><span>{NUM(tva)} FCFA</span></div>}
            <div className="fx-tr g"><span>Total TTC</span><span>{NUM(ttc)} FCFA</span></div>
          </div>

          {!isProforma&&(paiements||[]).length>0&&(
            <div className="fx-box">
              <div className="fx-bt">Règlements reçus</div>
              {(paiements||[]).slice().sort((a,b)=>String(a.date_versement||"").localeCompare(String(b.date_versement||""))).map((p,i)=>(
                <div className="fx-pr" key={p.id||i}>
                  <span>{fmtDate(p.date_versement||p.date_validation)}{p.moyen?" · "+p.moyen:""}{p.banque?" · "+p.banque:""}</span>
                  <b>{NUM(p.montant)} FCFA</b>
                </div>
              ))}
              <div className="fx-rest"><span>{reste>0?"Reste à payer":"Solde"}</span><span style={{color:reste>0?"#D6453D":"#1E7A2D"}}>{reste>0?NUM(reste)+" FCFA":"0 FCFA ✓"}</span></div>
            </div>
          )}

          {!isProforma&&kind!=="paid"&&(()=>{
            const PI = payInfo(entreprise);
            const qm = (PI.any||payerUrl(facture)) ? makeQrMatrix(payQrText(facture, entreprise, dueAmount)) : null;
            return (
            <div className="fx-box">
              <div className="fx-bt">Comment payer</div>
              <div className="fx-payrow">
                <div className="fx-how">
                  {PI.momo&&<div className="fx-line"><b>MTN Mobile Money</b> : {PI.momo}{PI.momoNom?" ("+PI.momoNom+")":""}</div>}
                  {(PI.banque||PI.compte)&&<div className="fx-line"><b>Virement</b> : {[PI.banque,PI.compte].filter(Boolean).join(" · ")}{PI.titulaire?" — "+PI.titulaire:""}</div>}
                  {!PI.any&&<div className="fx-line">Modes acceptés : <b>{facture.reglement||methodes}</b>.</div>}
                  <div className="fx-line">Merci d'indiquer la référence <b>{facture.numero}</b> lors du règlement.</div>
                </div>
                {qm&&(
                  <div className="fx-qr">
                    <svg viewBox={"0 0 "+(qm.n+4)+" "+(qm.n+4)} shapeRendering="crispEdges" role="img" aria-label="QR code de paiement"><rect width={qm.n+4} height={qm.n+4} fill="#fff"/><path d={qrSvgPath(qm)} fill="#0F1F17"/></svg>
                    <div className="fx-qrc">{payerUrl(facture)?<>Scannez pour payer<br/>ou envoyer votre preuve</>:<>Scannez pour lire<br/>les détails du paiement</>}</div>
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {facture.note&&<div className="fx-note"><strong>Note :</strong> {facture.note}</div>}
          {isProforma&&<div className="fx-note pf"><strong>Document non contractuel.</strong> Valable 30 jours à compter de la date d'émission.</div>}

          <div className="fx-foot">
            {E("entreprise_nom")} · NIU : {E("entreprise_niu")} · RC : {E("entreprise_rc")}<br/>
            {[E("entreprise_adresse"),E("entreprise_ville")].filter(Boolean).join(" · ")}{E("entreprise_site")?" · "+E("entreprise_site"):""}
          </div>
        </div>
      </div>
      {pdfPreview&&<PdfSheet preview={pdfPreview} onClose={()=>setPdfPreview(null)} />}
    </div>
  );
}

/* ══════════════════════════════════════
   APPLICATION PRINCIPALE
══════════════════════════════════════ */

const CSS_PP = `
.pp-root{position:fixed;inset:0;overflow-y:auto;-webkit-overflow-scrolling:touch;background:#F5F7F6;color:#0F1F17;font-family:var(--az-font,Inter,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif);padding:max(14px,env(safe-area-inset-top)) 14px max(26px,env(safe-area-inset-bottom));box-sizing:border-box}
.pp-root *{box-sizing:border-box}
.pp-wrap{max-width:520px;margin:0 auto}
.pp-top{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:6px 2px 14px}
.pp-top img{height:30px}
.pp-top span{font-size:12px;font-weight:600;color:#5B6B62;text-align:right}
.pp-card{background:#fff;border:1px solid #E3E9E6;border-radius:18px;padding:18px;margin-bottom:12px;box-shadow:0 1px 0 rgba(15,31,23,.02)}
.pp-eyebrow{font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#1E7A2D}
.pp-h{font-size:21px;font-weight:800;line-height:1.25;margin:4px 0 2px}
.pp-sub{font-size:13.5px;color:#5B6B62;line-height:1.5}
.pp-amt{margin-top:14px;border-radius:14px;background:#E8F5EA;padding:14px 16px}
.pp-amt small{display:block;font-size:11.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#1E7A2D}
.pp-amt b{display:block;font-size:30px;line-height:1.15;font-weight:800;margin-top:2px;color:#0F1F17}
.pp-amt.paid{background:#E8F5EA}
.pp-info{margin-top:10px;font-size:13px;line-height:1.5;color:#7A4B00;background:#FFF4DD;border-radius:12px;padding:10px 12px}
.pp-t{font-size:15px;font-weight:800;margin:0 0 10px}
.pp-row{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 0;border-top:1px solid #EEF2F0}
.pp-row:first-of-type{border-top:0;padding-top:2px}
.pp-l{min-width:0}
.pp-l small{display:block;font-size:11.5px;font-weight:600;color:#5B6B62;margin-bottom:1px}
.pp-l b{display:block;font-size:16px;font-weight:700;overflow-wrap:anywhere}
.pp-cp{flex-shrink:0;border:1px solid #BFE3C6;background:#fff;color:#1E7A2D;border-radius:10px;padding:8px 12px;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer;min-height:38px}
.pp-cp.ok{background:#2F9E3F;color:#fff;border-color:#2F9E3F}
.pp-note{font-size:12.5px;line-height:1.55;color:#5B6B62;margin-top:10px}
.pp-btn{width:100%;border:0;border-radius:14px;background:#2F9E3F;color:#fff;font-family:inherit;font-size:16px;font-weight:800;padding:14px 16px;min-height:50px;cursor:pointer}
.pp-btn:disabled{opacity:.55;cursor:default}
.pp-btn.ghost{background:#fff;color:#1E7A2D;border:1.5px solid #BFE3C6}
.pp-f{display:flex;flex-direction:column;gap:5px;margin-bottom:12px}
.pp-f label{font-size:12.5px;font-weight:700;color:#2B3A32}
.pp-f input,.pp-f select{font-family:inherit;font-size:16px;border:1.5px solid #D5DED9;border-radius:12px;padding:11px 12px;background:#fff;color:#0F1F17;min-height:46px;width:100%}
.pp-f input:focus,.pp-f select:focus{outline:none;border-color:#2F9E3F;box-shadow:0 0 0 3px rgba(47,158,63,.15)}
.pp-file{display:flex;align-items:center;gap:10px;border:1.5px dashed #9FD3A8;border-radius:12px;padding:12px;background:#F4FBF5;cursor:pointer}
.pp-file input{position:absolute;opacity:0;width:0;height:0}
.pp-file em{font-style:normal;font-size:14px;font-weight:700;color:#1E7A2D}
.pp-file span{font-size:12.5px;color:#5B6B62;overflow-wrap:anywhere}
.pp-prev{width:100%;max-height:200px;object-fit:contain;border-radius:10px;margin-top:8px;background:#F1F5F3}
.pp-err{margin:0 0 12px;font-size:13.5px;line-height:1.5;color:#A3231C;background:#FDECEA;border-radius:12px;padding:10px 12px}
.pp-ok{text-align:center;padding:26px 14px}
.pp-ok i{display:grid;place-items:center;width:58px;height:58px;border-radius:50%;background:#2F9E3F;color:#fff;font-style:normal;font-size:30px;margin:0 auto 12px}
.pp-foot{font-size:12px;color:#5B6B62;text-align:center;line-height:1.6;margin-top:6px}
.pp-foot a{color:#1E7A2D;font-weight:700}
`;

const PAYER_MOYENS = ["Mobile Money MTN","Mobile Money Orange","Virement bancaire"];
const lireFichierPreuve = (file) => new Promise((resolve, reject) => {
  const versBase64 = (blob, nom) => { const r=new FileReader(); r.onload=()=>resolve({nom, base64:String(r.result).split(",")[1]||"", url:URL.createObjectURL(blob), image:blob.type.startsWith("image/")}); r.onerror=()=>reject(new Error("Fichier illisible.")); r.readAsDataURL(blob); };
  if (file.type==="application/pdf") {
    if (file.size>7*1024*1024) return reject(new Error("Le PDF est trop volumineux (7 Mo maximum)."));
    return versBase64(file, file.name);
  }
  if (!/^image\//.test(file.type||"")) return reject(new Error("Format non accepté. Envoyez une photo (JPG, PNG, WEBP) ou un PDF."));
  const url = URL.createObjectURL(file); const img = new Image();
  img.onload = () => {
    try {
      const max = 1600; const k = Math.min(1, max/Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement("canvas"); c.width=Math.max(1,Math.round(img.naturalWidth*k)); c.height=Math.max(1,Math.round(img.naturalHeight*k));
      const x = c.getContext("2d"); x.fillStyle="#fff"; x.fillRect(0,0,c.width,c.height); x.drawImage(img,0,0,c.width,c.height);
      c.toBlob((b)=>{ URL.revokeObjectURL(url); if (!b) return reject(new Error("Photo illisible. Essayez une autre image.")); versBase64(b, (file.name||"preuve").replace(/\.[^.]+$/,"")+".jpg"); }, "image/jpeg", 0.82);
    } catch(e) { URL.revokeObjectURL(url); reject(new Error("Photo illisible. Essayez une autre image.")); }
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Photo illisible. Essayez une capture d'écran ou un PDF.")); };
  img.src = url;
});

function PagePayer({ code }) {
  const [etat, setEtat] = useState("chargement");
  const [d, setD] = useState(null);
  const [copie, setCopie] = useState("");
  const aujourdhui = new Date(Date.now()-new Date().getTimezoneOffset()*60000).toISOString().slice(0,10);
  const [form, setForm] = useState({ montant:"", moyen:"Mobile Money MTN", reference:"", date:aujourdhui, payeur:"" });
  const [fichier, setFichier] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const [fait, setFait] = useState(false);
  const charger = async () => {
    try {
      const r = await fetch("/api/payer/"+encodeURIComponent(code||""), { headers:{accept:"application/json"} });
      if (r.status===404) { setEtat("introuvable"); return; }
      if (!r.ok) { setEtat("erreur"); return; }
      const j = await r.json(); setD(j); setEtat("ok");
      setForm(f=>({...f, montant:String(Math.round(j.reste||0)||"")}));
    } catch(_) { setEtat("erreur"); }
  };
  useEffect(()=>{
    document.title = "Payer ma facture";
    try { let m=document.querySelector('meta[name="robots"]'); if(!m){ m=document.createElement("meta"); m.name="robots"; document.head.appendChild(m); } m.content="noindex,nofollow"; } catch(_) {}
    charger();
    // eslint-disable-next-line
  },[code]);
  const copier = async (cle, txt) => { try { await navigator.clipboard.writeText(String(txt||"")); setCopie(cle); setTimeout(()=>setCopie(""),1800); } catch(_) {} };
  const E = (k) => ((d&&d.entreprise)||{})[k]||"";
  const reste = Math.round((d&&d.reste)||0);
  const choisir = async (e) => {
    const file = e.target.files && e.target.files[0]; e.target.value="";
    if (!file) return;
    setErreur("");
    try { const f = await lireFichierPreuve(file); setFichier(f); } catch(err) { setFichier(null); setErreur(err.message||"Fichier illisible."); }
  };
  const envoyer = async () => {
    if (envoi) return;
    setErreur("");
    const montant = Math.round(Number(String(form.montant).replace(/\s/g,"").replace(",",".")));
    if (!Number.isFinite(montant) || montant<1) return setErreur("Merci d'indiquer le montant que vous avez payé.");
    if (montant>reste) return setErreur("Le montant ne peut pas dépasser le reste à payer ("+FCFA(reste)+").");
    if (!fichier) return setErreur("Merci de joindre la capture d'écran ou le reçu de votre paiement.");
    setEnvoi(true);
    try {
      const r = await fetch("/api/payer/"+encodeURIComponent(code)+"/preuve", { method:"POST", headers:{"content-type":"application/json"},
        body: JSON.stringify({ montant, moyen:form.moyen, reference:form.reference, date:form.date, payeur:form.payeur, fichier:{ nom:fichier.nom, base64:fichier.base64 } }) });
      let j=null; try { j = await r.json(); } catch(_) {}
      if (!r.ok) { setErreur((j&&j.erreur)||"Une erreur est survenue. Merci de réessayer."); return; }
      setFait(true);
    } catch(_) { setErreur("Connexion impossible. Vérifiez votre réseau puis réessayez."); }
    finally { setEnvoi(false); }
  };
  const ligne = (cle, titre, val) => val ? (
    <div className="pp-row" key={cle}>
      <div className="pp-l"><small>{titre}</small><b>{val}</b></div>
      <button type="button" className={"pp-cp"+(copie===cle?" ok":"")} onClick={()=>copier(cle,val)}>{copie===cle?"Copié ✓":"Copier"}</button>
    </div>
  ) : null;
  const entete = (
    <div className="pp-top"><img src={LOGO_UI} alt="Azongbio" /><span>{E("entreprise_nom")||"Azongbio"}</span></div>
  );
  return (
    <div className="pp-root">
      <style>{CSS_PP}</style>
      <div className="pp-wrap">
        {entete}
        {etat==="chargement"&&<div className="pp-card"><div className="pp-sub">Chargement de votre facture…</div></div>}
        {etat==="introuvable"&&<div className="pp-card"><div className="pp-h">Lien introuvable</div><div className="pp-sub" style={{marginTop:6}}>Ce lien de paiement n'est pas valide. Vérifiez le QR code ou demandez un nouveau lien à votre contact chez Azongbio.</div></div>}
        {etat==="erreur"&&<div className="pp-card"><div className="pp-h">Service momentanément indisponible</div><div className="pp-sub" style={{marginTop:6}}>Merci de réessayer dans quelques instants.</div><div style={{marginTop:14}}><button className="pp-btn" onClick={()=>{setEtat("chargement");charger();}}>Réessayer</button></div></div>}
        {etat==="ok"&&d&&d.inactif&&<div className="pp-card"><div className="pp-h">Lien de paiement désactivé</div><div className="pp-sub" style={{marginTop:6}}>Ce lien n'est plus actif. Merci de contacter {E("entreprise_nom")||"l'entreprise"}{E("entreprise_tel")?" au "+E("entreprise_tel"):""} pour régler votre facture.</div></div>}
        {etat==="ok"&&d&&!d.inactif&&(<>
          <div className="pp-card">
            <div className="pp-eyebrow">Facture</div>
            <div className="pp-h">{d.numero}</div>
            <div className="pp-sub">{d.client_nom?d.client_nom+" · ":""}{d.date_echeance?"échéance le "+fmtDate(d.date_echeance):""}</div>
            {reste>0?(
              <div className="pp-amt"><small>Reste à payer</small><b>{FCFA(reste)}</b></div>
            ):(
              <div className="pp-amt paid"><small>Facture réglée</small><b>Merci ! ✓</b></div>
            )}
            {reste>0&&Number(d.attente_montant)>0&&<div className="pp-info">Un paiement de {FCFA(d.attente_montant)} est en cours de vérification par notre équipe.</div>}
          </div>
          {reste>0&&!fait&&(<>
            <div className="pp-card">
              <div className="pp-t">Comment payer</div>
              {ligne("momo","Mobile Money MTN"+(E("paiement_momo_nom")?" · "+E("paiement_momo_nom"):""),E("paiement_momo_numero"))}
              {ligne("banque","Banque",E("paiement_banque"))}
              {ligne("compte","Numéro de compte"+(E("paiement_titulaire")?" · "+E("paiement_titulaire"):""),E("paiement_compte"))}
              {ligne("ref","Référence à indiquer",d.numero)}
              <div className="pp-note">Envoyez {FCFA(reste)} depuis votre application ou votre code Mobile Money, ou par virement, en indiquant la référence <b>{d.numero}</b>. Ensuite, touchez « J'ai payé » ci-dessous pour nous envoyer votre preuve.</div>
            </div>
            <div className="pp-card">
              {!ouvert?(
                <button className="pp-btn" onClick={()=>setOuvert(true)}>J'ai payé — envoyer ma preuve</button>
              ):(<>
                <div className="pp-t">Envoyer ma preuve de paiement</div>
                {erreur&&<div className="pp-err" role="alert">{erreur}</div>}
                <div className="pp-f"><label htmlFor="pp-m">Montant payé (FCFA)</label><input id="pp-m" inputMode="numeric" value={form.montant} onChange={e=>setForm({...form,montant:e.target.value})} /></div>
                <div className="pp-f"><label htmlFor="pp-mo">Moyen de paiement</label>
                  <select id="pp-mo" value={form.moyen} onChange={e=>setForm({...form,moyen:e.target.value})}>{PAYER_MOYENS.map(m=><option key={m} value={m}>{m}</option>)}</select></div>
                <div className="pp-f"><label htmlFor="pp-d">Date du paiement</label><input id="pp-d" type="date" value={form.date} max={aujourdhui} onChange={e=>setForm({...form,date:e.target.value})} /></div>
                <div className="pp-f"><label htmlFor="pp-r">Numéro de transaction (facultatif)</label><input id="pp-r" value={form.reference} maxLength={60} onChange={e=>setForm({...form,reference:e.target.value})} /></div>
                <div className="pp-f"><label htmlFor="pp-n">Votre nom (facultatif)</label><input id="pp-n" value={form.payeur} maxLength={80} onChange={e=>setForm({...form,payeur:e.target.value})} /></div>
                <div className="pp-f"><label>Capture d'écran ou reçu</label>
                  <label className="pp-file"><input type="file" accept="image/*,application/pdf" onChange={choisir} />
                    <em>{fichier?"Changer le fichier":"Choisir une photo ou un PDF"}</em><span>{fichier?fichier.nom:"Capture du message de paiement"}</span></label>
                  {fichier&&fichier.image&&<img className="pp-prev" src={fichier.url} alt="Aperçu de la preuve" />}
                </div>
                <button className="pp-btn" disabled={envoi} onClick={envoyer}>{envoi?"Envoi en cours…":"Envoyer ma preuve"}</button>
              </>)}
            </div>
          </>)}
          {fait&&(
            <div className="pp-card pp-ok"><i>✓</i><div className="pp-h">Merci, preuve reçue</div>
              <div className="pp-sub">Notre équipe vérifie votre paiement. Votre facture sera mise à jour dès sa validation.</div></div>
          )}
        </>)}
        <div className="pp-foot">
          {E("entreprise_nom")||"Azongbio"}{E("entreprise_tel")?<> · <a href={"tel:"+String(E("entreprise_tel")).replace(/[^0-9+]/g,"")}>{E("entreprise_tel")}</a></>:null}<br/>
          Page sécurisée · aucune connexion n'est demandée.
        </div>
      </div>
    </div>
  );
}

const CSS_SG = `
.sg-pad{background:#fff;border:1.5px dashed #9FD3A8;border-radius:14px;height:200px;touch-action:none;position:relative;overflow:hidden}
.sg-pad canvas{width:100%;height:100%;display:block;touch-action:none}
.sg-hint{position:absolute;inset:0;display:grid;place-items:center;color:#9AA9A0;font-size:14px;pointer-events:none;text-align:center;padding:0 20px}
.sg-text{white-space:pre-wrap;font-size:13.5px;line-height:1.6;background:#F7F9F8;border:1px solid #E3E9E6;border-radius:12px;padding:12px;max-height:42vh;overflow-y:auto;margin-top:10px;overflow-wrap:anywhere}
.sg-chk{display:flex;gap:10px;align-items:flex-start;font-size:13.5px;line-height:1.5;margin:14px 0}
.sg-chk input{width:22px;height:22px;flex-shrink:0;margin-top:1px;accent-color:#2F9E3F}
.sg-ref{display:grid;gap:3px;margin-top:10px;font-size:13.5px;line-height:1.45}
.sg-ref b{font-weight:700}
.sg-who{background:#F1FAF2;border:1px solid #BFE3C6;border-radius:12px;padding:10px 12px;font-size:14px;margin-bottom:12px}
.sg-img{display:block;max-width:100%;max-height:120px;margin:10px auto 0;background:#fff;border:1px solid #E3E9E6;border-radius:10px;padding:6px}
`;

function PageSigner({ code }) {
  const [etat, setEtat] = useState("chargement");
  const [d, setD] = useState(null);
  const [nom, setNom] = useState("");
  const [fonction, setFonction] = useState("");
  const [accord, setAccord] = useState(false);
  const [lire, setLire] = useState(false);
  const [aDessine, setADessine] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const canvasRef = useRef(null);
  const zoneRef = useRef(null);
  const dessinRef = useRef({ actif:false, dernier:null, trace:false });
  const charger = async () => {
    try {
      const r = await fetch("/api/signer/"+encodeURIComponent(code||""), { headers:{accept:"application/json"} });
      if (r.status===404) { setEtat("introuvable"); return; }
      if (!r.ok) { setEtat("erreur"); return; }
      const j = await r.json(); setD(j);
      setEtat(j.etat==="signe"?"signe":j.etat==="expire"?"expire":"ok");
    } catch(_) { setEtat("erreur"); }
  };
  useEffect(()=>{
    document.title = "Signer un document";
    try { let m=document.querySelector('meta[name="robots"]'); if(!m){ m=document.createElement("meta"); m.name="robots"; document.head.appendChild(m); } m.content="noindex,nofollow"; } catch(_) {}
    charger();
    // eslint-disable-next-line
  },[code]);
  const preparer = () => {
    const c = canvasRef.current, z = zoneRef.current;
    if (!c || !z) return;
    const rect = z.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.max(1,Math.round(rect.width*dpr));
    c.height = Math.max(1,Math.round(rect.height*dpr));
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr,0,0,dpr,0,0);
    ctx.clearRect(0,0,rect.width,rect.height);
    dessinRef.current = { actif:false, dernier:null, trace:false };
    setADessine(false);
  };
  useEffect(()=>{
    if (etat!=="ok") return;
    preparer();
    const surTaille = () => { if (!dessinRef.current.trace) preparer(); };
    window.addEventListener("resize", surTaille);
    return () => window.removeEventListener("resize", surTaille);
    // eslint-disable-next-line
  },[etat]);
  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return { x:e.clientX-r.left, y:e.clientY-r.top }; };
  const bas = (e) => {
    e.preventDefault();
    dessinRef.current.actif = true; dessinRef.current.dernier = pos(e);
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch(_) {}
    // un simple appui laisse un point
    const ctx = canvasRef.current.getContext("2d"); const p = dessinRef.current.dernier;
    ctx.fillStyle = "#16233A"; ctx.beginPath(); ctx.arc(p.x,p.y,1.4,0,Math.PI*2); ctx.fill();
  };
  const bouge = (e) => {
    const s = dessinRef.current;
    if (!s.actif) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"); const p = pos(e);
    ctx.strokeStyle = "#16233A"; ctx.lineWidth = 2.8; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(s.dernier.x,s.dernier.y); ctx.lineTo(p.x,p.y); ctx.stroke();
    s.dernier = p;
    if (!s.trace) { s.trace = true; setADessine(true); }
  };
  const haut = () => { dessinRef.current.actif = false; };
  // Recadre la signature sur le tracé, fond transparent, taille raisonnable
  const exporter = () => {
    const c = canvasRef.current; if (!c) return null;
    const w = c.width, h = c.height;
    const px = c.getContext("2d").getImageData(0,0,w,h).data;
    let x0=w, y0=h, x1=-1, y1=-1;
    for (let y=0;y<h;y++) for (let x=0;x<w;x++) if (px[(y*w+x)*4+3]>16) { if(x<x0)x0=x; if(x>x1)x1=x; if(y<y0)y0=y; if(y>y1)y1=y; }
    if (x1<0) return null;
    const m = Math.round(6*(window.devicePixelRatio||1));
    x0=Math.max(0,x0-m); y0=Math.max(0,y0-m); x1=Math.min(w-1,x1+m); y1=Math.min(h-1,y1+m);
    const sw=x1-x0+1, sh=y1-y0+1;
    if (sw+sh < 60) return null;
    const k = Math.min(1, 560/sw, 220/sh);
    const o = document.createElement("canvas");
    o.width = Math.max(1,Math.round(sw*k)); o.height = Math.max(1,Math.round(sh*k));
    o.getContext("2d").drawImage(c,x0,y0,sw,sh,0,0,o.width,o.height);
    return o.toDataURL("image/png");
  };
  const effacer = () => preparer();
  const nomPrevu = d && d.signataire_nom ? d.signataire_nom : "";
  const signer = async () => {
    if (envoi) return;
    setErreur("");
    const nomFinal = (nomPrevu || nom).trim();
    if (nomFinal.length<2) return setErreur("Merci d'indiquer votre nom.");
    const png = exporter();
    if (!png) return setErreur("Merci de dessiner votre signature dans le cadre (un peu plus grande).");
    if (!accord) return setErreur("Merci de cocher la case pour confirmer.");
    setEnvoi(true);
    try {
      const r = await fetch("/api/signer/"+encodeURIComponent(code), { method:"POST", headers:{"content-type":"application/json"}, body:JSON.stringify({ nom:nomFinal, fonction:fonction.trim(), signature:png }) });
      let j=null; try { j = await r.json(); } catch(_) {}
      if (r.status===409) { setEtat("signe"); return; }
      if (r.status===410) { setEtat("expire"); return; }
      if (!r.ok) { setErreur((j&&j.erreur)||"Une erreur est survenue. Merci de réessayer."); return; }
      setEtat("fait");
    } catch(_) { setErreur("Connexion impossible. Vérifiez votre réseau puis réessayez."); }
    finally { setEnvoi(false); }
  };
  const E = (d&&d.entreprise_nom) || "Azongbio";
  return (
    <div className="pp-root">
      <style>{CSS_PP+CSS_SG}</style>
      <div className="pp-wrap">
        <div className="pp-top"><img src={LOGO_UI} alt="Azongbio" /><span>{E}</span></div>
        {etat==="chargement"&&<div className="pp-card"><div className="pp-sub">Chargement du document…</div></div>}
        {etat==="introuvable"&&<div className="pp-card"><div className="pp-h">Lien introuvable</div><div className="pp-sub" style={{marginTop:6}}>Ce lien de signature n'est pas valide. Demandez un nouveau QR code à la personne qui vous l'a remis.</div></div>}
        {etat==="erreur"&&<div className="pp-card"><div className="pp-h">Service momentanément indisponible</div><div className="pp-sub" style={{marginTop:6}}>Merci de réessayer dans quelques instants.</div><div style={{marginTop:14}}><button className="pp-btn" onClick={()=>{setEtat("chargement");charger();}}>Réessayer</button></div></div>}
        {etat==="expire"&&<div className="pp-card"><div className="pp-h">Lien expiré</div><div className="pp-sub" style={{marginTop:6}}>Ce QR code n'est plus valable (il l'est 60 minutes). Demandez-en un nouveau à la personne qui vous l'a remis.</div></div>}
        {etat==="signe"&&<div className="pp-card"><div className="pp-h">Document déjà signé</div><div className="pp-sub" style={{marginTop:6}}>Ce document a déjà été signé. Il n'y a rien d'autre à faire.</div></div>}
        {etat==="fait"&&(
          <div className="pp-card pp-ok"><i>✓</i><div className="pp-h">Merci, document signé</div>
            <div className="pp-sub">Votre signature a été enregistrée. Vous pouvez fermer cette page.</div></div>
        )}
        {etat==="ok"&&d&&(<>
          <div className="pp-card">
            <div className="pp-eyebrow">Document à signer</div>
            <div className="pp-h">{d.type||"Document"}</div>
            <div className="sg-ref">
              {d.destinataire&&<div><span style={{color:"#5B6B62"}}>À l'attention de : </span><b>{d.destinataire}</b></div>}
              {d.objet&&<div><span style={{color:"#5B6B62"}}>Objet : </span><b>{d.objet}</b></div>}
              {d.doc_date&&<div><span style={{color:"#5B6B62"}}>Date : </span><b>{fmtDate(d.doc_date)}</b></div>}
            </div>
            <div style={{marginTop:12}}><button type="button" className="pp-btn ghost" onClick={()=>setLire(v=>!v)}>{lire?"Masquer le texte":"Lire le document"}</button></div>
            {lire&&<div className="sg-text">{d.corps}</div>}
          </div>
          <div className="pp-card">
            <div className="pp-t">Votre signature</div>
            {erreur&&<div className="pp-err" role="alert">{erreur}</div>}
            {nomPrevu?(
              <div className="sg-who">Signataire : <b>{nomPrevu}</b>{d.signataire_fonction?<> · {d.signataire_fonction}</>:null}</div>
            ):(<>
              <div className="pp-f"><label htmlFor="sg-n">Votre nom et prénom</label><input id="sg-n" value={nom} maxLength={80} autoComplete="name" onChange={e=>setNom(e.target.value)} /></div>
              <div className="pp-f"><label htmlFor="sg-f">Votre fonction (facultatif)</label><input id="sg-f" value={fonction} maxLength={80} onChange={e=>setFonction(e.target.value)} /></div>
            </>)}
            <div className="pp-f" style={{marginBottom:6}}><label>Signez avec le doigt dans le cadre</label></div>
            <div className="sg-pad" ref={zoneRef}>
              <canvas ref={canvasRef} onPointerDown={bas} onPointerMove={bouge} onPointerUp={haut} onPointerLeave={haut} onPointerCancel={haut} />
              {!aDessine&&<div className="sg-hint">Signez ici</div>}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:6}}><button type="button" className="pp-cp" onClick={effacer}>Effacer</button></div>
            <label className="sg-chk"><input type="checkbox" checked={accord} onChange={e=>setAccord(e.target.checked)} /><span>Je confirme avoir pris connaissance de ce document et j'accepte d'y apposer ma signature.</span></label>
            <button className="pp-btn" disabled={envoi||!aDessine||!accord||(!nomPrevu&&nom.trim().length<2)} onClick={signer}>{envoi?"Envoi en cours…":"Signer le document"}</button>
            <div className="pp-note">Ce lien est valable jusqu'à {sigHeureCourte(d.expire_at)}.</div>
          </div>
        </>)}
        <div className="pp-foot">{E}<br/>Page sécurisée · aucune connexion n'est demandée.</div>
      </div>
    </div>
  );
}

function PageVerifier({ code }) {
  const [etat, setEtat] = useState("chargement");
  const [d, setD] = useState(null);
  useEffect(()=>{
    document.title = "Vérification de signature";
    try { let m=document.querySelector('meta[name="robots"]'); if(!m){ m=document.createElement("meta"); m.name="robots"; document.head.appendChild(m); } m.content="noindex,nofollow"; } catch(_) {}
    (async()=>{
      try {
        const r = await fetch("/api/verifier/"+encodeURIComponent(code||""), { headers:{accept:"application/json"} });
        if (r.status===404) { setEtat("introuvable"); return; }
        if (!r.ok) { setEtat("erreur"); return; }
        setD(await r.json()); setEtat("ok");
      } catch(_) { setEtat("erreur"); }
    })();
  },[code]);
  const E = (d&&d.entreprise_nom) || "Azongbio";
  return (
    <div className="pp-root">
      <style>{CSS_PP+CSS_SG}</style>
      <div className="pp-wrap">
        <div className="pp-top"><img src={LOGO_UI} alt="Azongbio" /><span>{E}</span></div>
        {etat==="chargement"&&<div className="pp-card"><div className="pp-sub">Vérification en cours…</div></div>}
        {etat==="erreur"&&<div className="pp-card"><div className="pp-h">Service momentanément indisponible</div><div className="pp-sub" style={{marginTop:6}}>Merci de réessayer dans quelques instants.</div></div>}
        {etat==="introuvable"&&<div className="pp-card"><div className="pp-h">Signature introuvable</div><div className="pp-sub" style={{marginTop:6}}>Ce QR code ne correspond à aucune signature enregistrée par {E}. Le document n'est peut-être pas authentique : contactez l'entreprise pour vérification.</div></div>}
        {etat==="ok"&&d&&(
          <div className="pp-card">
            <div className="pp-eyebrow">Signature électronique</div>
            <div className="pp-h" style={{color:"#1E7A2D"}}>✓ Signature enregistrée</div>
            <div className="sg-ref">
              <div><span style={{color:"#5B6B62"}}>Document : </span><b>{d.type}</b></div>
              {d.destinataire&&<div><span style={{color:"#5B6B62"}}>À l'attention de : </span><b>{d.destinataire}</b></div>}
              {d.objet&&<div><span style={{color:"#5B6B62"}}>Objet : </span><b>{d.objet}</b></div>}
              {d.doc_date&&<div><span style={{color:"#5B6B62"}}>Date du document : </span><b>{fmtDate(d.doc_date)}</b></div>}
              <div><span style={{color:"#5B6B62"}}>Signé par : </span><b>{d.nom}</b>{d.fonction?<> · {d.fonction}</>:null}</div>
              <div><span style={{color:"#5B6B62"}}>Le : </span><b>{sigHeure(d.signe_le)}</b> <span style={{color:"#5B6B62"}}>(heure de Douala)</span></div>
              {d.empreinte&&<div><span style={{color:"#5B6B62"}}>Empreinte du texte : </span><b style={{fontFamily:"monospace"}}>{d.empreinte}</b></div>}
            </div>
            {d.signature&&<img className="sg-img" src={d.signature} alt="Signature" />}
            <div className="pp-note">Vérifiez que l'empreinte ci-dessus est identique à celle imprimée sous le QR code du document : si elle diffère, le texte a été modifié après la signature. Cette page ne montre pas le contenu du document.</div>
          </div>
        )}
        <div className="pp-foot">{E}</div>
      </div>
    </div>
  );
}

// ── Badges employés : PDF imprimable (recto/verso) + page publique de vérification /badge/<code> ──
const badgeUrl = (code) => PAYER_BASE+"/badge/"+code;
const badgePhotoRecadree = (dataUrl, wmm, hmm) => new Promise((res)=>{
  if (!dataUrl) return res(null);
  const img = new Image();
  img.onload = () => { try {
    const W=Math.round(wmm*12), H=Math.round(hmm*12);
    const c=document.createElement("canvas"); c.width=W; c.height=H; const g=c.getContext("2d");
    g.fillStyle="#ffffff"; g.fillRect(0,0,W,H);
    const r=Math.max(W/img.width,H/img.height); const dw=img.width*r, dh=img.height*r;
    g.drawImage(img,(W-dw)/2,0,dw,dh);
    res(c.toDataURL("image/jpeg",0.9));
  } catch(_) { res(null); } };
  img.onerror = () => res(null);
  img.src = dataUrl;
});
// items : [{ e: employé, code: code secret du badge }] — 9 badges par feuille A4, recto puis verso (recto-verso bord long)
// mode "carte" : une carte par page (54 x 85,6 mm, format CR80 vertical), recto puis verso, couleurs jusqu'au bord — pour imprimante de cartes PVC
// mode "a4"    : 9 badges par feuille A4, recto puis verso, avec repères de découpe
const badgesPdfBase64 = async (items, entreprise={}, mode="carte") => {
  const plein = mode==="carte";
  const doc = plein ? new jsPDF({unit:"mm",format:[54,85.6],orientation:"portrait"}) : new jsPDF({unit:"mm",format:"a4"});
  const logoAsset = await getPdfLogoAsset();
  const VERT=[30,138,50], INK=[15,31,23], GRIS=[91,107,98], PALE=[241,245,243];
  const CW=54, CH=plein?85.6:86, GX=6, GY=5, X0=(210-3*CW-2*GX)/2, Y0=(297-3*CH-2*GY)/2;
  const societe = pdfSafe(entreprise.entreprise_nom||"Azongbio");
  const tel = entreprise.entreprise_tel||"";
  const prep = [];
  for (const it of items) prep.push({ ...it, photo: await badgePhotoRecadree(it.e.photo_url, 30, 36) });
  const marques = (x,y) => {
    doc.setDrawColor(170,178,174); doc.setLineWidth(0.15);
    [[x,y,-1,-1],[x+CW,y,1,-1],[x,y+CH,-1,1],[x+CW,y+CH,1,1]].forEach(([cx,cy,dx,dy])=>{
      doc.line(cx+dx*1,cy,cx+dx*3.5,cy); doc.line(cx,cy+dy*1,cx,cy+dy*3.5);
    });
  };
  const recto = (x,y,it) => {
    const e=it.e;
    if (plein) { doc.setFillColor(...VERT); doc.rect(x,y,CW,CH,"F"); doc.setFillColor(255,255,255); doc.rect(x,y,CW,CH-5.5,"F"); }
    else { doc.setFillColor(...VERT); doc.roundedRect(x,y,CW,CH,3,3,"F"); doc.setFillColor(255,255,255); doc.roundedRect(x,y,CW,CH-5.5,3,3,"F"); }
    if (logoAsset?.dataUrl) { const lw=34, lh=Math.min(lw*(logoAsset.ratio||0.234),10); doc.addImage(logoAsset.dataUrl,"PNG",x+(CW-lw)/2,y+5,lw,lh); }
    doc.setFillColor(...VERT); doc.rect(x+6,y+16,CW-12,0.8,"F");
    const px=x+12, py=y+20;
    if (it.photo) doc.addImage(it.photo,"JPEG",px,py,30,36);
    else {
      doc.setFillColor(...PALE); doc.rect(px,py,30,36,"F");
      const ini=((e.prenom||"")[0]||"")+((e.nom||"")[0]||"");
      doc.setFont("helvetica","bold"); doc.setFontSize(22); doc.setTextColor(...VERT); doc.text(pdfSafe(ini.toUpperCase()||"?"),px+15,py+21,{align:"center"});
    }
    doc.setDrawColor(...VERT); doc.setLineWidth(0.6); doc.rect(px,py,30,36,"S");
    const nom = pdfSafe([e.prenom,e.nom].filter(Boolean).join(" ").trim()||"—");
    let fs=12; doc.setFont("helvetica","bold"); doc.setFontSize(fs);
    let lignes=doc.splitTextToSize(nom,CW-8);
    if (lignes.length>2) { fs=9.5; doc.setFontSize(fs); lignes=doc.splitTextToSize(nom,CW-8).slice(0,2); }
    doc.setTextColor(...INK); let ty=y+62; lignes.forEach(l=>{ doc.text(l,x+CW/2,ty,{align:"center"}); ty+=4.6; });
    let pf=8.5; const poste=pdfSafe(e.poste||"");
    doc.setFont("helvetica","normal"); doc.setFontSize(pf);
    while (pf>6.2 && doc.getTextWidth(poste)>CW-6) { pf-=0.3; doc.setFontSize(pf); }
    doc.setTextColor(...GRIS); doc.text(doc.splitTextToSize(poste,CW-6)[0]||"",x+CW/2,ty-0.4,{align:"center"});
    doc.setFont("helvetica","bold"); doc.setFontSize(7.5); doc.setTextColor(...VERT);
    doc.text(pdfSafe([e.ville,e.matricule].filter(Boolean).join("  ·  ")),x+CW/2,y+77.2,{align:"center"});
    doc.setTextColor(255,255,255); doc.setFontSize(6.5); doc.text("BADGE EMPLOYE",x+CW/2,y+CH-2,{align:"center"});
    if (!plein) { doc.setDrawColor(200,207,203); doc.setLineWidth(0.2); doc.roundedRect(x,y,CW,CH,3,3,"S"); }
  };
  const verso = (x,y,it) => {
    const e=it.e;
    if (plein) { doc.setFillColor(...VERT); doc.rect(x,y,CW,CH,"F"); }
    else { doc.setFillColor(...VERT); doc.roundedRect(x,y,CW,CH,3,3,"F"); }
    doc.setFillColor(255,255,255); doc.rect(x,y+9,CW,CH-15,"F");
    doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(255,255,255); doc.text("AZONGBIO",x+CW/2,y+6.2,{align:"center"});
    const qm = makeQrMatrix(badgeUrl(it.code));
    const qs=36, qx=x+(CW-qs)/2, qy=y+13;
    if (qm) {
      const mod=qs/(qm.n+4);
      doc.setFillColor(0,0,0);
      for (let r=0;r<qm.n;r++) for (let c=0;c<qm.n;c++) if (qm.rows[r][c]) doc.rect(qx+(c+2)*mod,qy+(r+2)*mod,mod+0.02,mod+0.02,"F");
    }
    doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(...INK);
    doc.text("Scannez pour vérifier ce badge",x+CW/2,y+53.5,{align:"center"});
    doc.setFont("helvetica","normal"); doc.setFontSize(6.8); doc.setTextColor(...GRIS);
    doc.text(pdfSafe("Matricule : "+(e.matricule||"—")),x+CW/2,y+57.5,{align:"center"});
    doc.setFontSize(6.2);
    const txt = "Ce badge est strictement personnel et reste la propriété de "+societe+". S'il est trouvé, merci de le rapporter"+(tel?" ou d'appeler le "+tel:"")+".";
    doc.text(doc.splitTextToSize(pdfSafe(txt),CW-9),x+CW/2,y+63,{align:"center",lineHeightFactor:1.35});
    doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(255,255,255);
    doc.text("braliebranding.com",x+CW/2,y+CH-2,{align:"center"});
    if (!plein) { doc.setDrawColor(200,207,203); doc.setLineWidth(0.2); doc.roundedRect(x,y,CW,CH,3,3,"S"); }
  };
  if (plein) {
    prep.forEach((it,k)=>{ if (k>0) doc.addPage(); recto(0,0,it); doc.addPage(); verso(0,0,it); });
  } else {
    for (let i=0;i<prep.length;i+=9) {
      const lot=prep.slice(i,i+9);
      if (i>0) doc.addPage();
      lot.forEach((it,k)=>{ const x=X0+(k%3)*(CW+GX), y=Y0+Math.floor(k/3)*(CH+GY); recto(x,y,it); marques(x,y); });
      doc.addPage();
      lot.forEach((it,k)=>{ const x=X0+(2-(k%3))*(CW+GX), y=Y0+Math.floor(k/3)*(CH+GY); verso(x,y,it); marques(x,y); });
    }
  }
  const un = prep.length===1;
  const fichier = un ? "Badge_"+String(prep[0].e.nom||"employe").replace(/[^A-Za-z0-9]+/g,"_") : (plein?"Badges_cartes_":"Badges_feuilles_A4_")+today();
  const datauri = doc.output("datauristring");
  return { base64: datauri.split(",").pop(), filename: fichier+".pdf" };
};

function PageBadge({ code }) {
  const [etat, setEtat] = useState("chargement");
  const [d, setD] = useState(null);
  useEffect(()=>{
    document.title = "Vérification de badge";
    try { let m=document.querySelector('meta[name="robots"]'); if(!m){ m=document.createElement("meta"); m.name="robots"; document.head.appendChild(m); } m.content="noindex,nofollow"; } catch(_) {}
    (async()=>{
      try {
        const r = await fetch("/api/badge/"+encodeURIComponent(code||""), { headers:{accept:"application/json"} });
        if (r.status===404) { setEtat("introuvable"); return; }
        if (!r.ok) { setEtat("erreur"); return; }
        setD(await r.json()); setEtat("ok");
      } catch(_) { setEtat("erreur"); }
    })();
  },[code]);
  const nom = d ? [d.prenom,d.nom].filter(Boolean).join(" ") : "";
  return (
    <div className="pp-root">
      <style>{CSS_PP}</style>
      <div className="pp-wrap">
        <div className="pp-top"><img src={LOGO_UI} alt="Azongbio" /><span>Azongbio</span></div>
        {etat==="chargement"&&<div className="pp-card"><div className="pp-sub">Vérification en cours…</div></div>}
        {etat==="erreur"&&<div className="pp-card"><div className="pp-h">Service momentanément indisponible</div><div className="pp-sub" style={{marginTop:6}}>Merci de réessayer dans quelques instants.</div></div>}
        {etat==="introuvable"&&<div className="pp-card"><div className="pp-h" style={{color:"#D6453D"}}>✗ Badge non reconnu</div><div className="pp-sub" style={{marginTop:6}}>Ce badge ne correspond à aucun employé d'Azongbio, ou il a été remplacé (badge perdu). Ne lui accordez pas votre confiance et prévenez l'entreprise.</div></div>}
        {etat==="ok"&&d&&(
          <div className="pp-card" style={{textAlign:"center"}}>
            <div className="pp-eyebrow">Badge employé</div>
            {d.photo?<img alt="" src={d.photo} style={{width:112,height:134,objectFit:"cover",objectPosition:"center top",borderRadius:10,border:"2px solid "+(d.actif?"#1E8A32":"#D6453D"),margin:"4px auto 10px",display:"block"}}/>:null}
            <div className="pp-h" style={{color:d.actif?"#1E7A2D":"#D6453D"}}>{d.actif?"✓ Badge valide":"✗ Badge plus valide"}</div>
            <div style={{fontSize:20,fontWeight:700,marginTop:10,color:"#0F1F17"}}>{nom}</div>
            {d.poste&&<div className="pp-sub" style={{marginTop:2}}>{d.poste}</div>}
            <div className="pp-sub" style={{marginTop:2}}>{[d.ville,d.matricule].filter(Boolean).join(" · ")}</div>
            <div className="pp-note" style={{marginTop:14}}>{d.actif?"Cette personne fait bien partie de l'équipe Azongbio. Vérifiez que la photo ci-dessus correspond à la personne qui vous présente le badge.":"Cette personne ne fait plus partie de l'équipe Azongbio. Ne lui accordez pas l'accès."}</div>
          </div>
        )}
        <div className="pp-foot">Azongbio</div>
      </div>
    </div>
  );
}

export default function App() {
  const codePayer = payerCodeFromUrl();
  if (codePayer!==null) return <PagePayer code={codePayer} />;
  const codeSigner = sigCodeFromUrl("signer");
  if (codeSigner!==null) return <PageSigner code={codeSigner} />;
  const codeVerif = sigCodeFromUrl("verifier");
  if (codeVerif!==null) return <PageVerifier code={codeVerif} />;
  const codeBadge = sigCodeFromUrl("badge");
  if (codeBadge!==null) return <PageBadge code={codeBadge} />;
  return <AppPrincipal />;
}

function AppPrincipal() {
  const [session, setSession]     = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [page, setPage]           = useState("tableau");
  const [employes, setEmployes]   = useState([]);
  const [departements, setDepartements] = useState([]);
  const [conges, setConges] = useState([]);
  const [documentsEmployes, setDocumentsEmployes] = useState([]);
  const [historiqueEmployes, setHistoriqueEmployes] = useState([]);
  const [evaluationsEmployes, setEvaluationsEmployes] = useState([]);
  const [contratsEmployes, setContratsEmployes] = useState([]);
  const [soldesConges, setSoldesConges] = useState([]);
  const [joursFeries, setJoursFeries] = useState([]);
  const [receptionsBouteilles, setReceptionsBouteilles] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [recettes, setRecettes] = useState([]);
  const [recetteEditProduit, setRecetteEditProduit] = useState(null);
  const [recetteSaving, setRecetteSaving] = useState(false);
  const [recetteQtes, setRecetteQtes] = useState({});
  const [showIngredientsPanel, setShowIngredientsPanel] = useState(false);
  const [immobilisations, setImmobilisations] = useState([]);
  const [mouvementsImmobilisations, setMouvementsImmobilisations] = useState([]);
  const [entretiensImmobilisations, setEntretiensImmobilisations] = useState([]);
  const [immoSearch, setImmoSearch] = useState("");
  const [immoVilleFilter, setImmoVilleFilter] = useState("toutes");
  const [immoCategorieFilter, setImmoCategorieFilter] = useState("toutes");
  const [immoShowForm, setImmoShowForm] = useState(false);
  const [immoSaving, setImmoSaving] = useState(false);
  const [immoForm, setImmoForm] = useState({
    id:null, numero_inventaire:"", designation:"", categorie:"Ordinateur", marque:"", modele:"",
    numero_serie:"", ville:"", localisation:"", employe_id:"", employe_nom:"",
    date_acquisition:today(), valeur_acquisition:"", duree_amortissement:"", fournisseur:"", etat:"Bon",
    statut:"En service", date_fin_garantie:"", prochaine_maintenance:"", notes:""
  });
  const [rhSearch, setRhSearch] = useState("");
  const [rhVilleFilter, setRhVilleFilter] = useState("toutes");
  const [rhStatutFilter, setRhStatutFilter] = useState("tous");
  const [rhSection, setRhSection] = useState("personnel");
  const [rhSaving, setRhSaving] = useState(false);
  const [produits, setProduits]   = useState([]);
  Object.keys(PRODUITS_CODES).forEach(k=>delete PRODUITS_CODES[k]);
  produits.forEach(p=>{ if (p && p.code && p.nom) PRODUITS_CODES[normNomProduit(p.nom)] = String(p.code); });
  const [clients, setClients]     = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [achatsCommandes, setAchatsCommandes] = useState([]);
  const [achatsReceptions, setAchatsReceptions] = useState([]);
  const [achatsFactures, setAchatsFactures] = useState([]);
  const [achatsPaiements, setAchatsPaiements] = useState([]);
  const [achatSection, setAchatSection] = useState("dashboard");
  const [achatSearch, setAchatSearch] = useState("");
  const [achatSaving, setAchatSaving] = useState(false);
  const [achatFormOpen, setAchatFormOpen] = useState(false);
  const [achatForm, setAchatForm] = useState({fournisseur_id:"",fournisseur_nom:"",ville:"",date_commande:today(),date_livraison_prevue:"",notes:"",lignes:[]});
  const [achatLigne, setAchatLigne] = useState({produit_id:"",code:"",designation:"",quantite:"",prix_unitaire:""});
  const [ventes, setVentes]       = useState([]);
  const [depenses, setDepenses]   = useState([]);
  const [lots, setLots]           = useState([]);
  const [factures, setFactures]   = useState([]);
  const [proformas, setProformas] = useState([]);
  const [depots, setDepots]       = useState([]);
  const [depotStocks, setDepotStocks] = useState([]);
  const [avaries, setAvaries] = useState([]);
  const [livraisons, setLivraisons] = useState([]);
  // Preuves de paiement (table public.paiements + bucket Storage preuves-paiement)
  const [paiements, setPaiements] = useState([]);
  const [paiementSaving, setPaiementSaving] = useState(false);
  const [paiementFilter, setPaiementFilter] = useState("tous");
  // origineType: "central" = magasin central de Dschang (comportement historique) ;
  // "ville" = dépannage inter-villes (le stock est déduit de la ville d'origine, pas du magasin central).
  const [livraisonForm, setLivraisonForm] = useState({ depotId:"", depotNom:"", ville:"", origineType:"central", villeOrigine:"", dateDepart:today(), dateArrivee:"", chauffeur:"", vehicule:"", agence:"", agenceTel:"", agenceRef:"", receptionnairePrevu:"", notes:"", lignes:[] });
  const [newLivraisonLigne, setNewLivraisonLigne] = useState({ produit:"", quantite:"" });
  const [livraisonView, setLivraisonView] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [docForm, setDocForm] = useState({type:"Lettre commerciale", clientNom:"", objet:"", corps:"", date:today()});
  const [objectifs, setObjectifs] = useState({});
  const [objHist, setObjHist] = useState({});
  const [objHistPret, setObjHistPret] = useState(false);
  const [objVue, setObjVue] = useState("mois");
  const [objJournalEmp, setObjJournalEmp] = useState("");
  const [editObjectif, setEditObjectif] = useState(null);
  const [objForm, setObjForm]     = useState({bouteilles:0, montant:0});
  const [docView, setDocView]     = useState(null);
  const [relDesel, setRelDesel] = useState({});
  const [relOuvert, setRelOuvert] = useState({});
  const [pdfPreview, setPdfPreview] = useState(null);
  const [emailForm, setEmailForm] = useState({to:"", subject:"", message:""});
  const [emailLoading, setEmailLoading] = useState(false);
  const [docSendModal, setDocSendModal] = useState(null);
  const [docSendForm, setDocSendForm] = useState({to:"", toName:"", subject:"", message:""});
  const [docSendLoading, setDocSendLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLuParAutre, setChatLuParAutre] = useState(0);
  const [factureVue, setFactureVue] = useState(()=>{ try { return localStorage.getItem("az_fac_vue")==="liste" ? "liste" : "client"; } catch(e) { return "client"; } });
  const [facOuverts, setFacOuverts] = useState({});
  // ── Factures à cocher (reprise des anciennes factures) : ouvert à tous, accès par fonctions de la base ──
  const [acListe, setAcListe] = useState(null);
  const [acDraft, setAcDraft] = useState({});
  const [acDate, setAcDate] = useState("facture");
  const [acVue, setAcVue] = useState("a_cocher");
  const [acRecherche, setAcRecherche] = useState("");
  const [acBusy, setAcBusy] = useState("");
  const [acErreurs, setAcErreurs] = useState([]);
  const acCharger = async () => {
    const { data, error } = await supabase.rpc("a_cocher_lister", { p_tva:getTvaRate(entreprise) });
    if (error) { setAcListe(l=>l||[]); setAcErreurs(["Chargement impossible : "+error.message]); return; }
    setAcListe(Array.isArray(data)?data:[]);
  };
  useEffect(()=>{
    if (!session || page!=="a_cocher") return;
    acCharger();
    const t = setInterval(acCharger, 45000);
    return ()=>clearInterval(t);
    // eslint-disable-next-line
  },[page,session?.id]);
  const acChoisir = (numero, choix, defMoyen) => setAcDraft(d=>{
    const cur = d[numero];
    if (cur && cur.choix===choix) { const n={...d}; delete n[numero]; return n; }
    return {...d,[numero]:{choix, montant:(cur&&cur.montant)||"", moyen:(cur&&cur.moyen)||defMoyen||""}};
  });
  const acModif = (numero, champ, val) => setAcDraft(d=>({...d,[numero]:{...d[numero],[champ]:val}}));
  const acAppel = async (nom, params, numero) => {
    const { error } = await supabase.rpc(nom, params);
    if (error) throw new Error(error.message);
  };
  const acRemettre = async (f) => {
    if (!window.confirm("Remettre la facture "+f.numero+" dans la liste « à cocher » ?"+(f.regul_statut==="À valider"?"\n(la saisie de paiement en attente sera supprimée)":""))) return;
    setAcBusy("remettre");
    try { await acAppel("a_cocher_remettre",{p_numero:f.numero}); await acCharger(); showToast("Facture "+f.numero+" remise à cocher"); }
    catch(e) { showToast("Erreur : "+e.message,false); }
    setAcBusy("");
  };
  const acValider = async (f) => {
    if (!canValidatePayments()) return showToast("Réservé aux personnes habilitées à valider les paiements",false);
    if (!window.confirm("Valider le paiement de "+FCFA(f.attente_montant)+" ("+(f.attente_moyen||"—")+") saisi par "+(f.attente_par||"?")+" sur la facture "+f.numero+" ?")) return;
    setAcBusy("valider");
    try { await acAppel("a_cocher_valider",{p_numero:f.numero,p_tva:getTvaRate(entreprise)}); await acCharger(); await load(); showToast("Paiement validé ✓"); }
    catch(e) { showToast("Erreur : "+e.message,false); }
    setAcBusy("");
  };
  // Enregistre les choix d'UN client (bouton « Enregistrer » de son bloc)
  const acEnregistrerClient = async (nom, fs) => {
    const lignes = fs.map(f=>({f,d:acDraft[f.numero]})).filter(x=>x.d);
    if (!lignes.length) return;
    for (const {f,d} of lignes) {
      if (d.choix==="non") continue;
      const reste = Math.round(Number(f.reste)||0);
      if (reste<=0) return showToast(f.numero+" : déjà entièrement réglée",false);
      if (!d.moyen) return showToast(f.numero+" : choisissez le mode de paiement",false);
      if (d.choix==="part") {
        const m = Number(d.montant);
        if (!(m>0) || m>=reste) return showToast(f.numero+" : montant partiel entre 1 et "+(reste-1)+" FCFA",false);
      }
    }
    const nbPay = lignes.filter(x=>x.d.choix!=="non").length, nbNon = lignes.length-nbPay;
    const valide = canValidatePayments();
    if (!window.confirm("Enregistrer "+lignes.length+" facture(s) de "+nom+" ?\n\n"+(nbPay?"• "+nbPay+" paiement(s) "+(valide?"validé(s) tout de suite":"envoyé(s) à la direction pour validation")+"\n":"")+(nbNon?"• "+nbNon+" facture(s) marquée(s) « Non payée »":""))) return;
    setAcBusy(nom); setAcErreurs([]);
    const ok=[], ko=[];
    for (const {f,d} of lignes) {
      try {
        const dateV = (acDate==="facture" && f.date_emission) ? String(f.date_emission).slice(0,10) : today();
        await acAppel("a_cocher_enregistrer",{
          p_numero:f.numero, p_choix:d.choix, p_montant:d.choix==="part"?Number(d.montant):null,
          p_moyen:d.choix==="non"?"":(d.moyen||""), p_date:dateV, p_tva:getTvaRate(entreprise)
        });
        ok.push(f.numero);
      } catch(e) { ko.push(f.numero+" : "+(e&&e.message?e.message:String(e))); }
    }
    await acCharger();
    if (valide) await load();
    setAcDraft(d=>{ const n={...d}; ok.forEach(x=>delete n[x]); return n; });
    setAcErreurs(ko); setAcBusy("");
    showToast(ok.length+" facture(s) enregistrée(s)"+(ko.length?" · "+ko.length+" problème(s)":(nbPay&&!valide?" — en attente de validation":" ✓")), ko.length===0);
  };
  const changerFactureVue = (v) => { setFactureVue(v); try { localStorage.setItem("az_fac_vue",v); } catch(e) {} };
  const [supMsgs, setSupMsgs] = useState([]);
  const [supPair, setSupPair] = useState(null);
  // Vue direction : toutes les discussions privées de l'équipe (lecture seule, ne marque rien comme lu).
  const loadSupervision = async () => {
    const { data, error } = await supabase.from("messages")
      .select("id,sender_nom,sender_matricule,destinataire_matricule,contenu,created_at")
      .not("destinataire_matricule","is",null).order("created_at",{ascending:true}).limit(3000);
    if (!error && data) setSupMsgs(data);
  };
  useEffect(()=>{ if (page==="chat_supervision") loadSupervision(); /* eslint-disable-next-line */ },[page]);
  const [activeChatThread, setActiveChatThread] = useState(null); // null = salon général, sinon matricule du collègue
  const [chatUnreadCounts, setChatUnreadCounts] = useState({});
  const [clientSearch, setClientSearch] = useState("");
  const [clientVue, setClientVue] = useState("fiches"); // fiches | suivi
  const [suiviCats, setSuiviCats] = useState([]);       // [] = toutes les catégories
  const [suiviVilles, setSuiviVilles] = useState([]);   // [] = toutes les villes
  const [suiviNom, setSuiviNom] = useState("");           // recherche par nom de client
  const [suiviDu, setSuiviDu] = useState("");
  const [suiviAu, setSuiviAu] = useState("");
  const [suiviSeulementDus, setSuiviSeulementDus] = useState(true);
  const [reportMonth, setReportMonth] = useState(today().slice(0,7));
  const [reportIncludeCreances, setReportIncludeCreances] = useState(false);
  const [productionDates, setProductionDates] = useState(()=>{
    const now=new Date();
    return {
      debut:localISODate(new Date(now.getFullYear(),now.getMonth(),1)),
      fin:today()
    };
  });
  const [productionSearch, setProductionSearch] = useState("");
  // ── Contrôle qualité par lot (fiche remplie par le producteur, en 2 étapes) ──
  const [fichesQualite, setFichesQualite] = useState([]);
  const [qcPhase, setQcPhase] = useState("start"); // "start" | "complete"
  const [qcFicheId, setQcFicheId] = useState(null);
  const [qcNumeroLot, setQcNumeroLot] = useState("");
  const [qcProduit, setQcProduit] = useState("");
  const [qcForm, setQcForm] = useState({ ...QC_EMPTY_FORM });
  const [qcConformites, setQcConformites] = useState({});
  const [qcSaving, setQcSaving] = useState(false);
  const [qcMessage, setQcMessage] = useState(null);
  const [qcGeneratingPdf, setQcGeneratingPdf] = useState(false);
  const [bulletinsPaie, setBulletinsPaie] = useState([]);
  const [codes, setCodes] = useState([]);
  const [codesMois, setCodesMois] = useState(today().slice(0,7));
  const [codeNouveau, setCodeNouveau] = useState({code:"",libelle:""});
  const [demandesSignature, setDemandesSignature] = useState([]);
  const [signatureRequestsView, setSignatureRequestsView] = useState(false);
  const [bulletinForm, setBulletinForm] = useState({ employeId:"", mois:today().slice(0,7), salaireBase:"", primes:"", heuresSup:"", calculerTaxes:true, autresRetenues:"", autresRetenuesLibelle:"" });
  const [bulletinSaving, setBulletinSaving] = useState(false);
  const [rentabiliteMois, setRentabiliteMois] = useState(today().slice(0,7));
  const [seuilInclureAmort, setSeuilInclureAmort] = useState(false);
  const [ingredientDate, setIngredientDate] = useState(today());
  const [ingredientRows, setIngredientRows] = useState([
    { id:1, nom:"Citron", montant:"", code:"1001" },
    { id:2, nom:"Gingembre", montant:"", code:"1002" },
    { id:3, nom:"Menthe", montant:"", code:"1003" },
    { id:4, nom:"Acide citrique", montant:"", code:"1006" },
  ]);
  const [ingredientSaving, setIngredientSaving] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [badgeBusy, setBadgeBusy] = useState(false);
  const [contratGenerating, setContratGenerating] = useState(false);
  const [procurationGenerating, setProcurationGenerating] = useState(false);
  const [factureStatutFilter, setFactureStatutFilter] = useState("tous"); // "tous" | "payee" | "attente"
  const [showCreancesClients, setShowCreancesClients] = useState(false);
  const [factureSearch, setFactureSearch] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [mobileChatConversationOpen, setMobileChatConversationOpen] = useState(false);
  const chatEndRef = useRef(null);
  const [lotPrint, setLotPrint]   = useState(null);
  const [lotEtiquette, setLotEtiquette] = useState(null);
  const [nbEtiquettes, setNbEtiquettes] = useState(10);
  const [receptionModal, setReceptionModal] = useState(null);
  const [receptionForm, setReceptionForm] = useState({etat:"Bon état", note:""});
  const [connexions, setConnexions] = useState([]);
  const [entreprise, setEntreprise] = useState({
    entreprise_nom: "Bralie Branding Food SARL",
    entreprise_adresse: "Quartier Centre Commercial, Face Paul Ladame",
    entreprise_ville: "Dschang, Région de lOuest, Cameroun",
    entreprise_tel: "+237 678 452 338",
    entreprise_email: "contact@braliebranding.com",
    entreprise_site: "www.braliebranding.com",
    entreprise_niu: "M01246370061B",
    entreprise_rc: "RC/DSC/2024/B/11",
    entreprise_rccm: "A compléter",
    entreprise_contribuable: "A compléter",
    tva_taux: "19.25",
    tva_active: "oui",
    facture_compteur: "6",
    proforma_compteur: "1",
    lot_compteur: "4",
    client_compteur: "6"
  });
  const [modal, setModal]         = useState(null);
  const [autoScanOpen, setAutoScanOpen] = useState(false);
  const [signatureFlow, setSignatureFlow] = useState(null);
  // Ouvre l'écran de signature tactile pour un ou plusieurs signataires avant
  // de générer un document. Se résout toujours avec un objet {clé: dataURL,...}
  // — jamais null — même si tous les signataires sont passés : l'appelant peut
  // donc toujours générer le PDF ensuite (signé, partiellement signé, ou vierge
  // comme avant si l'utilisateur choisit de signer plus tard à la main).
  const openSignatureFlow = (documentTitle, signers) => new Promise((resolve) => {
    setSignatureFlow({ documentTitle, signers, onComplete:(sigs)=>{ setSignatureFlow(null); resolve(sigs); } });
  });
  // Récupère une image (signature enregistrée sur une fiche) et la convertit en data URL
  // exploitable par jsPDF — que ce soit un chemin de stockage privé (bucket documents-employes)
  // ou une URL externe classique déjà présente dans le champ.
  const fetchSignatureDataUrl = async (raw) => {
    if (!raw) return null;
    try {
      let url = raw;
      if (!/^https?:\/\//i.test(raw) && !/^data:/i.test(raw)) {
        const { data, error } = await supabase.storage.from(EMPLOYE_DOCS_BUCKET).createSignedUrl(raw, 300);
        if (error || !data?.signedUrl) return null;
        url = data.signedUrl;
      } else if (/^data:/i.test(raw)) return raw;
      const resp = await fetch(url);
      if (!resp.ok) return null;
      const blob = await resp.blob();
      return await new Promise((resolve,reject)=>{
        const reader = new FileReader();
        reader.onloadend = ()=>resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  };
  // Mémorise le tracé d'une signature fraîchement dessinée sur la fiche du collaborateur, pour
  // qu'il n'ait plus jamais à la redessiner sur un prochain document. Pour la redessiner un jour
  // (ex. nouvelle signature), il suffit de vider le champ « URL de la signature » de sa fiche
  // (onglet Identité) — elle sera à nouveau demandée puis remémorisée automatiquement.
  const enregistrerSignatureFiche = async (employeId, dataUrl) => {
    try {
      const byteChars = atob(dataUrl.split(",").pop());
      const bytes = new Uint8Array(byteChars.length);
      for (let i=0;i<byteChars.length;i++) bytes[i]=byteChars.charCodeAt(i);
      const file = new Blob([bytes], { type:"image/png" });
      const path = `signatures/${employeId}.png`;
      await supabase.storage.from(EMPLOYE_DOCS_BUCKET).upload(path, file, { cacheControl:"3600", upsert:true, contentType:"image/png" });
      await supabase.from("employes").update({ signature_url: path }).eq("id", employeId);
    } catch(err) { console.warn("Signature non mémorisée pour la prochaine fois :", err); }
  };
  // Obtient la signature de chaque signataire : si la personne a déjà signé un document par le
  // passé, sa signature enregistrée est réutilisée directement (l'écran de signature n'apparaît
  // même pas) ; sinon l'écran de signature tactile s'ouvre pour elle, et son tracé est mémorisé
  // sur sa fiche pour la prochaine fois. `signers` doit inclure un `employeId` pour chaque
  // signataire — y compris la personne connectée elle-même (ex. le Mandant) — pour que la
  // mémorisation/réutilisation fonctionne.
  const obtenirSignatures = async (documentTitle, signers) => {
    const resolved = {};
    const aDessiner = [];
    for (const s of signers) {
      const emp = s.employeId ? employeeById(s.employeId) : null;
      const dataUrl = emp?.signature_url ? await fetchSignatureDataUrl(emp.signature_url) : null;
      if (dataUrl) resolved[s.key] = dataUrl;
      else aDessiner.push(s);
    }
    if (aDessiner.length) {
      const dessinees = await openSignatureFlow(documentTitle, aDessiner.map(s=>({key:s.key,label:s.label})));
      Object.assign(resolved, dessinees);
      aDessiner.forEach(s=>{ if (s.employeId && dessinees[s.key]) enregistrerSignatureFiche(s.employeId, dessinees[s.key]); });
    }
    return resolved;
  };
  // Notifie un collaborateur via le chat d'équipe déjà existant (même mécanisme que l'onglet Chat).
  const notifierSignatureRequise = async (matricule, texte) => {
    if (!matricule) return;
    try {
      await supabase.from("messages").insert({ sender_nom:session?.nom||"", sender_matricule:session?.matricule||"", destinataire_matricule:matricule, contenu:texte });
    } catch(err) { console.warn("Notification de signature non envoyée :", err); }
  };
  // Variante de openSignatureFlow qui, pour tout signataire identifié (employeId connu) mais
  // qui n'a pas signé en personne sur cet écran, enregistre une demande de signature à distance :
  // le collaborateur la retrouvera dans « Mes documents à signer » sur son propre téléphone/tablette,
  // et est prévenu par un message dans le chat d'équipe.
  const runSignatureFlow = async ({ titre, signers, typeDocument, payload }) => {
    const signatures = await obtenirSignatures(titre, signers);
    const manquants = signers.filter(s=>s.employeId && !signatures[s.key]);
    if (manquants.length) {
      try {
        const { error } = await supabase.from("demandes_signature").insert({
          type_document: typeDocument, titre, payload,
          signataires: manquants.map(s=>({key:s.key,label:s.label,employe_id:s.employeId})),
          demande_par: session?.nom||"", demande_par_matricule: session?.matricule||""
        });
        if (error) throw error;
        await Promise.all(manquants.map(s=>notifierSignatureRequise(s.matricule,"Un document attend votre signature : « "+titre+" ». Ouvrez « Mes documents à signer » dans votre profil pour signer.")));
        showToast("Signature demandée à "+manquants.map(s=>s.label).join(", ")+" ✓");
        await load();
      } catch(reqErr) {
        console.warn("Demande de signature non enregistrée :", reqErr);
        showToast("La demande de signature à distance n'a pas pu être enregistrée : "+reqErr.message,false);
      }
    }
    return signatures;
  };
  // Régénère le PDF final d'une demande de signature une fois complète (ou partiellement complète).
  const regenererPdfDemande = async (d, signatures) => {
    const p = d.payload||{};
    if (d.type_document==="fiche_remise") return generateFicheRemisePdfBase64(p.item, employes.find(x=>String(x.id)===String(p.employeId)), entreprise, signatures);
    if (d.type_document==="contrat") return generateContratTravailPdfBase64(p.data, employes.find(x=>String(x.id)===String(p.employeId)), entreprise, signatures);
    if (d.type_document==="procuration") return generateProcurationPdfBase64(p.data, employes.find(x=>String(x.id)===String(p.employeId)), entreprise, signatures);
    return null;
  };
  // Dépose un document déjà entièrement signé dans le dossier RH du collaborateur.
  // Utilisée à la fois quand toutes les signatures sont capturées en personne au
  // moment de la génération, et quand la dernière signature arrive plus tard via
  // une demande à distance (voir signerDemandeEnAttente) — dans les deux cas le
  // document ne doit être déposé/validé qu'une fois toutes les signatures réunies.
  const deposerDocumentSigne = async ({ employeId, employeNom, typeDocument, titre, pdf, dateDocument, dateExpiration, categorieHistorique, champHistorique, valeurHistorique }) => {
    const byteChars = atob(pdf.base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i=0;i<byteChars.length;i++) bytes[i]=byteChars.charCodeAt(i);
    const file = new Blob([bytes], { type:"application/pdf" });
    const path = `${employeId}/${Date.now()}_${pdf.filename.replace(/[^a-zA-Z0-9._-]/g,"_")}`;
    const { error: uploadError } = await supabase.storage.from(EMPLOYE_DOCS_BUCKET).upload(path, file, {
      cacheControl:"3600", upsert:false, contentType:"application/pdf"
    });
    if (uploadError) throw new Error("Envoi du PDF impossible : "+uploadError.message);
    const { error: insertError } = await supabase.from("documents_employes").insert({
      employe_id: String(employeId), employe_nom: employeNom,
      type_document: typeDocument, titre,
      fichier_url: path, nom_fichier: pdf.filename, mime_type:"application/pdf",
      taille_octets: file.size, date_document: dateDocument||today(),
      date_expiration: dateExpiration||null, confidentiel:true,
      statut: "Validé",
      ajoute_par_id: String(session.id||session.matricule||""), ajoute_par_nom: session.nom||""
    });
    if (insertError) {
      await supabase.storage.from(EMPLOYE_DOCS_BUCKET).remove([path]);
      throw new Error(insertError.message);
    }
    if (categorieHistorique) {
      await supabase.from("historique_employes").insert({
        employe_id:String(employeId), employe_nom:employeNom, categorie:categorieHistorique,
        champ:champHistorique||"Document généré", ancienne_valeur:"",
        nouvelle_valeur: valeurHistorique||titre, motif:"Généré depuis la fiche RH",
        effectue_par_id:String(session.id||session.matricule||""), effectue_par_nom:session.nom||"",
        ville: employeeById(employeId)?.ville||""
      });
    }
    return pdf;
  };
  // Demandes de signature où JE suis un signataire attendu et n'ai pas encore signé.
  const mesDemandesSignature = demandesSignature.filter(d=>
    d.statut!=="signe" && (d.signataires||[]).some(s=>String(s.employe_id)===String(session?.id) && !(d.signatures||{})[s.key])
  );
  // J'ouvre l'écran de signature pour ma/mes ligne(s) sur une demande reçue, puis on enregistre
  // ma signature ; si tout le monde a désormais signé, le PDF final est régénéré avec toutes les
  // signatures et la personne qui a fait la demande est prévenue.
  const signerDemandeEnAttente = async (d) => {
    const mesSlots = (d.signataires||[]).filter(s=>String(s.employe_id)===String(session?.id) && !(d.signatures||{})[s.key]);
    if (!mesSlots.length) return;
    const nouvelles = await openSignatureFlow(d.titre, mesSlots.map(s=>({key:s.key,label:s.label})));
    const signaturesFusionnees = { ...(d.signatures||{}), ...nouvelles };
    const restants = (d.signataires||[]).filter(s=>!signaturesFusionnees[s.key]);
    const statut = restants.length ? "en_attente" : "signe";
    try {
      const { error } = await supabase.from("demandes_signature").update({ signatures:signaturesFusionnees, statut, updated_at:new Date().toISOString() }).eq("id", d.id);
      if (error) throw error;
      if (statut==="signe") {
        try {
          // La signature de l'autre partie (ex. le Mandant) capturée en personne au
          // moment de la création n'est jamais perdue : elle est conservée dans
          // payload.mandantSignature en attendant que ce signataire distant termine.
          const mandantDejaCapture = d.payload?.mandantSignature;
          const signaturesCompletes = mandantDejaCapture ? { mandant: mandantDejaCapture, ...signaturesFusionnees } : signaturesFusionnees;
          const pdf = await regenererPdfDemande(d, signaturesCompletes);
          if (pdf) {
            await openPdf(pdf, d.titre);
            // Pour une procuration, le document n'était pas encore déposé dans le
            // dossier RH tant qu'il manquait une signature — on le dépose maintenant
            // qu'il est complet, avec le statut « Validé ».
            if (d.type_document==="procuration" && d.payload?.employeId) {
              const emp = employeeById(d.payload.employeId);
              const p = d.payload?.data||{};
              await deposerDocumentSigne({
                employeId: d.payload.employeId, employeNom: emp?.nom||"",
                typeDocument: "Procuration", titre: d.titre,
                pdf, dateDocument: p.date_debut, dateExpiration: p.date_fin,
                categorieHistorique: "Procuration", champHistorique: "Procuration accordée",
                valeurHistorique: (p.objet||"").slice(0,120)+" ("+fmtDate(p.date_debut)+" → "+fmtDate(p.date_fin)+")"
              });
            }
          }
        } catch(pdfErr) { console.warn("PDF signé non régénéré :", pdfErr); showToast("Signature enregistrée, mais le dépôt du document final a échoué : "+pdfErr.message,false); }
        if (d.demande_par_matricule) await notifierSignatureRequise(d.demande_par_matricule, "« "+d.titre+" » a été signé par "+(session?.nom||"un collaborateur")+".");
        showToast("Document signé ✓");
      } else {
        showToast("Votre signature a été enregistrée ✓");
      }
      setSignatureRequestsView(false);
      await load();
    } catch(err) { showToast("Erreur : "+err.message,false); }
  };
  const [myDocForm, setMyDocForm] = useState({ type_document:"", titre:"", file:null });
  const [myDocSaving, setMyDocSaving] = useState(false);
  const [paiementsFactureView, setPaiementsFactureView] = useState(null);
  const [declarationsVentes, setDeclarationsVentes] = useState([]);
  const [mouvementsStockLog, setMouvementsStockLog] = useState([]);
  const [showMvtJournal, setShowMvtJournal] = useState(false);
  const [mvtFiltre, setMvtFiltre] = useState({ville:"",sens:""});
  const [editLivraisonMode, setEditLivraisonMode] = useState(false);
  const [editLivraisonLignes, setEditLivraisonLignes] = useState([]);
  const [editLivraisonSaving, setEditLivraisonSaving] = useState(false);
  const [creatingAuthAccount, setCreatingAuthAccount] = useState(false);
  const [deletingEmploye, setDeletingEmploye] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [accesView, setAccesView] = useState(null);
  const [updatingGestionComptes, setUpdatingGestionComptes] = useState(false);
  const [prospections, setProspections] = useState([]);
  const [prospectionForm, setProspectionForm] = useState({ ville:"", produit:"", quantite:"", prospectNom:"", notes:"" });
  const [prospectionSaving, setProspectionSaving] = useState(false);
  const [declForm, setDeclForm] = useState({ quantite:"", note:"" });
  const [declSaving, setDeclSaving] = useState(false);
  const [form, setForm]           = useState({});
  const [toast, setToast]         = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [notifOuvert, setNotifOuvert] = useState(false);
  const [maCivilite, setMaCivilite] = useState(""); // "Mr", "Mme" ou "" (non renseigné)
  // ── Journal d'activité (traces) — visible uniquement par la direction ──
  const [journalRows, setJournalRows] = useState([]);
  const [journalTotal, setJournalTotal] = useState(0);
  const [journalResume, setJournalResume] = useState([]);
  const [journalBusy, setJournalBusy] = useState(false);
  const [journalErr, setJournalErr] = useState("");
  const [journalDu, setJournalDu] = useState(()=>localISODate(new Date(Date.now()-6*86400000)));
  const [journalAu, setJournalAu] = useState(()=>localISODate(new Date()));
  const [journalEmp, setJournalEmp] = useState("");
  const [journalAction, setJournalAction] = useState("");
  const [journalObjet, setJournalObjet] = useState("");
  const [journalQ, setJournalQ] = useState("");
  const [journalOuvert, setJournalOuvert] = useState(null);
  const [journalLimite, setJournalLimite] = useState(100);
  const [monActivite, setMonActivite] = useState(null);   // {nom, factures, ventes} — uniquement MES données
  const [monActiviteBusy, setMonActiviteBusy] = useState(false);
  const [monActiviteErr, setMonActiviteErr] = useState("");
  const [monDu, setMonDu] = useState("");
  const [monAu, setMonAu] = useState("");
  const [pushEtat, setPushEtat] = useState("verif"); // verif | non_supporte | a_activer | actif | refuse
  const [pushBusy, setPushBusy] = useState(false);
  const [alertesPref, setAlertesPref] = useState({messages:true,livraisons:true});
  const [bandeauAlertesOff, setBandeauAlertesOff] = useState(()=>{
    try { const t = parseInt(localStorage.getItem("az_bandeau_alertes")||"0",10); return !!t && (Date.now()-t) < 7*24*3600*1000; } catch(_) { return false; }
  });
  const pageNotifRef = useRef(""); pageNotifRef.current = page;
  const [pushMsg, setPushMsg] = useState("");
  const [profilPhotoBusy, setProfilPhotoBusy] = useState(false);
  const [profilPhotoMsg, setProfilPhotoMsg]   = useState(null);
  const [facView, setFacView]     = useState(null);
  const [proformaView, setProformaView] = useState(null);
  const [editFac, setEditFac]     = useState(null);
  const [editNewLigne, setEditNewLigne] = useState({ produit:"", qte:"", prixUnit:"" });
  const [loading, setLoading]     = useState(false);
  const [resettingApp, setResettingApp] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ ancienPwd:"", nouveauPwd:"", confirmPwd:"" });

  // ── État facForm avec moyen de règlement ──
  const [facForm, setFacForm] = useState({
    clientNom:"", clientCode:"", dateEmission:today(),
    dateEcheance:addDaysISO(today(),30),
    note:"", statut:"En attente", lignes:[], remisePct:0,
    appliquerTVA:false,
    reglement:"", ville:""
  });
  // Correctif du 05/10/2026 : quel que soit le chemin (liste déroulante, bouton « Facturer » de la
  // fiche client...), dès qu'un client est choisi sur la nouvelle facture, la ville de stock devient
  // celle du client si un dépôt existe dans cette ville.
  useEffect(() => {
    if (!facForm.clientNom) return;
    const cli = (facForm.clientCode && clients.find(c=>c.code===facForm.clientCode)) || clients.find(c=>c.nom===facForm.clientNom);
    const vc = (cli?.ville||"").trim();
    if (vc && depots.some(d=>(d.ville||"").trim()===vc) && facForm.ville!==vc) {
      setFacForm(f=>({...f,ville:vc}));
    }
    // eslint-disable-next-line
  }, [facForm.clientNom, facForm.clientCode, clients, depots]);
  const [newLigne, setNewLigne]   = useState({ produit:"", qte:"", prixUnit:"" });
  const [proformaForm, setProformaForm] = useState({ clientNom:"", clientCode:"", dateEmission:today(), dateEcheance:"", note:"", lignes:[], remisePct:0, appliquerTVA:false });
  const [newProformaLigne, setNewProformaLigne] = useState({ produit:"", qte:"", prixUnit:"" });
  const [rapportDates, setRapportDates] = useState({ debut:"", fin:"" });

  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };
  // ── Signature par téléphone (QR code) des lettres et documents ──
  const [sigModal, setSigModal] = useState(null);
  const sigOuvrir = () => {
    if (!String(docForm.corps||"").trim()) return showToast("Écrivez d'abord le texte du document.", false);
    setSigModal({ etape:"choix", mode:"moi", erreur:"" });
  };
  const sigCreer = async (mode) => {
    setSigModal(m=>({ ...(m||{}), etape:"creation", mode, erreur:"" }));
    const moi = mode==="moi";
    const emp = empreinteDoc(docForm);
    const ligne = {
      cree_par:session?.nom||"", type_document:docForm.type||"Document", destinataire:docForm.clientNom||"",
      objet:docForm.objet||"", doc_date:docForm.date||null, corps:docForm.corps||"", empreinte:emp,
      signataire_nom: moi?(session?.nom||""):null, signataire_fonction: moi?(session?.poste||"Direction"):null
    };
    const { data, error } = await supabase.from("signatures_documents").insert(ligne).select("id,token,expire_at").single();
    if (error || !data) { setSigModal({ etape:"choix", mode, erreur:"Impossible de créer le QR code : "+(error?.message||"erreur inconnue") }); return; }
    setSigModal({ etape:"attente", mode, moi, id:data.id, token:data.token, expire:data.expire_at, empreinte:emp, copie:false });
  };
  const sigFermer = async () => {
    const m = sigModal; setSigModal(null);
    if (m && m.etape==="attente" && m.id) { try { await supabase.from("signatures_documents").delete().eq("id",m.id).eq("statut","en_attente"); } catch(_) {} }
  };
  const sigRetirer = () => setDocForm(f=>{ const { signature, ...reste } = f; return reste; });
  // ── Archivage des documents signés (PDF dans un espace privé) + page « Documents signés » ──
  const [docOnglet, setDocOnglet] = useState("rediger");
  const [sigListe, setSigListe] = useState([]);
  const [sigListeEtat, setSigListeEtat] = useState("");
  const [sigRech, setSigRech] = useState("");
  const [sigTypeFiltre, setSigTypeFiltre] = useState("");
  const [sigArchEnCours, setSigArchEnCours] = useState(0);
  const sigEssayes = useRef({});
  const sigDocForm = (row) => ({
    type:row.type_document, clientNom:row.destinataire||"", objet:row.objet||"", date:row.doc_date||"", corps:row.corps||"",
    signature:{ png:row.signature_png, nom:row.nom_signe, fonction:row.fonction_signee||"", signe_le:row.signe_le, token:row.token, empreinte:row.empreinte, moi:!!row.signataire_nom }
  });
  const sigNomFichier = (row) => (String(row.type_document||"Document").replace(/\s+/g,"_")+"_"+String(row.destinataire||row.nom_signe||"").replace(/[^A-Za-z0-9]+/g,"_").slice(0,30)+"_"+String(row.signe_le||"").slice(0,10)+".pdf").replace(/_+/g,"_");
  const sigArchiver = async (row) => {
    const d = sigDocForm(row);
    if (!sigValide(d)) throw new Error("empreinte différente : PDF non archivé");
    const pdf = await generateDocumentPdfBase64(d, entreprise, session);
    const bin = atob(pdf.base64); const octets = new Uint8Array(bin.length);
    for (let i=0;i<bin.length;i++) octets[i] = bin.charCodeAt(i);
    const chemin = String(row.signe_le||"").slice(0,4)+"/"+row.token+".pdf";
    const { error } = await supabase.storage.from("documents-signes").upload(chemin, new Blob([octets],{type:"application/pdf"}), { contentType:"application/pdf", upsert:false });
    if (error && !/exist|duplicate/i.test(String(error.message||""))) throw error;
    const { error:e2 } = await supabase.from("signatures_documents").update({ pdf_path:chemin, archive_le:new Date().toISOString() }).eq("id",row.id);
    if (e2) throw e2;
    return chemin;
  };
  const sigCharger = async () => {
    setSigListeEtat("chargement");
    const { data, error } = await supabase.from("signatures_documents")
      .select("id,token,type_document,destinataire,objet,doc_date,nom_signe,fonction_signee,signe_le,pdf_path,cree_par,signataire_nom")
      .eq("statut","signe").order("signe_le",{ascending:false}).limit(300);
    if (error) { setSigListeEtat("erreur"); return; }
    setSigListe(data||[]); setSigListeEtat("ok");
  };
  const sigRowComplet = async (id) => {
    const { data, error } = await supabase.from("signatures_documents").select("*").eq("id",id).single();
    if (error || !data) throw new Error(error?.message||"document introuvable");
    return data;
  };
  const sigOuvrirArchive = async (row) => {
    try {
      let base64 = null;
      if (row.pdf_path) {
        const { data, error } = await supabase.storage.from("documents-signes").download(row.pdf_path);
        if (!error && data) {
          base64 = await new Promise((res,rej)=>{ const r=new FileReader(); r.onload=()=>res(String(r.result).split(",").pop()); r.onerror=rej; r.readAsDataURL(data); });
        }
      }
      if (!base64) {
        const full = await sigRowComplet(row.id);
        base64 = (await generateDocumentPdfBase64(sigDocForm(full), entreprise, session)).base64;
      }
      await openPdf({ base64, filename:sigNomFichier(row) }, row.type_document||"Document signé");
    } catch(e) { showToast("Impossible d'ouvrir le document : "+(e?.message||"erreur"), false); }
  };
  const sigEnvoyerArchive = async (row) => {
    try {
      const full = await sigRowComplet(row.id);
      const cli = clients.find(c=>c.nom===row.destinataire);
      setDocSendModal({ kind:"document", data:sigDocForm(full) });
      setDocSendForm({ to:cli?.email||"", toName:row.destinataire||"", subject:(row.type_document||"Document")+" — "+entreprise.entreprise_nom, message:"Veuillez trouver ci-joint le document signé." });
    } catch(e) { showToast("Erreur : "+(e?.message||"inconnue"), false); }
  };
  const sigArchiverMaintenant = async (row) => {
    try { setSigArchEnCours(n=>n+1); const full = await sigRowComplet(row.id); await sigArchiver(full); await sigCharger(); showToast("Document archivé ✓"); }
    catch(e) { showToast("Archivage impossible : "+(e?.message||"erreur"), false); }
    finally { setSigArchEnCours(n=>Math.max(0,n-1)); }
  };
  useEffect(()=>{ if (page==="documents" && docOnglet==="signes") sigCharger(); /* eslint-disable-next-line */ },[page,docOnglet]);
  // Rattrapage : les documents signés dont le PDF n'est pas encore rangé (signature arrivée après la fermeture de l'écran)
  useEffect(()=>{
    if (!(page==="documents" && docOnglet==="signes") || sigListeEtat!=="ok") return;
    const aFaire = sigListe.filter(r=>!r.pdf_path && !sigEssayes.current[r.id]).slice(0,12);
    if (!aFaire.length) return;
    let annule = false;
    (async()=>{
      setSigArchEnCours(aFaire.length);
      for (const r of aFaire) {
        if (annule) break;
        sigEssayes.current[r.id] = true;
        try { const full = await sigRowComplet(r.id); await sigArchiver(full); } catch(_) {}
        setSigArchEnCours(n=>Math.max(0,n-1));
      }
      if (!annule) sigCharger();
    })();
    return ()=>{ annule = true; };
    // eslint-disable-next-line
  },[sigListeEtat, sigListe.length, page, docOnglet]);
  useEffect(()=>{
    if (!sigModal || sigModal.etape!=="attente") return;
    let fini = false;
    const verifier = async () => {
      try {
        const { data } = await supabase.from("signatures_documents").select("statut,nom_signe,fonction_signee,signature_png,signe_le").eq("id",sigModal.id).maybeSingle();
        if (fini || !data) return;
        if (data.statut==="signe" && data.signature_png) {
          fini = true;
          setDocForm(f=> empreinteDoc(f)===sigModal.empreinte
            ? { ...f, signature:{ png:data.signature_png, nom:data.nom_signe, fonction:data.fonction_signee||"", signe_le:data.signe_le, token:sigModal.token, empreinte:sigModal.empreinte, moi:!!sigModal.moi } }
            : f);
          setSigModal(m=>m?{ ...m, etape:"fait", nom:data.nom_signe }:m);
          (async()=>{ try { const full = await sigRowComplet(sigModal.id); await sigArchiver(full); showToast("Document signé rangé dans « Documents signés » ✓"); } catch(_) { /* repris automatiquement depuis l'onglet Documents signés */ } })();
          return;
        }
        if (sigModal.expire && Date.now() > new Date(sigModal.expire).getTime()) { fini = true; setSigModal(m=>m?{ ...m, etape:"expire" }:m); }
      } catch(_) {}
    };
    const t = setInterval(verifier, 2500);
    const vis = () => { if (document.visibilityState==="visible") verifier(); };
    document.addEventListener("visibilitychange", vis);
    verifier();
    return () => { fini = true; clearInterval(t); document.removeEventListener("visibilitychange", vis); };
    // eslint-disable-next-line
  },[sigModal && sigModal.etape, sigModal && sigModal.id]);
  // Photo de profil de la personne connectée (lue dans la liste des employés).
  const maPhotoUrl = () => ((employes.find(e=>String(e.id)===String(session?.id))||{}).photo_url||"");
  const maPhotoRond = (taille) => maPhotoUrl() ? <img alt="" src={maPhotoUrl()} style={{width:taille,height:taille,borderRadius:"50%",objectFit:"cover",objectPosition:"center top",border:"2px solid #28506F",flexShrink:0}}/> : null;
  const sessionPermissions = () => {
    const parsed = safeJsonParse(session?.permissions, []);
    return Array.isArray(parsed) ? parsed : [];
  };
  const can = (moduleId) => sessionPermissions().includes(moduleId);
  const isDirecteur = () => estRoleDirecteur(session?.role, session?.poste);
  // Réservé au propriétaire : directeur ET compte Nouboubo (affichage seulement ; les données
  // elles-mêmes restent régies par les permissions habituelles).
  const estProprietaire = () => isDirecteur() && /nouboubo/i.test(String(session?.nom||""));
  const normalizeVille = value => String(value||"").trim().toLocaleLowerCase("fr-FR");
  const canSeeAllCities = () => isDirecteur() || can("voir_toutes_villes");
  const sessionVille = () => String(session?.ville||"").trim();
  const isDschangUser = () => normalizeVille(sessionVille())==="dschang";
  const accessScopeLabel = () => canSeeAllCities() ? "Ensemble des villes" : (sessionVille()||"Ville non attribuée");
  const canValidatePayments = () => isDirecteur() || can("valider_paiements");
  const canDeleteInvoices = () => isDirecteur() || can("supprimer_factures");
  const canEdit = () => isDirecteur() || can("peut_modifier");
  const canTransfertVilles = () => isDirecteur() || can("transferer_villes");
  // Journal d'activité : enregistre qu'un employé a OUVERT une page, une fiche ou un document.
  // Ne bloque jamais l'application (erreurs ignorées) et évite les doublons à moins de 20 s.
  const dernieresVues = useRef({});
  const journaliserVue = (objet, reference="", detail="") => {
    try {
      if (!session) return;
      const cle = objet+"|"+reference;
      const maintenant = Date.now();
      if (dernieresVues.current[cle] && maintenant-dernieresVues.current[cle] < 20000) return;
      dernieresVues.current[cle] = maintenant;
      supabase.rpc("journaliser_consultation",{ p_objet:String(objet||"").slice(0,80), p_reference:String(reference||"").slice(0,200), p_detail:String(detail||"").slice(0,400) }).then(()=>{},()=>{});
    } catch(_) {}
  };
  const openModal = (t, item={}) => {
    if (item && (item.id||item.numero||item.nom)) journaliserVue("Fiche ouverte ("+t+")", item.numero||item.nom||item.client_nom||String(item.id||"").slice(0,8));
    setModal(t); setForm({...item});
  };
  const closeModal = () => { setModal(null); setForm({}); };

  // ── Codification : tout achat, dépense et salaire porte un code à 4 chiffres ──
  const loadCodes = async () => {
    try { const { data } = await supabase.from("codes_comptables").select("*").order("code"); if (data) setCodes(data); } catch(_) {}
  };
  useEffect(()=>{ if (session) loadCodes(); /* eslint-disable-next-line */ },[session?.id]);
  const codeInfo = (c) => codes.find(x=>x.code===c) || null;
  const codeLabel = (c) => { const i=codeInfo(c); return i ? i.code+" · "+i.libelle : (c||"—"); };
  const codesPourSaisie = codes.filter(c=>c.actif && !String(c.code).startsWith("7"));
  const codesAchat = codes.filter(c=>c.actif && /^[12]/.test(String(c.code)));
  const groupesCodes = (liste) => { const m=[]; liste.forEach(c=>{ let g=m.find(x=>x.famille===c.famille); if(!g){ g={famille:c.famille,items:[]}; m.push(g); } g.items.push(c); }); return m; };
  const dernierPrixCode = (code) => {
    const tri = achatsCommandes.slice().sort((a,b)=>String(b.date_commande||"").localeCompare(String(a.date_commande||"")));
    for (const cmd of tri) {
      const lignes = Array.isArray(cmd.lignes) ? cmd.lignes : [];
      const l = lignes.find(x=>x && x.code===code && toFiniteNumber(x.prix_unitaire)>0);
      if (l) return toFiniteNumber(l.prix_unitaire);
    }
    return 0;
  };
  const ajouterCode = async () => {
    const code = String(codeNouveau.code||"").trim(); const libelle = String(codeNouveau.libelle||"").trim();
    if (!/^[0-9]{4}$/.test(code)) return showToast("Le code doit avoir exactement 4 chiffres",false);
    if (!libelle) return showToast("Indiquez le libellé du code",false);
    if (codes.some(c=>c.code===code)) return showToast("Ce code existe déjà",false);
    const voisin = codes.find(c=>String(c.code)[0]===code[0]);
    if (!voisin) return showToast("Le premier chiffre doit être 1 à 7 (une famille existante)",false);
    const { error } = await supabase.from("codes_comptables").insert({ code, libelle, famille:voisin.famille, categorie:voisin.categorie });
    if (error) return showToast("Erreur : "+error.message,false);
    setCodeNouveau({code:"",libelle:""}); await loadCodes(); showToast("Code "+code+" ajouté ✓");
  };
  const renommerCode = async (c) => {
    const l = window.prompt("Nouveau libellé du code "+c.code, c.libelle);
    if (l===null || !l.trim() || l.trim()===c.libelle) return;
    const { error } = await supabase.from("codes_comptables").update({ libelle:l.trim() }).eq("code",c.code);
    if (error) return showToast("Erreur : "+error.message,false);
    await loadCodes(); showToast("Code renommé ✓");
  };
  const basculerCode = async (c) => {
    const { error } = await supabase.from("codes_comptables").update({ actif:!c.actif }).eq("code",c.code);
    if (error) return showToast("Erreur : "+error.message,false);
    await loadCodes();
  };
  // Lien de paiement d'une facture : la direction peut le désactiver (le QR code n'ouvre plus la page)
  const toggleLienPaiement = async (f) => {
    const activer = f.lien_actif===false;
    if (!activer && !window.confirm("Désactiver le lien de paiement de la facture "+f.numero+" ?\n\nLe QR code n'ouvrira plus la page de paiement et le client ne pourra plus envoyer de preuve. Vous pourrez le réactiver à tout moment.")) return;
    const { error } = await supabase.from("factures").update({ lien_actif:activer }).eq("id",f.id);
    if (error) return showToast("Erreur : "+error.message,false);
    setFacView(v=>v&&v.facture&&v.facture.id===f.id?{...v,facture:{...v.facture,lien_actif:activer}}:v);
    await load();
    showToast(activer?"Lien de paiement réactivé ✓":"Lien de paiement désactivé ✓");
  };


  // Villes disponibles (02/10/2026) — liste gérée par la direction depuis Paramètres, au lieu
  // d'une liste figée dans le code (clients, fournisseurs, immobilisations). Stockée comme les
  // autres réglages simples, dans la table "parametres" (cle="villes_disponibles", valeur=JSON).
  // Ne concerne pas la ville d'une commande d'achat ni la "Ville d'affectation" d'un employé :
  // ces deux-là restent dérivées des dépôts réels (elles impliquent un dépôt/stock physique,
  // pas juste une étiquette descriptive) — ajouter une ville là se fait en créant un dépôt.
  const VILLES_BASE = ["Douala","Yaoundé","Bafoussam","Dschang","Bamenda","Buea","Garoua","Ngaoundéré","Kribi"];
  const villesDisponibles = (() => {
    const l = safeJsonParse(entreprise.villes_disponibles, null);
    return Array.isArray(l) && l.length ? l : VILLES_BASE;
  })();
  const ajouterVille = async (nomBrut) => {
    const nom = String(nomBrut||"").trim();
    if (!nom) return;
    if (villesDisponibles.some(v=>normalizeVille(v)===normalizeVille(nom))) {
      showToast("Cette ville est déjà dans la liste", false);
      return;
    }
    const next = [...villesDisponibles, nom].sort((a,b)=>a.localeCompare(b,"fr"));
    const valeur = JSON.stringify(next);
    const { data:ex } = await supabase.from("parametres").select("id").eq("cle","villes_disponibles").maybeSingle();
    const { error } = ex
      ? await supabase.from("parametres").update({valeur}).eq("cle","villes_disponibles")
      : await supabase.from("parametres").insert({cle:"villes_disponibles", valeur});
    if (error) { showToast("Erreur : "+error.message, false); return; }
    setEntreprise(prev=>({...prev, villes_disponibles:valeur}));
    showToast("Ville « "+nom+" » ajoutée ✓");
  };
  const retirerVille = async (nom) => {
    if (!window.confirm("Retirer « "+nom+" » de la liste des villes ?\n\nLes fiches qui l'utilisent déjà ne seront pas modifiées — elle disparaît seulement des listes proposées pour une nouvelle fiche.")) return;
    const next = villesDisponibles.filter(v=>v!==nom);
    const valeur = JSON.stringify(next);
    const { error } = await supabase.from("parametres").update({valeur}).eq("cle","villes_disponibles");
    if (error) { showToast("Erreur : "+error.message, false); return; }
    setEntreprise(prev=>({...prev, villes_disponibles:valeur}));
    showToast("Ville retirée");
  };

  // Pays disponibles (02/10/2026) — même mécanisme que les villes disponibles ci-dessus, pour
  // les fiches client, fournisseur et immobilisation qui le souhaitent (utile pour un contact ou
  // un équipement hors Cameroun).
  // Liste étendue le 05/10/2026 : les 54 pays d'Afrique sont désormais proposés par défaut (plus
  // besoin de les ajouter un par un) ; la direction peut toujours en retirer ou en ajouter
  // d'autres (hors Afrique, par exemple) depuis Paramètres.
  const PAYS_BASE = ["Cameroun","Afrique du Sud","Algérie","Angola","Bénin","Botswana","Burkina Faso","Burundi",
    "Cap-Vert","Comores","Congo-Brazzaville","Côte d'Ivoire","Djibouti","Égypte","Érythrée","Eswatini","Éthiopie",
    "Gabon","Gambie","Ghana","Guinée","Guinée équatoriale","Guinée-Bissau","Kenya","Lesotho","Liberia","Libye",
    "Madagascar","Malawi","Mali","Maroc","Maurice","Mauritanie","Mozambique","Namibie","Niger","Nigéria","Ouganda",
    "République centrafricaine","République démocratique du Congo","Rwanda","Sao Tomé-et-Principe","Sénégal",
    "Seychelles","Sierra Leone","Somalie","Soudan","Soudan du Sud","Tanzanie","Tchad","Togo","Tunisie","Zambie",
    "Zimbabwe"];
  // Villes principales par pays (05/10/2026) — capitale puis grandes villes, pour la liste
  // déroulante "Ville" des fiches client/fournisseur/immobilisation une fois un pays choisi.
  // Le Cameroun n'y figure pas : il utilise déjà sa propre liste "Villes disponibles" ci-dessus
  // (liée aux dépôts réels). Modifiable depuis Paramètres, comme le reste.
  const VILLES_PAR_PAYS_BASE = {
    "Afrique du Sud":["Pretoria","Johannesburg","Le Cap","Durban","Gqeberha"],
    "Algérie":["Alger","Oran","Constantine"],
    "Angola":["Luanda","Benguela"],
    "Bénin":["Porto-Novo","Cotonou","Abomey-Calavi"],
    "Botswana":["Gaborone","Francistown"],
    "Burkina Faso":["Ouagadougou","Bobo-Dioulasso"],
    "Burundi":["Gitega","Bujumbura"],
    "Cap-Vert":["Praia"],
    "Comores":["Moroni"],
    "Congo-Brazzaville":["Brazzaville","Pointe-Noire"],
    "Côte d'Ivoire":["Yamoussoukro","Abidjan","Bouaké"],
    "Djibouti":["Djibouti"],
    "Égypte":["Le Caire","Alexandrie","Gizeh","Port-Saïd"],
    "Érythrée":["Asmara"],
    "Eswatini":["Mbabane","Lobamba"],
    "Éthiopie":["Addis-Abeba","Dire Dawa"],
    "Gabon":["Libreville","Port-Gentil"],
    "Gambie":["Banjul"],
    "Ghana":["Accra","Kumasi"],
    "Guinée":["Conakry"],
    "Guinée équatoriale":["Ciudad de la Paz","Malabo","Bata"],
    "Guinée-Bissau":["Bissau"],
    "Kenya":["Nairobi","Mombasa","Kisumu"],
    "Lesotho":["Maseru"],
    "Liberia":["Monrovia"],
    "Libye":["Tripoli","Benghazi","Misrata"],
    "Madagascar":["Antananarivo","Toamasina"],
    "Malawi":["Lilongwe","Blantyre"],
    "Mali":["Bamako"],
    "Maroc":["Rabat","Casablanca","Marrakech","Fès","Tanger","Agadir"],
    "Maurice":["Port-Louis"],
    "Mauritanie":["Nouakchott"],
    "Mozambique":["Maputo","Matola","Nampula"],
    "Namibie":["Windhoek","Walvis Bay"],
    "Niger":["Niamey"],
    "Nigéria":["Abuja","Lagos","Kano","Ibadan","Port Harcourt"],
    "Ouganda":["Kampala","Jinja"],
    "République centrafricaine":["Bangui"],
    "République démocratique du Congo":["Kinshasa","Lubumbashi","Mbuji-Mayi","Kananga","Kisangani"],
    "Rwanda":["Kigali"],
    "Sao Tomé-et-Principe":["Sao Tomé"],
    "Sénégal":["Dakar","Touba"],
    "Seychelles":["Victoria"],
    "Sierra Leone":["Freetown"],
    "Somalie":["Mogadiscio","Hargeisa"],
    "Soudan":["Khartoum","Omdurman"],
    "Soudan du Sud":["Djouba"],
    "Tanzanie":["Dodoma","Dar es Salaam","Mwanza","Arusha"],
    "Tchad":["N'Djamena"],
    "Togo":["Lomé"],
    "Tunisie":["Tunis","Sfax","Sousse"],
    "Zambie":["Lusaka","Ndola"],
    "Zimbabwe":["Harare","Bulawayo"]
  };
  const paysDisponibles = (() => {
    const l = safeJsonParse(entreprise.pays_disponibles, null);
    return Array.isArray(l) && l.length ? l : PAYS_BASE;
  })();
  const ajouterPays = async (nomBrut) => {
    const nom = String(nomBrut||"").trim();
    if (!nom) return;
    if (paysDisponibles.some(p=>normalizeVille(p)===normalizeVille(nom))) {
      showToast("Ce pays est déjà dans la liste", false);
      return;
    }
    const next = [...paysDisponibles, nom].sort((a,b)=>a.localeCompare(b,"fr"));
    const valeur = JSON.stringify(next);
    const { data:ex } = await supabase.from("parametres").select("id").eq("cle","pays_disponibles").maybeSingle();
    const { error } = ex
      ? await supabase.from("parametres").update({valeur}).eq("cle","pays_disponibles")
      : await supabase.from("parametres").insert({cle:"pays_disponibles", valeur});
    if (error) { showToast("Erreur : "+error.message, false); return; }
    setEntreprise(prev=>({...prev, pays_disponibles:valeur}));
    showToast("Pays « "+nom+" » ajouté ✓");
  };
  const retirerPays = async (nom) => {
    if (nom==="Cameroun") { showToast("Impossible de retirer Cameroun, c'est la valeur par défaut", false); return; }
    if (!window.confirm("Retirer « "+nom+" » de la liste des pays ?\n\nLes fiches qui l'utilisent déjà ne seront pas modifiées — il disparaît seulement des listes proposées pour une nouvelle fiche.")) return;
    const next = paysDisponibles.filter(p=>p!==nom);
    const valeur = JSON.stringify(next);
    const { error } = await supabase.from("parametres").update({valeur}).eq("cle","pays_disponibles");
    if (error) { showToast("Erreur : "+error.message, false); return; }
    setEntreprise(prev=>({...prev, pays_disponibles:valeur}));
    showToast("Pays retiré");
  };

  // Villes par pays (05/10/2026) — une fois un pays choisi sur une fiche (client, fournisseur,
  // immobilisation), la liste déroulante "Ville" ne propose que les villes de CE pays, à partir
  // de VILLES_PAR_PAYS_BASE ci-dessus (modifiable depuis Paramètres, comme tout le reste).
  const villesParPays = (() => {
    const saved = safeJsonParse(entreprise.villes_par_pays, null);
    return (saved && typeof saved==="object" && !Array.isArray(saved)) ? saved : VILLES_PAR_PAYS_BASE;
  })();
  const villesPourPays = (pays) => {
    if (!pays || pays==="Cameroun") return villesDisponibles;
    const l = villesParPays[pays];
    return Array.isArray(l) && l.length ? l : (VILLES_PAR_PAYS_BASE[pays]||[]);
  };
  const ajouterVillePourPays = async (pays, nomBrut) => {
    const nom = String(nomBrut||"").trim();
    if (!nom || !pays || pays==="Cameroun") return;
    const actuelles = villesPourPays(pays);
    if (actuelles.some(v=>normalizeVille(v)===normalizeVille(nom))) {
      showToast("Cette ville est déjà dans la liste", false);
      return;
    }
    const prochainesPourPays = [...actuelles, nom].sort((a,b)=>a.localeCompare(b,"fr"));
    const prochaines = {...villesParPays, [pays]:prochainesPourPays};
    const valeur = JSON.stringify(prochaines);
    const { data:ex } = await supabase.from("parametres").select("id").eq("cle","villes_par_pays").maybeSingle();
    const { error } = ex
      ? await supabase.from("parametres").update({valeur}).eq("cle","villes_par_pays")
      : await supabase.from("parametres").insert({cle:"villes_par_pays", valeur});
    if (error) { showToast("Erreur : "+error.message, false); return; }
    setEntreprise(prev=>({...prev, villes_par_pays:valeur}));
    showToast("Ville « "+nom+" » ajoutée pour "+pays+" ✓");
  };
  const retirerVillePourPays = async (pays, nom) => {
    if (!window.confirm("Retirer « "+nom+" » de la liste des villes de "+pays+" ?\n\nLes fiches qui l'utilisent déjà ne seront pas modifiées.")) return;
    const actuelles = villesPourPays(pays);
    const prochainesPourPays = actuelles.filter(v=>v!==nom);
    const prochaines = {...villesParPays, [pays]:prochainesPourPays};
    const valeur = JSON.stringify(prochaines);
    const { data:ex } = await supabase.from("parametres").select("id").eq("cle","villes_par_pays").maybeSingle();
    const { error } = ex
      ? await supabase.from("parametres").update({valeur}).eq("cle","villes_par_pays")
      : await supabase.from("parametres").insert({cle:"villes_par_pays", valeur});
    if (error) { showToast("Erreur : "+error.message, false); return; }
    setEntreprise(prev=>({...prev, villes_par_pays:valeur}));
    showToast("Ville retirée");
  };

  // Dépôts — gérer soi-même la liste des dépôts (et donc des villes utilisables pour une
  // commande d'achat ou la "Ville d'affectation" d'un employé, qui dépendent toutes les deux de
  // l'existence d'un dépôt réel) depuis Paramètres. saveDepot/deleteDepot existaient déjà dans le
  // code mais n'étaient reliés à aucun écran — ajouter une ville à ces deux endroits précis
  // nécessitait donc jusqu'ici une intervention en base. Un garde-fou est ajouté à la
  // suppression : impossible de supprimer un dépôt qui a déjà du stock ou des livraisons, pour
  // ne jamais perdre silencieusement une donnée.
  const peutSupprimerDepot = (depotId) => {
    const aDuStock = depotStocks.some(s=>String(s.depot_id)===String(depotId)&&toFiniteNumber(s.quantite)>0);
    const aDesLivraisons = livraisons.some(l=>String(l.depot_id)===String(depotId));
    return !aDuStock && !aDesLivraisons;
  };

  // Garde une seule fiche canonique par clé métier, sans supprimer les anciennes lignes en base.
  const dedupeCanonical = (rows, keyFn) => {
    const result = new Map();
    for (const row of (rows||[])) {
      const key = String(keyFn(row)||"").trim().toLowerCase();
      if (!key) { result.set(`__id__${row?.id}`, row); continue; }
      const current = result.get(key);
      if (!current) { result.set(key,row); continue; }
      const rowActive = row?.actif !== false;
      const curActive = current?.actif !== false;
      const rowDate = new Date(row?.updated_at||row?.created_at||0).getTime()||0;
      const curDate = new Date(current?.updated_at||current?.created_at||0).getTime()||0;
      if ((rowActive&&!curActive) || (rowActive===curActive && rowDate>curDate)) result.set(key,row);
    }
    return [...result.values()];
  };

  const load = async () => {
    if (!session) return;
    const [e,p,c,f,v,d,l,fa,pf,dep,liv,co,pay,ds,av,rhDep,rhCong,rhDocs,rhHist,rhEval,rhContr,rhSoldes,immo,immoMvt,immoEnt,achCmd,achRec,achFac,achPay,qc] = await Promise.all([
      // Lecture via la vue "employes_annuaire" (et non la table directement) : elle masque les
      // champs RH sensibles (salaire, CNI, banque, adresse, notes...) pour qui n'a pas la
      // permission "rh_donnees_sensibles" (ni la direction) — correctif du 03/10/2026, voir
      // migration_rh_donnees_sensibles.sql. Mêmes employés visibles qu'avant (même ville, même
      // règles) : seuls certains champs changent selon qui regarde.
      supabase.from("employes_annuaire").select("*").order("created_at"),
      supabase.from("produits").select("*").order("nom"),
      supabase.from("clients").select("*").order("nom"),
      supabase.from("fournisseurs").select("*").order("nom"),
      supabase.from("ventes").select("*").order("date",{ascending:false}),
      supabase.from("depenses").select("*").order("date",{ascending:false}),
      supabase.from("lots").select("*").order("date",{ascending:false}),
      supabase.from("factures").select("*").order("created_at",{ascending:false}),
      supabase.from("proformas").select("*").order("created_at",{ascending:false}),
      supabase.from("depots").select("*").order("ville"),
      supabase.from("livraisons").select("*").order("created_at",{ascending:false}),
      supabase.from("connexions").select("*").order("date",{ascending:false}).limit(20),
      supabase.from("paiements").select("*").order("date_soumission",{ascending:false}),
      supabase.from("depot_stocks").select("*").order("ville").order("produit_nom"),
      supabase.from("avaries").select("*").order("date",{ascending:false}),
      supabase.from("departements").select("*").order("nom"),
      supabase.from("conges").select("*").order("created_at",{ascending:false}),
      supabase.from("documents_employes").select("*").order("created_at",{ascending:false}),
      supabase.from("historique_employes").select("*").order("created_at",{ascending:false}).limit(500),
      supabase.from("evaluations_employes").select("*").order("created_at",{ascending:false}),
      supabase.from("contrats_employes").select("*").order("created_at",{ascending:false}),
      supabase.from("soldes_conges").select("*").order("annee",{ascending:false}),
      supabase.from("immobilisations").select("*").order("created_at",{ascending:false}),
      supabase.from("mouvements_immobilisations").select("*").order("created_at",{ascending:false}),
      supabase.from("entretiens_immobilisations").select("*").order("date_prevue",{ascending:true}),
      supabase.from("achats_commandes").select("*").order("created_at",{ascending:false}),
      supabase.from("achats_receptions").select("*").order("created_at",{ascending:false}),
      supabase.from("achats_factures").select("*").order("created_at",{ascending:false}),
      supabase.from("achats_paiements").select("*").order("date_paiement",{ascending:false}),
      supabase.from("fiches_controle_lot").select("*").order("date_production",{ascending:false}),
    ]);
    const employeeRowsRaw=e.data||[];
    const employeeRows=dedupeCanonical(employeeRowsRaw,row=>row.username||row.matricule||row.id);
    const currentProfile=employeeRows.find(row=>
      String(row.id)===String(session.id) ||
      String(row.matricule||"")===String(session.matricule||"") ||
      String(row.username||"")===String(session.username||"")
    );
    const effectiveSession={...session,...(currentProfile||{}),ville:currentProfile?.ville||session.ville||""};
    const effectivePerms=(()=>{
      const parsed=safeJsonParse(effectiveSession.permissions,[]);
      return Array.isArray(parsed)?parsed:[];
    })();
    const globalAccess=
      estRoleDirecteur(effectiveSession.role, effectiveSession.poste) ||
      effectivePerms.includes("voir_toutes_villes");
    const activeCity=normalizeVille(effectiveSession.ville);
    const cityOf=row=>normalizeVille(row?.ville);
    const sameCity=row=>globalAccess || (!!activeCity && cityOf(row)===activeCity);
    const sameCityOrShared=row=>globalAccess || !cityOf(row) || (!!activeCity && cityOf(row)===activeCity);
    const clientCityByName=name=>cityOf((c.data||[]).find(row=>row.nom===name));
    const invoiceCity=row=>cityOf(row)||clientCityByName(row?.client_nom);
    const employeeCityByName=name=>cityOf(employeeRows.find(row=>row.nom===name));
    const paymentCity=row=>{
      if(cityOf(row))return cityOf(row);
      const invoice=(fa.data||[]).find(inv=>
        row?.facture_id ? String(inv.id)===String(row.facture_id) : inv.numero===row?.facture_numero
      );
      return invoiceCity(invoice);
    };
    // Filtre générique par ville pour les tables RH / immobilisations / achats.
    // Le Directeur (ou toute personne avec la permission "voir_toutes_villes") voit tout.
    const scopeRows = rows => globalAccess ? (rows||[]) : (rows||[]).filter(sameCity);

    if(currentProfile && (
      session.ville!==effectiveSession.ville ||
      session.permissions!==effectiveSession.permissions ||
      session.role!==effectiveSession.role
    )){
      setSession(prev=>({...prev,...currentProfile}));
    }

    // Le directeur (quelle que soit sa ville, même vide) doit toujours rester visible pour tout le
    // monde — sinon il disparaît des listes/du chat pour les employés d'une autre ville que la sienne.
    const isDirecteurRow = row => estRoleDirecteur(row?.role, row?.poste);
    setEmployes(globalAccess
      ? employeeRows
      : employeeRows.filter(row=>sameCity(row)||String(row.id)===String(effectiveSession.id)||isDirecteurRow(row))
    );
    if (p.data) setProduits(p.data);
    if (c.data) setClients(globalAccess ? c.data : c.data.filter(sameCity));
    if (f.data) setFournisseurs(globalAccess ? f.data : f.data.filter(sameCityOrShared));
    if (v.data) setVentes(globalAccess ? v.data : v.data.filter(sameCity));
    if (d.data) setDepenses(globalAccess ? d.data : d.data.filter(row=>{
      const rowCity=cityOf(row)||employeeCityByName(row?.employe);
      return !!activeCity && rowCity===activeCity;
    }));
    if (l.data) setLots(globalAccess || activeCity==="dschang" ? l.data.filter(row=>!cityOf(row)||cityOf(row)==="dschang") : []);
    // Fiches de contrôle qualité — même règle de visibilité que les lots (site de
    // production Dschang, ou accès global) : c'est le même périmètre d'usine.
    if (qc.data) setFichesQualite(globalAccess || activeCity==="dschang" ? qc.data.filter(row=>!cityOf(row)||cityOf(row)==="dschang") : []);
    // Réceptions de bouteilles vides — même règle de visibilité que les lots (site de
    // production Dschang, ou accès global) puisque c'est la même donnée d'usine.
    {
      const { data: recData, error: recErr } = await supabase.from("receptions_bouteilles").select("*").order("date",{ascending:false});
      if (recErr) console.error("Chargement des réceptions de bouteilles :", recErr.message);
      setReceptionsBouteilles(globalAccess || activeCity==="dschang" ? (recData||[]).filter(row=>!cityOf(row)||cityOf(row)==="dschang") : []);
    }
    if (fa.data) {
      const rows=fa.data.map(x=>({...x,lignes:Array.isArray(x.lignes)?x.lignes:safeJsonParse(x.lignes,[])}));
      setFactures(globalAccess ? rows : rows.filter(row=>invoiceCity(row)===activeCity));
    }
    if (pf.data) {
      const rows=pf.data.map(x=>({...x,lignes:Array.isArray(x.lignes)?x.lignes:safeJsonParse(x.lignes,[])}));
      setProformas(globalAccess ? rows : rows.filter(row=>(cityOf(row)||clientCityByName(row.client_nom))===activeCity));
    }
    // Les dépôts sont toujours visibles en entier (adresses non sensibles) : indispensable pour
    // que Dschang (site de production, qui n'a pas de dépôt propre) puisse choisir une ville
    // de destination lors de la création d'une livraison.
    if (dep.data) setDepots(dep.data);
    if (liv.data) setLivraisons(globalAccess ? liv.data : liv.data.filter(sameCity));
    if (co.data) setConnexions(globalAccess ? co.data : co.data.filter(row=>{
      const city=employeeCityByName(row.employe_nom);
      return city===activeCity;
    }));
    if (pay.data) setPaiements(globalAccess ? pay.data : pay.data.filter(row=>paymentCity(row)===activeCity));
    if (pay.error) console.error("Chargement des paiements :", pay.error.message);
    if (ds.data) setDepotStocks(globalAccess ? ds.data : ds.data.filter(sameCity));
    if (ds.error) console.error("Chargement des stocks par ville :", ds.error.message);
    if (av.data) setAvaries(globalAccess ? av.data : av.data.filter(sameCity));
    setDepartements(rhDep.data||[]);
    setConges(scopeRows(rhCong.data||[]));
    setDocumentsEmployes(rhDocs.data||[]);
    setHistoriqueEmployes(scopeRows(rhHist.data||[]));
    setEvaluationsEmployes(rhEval.data||[]);
    setContratsEmployes(scopeRows(rhContr.data||[]));
    setSoldesConges(rhSoldes.data||[]);
    setImmobilisations(scopeRows(immo.data||[]));
    setMouvementsImmobilisations(immoMvt.data||[]);
    setEntretiensImmobilisations(immoEnt.data||[]);
    const normalizePurchaseRows = rows => (rows||[]).map(row=>({...row,lignes:Array.isArray(row.lignes)?row.lignes:safeJsonParse(row.lignes,[])}));
    setAchatsCommandes(scopeRows(normalizePurchaseRows(achCmd.data||[])));
    setAchatsReceptions(scopeRows(achRec.data||[]));
    setAchatsFactures(scopeRows(achFac.data||[]));
    setAchatsPaiements(scopeRows(achPay.data||[]));
    if (achCmd.error) console.error("Chargement des achats :",achCmd.error.message);
    if (immo.error) console.error("Chargement des immobilisations :", immo.error.message);
    if (av.error) console.error("Chargement des avaries :", av.error.message);
    // Bulletins de paie — la base de données (RLS) filtre déjà correctement : la direction voit
    // tout, un employé ne reçoit que ses propres bulletins. Pas besoin de re-filtrer côté app.
    {
      const { data: bpData, error: bpErr } = await supabase.from("bulletins_paie").select("*").order("mois",{ascending:false});
      if (bpErr) console.error("Chargement des bulletins de paie :", bpErr.message);
      if (bpData) {
        setBulletinsPaie(dedupeCanonical(bpData,row=>`${row.employe_id}::${String(row.mois||"").slice(0,7)}`));
      }
    }
    // Demandes de signature à distance — chacun ne voit que celles où il est signataire ;
    // la Direction voit aussi celles qu'elle a envoyées, pour en suivre l'avancement.
    {
      const { data: dsData, error: dsErr } = await supabase.from("demandes_signature").select("*").order("created_at",{ascending:false});
      if (dsErr) console.error("Chargement des demandes de signature :", dsErr.message);
      setDemandesSignature(dsData||[]);
    }
    // Jours fériés — lecture ouverte à tous (RLS), pas de filtre par ville (calendrier national).
    {
      const { data: ferData, error: ferErr } = await supabase.from("jours_feries").select("*").order("date");
      if (ferErr) console.error("Chargement des jours fériés :", ferErr.message);
      setJoursFeries(ferData||[]);
    }
    // Ingrédients (matières premières) et recettes (composition des produits) — lecture ouverte
    // à tous comme pour les produits, pas de filtre par ville (référentiel unique de l'usine).
    {
      const [{ data: ingData, error: ingErr }, { data: recData2, error: recErr2 }] = await Promise.all([
        supabase.from("ingredients").select("*").order("nom"),
        supabase.from("recettes").select("*"),
      ]);
      if (ingErr) console.error("Chargement des ingrédients :", ingErr.message);
      if (recErr2) console.error("Chargement des recettes :", recErr2.message);
      setIngredients(ingData||[]);
      setRecettes(recData2||[]);
    }
    // Objectifs
    const {data:objData} = await supabase.from("parametres").select("*").like("cle","objectif_%");
    if (objData) {
      const obj = {};
      objData.forEach(o=>{ const k=o.cle.replace("objectif_",""); obj[k]=safeJsonParse(o.valeur,{}); });
      setObjectifs(obj);
    }
    {
      const { data:hData, error:hErr } = await supabase.from("parametres").select("cle,valeur").like("cle","journal_obj_%");
      if (!hErr && hData) {
        const h = {};
        hData.forEach(o=>{ const m=/^journal_obj_(.+)_(\d{4}-\d{2})$/.exec(o.cle); if (m) h[m[1]+"|"+m[2]] = safeJsonParse(o.valeur,{}); });
        setObjHist(h); setObjHistPret(true);
      }
    }
    // Paramètres entreprise
    const {data:par} = await supabase.from("parametres").select("*");
    if (par && par.length > 0) {
      const obj={};
      par.forEach(r=>{ if(r.cle && r.valeur) obj[r.cle]=r.valeur; });
      setEntreprise(prev=>({...prev,...obj}));
    }

    // Déclarations de ventes (saisie manuelle simple, sans impact sur le stock)
    const {data:decl, error:declErr} = await supabase.from("declarations_ventes").select("*").order("date",{ascending:false});
    if (declErr) console.error("Chargement des déclarations de ventes :", declErr.message);
    if (decl) setDeclarationsVentes(decl);

    // Journal des mouvements de stock par dépôt (pour calculer le total déjà reçu)
    const {data:mvt, error:mvtErr} = await supabase.from("mouvements_stock_log").select("*");
    if (mvtErr) console.error("Chargement du journal de stock :", mvtErr.message);
    if (mvt) setMouvementsStockLog(mvt);

    // Prospection (échantillons donnés aux prospects, hors ventes)
    const {data:pros, error:prosErr} = await supabase.from("prospections").select("*").order("date",{ascending:false});
    if (prosErr) console.error("Chargement des prospections :", prosErr.message);
    if (pros) setProspections(pros);
  };

  useEffect(()=>{ load(); },[session]);

  const resetApplication = async () => {
    if (resettingApp) return;
    const confirmed = window.confirm(
      "Réinitialiser l'affichage et recharger les données sans vous déconnecter ?"
    );
    if (!confirmed) return;

    setResettingApp(true);
    try {
      // Ferme les fenêtres temporaires sans toucher à la session.
      setModal(null);
      setForm({});
      setFacView(null);
      setProformaView(null);
      setEditFac(null);
      setLotPrint(null);
      setLotEtiquette(null);
      setReceptionModal(null);
      setLivraisonView(null);
      setEmailModal(null);
      setDocView(null);
      setDocSendModal(null);
      setShowProfile(false);
      setMobileMenuOpen(false);
      setMobileChatConversationOpen(false);

      // Remet les recherches et filtres temporaires à leur valeur initiale.
      setClientSearch("");
      setFactureSearch("");
      setProductionSearch("");
      setReportIncludeCreances(false);
      const now=new Date();
      setProductionDates({
        debut:localISODate(new Date(now.getFullYear(),now.getMonth(),1)),
        fin:today()
      });
      setFactureStatutFilter("tous");
      setPaiementFilter("tous");
      setRapportDates({debut:"",fin:""});
      setChatInput("");

      // Retour au tableau de bord puis relecture complète de Supabase.
      setPage("tableau");
      await load();
      showToast("Application réinitialisée et données actualisées.");
    } catch (error) {
      console.error("Réinitialisation de l'application :",error);
      showToast("La réinitialisation a échoué : "+(error?.message||"erreur inconnue"),false);
    } finally {
      setResettingApp(false);
    }
  };


  // Notifications du propriétaire : chargement, temps réel (cloche + numéro rouge + bip).
  const notifNonLues = notifs.filter(n=>!n.lue).length;
  useEffect(()=>{
    if (!session) return;
    let fini = false;
    (async()=>{
      try {
        const { data } = await supabase.from("notif_prefs").select("messages,livraisons").eq("matricule",session.matricule).maybeSingle();
        if (data && !fini) setAlertesPref({ messages:data.messages!==false, livraisons:data.livraisons!==false });
      } catch(_) {}
    })();
    return ()=>{ fini=true; };
    // eslint-disable-next-line
  },[session?.id]);
  const changerAlerte = async (cle, val) => {
    const avant = alertesPref;
    const next = { ...alertesPref, [cle]:val };
    setAlertesPref(next);
    try {
      const { error } = await supabase.rpc("maj_mes_alertes",{ p_messages:next.messages, p_livraisons:next.livraisons });
      if (error) throw error;
    } catch(e) { setAlertesPref(avant); showToast("Réglage non enregistré : "+((e&&e.message)||e), false); }
  };
  const repousserBandeauAlertes = () => {
    try { localStorage.setItem("az_bandeau_alertes", String(Date.now())); } catch(_) {}
    setBandeauAlertesOff(true);
  };
  const marquerNotifsLues = async (ids) => {
    if (!ids || !ids.length) return;
    setNotifs(l=>l.map(x=>ids.includes(x.id)?{...x,lue:true}:x));
    try { await supabase.from("notifications").update({lue:true}).in("id",ids); } catch(_) {}
  };
  useEffect(()=>{
    if (!session) return;
    let fini = false;
    const charger = async () => {
      const { data, error } = await supabase.from("notifications").select("*").order("created_at",{ascending:false}).limit(60);
      if (!error && data && !fini) setNotifs(data);
      if (error) console.warn("Notifications :", error.message);
    };
    charger();
    const ch = supabase.channel("azongbio-notifs-"+Date.now())
      .on("postgres_changes",{event:"INSERT",schema:"public",table:"notifications"},payload=>{
        const n = payload.new; if (!n) return;
        setNotifs(l=>[n,...l.filter(x=>x.id!==n.id)].slice(0,60));
        if (!(String(n.cible_table||"").startsWith("messages") && pageNotifRef.current==="chat")) {
          bipNotification();
          try { if (navigator.vibrate) navigator.vibrate(150); } catch(_) {}
          showToast(n.titre+" — "+n.message);
        }
      })
      .on("postgres_changes",{event:"UPDATE",schema:"public",table:"notifications"},payload=>{
        const n = payload.new; if (n) setNotifs(l=>l.map(x=>x.id===n.id?n:x));
      })
      .subscribe();
    const onVis = () => { if (!document.hidden) charger(); };
    document.addEventListener("visibilitychange", onVis);
    const fallback = setInterval(charger, 120000);
    return ()=>{ fini=true; clearInterval(fallback); document.removeEventListener("visibilitychange",onVis); supabase.removeChannel(ch); };
    // eslint-disable-next-line
  },[session?.id]);
  // Ouvre la chose créée (facture, proforma, livraison…) ou, à défaut, la page et surligne la ligne.
  const notifFocusTexte = (needle, essais=8) => {
    if (!needle) return;
    const cherche = (k) => {
      const cand = Array.from(document.querySelectorAll("tr, .card, .compact-row"));
      const el = cand.filter(e=>(e.textContent||"").includes(needle)).sort((a,b)=>(a.textContent||"").length-(b.textContent||"").length)[0];
      if (el) {
        try { el.scrollIntoView({block:"center",behavior:"smooth"}); } catch(_) { el.scrollIntoView(); }
        el.classList.remove("az-flash"); void el.offsetWidth; el.classList.add("az-flash");
        setTimeout(()=>{ try { el.classList.remove("az-flash"); } catch(_) {} }, 3600);
      } else if (k>0) setTimeout(()=>cherche(k-1), 350);
    };
    setTimeout(()=>cherche(essais), 450);
  };
  const ouvrirNotif = (n) => {
    if (!n) return;
    if (n.id && !n.lue) marquerNotifsLues([n.id]);
    setNotifOuvert(false);
    const premier = String(n.message||"").split(" — ")[0].trim();
    const trouver = (liste, champ) => (n.cible_id && liste.find(x=>String(x.id)===String(n.cible_id))) || (premier && liste.find(x=>x[champ]===premier));
    const cliDe = (f) => clients.find(c=>f.client_code&&c.code===f.client_code) || clients.find(c=>c.nom===f.client_nom);
    const quoi = n.cible_table || n.page || "";
    if (quoi==="messages_prive"||quoi==="messages_general") {
      setActiveChatThread(quoi==="messages_prive" ? String(n.cible_id||"") : null);
      setMobileChatConversationOpen(true);
      setPage("chat");
      return;
    }
    if (quoi==="factures"||n.type==="facture") {
      const f = trouver(factures,"numero");
      if (f) { setPage("factures"); setFacView({facture:f,client:cliDe(f)}); return; }
    }
    if (quoi==="proformas"||n.type==="proforma") {
      const f = trouver(proformas,"numero");
      if (f) { setPage("proformas"); setProformaView({facture:f,client:cliDe(f)}); return; }
    }
    if (quoi==="livraisons"||n.type==="livraison") {
      const l = trouver(livraisons,"numero");
      if (l) { setPage("livraisons"); setLivraisonView(l); return; }
    }
    if (quoi==="bulletins_paie") {
      if (isDirecteur()) { setPage("salaires"); return; }
      if (n.cible_id) { setFicheReview(String(n.cible_id)); return; }
    }
    if (quoi==="versements_employes") {
      if (isDirecteur()) { setPage("salaires"); return; }
      if (n.cible_id) { setVrMesReporte(r=>({...r,[String(n.cible_id)]:false})); setVrMesReview(String(n.cible_id)); vrMesCharger(); return; }
    }
    if (quoi==="paiements") setPaiementFilter("tous");
    if (quoi==="clients") setClientVue("fiches");
    if (n.page) setPage(n.page);
    notifFocusTexte(premier.replace(/\s*\(.*$/,""));
  };
  const ouvrirNotifRef = useRef(null); ouvrirNotifRef.current = ouvrirNotif;
  const donneesPretesRef = useRef(false); donneesPretesRef.current = (factures.length+clients.length+livraisons.length+proformas.length)>0;
  const ouvrirNotifParId = async (id, essai=0) => {
    if (!id) return;
    if (!donneesPretesRef.current && essai<10) { setTimeout(()=>ouvrirNotifParId(id,essai+1), 500); return; }
    try {
      const { data } = await supabase.from("notifications").select("*").eq("id",id).maybeSingle();
      if (data) { setNotifs(l=>l.some(x=>x.id===data.id)?l:[data,...l]); ouvrirNotifRef.current(data); }
    } catch(_) {}
  };
  const ouvrirNotifParIdRef = useRef(null); ouvrirNotifParIdRef.current = ouvrirNotifParId;
  // Alerte du téléphone : l'adresse contient « ?notif=<id> » (app fermée) ou le service d'alertes envoie un message (app ouverte).
  useEffect(()=>{
    if (!session) return;
    let id = null;
    try { id = new URLSearchParams(window.location.search).get("notif"); } catch(_) {}
    if (id) {
      try { window.history.replaceState({}, "", window.location.pathname); } catch(_) {}
      ouvrirNotifParIdRef.current(id);
    }
    // eslint-disable-next-line
  },[session?.id]);
  useEffect(()=>{
    if (!("serviceWorker" in navigator)) return;
    const h = (e) => { const d = e && e.data; if (d && d.type==="ouvrir-notif" && d.id) ouvrirNotifParIdRef.current(d.id); };
    navigator.serviceWorker.addEventListener("message", h);
    return ()=>navigator.serviceWorker.removeEventListener("message", h);
  },[]);
  // Hauteur réelle de l'en-tête, pour caler le menu de gauche juste en dessous.
  useEffect(()=>{
    const set = () => { try { const h=document.querySelector("header"); if (h) document.documentElement.style.setProperty("--az-hh", h.offsetHeight+"px"); } catch(_) {} };
    set();
    let ro=null;
    try { const h=document.querySelector("header"); if (h && typeof ResizeObserver!=="undefined") { ro=new ResizeObserver(set); ro.observe(h); } } catch(_) {}
    window.addEventListener("resize",set);
    return ()=>{ try { if (ro) ro.disconnect(); } catch(_) {} window.removeEventListener("resize",set); };
  },[session?.id]);
  const [salutCourante, setSalutCourante] = useState(()=>salutationHeure());
  useEffect(()=>{
    const t = setInterval(()=>setSalutCourante(salutationHeure()), 60000);
    return ()=>clearInterval(t);
  },[]);
  // Numéro rouge sur l'icône de l'application (si installée) et dans le titre de l'onglet.
  useEffect(()=>{
    try { if (navigator.setAppBadge) { notifNonLues>0 ? navigator.setAppBadge(notifNonLues) : navigator.clearAppBadge(); } } catch(_) {}
    try { document.title = (notifNonLues>0?"("+notifNonLues+") ":"") + document.title.replace(/^\(\d+\)\s/,""); } catch(_) {}
  },[notifNonLues]);

  // Mon activité : chargement de MES factures et MES ventes (fonction réservée à ma propre fiche).
  const chargerMonActivite = async () => {
    setMonActiviteBusy(true); setMonActiviteErr("");
    try {
      const { data, error } = await supabase.rpc("mon_activite");
      if (error) throw new Error(error.message);
      setMonActivite(data||{nom:"",factures:[],ventes:[]});
    } catch(e) { setMonActiviteErr(e&&e.message?e.message:String(e)); }
    finally { setMonActiviteBusy(false); }
  };
  useEffect(()=>{ if (session && page==="mon_activite") chargerMonActivite(); /* eslint-disable-next-line */ },[page,session?.id]);

  // ── Journal d'activité : consultations enregistrées automatiquement ──
  useEffect(()=>{
    if (!session || !page) return;
    const libelles = { mon_activite:"Mon activité", journal_activite:"Journal d'activité", chat_supervision:"Discussions de l'équipe (direction)", "nouvelle-facture":"Nouvelle facture", "nouvelle-proforma":"Nouvelle proforma" };
    const m = ALL_MODULES.find(n=>n.id===page);
    journaliserVue("Page", libelles[page] || (m&&m.label) || page);
    // eslint-disable-next-line
  },[page,session?.id]);
  useEffect(()=>{ if (facView&&facView.facture) journaliserVue("Facture", facView.facture.numero||"", "Ouverture / impression"+(facView.facture.client_nom?" — "+facView.facture.client_nom:"")); /* eslint-disable-next-line */ },[facView]);
  useEffect(()=>{ if (proformaView&&proformaView.facture) journaliserVue("Proforma", proformaView.facture.numero||"", "Ouverture / impression"+(proformaView.facture.client_nom?" — "+proformaView.facture.client_nom:"")); /* eslint-disable-next-line */ },[proformaView]);
  useEffect(()=>{ if (livraisonView) journaliserVue("Livraison", livraisonView.numero||"", "Fiche ouverte"+(livraisonView.depot_nom?" — "+livraisonView.depot_nom:"")); /* eslint-disable-next-line */ },[livraisonView]);
  useEffect(()=>{ if (docView) journaliserVue("Document", (docView.type||"Document")+(docView.numero?" "+docView.numero:""), "Aperçu / impression"); /* eslint-disable-next-line */ },[docView]);
  useEffect(()=>{ if (paiementsFactureView) journaliserVue("Paiements d'une facture", paiementsFactureView.numero||"", "Historique des paiements consulté"); /* eslint-disable-next-line */ },[paiementsFactureView]);

  // ── Journal d'activité : lecture (direction seulement) ──
  const journalFiltrer = (q) => {
    const du = journalDu ? new Date(journalDu+"T00:00:00").toISOString() : null;
    const au = journalAu ? new Date(new Date(journalAu+"T00:00:00").getTime()+86400000).toISOString() : null;
    if (du) q = q.gte("created_at", du);
    if (au) q = q.lt("created_at", au);
    if (journalEmp) q = q.eq("employe_nom", journalEmp);
    if (journalAction) q = q.eq("action", journalAction);
    if (journalObjet) q = q.eq("objet", journalObjet);
    const t = journalQ.trim().replace(/[,()%*]/g," ").trim();
    if (t) q = q.or("reference.ilike.*"+t+"*,detail.ilike.*"+t+"*,objet.ilike.*"+t+"*,employe_nom.ilike.*"+t+"*");
    return q;
  };
  const chargerJournal = async () => {
    setJournalBusy(true); setJournalErr("");
    try {
      const { data, count, error } = await journalFiltrer(supabase.from("journal_activite").select("*",{count:"exact"}))
        .order("created_at",{ascending:false}).limit(journalLimite);
      if (error) throw error;
      setJournalRows(data||[]); setJournalTotal(count||0);
      const du = journalDu ? new Date(journalDu+"T00:00:00").toISOString() : null;
      const au = journalAu ? new Date(new Date(journalAu+"T00:00:00").getTime()+86400000).toISOString() : null;
      const { data:res, error:errRes } = await supabase.rpc("journal_resume",{ p_du:du, p_au:au });
      if (!errRes) setJournalResume(res||[]);
    } catch(e) { setJournalErr(e&&e.message?e.message:String(e)); }
    finally { setJournalBusy(false); }
  };
  useEffect(()=>{
    if (!session || page!=="journal_activite" || !isDirecteur()) return;
    const t = setTimeout(chargerJournal, journalQ?350:0);
    return ()=>clearTimeout(t);
    // eslint-disable-next-line
  },[page,session?.id,journalDu,journalAu,journalEmp,journalAction,journalObjet,journalQ,journalLimite]);
  const journalLibelleFiltres = () => {
    const parts = [];
    parts.push((journalDu||journalAu) ? ((journalDu?"du "+fmtDate(journalDu)+" ":"")+(journalAu?"au "+fmtDate(journalAu):"")).trim() : "toute la période");
    if (journalEmp) parts.push("employé : "+journalEmp);
    if (journalAction) parts.push("action : "+({creation:"créations",modification:"modifications",suppression:"suppressions",consultation:"consultations",connexion:"connexions"}[journalAction]||journalAction));
    if (journalObjet) parts.push("objet : "+journalObjet);
    if (journalQ.trim()) parts.push("recherche : « "+journalQ.trim()+" »");
    return parts.join(" — ");
  };
  const imprimerJournal = async () => {
    setReportGenerating(true);
    try {
      const { data, count, error } = await journalFiltrer(supabase.from("journal_activite").select("*",{count:"exact"}))
        .order("created_at",{ascending:false}).limit(1500);
      if (error) throw error;
      const pdf = await generateJournalPdfBase64(entreprise, data||[], journalLibelleFiltres(), count||0);
      await openPdf(pdf,"Document Azongbio");
    } catch(e) { showToast("Erreur : "+(e&&e.message?e.message:e), false); }
    setReportGenerating(false);
  };

  // Civilité (Mr / Mme) pour « Bonjour » : lue via une fonction réservée à sa propre fiche.
  useEffect(()=>{
    if (!session) { setMaCivilite(""); return; }
    let fini = false;
    (async()=>{
      try {
        const { data, error } = await supabase.rpc("ma_civilite");
        if (!fini && !error) setMaCivilite(data==="Mr"||data==="Mme"?data:"");
      } catch(_) {}
    })();
    return ()=>{ fini=true; };
    // eslint-disable-next-line
  },[session?.id]);

  // Notifications sur téléphone : enregistrement du service worker et état de cet appareil.
  useEffect(()=>{
    if (!session) return;
    if (!pushSupporte()) { setPushEtat("non_supporte"); return; }
    let fini = false;
    navigator.serviceWorker.register("/sw.js").then(async reg=>{
      try {
        const sub = await reg.pushManager.getSubscription();
        if (fini) return;
        if (sub) setPushEtat("actif");
        else if (Notification.permission==="denied") setPushEtat("refuse");
        else setPushEtat("a_activer");
      } catch(_) { if (!fini) setPushEtat("a_activer"); }
    }).catch(()=>{ if (!fini) setPushEtat("non_supporte"); });
    return ()=>{ fini=true; };
    // eslint-disable-next-line
  },[session?.id]);
  const activerPush = async () => {
    setPushBusy(true); setPushMsg("");
    try {
      const perm = await Notification.requestPermission();
      if (perm!=="granted") { setPushEtat(perm==="denied"?"refuse":"a_activer"); setPushMsg("Autorisation non accordée."); return; }
      const reg = await navigator.serviceWorker.ready;
      const rep = await fetch("/api/push-public-key");
      const { key } = await rep.json();
      if (!key) throw new Error("Clé du serveur absente");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly:true, applicationServerKey:pushCleVersOctets(key) });
      const j = sub.toJSON();
      const { error } = await supabase.rpc("enregistrer_push",{ p_endpoint:j.endpoint, p_p256dh:j.keys.p256dh, p_auth:j.keys.auth, p_agent:navigator.userAgent });
      if (error) throw new Error(error.message);
      setPushEtat("actif"); setPushMsg("Cet appareil recevra les notifications.");
    } catch(e) { setPushMsg("Activation impossible : "+(e&&e.message?e.message:e)); }
    finally { setPushBusy(false); }
  };
  const desactiverPush = async () => {
    setPushBusy(true); setPushMsg("");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { const ep = sub.endpoint; await sub.unsubscribe(); await supabase.rpc("desinscrire_push",{ p_endpoint:ep }); }
      setPushEtat("a_activer"); setPushMsg("Notifications désactivées sur cet appareil.");
    } catch(e) { setPushMsg("Désactivation impossible : "+(e&&e.message?e.message:e)); }
    finally { setPushBusy(false); }
  };

  // Synchronisation en temps réel des données métier entre tous les appareils.
  // Un petit délai regroupe les changements successifs d'une même transaction.
  useEffect(()=>{
    if (!session) return;

    let refreshTimer = null;
    const scheduleReload = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(()=>{ load(); }, 350);
    };

    let channel = supabase.channel(
      "azongbio-business-"+session.matricule+"-"+Date.now()
    );

    REALTIME_BUSINESS_TABLES.forEach(table=>{
      channel = channel.on(
        "postgres_changes",
        { event:"*", schema:"public", table },
        scheduleReload
      );
    });

    channel.subscribe(status=>{
      if (status==="CHANNEL_ERROR" || status==="TIMED_OUT") {
        console.warn("Synchronisation Realtime indisponible :", status);
      }
    });

    // Secours discret en cas de coupure temporaire de Realtime. Espacé pour limiter
    // la consommation de bande passante (auparavant 60s, beaucoup trop fréquent).
    const fallback = setInterval(scheduleReload, 300000);

    return ()=>{
      if (refreshTimer) clearTimeout(refreshTimer);
      clearInterval(fallback);
      supabase.removeChannel(channel);
    };
  },[session]);

  // Bug corrigé le 05/10/2026 : cet effet réinitialisait TOUT le formulaire Paramètres
  // (y compris "Nouvelle ville...", "Nouveau pays..." en cours de saisie, ou tout autre champ
  // en cours de modification) dès que `entreprise` changeait de référence — ce qui arrive à
  // chaque synchronisation Realtime en arrière-plan (n'importe quel employé qui enregistre une
  // vente, une livraison, une facture... dans n'importe quelle ville déclenche un rechargement
  // global, y compris de `entreprise`). Résultat : la lettre tapée à l'instant disparaissait
  // aussitôt, rendant impossible l'ajout d'une ville ou d'un pays en période d'activité normale.
  // On ne resynchronise donc plus qu'à l'ENTRÉE sur la page Paramètres (changement de `page`),
  // jamais pendant qu'on y est déjà — un retour sur la page la resynchronise si besoin.
  useEffect(()=>{ if (page==="parametres") setForm({...entreprise}); },[page]);

  const saveDeclaration = async () => {
    const qte = parseInt(declForm.quantite);
    if (!qte || qte<=0) return showToast("Indique un nombre de bouteilles",false);
    setDeclSaving(true);
    const { error } = await supabase.from("declarations_ventes").insert({
      employe_id: String(session.id), employe_nom: session.nom,
      date: today(), quantite: qte, note: declForm.note||""
    });
    setDeclSaving(false);
    if (error) return showToast("Erreur : "+error.message, false);
    await load();
    setDeclForm({ quantite:"", note:"" });
    showToast("Enregistré ✓");
  };

  const saveProspection = async () => {
    const { ville, produit, quantite, prospectNom, notes } = prospectionForm;
    if (!ville||!produit||!quantite) return showToast("Ville, produit et quantité requis",false);
    setProspectionSaving(true);
    const { error } = await supabase.rpc("enregistrer_prospection_transaction", {
      p_ville:ville, p_produit_nom:produit, p_quantite:parseInt(quantite),
      p_prospect_nom:prospectNom||"", p_notes:notes||"", p_acteur:session.nom
    });
    setProspectionSaving(false);
    if (error) return showToast("Erreur : "+error.message, false);
    await load();
    setProspectionForm({ ville: isDirecteur()?"":(session.ville||""), produit:"", quantite:"", prospectNom:"", notes:"" });
    showToast("Prospection enregistrée ✓");
  };

  const login = (emp) => { setSession(emp); setPage("tableau"); showToast("Bienvenue, "+emp.nom.split(" ")[0]+" !"); };

  // Ouvre un PDF déjà généré (partage natif, téléchargement, ou aperçu dans l'app avec bouton Fermer
  // si l'app est installée en PWA et que le partage natif n'est pas disponible).
  const openPdf = (pdf, title) => { journaliserVue("Document PDF", (pdf&&pdf.filename)||title||"", "Ouverture / impression"); return deliverPdfOnDevice({
    ...pdf,
    title,
    onOpenInApp: (url, filename, t) => setPdfPreview({ url, filename, title: t })
  }); };
  const employeActifRH = (e) => e.actif!==false && String(e.statut_rh||"Actif").toLowerCase()!=="inactif";
  const genererBadges = async (liste, mode="carte") => {
    if (!isDirecteur()) return;
    const aImprimer = (liste||[]).filter(employeActifRH);
    if (!aImprimer.length) { showToast("Aucun employé actif à imprimer.",false); return; }
    setBadgeBusy(true);
    try {
      const { data, error } = await supabase.rpc("badge_codes",{ p_ids: aImprimer.map(e=>String(e.id)) });
      if (error) throw error;
      const codes = {}; (data||[]).forEach(r=>{ codes[String(r.id)] = r.badge_code; });
      const items = aImprimer.filter(e=>codes[String(e.id)]).map(e=>({ e, code: codes[String(e.id)] }));
      if (!items.length) throw new Error("codes de badge introuvables");
      const pdf = await badgesPdfBase64(items, entreprise, mode);
      await openPdf(pdf, "Badges");
    } catch(err) { showToast("Erreur : "+((err&&err.message)||"badge impossible"),false); }
    setBadgeBusy(false);
  };
  const renouvelerBadge = async (e) => {
    if (!isDirecteur()) return;
    if (!window.confirm("Badge perdu ?\n\nL'ancien badge de "+[e.prenom,e.nom].filter(Boolean).join(" ")+" ne sera plus valide : son QR code affichera « badge non reconnu ». Un nouveau badge est préparé tout de suite.")) return;
    setBadgeBusy(true);
    try { const { error } = await supabase.rpc("badge_renouveler",{ p_id: String(e.id) }); if (error) throw error; }
    catch(err) { showToast("Erreur : "+((err&&err.message)||"renouvellement impossible"),false); setBadgeBusy(false); return; }
    setBadgeBusy(false);
    await genererBadges([e]);
  };
  const logout = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (event?.stopPropagation) event.stopPropagation();
    if (loggingOut) return; // évite les doubles clics

    // Un seul écran de transition affiché immédiatement : on n'efface PAS la
    // session ici, pour ne pas faire apparaître brièvement l'écran de login
    // juste avant le rechargement complet (c'est ce qui causait le clignotement).
    setLoggingOut(true);

    // Nettoie uniquement les éventuelles anciennes clés de session.
    try {
      localStorage.removeItem("azongbio_session");
      localStorage.removeItem("session");
      localStorage.removeItem("user");
      sessionStorage.removeItem("azongbio_session");
      sessionStorage.removeItem("session");
      sessionStorage.removeItem("user");
    } catch (error) {
      console.warn("Nettoyage de session impossible :", error);
    }

    // Supprime aussi une éventuelle session Supabase Auth, en arrière-plan : on ne
    // bloque JAMAIS la navigation là-dessus (un appel réseau qui traîne ne doit
    // plus jamais empêcher la déconnexion de se terminer).
    try { supabase.auth.signOut({ scope:"local" }).catch(()=>{}); } catch {}
    try { window.history.replaceState(null, "", window.location.pathname); } catch {}

    // Le projet utilise une authentification applicative sans session Supabase Auth.
    // Une navigation complète garantit qu'aucun état React ou abonnement Realtime ne subsiste.
    // Pas de setTimeout : on part directement, l'écran de transition ci-dessus reste
    // affiché jusqu'au rechargement, donc aucun flash de l'écran de login n'est visible.
    window.location.replace("/");
  };

  const nextNumero = async (cle, prefix, pad=3) => {
    const { data, error } = await supabase.rpc("next_document_number", {
      p_cle:cle, p_prefix:prefix, p_pad:Number(pad)||3
    });
    if (error) throw new Error("Numérotation atomique indisponible. Exécute la migration SQL fournie : "+error.message);
    return data;
  };

  const currentYear = () => String(new Date().getFullYear());
  const genFacNum = () => nextNumero("facture_compteur",`AZB-${currentYear()}-`);
  const genLivraisonNum = () => nextNumero("livraison_compteur",`LIV-${currentYear()}-`);
  const genProformaNum = () => nextNumero("proforma_compteur",`PRO-${currentYear()}-`);
  const genLotNum = (prod) => nextNumero("lot_compteur","LOT-"+prod.replace(/[^A-Za-z]/g,"").slice(0,3).toUpperCase()+"-",4).then(n=>n+"-"+currentYear().slice(-2));
  const genCliCode = (nom) => nextNumero("client_compteur","CLI-").then(n=>n+"-"+nom.slice(0,3).toUpperCase().replace(/\s/g,""));
  const CAT_CODE_IMMO = { "Ordinateur":"INFO","Téléphone":"TEL","Véhicule":"VEH","Machine":"MAC","Imprimante":"IMP","Mobilier":"MOB","Groupe électrogène":"GRP","Équipement réseau":"RES","Bâtiment":"BAT","Autre":"DIV" };
  const genImmoNum = (categorie) => {
    const catCode = CAT_CODE_IMMO[categorie] || "DIV";
    // Une séquence distincte par catégorie : BB-INFO-0001, BB-VEH-0001, etc.
    return nextNumero("immo_compteur_"+catCode, `BB-${catCode}-`, 4);
  };


  // ── Fonction centrale de gestion du stock ──
  // Utilisée partout où le stock doit changer (vente, facture, livraison, lot...)
  // delta positif = ajoute du stock, delta négatif = en retire. Ne descend jamais sous 0.
  const adjustStock = async (produitNom, delta) => {
    const prod = produits.find(p=>p.nom===produitNom);
    if (!prod) return { ok:false, error:"Produit introuvable : "+produitNom };
    const d = Number(delta);
    if (!Number.isFinite(d) || !Number.isInteger(d) || d===0) return { ok:false, error:"Variation de stock invalide" };
    const { data, error } = await supabase.rpc("ajuster_stock_transaction", {
      p_produit_id:String(prod.id), p_delta:d
    });
    if (error) return { ok:false, error:error.message };
    return { ok:true, newStock:Number(data) };
  };

  const getDepotStock = (depotId, produitNom) => {
    const row = depotStocks.find(s=>
      String(s.depot_id)===String(depotId) && s.produit_nom===produitNom
    );
    return toFiniteNumber(row?.quantite);
  };

  const getClientDepot = (clientNom) => {
    const client = clients.find(c=>c.nom===clientNom);
    if (!client) return null;
    const clientCity = String(client.ville||"").trim().toLowerCase();
    return depots.find(d=>String(d.ville||"").trim().toLowerCase()===clientCity) || null;
  };


  const resetImmoForm = () => setImmoForm({
    id:null, numero_inventaire:"", designation:"", categorie:"Ordinateur", marque:"", modele:"",
    numero_serie:"", ville:sessionVille()||"Dschang", pays:"Cameroun", localisation:"", employe_id:"", employe_nom:"",
    date_acquisition:today(), valeur_acquisition:"", duree_amortissement:"", fournisseur:"", etat:"Bon",
    statut:"En service", date_fin_garantie:"", prochaine_maintenance:"", notes:""
  });

  const saveImmobilisation = async () => {
    if (!String(immoForm.designation||"").trim()) return showToast("La désignation est obligatoire",false);
    if (!String(immoForm.ville||"").trim()) return showToast("La ville est obligatoire",false);
    setImmoSaving(true);
    try {
      const emp=employes.find(e=>String(e.id)===String(immoForm.employe_id));
      let numeroInventaire=String(immoForm.numero_inventaire||"").trim();
      // Génère un code atomique (IMMO-2026-0001) à la création si le champ est laissé vide.
      // Si l'appel échoue (ex. compteur pas encore initialisé côté base), on retombe sur un
      // code local unique plutôt que de bloquer tout l'enregistrement de la fiche.
      if(!immoForm.id && !numeroInventaire){
        try {
          numeroInventaire=await genImmoNum(immoForm.categorie);
        } catch(numErr){
          console.warn("Génération auto du n° inventaire indisponible, code de secours utilisé :", numErr);
          const catCode = CAT_CODE_IMMO[immoForm.categorie] || "DIV";
          numeroInventaire="BB-"+catCode+"-"+String(Date.now()).slice(-6);
        }
      }
      const payload={...immoForm, numero_inventaire:numeroInventaire, employe_nom:emp?.nom||immoForm.employe_nom||"", valeur_acquisition:toFiniteNumber(immoForm.valeur_acquisition), duree_amortissement:toFiniteNumber(immoForm.duree_amortissement)||dureeAmortissementDefaut(immoForm.categorie), updated_at:new Date().toISOString()};
      // Postgres refuse une chaîne vide "" pour une colonne de type date : il faut null.
      ["date_acquisition","date_fin_garantie","prochaine_maintenance"].forEach(k=>{
        if(!payload[k]) payload[k]=null;
      });
      delete payload.id;
      let genererFicheRemisePour = null;
      if (immoForm.id) {
        const old=immobilisations.find(x=>String(x.id)===String(immoForm.id));
        const {error}=await supabase.from("immobilisations").update(payload).eq("id",immoForm.id);
        if(error) throw error;
        if(old && (old.ville!==payload.ville || old.localisation!==payload.localisation || old.employe_id!==payload.employe_id || old.statut!==payload.statut)){
          await supabase.from("mouvements_immobilisations").insert({immobilisation_id:String(immoForm.id),numero_inventaire:old.numero_inventaire||"",type_mouvement:"Mise à jour",ancienne_ville:old.ville||"",nouvelle_ville:payload.ville||"",ancienne_localisation:old.localisation||"",nouvelle_localisation:payload.localisation||"",ancien_employe:old.employe_nom||"",nouvel_employe:payload.employe_nom||"",effectue_par:session?.nom||"",notes:"Modification de la fiche"});
        }
        // Nouvelle affectation à quelqu'un (création OU changement de responsable) → fiche à générer automatiquement.
        if (payload.employe_id && (!old || old.employe_id!==payload.employe_id)) {
          genererFicheRemisePour = {...payload, id:immoForm.id, numero_inventaire:numeroInventaire};
        }
      } else {
        const {data,error}=await supabase.from("immobilisations").insert(payload).select().single();
        if(error) throw error;
        await supabase.from("mouvements_immobilisations").insert({immobilisation_id:String(data.id),numero_inventaire:data.numero_inventaire||"",type_mouvement:"Entrée",nouvelle_ville:data.ville||"",nouvelle_localisation:data.localisation||"",nouvel_employe:data.employe_nom||"",effectue_par:session?.nom||"",notes:"Création de l'immobilisation"});
        if (data.employe_id) genererFicheRemisePour = data;
      }
      await load(); setImmoShowForm(false); resetImmoForm(); showToast("Immobilisation enregistrée ✓");
      // Fiche de remise préremplie, générée et proposée automatiquement dès qu'un responsable est affecté.
      if (genererFicheRemisePour) {
        try {
          const emp2 = employes.find(e=>String(e.id)===String(genererFicheRemisePour.employe_id)) || emp;
          const titreFiche = "Fiche de remise — "+(genererFicheRemisePour.designation||"matériel");
          const signatures = await runSignatureFlow({
            titre: titreFiche,
            typeDocument: "fiche_remise",
            payload: { item: genererFicheRemisePour, employeId: genererFicheRemisePour.employe_id },
            signers: [
              { key:"collaborateur", label:"Le collaborateur", employeId: genererFicheRemisePour.employe_id, matricule: emp2?.matricule },
              { key:"entreprise", label:"Pour l'entreprise", employeId: session?.id, matricule: session?.matricule }
            ]
          });
          const pdf = await generateFicheRemisePdfBase64(genererFicheRemisePour, emp2, entreprise, signatures);
          await openPdf(pdf,"Fiche de remise");
        } catch(pdfErr) {
          console.warn("Fiche de remise non générée automatiquement :", pdfErr);
        }
      }
    } catch(e){ showToast("Erreur : "+e.message,false); } finally { setImmoSaving(false); }
  };

  const deleteImmobilisation = async (item) => {
    if(!isDirecteur()) return showToast("Action réservée à la Direction",false);
    if(!window.confirm("Supprimer cette immobilisation ? Son historique sera conservé.")) return;
    const {error}=await supabase.from("immobilisations").delete().eq("id",item.id);
    if(error) return showToast("Erreur : "+error.message,false);
    await supabase.from("mouvements_immobilisations").insert({immobilisation_id:String(item.id),numero_inventaire:item.numero_inventaire||"",type_mouvement:"Sortie",ancienne_ville:item.ville||"",ancienne_localisation:item.localisation||"",ancien_employe:item.employe_nom||"",effectue_par:session?.nom||"",notes:"Suppression de la fiche par la Direction"});
    await load(); showToast("Immobilisation supprimée");
  };

  const resetAchatForm = () => {
    setAchatForm({fournisseur_id:"",fournisseur_nom:"",ville:sessionVille()||"Dschang",date_commande:today(),date_livraison_prevue:"",notes:"",lignes:[]});
    setAchatLigne({produit_id:"",code:"",designation:"",quantite:"",prix_unitaire:""});
  };
  const addAchatLigne = () => {
    const q=toFiniteNumber(achatLigne.quantite), pu=toFiniteNumber(achatLigne.prix_unitaire);
    if(!achatLigne.designation.trim()||q<=0) return showToast("Renseigne la désignation et une quantité valide.",false);
    if(!achatLigne.code) return showToast("Choisissez le code de l'article.",false);
    setAchatForm(f=>({...f,lignes:[...f.lignes,{...achatLigne,quantite:q,prix_unitaire:pu,montant:q*pu}]}));
    setAchatLigne({produit_id:"",code:"",designation:"",quantite:"",prix_unitaire:""});
  };
  const saveAchatCommande = async () => {
    if(!achatForm.fournisseur_nom||!achatForm.ville||!achatForm.lignes.length) return showToast("Fournisseur, ville et au moins une ligne sont obligatoires.",false);
    setAchatSaving(true);
    try{
      const numero=await nextNumero("achat_compteur",`ACH-${currentYear()}-`);
      const total=achatForm.lignes.reduce((a,l)=>a+toFiniteNumber(l.montant),0);
      const reqCmd=supabase.from("achats_commandes").insert({...achatForm,numero,total,statut:"Commandée",cree_par:session?.nom||""});
      const {data:cree,error}=isDirecteur()?await reqCmd.select().single():await reqCmd;
      if(error) throw error;
      await load(); resetAchatForm(); setAchatFormOpen(false);
      if (isDirecteur() && cree) { showToast("Bon de commande "+numero+" créé — signez-le pour le valider"); setAchatSaving(false); bcSigner(cree); return; }
      showToast("Bon de commande "+numero+" créé ✓ — il est envoyé à la direction pour validation");
    }catch(e){showToast("Erreur : "+e.message,false);}finally{setAchatSaving(false);}
  };
  const receiveAchatCommande = async commande => {
    if(!window.confirm("Confirmer la réception de "+commande.numero+" ? Le stock des produits correspondants sera crédité.")) return;
    setAchatSaving(true);
    try{
      const {error}=await supabase.rpc("confirmer_reception_achat_transaction",{p_commande_id:String(commande.id),p_recu_par:session?.nom||"",p_notes:"Réception confirmée depuis l’ERP"});
      if(error) throw error;
      await load(); showToast("Réception confirmée et stock mis à jour ✓");
    }catch(e){showToast("Erreur : "+e.message,false);}finally{setAchatSaving(false);}
  };
  const createFactureAchat = async commande => {
    // Correctif du 30/09/2026 : rien n'empêchait jusqu'ici de créer (puis payer, voir
    // payFactureAchat) une facture fournisseur pour une commande jamais réceptionnée — le
    // bouton "Facture" était affiché pour toutes les commandes sans distinction. Une facture
    // avant réception reste parfois légitime (le fournisseur facture à l'avance), donc pas
    // bloquée, mais désormais signalée explicitement pour éviter une erreur ou un paiement
    // engagé par inadvertance sur une commande non reçue.
    if (commande.statut!=="Reçue" && !window.confirm("Cette commande (\""+commande.numero+"\") n'a pas encore été réceptionnée. Créer quand même une facture fournisseur ?")) return;
    const numero=prompt("Numéro de la facture fournisseur :","");
    if(!numero) return;
    const echeance=prompt("Date d’échéance (AAAA-MM-JJ) :",addDaysISO(today(),30))||addDaysISO(today(),30);
    const {error}=await supabase.from("achats_factures").insert({numero,commande_id:String(commande.id),commande_numero:commande.numero,fournisseur_id:commande.fournisseur_id,fournisseur_nom:commande.fournisseur_nom,ville:commande.ville,date_facture:today(),date_echeance:echeance,montant_ttc:commande.total||0,statut:"À payer",cree_par:session?.nom||""});
    if(error)return showToast("Erreur : "+error.message,false);
    await load();showToast("Facture fournisseur enregistrée ✓");
  };
  const payFactureAchat = async facture => {
    // Même logique défensive que createFactureAchat : avertit avant de régler une facture
    // dont la commande liée n'a jamais été réceptionnée, sans bloquer les cas légitimes
    // (paiement d'avance, commande créée hors de ce cycle, etc.).
    const commandeLiee = facture.commande_id ? achatsCommandes.find(c=>String(c.id)===String(facture.commande_id)) : null;
    if (commandeLiee && commandeLiee.statut!=="Reçue" && !window.confirm("La commande liée à cette facture (\""+commandeLiee.numero+"\") n'a pas encore été réceptionnée. Enregistrer quand même le paiement ?")) return;
    const reste=Math.max(0,toFiniteNumber(facture.montant_ttc)-achatsPaiements.filter(p=>String(p.facture_id)===String(facture.id)).reduce((a,p)=>a+toFiniteNumber(p.montant),0));
    const raw=prompt("Montant du paiement (FCFA) :",String(reste));
    const montant=toFiniteNumber(raw);
    if(montant<=0)return;
    const mode=prompt("Mode de paiement :","Virement bancaire")||"Virement bancaire";
    const {error}=await supabase.from("achats_paiements").insert({facture_id:String(facture.id),facture_numero:facture.numero,fournisseur_nom:facture.fournisseur_nom,ville:facture.ville,date_paiement:today(),montant,mode_paiement:mode,enregistre_par:session?.nom||""});
    if(error)return showToast("Erreur : "+error.message,false);
    const nouveauReste=Math.max(0,reste-montant);
    // Correctif du 30/09/2026 : cette mise à jour du statut de la facture n'était jusqu'ici
    // jamais vérifiée — en cas d'échec (RLS, réseau...), le paiement était déjà enregistré et
    // annoncé réussi, mais la facture restait affichée "À payer" indéfiniment, ce qui aurait pu
    // laisser croire à tort qu'elle n'avait pas encore été payée.
    const { error: statutErr } = await supabase.from("achats_factures").update({statut:nouveauReste<=0?"Payée":"Partiellement payée"}).eq("id",facture.id);
    if (statutErr) { showToast("Paiement enregistré, mais la mise à jour du statut de la facture a échoué : "+statutErr.message, false); await load(); return; }
    await load();showToast("Paiement fournisseur enregistré ✓");
  };

  // Correctif du 05/10/2026 : « Stock & Produits » doit montrer ce qui se trouve au MAGASIN CENTRAL
  // (ligne "Magasin central" des stocks par dépôt). produits.stock est un total qui dérivait (les
  // factures de ville y touchaient aussi) ; on ne s'y rabat que si la ligne centrale n'existe pas
  // ou n'est pas visible pour cet utilisateur.
  const stockCentral = (p) => {
    const row = depotStocks.find(s=>s.ville==="Magasin central"&&s.produit_nom===p?.nom);
    return row ? toFiniteNumber(row.quantite) : toFiniteNumber(p?.stock);
  };

  const stockTotalVisible = canSeeAllCities()
    ? produits.reduce((s,p)=>s+stockCentral(p),0)+depotStocks.filter(row=>row.ville!=="Magasin central").reduce((s,row)=>s+toFiniteNumber(row.quantite),0)
    : isDschangUser()
      ? produits.reduce((s,p)=>s+toFiniteNumber(p.stock),0)
      : depotStocks.reduce((s,row)=>s+toFiniteNumber(row.quantite),0);
  const totalVentes   = ventes.reduce((s,v)=>s+toFiniteNumber(v.montant),0);
  // Correctif du 02/10/2026 : la catégorie de dépense "Personnel (hors fiche de paie)" désigne
  // de l'argent versé à du personnel en dehors des fiches de paie — c'est donc, par nature, du
  // salaire, pas une dépense diverse. Elle était pourtant comptée avec l'électricité/le loyer/
  // etc. dans "Autres dépenses", ce qui gonflait les dépenses et sous-comptait les salaires
  // réels versés. Elle est maintenant retirée de "Autres dépenses" et ajoutée à "Salaires" —
  // seule la répartition par ligne change, le total des sorties (totalDepenses) reste identique.
  const estDepensePersonnelHorsFichePaie = (d) => d?.categorie === "Personnel (hors fiche de paie)";
  const totalDepensesManuelles = depenses.filter(d=>!estDepensePersonnelHorsFichePaie(d)).reduce((s,d)=>s+d.montant,0);
  const totalAchatsFournisseurs = achatsFactures.reduce((s,f)=>s+toFiniteNumber(f.montant_ttc),0);
  const totalSalaires = bulletinsPaie.reduce((s,b)=>s+toFiniteNumber(b.net),0)
    + depenses.filter(estDepensePersonnelHorsFichePaie).reduce((s,d)=>s+toFiniteNumber(d.montant),0);
  // Correctif du 30/09/2026 : l'amortissement des immobilisations est calculé correctement
  // (voir plus haut, utilisé jusqu'ici seulement dans le PDF téléchargeable) mais n'était
  // jamais déduit ici — ce "Bénéfice net" affiché à l'écran était donc systématiquement
  // supérieur au chiffre du rapport PDF, sans que rien ne l'indique.
  const totalAmortissementCumule = immobilisations.reduce((s,item)=>s+cumulAmortiADate(item),0);
  // Correctif du 05/10/2026 : à la demande de la direction, les "dépenses" ne comptent plus que
  // ce qui sert à faire fonctionner l'entreprise (dépenses saisies, achats fournisseurs, salaires).
  // L'amortissement des immobilisations (étalement de leur prix d'achat) est présenté à part.
  const totalDepenses = totalDepensesManuelles + totalAchatsFournisseurs + totalSalaires;
  const benefice      = totalVentes - totalDepenses; // bénéfice d'exploitation (avant amortissement)
  const beneficeApresAmort = benefice - totalAmortissementCumule;
  const ventesFiltered = rapportDates.debut && rapportDates.fin ? ventes.filter(v=>v.date>=rapportDates.debut&&v.date<=rapportDates.fin) : ventes;
  const depFiltered    = rapportDates.debut && rapportDates.fin ? depenses.filter(d=>d.date>=rapportDates.debut&&d.date<=rapportDates.fin) : depenses;
  const totalVF = ventesFiltered.reduce((s,v)=>s+v.montant,0);
  const totalDF = depFiltered.reduce((s,d)=>s+d.montant,0);
  const tvaTaux = getTvaRate(entreprise);
  const calcTTC = (facture) => calculateInvoiceTotals(facture, entreprise).ttc;
  const isPaiementValide = (p) => String(p?.statut||"").toLowerCase().startsWith("valid");
  const paiementFactureMatch = (p,f) => p?.facture_id ? String(p.facture_id)===String(f.id) : p?.facture_numero===f.numero;
  const totalPayeValide = (facture) => paiements.filter(p=>isPaiementValide(p)&&paiementFactureMatch(p,facture)).reduce((sum,p)=>sum+toFiniteNumber(p.montant),0);
  const resteFacture = (facture) => (String(facture?.statut||"")==="Payée") ? 0 : Math.max(0,calcTTC(facture)-totalPayeValide(facture));
  const encaissements = paiements.filter(isPaiementValide).reduce((sum,p)=>sum+toFiniteNumber(p.montant),0);
  const creances = factures.reduce((sum,f)=>sum+resteFacture(f),0);
  const facPayees = factures.filter(f=>resteFacture(f)<=0).length;
  const facAttente = factures.filter(f=>resteFacture(f)>0).length;
  const facFiltrees = factures.filter(f=>{
    const statusOk = factureStatutFilter==="tous" || (factureStatutFilter==="payee" ? resteFacture(f)<=0 : resteFacture(f)>0);
    if (!statusOk) return false;
    const query = factureSearch.trim().toLowerCase();
    if (!query) return true;
    const searchable = [f.numero,f.client_nom,f.date_emission,f.date_echeance,f.reglement,f.statut].filter(Boolean).join(" ").toLowerCase();
    return searchable.includes(query);
  });
  const facItems = (()=>{
    if (factureVue==="liste") return facFiltrees;
    const groupes = {};
    facFiltrees.forEach(f=>{
      const nom = (f.client_nom||"").trim() || "Client non défini";
      const g = groupes[nom] = groupes[nom] || {nom, factures:[], total:0, paye:0, reste:0};
      const ttc = calculateInvoiceTotals(f,entreprise).ttc;
      const reste = resteFacture(f);
      g.factures.push(f); g.total += ttc; g.reste += reste; g.paye += Math.max(0,ttc-reste);
    });
    const liste = Object.values(groupes).sort((a,b)=> (b.reste-a.reste) || a.nom.localeCompare(b.nom,"fr"));
    const ouvrirTout = !!factureSearch.trim();
    const out = [];
    liste.forEach(g=>{
      const ouvert = ouvrirTout || !!facOuverts[g.nom];
      out.push({__groupe:true, id:"grp-"+g.nom, nom:g.nom, nb:g.factures.length, total:g.total, paye:g.paye, reste:g.reste, ouvert});
      if (ouvert) g.factures.slice().sort((a,b)=>String(b.date_emission||"").localeCompare(String(a.date_emission||""))).forEach(f=>out.push(f));
    });
    return out;
  })();

  // ── Coût de revient "ingrédients" d'un produit, calculé automatiquement à partir de sa recette
  // (quantité de chaque ingrédient par bouteille × prix d'achat de l'ingrédient). ──
  const coutIngredientsProduit = (produitId) => {
    return recettes
      .filter(r=>String(r.produit_id)===String(produitId))
      .reduce((s,r)=>{
        const ing = ingredients.find(i=>String(i.id)===String(r.ingredient_id));
        return s + toFiniteNumber(r.quantite_par_bouteille) * toFiniteNumber(ing?.prix_unitaire);
      },0);
  };
  // Coût "effectif" d'un produit : celui calculé depuis sa recette dès qu'au moins une ligne
  // existe, sinon on retombe sur le coût de revient saisi manuellement (compatibilité).
  const coutRevientEffectif = (produit) => {
    const aRecette = recettes.some(r=>String(r.produit_id)===String(produit?.id));
    return aRecette ? coutIngredientsProduit(produit.id) : toFiniteNumber(produit?.cout_revient);
  };

  // ── Indicateurs de gestion avancés ──
  const tresorerie = encaissements - totalDepenses; // profit réel encaissé (base caisse, pas comptable)
  const facturesEnRetard = factures.filter(f=>resteFacture(f)>0 && f.date_echeance && endOfLocalDay(f.date_echeance) < new Date());
  const montantEnRetard = facturesEnRetard.reduce((s,f)=>s+calcTTC(f),0);
  const margeGlobaleEstimee = (() => {
    let cout = 0;
    ventes.forEach(v=>{ const p = produits.find(pr=>pr.nom===v.produit); if (p) cout += coutRevientEffectif(p)*v.qte; });
    return totalVentes - cout;
  })();
  const margeGlobalePct = totalVentes>0 ? Math.round((margeGlobaleEstimee/totalVentes)*100) : 0;
  const objectifsEnDanger = (() => {
    const now = new Date();
    const joursEcoules = now.getDate();
    const joursDansMois = new Date(now.getFullYear(), now.getMonth()+1, 0).getDate();
    const avancementAttendu = joursEcoules/joursDansMois;
    const mois = localISODate(now).slice(0,7);
    return employes.filter(e=>{ const p=safeJsonParse(e.permissions,[]); return e.actif&&!estRoleDirecteur(e.role,e.poste)&&Array.isArray(p)&&p.includes("ventes"); }).filter(e=>{
      const obj = objectifs[e.id];
      if (!obj || !obj.montant) return false;
      const realMnt = ventes.filter(v=>v.employe===e.nom && v.date&&v.date.slice(0,7)===mois).reduce((s,v)=>s+v.montant,0);
      const avancementReel = realMnt/obj.montant;
      return avancementReel < avancementAttendu - 0.15; // en retard de plus de 15 points sur le rythme attendu
    });
  })();

  // Navigation V2.5 : la Direction voit tous les modules métier.
  // Les autres utilisateurs restent limités à leurs permissions explicites.
  // Immobilisations est réservé à la Direction (ou permission explicite) : la liste complète
  // du parc matériel de tous les employés n'a pas à être visible par chacun. Un collaborateur
  // retrouve SES PROPRES équipements affectés dans son profil ("Mes équipements"), pas ici.
  const navItems = ALL_MODULES.filter(n=>
    (isDirecteur() || can(n.id)) &&
    n.cat!=="permission_only" &&
    n.cat!=="header_only" &&
    (n.id!=="production" || canSeeAllCities() || isDschangUser()) &&
    (n.id!=="caisse" || isDirecteur()) &&
    // Correctif du 02/10/2026 : "Livraisons vers dépôts" n'est plus un onglet séparé du menu —
    // c'est maintenant un onglet à l'intérieur de "Parcours du stock" (fusion demandée). La
    // permission "livraisons" existe toujours (ALL_MODULES n'est pas touché, donc elle reste
    // assignable depuis la fiche employé), seule son entrée dans le menu latéral disparaît.
    n.id!=="livraisons"
  );
  // « Mon activité » : visible par TOUS les employés (chacun ne voit que ses propres chiffres).
  const navItemsAvecActivite = (() => {
    const i = navItems.findIndex(n=>n.id==="tableau");
    const extra = { id:"mon_activite", label:"Mon activité", cat:"principal" };
    let items = i>=0 ? [...navItems.slice(0,i+1), extra, ...navItems.slice(i+1)] : [extra, ...navItems];
    // « Journal d'activité » : réservé à la direction.
    if (isDirecteur()) {
      const j = { id:"journal_activite", label:"Journal d'activité", cat:"admin" };
      const k = items.findIndex(n=>n.id==="parametres");
      items = k>=0 ? [...items.slice(0,k+1), j, ...items.slice(k+1)] : [...items, j];
    }
    {
      const a = { id:"a_cocher", label:"Factures à cocher", cat:"finance" };
      const k = items.findIndex(n=>n.id==="paiements");
      items = k>=0 ? [...items.slice(0,k+1), a, ...items.slice(k+1)] : [...items, a];
    }
    {
      const an = { id:"annonces", label:"Annonces", cat:"communication" };
      const k = items.findIndex(n=>n.id==="messagerie");
      items = k>=0 ? [...items.slice(0,k+1), an, ...items.slice(k+1)] : [...items, an];
    }
    {
      const dd = { id:"demandes_depense", label:"Demandes de dépense", cat:"finance" };
      const k = items.findIndex(n=>n.id==="a_cocher");
      items = k>=0 ? [...items.slice(0,k+1), dd, ...items.slice(k+1)] : [...items, dd];
    }
    return items;
  })();
  const rattacherVente = construireRattachementClients(clients, factures);
  // Le CODE client est la référence. Quand deux fiches portent le même nom, on affiche en plus
  // l'adresse et la ville pour les distinguer dans les listes déroulantes.
  const nomsDoubles = nomsClientsEnDouble(clients);
  const etiquetteClient = (c) => c.nom+" ("+c.code+")"+(nomsDoubles[normNomClient(c.nom)] ? " — "+([c.adresse,c.ville].filter(Boolean).join(", ")||"adresse manquante") : "");
  const trouverClient = (nom, code) => (code && clients.find(c=>c.code===code)) || clients.find(c=>c.nom===nom);
  const CAT_LABELS = { principal:null, commercial:"Commercial", stock:"Opérations", finance:"Finance", communication:"Communication", admin:"Administration" };
  const groupNavItems = (items) => {
    const order = ["principal","commercial","stock","finance","communication","admin"];
    const byCat = {};
    items.forEach(n=>{ const c = n.cat || (n.id==="paiements" ? "finance" : "commercial"); (byCat[c] = byCat[c] || []).push(n); });
    return order.filter(c=>byCat[c]).map(c=>({ cat:c, items:byCat[c] }))
      .concat(Object.keys(byCat).filter(c=>!order.includes(c)).map(c=>({ cat:c, items:byCat[c] })));
  };
  const chatUnreadTotal = Object.values(chatUnreadCounts)
    .reduce((sum,value)=>sum+Math.max(0,toFiniteNumber(value,0)),0);
  const chatUnread = chatUnreadTotal>0;
  const chatConversationKey = (thread) =>
    thread===null ? GENERAL_CHAT_KEY : String(thread||"");

  const refreshUnreadCounts = async () => {
    if (!session?.matricule || !can("chat")) {
      setChatUnreadCounts({});
      return;
    }

    const [{data:reads,error:readsError},{data:messagesData,error:messagesError}] = await Promise.all([
      supabase.from("message_lectures")
        .select("conversation_cle,lu_jusqua")
        .eq("utilisateur_matricule",session.matricule),
      supabase.from("messages")
        .select("id,sender_matricule,destinataire_matricule,created_at")
        .or(`destinataire_matricule.is.null,destinataire_matricule.eq.${session.matricule}`)
        .order("created_at",{ascending:false})
        .limit(2000)
    ]);

    if (readsError) {
      console.error("Chargement des lectures du chat :",readsError.message);
      return;
    }
    if (messagesError) {
      console.error("Chargement des messages non lus :",messagesError.message);
      return;
    }

    const readMap = {};
    (reads||[]).forEach(row=>{
      readMap[String(row.conversation_cle)] = new Date(row.lu_jusqua||0).getTime();
    });

    const counts = {};
    (messagesData||[]).forEach(message=>{
      if (message.sender_matricule===session.matricule) return;

      let key = null;
      if (message.destinataire_matricule==null) {
        key = GENERAL_CHAT_KEY;
      } else if (message.destinataire_matricule===session.matricule) {
        key = String(message.sender_matricule||"");
      }
      if (!key) return;

      const messageTime = new Date(message.created_at||0).getTime();
      const lastReadTime = readMap[key] || 0;
      if (messageTime>lastReadTime) counts[key]=(counts[key]||0)+1;
    });

    setChatUnreadCounts(counts);
  };

  const markConversationRead = async (thread=activeChatThread) => {
    if (!session?.matricule || !can("chat")) return;
    const key = chatConversationKey(thread);
    const { error } = await supabase.rpc("marquer_conversation_lue",{
      p_utilisateur_matricule:session.matricule,
      p_conversation_cle:key
    });
    if (error) {
      console.error("Marquage de la conversation comme lue :",error.message);
      return;
    }
    setChatUnreadCounts(prev=>{
      if (!prev[key]) return prev;
      return {...prev,[key]:0};
    });
  };

  useEffect(()=>{
    if (!session?.matricule || !can("chat")) {
      setChatUnreadCounts({});
      return;
    }

    let cancelled=false;
    const initialize = async () => {
      const { error } = await supabase.rpc("initialiser_lectures_chat",{
        p_utilisateur_matricule:session.matricule
      });
      if (error) console.error("Initialisation du suivi de lecture :",error.message);
      if (!cancelled) await refreshUnreadCounts();
    };
    initialize();
    return ()=>{ cancelled=true; };
  },[session]);

  const openChat = () => {
    setMobileChatConversationOpen(false);
    setPage("chat");
  };
  const [isMobile, setIsMobile] = useState(false);
  // Une conversation ouverte à l'écran : ses alertes sont lues d'office.
  useEffect(()=>{
    if (!session || page!=="chat") return;
    if (isMobile && !mobileChatConversationOpen) return;
    const cle = activeChatThread==null ? "" : String(activeChatThread);
    const tab = activeChatThread==null ? "messages_general" : "messages_prive";
    const ids = notifs.filter(n=>!n.lue && n.cible_table===tab && String(n.cible_id||"")===cle).map(n=>n.id);
    if (ids.length) marquerNotifsLues(ids);
    // eslint-disable-next-line
  },[page,activeChatThread,mobileChatConversationOpen,notifs,isMobile]);
  const MAIL_REMINDER_MS = 4*60*60*1000; // rappel visuel toutes les 4h (pas un vrai comptage de mails)
  const [mailReminder, setMailReminder] = useState(()=>{
    try {
      const last = localStorage.getItem("azongbio_last_mail_check");
      return !last || (Date.now()-parseInt(last,10)) > MAIL_REMINDER_MS;
    } catch(e) { return true; }
  });
  useEffect(()=>{
    const t = setInterval(()=>{
      try {
        const last = localStorage.getItem("azongbio_last_mail_check");
        setMailReminder(!last || (Date.now()-parseInt(last,10)) > MAIL_REMINDER_MS);
      } catch(e) {}
    }, 5*60*1000);
    return ()=>clearInterval(t);
  },[]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(()=>{
    if (typeof window === "undefined") return;
    const handler = () => setIsMobile(window.innerWidth <= 640);
    handler();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  },[]);
  useEffect(()=>{ setMobileMenuOpen(false); },[page]);

  /* ── Saves ── */
  const saveVente = async () => {
    if (!form.client) return showToast("Client requis",false);
    if (!form.depotId) return showToast("Ville / dépôt de vente requis",false);

    const depot = depots.find(d=>String(d.id)===String(form.depotId));
    if (!depot) return showToast("Dépôt introuvable",false);

    const lignesVente = (form.lignesVente||[]).map(ligne=>{
      const prod=produits.find(p=>p.nom===ligne.produit);
      return {
        produit_id:String(prod?.id||""),
        produit:ligne.produit,
        qte:Number(ligne.qte),
        prix_unitaire:toFiniteNumber(prod?.prix),
        montant:Number(ligne.qte)*toFiniteNumber(prod?.prix)
      };
    });
    if (lignesVente.length===0) return showToast("Ajoutez au moins un produit",false);
    if (lignesVente.some(l=>!l.produit_id||!l.produit||!Number.isInteger(l.qte)||l.qte<=0)) {
      return showToast("Produits ou quantités invalides",false);
    }

    for (const ligne of lignesVente) {
      const disponible=getDepotStock(form.depotId,ligne.produit);
      if (ligne.qte>disponible) {
        return showToast(
          `Stock insuffisant à ${depot.ville} pour ${ligne.produit} (${disponible} btl disponibles)`,
          false
        );
      }
    }

    setLoading(true);
    const { error } = await supabase.rpc("enregistrer_vente_depot_transaction", {
      p_date:form.date||today(),
      p_client:form.client,
      p_employe:session.nom,
      p_depot_id:String(form.depotId),
      p_ville:depot.ville||"",
      p_lignes:lignesVente
    });
    if (error) {
      showToast("Vente non enregistrée : "+error.message,false);
      setLoading(false);
      return;
    }
    await load();
    setLoading(false);
    showToast("Vente enregistrée et stock de "+depot.ville+" déduit ✓");
    closeModal();
  };



  // ── Caisse (espèces) : solde attendu = départ + espèces reçues + autres entrées − dépenses en espèces − versements en banque − autres sorties ──
  const [caisseMvts, setCaisseMvts] = useState([]);
  const [caisseParam, setCaisseParam] = useState({debut:"2026-09-01",depart:0});
  const [caisseForm, setCaisseForm] = useState(null);
  const [caisseCfg, setCaisseCfg] = useState(null);
  const [caisseCompte, setCaisseCompte] = useState("");
  const [caisseExpl, setCaisseExpl] = useState({});
  const caisseChargee = useRef(false);
  const expliquerEcartCaisse = async (m) => {
    const texte = String(caisseExpl[m.id]||"").trim();
    if (!texte) return showToast("Écrivez l'explication de l'écart",false);
    const { error } = await supabase.from("caisse_mouvements").update({ explication:texte, a_expliquer:false }).eq("id",m.id);
    if (error) return showToast("Erreur : "+error.message,false);
    setCaisseExpl(x=>({...x,[m.id]:""}));
    await loadCaisse(); showToast("Écart expliqué et clos ✓");
  };
  const loadCaisse = async () => {
    try {
      const [m,p] = await Promise.all([
        supabase.from("caisse_mouvements").select("*").order("date",{ascending:false}).order("created_at",{ascending:false}),
        supabase.from("parametres").select("cle,valeur").in("cle",["caisse_debut","caisse_solde_depart"])
      ]);
      if (m.data) setCaisseMvts(m.data);
      if (p.data) { const g=k=>(p.data.find(x=>x.cle===k)||{}).valeur; setCaisseParam({debut:String(g("caisse_debut")||"2026-09-01").slice(0,10),depart:toFiniteNumber(g("caisse_solde_depart"),0)}); }
    } catch(_) {}
  };
  useEffect(()=>{ if ((page==="caisse"||!caisseChargee.current) && session && estRoleDirecteur(session.role,session.poste)) { caisseChargee.current=true; loadCaisse(); } /* eslint-disable-next-line */ },[page,session?.id]);
  const caisseCalc = (() => {
    const deb = caisseParam.debut||"2026-09-01";
    const estEsp = v => /esp/i.test(String(v||""));
    const jour = v => String(v||"").slice(0,10);
    const valide = p => String(p?.statut||"").toLowerCase().startsWith("valid");
    const recus = paiements.filter(p=>valide(p) && estEsp(p.moyen) && jour(p.date_versement)>=deb);
    const deps = depenses.filter(x=>jour(x.date)>=deb);
    const achs = achatsPaiements.filter(a=>estEsp(a.mode_paiement) && jour(a.date_paiement)>=deb);
    const mv = caisseMvts.filter(m=>jour(m.date)>=deb);
    const somme = l => l.reduce((t,x)=>t+toFiniteNumber(x.montant),0);
    const recu = somme(recus);
    const dep = somme(deps.filter(x=>estEsp(x.moyen||"Espèces")));
    const ach = somme(achs);
    const vers = somme(mv.filter(m=>m.type==="versement_banque"));
    const sortieAutre = somme(mv.filter(m=>m.type==="sortie_autre"));
    const entreeAutre = somme(mv.filter(m=>m.type==="entree_autre"));
    const ajust = somme(mv.filter(m=>m.type==="ajustement"));
    const solde = toFiniteNumber(caisseParam.depart) + recu + entreeAutre + ajust - dep - ach - vers - sortieAutre;
    const journal = [
      ...recus.map(p=>({k:"p"+p.id,date:jour(p.date_versement),type:"Espèces reçues",label:(p.facture_numero||"")+" · "+(p.client_nom||""),detail:"Reçu par "+(p.soumis_par||"—")+(p.commentaire?" · "+p.commentaire:""),entree:toFiniteNumber(p.montant),sortie:0})),
      ...deps.map(x=>{ const esp=estEsp(x.moyen||"Espèces"); return {k:"d"+x.id,date:jour(x.date),type:"Dépense",label:(x.code?x.code+" · ":"")+(x.libelle||""),detail:esp?"":"Payée par "+(x.moyen||"autre moyen")+" — ne sort pas de la caisse",entree:0,sortie:esp?toFiniteNumber(x.montant):0,dep:x,barre:!esp}; }),
      ...achs.map(a=>({k:"a"+a.id,date:jour(a.date_paiement),type:"Achat fournisseur",label:(a.fournisseur_nom||"")+" · "+(a.facture_numero||""),detail:"",entree:0,sortie:toFiniteNumber(a.montant)})),
      ...mv.map(m=>({k:"m"+m.id,date:jour(m.date),type:({versement_banque:"Versement en banque",sortie_autre:"Autre sortie",entree_autre:"Autre entrée",ajustement:"Ajustement (comptage)"})[m.type]||m.type,label:m.libelle||"",detail:(m.cree_par?"Saisi par "+m.cree_par:"")+(m.a_expliquer&&!m.explication?" · ⚠ à expliquer":"")+(m.explication?" · Expliqué : "+m.explication:""),entree:(m.type==="entree_autre"||(m.type==="ajustement"&&toFiniteNumber(m.montant)>0))?Math.abs(toFiniteNumber(m.montant)):0,sortie:(m.type==="versement_banque"||m.type==="sortie_autre"||(m.type==="ajustement"&&toFiniteNumber(m.montant)<0))?Math.abs(toFiniteNumber(m.montant)):0,mvt:m}))
    ].sort((a,b)=>b.date.localeCompare(a.date));
    return { deb, recu, dep, ach, vers, sortieAutre, entreeAutre, ajust, solde, journal };
  })();
  const sauverCaisseMvt = async () => {
    const f = caisseForm||{};
    const montant = toFiniteNumber(f.montant);
    if (!(montant>0)) return showToast("Indiquez un montant supérieur à 0",false);
    if (f.type!=="versement_banque" && !String(f.libelle||"").trim()) return showToast("Indiquez le motif",false);
    const { error } = await supabase.from("caisse_mouvements").insert({ date:f.date||today(), type:f.type, montant, libelle:String(f.libelle||"").trim(), cree_par:session?.nom||"" });
    if (error) return showToast("Erreur : "+error.message,false);
    setCaisseForm(null); await loadCaisse(); showToast("Enregistré dans la caisse ✓");
  };
  const supprimerCaisseMvt = async (m) => {
    if (!window.confirm("Supprimer ce mouvement de caisse ("+FCFA(Math.abs(toFiniteNumber(m.montant)))+") ?")) return;
    const { error } = await supabase.from("caisse_mouvements").delete().eq("id",m.id);
    if (error) return showToast("Erreur : "+error.message,false);
    await loadCaisse(); showToast("Mouvement supprimé ✓");
  };
  const changerMoyenDepense = async (x, moyen) => {
    const { error } = await supabase.from("depenses").update({moyen}).eq("id",x.id);
    if (error) return showToast("Erreur : "+error.message,false);
    await load(); showToast("Dépense mise à jour ✓");
  };
  const enregistrerEcartCaisse = async () => {
    const compte = Math.round(toFiniteNumber(caisseCompte,NaN));
    if (!Number.isFinite(compte) || String(caisseCompte).trim()==="") return showToast("Indiquez le montant compté",false);
    const ecart = compte - Math.round(caisseCalc.solde);
    if (ecart===0) return showToast("Aucun écart : la caisse est juste ✓");
    if (!window.confirm("Enregistrer un écart de "+(ecart>0?"+":"")+FCFA(ecart)+" ? Le solde deviendra "+FCFA(compte)+".")) return;
    const { error } = await supabase.from("caisse_mouvements").insert({ date:today(), type:"ajustement", montant:ecart, libelle:"Comptage de la caisse du "+fmtDate(today())+" : "+FCFA(compte)+" comptés", cree_par:session?.nom||"", a_expliquer:true });
    if (error) return showToast("Erreur : "+error.message,false);
    setCaisseCompte(""); await loadCaisse(); showToast("Écart enregistré ✓");
  };
  const sauverCaisseCfg = async () => {
    const c = caisseCfg||{};
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(c.debut||""))) return showToast("Date de début invalide",false);
    const r1 = await supabase.from("parametres").update({valeur:String(c.debut)}).eq("cle","caisse_debut");
    const r2 = await supabase.from("parametres").update({valeur:String(Math.round(toFiniteNumber(c.depart)))}).eq("cle","caisse_solde_depart");
    if (r1.error||r2.error) return showToast("Erreur : "+((r1.error||r2.error).message),false);
    setCaisseCfg(null); await loadCaisse(); showToast("Réglage de la caisse enregistré ✓");
  };

  // ── Accord préalable du directeur pour les dépenses ──
  const [demandes, setDemandes] = useState([]);
  const [demMsgs, setDemMsgs] = useState([]);
  const [demFiltre, setDemFiltre] = useState("");
  const [demAction, setDemAction] = useState(null);
  const [demEnvoi, setDemEnvoi] = useState(false);
  const [demCodesOuvert, setDemCodesOuvert] = useState(false);
  const codeSansDemande = (c) => !!(codeInfo(c)||{}).sans_demande;
  const accordRequis = (c) => !isDirecteur() && !codeSansDemande(c);
  const chargerDemandes = async () => {
    try {
      const r1 = await supabase.from("demandes_depense").select("*").order("created_at",{ascending:false}).limit(300);
      if (r1.error) return;
      setDemandes(r1.data||[]);
      const r2 = await supabase.from("demandes_depense_messages").select("*").order("created_at",{ascending:true}).limit(2000);
      if (!r2.error) setDemMsgs(r2.data||[]);
    } catch(_) {}
  };
  useEffect(()=>{
    if (!session) return;
    chargerDemandes();
    const t = setInterval(()=>{ if (document.visibilityState==="visible") chargerDemandes(); }, 30000);
    return ()=>clearInterval(t);
    // eslint-disable-next-line
  },[session?.id, page==="demandes_depense"]);
  // ── Annonces aux employés avec accusé de lecture ──
  const [annonces, setAnnonces] = useState([]);
  const [annLectures, setAnnLectures] = useState([]);
  const [annReporte, setAnnReporte] = useState({});
  const [annBusy, setAnnBusy] = useState(false);
  const [annNouveau, setAnnNouveau] = useState(false);
  const [annTitre, setAnnTitre] = useState("");
  const [annMsg, setAnnMsg] = useState("");
  const [annPage, setAnnPage] = useState("");
  const [annIA, setAnnIA] = useState({busy:"",prop:null,err:""});
  const chargerAnnonces = async () => {
    try {
      const a = await supabase.from("annonces").select("*").order("created_at",{ascending:true}).limit(50);
      if (a.error) return;
      setAnnonces(a.data||[]);
      const l = await supabase.from("annonces_lectures").select("*").limit(3000);
      if (!l.error) setAnnLectures(l.data||[]);
    } catch(_) {}
  };
  useEffect(()=>{
    if (!session) return;
    chargerAnnonces();
    const t = setInterval(()=>{ if (document.visibilityState==="visible") chargerAnnonces(); }, 60000);
    return ()=>clearInterval(t);
    // eslint-disable-next-line
  },[session?.id]);
  const annonceALire = () => (!session || isDirecteur()) ? null : (annonces.find(a=>!annReporte[a.id] && !annLectures.some(l=>String(l.annonce_id)===String(a.id))) || null);
  const annonceLire = async (a, aller) => {
    setAnnBusy(true);
    try {
      const { error } = await supabase.rpc("annonce_lire",{ p_id:a.id });
      if (error) throw error;
      setAnnLectures(ls=>[...ls,{annonce_id:a.id}]);
      if (aller && a.page) setPage(a.page);
      showToast("Merci : le directeur est prévenu que vous avez lu ✓");
    } catch(e) { showToast("Erreur : "+((e&&e.message)||"lecture impossible"),false); }
    setAnnBusy(false);
  };
  const annonceRappeler = async (a) => {
    setAnnBusy(true);
    try {
      const { data, error } = await supabase.rpc("annonce_rappeler",{ p_id:a.id });
      if (error) throw error;
      showToast(Number(data)>0 ? "Rappel envoyé à "+data+" employé(s) ✓" : "Tout le monde a déjà lu ✓");
    } catch(e) { showToast("Erreur : "+((e&&e.message)||"rappel impossible"),false); }
    setAnnBusy(false);
  };
  const annonceCibles = () => employes.filter(e=>e.actif!==false && String(e.statut_rh||"Actif").toLowerCase()!=="inactif" && e.matricule && !estRoleDirecteur(e.role,e.poste));
  const annonceLuPar = (a, e) => annLectures.find(l=>String(l.annonce_id)===String(a.id) && ((e.auth_user_id && String(l.lecteur_uid)===String(e.auth_user_id)) || (l.lecteur_matricule && l.lecteur_matricule===e.matricule)));
  const annoncePublier = async () => {
    const n = annonceCibles().length;
    if (!window.confirm("Envoyer cette annonce à "+n+" employé(s) ?\n\nIls la verront à l'ouverture de l'application et recevront une notification. Vous serez prévenu à chaque lecture.")) return;
    setAnnBusy(true);
    try {
      const { error } = await supabase.rpc("annonce_publier",{ p_titre:annTitre.trim(), p_message:annMsg.trim(), p_page:annPage||null });
      if (error) throw error;
      setAnnTitre(""); setAnnMsg(""); setAnnPage(""); setAnnNouveau(false);
      await chargerAnnonces();
      showToast("Annonce envoyée à "+n+" employé(s) ✓");
    } catch(e) { showToast("Erreur : "+((e&&e.message)||"envoi impossible"),false); }
    setAnnBusy(false);
  };
  const annonceNonLue = () => !isDirecteur() && annonces.some(a=>!annLectures.some(l=>String(l.annonce_id)===String(a.id)));
  const annonceIA = async (mode) => {
    if (!annMsg.trim() && !annTitre.trim()) { setAnnIA({busy:"",prop:null,err:"Écrivez d'abord votre message, puis demandez à l'IA de le corriger ou de le reformuler."}); return; }
    setAnnIA({busy:mode,prop:null,err:""});
    try {
      const { data:{ session:authS } } = await supabase.auth.getSession();
      const r = await fetch("/api/reformuler",{ method:"POST", headers:{"Content-Type":"application/json", ...(authS?.access_token?{"Authorization":"Bearer "+authS.access_token}:{})}, body:JSON.stringify({ mode, titre:annTitre, message:annMsg }) });
      const j = await r.json().catch(()=>({}));
      if (!r.ok) {
        const m = { ia_non_configuree:"L'IA n'est pas encore activée sur le serveur : il manque sa clé (voir les explications de Claude).", non_autorise:"Réservé au directeur (reconnectez-vous si besoin).", trop_de_demandes:"Trop de demandes : réessayez dans quelques minutes.", texte_vide:"Écrivez d'abord votre message.", ia_indisponible:"L'IA ne répond pas pour le moment : réessayez dans un instant." }[j.erreur];
        throw new Error(m||"L'IA est momentanément indisponible.");
      }
      setAnnIA({busy:"",prop:{ titre:j.titre!=null?j.titre:annTitre, message:j.message||annMsg },err:""});
    } catch(e) { setAnnIA({busy:"",prop:null,err:(e&&e.message)||"L'IA est momentanément indisponible."}); }
  };
  // ── Fiches de paie envoyées pour signature ──
  const [ficheReview, setFicheReview] = useState(null);
  const [ficheBusy, setFicheBusy] = useState(false);
  const [ficheReporte, setFicheReporte] = useState({});
  const [ficheMois, setFicheMois] = useState("");
  const ficheRappelFait = useRef(false);
  useEffect(()=>{
    // Rappels automatiques (tous les 3 jours, 3 au maximum) : déclenchés une fois par ouverture de l'application
    if (!session || ficheRappelFait.current) return;
    ficheRappelFait.current = true;
    const t = setTimeout(()=>{ supabase.rpc("fiche_rappeler",{p_forcer:false,p_mois:null}).then(()=>{},()=>{}); }, 6000);
    return ()=>clearTimeout(t);
    // eslint-disable-next-line
  },[session?.id]);
  const ficheMoisLbl = (m) => { const d = new Date(String(m||"").slice(0,7)+"-01T12:00:00"); const s = isNaN(d) ? String(m||"") : d.toLocaleDateString("fr-FR",{month:"long",year:"numeric"}); return s.charAt(0).toUpperCase()+s.slice(1); };
  const ficheDateCourte = (v) => { const d = new Date(v); return isNaN(d) ? "" : d.toLocaleDateString("fr-FR"); };
  const ficheEtat = (b) => b?.signe_le ? "signee" : (b?.envoye_le ? "a_signer" : "non_envoyee");
  const ficheMesASigner = () => session ? bulletinsPaie.filter(b=>String(b.employe_id)===String(session.id) && ficheEtat(b)==="a_signer") : [];
  const ficheAReviewer = () => {
    if (!session || isDirecteur() || annonceALire() || ficheBusy) return null;
    if (ficheReview) return bulletinsPaie.find(b=>String(b.id)===String(ficheReview)) || null;
    return ficheMesASigner().find(b=>!ficheReporte[b.id]) || null;
  };
  const ficheEmpDe = (b) => employes.find(e=>String(e.id)===String(b.employe_id)) || (String(session?.id)===String(b.employe_id) ? session : null) || { nom:b.employe_nom };
  const ficheOuvrirPdf = async (b, avecSignature) => {
    try {
      let sigs = {};
      if (avecSignature && b.signe_le) {
        const { data, error } = await supabase.from("bulletins_signatures").select("signature_png").eq("bulletin_id",String(b.id)).maybeSingle();
        if (error) throw error;
        if (data?.signature_png) sigs = { employe:data.signature_png };
      }
      const pdf = await generateBulletinPaiePdfBase64(b, ficheEmpDe(b), entreprise, sigs);
      await deliverPdfOnDevice({ ...pdf, title:"Bulletin de paie — "+b.employe_nom+" — "+ficheMoisLbl(b.mois), onOpenInApp:(url,filename,title)=>setPdfPreview({url,filename,title}) });
    } catch(e) { console.error("Fiche de paie PDF :", e); showToast("Impossible d'ouvrir la fiche : "+(e?.message||"erreur inconnue"),false); }
  };
  const ficheSigner = async (b) => {
    if (ficheBusy || !b) return;
    const lbl = ficheMoisLbl(b.mois);
    setFicheBusy(true);
    try {
      const sigs = await openSignatureFlow("Fiche de paie — "+lbl, [{key:"employe",label:"Ma signature"}]);
      const png = sigs && sigs.employe;
      if (!png) { setFicheBusy(false); return; }
      const pdf = await generateBulletinPaiePdfBase64(b, ficheEmpDe(b), entreprise, { employe:png });
      let path = null;
      try {
        const bytes = Uint8Array.from(atob(pdf.base64), c=>c.charCodeAt(0));
        const p = "bulletins/"+b.id+"_"+Date.now()+".pdf";
        const { error:upErr } = await supabase.storage.from("documents-signes").upload(p, new Blob([bytes],{type:"application/pdf"}), { contentType:"application/pdf", upsert:false });
        if (!upErr) path = p;
      } catch(_) {}
      const { error } = await supabase.rpc("fiche_signer",{ p_id:String(b.id), p_png:png, p_pdf_path:path, p_appareil:String(navigator.userAgent||"").slice(0,180) });
      if (error) throw error;
      try {
        await deposerDocumentSigne({ employeId:b.employe_id, employeNom:b.employe_nom, typeDocument:"Fiche de paie", titre:"Fiche de paie signée — "+lbl, pdf, dateDocument:today() });
      } catch(_) {}
      setFicheReview(null);
      showToast("Fiche signée ✓ — la direction est prévenue");
      await load();
      try { await openPdf(pdf, "Fiche de paie signée — "+lbl); } catch(_) {}
    } catch(e) {
      console.error("Signature fiche de paie :", e);
      const m = String(e?.message||"");
      showToast(m.includes("deja_signee") ? "Cette fiche est déjà signée" : m.includes("pas_votre_fiche") ? "Cette fiche n'est pas la vôtre" : "Signature non enregistrée : "+m, false);
    }
    setFicheBusy(false);
  };
  const ficheEnvoyer = async (b) => {
    const { data, error } = await supabase.rpc("fiche_envoyer",{ p_id:String(b.id), p_mois:null });
    if (error) { showToast("Envoi impossible : "+error.message,false); return; }
    showToast(data>0 ? "Fiche envoyée à "+b.employe_nom+" ✓" : "Aucun envoi (employé sans matricule ?)", data>0);
    await load();
  };
  const ficheEnvoyerMois = async (m) => {
    const { data, error } = await supabase.rpc("fiche_envoyer",{ p_id:null, p_mois:m });
    if (error) { showToast("Envoi impossible : "+error.message,false); return; }
    showToast(data+" fiche(s) envoyée(s) pour signature ✓");
    await load();
  };
  const ficheRelancerMois = async (m) => {
    const { data, error } = await supabase.rpc("fiche_rappeler",{ p_forcer:true, p_mois:m });
    if (error) { showToast("Relance impossible : "+error.message,false); return; }
    showToast(data+" relance(s) envoyée(s) ✓");
    await load();
  };
  const ficheChip = (b) => {
    const e = ficheEtat(b);
    const st = e==="signee" ? {c:"#1E7A2D",bg:"rgba(47,158,63,.12)",t:"Signée le "+ficheDateCourte(b.signe_le)} : e==="a_signer" ? {c:"#8A6A12",bg:"rgba(212,168,75,.2)",t:"À signer"+(b.nb_rappels>0?" · relancé "+b.nb_rappels+"×":"")} : {c:"#5B6B62",bg:"#EDF1EF",t:"Non envoyée"};
    return <span style={{display:"inline-block",padding:"3px 8px",borderRadius:12,fontSize:10,fontWeight:600,color:st.c,background:st.bg,whiteSpace:"nowrap"}}>{st.t}</span>;
  };
  // ── Versements aux employés (avances, primes…) : bon signé + suivi des soldes ──
  const VR_TYPES = [
    { k:"Avance sur salaire", code:"6005", recup:true, aide:"Argent donné d'avance sur le salaire : vous le retenez ensuite sur les fiches de paie, du montant que vous décidez à chaque fois." },
    { k:"Avance sur commissions", code:"6005", recup:true, aide:"Argent donné d'avance sur des commissions à venir : retenu ensuite sur les fiches de paie, du montant que vous décidez à chaque fois." },
    { k:"Prime exceptionnelle", code:"6002", recup:false, aide:"Prime donnée en plus du salaire : rien à récupérer ensuite." },
    { k:"Remboursement de frais", code:"6005", recup:false, aide:"Frais avancés par l'employé et remboursés : rien à récupérer ensuite." },
    { k:"Autre versement", code:"6005", recup:false, aide:"Autre somme versée à l'employé (précisez le motif) : rien à récupérer ensuite." }
  ];
  const vrType = (k) => VR_TYPES.find(t=>t.k===k) || VR_TYPES[0];
  const [vrListe, setVrListe] = useState([]);
  const [vrEtat, setVrEtat] = useState("");
  const [vrModal, setVrModal] = useState(null);
  const [vrBusy, setVrBusy] = useState(false);
  const [vrRet, setVrRet] = useState({});
  const vrEmpPrec = useRef("");
  const vrCharger = async () => {
    if (!session || !isDirecteur()) return;
    setVrEtat("chargement");
    const { data, error } = await supabase.from("versements_employes")
      .select("id,numero,employe_id,employe_nom,type_versement,motif,montant,date_versement,moyen,recuperable,solde_restant,depense_id,statut,signe_le,pdf_path,created_at,envoye_le,nb_rappels")
      .order("created_at",{ascending:false}).limit(300);
    if (error) { setVrEtat("erreur"); return; }
    setVrListe(data||[]); setVrEtat("ok");
  };
  useEffect(()=>{
    if (session && isDirecteur() && (page==="salaires" || page==="documents" || page==="comptabilite")) vrCharger();
    // eslint-disable-next-line
  },[session?.id, page, docOnglet]);
  const vrSomme = (o) => Object.values(o||{}).reduce((s,x)=>s+(Number(x)||0),0);
  const vrOuvertes = (employeId) => vrListe.filter(v=>v.recuperable && v.statut==="signe" && Number(v.solde_restant)>0 && String(v.employe_id)===String(employeId));
  const vrOuvrir = (init) => setVrModal({ employeId:"", type:"Avance sur salaire", montant:"", date:today(), moyen:"Espèces", motif:"", ...(init||{}) });
  const vrEmpDe = (v) => employes.find(e=>String(e.id)===String(v.employe_id)) || { id:v.employe_id, nom:v.employe_nom };
  const vrTitreBon = (v) => v.type_versement+" — "+v.employe_nom+" — "+v.numero;
  const vrSignatures = async (v) => {
    const { data, error } = await supabase.from("versements_employes").select("signature_employe,signature_direction").eq("id",v.id).maybeSingle();
    if (error) throw error;
    return { employe:data?.signature_employe||null, direction:data?.signature_direction||null };
  };
  const vrOuvrirBon = async (v) => {
    try {
      let base64 = null;
      if (v.statut==="signe" && v.pdf_path) {
        const { data, error } = await supabase.storage.from("documents-signes").download(v.pdf_path);
        if (!error && data) base64 = await new Promise((res,rej)=>{ const r=new FileReader(); r.onload=()=>res(String(r.result).split(",").pop()); r.onerror=rej; r.readAsDataURL(data); });
      }
      if (!base64) {
        const sigs = v.statut==="signe" ? await vrSignatures(v) : {};
        base64 = (await generateBonVersementPdfBase64(v, vrEmpDe(v), entreprise, sigs)).base64;
      }
      await openPdf({ base64, filename:"Bon_versement_"+v.numero+".pdf" }, vrTitreBon(v));
    } catch(e) { console.error("Bon de versement :", e); showToast("Impossible d'ouvrir le bon : "+(e?.message||"erreur"),false); }
  };
  const vrSigner = async (v) => {
    if (vrBusy) return;
    setVrBusy(true);
    try {
      const sigs = await openSignatureFlow("Bon de versement "+v.numero, [
        { key:"employe", label:"Signature de "+v.employe_nom+" (bénéficiaire)" },
        { key:"direction", label:"Signature de la direction" }
      ]);
      if (!sigs || !sigs.employe || !sigs.direction) { showToast("Il faut les deux signatures : le bon reste « À signer »",false); setVrBusy(false); return; }
      const emp = vrEmpDe(v);
      const pdf = await generateBonVersementPdfBase64(v, emp, entreprise, sigs);
      let path = null;
      try {
        const bytes = Uint8Array.from(atob(pdf.base64), c=>c.charCodeAt(0));
        const p = "versements/"+v.id+".pdf";
        const { error:upErr } = await supabase.storage.from("documents-signes").upload(p, new Blob([bytes],{type:"application/pdf"}), { contentType:"application/pdf", upsert:false });
        if (!upErr || /exist|duplicate/i.test(String(upErr.message||""))) path = p;
      } catch(_) {}
      const { error } = await supabase.from("versements_employes").update({ statut:"signe", signature_employe:sigs.employe, signature_direction:sigs.direction, signe_le:new Date().toISOString(), pdf_path:path }).eq("id",v.id).eq("statut","a_signer");
      if (error) throw error;
      let rangé = true;
      try {
        await deposerDocumentSigne({ employeId:v.employe_id, employeNom:v.employe_nom, typeDocument:"Bon de versement", titre:"Bon de versement signé — "+v.type_versement+" — "+v.numero, pdf, dateDocument:v.date_versement||today() });
      } catch(e) { rangé = false; console.error("Dépôt du bon dans le dossier de l'employé :", e); }
      showToast(rangé ? "Bon signé ✓ — rangé dans les documents de "+v.employe_nom+" et dans « Documents signés »" : "Bon signé ✓ (mais non déposé dans le dossier de l'employé : "+"réessayez depuis la fiche RH)", rangé);
      await vrCharger();
      try { await openPdf(pdf, vrTitreBon(v)); } catch(_) {}
    } catch(e) {
      console.error("Signature du bon de versement :", e);
      showToast("Signature non enregistrée : "+(e?.message||"erreur"),false);
    }
    setVrBusy(false);
  };
  // ── Versements : envoi du bon pour signature à distance (l'employé signe sur son téléphone) ──
  const [vrMes, setVrMes] = useState([]);
  const [vrMesReview, setVrMesReview] = useState(null);
  const [vrMesReporte, setVrMesReporte] = useState({});
  const [vrMesBusy, setVrMesBusy] = useState(false);
  const vrErrMsg = (e) => {
    const m = String((e && e.message) || e || "");
    return m.includes("employe_sans_matricule") ? "cet employé n'a pas de matricule : impossible de le prévenir, faites-le signer en personne"
      : m.includes("deja_signe") ? "ce bon est déjà signé"
      : m.includes("pas_en_attente") ? "ce bon n'est pas en attente de signature"
      : m.includes("pas_votre_bon") ? "ce bon n'est pas le vôtre"
      : m.includes("pas_encore_envoye") ? "ce bon n'a pas encore été envoyé"
      : m.includes("signature_invalide") ? "signature illisible, recommencez"
      : m || "erreur";
  };
  const vrEnvoyer = async (v) => {
    if (vrBusy || !v) return;
    setVrBusy(true);
    try {
      const sigs = await openSignatureFlow("Bon de versement "+v.numero+" — signature de la direction", [{ key:"direction", label:"Signature de la direction" }]);
      const png = sigs && sigs.direction;
      if (!png) { showToast("Envoi annulé : le bon reste « À signer » (vous pouvez l'envoyer plus tard)",false); setVrBusy(false); return; }
      const nomDir = [session?.prenom, session?.nom].filter(Boolean).join(" ").trim() || session?.nom || "";
      const { error } = await supabase.rpc("versement_envoyer",{ p_id:v.id, p_png_direction:png, p_direction_nom:nomDir });
      if (error) throw error;
      showToast("Bon envoyé à "+v.employe_nom+" ✓ — il est prévenu et signera sur son téléphone");
      await vrCharger();
    } catch(e) {
      console.error("Envoi du bon pour signature :", e);
      showToast("Envoi impossible : "+vrErrMsg(e),false);
    }
    setVrBusy(false);
  };
  const vrRelancer = async (v) => {
    const { error } = await supabase.rpc("versement_relancer",{ p_id:v.id });
    if (error) return showToast("Relance impossible : "+vrErrMsg(error),false);
    showToast("Relance envoyée à "+v.employe_nom+" ✓");
    vrCharger();
  };
  // Côté employé
  const vrMesCharger = async () => {
    if (!session || isDirecteur()) return;
    const { data, error } = await supabase.rpc("versement_mes_a_signer");
    if (!error && Array.isArray(data)) setVrMes(data);
  };
  useEffect(()=>{
    if (!session || isDirecteur()) { setVrMes([]); return; }
    vrMesCharger();
    const t = setInterval(()=>{ if (typeof document==="undefined" || !document.hidden) vrMesCharger(); }, 60000);
    return ()=>clearInterval(t);
    // eslint-disable-next-line
  },[session?.id, page]);
  const vrMesAReviewer = () => {
    if (!session || isDirecteur() || annonceALire() || ficheAReviewer() || vrMesBusy || pdfPreview) return null;
    if (vrMesReview) return vrMes.find(x=>String(x.id)===String(vrMesReview)) || null;
    return vrMes.find(x=>!vrMesReporte[x.id]) || null;
  };
  const vrMesPdf = async (m) => {
    try {
      const { data, error } = await supabase.rpc("versement_lire_pour_signature",{ p_id:m.id });
      if (error) throw error;
      const pdf = await generateBonVersementPdfBase64(data, session, entreprise, { direction:data.signature_direction });
      await openPdf(pdf, "Bon de versement — "+m.numero);
    } catch(e) { console.error("Bon de versement :", e); showToast("Impossible d'ouvrir le bon : "+vrErrMsg(e),false); }
  };
  const vrMesSigner = async (m) => {
    if (vrMesBusy || !m) return;
    setVrMesBusy(true);
    try {
      const { data:full, error:errL } = await supabase.rpc("versement_lire_pour_signature",{ p_id:m.id });
      if (errL) throw errL;
      const sigs = await openSignatureFlow("Bon de versement "+m.numero, [{ key:"employe", label:"Ma signature (reçu la somme indiquée)" }]);
      const png = sigs && sigs.employe;
      if (!png) { setVrMesBusy(false); return; }
      const pdf = await generateBonVersementPdfBase64(full, session, entreprise, { employe:png, direction:full.signature_direction });
      let path = null;
      try {
        const bytes = Uint8Array.from(atob(pdf.base64), c=>c.charCodeAt(0));
        const p = "versements/"+m.id+".pdf";
        const { error:upErr } = await supabase.storage.from("documents-signes").upload(p, new Blob([bytes],{type:"application/pdf"}), { contentType:"application/pdf", upsert:false });
        if (!upErr || /exist|duplicate/i.test(String(upErr.message||""))) path = p;
      } catch(_) {}
      const { error } = await supabase.rpc("versement_signer_employe",{ p_id:m.id, p_png:png, p_pdf_path:path });
      if (error) throw error;
      try {
        await deposerDocumentSigne({ employeId:session.id, employeNom:session.nom, typeDocument:"Bon de versement", titre:"Bon de versement signé — "+m.type_versement+" — "+m.numero, pdf, dateDocument:m.date_versement||today() });
      } catch(_) {}
      setVrMesReview(null);
      setVrMes(l=>l.filter(x=>x.id!==m.id));
      showToast("Bon signé ✓ — la direction est prévenue");
      try { await openPdf(pdf, "Bon de versement signé — "+m.numero); } catch(_) {}
    } catch(e) {
      console.error("Signature du bon de versement :", e);
      showToast("Signature non enregistrée : "+vrErrMsg(e),false);
      vrMesCharger();
    }
    setVrMesBusy(false);
  };
  const vrCreer = async (envoyer) => {
    const f = vrModal; if (!f || vrBusy) return;
    envoyer = envoyer === true;
    const emp = employes.find(e=>String(e.id)===String(f.employeId));
    const montant = Math.round(Number(f.montant)||0);
    if (!emp) return showToast("Choisissez l'employé",false);
    if (!(montant>0)) return showToast("Indiquez un montant supérieur à 0",false);
    if (f.type==="Autre versement" && !String(f.motif||"").trim()) return showToast("Précisez le motif de ce versement",false);
    const t = vrType(f.type);
    setVrBusy(true);
    const info = codeInfo(t.code);
    const { data:dep, error:errDep } = await supabase.from("depenses").insert({
      date:f.date||today(), libelle:t.k+" — "+emp.nom, montant, categorie:info?info.categorie:"Personnel (hors fiche de paie)",
      fournisseur:"", code:info?t.code:null, moyen:f.moyen||"Espèces", ville:sessionVille()||"Dschang", employe:session.nom
    }).select("id").single();
    if (errDep) { showToast("Dépense non enregistrée : "+errDep.message,false); setVrBusy(false); return; }
    const { data:row, error:errV } = await supabase.from("versements_employes").insert({
      employe_id:String(emp.id), employe_nom:emp.nom, type_versement:t.k, motif:String(f.motif||"").trim(), montant,
      date_versement:f.date||today(), moyen:f.moyen||"Espèces", recuperable:t.recup, depense_id:String(dep.id), cree_par:session.nom||""
    }).select().single();
    if (errV) {
      await supabase.from("depenses").delete().eq("id",dep.id);
      showToast("Versement non enregistré : "+errV.message,false); setVrBusy(false); return;
    }
    await load(); await vrCharger();
    setVrModal(null); setVrBusy(false);
    showToast(envoyer ? "Versement enregistré — signez maintenant pour l'envoyer à l'employé" : "Versement enregistré — faites maintenant signer le bon");
    if (envoyer) vrEnvoyer(row); else vrSigner(row);
  };
  const vrAnnuler = async (v) => {
    if (v.statut!=="a_signer") return;
    if (!window.confirm("Annuler le versement "+v.numero+" de "+FCFA(v.montant)+" à "+v.employe_nom+" ?\nLa dépense correspondante sera supprimée.")) return;
    const { error } = await supabase.from("versements_employes").delete().eq("id",v.id).eq("statut","a_signer");
    if (error) return showToast("Annulation impossible : "+error.message,false);
    if (v.depense_id) await supabase.from("depenses").delete().eq("id",v.depense_id);
    await load(); await vrCharger(); showToast("Versement annulé");
  };
  // Retenue sur la fiche en cours de saisie : le montant retenu s'ajoute aux « autres retenues »
  const vrLibelleRet = (o) => {
    const types = Array.from(new Set(Object.keys(o).filter(id=>o[id]>0).map(id=>(vrListe.find(v=>v.id===id)||{}).type_versement).filter(Boolean)));
    return types.length===1 ? "Retenue "+types[0].toLowerCase() : types.length>1 ? "Retenue sur avances" : "";
  };
  const vrSetRet = (v, val) => {
    const cap = Math.max(0, Math.round(Number(v.solde_restant)||0));
    let n = Math.max(0, Math.round(Number(val)||0)); if (n>cap) n = cap;
    const next = { ...vrRet }; if (n>0) next[v.id] = n; else delete next[v.id];
    const delta = vrSomme(next) - vrSomme(vrRet);
    const lbl = vrLibelleRet(next);
    setVrRet(next);
    setBulletinForm(f=>{
      const total = Math.max(0, (parseInt(f.autresRetenues||0)||0) + delta);
      const auto = !f.autresRetenuesLibelle || /^Retenue /.test(f.autresRetenuesLibelle);
      return { ...f, autresRetenues: total?String(total):"", autresRetenuesLibelle: auto?lbl:f.autresRetenuesLibelle };
    });
  };
  const vrItemsRet = () => Object.keys(vrRet).map(id=>({ id, montant:vrRet[id] })).filter(x=>x.montant>0);
  useEffect(()=>{
    if (vrEmpPrec.current === bulletinForm.employeId) return;
    vrEmpPrec.current = bulletinForm.employeId;
    const s = vrSomme(vrRet);
    if (s>0) {
      setBulletinForm(f=>{
        const t = Math.max(0, (parseInt(f.autresRetenues||0)||0) - s);
        return { ...f, autresRetenues: t?String(t):"", autresRetenuesLibelle: /^Retenue /.test(f.autresRetenuesLibelle||"") ? "" : f.autresRetenuesLibelle };
      });
    }
    setVrRet({});
    // eslint-disable-next-line
  },[bulletinForm.employeId]);
  const vrDateCourte = (v) => { const d = new Date(v); return isNaN(d) ? "" : d.toLocaleDateString("fr-FR"); };
  const vrChip = (v) => v.statut==="signe"
    ? <span style={{display:"inline-block",padding:"3px 8px",borderRadius:12,fontSize:10,fontWeight:600,color:"#1E7A2D",background:"rgba(47,158,63,.12)",whiteSpace:"nowrap"}}>Signé le {vrDateCourte(v.signe_le)}</span>
    : v.envoye_le
      ? <span style={{display:"inline-block",padding:"3px 8px",borderRadius:12,fontSize:10,fontWeight:600,color:"#1F5E8F",background:"rgba(31,94,143,.12)"}}>Envoyé à l'employé le {vrDateCourte(v.envoye_le)} — en attente de sa signature{Number(v.nb_rappels)>0?" · relancé "+v.nb_rappels+" fois":""}</span>
      : <span style={{display:"inline-block",padding:"3px 8px",borderRadius:12,fontSize:10,fontWeight:600,color:"#8A6A12",background:"rgba(212,168,75,.2)",whiteSpace:"nowrap"}}>À signer</span>;
  // ── Bons de commande fournisseurs : validation + signature de la direction, PDF, envoi ──
  const [bcBusy, setBcBusy] = useState(false);
  const bcEtat = (c) => c.validation==="a_valider" ? "a_valider" : c.validation==="refusee" ? "refusee" : "validee";
  const bcFournisseur = (c) => fournisseurs.find(f=>String(f.id)===String(c.fournisseur_id)) || { nom:c.fournisseur_nom };
  const bcCoordonnees = (f) => {
    const t = String((f&&f.contact)||"");
    const email = (t.match(/[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+/)||[""])[0];
    return { email };
  };
  const bcDate = (v) => { const d = new Date(v); return isNaN(d) ? "" : d.toLocaleDateString("fr-FR"); };
  const bcChip = (c) => {
    const e = bcEtat(c);
    const base = {display:"inline-block",padding:"3px 8px",borderRadius:12,fontSize:10,fontWeight:600,marginLeft:6};
    return <>
      {e==="a_valider"&&<span style={{...base,color:"#8A6A12",background:"rgba(212,168,75,.2)"}}>À valider par la direction</span>}
      {e==="refusee"&&<span style={{...base,color:"#A3231C",background:"rgba(163,35,28,.1)"}} title={c.refus_motif||""}>Refusée{c.refus_motif?" : "+c.refus_motif:""}</span>}
      {e==="validee"&&c.signe_le&&<span style={{...base,color:"#1E7A2D",background:"rgba(47,158,63,.12)"}}>Signé le {bcDate(c.signe_le)}</span>}
      {e==="validee"&&c.envoye_le&&<span style={{...base,color:"#1F5E8F",background:"rgba(31,94,143,.12)"}}>Envoyé au fournisseur le {bcDate(c.envoye_le)}</span>}
    </>;
  };
  const bcErrMsg = (e) => {
    const m = String((e && e.message) || e || "");
    return m.includes("reserve_directeur") ? "réservé à la direction"
      : m.includes("deja_signe") ? "ce bon est déjà signé"
      : m.includes("bon_refuse") ? "ce bon a été refusé"
      : m.includes("pas_a_valider") ? "ce bon n'est plus à valider"
      : m.includes("bon_non_valide") ? "ce bon n'est pas validé"
      : m.includes("signature_invalide") ? "signature illisible, recommencez"
      : m || "erreur";
  };
  // PDF « tel qu'il part chez le fournisseur » : celui rangé à la signature, sinon refabriqué avec la signature enregistrée
  const bcPdfSigne = async (c) => {
    if (c.pdf_path) {
      try {
        const { data, error } = await supabase.storage.from("documents-signes").download(c.pdf_path);
        if (!error && data) {
          const base64 = await new Promise((res,rej)=>{ const r=new FileReader(); r.onload=()=>res(String(r.result).split(",").pop()); r.onerror=rej; r.readAsDataURL(data); });
          return { base64, filename:"Bon_commande_"+String(c.numero||"")+".pdf" };
        }
      } catch(_) {}
    }
    let direction = null;
    if (isDirecteur() && c.signe_le) {
      const { data } = await supabase.from("achats_signatures").select("signature_png").eq("commande_id",String(c.id)).maybeSingle();
      direction = data?.signature_png || null;
    }
    return generateBonCommandePdfBase64(c, bcFournisseur(c), entreprise, { direction });
  };
  const bcVoir = async (c) => {
    try {
      const pdf = c.signe_le ? await bcPdfSigne(c) : await generateBonCommandePdfBase64(c, bcFournisseur(c), entreprise, {});
      await openPdf(pdf, "Bon de commande — "+c.numero);
    } catch(e) { console.error("Bon de commande :", e); showToast("Impossible d'ouvrir le bon : "+(e?.message||"erreur"),false); }
  };
  const bcSigner = async (c) => {
    if (bcBusy || !c || !isDirecteur()) return;
    setBcBusy(true);
    try {
      const sigs = await openSignatureFlow("Bon de commande "+c.numero, [{ key:"direction", label:"Signature de la direction" }]);
      const png = sigs && sigs.direction;
      if (!png) { showToast("Bon non signé : il reste « À valider »",false); setBcBusy(false); return; }
      const nomDir = [session?.prenom, session?.nom].filter(Boolean).join(" ").trim() || session?.nom || "";
      const signeLe = new Date().toISOString();
      const pdf = await generateBonCommandePdfBase64({ ...c, validation:"validee", signe_le:signeLe, signe_par:nomDir }, bcFournisseur(c), entreprise, { direction:png });
      let path = null;
      try {
        const bytes = Uint8Array.from(atob(pdf.base64), ch=>ch.charCodeAt(0));
        const p = "bons-commande/"+c.id+".pdf";
        const { error:upErr } = await supabase.storage.from("documents-signes").upload(p, new Blob([bytes],{type:"application/pdf"}), { contentType:"application/pdf", upsert:false });
        if (!upErr || /exist|duplicate/i.test(String(upErr.message||""))) path = p;
      } catch(_) {}
      const { error } = await supabase.rpc("achat_signer",{ p_id:String(c.id), p_png:png, p_pdf_path:path, p_signe_par:nomDir });
      if (error) throw error;
      await load();
      showToast("Bon "+c.numero+" signé et validé ✓ — vous pouvez l'envoyer au fournisseur");
      try { await openPdf(pdf, "Bon de commande — "+c.numero); } catch(_) {}
    } catch(e) {
      console.error("Signature du bon de commande :", e);
      showToast("Signature non enregistrée : "+bcErrMsg(e),false);
    }
    setBcBusy(false);
  };
  const bcRefuser = async (c) => {
    if (!isDirecteur()) return;
    const motif = window.prompt("Pourquoi refusez-vous le bon "+c.numero+" ? (le motif sera communiqué à son auteur)","");
    if (motif===null) return;
    const { error } = await supabase.rpc("achat_refuser",{ p_id:String(c.id), p_motif:motif });
    if (error) return showToast("Refus impossible : "+bcErrMsg(error),false);
    await load(); showToast("Bon "+c.numero+" refusé — son auteur est prévenu");
  };
  const bcMarquerEnvoye = async (c, canal) => {
    const { error } = await supabase.rpc("achat_marquer_envoye",{ p_id:String(c.id), p_canal:canal });
    if (error) { console.error("Suivi d'envoi :", error); return; }
    await load();
  };
  const bcEnvoyerMail = (c) => {
    const f = bcFournisseur(c);
    setDocSendModal({ kind:"bon_commande", data:c, client:f });
    setDocSendForm({ to:bcCoordonnees(f).email, toName:f.nom||"", subject:"Bon de commande "+c.numero+" — "+(entreprise.entreprise_nom||""),
      message:"Veuillez trouver ci-joint notre bon de commande n° "+c.numero+".\nMerci de nous confirmer sa prise en compte et la date de livraison." });
  };
  const bcPartager = async (c) => {
    try {
      const pdf = await bcPdfSigne(c);
      const r = await openPdf(pdf, "Bon de commande — "+c.numero);
      if (!r || r.mode!=="cancelled") bcMarquerEnvoye(c, "partage");
    } catch(e) { console.error("Partage du bon :", e); showToast("Impossible de préparer le bon : "+(e?.message||"erreur"),false); }
  };
  const bcPeutReceptionner = (c) => bcEtat(c)==="validee";
  const bcActions = (c) => {
    const e = bcEtat(c), dir = isDirecteur();
    return <>
      {dir&&e==="a_valider"&&<button type="button" className="btn btn-gold btn-sm" disabled={bcBusy} onClick={()=>bcSigner(c)}>Signer et valider</button>}
      {dir&&e==="a_valider"&&<button type="button" className="btn btn-ghost btn-sm" disabled={bcBusy} onClick={()=>bcRefuser(c)}>Refuser</button>}
      {dir&&e==="validee"&&!c.signe_le&&<button type="button" className="btn btn-ghost btn-sm" disabled={bcBusy} onClick={()=>bcSigner(c)}>Signer</button>}
      {e==="validee"&&c.statut!=="Reçue"&&<button type="button" className="btn btn-gold btn-sm" disabled={achatSaving} onClick={()=>receiveAchatCommande(c)}>Réceptionner</button>}
      {e==="validee"&&<button type="button" className="btn btn-ghost btn-sm" onClick={()=>createFactureAchat(c)}>Facture</button>}
      <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={()=>bcVoir(c)}><Eye size={14}/> Bon</button>
      {dir&&e==="validee"&&c.signe_le&&<button type="button" className="btn btn-ghost btn-sm btn-icon" title="Envoyer par e-mail au fournisseur" onClick={()=>bcEnvoyerMail(c)}><Mail size={13}/> E-mail</button>}
      {dir&&e==="validee"&&c.signe_le&&<button type="button" className="btn btn-ghost btn-sm btn-icon" title="Envoyer par WhatsApp ou autre application" onClick={()=>bcPartager(c)}><Send size={13}/> WhatsApp</button>}
    </>;
  };
  const demandeAlerte = () => isDirecteur() ? demandes.some(d=>d.statut==="en_attente") : demandes.some(d=>d.statut==="a_clarifier"||d.statut==="acceptee");
  const DEM_STATUT = {
    en_attente:["À décider","En attente du directeur","#A8680A","#FFF4DD"],
    a_clarifier:["Réponse du demandeur attendue","Précisions demandées","#2F6FDE","#E9F0FD"],
    acceptee:["Acceptée","Acceptée — à enregistrer","#1E7A2D","#E8F5EA"],
    refusee:["Refusée","Refusée","#B3372F","#FDECEA"],
    utilisee:["Dépense enregistrée","Dépense enregistrée","#2B3A32","#EEF3F0"],
    annulee:["Annulée","Annulée","#5B6B62","#EEF3F0"]
  };
  const demErreur = (e) => {
    const m = String((e&&e.message)||"");
    if (m.includes("deja_traitee")) return "Cette demande a déjà été traitée.";
    if (m.includes("message_requis")) return "Le message est obligatoire.";
    if (m.includes("montant_invalide")) return "Montant invalide.";
    if (m.includes("reserve_directeur")) return "Réservé au directeur.";
    if (m.includes("accord_requis")) return "Cette dépense demande l'accord du directeur : envoyez d'abord une demande.";
    return m||"erreur inconnue";
  };
  const envoyerDemandeDepense = async () => {
    setLoading(true);
    const info = codeInfo(form.code);
    const { error } = await supabase.from("demandes_depense").insert({
      date_depense:form.date||today(), libelle:String(form.libelle||"").trim(), montant:parseInt(form.montant), code:form.code,
      categorie:info?info.categorie:(form.categorie||"Autre"), fournisseur:form.fournisseur||"", moyen:form.moyen||"Espèces",
      explication:String(form.explication||"").trim(), ville:sessionVille()||""
    });
    setLoading(false);
    if (error) return showToast("Erreur : "+demErreur(error), false);
    closeModal(); await chargerDemandes(); setDemFiltre("encours"); setPage("demandes_depense");
    showToast("Demande envoyée au directeur ✓ — vous serez prévenu de sa réponse");
  };
  const demEnvoyerAction = async () => {
    const a = demAction; if (!a || demEnvoi) return;
    const texte = String(a.texte||"").trim();
    setDemEnvoi(true);
    try {
      if (a.type==="repondre") {
        const { error } = await supabase.rpc("demande_repondre",{ p_id:a.d.id, p_message:texte });
        if (error) throw error;
        showToast("Message envoyé au directeur ✓");
      } else {
        if ((a.type==="refusee"||a.type==="a_clarifier") && texte.length<2) { setDemEnvoi(false); return showToast(a.type==="refusee"?"Indiquez le motif du refus":"Écrivez votre question",false); }
        const montant = a.type==="acceptee" ? Math.round(toFiniteNumber(a.montant,a.d.montant)) : null;
        if (a.type==="acceptee" && !(montant>=1 && montant<=toFiniteNumber(a.d.montant))) { setDemEnvoi(false); return showToast("Le montant accordé doit être entre 1 et "+FCFA(a.d.montant),false); }
        const { error } = await supabase.rpc("demande_decider",{ p_id:a.d.id, p_decision:a.type, p_message:texte, p_montant:montant });
        if (error) throw error;
        showToast(a.type==="acceptee"?"Dépense acceptée ✓":a.type==="refusee"?"Demande refusée":"Question envoyée ✓");
      }
      setDemAction(null); await chargerDemandes();
    } catch(e) { showToast("Erreur : "+demErreur(e), false); }
    setDemEnvoi(false);
  };
  const demAnnuler = async (d) => {
    if (!window.confirm("Annuler cette demande de dépense ?")) return;
    const { error } = await supabase.rpc("demande_annuler",{ p_id:d.id });
    if (error) return showToast("Erreur : "+demErreur(error),false);
    await chargerDemandes(); showToast("Demande annulée");
  };
  const demEnregistrerDepense = (d) => {
    openModal("depense",{ demande_id:d.id, date:today(), libelle:d.libelle, montant:String(Math.round(toFiniteNumber(d.montant_accorde||d.montant))), code:d.code, categorie:d.categorie, fournisseur:d.fournisseur||"", moyen:d.moyen||"Espèces" });
  };
  const demBasculerCode = async (c) => {
    const { error } = await supabase.rpc("code_sans_demande",{ p_code:c.code, p_valeur:!c.sans_demande });
    if (error) return showToast("Erreur : "+demErreur(error),false);
    await loadCodes(); showToast(c.sans_demande?"Ce code demande maintenant l'accord":"Ce code ne demande plus d'accord");
  };

  const saveDepense = async () => {
    if (!form.libelle||!form.montant) return showToast("Champs requis",false);
    if (!form.code) return showToast("Choisissez le code de la dépense : toute dépense se saisit par code",false);
    if (!(parseInt(form.montant)>0)) return showToast("Indiquez un montant supérieur à 0",false);
    if (!isDirecteur()) {
      if (form.demande_id) {
        const dem = demandes.find(x=>String(x.id)===String(form.demande_id));
        if (!dem || dem.statut!=="acceptee") return showToast("Cette demande n'est plus utilisable : refaites une demande",false);
        if (parseInt(form.montant) > toFiniteNumber(dem.montant_accorde||dem.montant)) return showToast("Le montant dépasse l'accord ("+FCFA(dem.montant_accorde||dem.montant)+") : refaites une demande",false);
      } else if (!form.id && accordRequis(form.code)) {
        return envoyerDemandeDepense();
      } else if (form.id && accordRequis(form.code)) {
        const avant = depenses.find(x=>String(x.id)===String(form.id));
        if (avant && (form.code!==avant.code || parseInt(form.montant) > toFiniteNumber(avant.montant))) return showToast("Pour une dépense plus élevée ou d'un autre code, faites une nouvelle demande au directeur",false);
      }
    }
    setLoading(true);
    const infoCode = codeInfo(form.code);
    const payload = { date:form.date||today(), libelle:form.libelle, montant:parseInt(form.montant), categorie:infoCode?infoCode.categorie:(form.categorie||"Autre"), fournisseur:form.fournisseur||"", code:form.code||null, moyen:form.moyen||"Espèces" };
    const { error } = form.id
      ? await supabase.from("depenses").update(payload).eq("id",form.id)
      : await supabase.from("depenses").insert({...payload, ville:sessionVille()||"Dschang", employe:session.nom, ...(form.demande_id?{demande_id:form.demande_id}:{})});
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast(form.id?"Dépense mise à jour ✓":"Dépense enregistrée ✓"); closeModal();
  };

  const deleteDepense = async (d) => {
    if (!window.confirm(`Supprimer la dépense "${d.libelle}" (${FCFA(d.montant)}) ?`)) return;
    const { error } = await supabase.from("depenses").delete().eq("id",d.id);
    if (error) return showToast("Erreur : "+error.message, false);
    await load(); showToast("Dépense supprimée ✓");
  };

  // ── Saisie rapide des achats de matières premières (Comptabilité) ──
  const saveIngredientRows = async () => {
    const lignesRemplies = ingredientRows.filter(r=>r.nom.trim() && r.montant && parseInt(r.montant)>0);
    if (lignesRemplies.length===0) return showToast("Renseigne au moins un montant",false);
    { const sans = lignesRemplies.find(r=>!r.code); if (sans) return showToast("Choisissez le code de « "+sans.nom+" »",false); }
    { const aDemander = lignesRemplies.find(r=>accordRequis(r.code)); if (aDemander) return showToast("« "+aDemander.nom+" » demande l'accord du directeur : utilisez « Demandes de dépense » (menu Finance) pour l'envoyer",false); }
    setIngredientSaving(true);
    for (const r of lignesRemplies) {
      const { error } = await supabase.from("depenses").insert({
        date: ingredientDate, libelle: r.nom.trim(), montant: parseInt(r.montant),
        categorie: (codeInfo(r.code)||{}).categorie||"Production", fournisseur: "", code: r.code||null
      });
      if (error) { showToast("Erreur sur "+r.nom+" : "+error.message, false); setIngredientSaving(false); return; }
    }
    await load(); setIngredientSaving(false);
    setIngredientRows(rows=>rows.map(r=>({...r, montant:""})));
    showToast(lignesRemplies.length+" achat(s) enregistré(s) ✓");
  };

  const saveClient = async () => {
    if (!form.nom||!form.nom.trim()) return showToast("Nom requis",false);
    const nomMaj = form.nom.trim().toUpperCase().replace(/\s+/g," ");
    const adr = (form.adresse||"").trim();
    // Le code client est la référence. Deux fiches peuvent avoir le même nom seulement si leur
    // adresse est différente : l'adresse devient donc obligatoire.
    const autresMemeNom = clients.filter(c=>String(c.id)!==String(form.id||"") && normNomClient(c.nom)===normNomClient(nomMaj));
    if (!adr) return showToast("L'adresse est obligatoire (elle distingue deux clients de même nom)",false);
    if (!form.ville) return showToast("La ville est obligatoire",false);
    const memeAdresse = autresMemeNom.find(c=>normNomClient(c.adresse)===normNomClient(adr));
    if (memeAdresse) return showToast("Cette fiche existe déjà : "+memeAdresse.nom+" ("+memeAdresse.code+") à la même adresse. Ouvrez-la au lieu d'en créer une autre.",false);
    const ancienNom = form.id ? (clients.find(c=>String(c.id)===String(form.id))||{}).nom : null;
    const nomInchange = !!form.id && normNomClient(ancienNom)===normNomClient(nomMaj);
    if (autresMemeNom.length>0 && !nomInchange) {
      const liste = autresMemeNom.map(c=>"• "+c.code+" — "+(c.adresse||"adresse manquante")+(c.ville?", "+c.ville:"")).join("\n");
      if (!window.confirm("Attention : "+autresMemeNom.length+" autre(s) client(s) portent déjà le nom « "+nomMaj+" » :\n\n"+liste+"\n\nCe client-ci est à une adresse différente ("+adr+(form.ville?", "+form.ville:"")+").\nEst-ce bien un AUTRE client ?")) return;
    }
    setLoading(true);
    if (form.id) {
      // Si le nom change, les factures, proformas, ventes, paiements et avaries de ce client suivent.
      const ancienClient = clients.find(c=>String(c.id)===String(form.id));
      if (ancienClient && ancienClient.nom!==nomMaj) {
        const { error:errRen } = await supabase.rpc("renommer_client_transaction",{ p_client_id:form.id, p_nouveau:nomMaj });
        if (errRen) { showToast("Renommage impossible : "+errRen.message, false); setLoading(false); return; }
      }
      const { error } = await supabase.from("clients").update({ nom:nomMaj, contact:form.contact, email:form.email, adresse:form.adresse, ville:form.ville, pays:form.pays||"Cameroun", type:form.type, niu:form.niu||"", rc:form.rc||"", rccm:form.rccm||"", contact_nom:form.contact_nom||"", contact_tel:form.contact_tel||"", contact_email:form.contact_email||"" }).eq("id",form.id);
      if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
      showToast("Client mis à jour ✓");
    } else {
      let code;
      try { code=await genCliCode(nomMaj); }
      catch(e) { showToast("Client non créé : "+e.message,false); setLoading(false); return; }
      const { error } = await supabase.from("clients").insert({ code, nom:nomMaj, contact:form.contact||"", email:form.email||"", adresse:form.adresse||"", ville:form.ville||"", pays:form.pays||"Cameroun", type:form.type||"Détaillant", niu:form.niu||"" });
      if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
      showToast("Client ajouté ✓");
    }
    await load(); setLoading(false); closeModal();
  };

  const deleteClient = async (id) => {
    if (!window.confirm("Supprimer définitivement ce client ? Cette action est irréversible.")) return;
    setLoading(true);
    const { error } = await supabase.from("clients").delete().eq("id",id);
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast("Client supprimé");
  };

  const saveFournisseur = async () => {
    if (!form.nom) return showToast("Nom requis",false);
    setLoading(true);
    const { error } = form.id
      ? await supabase.from("fournisseurs").update({ nom:form.nom, contact:form.contact, ville:form.ville, pays:form.pays||"Cameroun", categorie:form.categorie }).eq("id",form.id)
      : await supabase.from("fournisseurs").insert({ code:"FRN-"+String(fournisseurs.length+1).padStart(3,"0"), nom:form.nom, contact:form.contact||"", ville:form.ville||"", pays:form.pays||"Cameroun", categorie:form.categorie||"Autre" });
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast("Fournisseur sauvegardé ✓"); closeModal();
  };

  const deleteFournisseur = async (id) => {
    if (!window.confirm("Supprimer définitivement ce fournisseur ?")) return;
    setLoading(true);
    const { error } = await supabase.from("fournisseurs").delete().eq("id",id);
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast("Fournisseur supprimé");
  };

  const prochainCodeProduit = () => { const n = produits.map(p=>parseInt(p.code)).filter(x=>x>=9000 && x<10000); return String(n.length?Math.max(...n)+1:9001); };
  const codeProduitSaisi = () => String(form.code!==undefined && form.code!==null ? form.code : (form.id ? "" : prochainCodeProduit())).trim();
  const saveProduit = async () => {
    if (!form.nom||!form.prix) return showToast("Nom et prix requis",false);
    const codeP = codeProduitSaisi();
    if (!/^[0-9]{4}$/.test(codeP)) return showToast("Le code produit (4 chiffres) est obligatoire",false);
    const memeCode = produits.find(x=>String(x.id)!==String(form.id||"") && String(x.code)===codeP);
    if (memeCode) return showToast("Le code "+codeP+" est déjà utilisé par « "+memeCode.nom+" »",false);
    setLoading(true);
    // Correctif du 02/10/2026 : la mise à jour d'un produit existant écrasait jusqu'ici
    // directement produits.stock avec le contenu du champ "Stock initial" (pré-rempli avec la
    // valeur en cours), sans jamais synchroniser le stock par dépôt ni laisser de trace dans le
    // journal des mouvements. Un simple clic sur "Mettre à jour" avec ce champ vidé ou modifié
    // par erreur suffisait à désynchroniser silencieusement le stock central réel de ce que
    // montrait "Parcours du stock" — exactement l'incident qui vient d'être corrigé en base.
    // Le champ ne servait de toute façon à rien à la création (la ligne juste en dessous
    // forçait déjà stock:0 pour un nouveau produit) : il est retiré, le stock se gère
    // uniquement via les chemins sûrs ("+ Stock", réceptions, livraisons, contrôle qualité).
    const { error } = form.id
      ? await supabase.from("produits").update({ code:codeP, nom:form.nom, prix:parseInt(form.prix), cout_revient:parseInt(form.cout_revient||0), seuil_alerte:parseInt(form.seuil_alerte||60), couleur:form.couleur||"#D4A84B" }).eq("id",form.id)
      : await supabase.from("produits").insert({ code:codeP, nom:form.nom, stock:0, prix:parseInt(form.prix), cout_revient:parseInt(form.cout_revient||0), seuil_alerte:parseInt(form.seuil_alerte||60), couleur:form.couleur||"#D4A84B" });
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    showToast(form.id ? "Produit mis à jour ✓" : "Produit ajouté ✓");
    await load(); setLoading(false); closeModal();
  };

  const deleteProduit = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    setLoading(true);
    const { error } = await supabase.from("produits").delete().eq("id",id);
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast("Produit supprimé");
  };

  // ── Ingrédients (matières premières) : prix d'achat de référence, utilisé pour calculer
  // automatiquement le coût de revient des produits via leur recette (voir plus bas). ──
  const saveIngredient = async () => {
    if (!form.nom) return showToast("Nom requis",false);
    setLoading(true);
    const payload = { nom:form.nom, unite:form.unite||"kg", prix_unitaire:toFiniteNumber(form.prix_unitaire) };
    const { error } = form.id
      ? await supabase.from("ingredients").update(payload).eq("id",form.id)
      : await supabase.from("ingredients").insert(payload);
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast(form.id?"Ingrédient mis à jour ✓":"Ingrédient ajouté ✓"); closeModal();
  };

  const deleteIngredient = async (id) => {
    if (!window.confirm("Supprimer cet ingrédient ? Il sera retiré des recettes qui l'utilisent.")) return;
    setLoading(true);
    const { error } = await supabase.from("ingredients").delete().eq("id",id);
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast("Ingrédient supprimé");
  };

  const saveRecetteLigne = async (produitId, ingredientId, quantite) => {
    const q = toFiniteNumber(quantite);
    const existante = recettes.find(r=>String(r.produit_id)===String(produitId)&&String(r.ingredient_id)===String(ingredientId));
    if (q<=0) {
      if (existante) await supabase.from("recettes").delete().eq("id",existante.id);
    } else if (existante) {
      await supabase.from("recettes").update({ quantite_par_bouteille:q }).eq("id",existante.id);
    } else {
      await supabase.from("recettes").insert({ produit_id:produitId, ingredient_id:ingredientId, quantite_par_bouteille:q });
    }
  };

  // ── Ajout rapide de stock (sans passer par un lot de production formel) ──
  const saveAjoutStock = async () => {
    if (!form.qte || parseInt(form.qte)<=0) return showToast("Quantité requise",false);
    setLoading(true);
    const res = await adjustStock(form.produitNom, parseInt(form.qte));
    if (!res.ok) { showToast("Erreur : "+res.error, false); setLoading(false); return; }
    await load(); setLoading(false); showToast(NUM(form.qte)+" btl ajoutée(s) au stock de "+form.produitNom+" ✓"); closeModal();
  };

  // ── Avaries saisies depuis la page Avaries : retire du stock de la ville choisie, garde lieu / adresse / ville ──
  const ouvrirAvarieVille = () => {
    setForm({ ville:(!canSeeAllCities()&&sessionVille())?sessionVille():"", produitNom:"", qte:"", raison:"", clientNom:"", clientCode:"", factureId:"", lieu:"Chez le client", adresse:"", lieuVille:"", remplacement:"À remplacer" });
    setModal("avarie_ville");
  };
  const saveAvarieVille = async () => {
    const qte=parseInt(form.qte,10);
    if (!form.ville) return showToast("Choisis la ville du stock concerné",false);
    if (!form.produitNom) return showToast("Choisis le produit",false);
    if (!qte||qte<=0) return showToast("Quantité invalide",false);
    setLoading(true);
    const { error } = await supabase.rpc("enregistrer_avarie_ville_transaction",{
      p_ville:form.ville, p_produit_nom:form.produitNom, p_quantite:qte, p_raison:form.raison||"", p_acteur:session.nom,
      p_lieu:form.lieu||"", p_adresse:form.adresse||"", p_lieu_ville:form.lieuVille||form.ville,
      p_client_nom:form.clientNom||"", p_facture_id:form.factureId?String(form.factureId):null, p_remplacement:form.remplacement||"À remplacer"
    });
    if (error) { showToast("Avarie non enregistrée : "+error.message,false); setLoading(false); return; }
    await load(); setLoading(false);
    showToast("Avarie enregistrée : le stock de "+form.ville+" a baissé de "+qte+" btl ✓");
    closeModal();
  };
  const marquerAvarieRemplacee = async (id, statut) => {
    const { error } = await supabase.rpc("marquer_avarie_remplacee",{ p_id:id, p_statut:statut });
    if (error) return showToast("Impossible : "+error.message,false);
    await load();
    showToast(statut==="Remplacée"?"Marquée comme remplacée ✓":"Statut mis à jour ✓");
  };

  // ── Déclaration d'avarie : retire du lot concerné, du stock, et ajuste la facture du client si fourni ──
  const saveAvarie = async () => {
    if (!form.lotId||!form.qte||parseInt(form.qte)<=0) return showToast("Lot et quantité requis",false);
    const lot=lots.find(l=>String(l.id)===String(form.lotId));
    if(!lot) return showToast("Lot introuvable",false);
    const qteAvarie=parseInt(form.qte,10);
    if(qteAvarie>toFiniteNumber(lot.quantite)) return showToast("Quantité supérieure à ce qui reste dans le lot ("+lot.quantite+" btl)",false);

    let nouvellesLignes=null;
    if(form.factureId){
      const fac=factures.find(f=>String(f.id)===String(form.factureId));
      if(!fac) return showToast("Facture introuvable",false);
      let restantARetirer=qteAvarie;
      nouvellesLignes=(fac.lignes||[]).map(l=>{
        if(l.designation!==lot.produit||restantARetirer<=0) return l;
        const retire=Math.min(toFiniteNumber(l.qte),restantARetirer);
        restantARetirer-=retire;
        const nouvelleQte=toFiniteNumber(l.qte)-retire;
        return {...l,qte:nouvelleQte,montant:nouvelleQte*toFiniteNumber(l.prixUnit)};
      }).filter(l=>toFiniteNumber(l.qte)>0);
      if(restantARetirer>0) return showToast("La facture ne contient pas assez d'unités de "+lot.produit,false);
    }

    setLoading(true);
    const {error}=await supabase.rpc("enregistrer_avarie_transaction",{
      p_lot_id:String(lot.id),p_quantite:qteAvarie,p_raison:form.raison||"",p_acteur:session.nom,
      p_facture_id:form.factureId?String(form.factureId):null,p_nouvelles_lignes:nouvellesLignes
    });
    if(error){showToast("Avarie non enregistrée : "+error.message,false);setLoading(false);return;}
    const factureAvarie=form.factureId?factures.find(f=>String(f.id)===String(form.factureId)):null;
    const clientAvarie=form.clientNom||factureAvarie?.client_nom||"";
    const clientInfo=clients.find(c=>c.nom===clientAvarie);
    // Correctif du 30/09/2026 : sans facture liée ni fiche client renseignant une ville, ce
    // champ arrivait vide — la sécurité en base (qui compare à la ville de la personne
    // connectée) rejetait alors systématiquement cet enregistrement pour tout non-directeur,
    // avec pour seul symptôme visible un message d'erreur secondaire peu remarqué en bas de
    // l'écran. Complété par la ville de la personne qui déclare l'avarie, en dernier recours.
    const {error:auditError}=await supabase.from("avaries").insert({
      date:today(),
      lot_id:String(lot.id),
      lot_numero:lot.numero||"",
      produit:lot.produit||"",
      quantite:qteAvarie,
      raison:form.raison||"",
      acteur:session.nom,
      client_nom:clientAvarie,
      facture_id:form.factureId?String(form.factureId):null,
      ville:clientInfo?.ville||factureAvarie?.ville||sessionVille()||"",
      adresse:clientInfo?.adresse||"",
      lieu_ville:clientInfo?.ville||"",
      remplacement:"Sans remplacement"
    });
    await load();setLoading(false);
    showToast(auditError
      ?"Avarie enregistrée, mais son journal de rapport n'a pas été créé : "+auditError.message
      :"Avarie enregistrée avec stock, facture et journal synchronisés ✓",
      !auditError
    );
    closeModal();
  };


  const employeeById = id => employes.find(e=>String(e.id)===String(id));
  const rhEmployeeLabel = id => {
    const e=employeeById(id); return e ? [e.prenom,e.nom].filter(Boolean).join(" ") : "Employé";
  };
  // Nombre de jours OUVRÉS (lun.-ven., hors jours fériés déclarés) entre deux dates
  // incluses. Aligné avec le décompte automatique fait côté base de données
  // (fonction jours_ouvres_periode, qui exclut aussi la table jours_feries).
  const rhDaysInclusive = (debut,fin) => {
    const d1=parseDateSafe(debut), d2=parseDateSafe(fin);
    if(!d1||!d2||d2<d1) return 0;
    const feries=new Set(joursFeries.map(j=>j.date));
    let count=0, cur=new Date(d1);
    while(cur<=d2){
      const jour=cur.getDay(); // 0=dimanche, 6=samedi
      if(jour!==0 && jour!==6 && !feries.has(localISODate(cur))) count++;
      cur.setDate(cur.getDate()+1);
    }
    return count;
  };
  const saveCongeRH = async () => {
    if(!form.employe_id||!form.date_debut||!form.date_fin) return showToast("Employé et dates requis",false);
    const nb=rhDaysInclusive(form.date_debut,form.date_fin);
    if(nb<=0) return showToast("Période de congé invalide",false);
    const emp=employeeById(form.employe_id);
    setRhSaving(true);
    const payload={
      employe_id:String(form.employe_id), employe_nom:rhEmployeeLabel(form.employe_id), ville:emp?.ville||"",
      type_conge:form.type_conge||"Congé annuel", date_debut:form.date_debut, date_fin:form.date_fin,
      nb_jours:nb, motif:form.motif||"", statut:form.statut||"En attente",
      demande_par:form.demande_par||session.nom||"", justificatif_url:form.justificatif_url||"", updated_at:new Date().toISOString()
    };
    const q=form.id?supabase.from("conges").update(payload).eq("id",form.id):supabase.from("conges").insert(payload);
    const {error}=await q; setRhSaving(false);
    if(error) return showToast("Congé non enregistré : "+error.message,false);
    await supabase.from("historique_employes").insert({employe_id:String(form.employe_id),employe_nom:payload.employe_nom,categorie:"Congé",champ:payload.type_conge,ancienne_valeur:"",nouvelle_valeur:`${payload.date_debut} au ${payload.date_fin} — ${payload.statut}`,motif:payload.motif,effectue_par_id:String(session.id||session.matricule||""),effectue_par_nom:session.nom||"",ville:payload.ville});
    await load(); closeModal(); showToast("Demande de congé enregistrée ✓");
  };
  const saveJourFerie = async () => {
    if(!form.date||!form.libelle) return showToast("Date et libellé requis",false);
    setRhSaving(true);
    const payload={ date:form.date, libelle:form.libelle, mobile:!!form.mobile };
    const q=form.id?supabase.from("jours_feries").update(payload).eq("id",form.id):supabase.from("jours_feries").insert(payload);
    const {error}=await q; setRhSaving(false);
    if(error) return showToast("Jour férié non enregistré : "+(error.code==="23505"?"cette date est déjà déclarée":error.message),false);
    await load(); closeModal(); showToast("Jour férié enregistré ✓");
  };
  const decideCongeRH = async (conge,statut) => {
    const commentaire=window.prompt(`Commentaire de décision (${statut}) :`,conge.commentaire_validation||"");
    if(commentaire===null) return;
    const {error}=await supabase.from("conges").update({statut,valide_par_id:String(session.id||session.matricule||""),valide_par_nom:session.nom||"",date_validation:new Date().toISOString(),commentaire_validation:commentaire,updated_at:new Date().toISOString()}).eq("id",conge.id);
    if(error) return showToast("Décision non enregistrée : "+error.message,false);
    await supabase.from("historique_employes").insert({employe_id:String(conge.employe_id),employe_nom:conge.employe_nom||rhEmployeeLabel(conge.employe_id),categorie:"Validation congé",champ:conge.type_conge||"Congé",ancienne_valeur:conge.statut||"En attente",nouvelle_valeur:statut,motif:commentaire,effectue_par_id:String(session.id||session.matricule||""),effectue_par_nom:session.nom||"",ville:conge.ville||""});
    await load(); showToast(`Congé ${statut.toLowerCase()} ✓`);
    // Correctif du 30/09/2026 (audit — vague 7, à la demande de la direction) : le solde de
    // congés (jusqu'ici jamais mis à jour) est maintenant décompté automatiquement dès qu'un
    // congé est approuvé, via recalculerSoldeConges (voir plus bas).
    if (statut==="Approuvé") await recalculerSoldeConges(conge.employe_id);
  };
  // Recalcule et enregistre le solde de congés (année en cours) d'un employé : 1,5 jour ouvrable
  // acquis par mois entièrement écoulé de service à temps plein (Code du travail camerounais),
  // proratisé sur le mois d'embauche et sur tout mois où un congé approuvé réduit le temps
  // travaillé ; "pris" = somme des congés approuvés de l'année. Calculé côté base de données
  // (conges_solde_calcule) pour rester fiable même si plusieurs personnes consultent la fiche.
  const recalculerSoldeConges = async (employeId) => {
    const { data, error } = await supabase.rpc("conges_solde_calcule", { p_employe_id: String(employeId) });
    if (error) { showToast("Solde de congés non recalculé : "+error.message, false); return null; }
    await load();
    return data;
  };
  const saveDocumentRH = async () => {
    if(!form.employe_id||!form.type_document||!form.fichier_url) return showToast("Employé, type et lien du fichier requis",false);
    const emp=employeeById(form.employe_id); setRhSaving(true);
    const payload={employe_id:String(form.employe_id),employe_nom:rhEmployeeLabel(form.employe_id),type_document:form.type_document,titre:form.titre||form.type_document,fichier_url:form.fichier_url,nom_fichier:form.nom_fichier||"",mime_type:form.mime_type||"",date_document:form.date_document||null,date_expiration:form.date_expiration||null,confidentiel:form.confidentiel!==false,notes:form.notes||"",ajoute_par_id:String(session.id||session.matricule||""),ajoute_par_nom:session.nom||"",updated_at:new Date().toISOString()};
    const q=form.id?supabase.from("documents_employes").update(payload).eq("id",form.id):supabase.from("documents_employes").insert(payload);
    const {error}=await q; setRhSaving(false);
    if(error) return showToast("Document non enregistré : "+error.message,false);
    await supabase.from("historique_employes").insert({employe_id:String(form.employe_id),employe_nom:payload.employe_nom,categorie:"Document",champ:payload.type_document,ancienne_valeur:"",nouvelle_valeur:payload.titre,motif:payload.notes,effectue_par_id:String(session.id||session.matricule||""),effectue_par_nom:session.nom||"",ville:emp?.ville||""});
    await load(); closeModal(); showToast("Document RH enregistré ✓");
  };

  // Le directeur saisit les données du contrat ; le PDF est généré puis déposé
  // directement dans « Mes documents RH » du collaborateur concerné.
  const genererContratTravail = async () => {
    const c = form;
    if (!c.employe_id) return showToast("Employé introuvable",false);
    if (!c.type_contrat) return showToast("Choisis le type de contrat",false);
    if (!c.poste) return showToast("Le poste est requis",false);
    if (!c.date_debut) return showToast("La date de début est requise",false);
    if (String(c.type_contrat).toUpperCase()==="CDD" && !c.date_fin_contrat) return showToast("Indique la date de fin pour un CDD",false);
    if (!toFiniteNumber(c.salaire_base)) return showToast("Le salaire de base est requis",false);
    const employe = employeeById(c.employe_id);
    if (!employe) return showToast("Fiche employé introuvable",false);
    setContratGenerating(true);
    try {
      const nomEmploye = [employe.prenom,employe.nom].filter(Boolean).join(" ")||employe.nom;
      const titreContrat = "Contrat de travail — "+nomEmploye;
      const signatures = await runSignatureFlow({
        titre: titreContrat,
        typeDocument: "contrat",
        payload: { data: c, employeId: employe.id },
        signers: [
          { key:"employeur", label:"L'Employeur", employeId: session?.id, matricule: session?.matricule },
          { key:"salarie", label:"Le Salarié ("+nomEmploye+")", employeId: employe.id, matricule: employe.matricule }
        ]
      });
      const pdf = await generateContratTravailPdfBase64(c, employe, entreprise, signatures);
      const byteChars = atob(pdf.base64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i=0;i<byteChars.length;i++) bytes[i]=byteChars.charCodeAt(i);
      const file = new Blob([bytes], { type:"application/pdf" });
      const path = `${employe.id}/${Date.now()}_${pdf.filename.replace(/[^a-zA-Z0-9._-]/g,"_")}`;
      const { error: uploadError } = await supabase.storage.from(EMPLOYE_DOCS_BUCKET).upload(path, file, {
        cacheControl:"3600", upsert:false, contentType:"application/pdf"
      });
      if (uploadError) throw new Error("Envoi du PDF impossible : "+uploadError.message);

      const titre = "Contrat de travail — "+c.type_contrat;
      const { error: insertError } = await supabase.from("documents_employes").insert({
        employe_id: String(employe.id), employe_nom: employe.nom,
        type_document: "Contrat de travail", titre,
        fichier_url: path, nom_fichier: pdf.filename, mime_type:"application/pdf",
        taille_octets: file.size, date_document: c.date_debut||today(),
        date_expiration: c.date_fin_contrat||null, confidentiel:true,
        statut: "Validé",
        ajoute_par_id: String(session.id||session.matricule||""), ajoute_par_nom: session.nom||""
      });
      if (insertError) {
        await supabase.storage.from(EMPLOYE_DOCS_BUCKET).remove([path]);
        throw new Error(insertError.message);
      }

      // Reporte les termes du contrat sur la fiche RH de l'employé, comme le ferait
      // une mise à jour manuelle de l'onglet « Contrat & paie ».
      await supabase.from("employes").update({
        type_contrat: c.type_contrat, date_embauche: employe.date_embauche||c.date_debut,
        date_fin_contrat: c.date_fin_contrat||null, periode_essai_fin: c.periode_essai_fin||null,
        salaire_base: toFiniteNumber(c.salaire_base), prime_fixe: toFiniteNumber(c.prime_fixe),
        poste: c.poste||employe.poste, ville: c.ville||employe.ville,
        updated_at: new Date().toISOString()
      }).eq("id", employe.id);

      await supabase.from("historique_employes").insert({
        employe_id:String(employe.id), employe_nom:employe.nom, categorie:"Contrat",
        champ:"Contrat de travail généré", ancienne_valeur:employe.type_contrat||"",
        nouvelle_valeur:c.type_contrat, motif:"Généré depuis la fiche RH",
        effectue_par_id:String(session.id||session.matricule||""), effectue_par_nom:session.nom||"",
        ville:employe.ville||""
      });

      await load();
      closeModal();
      showToast("Contrat généré et déposé dans le profil du collaborateur ✓");
      await openPdf(pdf,"Contrat de travail");
    } catch (error) {
      showToast("Erreur : "+(error?.message||"génération impossible"), false);
    }
    setContratGenerating(false);
  };
  const genererProcuration = async () => {
    const c = form;
    if (!c.employe_id) return showToast("Employé introuvable",false);
    const procType = c.procurationType||"encaissement";
    let objetFinal = "";
    if (procType==="encaissement") {
      const ids = Array.isArray(c.clientIds) ? c.clientIds : [];
      if (!ids.length) return showToast("Choisis au moins un client",false);
      const noms = ids.map(id=>clients.find(cl=>String(cl.id)===String(id))?.nom).filter(Boolean);
      objetFinal = "Procéder, au nom et pour le compte de l'entreprise, à l'encaissement des paiements dus par le(s) client(s) suivant(s) : "+noms.join(", ")+".";
    } else {
      if (!c.objet||!c.objet.trim()) return showToast("L'objet de la procuration est requis",false);
      objetFinal = c.objet.trim();
    }
    if (!c.date_debut) return showToast("La date de début est requise",false);
    if (!c.date_fin) return showToast("La date de fin est requise",false);
    if (new Date(c.date_fin) < new Date(c.date_debut)) return showToast("La date de fin doit être après la date de début",false);
    const employe = employeeById(c.employe_id);
    if (!employe) return showToast("Fiche employé introuvable",false);
    if (String(employe.id)===String(session?.id)) return showToast("Le Mandant et le Mandataire ne peuvent pas être la même personne — choisis un autre collaborateur.",false);
    setProcurationGenerating(true);
    try {
      const nomEmploye = [employe.prenom,employe.nom].filter(Boolean).join(" ")||employe.nom;
      const titreProcuration = "Procuration — "+nomEmploye;
      const dataForPdf = { ...c, objet: objetFinal };
      const nomMandantActuel = [session?.prenom,session?.nom].filter(Boolean).join(" ")||session?.nom||"Vous";

      // La procuration ne doit être générée et déposée que lorsque les DEUX parties
      // ont signé (le Mandant, c'est-à-dire le directeur connecté, et le Mandataire).
      // On capture d'abord la signature du Mandant — obligatoire, puisque c'est lui
      // qui est en train de créer le document, il n'y a personne d'autre à qui la
      // demander plus tard.
      const captureMandant = await obtenirSignatures(titreProcuration, [{ key:"mandant", label:"Le Mandant — "+nomMandantActuel, employeId: session?.id }]);
      if (!captureMandant.mandant) {
        showToast("Ta signature (Mandant) est requise pour créer la procuration — reprends la génération et signe dans le cadre prévu.",false);
        setProcurationGenerating(false);
        return;
      }

      // Puis celle du Mandataire : s'il est présent, il peut signer tout de suite ;
      // sinon une demande de signature à distance est créée automatiquement (retrouvable
      // dans « Mes documents à signer » sur son profil) — la signature du Mandant est
      // conservée dans le payload pour ne pas être perdue d'ici là.
      const signatures = await runSignatureFlow({
        titre: titreProcuration,
        typeDocument: "procuration",
        payload: { data: dataForPdf, employeId: employe.id, mandantSignature: captureMandant.mandant },
        signers: [
          { key:"mandataire", label:"Le Mandataire ("+nomEmploye+")", employeId: employe.id, matricule: employe.matricule }
        ]
      });
      const signaturesCompletes = { mandant: captureMandant.mandant, ...signatures };

      await load();
      closeModal();

      if (!signaturesCompletes.mandataire) {
        // Signature du Mandataire pas encore obtenue : on ne dépose rien de définitif
        // pour l'instant — la procuration apparaîtra en « En attente de signature »
        // et sera générée puis déposée automatiquement dès que le Mandataire signera
        // (voir signerDemandeEnAttente).
        showToast("Procuration en attente de la signature de "+nomEmploye+" — le document final sera déposé automatiquement dès qu'elle/il aura signé.");
        setProcurationGenerating(false);
        return;
      }

      const pdf = await generateProcurationPdfBase64(dataForPdf, employe, entreprise, signaturesCompletes);
      await deposerDocumentSigne({
        employeId: employe.id, employeNom: employe.nom,
        typeDocument: "Procuration", titre: "Procuration — "+objetFinal.slice(0,60),
        pdf, dateDocument: c.date_debut, dateExpiration: c.date_fin,
        categorieHistorique: "Procuration", champHistorique: "Procuration accordée",
        valeurHistorique: objetFinal.slice(0,120)+" ("+fmtDate(c.date_debut)+" → "+fmtDate(c.date_fin)+")"
      });

      await load();
      showToast("Procuration générée et déposée dans le profil du collaborateur ✓ (signée par les deux parties)");
      await openPdf(pdf,"Procuration");
    } catch (error) {
      showToast("Erreur : "+(error?.message||"génération impossible"), false);
    }
    setProcurationGenerating(false);
  };
  // Supprime une procuration déjà générée (document déposé) — retire le PDF du
  // stockage et la fiche du dossier RH, et journalise la suppression.
  const deleteProcurationDocument = async (d) => {
    if (!window.confirm("Supprimer définitivement cette procuration (« "+(d.titre||"")+" ») ? Cette action est irréversible.")) return;
    try {
      if (d.fichier_url) await supabase.storage.from(EMPLOYE_DOCS_BUCKET).remove([d.fichier_url]);
      const { error } = await supabase.from("documents_employes").delete().eq("id", d.id);
      if (error) throw error;
      await supabase.from("historique_employes").insert({
        employe_id:String(d.employe_id||""), employe_nom:d.employe_nom||"", categorie:"Procuration",
        champ:"Procuration supprimée", ancienne_valeur:d.titre||"", nouvelle_valeur:"",
        motif:"Supprimée depuis la liste des procurations",
        effectue_par_id:String(session.id||session.matricule||""), effectue_par_nom:session.nom||"",
        ville: employeeById(d.employe_id)?.ville||""
      });
      await load();
      showToast("Procuration supprimée ✓");
    } catch (err) { showToast("Suppression impossible : "+err.message,false); }
  };
  // Annule une demande de procuration encore en attente de la signature du Mandataire
  // (aucun document n'a encore été déposé à ce stade, il n'y a donc que la demande à retirer).
  const deleteProcurationDemande = async (d) => {
    if (!window.confirm("Annuler cette demande de procuration en attente de signature ?")) return;
    const { error } = await supabase.from("demandes_signature").delete().eq("id", d.id);
    if (error) return showToast("Annulation impossible : "+error.message,false);
    await load();
    showToast("Demande de procuration annulée ✓");
  };
  const saveEvaluationRH = async () => {
    if(!form.employe_id) return showToast("Employé requis",false);
    const note=form.note===""||form.note==null?null:Number(form.note);
    if(note!=null&&(note<0||note>100)) return showToast("La note doit être comprise entre 0 et 100",false);
    setRhSaving(true);
    const payload={employe_id:String(form.employe_id),employe_nom:rhEmployeeLabel(form.employe_id),evaluateur_id:String(session.id||session.matricule||""),evaluateur_nom:session.nom||"",periode_debut:form.periode_debut||null,periode_fin:form.periode_fin||null,type_evaluation:form.type_evaluation||"Évaluation périodique",objectifs:form.objectifs||"",resultats:form.resultats||"",points_forts:form.points_forts||"",axes_amelioration:form.axes_amelioration||"",note,recommandations:form.recommandations||"",statut:form.statut||"Brouillon",date_validation:(form.statut==="Validée"?new Date().toISOString():null),updated_at:new Date().toISOString()};
    const q=form.id?supabase.from("evaluations_employes").update(payload).eq("id",form.id):supabase.from("evaluations_employes").insert(payload);
    const {error}=await q; setRhSaving(false);
    if(error) return showToast("Évaluation non enregistrée : "+error.message,false);
    await supabase.from("historique_employes").insert({employe_id:String(form.employe_id),employe_nom:payload.employe_nom,categorie:"Évaluation",champ:payload.type_evaluation,ancienne_valeur:"",nouvelle_valeur:note==null?payload.statut:`${note}/100 — ${payload.statut}`,motif:payload.recommandations,effectue_par_id:String(session.id||session.matricule||""),effectue_par_nom:session.nom||"",ville:employeeById(form.employe_id)?.ville||""});
    await load(); closeModal(); showToast("Évaluation enregistrée ✓");
  };
  const deleteRhRecord = async (table,id,label) => {
    if(!window.confirm(`Supprimer ${label} ?`)) return;
    const {error}=await supabase.from(table).delete().eq("id",id);
    if(error) return showToast("Suppression impossible : "+error.message,false);
    await load(); showToast("Élément supprimé ✓");
  };


  const saveEmploye = async () => {
    if (!form.nom) return showToast("Nom requis",false);
    // Les fonctions cochées déterminent le rôle principal (une seule valeur, calculée par
    // priorité) utilisé partout ailleurs dans l'appli — voir rolePrincipalDeFonctions.
    const fonctions = Array.isArray(form.fonctions) && form.fonctions.length ? form.fonctions : (form.role?[form.role]:["employe"]);
    const rolePrincipal = rolePrincipalDeFonctions(fonctions);
    if (!form.ville && !["directeur","administrateur"].includes(rolePrincipal))
      return showToast("La ville d'affectation est obligatoire",false);
    // Le username et le mot de passe sont désormais optionnels à la création : on peut
    // enregistrer un employé avec juste son nom, puis lui créer son compte de connexion
    // plus tard depuis l'onglet "Accès ERP" (bouton "Créer le compte de connexion"). À la
    // création seulement, on exige que les deux soient remplis ensemble, jamais un seul des deux.
    //
    // Bug corrigé le 04/10/2026 : cette règle s'appliquait aussi en MODIFICATION d'une fiche
    // existante. Or pour un employé qui a déjà un compte, le champ "Username" arrive pré-rempli
    // avec son identifiant actuel (normal), alors que "Nouveau mot de passe" reste vide (on ne
    // veut pas le réinitialiser à chaque modif) — ce qui déclenchait systématiquement ce message
    // et empêchait d'enregistrer la moindre modification (ville, poste, une case cochée...) sur
    // un employé ayant déjà un compte de connexion. La règle ne doit porter que sur la création.
    if (!form.id) {
      const usernameProvided = String(form.username||"").trim().length>0;
      const passwordProvided = String(form.password||"").trim().length>0;
      if (usernameProvided !== passwordProvided)
        return showToast("Renseigne l'identifiant ET le mot de passe ensemble, ou laisse les deux vides pour créer le compte plus tard.",false);
    }
    setLoading(true);
    const perms = Array.isArray(form.permissions) ? form.permissions : [];
    const ancienEmploye = form.id ? employes.find(e=>String(e.id)===String(form.id)) : null;
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch("/api/save-employee", {
        method:"POST",
        headers:{"Content-Type":"application/json", ...(authSession?.access_token?{"Authorization":"Bearer "+authSession.access_token}:{})},
        body: JSON.stringify({
          id: form.id, nom: form.nom, poste: form.poste||"", service: form.service||"",
          role: rolePrincipal, fonctions, ville:form.ville||"", username: String(form.username||"").trim().toLowerCase(), password: form.password||undefined,
          tel: form.tel||"", email: form.email||"", actif: form.actif!==false, permissions: perms
        })
      });
      const result = await res.json().catch(()=>({}));
      if (!res.ok || !result.ok) { showToast("Erreur : "+(result.error||"inconnue"), false); setLoading(false); return; }
      // Avertissement non bloquant (ex: échec de synchronisation du compte de connexion après
      // un changement d'identifiant) — affiché tel quel dès qu'il existe, la fiche est déjà
      // enregistrée à ce stade donc on ne bloque pas, mais l'info ne doit pas passer inaperçue.
      const saveWarning = result.warning || null;
      // Toujours résoudre l'identifiant réel enregistré en base. Certaines versions
      // de /api/save-employee ne renvoient pas l'ID, ce qui faisait exécuter une mise
      // à jour RH sur zéro ligne sans erreur visible.
      let employeeId=form.id||result.employe?.id||result.id||null;
      if(!employeeId){
        const {data:employeeRow,error:employeeLookupError}=await supabase
          .from("employes")
          .select("id")
          .eq("username",String(form.username||"").trim().toLowerCase())
          .maybeSingle();
        if(employeeLookupError){
          showToast("Compte sauvegardé, mais identification de la fiche RH impossible : "+employeeLookupError.message,false);
          setLoading(false); return;
        }
        employeeId=employeeRow?.id||null;
      }
      if(!employeeId){
        showToast("Compte sauvegardé, mais la fiche RH correspondante est introuvable",false);
        setLoading(false); return;
      }
      const rhPayload={
        prenom:form.prenom||"", sexe:form.sexe||"", date_naissance:form.date_naissance||null,
        lieu_naissance:form.lieu_naissance||"", nationalite:form.nationalite||"", numero_cni:form.numero_cni||"",
        date_validite_cni:form.date_validite_cni||null,
        adresse:form.adresse||"", contact_urgence_nom:form.contact_urgence_nom||"", contact_urgence_tel:form.contact_urgence_tel||"",
        poste:form.poste||"", service:form.service||"", departement:form.departement||form.service||"",
        manager_id:form.manager_id||null, ville:form.ville||"", role:rolePrincipal, fonctions, permissions:perms,
        date_embauche:form.date_embauche||null, date_depart:form.date_depart||null,
        type_contrat:form.type_contrat||"", date_fin_contrat:form.date_fin_contrat||null,
        periode_essai_fin:form.periode_essai_fin||null, statut_rh:form.statut_rh||(form.actif===false?"Inactif":"Actif"),
        salaire_base:toFiniteNumber(form.salaire_base), prime_fixe:toFiniteNumber(form.prime_fixe),
        commission:toFiniteNumber(form.commission), mode_paiement:form.mode_paiement||"",
        banque:form.banque||"", numero_compte:form.numero_compte||"", photo_url:form.photo_url||"",
        signature_url:form.signature_url||"", notes_rh:form.notes_rh||"", updated_at:new Date().toISOString()
      };
      const {data:rhSavedRows,error:rhSaveError}=await supabase
        .from("employes")
        .update(rhPayload)
        .eq("id",employeeId)
        .select("id");
      if(rhSaveError){
        showToast("Compte sauvegardé, mais la fiche RH n'a pas été enregistrée : "+rhSaveError.message,false);
        setLoading(false); return;
      }
      if(!rhSavedRows?.length){
        showToast("Compte sauvegardé, mais aucune fiche RH n'a été mise à jour. Vérifie les droits Supabase sur employes.",false);
        setLoading(false); return;
      }
      const employeeIdText=String(employeeId||"");
      if(ancienEmploye){
        const champsSuivis=[
          ["poste","Poste"],["service","Service"],["departement","Département"],["ville","Ville"],
          ["manager_id","Responsable hiérarchique"],["type_contrat","Type de contrat"],
          ["date_fin_contrat","Fin de contrat"],["statut_rh","Statut RH"],
          ["salaire_base","Salaire de base"],["prime_fixe","Prime fixe"],["commission","Commission"]
        ];
        const changements=champsSuivis.filter(([champ])=>String(ancienEmploye[champ]??"")!==String(rhPayload[champ]??""));
        if(changements.length){
          await supabase.from("historique_employes").insert(changements.map(([champ,label])=>({
            employe_id:employeeIdText, employe_nom:form.nom, categorie:"Mise à jour RH", champ:label,
            ancienne_valeur:String(ancienEmploye[champ]??""), nouvelle_valeur:String(rhPayload[champ]??""),
            motif:form.motif_modification||"", effectue_par_id:String(session.id||session.matricule||""),
            effectue_par_nom:session.nom||"", ville:form.ville||""
          })));
        }
      } else {
        await supabase.from("historique_employes").insert({
          employe_id:employeeIdText, employe_nom:form.nom, categorie:"Création", champ:"Fiche employé",
          ancienne_valeur:"", nouvelle_valeur:"Employé créé", effectue_par_id:String(session.id||session.matricule||""),
          effectue_par_nom:session.nom||"", ville:form.ville||""
        });
      }
      if (saveWarning) alert(saveWarning);
      showToast(form.id ? "Fiche employé mise à jour ✓" : "Employé créé ✓");
      await load(); setLoading(false); closeModal();
    } catch(e) { showToast("Erreur: "+e.message, false); setLoading(false); }
  };

  // Suppression définitive d'une fiche employé, réservée à la correction d'erreurs de
  // saisie (ex. doublon, fiche de test). Passe par le serveur (clé service_role) pour
  // pouvoir aussi supprimer le compte Supabase Auth associé — sans quoi le compte
  // resterait orphelin et provoquerait la même erreur "duplicate key" déjà rencontrée.
  // Bloquée côté serveur si l'employé a de la paie enregistrée ou une procuration
  // signée, pour ne jamais perdre une trace comptable ou légale.
  const deleteEmploye = async () => {
    if (!form.id) return;
    const nomComplet = [form.prenom, form.nom].filter(Boolean).join(" ") || form.nom;
    if (!window.confirm(`Supprimer définitivement la fiche de ${nomComplet} ?\n\nCeci efface la fiche RH, son historique, ses congés, évaluations, documents RH et son compte de connexion. Cette action est irréversible.\n\nSi cet employé a de la paie ou une procuration signée enregistrée, la suppression sera refusée.`)) return;
    setDeletingEmploye(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch("/api/delete-employee", {
        method:"POST",
        headers:{"Content-Type":"application/json", ...(authSession?.access_token?{"Authorization":"Bearer "+authSession.access_token}:{})},
        body: JSON.stringify({ id: form.id })
      });
      const result = await res.json().catch(()=>({}));
      if (!res.ok || !result.ok) { showToast("Suppression impossible : "+(result.error||"erreur inconnue"), false); setDeletingEmploye(false); return; }
      setDeletingEmploye(false);
      showToast("Fiche supprimée ✓");
      await load(); closeModal();
    } catch(e) { showToast("Erreur : "+e.message, false); setDeletingEmploye(false); }
  };

  // Stock de bouteilles vides disponible = tout ce qui a été reçu à l'usine, moins tout
  // ce que les lots de production ont déjà consommé. C'est un pool UNIQUE et partagé
  // entre toutes les saveurs (les bouteilles vides ne sont pas spécifiques à un produit
  // avant remplissage) — d'où l'absence de paramètre produit ici.
  const stockBouteillesVides = () => {
    const entrees = receptionsBouteilles.reduce((s,r)=>s+toFiniteNumber(r.quantite),0);
    const utilisees = lots.reduce((s,l)=>s+toFiniteNumber(l.bouteilles_utilisees),0);
    return entrees - utilisees;
  };

  // Bilan mensuel des bouteilles vides, avec REPORT : le reste à la fin d'un mois devient la
  // disponibilité de début du mois suivant. Entrées = réceptions saisies dans l'appli ; sorties =
  // bouteilles utilisées par les lots de production. Le reste du dernier mois est, par
  // construction, égal à stockBouteillesVides().
  const bilanBouteillesParMois = () => {
    const entrees = {}, sorties = {};
    receptionsBouteilles.forEach(r=>{ const k=String(r.date||"").slice(0,7); if(k.length===7) entrees[k]=(entrees[k]||0)+toFiniteNumber(r.quantite); });
    lots.forEach(l=>{ const k=String(l.date||"").slice(0,7); if(k.length===7) sorties[k]=(sorties[k]||0)+toFiniteNumber(l.bouteilles_utilisees); });
    const cles = Object.keys({...entrees,...sorties}).sort();
    if (!cles.length) return [];
    const moisCourant = today().slice(0,7);
    const dernier = cles[cles.length-1] > moisCourant ? cles[cles.length-1] : moisCourant;
    let [y,m] = cles[0].split("-").map(Number);
    const lignes = [];
    let reste = 0, guard = 0;
    while (guard++ < 600) {
      const k = y+"-"+String(m).padStart(2,"0");
      const e = entrees[k]||0, so = sorties[k]||0;
      lignes.push({ mois:k, debut:reste, entrees:e, sorties:so, fin:reste+e-so });
      reste = reste+e-so;
      if (k>=dernier) break;
      m++; if (m>12) { m=1; y++; }
    }
    return lignes;
  };

  const saveReceptionBouteille = async () => {
    if(!form.quantite||parseInt(form.quantite)<=0) return showToast("Quantité requise",false);
    setLoading(true);
    const { error } = await supabase.from("receptions_bouteilles").insert({
      date:form.date||today(), quantite:parseInt(form.quantite),
      fournisseur:form.fournisseur||"", ville:"Dschang", notes:form.notes||"",
      cree_par_id:String(session.id||session.matricule||""), cree_par_nom:session.nom||""
    });
    setLoading(false);
    if (error) return showToast("Réception non enregistrée : "+error.message,false);
    await load(); closeModal(); showToast("Réception de bouteilles enregistrée ✓");
  };

  const saveLot = async () => {
    if (!form.produit) return showToast("Produit requis",false);
    // Le nombre de bouteilles produites se saisit désormais en fin de production (à la
    // création, on ne connaît pas encore forcément ce chiffre) — donc optionnel ici.
    const quantite = form.quantite===""||form.quantite==null ? 0 : parseInt(form.quantite,10);
    // Contrôle bouteilles vides : un lot ne peut pas consommer plus de bouteilles vides
    // que ce qui est réellement disponible dans le pool commun — même principe que le
    // stock des dépôts qui limite déjà les factures/livraisons à ce qui est en stock.
    const bouteillesUtilisees = form.bouteilles_utilisees===""||form.bouteilles_utilisees==null ? null : parseInt(form.bouteilles_utilisees,10);
    if (bouteillesUtilisees!=null) {
      const dejaUtiliseParCeLot = form.id ? toFiniteNumber(lots.find(l=>String(l.id)===String(form.id))?.bouteilles_utilisees) : 0;
      const dispo = stockBouteillesVides() + dejaUtiliseParCeLot;
      if (bouteillesUtilisees > dispo) return showToast(`Stock de bouteilles vides insuffisant (disponible : ${dispo})`,false);
    }
    setLoading(true);
    if (form.id) {
      const ancienneQuantite = toFiniteNumber(lots.find(l=>String(l.id)===String(form.id))?.quantite);
      const { error } = await supabase.from("lots").update({ produit:form.produit, date:form.date||today(), quantite, ville:"Dschang", statut:form.statut, notes:form.notes||"", bouteilles_utilisees:bouteillesUtilisees }).eq("id",form.id);
      if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
      // La quantité étant maintenant souvent renseignée après coup (édition du lot), on
      // répercute la différence sur le stock du produit fini plutôt que de l'ignorer.
      const delta = quantite - ancienneQuantite;
      if (delta !== 0) {
        const stockRes = await adjustStock(form.produit, delta);
        if (!stockRes.ok) showToast("Lot mis à jour mais erreur de mise à jour du stock : "+stockRes.error, false);
      }
    } else {
      let numero;
      try { numero=await genLotNum(form.produit); }
      catch(e) { showToast("Lot non créé : "+e.message,false); setLoading(false); return; }
      const { error:e1 } = await supabase.from("lots").insert({ numero, produit:form.produit, date:form.date||today(), quantite, ville:"Dschang", statut:"En stock", notes:form.notes||"", bouteilles_utilisees:bouteillesUtilisees, operateur:session.nom });
      if (e1) { showToast("Erreur : "+e1.message, false); setLoading(false); return; }
      if (quantite > 0) {
        const stockRes = await adjustStock(form.produit, quantite);
        if (!stockRes.ok) showToast("Lot créé mais erreur de mise à jour du stock : "+stockRes.error, false);
      }
    }
    await load(); setLoading(false); showToast("Lot sauvegardé ✓"); closeModal();
  };

  const deleteLot = async (lot) => {
    if (!window.confirm("Supprimer le lot "+lot.numero+" ?")) return;
    setLoading(true);
    const stockRes = await adjustStock(lot.produit, -lot.quantite);
    if (!stockRes.ok) { showToast("Erreur de mise à jour du stock : "+stockRes.error, false); setLoading(false); return; }
    const { error } = await supabase.from("lots").delete().eq("id", lot.id);
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast("Lot supprimé ✓");
  };

  // ══ CONTRÔLE QUALITÉ PAR LOT (fiche remplie par le producteur, en 2 étapes) ══
  const QC_BUCKET = "controle-qualite";

  // Étape 1 : ouverture du lot (n° imprimé par la machine + produit). Le producteur
  // et la date sont automatiques côté base (producteur_id/date_production par défaut).
  const qcOuvrirLot = async () => {
    if (!qcNumeroLot.trim()) return setQcMessage({ type:"error", text:"Saisis le n° de lot imprimé par la machine." });
    if (!qcProduit) return setQcMessage({ type:"error", text:"Choisis le produit." });
    setQcSaving(true); setQcMessage(null);
    const { data, error } = await supabase.from("fiches_controle_lot").insert({
      numero_lot: qcNumeroLot.trim(), produit: qcProduit,
      producteur_nom: session.nom||"", ville: session.ville||""
    }).select("id").single();
    setQcSaving(false);
    if (error) return setQcMessage({ type:"error", text:"Erreur : "+error.message });
    setQcFicheId(data.id); setQcForm({...QC_EMPTY_FORM}); setQcConformites({}); setQcPhase("complete");
    setQcMessage({ type:"success", text:`Lot ${qcNumeroLot} ouvert. À compléter en fin de production.` });
    await load();
  };

  // Reprendre un lot resté ouvert (repris depuis la liste, quel que soit qui l'a ouvert —
  // utile en cas de relève d'équipe).
  const qcReprendreLot = async (id) => {
    if (!id) return;
    const { data, error } = await supabase.from("fiches_controle_lot").select("*").eq("id", id).single();
    if (error || !data) return showToast("Impossible de charger ce lot : "+(error?.message||"introuvable"), false);
    setQcFicheId(data.id); setQcNumeroLot(data.numero_lot); setQcProduit(data.produit);
    setQcConformites(data.conformites||{});
    setQcForm({
      volume_litres: data.volume_litres ?? "", nombre_bouteilles: data.nombre_bouteilles ?? "",
      ph_avant: data.ph_avant ?? "", ph_apres: data.ph_apres ?? "",
      temp_max_pasteur: data.temp_max_pasteur ?? "", duree_maintien: data.duree_maintien ?? "",
      temp_remplissage: data.temp_remplissage ?? "", temp_refroidissement: data.temp_refroidissement ?? "",
      heure_debut: data.heure_debut ?? "", heure_fin: data.heure_fin ?? "",
      delai_embouteillage: data.delai_embouteillage ?? "",
      decision: data.decision ?? "", observations: data.observations ?? "",
      visa_responsable: data.visa_responsable ?? "",
    });
    setQcPhase("complete"); setQcMessage(null);
  };

  const qcAnnulerSaisie = () => {
    setQcPhase("start"); setQcFicheId(null); setQcNumeroLot(""); setQcProduit("");
    setQcForm({...QC_EMPTY_FORM}); setQcConformites({}); setQcMessage(null);
  };

  // Étape 2 : enregistrer la progression, ou clôturer. Si le lot est clôturé avec la
  // décision « libéré » (tout est allé bien), le PDF récapitulatif est généré et déposé
  // automatiquement, identifié par le n° de lot — sinon (bloqué, ou simple sauvegarde
  // intermédiaire) aucun PDF n'est produit.
  const qcEnregistrer = async (cloturer) => {
    if (!qcFicheId) return;
    if (cloturer && qcForm.nombre_bouteilles==="") return setQcMessage({ type:"error", text:"Renseigne le nombre de bouteilles (compteur machine) avant de clôturer." });
    if (cloturer && !qcForm.decision) return setQcMessage({ type:"error", text:"Choisis une décision (lot libéré ou bloqué) avant de clôturer." });
    setQcSaving(true); setQcMessage(null);
    const num = (v) => (v===""||v==null ? null : Number(v));
    // Correctif du 30/09/2026 : pour un lot libéré, statut:"cloture" n'est plus posé ici mais
    // seulement après que l'entrée en stock plus bas a réellement réussi — avant ce correctif,
    // une fiche pouvait se retrouver marquée « clôturée » (donc vendable) alors que son stock
    // n'avait jamais été crédité, en cas d'échec de cette étape suivante. Un lot bloqué n'a pas
    // cette étape à attendre : sa clôture peut être posée immédiatement.
    const payload = {
      volume_litres: num(qcForm.volume_litres), nombre_bouteilles: num(qcForm.nombre_bouteilles),
      ph_avant: num(qcForm.ph_avant), ph_apres: num(qcForm.ph_apres),
      temp_max_pasteur: num(qcForm.temp_max_pasteur), duree_maintien: qcForm.duree_maintien||null,
      temp_remplissage: num(qcForm.temp_remplissage), temp_refroidissement: num(qcForm.temp_refroidissement),
      heure_debut: qcForm.heure_debut||null, heure_fin: qcForm.heure_fin||null,
      delai_embouteillage: qcForm.delai_embouteillage||null,
      conformites: qcConformites,
      decision: qcForm.decision||null,
      observations: qcForm.observations||null,
      visa_responsable: qcForm.visa_responsable||null,
      updated_at: new Date().toISOString(),
      ...(cloturer && qcForm.decision!=="libere" ? { statut:"cloture" } : {}),
    };
    const { error } = await supabase.from("fiches_controle_lot").update(payload).eq("id", qcFicheId);
    if (error) { setQcSaving(false); return setQcMessage({ type:"error", text:"Erreur : "+error.message }); }

    let entreeStockReussie = true;
    if (cloturer && qcForm.decision==="libere") {
      setQcGeneratingPdf(true);
      try {
        const ficheComplete = { numero_lot:qcNumeroLot, produit:qcProduit, producteur_nom:session.nom||"", ville:session.ville||"", ...payload };
        const pdf = await generateFicheControleLotPdfBase64(ficheComplete, entreprise);
        const file = base64PdfToBlob(pdf.base64);
        const path = `${qcNumeroLot.replace(/[^a-zA-Z0-9._-]/g,"_")}/${Date.now()}_${pdf.filename}`;
        const { error: upErr } = await supabase.storage.from(QC_BUCKET).upload(path, file, { cacheControl:"3600", upsert:false, contentType:"application/pdf" });
        if (upErr) showToast("L'envoi du PDF a échoué : "+upErr.message, false);
        else await supabase.from("fiches_controle_lot").update({ pdf_path: path }).eq("id", qcFicheId);
      } catch (e) {
        showToast("La génération du PDF a échoué : "+e.message, false);
      }
      setQcGeneratingPdf(false);

      // Un lot libéré est un lot vendable : il alimente directement le stock, comme le
      // ferait une saisie manuelle dans « Production / Lots ». Un lot bloqué n'y entre
      // jamais. Hypothèse faute d'un champ dédié dans la fiche : une bouteille produite
      // consomme une bouteille vide (pas de casse comptée séparément) — à corriger à la
      // main dans « Production / Lots » si de la casse a eu lieu sur ce lot. C'est ce qui
      // fait avancer le compteur de bouteilles vides restantes affiché pendant la
      // production ; le contrôle qualité lui-même ne vérifie que la conformité aux normes
      // (pH, température, aspect), pas le stock — l'écart affiché dans Production/Rapports
      // reste donc à zéro par construction pour un lot issu du contrôle qualité, ce qui est
      // normal et attendu (ce n'est pas un indicateur d'anomalie pour ces lots-là).
      // La clôture (statut:"cloture") n'est posée qu'à la toute fin de ce bloc, uniquement
      // si l'entrée en stock a réussi — voir le commentaire au début de la fonction.
      try {
        const nombreBouteilles = num(qcForm.nombre_bouteilles) || 0;
        const numeroStock = await genLotNum(qcProduit);
        const dispo = stockBouteillesVides();
        if (nombreBouteilles > dispo) {
          showToast(`Stock de bouteilles vides insuffisant (disponible : ${dispo})`, false);
        }
        const { error: lotErr } = await supabase.from("lots").insert({
          numero: numeroStock, produit: qcProduit, date: today(), quantite: nombreBouteilles,
          operateur: session.nom||"", ville: "Dschang", statut: "En stock",
          notes: `Contrôle qualité — lot ${qcNumeroLot}`, bouteilles_utilisees: nombreBouteilles
        });
        if (lotErr) {
          entreeStockReussie = false;
          showToast("La fiche n'a pas été clôturée : l'entrée en stock a échoué : "+lotErr.message, false);
        } else {
          const stockRes = await adjustStock(qcProduit, nombreBouteilles);
          if (!stockRes.ok) {
            entreeStockReussie = false;
            showToast("La fiche n'a pas été clôturée : erreur de mise à jour du stock : "+stockRes.error, false);
          } else {
            const { error: clotureErr } = await supabase.from("fiches_controle_lot").update({ lot_stock_numero: numeroStock, statut:"cloture" }).eq("id", qcFicheId);
            if (clotureErr) { entreeStockReussie = false; showToast("Entrée en stock faite, mais la clôture de la fiche a échoué : "+clotureErr.message, false); }
          }
        }
      } catch (e) {
        entreeStockReussie = false;
        showToast("La fiche n'a pas été clôturée : l'entrée en stock a échoué : "+e.message, false);
      }
    }

    setQcSaving(false);
    await load();
    if (cloturer && entreeStockReussie) { setQcMessage({ type:"success", text:`Lot ${qcNumeroLot} clôturé.` }); qcAnnulerSaisie(); }
    else if (cloturer) setQcMessage({ type:"error", text:"Progression enregistrée, mais la clôture n'a pas pu être finalisée (voir message ci-dessus)." });
    else setQcMessage({ type:"success", text:"Progression enregistrée (lot toujours ouvert)." });
  };

  const qcOuvrirPdf = async (fiche) => {
    // On refait le PDF à partir des données enregistrées du lot : l'ancien fichier rangé à la
    // clôture garde sa présentation d'origine, alors que celui-ci suit toujours la présentation actuelle.
    try {
      const pdf = await generateFicheControleLotPdfBase64({ ...fiche, conformites: fiche.conformites || {} }, entreprise);
      await openPdf(pdf, "Fiche de contrôle qualité");
      return;
    } catch (e) {
      console.warn("Fiche qualité : régénération impossible, ouverture du fichier rangé.", e);
    }
    if (!fiche.pdf_path) return showToast("Impossible d'ouvrir la fiche.", false);
    const { data, error } = await supabase.storage.from(QC_BUCKET).createSignedUrl(fiche.pdf_path, 300);
    if (error || !data?.signedUrl) return showToast("Impossible d'ouvrir le PDF : "+(error?.message||"lien indisponible"), false);
    window.open(toProxyUrl(data.signedUrl), "_blank", "noopener");
  };

  // ══ PREUVES DE PAIEMENT ══
  const PAYMENT_BUCKET = "preuves-paiement";
  const EMPLOYE_DOCS_BUCKET = "documents-employes";

  // Un collaborateur envoie lui-même un document (CNI, diplôme...) : il part au statut
  // "En attente" et n'est visible que de lui-même et de la direction, jusqu'à validation.
  const saveMyDocument = async () => {
    const file = myDocForm.file;
    if (!file) return showToast("Choisis un fichier", false);
    if (!myDocForm.type_document || !myDocForm.titre) return showToast("Renseigne le type et le titre", false);
    const allowed = ["application/pdf","image/jpeg","image/png","image/webp"];
    if (!allowed.includes(file.type)) return showToast("Format accepté : PDF, JPG, PNG ou WEBP", false);
    if (file.size > 10*1024*1024) return showToast("Le fichier ne doit pas dépasser 10 Mo", false);

    setMyDocSaving(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g,"_");
      const path = `${session.id}/${Date.now()}_${safeName}`;
      const { error: uploadError } = await supabase.storage.from(EMPLOYE_DOCS_BUCKET).upload(path, file, {
        cacheControl:"3600", upsert:false, contentType:file.type
      });
      if (uploadError) throw new Error("Envoi du fichier impossible : "+uploadError.message);

      const { error: insertError } = await supabase.from("documents_employes").insert({
        employe_id: String(session.id),
        employe_nom: session.nom,
        type_document: myDocForm.type_document,
        titre: myDocForm.titre,
        fichier_url: path,
        nom_fichier: file.name,
        mime_type: file.type,
        taille_octets: file.size,
        date_document: today(),
        statut: "En attente",
        ajoute_par_id: String(session.id),
        ajoute_par_nom: session.nom
      });
      if (insertError) {
        await supabase.storage.from(EMPLOYE_DOCS_BUCKET).remove([path]);
        throw new Error(insertError.message);
      }
      await load();
      setMyDocForm({ type_document:"", titre:"", file:null });
      showToast("Document envoyé — en attente de validation par la direction ✓");
    } catch (error) {
      showToast("Erreur : "+(error?.message||"envoi impossible"), false);
    }
    setMyDocSaving(false);
  };

  const openMyDocument = async (doc) => {
    try {
      const { data, error } = await supabase.storage.from(EMPLOYE_DOCS_BUCKET).createSignedUrl(doc.fichier_url, 300);
      if (error) throw error;
      setPdfPreview({url:toProxyUrl(data.signedUrl), filename:doc.titre||"document.pdf", title:doc.titre||"Document"});
    } catch (error) {
      showToast("Impossible d'ouvrir le document : "+(error?.message||"erreur"), false);
    }
  };

  const validerMonDocument = async (doc) => {
    const { error } = await supabase.from("documents_employes").update({ statut:"Validé" }).eq("id", doc.id);
    if (error) return showToast("Erreur : "+error.message, false);
    await load();
    showToast("Document validé ✓");
  };


  const openPaiementModal = () => {
    openModal("paiement", {
      facture_numero:"", facture_id:null, client_nom:"", montant:"",
      date_versement:today(), moyen:"", reference_virement:"", preuveFile:null
    });
  };

  const selectPaiementFacture = (numero) => {
    const fac = factures.find(f=>f.numero===numero);
    const restant = fac ? resteFacture(fac) : 0;
    setForm(prev=>({
      ...prev,
      facture_numero:numero,
      reference_virement:(!prev.reference_virement||prev.reference_virement===prev.facture_numero)?numero:prev.reference_virement,
      facture_id:fac?.id||null,
      client_nom:fac?.client_nom||"",
      montant:restant||"",
      montant_facture:fac?calcTTC(fac):0,
      deja_paye:fac?totalPayeValide(fac):0,
      restant
    }));
  };

  const savePaiement = async () => {
    if (form.editId) return savePaiementModifie();
    if (!form.facture_numero) return showToast("Sélectionne une facture", false);
    if (!form.date_versement || !form.montant || Number(form.montant)<=0) return showToast("Date et montant requis", false);
    if (!form.moyen) return showToast("Choisis le moyen de paiement", false);
    if (!form.preuveFile) return showToast("Ajoute une image ou un PDF comme preuve", false);
    const fac = factures.find(f=>String(f.id)===String(form.facture_id)||f.numero===form.facture_numero);
    if (!fac) return showToast("Facture introuvable",false);
    const restant = resteFacture(fac);
    if (restant<=0) return showToast("Cette facture est déjà entièrement réglée",false);
    if (Number(form.montant)>restant) return showToast("Le versement dépasse le reste dû ("+FCFA(restant)+")",false);

    const file = form.preuveFile;
    const allowed = ["application/pdf","image/jpeg","image/png","image/webp"];
    if (!allowed.includes(file.type)) return showToast("Format accepté : PDF, JPG, PNG ou WEBP", false);
    if (file.size > 10*1024*1024) return showToast("Le fichier ne doit pas dépasser 10 Mo", false);

    setPaiementSaving(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g,"_");
      const folder = String(form.facture_numero).replace(/[^a-zA-Z0-9_-]/g,"_");
      const path = `${folder}/${Date.now()}_${safeName}`;
      const { error:uploadError } = await supabase.storage.from(PAYMENT_BUCKET).upload(path, file, {
        cacheControl:"3600", upsert:false, contentType:file.type
      });
      if (uploadError) throw new Error("Envoi du justificatif impossible : "+uploadError.message);

      const { error:insertError } = await supabase.from("paiements").insert({
        facture_numero:form.facture_numero,
        facture_id:form.facture_id || null,
        client_nom:form.client_nom || "",
        montant:Number(form.montant),
        date_versement:form.date_versement,
        moyen:form.moyen,
        reference_virement:form.reference_virement || "",
        preuve_url:path,
        statut:"En attente validation",
        soumis_par:session.nom,
        date_soumission:new Date().toISOString(),
        // Sans ce champ, l'enregistrement était rejeté par la sécurité en base pour tout
        // employé non-directeur (elle exige ville = sa propre ville) — trouvé lors de l'audit
        // du 30/09/2026 : la fonctionnalité était concrètement inutilisable pour eux.
        ville: fac.ville || sessionVille() || ""
      });
      if (insertError) {
        await supabase.storage.from(PAYMENT_BUCKET).remove([path]);
        throw new Error(insertError.message);
      }
      await load();
      closeModal();
      showToast("Preuve de paiement envoyée au directeur ✓");
    } catch(e) {
      showToast("Erreur : "+e.message, false);
    } finally {
      setPaiementSaving(false);
    }
  };

  const openPaiementProof = async (preuveUrl) => {
    if (!preuveUrl) return showToast("Aucun justificatif disponible", false);
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;

    // Application installée (PWA) : un nouvel onglet ne laisse aucun moyen de revenir en
    // arrière sur téléphone (pas de barre d'adresse ni de bouton "précédent" en mode
    // standalone) — le justificatif reste coincé à l'écran. On récupère donc le fichier et
    // on l'affiche DANS l'app, dans l'aperçu avec bouton "Fermer", comme pour les PDF
    // générés par l'application.
    if (isStandalone) {
      setLoading(true);
      try {
        let signedUrl = preuveUrl;
        if (!/^https?:\/\//i.test(preuveUrl)) {
          const { data, error } = await supabase.storage.from(PAYMENT_BUCKET).createSignedUrl(preuveUrl, 300);
          if (error || !data?.signedUrl) throw new Error(error?.message || "lien indisponible");
          signedUrl = data.signedUrl;
        }
        const res = await fetch(toProxyUrl(signedUrl));
        if (!res.ok) throw new Error("HTTP "+res.status);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPdfPreview({ url, filename:"justificatif", title:"Preuve de paiement" });
      } catch (e) {
        showToast("Impossible d'ouvrir le justificatif : "+e.message, false);
      }
      setLoading(false);
      return;
    }

    // Navigateur classique (pas installé en PWA) : un nouvel onglet se ferme normalement
    // avec les contrôles habituels du navigateur, pas besoin de l'aperçu intégré.
    // L'onglet doit être ouvert tout de suite (avant l'attente réseau ci-dessous), sinon les
    // navigateurs le bloquent comme un pop-up non sollicité.
    const tab = window.open("", "_blank");
    if (/^https?:\/\//i.test(preuveUrl)) {
      const proxied = toProxyUrl(preuveUrl);
      if (tab) tab.location.href = proxied; else window.open(proxied, "_blank", "noopener");
      return;
    }
    const { data, error } = await supabase.storage.from(PAYMENT_BUCKET).createSignedUrl(preuveUrl, 300);
    if (error || !data?.signedUrl) {
      if (tab) tab.close();
      return showToast("Impossible d'ouvrir le justificatif : "+(error?.message||"lien indisponible"), false);
    }
    if (tab) tab.location.href = toProxyUrl(data.signedUrl); else window.open(toProxyUrl(data.signedUrl), "_blank", "noopener");
  };

  const ouvrirModifPaiement = (p) => {
    const fac = factures.find(f=>String(f.id)===String(p.facture_id)||f.numero===p.facture_numero);
    openModal("paiement", {
      editId:p.id, facture_numero:p.facture_numero, facture_id:p.facture_id||fac?.id||null, client_nom:p.client_nom||fac?.client_nom||"",
      montant:p.montant, date_versement:String(p.date_versement||today()).slice(0,10), moyen:p.moyen||"", reference_virement:p.reference_virement||"",
      preuveFile:null, preuve_actuelle:p.preuve_url||"", motif_refus:p.commentaire||"",
      montant_facture:fac?calcTTC(fac):0, deja_paye:fac?totalPayeValide(fac):0, restant:fac?resteFacture(fac):0
    });
  };

  const supprimerPaiementRefuse = async (p) => {
    if (!isDirecteur()) return showToast("Action réservée à la direction",false);
    if (!window.confirm("Supprimer définitivement cette preuve refusée ?\n\n"+p.facture_numero+" — "+FCFA(p.montant)+" ("+(p.moyen||"—")+")\n\nCette action est irréversible.")) return;
    setLoading(true);
    const { data, error } = await supabase.rpc("supprimer_paiement_refuse",{ p_id:String(p.id) });
    if (error) { setLoading(false); return showToast("Suppression impossible : "+error.message,false); }
    if (data?.preuve && !/^https?:\/\//i.test(data.preuve)) { try { await supabase.storage.from(PAYMENT_BUCKET).remove([data.preuve]); } catch(_) {} }
    await load(); setLoading(false); showToast("Preuve refusée supprimée ✓");
  };

  const savePaiementModifie = async () => {
    if (!isDirecteur()) return showToast("Action réservée à la direction",false);
    if (!form.date_versement || !form.montant || Number(form.montant)<=0) return showToast("Date et montant requis", false);
    if (!form.moyen) return showToast("Choisis le moyen de paiement", false);
    const fac = factures.find(f=>String(f.id)===String(form.facture_id)||f.numero===form.facture_numero);
    if (!fac) return showToast("Facture introuvable",false);
    const restant = resteFacture(fac);
    if (restant<=0) return showToast("Cette facture est déjà entièrement réglée",false);
    if (Number(form.montant)>restant) return showToast("Le versement dépasse le reste dû ("+FCFA(restant)+")",false);
    const file = form.preuveFile;
    if (file) {
      if (!["application/pdf","image/jpeg","image/png","image/webp"].includes(file.type)) return showToast("Format accepté : PDF, JPG, PNG ou WEBP", false);
      if (file.size > 10*1024*1024) return showToast("Le fichier ne doit pas dépasser 10 Mo", false);
    }
    setPaiementSaving(true);
    let path = "";
    try {
      if (file) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g,"_");
        const folder = String(form.facture_numero).replace(/[^a-zA-Z0-9_-]/g,"_");
        path = `${folder}/${Date.now()}_${safeName}`;
        const { error:uploadError } = await supabase.storage.from(PAYMENT_BUCKET).upload(path, file, { cacheControl:"3600", upsert:false, contentType:file.type });
        if (uploadError) { path = ""; throw new Error("Envoi du justificatif impossible : "+uploadError.message); }
      }
      const { data, error } = await supabase.rpc("modifier_paiement_refuse",{ p_id:String(form.editId), p_montant:Number(form.montant), p_date:form.date_versement, p_moyen:form.moyen, p_reference:form.reference_virement||"", p_preuve:path });
      if (error) { if (path) { try { await supabase.storage.from(PAYMENT_BUCKET).remove([path]); } catch(_) {} } throw new Error(error.message); }
      if (path && data?.ancienne_preuve && !/^https?:\/\//i.test(data.ancienne_preuve)) { try { await supabase.storage.from(PAYMENT_BUCKET).remove([data.ancienne_preuve]); } catch(_) {} }
      await load();
      closeModal();
      showToast("Preuve corrigée : elle repart en attente de validation ✓");
    } catch(e) {
      showToast("Erreur : "+e.message, false);
    } finally {
      setPaiementSaving(false);
    }
  };

  const decidePaiement = async (paiement, decision) => {
    // Correctif du 30/09/2026 : ce message disait "réservée à la direction", mais l'autorisation
    // peut aussi être accordée individuellement (permission "valider_paiements") à une personne
    // qui n'est pas directeur — le message donnait une fausse impression de règle absolue.
    if (!canValidatePayments()) return showToast("Validation réservée aux personnes habilitées",false);
    if (!String(paiement?.statut||"").toLowerCase().includes("attente")) return showToast("Cette preuve a déjà été traitée",false);
    const commentaire = window.prompt(
      decision==="Validé" ? "Commentaire de validation (facultatif)" : "Motif du refus (requis)",
      paiement.commentaire||""
    );
    if (commentaire===null) return;
    if (decision==="Refusé"&&!commentaire.trim()) return showToast("Indique le motif du refus",false);
    if (!window.confirm(decision==="Validé"?"Valider définitivement ce paiement ?":"Refuser cette preuve de paiement ?")) return;

    setLoading(true);
    const { data, error } = await supabase.rpc("decider_paiement_transaction", {
      p_paiement_id:String(paiement.id),
      p_decision:decision,
      p_commentaire:commentaire.trim(),
      p_validateur_nom:session.nom,
      p_tva_taux:getTvaRate(entreprise)
    });
    if (error) { setLoading(false); return showToast("Décision non enregistrée : "+error.message,false); }
    await load();
    setLoading(false);
    if (decision==="Refusé") return showToast("Preuve refusée");
    const statut=data?.statut_facture||"mise à jour";
    showToast("Paiement validé — facture "+statut+" ✓");
  };


  const changePassword = async () => {
    if (!profileForm.ancienPwd||!profileForm.nouveauPwd||!profileForm.confirmPwd) return showToast("Remplissez tous les champs",false);
    if (profileForm.nouveauPwd !== profileForm.confirmPwd) return showToast("Les mots de passe ne correspondent pas",false);
    if (profileForm.nouveauPwd.length < 6) return showToast("Mot de passe trop court",false);
    setLoading(true);
    try {
      const { data: { session: authSession3 } } = await supabase.auth.getSession();
      const res = await fetch("/api/change-password", {
        method:"POST",
        headers:{"Content-Type":"application/json", ...(authSession3?.access_token ? {"Authorization":"Bearer "+authSession3.access_token} : {})},
        body: JSON.stringify({ employeId: session.id, ancienPwd: profileForm.ancienPwd, nouveauPwd: profileForm.nouveauPwd })
      });
      const result = await res.json().catch(()=>({}));
      setLoading(false);
      if (!res.ok || !result.ok) { showToast("Erreur : "+(result.error||"inconnue"), false); return; }
      setProfileForm({ ancienPwd:"", nouveauPwd:"", confirmPwd:"" }); setShowProfile(false);
      showToast("Mot de passe changé ✓");
    } catch(e) { setLoading(false); showToast("Erreur : "+e.message, false); }
  };

  /* ── Factures ── */
  // Modifier le prix unitaire d'une ligne déjà ajoutée (facture, modification de facture,
  // proforma) : le montant de la ligne est recalculé. Un champ vidé reste vide (pour pouvoir
  // retaper un nouveau prix) ; la création/modification refuse ensuite une ligne à prix nul.
  const majPrixLigne = (lignes, i, val) => (lignes||[]).map((x,idx)=>{
    if (idx!==i) return x;
    const p = val===""?"":parseFloat(val);
    return {...x, prixUnit:p, montant:toFiniteNumber(x.qte)*toFiniteNumber(p)};
  });
  const majQteLigne = (lignes, i, val) => (lignes||[]).map((x,idx)=>{
    if (idx!==i) return x;
    const q = val===""?NaN:parseInt(val,10);
    const qte = Number.isFinite(q)?q:"";
    return {...x, qte, montant:toFiniteNumber(qte)*toFiniteNumber(x.prixUnit)};
  });
  const ajouterLigneEdit = () => {
    const n = editNewLigne;
    const qte = parseInt(n.qte,10);
    if (!n.produit || !(qte>0)) return showToast("Choisissez un produit et une quantité",false);
    const prod = produits.find(p=>p.nom===n.produit);
    const prixUnit = n.prixUnit!=="" ? parseFloat(n.prixUnit) : toFiniteNumber(prod?.prix,500);
    if (!(prixUnit>0)) return showToast("Prix invalide",false);
    setEditFac(f=>({...f,lignes:[...(f.lignes||[]),{designation:n.produit,qte,prixUnit,montant:qte*prixUnit}]}));
    setEditNewLigne({produit:"",qte:"",prixUnit:""});
  };
  const ajouterLigne = () => {
    if (!newLigne.produit||!newLigne.qte) return;
    const qte=parseInt(newLigne.qte), prixUnit=newLigne.prixUnit?parseInt(newLigne.prixUnit):(produits.find(p=>p.nom===newLigne.produit)?.prix||500);
    setFacForm(f=>({...f,lignes:[...f.lignes,{designation:newLigne.produit,qte,prixUnit,montant:qte*prixUnit}]}));
    setNewLigne({produit:"",qte:"",prixUnit:""});
  };

  // Correctif du 30/09/2026 : disabled={loading} ne protège pas contre un double-clic ou un
  // double-tap très rapproché (fréquent en usage mobile terrain) — le second clic peut partir
  // avant que React n'ait eu le temps de rafraîchir l'attribut "disabled" du bouton à l'écran,
  // créant deux factures pour la même vente. Cette référence, elle, est vérifiée et posée de
  // façon strictement synchrone dès le tout premier clic, sans attendre un nouveau rendu.
  const creerFactureLockRef = useRef(false);
  const creerFacture = async () => {
    if (creerFactureLockRef.current) return;
    if (!facForm.clientNom||facForm.lignes.length===0) return showToast("Client et lignes requis",false);
    if (!facForm.ville) return showToast("Choisis la ville de facturation (détermine le stock déduit)",false);
    if (facForm.lignes.some(l=>!l.designation||!Number.isInteger(Number(l.qte))||Number(l.qte)<=0||toFiniteNumber(l.prixUnit)<=0)) return showToast("Vérifie les lignes de facture",false);
    creerFactureLockRef.current = true;
    setLoading(true);
    try {
      const num=await genFacNum();
      const ech=facForm.dateEcheance||addDaysISO(facForm.dateEmission||today(),30);
      const cli=(facForm.clientCode&&clients.find(c=>c.code===facForm.clientCode))||clients.find(c=>c.nom===facForm.clientNom);
      const { error }=await supabase.rpc("creer_facture_transaction",{
        p_numero:num,p_client_nom:facForm.clientNom,p_client_code:cli?.code||"",
        p_date_emission:facForm.dateEmission||today(),p_date_echeance:ech,p_statut:"En attente",
        p_remise_pct:toFiniteNumber(facForm.remisePct),p_appliquer_tva:facForm.appliquerTVA!==false,
        p_tva_taux:toFiniteNumber(entreprise.tva_taux,19.25),p_note:facForm.note||"",p_reglement:facForm.reglement||"",p_employe:session.nom,p_lignes:facForm.lignes,
        p_ville:facForm.ville
      });
      if(error) throw new Error(error.message);
      await load();
      setFacForm({clientNom:"",clientCode:"",dateEmission:today(),dateEcheance:addDaysISO(today(),30),note:"",statut:"En attente",lignes:[],remisePct:0,appliquerTVA:false,reglement:"",ville:isDirecteur()?"":(session.ville||"")});
      showToast("Facture "+num+" créée ✓"); setPage("factures");
    } catch(e) { showToast("Facture non créée : "+e.message,false); }
    finally { setLoading(false); creerFactureLockRef.current = false; }
  };


  const updateFacture = async () => {
    if (!editFac) return;
    if ((editFac.lignes||[]).some(l=>!l.designation||!Number.isInteger(Number(l.qte))||Number(l.qte)<=0||toFiniteNumber(l.prixUnit)<=0)) return showToast("Vérifie les lignes de facture",false);
    if (!(editFac.lignes||[]).length) return showToast("La facture doit garder au moins une ligne",false);
    {
      const ancienne = factures.find(x=>String(x.id)===String(editFac.id));
      const dejaPaye = ancienne ? totalPayeValide(ancienne) : 0;
      const ttcNouveau = calculateInvoiceTotals(editFac,entreprise).ttc;
      const ttcAncien = ancienne ? calculateInvoiceTotals(ancienne,entreprise).ttc : ttcNouveau;
      if (dejaPaye>0 && ttcNouveau!==ttcAncien) {
        const suite = ttcNouveau>dejaPaye ? "Reste à payer : "+FCFA(ttcNouveau-dejaPaye) : "Le client aura payé "+FCFA(dejaPaye-ttcNouveau)+" de trop.";
        if (!window.confirm("Cette facture a déjà "+FCFA(dejaPaye)+" de paiements validés.\n\nTotal avant : "+FCFA(ttcAncien)+"\nNouveau total : "+FCFA(ttcNouveau)+"\n"+suite+"\n\nConfirmer la modification ?")) return;
      }
    }
    setLoading(true);
    const { error }=await supabase.rpc("modifier_facture_transaction",{
      p_facture_id:String(editFac.id),p_client_nom:editFac.client_nom,
      p_date_emission:editFac.date_emission,p_date_echeance:editFac.date_echeance,
      p_remise_pct:toFiniteNumber(editFac.remise_pct),p_appliquer_tva:editFac.appliquer_tva!==false,
      p_tva_taux:toFiniteNumber(editFac.tva_taux,toFiniteNumber(entreprise.tva_taux,19.25)),p_note:editFac.note||"",p_reglement:editFac.reglement||"",p_employe:session.nom,p_lignes:editFac.lignes||[]
    });
    if(error){showToast("Facture non modifiée : "+error.message,false);setLoading(false);return;}
    await load();setLoading(false);setEditFac(null);showToast("Facture mise à jour avec stock et ventes synchronisés ✓");
  };


  const deleteFacture = async (id) => {
    if (!canDeleteInvoices()) {
      return showToast("Vous n’avez pas le droit de supprimer les factures",false);
    }

    const facture = factures.find(f=>String(f.id)===String(id));
    const numero = facture?.numero || "";
    if (!window.confirm(
      "Supprimer définitivement la facture"
      +(numero ? " "+numero : "")
      +" ?\n\nLes paiements et justificatifs associés seront également supprimés. Le stock sera restauré lorsque cette facture est gérée transactionnellement."
    )) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("supprimer_facture_transaction", {
        p_facture_id:String(id)
      });
      if (error) throw new Error(error.message);

      const result = Array.isArray(data) ? data[0] : data;
      const rawPaths = result?.preuve_paths;
      const proofPaths = (
        Array.isArray(rawPaths)
          ? rawPaths
          : safeJsonParse(rawPaths, [])
      ).filter(path=>
        typeof path==="string"
        && path.trim()
        && !/^https?:\/\//i.test(path)
      );

      let storageWarning = "";
      if (proofPaths.length>0) {
        const { error:storageError } = await supabase.storage
          .from(PAYMENT_BUCKET)
          .remove(proofPaths);

        if (storageError) {
          console.error("Suppression des justificatifs Storage :", storageError);
          storageWarning = " La facture a été supprimée, mais certains fichiers du stockage doivent être retirés manuellement.";
        }
      }

      await load();
      const nbPaiements = toFiniteNumber(result?.paiements_supprimes, 0);
      const stockMessage = result?.stock_restaure
        ? ", stock restauré"
        : "";
      showToast(
        "Facture supprimée"
        +stockMessage
        +(nbPaiements>0 ? " et "+nbPaiements+" preuve(s) retirée(s)" : "")
        +" ✓"
        +storageWarning,
        !storageWarning
      );
    } catch(e) {
      showToast("Facture non supprimée : "+e.message,false);
    } finally {
      setLoading(false);
    }
  };


  /* ── Proformas ── */
  const ajouterLigneProforma = () => {
    if (!newProformaLigne.produit||!newProformaLigne.qte) return;
    const qte=parseInt(newProformaLigne.qte), prixUnit=newProformaLigne.prixUnit?parseInt(newProformaLigne.prixUnit):(produits.find(p=>p.nom===newProformaLigne.produit)?.prix||500);
    setProformaForm(f=>({...f,lignes:[...f.lignes,{designation:newProformaLigne.produit,qte,prixUnit,montant:qte*prixUnit}]}));
    setNewProformaLigne({produit:"",qte:"",prixUnit:""});
  };

  // Même correctif que creerFactureLockRef ci-dessus, pour la même raison (double-clic/double-tap).
  const creerProformaLockRef = useRef(false);
  const creerProforma = async () => {
    if (creerProformaLockRef.current) return;
    if (!proformaForm.clientNom||proformaForm.lignes.length===0) return showToast("Client et lignes requis",false);
    if (proformaForm.lignes.some(l=>toFiniteNumber(l.prixUnit)<=0)) return showToast("Vérifie les prix des lignes",false);
    creerProformaLockRef.current = true;
    setLoading(true);
    let num;
    try { num=await genProformaNum(); }
    catch(e) { showToast("Proforma non créée : "+e.message,false); setLoading(false); creerProformaLockRef.current = false; return; }
    const ech = proformaForm.dateEcheance||addDaysISO(today(),30);
    const cli = (proformaForm.clientCode&&clients.find(c=>c.code===proformaForm.clientCode)) || clients.find(c=>c.nom===proformaForm.clientNom);
    const { error } = await supabase.from("proformas").insert({ numero:num, client_nom:proformaForm.clientNom, client_code:cli?.code||"", date_emission:proformaForm.dateEmission||today(), date_echeance:ech, note:proformaForm.note, remise_pct:proformaForm.remisePct, appliquer_tva:proformaForm.appliquerTVA, tva_taux:toFiniteNumber(entreprise.tva_taux,19.25), employe:session.nom, ville:cli?.ville||sessionVille()||"", lignes:proformaForm.lignes });
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); creerProformaLockRef.current = false; return; }
    await load(); setLoading(false); creerProformaLockRef.current = false;
    setProformaForm({clientNom:"",clientCode:"",dateEmission:today(),dateEcheance:"",note:"",lignes:[],remisePct:0,appliquerTVA:false});
    showToast("Proforma "+num+" créée ✓"); setPage("proformas");
  };

  const deleteProforma = async (id) => {
    if (!window.confirm("Supprimer cette proforma ?")) return;
    setLoading(true);
    const { error } = await supabase.from("proformas").delete().eq("id", id);
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast("Proforma supprimée");
  };

  const convertirProformaEnFacture = async (pf) => {
    if (pf.convertie_en_facture_id) return showToast("Cette proforma a déjà été convertie",false);
    if (!window.confirm("Convertir cette proforma en facture définitive ?")) return;
    setLoading(true);
    try {
      const num=await genFacNum();
      const { data,error }=await supabase.rpc("convertir_proforma_transaction",{
        p_proforma_id:String(pf.id),p_numero_facture:num,p_date_emission:today(),
        p_date_echeance:addDaysISO(today(),30),p_employe:session.nom
      });
      if(error) throw new Error(error.message);
      await load();showToast("Facture "+(data?.numero||num)+" créée ✓");setPage("factures");
    } catch(e){showToast("Conversion impossible : "+e.message,false);}
    finally{setLoading(false);}
  };


  const creerLivraison = async () => {
    if (!livraisonForm.depotId||livraisonForm.lignes.length===0) return showToast("Dépôt et produits requis",false);
    const estDepannageVille = livraisonForm.origineType==="ville";
    const dep = depots.find(d=>d.id===livraisonForm.depotId);
    if (estDepannageVille) {
      if (!livraisonForm.villeOrigine) return showToast("Choisis la ville d'origine du dépannage",false);
      // Correctif du 02/10/2026 (fusion partie 2) : Dschang a maintenant son propre dépôt,
      // comme les autres villes — ce n'est plus "le magasin central" au sens du dépannage.
      // Seul le vrai magasin central (ville="Magasin central") reste exclu de ce circuit.
      if (dep?.ville===livraisonForm.villeOrigine) return showToast("La ville d'origine et la ville de destination doivent être différentes",false);
      // Vérifie la disponibilité du stock à la ville d'origine avant de créer la livraison
      for (const ligne of livraisonForm.lignes) {
        const dispo = depotStocks.find(s=>s.ville===livraisonForm.villeOrigine&&s.produit_nom===ligne.produit)?.quantite||0;
        if (dispo < parseInt(ligne.quantite)) return showToast(`Stock insuffisant à ${livraisonForm.villeOrigine} pour ${ligne.produit} (disponible : ${dispo})`,false);
      }
    }
    setLoading(true);
    let num;
    try { num=await genLivraisonNum(); }
    catch(e) { showToast("Livraison non créée : "+e.message,false); setLoading(false); return; }
    // Correctif du 30/09/2026 (audit — vague 7) : l'insertion de la livraison et le débit du
    // stock d'origine se faisaient auparavant en plusieurs étapes séparées (un insert, puis une
    // boucle d'appels RPC) — un échec à mi-parcours laissait une livraison "En transit" bien
    // réelle en base avec le stock seulement partiellement débité. Les deux étapes sont
    // désormais réunies côté serveur dans une seule transaction SQL (creer_livraison_transaction) :
    // en cas d'échec sur une ligne, tout est annulé, y compris l'insertion et les débits déjà
    // faits sur les lignes précédentes.
    const lignesPourServeur = livraisonForm.lignes.map(l=>{
      const prod=produits.find(p=>p.nom===l.produit);
      return {produit_id:String(prod?.id||""),produit:l.produit,quantite:parseInt(l.quantite)};
    });
    const { error:e0 } = await supabase.rpc("creer_livraison_transaction", {
      p_numero:num, p_depot_id:livraisonForm.depotId, p_depot_nom:dep?.nom||"", p_ville:dep?.ville||"",
      p_ville_origine: estDepannageVille ? livraisonForm.villeOrigine : null,
      p_date_depart:livraisonForm.dateDepart, p_date_arrivee:livraisonForm.dateArrivee||null,
      p_chauffeur:livraisonForm.chauffeur, p_vehicule:livraisonForm.vehicule,
      p_agence:livraisonForm.agence, p_agence_tel:livraisonForm.agenceTel, p_agence_ref:livraisonForm.agenceRef,
      p_receptionnaire_prevu:livraisonForm.receptionnairePrevu||null,
      p_notes:livraisonForm.notes, p_employe:session.nom, p_lignes:lignesPourServeur
    });
    if (e0) { showToast("Erreur : "+e0.message, false); setLoading(false); return; }
    await load(); setLoading(false);
    setLivraisonForm({depotId:"",depotNom:"",ville:"",origineType:"central",villeOrigine:"",dateDepart:today(),dateArrivee:"",chauffeur:"",vehicule:"",agence:"",agenceTel:"",agenceRef:"",receptionnairePrevu:"",notes:"",lignes:[]});
    showToast("Livraison "+num+" créée ✓"); setPage("livraisons");
  };

  // ── Groupes de clients à plusieurs points de vente (SODICAM, SANTA LUCIA…), reconnus par le nom ──
  const groupesClients = () => {
    const GEN = new Set(["boulangerie","supermarche","superette","ets","hotel","restaurant","mr","mrs","mme","mlle","pharmacie","station","bar","depot","magasin","societe","sarl","maison","chez","entreprise","groupe","boutique","epicerie","alimentation","snack","cafe","hopital","clinique","ecole","college","lycee","eglise","pressing","la","le","les","de","du","des"]);
    const ART = new Set(["la","le","les","l","de","du","des","d","et","au","aux","a","chez"]);
    const toks = c => normNomClient(c.nom).replace(/[,.'’\/()&-]/g," ").split(/\s+/).filter(Boolean);
    const T = (clients||[]).map(c=>({c,t:toks(c)})).filter(x=>x.t.length);
    const cle = (t,n)=>t.slice(0,n).join(" ");
    const groupes = {};
    T.forEach(({c,t})=>{
      for (let n=1;n<=t.length;n++) {
        if (n===1 && GEN.has(t[0])) continue;
        if (n>1 && ART.has(t[n-1])) continue;
        const k = cle(t,n);
        const nb = T.filter(o=>o.t.length>=n && cle(o.t,n)===k).length;
        if (nb>=2) { (groupes[k]=groupes[k]||[]).push({c,t}); return; }
      }
    });
    return Object.keys(groupes).map(k=>{
      const mem = groupes[k];
      let n = mem[0].t.length;
      mem.forEach(m=>{ let i=0; while(i<n && i<m.t.length && m.t[i]===mem[0].t[i]) i++; n=i; });
      const label = String(mem[0].c.nom||"").trim().split(/\s+/).slice(0,Math.max(1,n)).join(" ").toUpperCase();
      return { key:k, label, membres:mem.map(m=>m.c) };
    }).sort((a,b)=>a.label.localeCompare(b.label,"fr"));
  };
  const fichesDest = (destKey) => {
    if (!destKey) return [];
    if (destKey.startsWith("G:")) return (groupesClients().find(g=>g.key===destKey.slice(2))||{membres:[]}).membres;
    const c = clients.find(x=>String(x.id)===destKey.slice(2));
    return c ? [c] : [];
  };
  const facturesImpayees = (fiches) => {
    const cnt = {};
    (clients||[]).forEach(c=>{ const k=normNomClient(c.nom); cnt[k]=(cnt[k]||0)+1; });
    const out = [];
    fiches.forEach(c=>{
      factures.filter(f=>f.client_code ? f.client_code===c.code : (normNomClient(f.client_nom)===normNomClient(c.nom) && cnt[normNomClient(c.nom)]===1))
        .forEach(f=>{
          const reste = resteFacture(f);
          if (reste>0) { const e=endOfLocalDay(f.date_echeance); out.push({ f, c, reste, retard: !!(e && e<new Date()) }); }
        });
    });
    return out.sort((a,b)=>String(a.c.nom).localeCompare(String(b.c.nom),"fr")||String(a.f.date_emission).localeCompare(String(b.f.date_emission)));
  };
  const blocRelance = (lignes, plusieursPoints) => {
    if (!lignes.length) return "";
    const total = lignes.reduce((s,x)=>s+x.reste,0);
    if (plusieursPoints) {
      // Un groupe (SODICAM, SANTA LUCIA…) : une ligne par structure avec ce qu'elle doit encore.
      const parStructure = [];
      lignes.forEach(x=>{ let p=parStructure.find(q=>q.c.id===x.c.id); if(!p){ p={c:x.c,items:[]}; parStructure.push(p);} p.items.push(x); });
      return "Structure"+(parStructure.length>1?"s":"")+" concernée"+(parStructure.length>1?"s":"")+" :\n"
        + parStructure.map(p=>"  • "+p.c.nom+" — "+p.items.length+" facture"+(p.items.length>1?"s":"")+" ("+p.items.map(x=>x.f.numero).join(", ")+") — reste à payer : "+FCFA(p.items.reduce((s,x)=>s+x.reste,0))).join("\n")
        + "\nTotal restant dû : "+FCFA(total);
    }
    return "Facture"+(lignes.length>1?"s":"")+" concernée"+(lignes.length>1?"s":"")+" :\n"
      + lignes.map(x=>"  • N° "+x.f.numero+" — émise le "+fmtDate(x.f.date_emission)+(x.f.date_echeance?", échéance le "+fmtDate(x.f.date_echeance):"")+" — reste à payer : "+FCFA(x.reste)).join("\n")
      + "\nTotal restant dû : "+FCFA(total);
  };
  const docRelancePour = (type, clientNom, destKey, desel) => {
    const rel = type==="Lettre de relance" || type==="Mise en demeure";
    if (!rel || !destKey) return { corps:buildDocCorps(type, clientNom), objet:null };
    const lignes = facturesImpayees(fichesDest(destKey)).filter(x=>!desel[String(x.f.id)]);
    const total = lignes.reduce((s,x)=>s+x.reste,0);
    return {
      corps: buildDocCorps(type, clientNom, blocRelance(lignes, destKey.startsWith("G:")), lignes.length),
      objet: (type==="Mise en demeure"?"Mise en demeure de payer":"Relance de paiement")+(lignes.length?" — "+lignes.length+" facture"+(lignes.length>1?"s":"")+" impayée"+(lignes.length>1?"s":"")+" ("+FCFA(total)+")":"")
    };
  };
  const majRelance = (desel) => {
    setRelDesel(desel);
    setDocForm(f=>{ const r=docRelancePour(f.type,f.clientNom,f.destKey,desel); return {...f, corps:r.corps, ...(r.objet?{objet:r.objet}:{})}; });
  };
  const emailDestDoc = () => {
    const fiches = docForm.destKey ? fichesDest(docForm.destKey) : clients.filter(c=>c.nom===docForm.clientNom);
    return fiches.find(c=>c.email) || fiches[0] || null;
  };

  const buildDocCorps = (type, clientNom, blocFactures="", nbFactures=0) => {
    const multi = nbFactures>1;
    const dest = clientNom ? clientNom.trim() : "";
    const priceLines = produits.map(p=>`  • ${p.nom} — ${FCFA(p.prix)} / bouteille`).join("\n");
    const templates = {
      "Lettre commerciale":
`Madame, Monsieur,

C'est avec un grand plaisir que nous prenons contact avec ${dest ? "votre établissement, "+dest+"," : "vous"} au nom de Bralie Branding Food SARL, entreprise camerounaise basée à Dschang, spécialisée dans la fabrication artisanale de thés glacés 100 % naturels commercialisés sous la marque Azongbio.

Notre gamme est aujourd'hui composée de trois saveurs authentiques, élaborées à partir d'ingrédients soigneusement sélectionnés dans le terroir de l'Ouest Cameroun : le Thé noir Gingembre-Citron, reconnu pour son caractère vif et rafraîchissant, le Thé Vert Citron, apprécié pour sa légèreté et ses notes acidulées, ainsi que le Thé noir Menthe, prisé pour sa fraîcheur intense. Chaque bouteille est élaborée selon un procédé garantissant à la fois la sécurité alimentaire et la préservation des qualités gustatives naturelles du produit, sans colorants ni arômes artificiels ajoutés.

Conscients de l'importance d'un réseau de distribution fiable et de proximité pour la réussite de notre marque, nous sommes activement à la recherche de partenaires commerciaux partageant nos valeurs de qualité, d'authenticité et de satisfaction client. Nous serions donc particulièrement honorés d'envisager avec ${dest || "votre structure"} la mise en place d'une collaboration durable, que ce soit sous forme de distribution, de revente en l'état, ou d'inclusion dans votre offre actuelle de boissons.

Nous restons naturellement à votre entière disposition pour vous fournir tout complément d'information utile, organiser une dégustation de nos produits sur rendez-vous, ou discuter ensemble des modalités pratiques d'un éventuel partenariat commercial.

Dans l'attente du plaisir de vous rencontrer ou de vous lire, nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées et de notre considération la plus respectueuse.`,

      "Offre de prix":
`Madame, Monsieur,

Nous vous remercions vivement de l'intérêt que vous portez aux produits Azongbio et avons le plaisir de vous transmettre, ci-après, notre offre de prix détaillée${dest ? " à l'attention de "+dest : ""}.

Notre gamme actuelle et nos tarifs en vigueur se présentent comme suit (prix unitaires, hors frais de livraison éventuels) :

${priceLines || "  • (aucun produit enregistré pour le moment)"}

Ces tarifs s'entendent pour toute commande standard et pourront faire l'objet d'un ajustement préférentiel pour les commandes en volume important, sur simple demande de votre part.

Nos conditions générales de vente sont les suivantes :
  • Paiement : à réception de facture, sauf accord particulier (échéance à 30 jours possible pour nos partenaires réguliers)
  • Livraison : au départ de Dschang, ou depuis le dépôt régional le plus proche de votre localité
  • Validité de la présente offre : 30 jours à compter de sa date d'émission
  • Emballage : bouteilles consignées ou vendues, selon accord entre les parties

Nous restons à votre entière disposition pour toute précision complémentaire ou pour ajuster cette offre à vos besoins spécifiques, qu'il s'agisse des quantités, de la fréquence de livraison ou du conditionnement souhaité.

En vous remerciant par avance de la confiance que vous voudrez bien nous accorder, nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées.`,

      "Bon de commande":
`Nous soussignés, Bralie Branding Food SARL, société immatriculée sous le numéro ${entreprise.entreprise_rc||"[à compléter]"}, sise ${entreprise.entreprise_adresse}, ${entreprise.entreprise_ville}, passons par la présente commande ferme${dest ? " auprès de "+dest : ""} pour les articles détaillés ci-dessous :

Désignation | Quantité | Prix unitaire | Total
........................................................................

La présente commande est soumise aux conditions suivantes :
  • Conditions de livraison : à préciser d'un commun accord entre les parties
  • Date de livraison souhaitée : à préciser
  • Mode de paiement : à préciser (virement bancaire, mobile money, espèces)
  • Toute modification de quantité, de prix ou de délai devra faire l'objet d'un accord écrit préalable entre les deux parties

Nous vous remercions de bien vouloir nous confirmer la bonne réception de la présente commande, ainsi que le délai de livraison prévisionnel envisagé.

Veuillez agréer, Madame, Monsieur, l'expression de nos salutations distinguées.`,

      "Mise en demeure":
`Madame, Monsieur,

Sauf erreur ou omission de notre part, nous constatons à ce jour que${dest ? " votre établissement, "+dest+"," : ""} n'a pas procédé au règlement de la ou des factures émises à son attention par Bralie Branding Food SARL, et ce malgré les relances amiables qui ont pu vous être adressées à ce sujet.

Nous nous permettons de vous rappeler que le non-règlement d'une facture dans les délais convenus constitue un manquement aux obligations contractuelles qui nous lient, et peut exposer votre structure à des pénalités de retard ainsi qu'à des frais de recouvrement, conformément aux dispositions en vigueur.

Par la présente, nous vous mettons formellement en demeure de procéder au règlement intégral des sommes dues dans un délai de huit (8) jours à compter de la réception de ce courrier.

À défaut de régularisation dans le délai imparti, nous nous verrons contraints, à notre grand regret, d'engager toute procédure de recouvrement amiable ou judiciaire que nous jugerions utile pour recouvrer notre créance, sans préjudice des intérêts de retard et frais annexes qui pourraient s'y ajouter.

Nous restons naturellement disposés à examiner avec vous toute solution amiable permettant de régulariser rapidement cette situation, et vous invitons à nous contacter dans les meilleurs délais à cet effet.

Veuillez agréer, Madame, Monsieur, l'expression de nos salutations distinguées.`,

      "Lettre de relance":
`Madame, Monsieur,

Sauf erreur ou paiement récent qui aurait croisé notre courrier, nous constatons que le règlement ${multi ? (dest ? "des factures concernant "+dest : "de vos factures") : (dest ? "de la facture concernant "+dest : "de votre facture")}, arrivée${multi?"s":""} à échéance, ne nous est pas encore parvenu à ce jour.

Il s'agit très certainement d'un simple oubli de votre part, aussi nous permettons-nous de vous adresser ce rappel amical afin de régulariser cette situation dans les meilleurs délais.

Si le règlement a déjà été effectué entre-temps, nous vous prions de bien vouloir ne pas tenir compte de la présente relance, et vous remercions par avance de nous transmettre une copie du justificatif de paiement correspondant, pour la bonne tenue de nos comptes.

Dans le cas contraire, nous vous serions reconnaissants de bien vouloir procéder au règlement de votre solde dans les plus brefs délais, ou de revenir vers nous afin de convenir ensemble d'un échéancier adapté si votre situation le nécessite.

Nous vous remercions par avance de votre compréhension et de votre réactivité, et restons à votre entière disposition pour tout complément d'information.

Veuillez agréer, Madame, Monsieur, l'expression de nos salutations les meilleures.`
    };
    const texte = templates[type] || "";
    if (blocFactures && (type==="Lettre de relance"||type==="Mise en demeure")) {
      const parts = texte.split("\n\n");
      if (parts.length>2) { parts.splice(2,0,blocFactures); return parts.join("\n\n"); }
      return texte+"\n\n"+blocFactures;
    }
    return texte;
  };

  // Adresse d'envoi unique pour tous les collaborateurs. Auparavant, cette fonction
  // pouvait renvoyer l'email personnel de l'employé connecté (s'il se terminait par
  // @braliebranding.com) ou "contact@braliebranding.com" — des adresses qui ne sont
  // pas déclarées comme expéditeurs vérifiés dans Brevo, ce qui aurait fait échouer
  // l'envoi pour tout le monde sauf celui qui utilise exactement facture@ ou direction@.
  // On fixe donc une seule adresse, celle réellement vérifiée, pour que l'envoi
  // fonctionne de façon fiable quel que soit le collaborateur connecté.
  const getSenderAddress = () => "facture@braliebranding.com";

  const loadChatMessages = async () => {
    if (!session) return;
    let query = supabase.from("messages").select("*").order("created_at",{ascending:true}).limit(300);
    if (activeChatThread===null) {
      query = query.is("destinataire_matricule", null);
    } else {
      query = query.or(
        `and(sender_matricule.eq.${session.matricule},destinataire_matricule.eq.${activeChatThread}),and(sender_matricule.eq.${activeChatThread},destinataire_matricule.eq.${session.matricule})`
      );
    }
    const { data, error } = await query;
    if (!error && data) setChatMessages(data);
    // Accusé de lecture (comme WhatsApp) : à quelle heure l'autre personne a ouvert cette discussion.
    if (activeChatThread===null) setChatLuParAutre(0);
    else {
      const { data:lu } = await supabase.from("message_lectures").select("lu_jusqua")
        .eq("utilisateur_matricule",String(activeChatThread)).eq("conversation_cle",String(session.matricule)).maybeSingle();
      setChatLuParAutre(lu?.lu_jusqua ? new Date(lu.lu_jusqua).getTime() : 0);
    }
  };

  // Charge le fil choisi et le marque comme lu uniquement quand la conversation
  // est réellement ouverte. Sur mobile, la simple liste des discussions ne suffit pas.
  useEffect(()=>{
    if (page!=="chat"||!session||!can("chat")) return;
    const conversationVisible = !isMobile || mobileChatConversationOpen;
    if (!conversationVisible) return;

    let cancelled=false;
    const syncConversation = async () => {
      await loadChatMessages();
      if (!cancelled) await markConversationRead(activeChatThread);
    };
    syncConversation();
    return ()=>{ cancelled=true; };
  },[page, activeChatThread, session, isMobile, mobileChatConversationOpen]);

  // Comme WhatsApp : le fil reste positionné sur le message le plus récent.
  useEffect(()=>{
    if (page!=="chat") return;
    const timer = setTimeout(()=>{
      chatEndRef.current?.scrollIntoView({behavior:"smooth",block:"end"});
    },80);
    return ()=>clearTimeout(timer);
  },[page,activeChatThread,chatMessages.length]);

  // Messages instantanés + compteurs non lus synchronisés entre téléphone et ordinateur.
  useEffect(()=>{
    if (!session?.matricule || !can("chat")) return;

    const getMessageKey = (message) => {
      if (!message) return null;
      if (message.destinataire_matricule==null) return GENERAL_CHAT_KEY;
      if (message.destinataire_matricule===session.matricule) {
        return String(message.sender_matricule||"");
      }
      if (message.sender_matricule===session.matricule) {
        return String(message.destinataire_matricule||"");
      }
      return null;
    };

    const channel = supabase
      .channel("azongbio-chat-"+session.matricule+"-"+Date.now())
      .on(
        "postgres_changes",
        {event:"*",schema:"public",table:"messages"},
        async payload=>{
          const message = payload.new && Object.keys(payload.new).length
            ? payload.new
            : payload.old;
          const messageKey = getMessageKey(message);
          const activeKey = chatConversationKey(activeChatThread);

          await refreshUnreadCounts();
          const conversationVisible =
            page==="chat" && (!isMobile || mobileChatConversationOpen);

          if (conversationVisible) {
            await loadChatMessages();
            if (messageKey===activeKey) await markConversationRead(activeChatThread);
          }
        }
      )
      .on(
        "postgres_changes",
        {event:"*",schema:"public",table:"message_lectures"},
        payload=>{
          const row = payload.new && Object.keys(payload.new).length
            ? payload.new
            : payload.old;
          if (row?.utilisateur_matricule===session.matricule) {
            refreshUnreadCounts();
          } else if (String(row?.conversation_cle)===String(session.matricule) && page==="chat" && (!isMobile || mobileChatConversationOpen)) {
            loadChatMessages();
          }
        }
      )
      .subscribe(status=>{
        if (status==="CHANNEL_ERROR" || status==="TIMED_OUT") {
          console.warn("Chat Realtime indisponible :",status);
        }
      });

    const fallback = setInterval(()=>{
      refreshUnreadCounts();
      if (page==="chat" && (!isMobile || mobileChatConversationOpen)) {
        loadChatMessages();
      }
    },120000);

    return ()=>{
      clearInterval(fallback);
      supabase.removeChannel(channel);
    };
  },[session,page,activeChatThread,isMobile,mobileChatConversationOpen]);

  const saveBulletinPaie = async (employe, bulletin, formOverride, retItems) => {
    const bf = formOverride||bulletinForm;
    if(!employe) { showToast("Employé introuvable",false); return null; }
    if(!bf.mois) { showToast("Mois du bulletin requis",false); return null; }
    setBulletinSaving(true);
    const payload={
      employe_id: employe.id,
      employe_nom: employe.nom,
      ville: employe.ville||sessionVille()||"Dschang",
      mois: String(bf.mois).slice(0,7),
      salaire_base: parseInt(bf.salaireBase||0),
      primes: parseInt(bf.primes||0),
      heures_sup: parseInt(bf.heuresSup||0),
      autres_retenues: parseInt(bf.autresRetenues||0),
      autres_retenues_libelle: bf.autresRetenuesLibelle||"",
      brut: bulletin.brut, cnps: bulletin.cnps, cfc: bulletin.cfc,
      irpp: bulletin.irpp, cac: bulletin.cac,
      calculer_taxes: bf.calculerTaxes !== false,
      total_retenues: bulletin.totalRetenues, net: bulletin.net
    };
    // Réutilise le bulletin existant pour le même employé/mois au lieu d'en créer un second.
    const {data:existing,error:findError}=await supabase.from("bulletins_paie")
      .select("id").eq("employe_id",employe.id).eq("mois",payload.mois)
      .order("created_at",{ascending:false}).limit(1).maybeSingle();
    if(findError){ setBulletinSaving(false); showToast("Recherche du bulletin impossible : "+findError.message,false); return null; }
    const query=existing?.id
      ? supabase.from("bulletins_paie").update(payload).eq("id",existing.id).select().single()
      : supabase.from("bulletins_paie").insert(payload).select().single();
    const {data,error}=await query;
    setBulletinSaving(false);
    if(error){ showToast("Erreur : "+error.message,false); return null; }
    if (isDirecteur() && data?.id) {
      const { error:errRet } = await supabase.rpc("versement_retenir",{ p_bulletin_id:String(data.id), p_items:Array.isArray(retItems)?retItems:[] });
      if (errRet) {
        const m = String(errRet.message||"");
        showToast("Fiche enregistrée, mais la retenue d'avance n'a pas pu être appliquée : "+(m.includes("montant_trop_eleve")?"montant supérieur au solde de l'avance":m),false);
      }
      vrCharger();
    }
    showToast(existing?.id ? "Bulletin existant mis à jour ✓" : "Bulletin de paie généré ✓");
    await load();
    return data;
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    setChatSending(true);
    const { error } = await supabase.from("messages").insert({
      sender_nom: session.nom,
      sender_matricule: session.matricule,
      destinataire_matricule: activeChatThread,
      contenu: chatInput.trim(),
    });
    if (error) { showToast("Erreur d'envoi : "+error.message, false); setChatSending(false); return; }
    setChatInput("");
    await loadChatMessages();
    setChatSending(false);
  };

  const sendDocSendModal = async () => {
    if (!docSendForm.to) return showToast("Adresse email requise",false);
    setDocSendLoading(true);
    try {
      let base64, filename;
      if (docSendModal.kind === "proforma") {
        ({ base64, filename } = await generateInvoicePdfBase64(docSendModal.data, docSendModal.client, entreprise, true));
      } else if (docSendModal.kind === "rapport") {
        ({ base64, filename } = await generateMonthlyReportPdfBase64(
          docSendModal.data.monthDate,entreprise,depenses,lots,ventes,avaries,livraisons,
          factures,paiements,Boolean(docSendModal.data.includeCreances),immobilisations
        ));
      } else if (docSendModal.kind === "rapport_periode") {
        ({ base64, filename } = await generateCustomPeriodReportPdfBase64(
          docSendModal.data.debut,docSendModal.data.fin,entreprise,depenses,lots,ventes,avaries,
          livraisons,factures,paiements,Boolean(docSendModal.data.includeCreances),immobilisations
        ));
      } else if (docSendModal.kind === "creances") {
        ({ base64, filename } = await generateCreancesPdfBase64(entreprise, clients, factures, paiements));
      } else if (docSendModal.kind === "bulletin") {
        ({ base64, filename } = await generateBulletinPaiePdfBase64(docSendModal.data, docSendModal.client, entreprise));
      } else if (docSendModal.kind === "bon_commande") {
        ({ base64, filename } = await bcPdfSigne(docSendModal.data));
      } else {
        ({ base64, filename } = await generateDocumentPdfBase64(docSendModal.data, entreprise, session));
      }
      const fromAddress = getSenderAddress();
      const htmlBody = `<div style="font-family:Georgia,serif;color:#1a1a1a">
        <p>Bonjour ${docSendForm.toName||""},</p>
        <p>${(docSendForm.message||"Veuillez trouver ci-joint le document demandé.").replace(/\n/g,"<br/>")}</p>
        <p>Cordialement,<br/>${session.nom}<br/>${entreprise.entreprise_nom}</p>
      </div>`;
      const { data: { session: authSession1 } } = await supabase.auth.getSession();
      const res = await fetch("/api/send-invoice", {
        method:"POST",
        headers:{"Content-Type":"application/json", ...(authSession1?.access_token?{"Authorization":"Bearer "+authSession1.access_token}:{})},
        body: JSON.stringify({
          to: docSendForm.to,
          toName: docSendForm.toName,
          subject: docSendForm.subject,
          htmlBody,
          textBody: docSendForm.message||"Veuillez trouver ci-joint le document demandé.",
          attachmentBase64: base64,
          attachmentName: filename,
          fromAddress,
          fromName: session.nom,
        })
      });
      const data = await res.json().catch(()=>({}));
      setDocSendLoading(false);
      if (res.ok && data.ok) { showToast("Email envoyé à "+docSendForm.to+" ✓"); if (docSendModal.kind==="bon_commande") bcMarquerEnvoye(docSendModal.data,"email"); setDocSendModal(null); }
      else showToast("Erreur envoi ("+res.status+") : "+JSON.stringify(data.error||"inconnue"),false);
    } catch(e) {
      setDocSendLoading(false);
      showToast("Erreur : "+e.message,false);
    }
  };

  const printLotRecap = async () => {
    if (!lotPrint) return;
    try {
      const doc = new jsPDF({unit:"mm",format:"a4"});
      const logoAsset = await getPdfLogoAsset();
      let y = drawPdfLetterhead(doc, entreprise, {
        title:"FICHE DE LOT",
        reference:lotPrint.numero || "",
        subtitle:fmtDate(lotPrint.date)
      }, logoAsset);

      y = drawPdfSectionTitle(doc, "Informations de production", y+2);
      const rows = [
        ["Produit", lotPrint.produit],
        ["Date de production", fmtDate(lotPrint.date)],
        ["Quantité", pdfNUM(lotPrint.quantite)+" bouteilles"],
        ["Opérateur", lotPrint.operateur || "—"],
        ["Statut", lotPrint.statut || "—"],
        ["Notes", lotPrint.notes || "—"]
      ];

      const lotCols=[
        {label:"INFORMATION",width:56},
        {label:"VALEUR",width:126}
      ];
      y=drawPdfTableHeader(doc,y,lotCols);
      rows.forEach(([label,value])=>{
        const cells=[
          {text:String(label).toUpperCase(),fontSize:7.3,bold:true,color:PDF_BRAND.muted},
          {text:String(value??"—"),fontSize:9}
        ];
        const rowH=getPdfTableRowHeight(doc,lotCols,cells,{minHeight:10});
        y=drawPdfTableRow(doc,y,lotCols,cells,{height:rowH,minHeight:10});
      });

      addPdfPageFooters(doc, entreprise, "Fiche de lot");
      const datauri = doc.output("datauristring");
      await deliverPdfOnDevice({
        base64:datauri.split(",").pop(),
        filename:"Fiche_lot_"+(lotPrint.numero||today())+".pdf",
        title:"Fiche de lot "+(lotPrint.numero||""),
        direct:true,
        onOpenInApp:(url,filename,title)=>setPdfPreview({url,filename,title})
      });
      setLotPrint(null);
    } catch(error) {
      showToast("Erreur PDF : "+(error?.message||"inconnue"), false);
    }
  };

  const printEtiquettes = async () => {
    if (!lotEtiquette) return;
    try {
      const doc = new jsPDF({unit:"mm",format:"a4"});
      const n = Math.max(1, parseInt(nbEtiquettes)||1);
      const cols = 3;
      const labelW = 58;
      const labelH = 38;
      const gapX = 6;
      const gapY = 6;
      const startX = 12;
      const startY = 12;

      for (let i=0; i<n; i++) {
        const pageIndex = Math.floor(i/21);
        const localIndex = i%21;
        if (pageIndex>0 && localIndex===0) doc.addPage();

        const col = localIndex%cols;
        const row = Math.floor(localIndex/cols);
        const x = startX + col*(labelW+gapX);
        const y = startY + row*(labelH+gapY);

        doc.setDrawColor(...PDF_BRAND.line);
        doc.setLineWidth(0.35);
        doc.roundedRect(x,y,labelW,labelH,1.5,1.5,"S");
        doc.setDrawColor(...PDF_BRAND.accent);
        doc.setLineWidth(0.8);
        doc.line(x+4,y+4,x+labelW-4,y+4);

        doc.setTextColor(...PDF_BRAND.dark);
        doc.setFont("helvetica","bold");
        doc.setFontSize(9);
        doc.text("AZONGBIO",x+labelW/2,y+9,{align:"center"});

        doc.setTextColor(...PDF_BRAND.ink);
        doc.setFontSize(10);
        doc.text(
          doc.splitTextToSize(pdfSafe(lotEtiquette.produit||""),labelW-8),
          x+labelW/2,y+15,{align:"center",maxWidth:labelW-8}
        );

        doc.setFont("helvetica","normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...PDF_BRAND.muted);
        doc.text("Lot : "+pdfSafe(lotEtiquette.numero||""),x+labelW/2,y+27,{align:"center"});
        doc.text("Produit le "+pdfSafe(fmtDate(lotEtiquette.date)),x+labelW/2,y+32,{align:"center"});
      }

      const datauri = doc.output("datauristring");
      await deliverPdfOnDevice({
        base64:datauri.split(",").pop(),
        filename:"Etiquettes_"+(lotEtiquette.numero||today())+".pdf",
        title:"Étiquettes "+(lotEtiquette.numero||""),
        direct:true,
        onOpenInApp:(url,filename,title)=>setPdfPreview({url,filename,title})
      });
      setLotEtiquette(null);
    } catch(error) {
      showToast("Erreur PDF : "+(error?.message||"inconnue"), false);
    }
  };

  // ── Rapport opérationnel professionnel, sans créanciers par défaut ──
  const generatePeriodReportPdfBase64 = async ({
    y0,
    y1,
    periodLabel,
    entreprise,
    depensesList=[],
    lotsList=[],
    ventesList=[],
    avariesList=[],
    livraisonsList=[],
    facturesList=[],
    paiementsList=[],
    immobilisationsList=[],
    includeCreances=false
  }) => {
    const doc=new jsPDF({unit:"mm",format:"a4"});
    const logoAsset=await getPdfLogoAsset();
    const headerOptions={
      title:"RAPPORT OPÉRATIONNEL",
      reference:periodLabel,
      subtitle:"Production · Ventes · Dépenses · Avaries · Villes · Collaborateurs"
    };
    const newPage=()=>{
      doc.addPage();
      return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
    };
    let y=drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
    const {ink,muted,pale,line}=PDF_BRAND;

    const inRange=value=>{
      const d=parseDateSafe(value);
      return d&&d>=y0&&d<=y1;
    };
    const monthKey=value=>{
      const d=parseDateSafe(value);
      return d?`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`:"";
    };
    const monthLabel=key=>{
      const [yy,mm]=key.split("-").map(Number);
      const raw=new Date(yy,mm-1,1).toLocaleDateString("fr-FR",{month:"short",year:"numeric"});
      return raw.charAt(0).toUpperCase()+raw.slice(1).replace(".","");
    };
    const ensureSpace=(needed=18,sectionTitle="")=>{
      if(y+needed>275){
        y=newPage();
        if(sectionTitle)y=drawPdfSectionTitle(doc,sectionTitle,y);
      }
    };
    const ensureTableSpace=(rowH,sectionTitle,columns)=>{
      if(y+rowH>275){
        y=newPage();
        if(sectionTitle)y=drawPdfSectionTitle(doc,sectionTitle,y);
        y=drawPdfTableHeader(doc,y,columns);
      }
    };
    const writeEmpty=message=>{
      doc.setTextColor(...muted);
      doc.setFont("helvetica","normal");
      doc.setFontSize(8);
      doc.text(message,17,y);
      y+=9;
    };

    const lotsPeriode=lotsList.filter(l=>inRange(l.date));
    const ventesPeriode=ventesList.filter(v=>inRange(v.date));
    const depensesPeriode=depensesList.filter(d=>inRange(d.date));
    const avariesPeriode=avariesList.filter(a=>inRange(a.date));
    const livraisonsPeriode=livraisonsList.filter(l=>inRange(l.date_reception||l.date));

    const totalProduit=lotsPeriode.reduce((s,l)=>s+toFiniteNumber(l.quantite),0);
    const totalVendu=ventesPeriode.reduce((s,v)=>s+toFiniteNumber(v.qte),0);
    const chiffreAffaires=ventesPeriode.reduce((s,v)=>s+toFiniteNumber(v.montant),0);
    const totalDepenses=depensesPeriode.reduce((s,d)=>s+toFiniteNumber(d.montant),0);
    const totalAvaries=avariesPeriode.reduce((s,a)=>s+toFiniteNumber(a.quantite),0);
    const rendementNet=Math.max(0,totalProduit-totalAvaries);
    const tauxAvarie=totalProduit?totalAvaries/totalProduit*100:0;
    // Amortissement : jamais inclus dans les dépenses — étalé sur la durée de vie de
    // chaque immobilisation, calculé mois par mois sur les mois recouverts par la période.
    const amortissementsPeriode=(immobilisationsList||[])
      .map(item=>({item,montant:amortissementSurPeriode(item,y0,y1)}))
      .filter(x=>x.montant>0.5);
    const totalAmortissement=amortissementsPeriode.reduce((s,x)=>s+x.montant,0);

    // Synthèse exécutive
    y=drawPdfSectionTitle(doc,"Synthèse exécutive",y);
    const cards=[
      ["Bouteilles produites",pdfNUM(totalProduit)+" btl"],
      ["Bouteilles vendues",pdfNUM(totalVendu)+" btl"],
      ["Avaries déclarées",pdfNUM(totalAvaries)+" btl"],
      ["Dépenses enregistrées",pdfFCFA(totalDepenses)],
      ["Chiffre d'affaires ventes",pdfFCFA(chiffreAffaires)],
      ["Amortissement immobilisations",pdfFCFA(totalAmortissement)],
      ["Production nette après avaries",pdfNUM(rendementNet)+" btl"]
    ];
    cards.forEach(([label,value],idx)=>{
      const col=idx%2,row=Math.floor(idx/2);
      const x=14+col*92,yy=y+row*23;
      doc.setFillColor(...pale);
      doc.roundedRect(x,yy,88,18,2,2,"F");
      doc.setTextColor(...muted);
      doc.setFont("helvetica","bold");
      doc.setFontSize(6.5);
      doc.text(label.toUpperCase(),x+5,yy+5.5,{maxWidth:78});
      doc.setTextColor(...ink);
      doc.setFontSize(10);
      doc.text(value,x+5,yy+13,{maxWidth:78});
    });
    y+=Math.ceil(cards.length/2)*23+7;
    doc.setTextColor(...muted);
    doc.setFont("helvetica","normal");
    doc.setFontSize(7.5);
    doc.text(
      `Taux d'avarie : ${tauxAvarie.toFixed(2).replace(".",",")} % · Lots produits : ${lotsPeriode.length} · Ventes enregistrées : ${ventesPeriode.length}`,
      17,y
    );
    y+=10;

    // Évolution mensuelle
    ensureSpace(28);
    y=drawPdfSectionTitle(doc,"Indicateurs mois par mois",y);
    const monthKeys=[];
    const cursor=new Date(y0.getFullYear(),y0.getMonth(),1);
    const last=new Date(y1.getFullYear(),y1.getMonth(),1);
    while(cursor<=last){
      monthKeys.push(`${cursor.getFullYear()}-${String(cursor.getMonth()+1).padStart(2,"0")}`);
      cursor.setMonth(cursor.getMonth()+1);
    }
    const monthRows=monthKeys.map(key=>({
      label:monthLabel(key),
      produit:lotsPeriode.filter(l=>monthKey(l.date)===key).reduce((s,l)=>s+toFiniteNumber(l.quantite),0),
      vendu:ventesPeriode.filter(v=>monthKey(v.date)===key).reduce((s,v)=>s+toFiniteNumber(v.qte),0),
      avarie:avariesPeriode.filter(a=>monthKey(a.date)===key).reduce((s,a)=>s+toFiniteNumber(a.quantite),0),
      depense:depensesPeriode.filter(d=>monthKey(d.date)===key).reduce((s,d)=>s+toFiniteNumber(d.montant),0)
    }));
    const monthCols=[
      {label:"MOIS",width:55},
      {label:"PRODUIT",width:31,align:"right"},
      {label:"VENDU",width:31,align:"right"},
      {label:"AVARIES",width:31,align:"right"},
      {label:"DÉPENSES",width:34,align:"right"}
    ];
    y=drawPdfTableHeader(doc,y,monthCols);
    monthRows.forEach(row=>{
      const cells=[
        {text:row.label,fontSize:7.3},
        {text:pdfNUM(row.produit),align:"right",fontSize:7.3},
        {text:pdfNUM(row.vendu),align:"right",fontSize:7.3},
        {text:pdfNUM(row.avarie),align:"right",fontSize:7.3},
        {text:pdfFCFA(row.depense),align:"right",fontSize:7.3,bold:true}
      ];
      const rowH=getPdfTableRowHeight(doc,monthCols,cells,{minHeight:8.5});
      ensureTableSpace(rowH,"Indicateurs mois par mois — suite",monthCols);
      y=drawPdfTableRow(doc,y,monthCols,cells,{height:rowH,minHeight:8.5});
    });
    y+=8;

    // Statistiques ville par ville
    ensureSpace(30);
    y=drawPdfSectionTitle(doc,"Statistiques ville par ville",y);
    const cityMap={};
    const getCity=name=>String(name||"Non définie").trim()||"Non définie";
    ventesPeriode.forEach(v=>{
      const city=getCity(v.ville);
      if(!cityMap[city])cityMap[city]={vendu:0,ca:0,recu:0,avaries:0};
      cityMap[city].vendu+=toFiniteNumber(v.qte);
      cityMap[city].ca+=toFiniteNumber(v.montant);
    });
    livraisonsPeriode.forEach(l=>{
      const city=getCity(l.ville);
      if(!cityMap[city])cityMap[city]={vendu:0,ca:0,recu:0,avaries:0};
      cityMap[city].recu+=toFiniteNumber(l.quantite);
    });
    avariesPeriode.forEach(a=>{
      const city=getCity(a.ville);
      if(!cityMap[city])cityMap[city]={vendu:0,ca:0,recu:0,avaries:0};
      cityMap[city].avaries+=toFiniteNumber(a.quantite);
    });
    const cityRows=Object.entries(cityMap).sort((a,b)=>b[1].vendu-a[1].vendu);
    if(!cityRows.length)writeEmpty("Aucune donnée par ville sur cette période.");
    else{
      const cityCols=[
        {label:"VILLE",width:55},
        {label:"REÇU",width:31,align:"right"},
        {label:"VENDU",width:31,align:"right"},
        {label:"AVARIES",width:31,align:"right"},
        {label:"CA VENTES",width:34,align:"right"}
      ];
      y=drawPdfTableHeader(doc,y,cityCols);
      cityRows.forEach(([city,data])=>{
        const cells=[
          {text:city,fontSize:7.2},
          {text:pdfNUM(data.recu),align:"right",fontSize:7.2},
          {text:pdfNUM(data.vendu),align:"right",fontSize:7.2},
          {text:pdfNUM(data.avaries),align:"right",fontSize:7.2},
          {text:pdfFCFA(data.ca),align:"right",fontSize:7.2,bold:true}
        ];
        const rowH=getPdfTableRowHeight(doc,cityCols,cells,{minHeight:8.5});
        ensureTableSpace(rowH,"Statistiques ville par ville — suite",cityCols);
        y=drawPdfTableRow(doc,y,cityCols,cells,{height:rowH,minHeight:8.5});
      });
      y+=8;
    }

    // Collaborateurs
    ensureSpace(30);
    y=drawPdfSectionTitle(doc,"Performance collaborateur par collaborateur",y);
    const collabMap={};
    const ensureCollab=name=>{
      const key=String(name||"Non attribué").trim()||"Non attribué";
      if(!collabMap[key])collabMap[key]={produit:0,lots:0,vendu:0,ca:0,avaries:0};
      return collabMap[key];
    };
    lotsPeriode.forEach(l=>{
      const row=ensureCollab(l.operateur);row.produit+=toFiniteNumber(l.quantite);row.lots+=1;
    });
    ventesPeriode.forEach(v=>{
      const row=ensureCollab(v.employe);row.vendu+=toFiniteNumber(v.qte);row.ca+=toFiniteNumber(v.montant);
    });
    avariesPeriode.forEach(a=>{
      ensureCollab(a.acteur).avaries+=toFiniteNumber(a.quantite);
    });
    const collabRows=Object.entries(collabMap).sort((a,b)=>(b[1].produit+b[1].vendu)-(a[1].produit+a[1].vendu));
    if(!collabRows.length)writeEmpty("Aucune activité collaborateur sur cette période.");
    else{
      const collabCols=[
        {label:"COLLABORATEUR",width:65},
        {label:"PRODUIT",width:30,align:"right"},
        {label:"VENDU",width:28,align:"right"},
        {label:"AVARIES",width:28,align:"right"},
        {label:"CA",width:31,align:"right"}
      ];
      y=drawPdfTableHeader(doc,y,collabCols);
      collabRows.forEach(([name,data])=>{
        const cells=[
          {text:name,fontSize:7.1},
          {text:pdfNUM(data.produit),align:"right",fontSize:7.1},
          {text:pdfNUM(data.vendu),align:"right",fontSize:7.1},
          {text:pdfNUM(data.avaries),align:"right",fontSize:7.1},
          {text:pdfFCFA(data.ca),align:"right",fontSize:7.1,bold:true}
        ];
        const rowH=getPdfTableRowHeight(doc,collabCols,cells,{minHeight:8.5});
        ensureTableSpace(rowH,"Performance collaborateurs — suite",collabCols);
        y=drawPdfTableRow(doc,y,collabCols,cells,{height:rowH,minHeight:8.5});
      });
      y+=8;
    }

    // Détail dépenses
    ensureSpace(30);
    y=drawPdfSectionTitle(doc,"Détail complet des dépenses",y);
    if(!depensesPeriode.length)writeEmpty("Aucune dépense enregistrée pour cette période.");
    else{
      const depCols=[
        {label:"DATE",width:26},
        {label:"LIBELLÉ",width:58},
        {label:"CATÉGORIE",width:32},
        {label:"FOURNISSEUR",width:28},
        {label:"MONTANT",width:38,align:"right"}
      ];
      y=drawPdfTableHeader(doc,y,depCols);
      depensesPeriode.slice().sort((a,b)=>String(a.date).localeCompare(String(b.date))).forEach(d=>{
        const cells=[
          {text:fmtDate(d.date),fontSize:6.8},
          {text:d.libelle||"—",fontSize:6.8},
          {text:d.categorie||"Autre",fontSize:6.8},
          {text:d.fournisseur||"—",fontSize:6.8},
          {text:pdfFCFA(d.montant),align:"right",fontSize:6.8,bold:true}
        ];
        const rowH=getPdfTableRowHeight(doc,depCols,cells,{minHeight:8.5});
        ensureTableSpace(rowH,"Détail des dépenses — suite",depCols);
        y=drawPdfTableRow(doc,y,depCols,cells,{height:rowH,minHeight:8.5});
      });
      y+=8;
    }

    // Amortissement des immobilisations — distinct des dépenses courantes : c'est la
    // charge comptable de la période pour le matériel déjà acquis (bouteilles, machines,
    // véhicules...), étalée linéairement sur sa durée de vie plutôt que payée d'un coup.
    ensureSpace(30);
    y=drawPdfSectionTitle(doc,"Amortissement des immobilisations",y);
    if(!amortissementsPeriode.length)writeEmpty("Aucun amortissement imputable à cette période.");
    else{
      const amoCols=[
        {label:"IMMOBILISATION",width:70},
        {label:"VALEUR D'ORIGINE",width:32,align:"right"},
        {label:"DURÉE",width:18,align:"right"},
        {label:"AMORT. PÉRIODE",width:32,align:"right"},
        {label:"VNC ACTUELLE",width:30,align:"right"}
      ];
      y=drawPdfTableHeader(doc,y,amoCols);
      amortissementsPeriode.slice().sort((a,b)=>b.montant-a.montant).forEach(({item,montant})=>{
        const duree=toFiniteNumber(item.duree_amortissement)||dureeAmortissementDefaut(item.categorie);
        const cells=[
          {text:(item.designation||"—")+(item.numero_inventaire?" · "+item.numero_inventaire:""),fontSize:7},
          {text:pdfFCFA(item.valeur_acquisition),align:"right",fontSize:7},
          {text:duree+" ans",align:"right",fontSize:7},
          {text:pdfFCFA(montant),align:"right",fontSize:7,bold:true},
          {text:pdfFCFA(valeurNetteComptable(item,y1)),align:"right",fontSize:7}
        ];
        const rowH=getPdfTableRowHeight(doc,amoCols,cells,{minHeight:8.5});
        ensureTableSpace(rowH,"Amortissement des immobilisations — suite",amoCols);
        y=drawPdfTableRow(doc,y,amoCols,cells,{height:rowH,minHeight:8.5});
      });
      y+=2;
      doc.setTextColor(...ink);doc.setFont("helvetica","bold");doc.setFontSize(8.5);
      doc.text("Total amortissement de la période : "+pdfFCFA(totalAmortissement),17,y+4);
      y+=12;
    }

    // Détail avaries
    ensureSpace(30);
    y=drawPdfSectionTitle(doc,"Détail des avaries",y);
    if(!avariesPeriode.length)writeEmpty("Aucune avarie déclarée pour cette période.");
    else{
      const avCols=[
        {label:"DATE",width:26},
        {label:"PRODUIT / LOT",width:66},
        {label:"VILLE",width:30},
        {label:"RESPONSABLE",width:30},
        {label:"QTÉ",width:30,align:"right"}
      ];
      y=drawPdfTableHeader(doc,y,avCols);
      avariesPeriode.slice().sort((a,b)=>String(a.date).localeCompare(String(b.date))).forEach(a=>{
        const produitLot=`${a.produit||"—"} · ${a.lot_numero||"lot non défini"}${a.raison?"\nMotif : "+a.raison:""}`;
        const cells=[
          {text:fmtDate(a.date),fontSize:6.8},
          {text:produitLot,fontSize:6.8},
          {text:a.ville||"—",fontSize:6.8},
          {text:a.acteur||"—",fontSize:6.8},
          {text:pdfNUM(a.quantite),align:"right",fontSize:6.8,bold:true}
        ];
        const rowH=getPdfTableRowHeight(doc,avCols,cells,{minHeight:8.5});
        ensureTableSpace(rowH,"Détail des avaries — suite",avCols);
        y=drawPdfTableRow(doc,y,avCols,cells,{height:rowH,minHeight:8.5});
      });
      y+=8;
    }

    // Créanciers : jamais inclus sauf demande explicite.
    if(includeCreances){
      ensureSpace(32);
      y=drawPdfSectionTitle(doc,"Annexe facultative — Liste des créanciers",y);
      const paiementValide=p=>String(p?.statut||"").toLowerCase().startsWith("valid");
      const payePour=f=>paiementsList
        .filter(p=>paiementValide(p)&&(p.facture_id?String(p.facture_id)===String(f.id):p.facture_numero===f.numero))
        .reduce((s,p)=>s+toFiniteNumber(p.montant),0);
      const restePour=f=>(String(f?.statut||"")==="Payée") ? 0 : Math.max(0,calculateInvoiceTotals(f,entreprise).ttc-payePour(f));
      const creditorMap={};
      facturesList.filter(f=>restePour(f)>0).forEach(f=>{
        const name=f.client_nom||"Client non défini";
        if(!creditorMap[name])creditorMap[name]={nb:0,reste:0};
        creditorMap[name].nb+=1;creditorMap[name].reste+=restePour(f);
      });
      const creditorRows=Object.entries(creditorMap).sort((a,b)=>b[1].reste-a[1].reste);
      if(!creditorRows.length)writeEmpty("Aucune créance en cours.");
      else{
        const crCols=[
          {label:"CRÉANCIER",width:105},
          {label:"FACTURES",width:30,align:"right"},
          {label:"SOLDE DÛ",width:47,align:"right"}
        ];
        y=drawPdfTableHeader(doc,y,crCols);
        creditorRows.forEach(([name,data])=>{
          const cells=[
            {text:name,fontSize:7.2},
            {text:pdfNUM(data.nb),align:"right",fontSize:7.2},
            {text:pdfFCFA(data.reste),align:"right",fontSize:7.2,bold:true}
          ];
          const rowH=getPdfTableRowHeight(doc,crCols,cells,{minHeight:8.5});
          ensureTableSpace(rowH,"Annexe créanciers — suite",crCols);
          y=drawPdfTableRow(doc,y,crCols,cells,{height:rowH,minHeight:8.5});
        });
      }
    }

    addPdfPageFooters(doc,entreprise,includeCreances?"Rapport opérationnel avec annexe créanciers":"Rapport opérationnel");
    const datauri=doc.output("datauristring");
    return{
      base64:datauri.split(",").pop(),
      filename:"Rapport_operationnel_"+periodLabel.replace(/[\s\/]+/g,"_")+(includeCreances?"_avec_creanciers":"")+".pdf"
    };
  };

  const generateMonthlyReportPdfBase64=async(
    monthDate,entreprise,depensesList,lotsList,ventesList,avariesList,livraisonsList,
    facturesList,paiementsList,includeCreances=false,immobilisationsList=[]
  )=>{
    const y0=new Date(monthDate.getFullYear(),monthDate.getMonth(),1);
    const y1=new Date(monthDate.getFullYear(),monthDate.getMonth()+1,0,23,59,59);
    const raw=monthDate.toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
    const periodLabel=raw.charAt(0).toUpperCase()+raw.slice(1);
    return generatePeriodReportPdfBase64({
      y0,y1,periodLabel,entreprise,depensesList,lotsList,ventesList,avariesList,
      livraisonsList,facturesList,paiementsList,immobilisationsList,includeCreances
    });
  };

  const generateCustomPeriodReportPdfBase64=async(
    debut,fin,entreprise,depensesList,lotsList,ventesList,avariesList,livraisonsList,
    facturesList,paiementsList,includeCreances=false,immobilisationsList=[]
  )=>{
    const y0=new Date(debut+"T00:00:00");
    const y1=new Date(fin+"T23:59:59");
    const periodLabel=fmtDate(debut)+" au "+fmtDate(fin);
    return generatePeriodReportPdfBase64({
      y0,y1,periodLabel,entreprise,depensesList,lotsList,ventesList,avariesList,
      livraisonsList,facturesList,paiementsList,immobilisationsList,includeCreances
    });
  };

  // ── Liste dédiée des créances clients (factures impayées) ──
  const generateCreancesPdfBase64 = async (entreprise, clientsList, facturesList, paiementsList=[]) => {
    const doc=new jsPDF({unit:"mm",format:"a4"});
    const logoAsset=await getPdfLogoAsset();
    const headerOptions={title:"ÉTAT DES CRÉANCES",reference:fmtDate(today()),subtitle:"Situation des factures impayées"};
    const newPage=()=>{doc.addPage();return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);};
    let y=drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
    const {ink,muted,pale,dark,gold,line}=PDF_BRAND;
    const calcTTC=f=>calculateInvoiceTotals(f,entreprise).ttc;
    const paiementValide=p=>String(p?.statut||"").toLowerCase().startsWith("valid");
    const payePour=f=>paiementsList.filter(p=>paiementValide(p)&&(p.facture_id?String(p.facture_id)===String(f.id):p.facture_numero===f.numero)).reduce((sum,p)=>sum+toFiniteNumber(p.montant),0);
    const restePour=f=>(String(f?.statut||"")==="Payée") ? 0 : Math.max(0,calcTTC(f)-payePour(f));
    const parClient={};
    facturesList.filter(f=>restePour(f)>0).forEach(f=>{
      if(!parClient[f.client_nom])parClient[f.client_nom]={montant:0,nb:0,plusAncienne:f.date_echeance};
      parClient[f.client_nom].montant+=restePour(f);parClient[f.client_nom].nb+=1;
      if(parseDateSafe(f.date_echeance)<parseDateSafe(parClient[f.client_nom].plusAncienne))parClient[f.client_nom].plusAncienne=f.date_echeance;
    });
    const liste=Object.entries(parClient).sort((a,b)=>b[1].montant-a[1].montant), total=liste.reduce((sum,[,v])=>sum+v.montant,0);

    doc.setFillColor(...pale);doc.roundedRect(14,y,182,19,2,2,"F");doc.setTextColor(...muted);doc.setFont("helvetica","bold");doc.setFontSize(7);doc.text("SYNTHÈSE",19,y+6);doc.setTextColor(...ink);doc.setFontSize(10);doc.text(liste.length+" client(s) débiteur(s)",19,y+13);doc.text(pdfFCFA(total),191,y+13,{align:"right"});y+=28;
    const creanceCols=[
      {label:"CLIENT",width:78},
      {label:"FACTURES",width:25,align:"right"},
      {label:"PLUS ANCIENNE ÉCHÉANCE",width:42},
      {label:"MONTANT DÛ",width:37,align:"right"}
    ];
    y=drawPdfTableHeader(doc,y,creanceCols);
    liste.forEach(([client,info])=>{
      const cells=[
        {text:client,fontSize:8},
        {text:String(info.nb),align:"right",fontSize:8},
        {text:fmtDate(info.plusAncienne),fontSize:7.4},
        {text:pdfFCFA(info.montant),align:"right",fontSize:8,bold:true}
      ];
      const rowH=getPdfTableRowHeight(doc,creanceCols,cells,{minHeight:9});
      if(y+rowH>273){y=newPage();y=drawPdfTableHeader(doc,y,creanceCols);}
      y=drawPdfTableRow(doc,y,creanceCols,cells,{height:rowH,minHeight:9});
    });
    if(!liste.length){doc.setTextColor(...muted);doc.setFontSize(8.5);doc.text("Aucune créance en cours.",17,y+3);}
    addPdfPageFooters(doc,entreprise,"État des créances");
    const datauri=doc.output("datauristring");return{base64:datauri.split(",").pop(),filename:"Creances_clients_"+today()+".pdf"};
  };

  // ── Suivi des clients par catégorie et par ville (facturé / encaissé / reste à payer) ──
  const SUIVI_ORDRE_CATS = ["Détaillant","Grossiste","HoReCa","Institution","Autre","Non classé"];
  const calculerSuiviClients = () => {
    const catDe = nom => (clients.find(c=>c.nom===nom)?.type) || "Non classé";
    const sansAccent = t => String(t||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase().replace(/\s+/g," ").trim();
    const nomRecherche = sansAccent(suiviNom);
    const map = {};
    factures.forEach(f=>{
      const d = String(f.date_emission||"").slice(0,10);
      if (suiviDu && d < suiviDu) return;
      if (suiviAu && d > suiviAu) return;
      const ville = String(f.ville||"").trim() || "Non précisée";
      if (suiviVilles.length && !suiviVilles.includes(ville)) return;
      if (nomRecherche && !sansAccent(f.client_nom).includes(nomRecherche)) return;
      const cat = catDe(f.client_nom);
      if (suiviCats.length && !suiviCats.includes(cat)) return;
      const k = f.client_nom+"||"+ville;
      const r = map[k] || (map[k]={client:f.client_nom,ville,cat,nb:0,facture:0,paye:0,reste:0});
      const ttc = calcTTC(f), reste = resteFacture(f);
      r.nb += 1; r.facture += ttc; r.reste += reste; r.paye += Math.max(0,ttc-reste);
    });
    let lignes = Object.values(map);
    if (suiviSeulementDus) lignes = lignes.filter(r=>r.reste>0);
    const rang = c => { const i=SUIVI_ORDRE_CATS.indexOf(c); return i<0?SUIVI_ORDRE_CATS.length:i; };
    lignes.sort((a,b)=> rang(a.cat)-rang(b.cat) || a.cat.localeCompare(b.cat) || b.reste-a.reste || a.client.localeCompare(b.client));
    const groupes = [];
    lignes.forEach(r=>{
      let g = groupes[groupes.length-1];
      if (!g || g.cat!==r.cat) { g={cat:r.cat,lignes:[],nb:0,facture:0,paye:0,reste:0}; groupes.push(g); }
      g.lignes.push(r); g.nb+=r.nb; g.facture+=r.facture; g.paye+=r.paye; g.reste+=r.reste;
    });
    const total = groupes.reduce((t,g)=>({nb:t.nb+g.nb,facture:t.facture+g.facture,paye:t.paye+g.paye,reste:t.reste+g.reste,clients:t.clients+g.lignes.length}),{nb:0,facture:0,paye:0,reste:0,clients:0});
    return { groupes, total };
  };
  const libelleFiltresSuivi = () => [
    "Catégories : "+(suiviCats.length?suiviCats.join(", "):"toutes"),
    "Client : "+(suiviNom.trim()?"contient \""+suiviNom.trim()+"\"":"tous"),
    "Ville : "+(suiviVilles.length?suiviVilles.join(", "):"toutes"),
    "Période : "+((suiviDu||suiviAu)?((suiviDu?"du "+fmtDate(suiviDu)+" ":"")+(suiviAu?"au "+fmtDate(suiviAu):"")).trim():"depuis toujours"),
    suiviSeulementDus?"clients avec reste à payer uniquement":"tous les clients",
  ].join("  |  ");
  const generateSuiviClientsPdfBase64 = async (entreprise, suivi, filtres) => {
    const doc=new jsPDF({unit:"mm",format:"a4"});
    const logoAsset=await getPdfLogoAsset();
    const headerOptions={title:"SUIVI DES CLIENTS",reference:fmtDate(today()),subtitle:"Par catégorie et par ville"};
    const newPage=()=>{doc.addPage();return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);};
    let y=drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
    const {ink,muted,pale}=PDF_BRAND;
    doc.setFillColor(...pale);doc.roundedRect(14,y,182,19,2,2,"F");
    doc.setTextColor(...muted);doc.setFont("helvetica","bold");doc.setFontSize(7);doc.text("SYNTHÈSE",19,y+6);
    doc.setFont("helvetica","normal");doc.setFontSize(6.6);doc.text(doc.splitTextToSize(filtres,120),47,y+5.2);
    doc.setTextColor(...ink);doc.setFont("helvetica","bold");doc.setFontSize(9.5);
    doc.text(suivi.total.clients+" ligne(s) client",19,y+14.5);
    doc.text("Reste à payer : "+pdfFCFA(suivi.total.reste),191,y+14.5,{align:"right"});
    y+=26;
    const cols=[
      {label:"CLIENT",width:54},
      {label:"VILLE",width:25},
      {label:"FACT.",width:13,align:"right"},
      {label:"FACTURÉ",width:30,align:"right"},
      {label:"ENCAISSÉ",width:30,align:"right"},
      {label:"RESTE À PAYER",width:30,align:"right"}
    ];
    y=drawPdfTableHeader(doc,y,cols);
    const ligne=(cells,opts={})=>{
      const h=getPdfTableRowHeight(doc,cols,cells,{minHeight:8.5});
      if(y+h+(opts.keep?h*2:0)>273){y=newPage();y=drawPdfTableHeader(doc,y,cols);}
      y=drawPdfTableRow(doc,y,cols,cells,{height:h,minHeight:8.5,...(opts.fill?{fill:opts.fill}:{})});
    };
    suivi.groupes.forEach(g=>{
      ligne([{text:g.cat.toUpperCase()+"  ("+g.lignes.length+" client"+(g.lignes.length>1?"s":"")+")",bold:true,fontSize:8},{text:" "},{text:" "},{text:" "},{text:" "},{text:" "}],{fill:[236,242,239],keep:true});
      g.lignes.forEach(r=>ligne([
        {text:r.client,fontSize:7.8},{text:r.ville,fontSize:7.4},{text:String(r.nb),align:"right",fontSize:7.8},
        {text:pdfFCFA(r.facture),align:"right",fontSize:7.8},{text:pdfFCFA(r.paye),align:"right",fontSize:7.8},
        {text:pdfFCFA(r.reste),align:"right",fontSize:7.8,bold:r.reste>0}
      ]));
      ligne([{text:"Sous-total "+g.cat,bold:true,fontSize:7.8},{text:" "},{text:String(g.nb),align:"right",bold:true,fontSize:7.8},
        {text:pdfFCFA(g.facture),align:"right",bold:true,fontSize:7.8},{text:pdfFCFA(g.paye),align:"right",bold:true,fontSize:7.8},
        {text:pdfFCFA(g.reste),align:"right",bold:true,fontSize:7.8}],{fill:[247,249,248]});
    });
    if(!suivi.groupes.length){doc.setTextColor(...muted);doc.setFontSize(8.5);doc.text("Aucun client pour ces filtres.",17,y+5);y+=10;}
    else ligne([{text:"TOTAL GÉNÉRAL",bold:true,fontSize:8.4},{text:" "},{text:String(suivi.total.nb),align:"right",bold:true,fontSize:8.4},
      {text:pdfFCFA(suivi.total.facture),align:"right",bold:true,fontSize:8.4},{text:pdfFCFA(suivi.total.paye),align:"right",bold:true,fontSize:8.4},
      {text:pdfFCFA(suivi.total.reste),align:"right",bold:true,fontSize:8.4}],{fill:[226,238,233]});
    addPdfPageFooters(doc,entreprise,"Suivi des clients");
    const datauri=doc.output("datauristring");return{base64:datauri.split(",").pop(),filename:"Suivi_clients_"+today()+".pdf"};
  };

  const JOURNAL_ACTIONS = { creation:"Création", modification:"Modification", suppression:"Suppression", consultation:"Consultation", connexion:"Connexion" };
  const generateJournalPdfBase64 = async (entreprise, rows, filtres, total) => {
    const doc=new jsPDF({unit:"mm",format:"a4"});
    const logoAsset=await getPdfLogoAsset();
    const headerOptions={title:"JOURNAL D'ACTIVITÉ",reference:fmtDate(today()),subtitle:"Traces des actions des employés"};
    const newPage=()=>{doc.addPage();return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);};
    let y=drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
    const {ink,muted,pale}=PDF_BRAND;
    doc.setFillColor(...pale);doc.roundedRect(14,y,182,19,2,2,"F");
    doc.setTextColor(...muted);doc.setFont("helvetica","bold");doc.setFontSize(7);doc.text("FILTRES",19,y+6);
    doc.setFont("helvetica","normal");doc.setFontSize(6.8);doc.text(doc.splitTextToSize(filtres,150),47,y+5.2);
    doc.setTextColor(...ink);doc.setFont("helvetica","bold");doc.setFontSize(9.5);
    doc.text(total+" action(s) enregistrée(s)"+(rows.length<total?" — "+rows.length+" les plus récentes imprimées":""),19,y+14.5);
    y+=26;
    const cols=[
      {label:"DATE ET HEURE",width:27},
      {label:"EMPLOYÉ",width:26},
      {label:"ACTION",width:21},
      {label:"OBJET / RÉFÉRENCE",width:40},
      {label:"DÉTAIL",width:68}
    ];
    y=drawPdfTableHeader(doc,y,cols);
    rows.forEach(r=>{
      const dt=new Date(r.created_at).toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
      const cells=[
        {text:dt,fontSize:7},
        {text:r.employe_nom||"—",fontSize:7.4,bold:true},
        {text:JOURNAL_ACTIONS[r.action]||r.action,fontSize:7.2,bold:r.action==="suppression"},
        {text:(r.objet||"")+(r.reference?"\n"+r.reference:""),fontSize:7},
        {text:r.detail||"",fontSize:6.8}
      ];
      const h=getPdfTableRowHeight(doc,cols,cells,{minHeight:8.5});
      if(y+h>273){y=newPage();y=drawPdfTableHeader(doc,y,cols);}
      y=drawPdfTableRow(doc,y,cols,cells,{height:h,minHeight:8.5,...(r.action==="suppression"?{fill:[252,236,233]}:{})});
    });
    if(!rows.length){doc.setTextColor(...muted);doc.setFontSize(8.5);doc.text("Aucune action pour ces filtres.",17,y+5);y+=10;}
    addPdfPageFooters(doc,entreprise,"Journal d'activité");
    const datauri=doc.output("datauristring");return{base64:datauri.split(",").pop(),filename:"Journal_activite_"+today()+".pdf"};
  };

  // ── Mon activité : MON chiffre d'affaires (période choisie) et MES créances à réclamer ──
  const calculerMonActivite = () => {
    const data = monActivite || { factures:[], ventes:[] };
    const dansPeriode = d => { const x=String(d||"").slice(0,10); return !(monDu&&x<monDu) && !(monAu&&x>monAu); };
    const ca = (data.ventes||[]).filter(v=>dansPeriode(v.date)).reduce((t,v)=>t+toFiniteNumber(v.montant),0);
    const facturesPeriode = (data.factures||[]).filter(f=>dansPeriode(f.date_emission));
    const facture = facturesPeriode.reduce((t,f)=>t+calcTTC(f),0);
    const resteDe = f => (String(f?.statut||"")==="Payée") ? 0 : Math.max(0, calcTTC(f)-toFiniteNumber(f.paye));
    const encaisse = facturesPeriode.reduce((t,f)=>t+Math.max(0,calcTTC(f)-resteDe(f)),0);
    const maintenant = Date.now();
    const parClient = {};
    (data.factures||[]).forEach(f=>{
      const reste = resteDe(f);
      if (reste<=0) return;
      const fin = f.date_echeance ? endOfLocalDay(f.date_echeance) : null;
      const retard = fin ? Math.floor((maintenant-fin.getTime())/86400000) : 0;
      const c = parClient[f.client_nom] || (parClient[f.client_nom]={client:f.client_nom,reste:0,factures:[]});
      c.reste += reste;
      c.factures.push({numero:f.numero,date:f.date_emission,echeance:f.date_echeance,reste,retard:retard>0?retard:0,ville:f.ville||""});
    });
    const clientsDus = Object.values(parClient).sort((a,b)=>b.reste-a.reste);
    clientsDus.forEach(c=>c.factures.sort((a,b)=>String(a.echeance||"").localeCompare(String(b.echeance||""))));
    const creances = clientsDus.reduce((t,c)=>t+c.reste,0);
    const enRetard = clientsDus.reduce((t,c)=>t+c.factures.filter(f=>f.retard>0).reduce((u,f)=>u+f.reste,0),0);
    return { ca, facture, encaisse, creances, enRetard, clientsDus, nbFacturesDues: clientsDus.reduce((t,c)=>t+c.factures.length,0) };
  };
  const generateMonActivitePdfBase64 = async (entreprise, nomEmploye, r, periodeLibelle) => {
    const doc=new jsPDF({unit:"mm",format:"a4"});
    const logoAsset=await getPdfLogoAsset();
    const headerOptions={title:"MES CRÉANCES À RÉCLAMER",reference:fmtDate(today()),subtitle:nomEmploye};
    const newPage=()=>{doc.addPage();return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);};
    let y=drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
    const {ink,muted,pale}=PDF_BRAND;
    doc.setFillColor(...pale);doc.roundedRect(14,y,182,24,2,2,"F");
    doc.setTextColor(...muted);doc.setFont("helvetica","bold");doc.setFontSize(7);doc.text("MON CHIFFRE D'AFFAIRES",19,y+6);doc.text("PÉRIODE",19,y+17);
    doc.setTextColor(...ink);doc.setFontSize(10);doc.text(pdfFCFA(r.ca),19,y+11.5);
    doc.setFont("helvetica","normal");doc.setFontSize(8);doc.text(periodeLibelle,19,y+21.5);
    doc.setTextColor(...muted);doc.setFont("helvetica","bold");doc.setFontSize(7);doc.text("RESTE À RÉCLAMER",191,y+6,{align:"right"});doc.text("DONT EN RETARD",191,y+17,{align:"right"});
    doc.setTextColor(...ink);doc.setFontSize(10);doc.text(pdfFCFA(r.creances),191,y+11.5,{align:"right"});doc.text(pdfFCFA(r.enRetard),191,y+21.5,{align:"right"});
    y+=32;
    const cols=[
      {label:"CLIENT / FACTURE",width:62},
      {label:"DATE",width:25},
      {label:"ÉCHÉANCE",width:27},
      {label:"RETARD",width:22,align:"right"},
      {label:"RESTE À PAYER",width:46,align:"right"}
    ];
    y=drawPdfTableHeader(doc,y,cols);
    const ligne=(cells,opts={})=>{
      const h=getPdfTableRowHeight(doc,cols,cells,{minHeight:8.5});
      if(y+h+(opts.keep?h*2:0)>273){y=newPage();y=drawPdfTableHeader(doc,y,cols);}
      y=drawPdfTableRow(doc,y,cols,cells,{height:h,minHeight:8.5,...(opts.fill?{fill:opts.fill}:{})});
    };
    r.clientsDus.forEach(c=>{
      ligne([{text:c.client,bold:true,fontSize:8},{text:" "},{text:" "},{text:c.factures.length+" facture"+(c.factures.length>1?"s":""),align:"right",fontSize:7.2},{text:pdfFCFA(c.reste),align:"right",bold:true,fontSize:8}],{fill:[236,242,239],keep:true});
      c.factures.forEach(f=>ligne([
        {text:"   "+f.numero,fontSize:7.6},{text:fmtDate(f.date),fontSize:7.4},{text:f.echeance?fmtDate(f.echeance):"-",fontSize:7.4},
        {text:f.retard>0?f.retard+" j":"-",align:"right",fontSize:7.6,bold:f.retard>0},{text:pdfFCFA(f.reste),align:"right",fontSize:7.8}
      ]));
    });
    if(!r.clientsDus.length){doc.setTextColor(...muted);doc.setFontSize(8.5);doc.text("Aucune créance à réclamer. Bravo !",17,y+5);y+=10;}
    else ligne([{text:"TOTAL À RÉCLAMER",bold:true,fontSize:8.4},{text:" "},{text:" "},{text:r.nbFacturesDues+" fact.",align:"right",bold:true,fontSize:8},{text:pdfFCFA(r.creances),align:"right",bold:true,fontSize:8.6}],{fill:[226,238,233]});
    addPdfPageFooters(doc,entreprise,"Mes créances");
    const datauri=doc.output("datauristring");return{base64:datauri.split(",").pop(),filename:"Mes_creances_"+today()+".pdf"};
  };

  // ── Liste PDF des clients ──
  const generateClientsListPdfBase64 = async (entreprise, clientsList, ventesList, rattacher=(v,c)=>v.client===c.nom) => {
    const doc=new jsPDF({unit:"mm",format:"a4"});const logoAsset=await getPdfLogoAsset();const headerOptions={title:"RÉPERTOIRE CLIENTS",reference:clientsList.length+" client(s)",subtitle:"Édité le "+fmtDate(today())};const newPage=()=>{doc.addPage();return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);};let y=drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);const {ink,line}=PDF_BRAND;
    const columns=[
      {label:"CLIENT",width:66},
      {label:"CODE",width:30},
      {label:"VILLE",width:31},
      {label:"CONTACT",width:32},
      {label:"CA TOTAL",width:23,align:"right"}
    ];
    y=drawPdfTableHeader(doc,y,columns);
    clientsList.forEach(c=>{
      const ca=ventesList.filter(v=>rattacher(v,c)).reduce((sum,v)=>sum+toFiniteNumber(v.montant),0);
      const cells=[
        {text:c.nom,fontSize:7.8},
        {text:c.code||"—",fontSize:7.5},
        {text:c.ville||"—",fontSize:7.5},
        {text:c.contact||"—",fontSize:7.3},
        {text:pdfFCFA(ca),align:"right",fontSize:7.2,bold:true}
      ];
      const rowH=getPdfTableRowHeight(doc,columns,cells,{minHeight:9});
      if(y+rowH>273){y=newPage();y=drawPdfTableHeader(doc,y,columns);}
      y=drawPdfTableRow(doc,y,columns,cells,{height:rowH,minHeight:9});
    });
    addPdfPageFooters(doc,entreprise,"Répertoire clients");const datauri=doc.output("datauristring");return{base64:datauri.split(",").pop(),filename:"Liste_clients_"+today()+".pdf"};
  };

  // ── Liste PDF des employés ──
  const generateEmployeesListPdfBase64 = async (entreprise, employesList) => {
    const doc=new jsPDF({unit:"mm",format:"a4"});const logoAsset=await getPdfLogoAsset();const headerOptions={title:"REGISTRE DU PERSONNEL",reference:employesList.length+" employé(s)",subtitle:"Édité le "+fmtDate(today())};const newPage=()=>{doc.addPage();return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);};let y=drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);const {ink,line}=PDF_BRAND;
    const columns=[
      {label:"NOM",width:58},
      {label:"MATRICULE",width:31},
      {label:"POSTE",width:39},
      {label:"SERVICE",width:36},
      {label:"STATUT",width:18,align:"center"}
    ];
    y=drawPdfTableHeader(doc,y,columns);
    employesList.forEach(emp=>{
      const cells=[
        {text:emp.nom,fontSize:7.8},
        {text:emp.matricule||"—",fontSize:7.5},
        {text:emp.poste||"—",fontSize:7.5},
        {text:emp.service||"—",fontSize:7.3},
        {text:emp.actif?"Actif":"Inactif",align:"center",fontSize:7.4,bold:true}
      ];
      const rowH=getPdfTableRowHeight(doc,columns,cells,{minHeight:9});
      if(y+rowH>273){y=newPage();y=drawPdfTableHeader(doc,y,columns);}
      y=drawPdfTableRow(doc,y,columns,cells,{height:rowH,minHeight:9});
    });
    addPdfPageFooters(doc,entreprise,"Registre du personnel");const datauri=doc.output("datauristring");return{base64:datauri.split(",").pop(),filename:"Liste_employes_"+today()+".pdf"};
  };

  // ── Fiche d'accès ERP — remise à chaque nouvel employé lors de la création de son compte :
  // explique comment se connecter, comment installer l'ERP sur l'écran d'accueil du téléphone,
  // rappelle son identifiant et son mot de passe temporaire, et propose un QR code direct.
  const generateAccesErpPdfBase64 = async (employe, entreprise, credentials={}) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const { ink, muted, pale, dark, accent, line } = PDF_BRAND;
    const nomComplet = [employe?.prenom, employe?.nom].filter(Boolean).join(" ").trim() || employe?.nom || "—";
    const headerOptions = {
      title:"ACCÈS À L'ERP",
      reference: nomComplet,
      subtitle: "Édité le "+fmtDate(today())
    };
    const newPage=()=>{doc.addPage();return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);};
    let y = drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);

    // Petit utilitaire local pour du texte qui s'enroule sur plusieurs lignes, dans une
    // largeur donnée — utilisé pour tous les paragraphes explicatifs de cette fiche.
    const paragraph=(text,x,width,{fontSize=9,style="normal",color=ink,lineH=4.6}={})=>{
      doc.setFont("helvetica",style);doc.setFontSize(fontSize);doc.setTextColor(...color);
      const lines=doc.splitTextToSize(pdfSafe(text),width);
      doc.text(lines,x,y,{lineHeightFactor:1.3});
      y+=lines.length*lineH;
    };

    paragraph(
      `Bonjour ${nomComplet.split(" ")[0]||nomComplet}, voici comment accéder au système de gestion interne AZONGBIO ERP depuis votre ordinateur ou votre téléphone.`,
      14,182,{fontSize:9.5,lineH:5}
    );
    y+=4;

    y=drawPdfSectionTitle(doc,"1. Se connecter à l'ERP",y);
    paragraph(`• Ouvrez votre navigateur (Chrome, Safari...) et allez sur : ${ERP_LOGIN_URL}`,17,178,{lineH:5});
    paragraph("• Entrez votre identifiant et votre mot de passe temporaire ci-dessous.",17,178,{lineH:5});
    paragraph("• Il vous sera demandé de changer ce mot de passe dès votre première connexion — choisissez-en un que vous seul(e) connaissez.",17,178,{lineH:5});
    y+=3;

    // Encadré identifiants, bien visible.
    const boxH=30;
    if(y+boxH>270){y=newPage();}
    doc.setFillColor(...pale);doc.setDrawColor(...accent);doc.setLineWidth(0.5);
    doc.roundedRect(14,y,182,boxH,2,2,"FD");
    doc.setFont("helvetica","bold");doc.setFontSize(7.5);doc.setTextColor(...muted);
    doc.text("IDENTIFIANT",22,y+9);doc.text("MOT DE PASSE TEMPORAIRE",22,y+21);
    doc.setFontSize(13);doc.setTextColor(...dark);
    doc.text(pdfSafe(credentials.username||"—"),80,y+9.5);
    doc.text(pdfSafe(credentials.password||"—"),80,y+21.5);
    y+=boxH+10;

    y=drawPdfSectionTitle(doc,"2. Installer l'ERP sur l'écran d'accueil du téléphone",y);
    paragraph("Sur iPhone (Safari) :",17,178,{style:"bold",fontSize:8.8,lineH:5});
    paragraph(`1) Ouvrez ${ERP_LOGIN_URL} dans Safari.  2) Appuyez sur l'icône Partager (le carré avec une flèche vers le haut).  3) Choisissez « Sur l'écran d'accueil ».  4) Appuyez sur « Ajouter ».`,17,178,{fontSize:8.5,lineH:4.6});
    y+=2;
    paragraph("Sur Android (Chrome) :",17,178,{style:"bold",fontSize:8.8,lineH:5});
    paragraph(`1) Ouvrez ${ERP_LOGIN_URL} dans Chrome.  2) Appuyez sur le menu ⋮ en haut à droite.  3) Choisissez « Ajouter à l'écran d'accueil » (ou « Installer l'application »).  4) Confirmez.`,17,178,{fontSize:8.5,lineH:4.6});
    y+=2;
    paragraph("Une icône AZONGBIO apparaît alors sur l'écran d'accueil, comme une application normale.",17,178,{style:"italic",fontSize:8.3,color:muted,lineH:4.6});
    y+=6;

    // QR code, avec repli propre en texte si le réseau n'était pas disponible à la génération.
    if(y+66>275){y=newPage();}
    y=drawPdfSectionTitle(doc,"3. Ou scannez ce code pour ouvrir l'ERP directement",y);
    const qrDataUrl = await fetchQrCodeDataUrl(ERP_LOGIN_URL);
    const qrSize=46;
    if(qrDataUrl){
      doc.addImage(qrDataUrl,"PNG",17,y,qrSize,qrSize);
    } else {
      doc.setDrawColor(...line);doc.setLineWidth(0.3);doc.roundedRect(17,y,qrSize,qrSize,2,2);
      doc.setFont("helvetica","italic");doc.setFontSize(7.5);doc.setTextColor(...muted);
      doc.text("QR code indisponible —",17+qrSize/2,y+qrSize/2-2,{align:"center",maxWidth:qrSize-6});
      doc.text("utilisez l'adresse ci-contre.",17+qrSize/2,y+qrSize/2+3,{align:"center",maxWidth:qrSize-6});
    }
    doc.setFont("helvetica","normal");doc.setFontSize(9);doc.setTextColor(...ink);
    doc.text(doc.splitTextToSize(pdfSafe(ERP_LOGIN_URL),110),17+qrSize+10,y+qrSize/2-3,{lineHeightFactor:1.3});
    y+=qrSize+10;

    addPdfPageFooters(doc,entreprise,"Accès ERP");
    const datauri=doc.output("datauristring");
    return{base64:datauri.split(",").pop(),filename:"Acces_ERP_"+(employe?.matricule||nomComplet).replace(/[\s\/]+/g,"_")+".pdf"};
  };

  // ── Fiche de remise de matériel (préremplie, à faire signer par le collaborateur) ──
  const generateFicheRemisePdfBase64 = async (item, employe, entreprise, signatures={}) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const { ink, line, dark } = PDF_BRAND;
    const headerOptions = {
      title:"FICHE DE REMISE DE MATÉRIEL",
      reference: item.numero_inventaire || "",
      subtitle: "Édité le "+fmtDate(today())
    };
    let y = drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);

    // Nom complet et coordonnées du collaborateur, réunis pour être injectés dans le texte.
    const nomComplet = [employe?.prenom, employe?.nom||item.employe_nom].filter(Boolean).join(" ").trim() || item.employe_nom || "—";
    const coordonnees = [];
    if (employe?.matricule) coordonnees.push("matricule "+employe.matricule);
    if (employe?.poste) coordonnees.push("occupant le poste de "+employe.poste);
    if (employe?.ville||item.ville) coordonnees.push("en poste à "+(employe?.ville||item.ville));
    if (employe?.adresse) coordonnees.push("domicilié(e) à "+employe.adresse);
    if (employe?.tel) coordonnees.push("joignable au "+employe.tel);
    if (employe?.email) coordonnees.push("email "+employe.email);
    if (employe?.numero_cni) coordonnees.push("titulaire de la CNI n° "+employe.numero_cni);
    const coordonneesTxt = coordonnees.length ? ", "+coordonnees.join(", ")+"," : ",";

    y = drawPdfSectionTitle(doc,"Conditions de remise",y+4);
    doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.2);
    const clause =
      "Je soussigné(e) "+pdfSafe(nomComplet)+pdfSafe(coordonneesTxt)+
      " reconnais avoir reçu ce jour, "+fmtDate(today())+", de la part de "+
      pdfSafe(enterpriseValue(entreprise,"entreprise_nom","Bralie Branding Food SARL"))+
      ", le matériel suivant : "+pdfSafe(item.designation||"—")+
      (item.categorie?" ("+pdfSafe(item.categorie)+")":"")+
      (([item.marque,item.modele].filter(Boolean).join(" "))?", marque/modèle "+pdfSafe([item.marque,item.modele].filter(Boolean).join(" ")):"")+
      (item.numero_serie?", numéro de série "+pdfSafe(item.numero_serie):"")+
      (item.numero_inventaire?", référencé sous le n° d'inventaire "+pdfSafe(item.numero_inventaire):"")+
      ", en état "+pdfSafe((item.etat||"—").toLowerCase())+" à la remise. "+
      "Je m'engage à en faire un usage conforme à mon activité au sein de l'entreprise, à en assurer la garde et le bon entretien, "+
      "et à signaler sans délai toute panne, perte ou dommage. Ce matériel reste la propriété de "+
      pdfSafe(enterpriseValue(entreprise,"entreprise_nom","Bralie Branding Food SARL"))+
      " et devra être restitué en bon état sur simple demande de la Direction ou à la fin du contrat de travail.";
    const clauseLines = doc.splitTextToSize(clause,182);
    doc.text(clauseLines,14,y+3);
    y += clauseLines.length*4.6 + 14;

    doc.setDrawColor(...line); doc.setLineWidth(0.35);
    doc.text("Date de remise : "+fmtDate(today()),14,y); y+=14;

    const sigW = 84;
    [["Le collaborateur",14,"collaborateur"],["Pour "+pdfSafe(enterpriseValue(entreprise,"entreprise_nom","l'entreprise")),112,"entreprise"]].forEach(([label,x,sigKey])=>{
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(8.5);
      doc.text(pdfSafe(label),x+sigW/2,y,{align:"center",maxWidth:sigW});
      drawPdfSignatureBlock(doc,x,y+22,sigW,{dataUrl:signatures?.[sigKey]});
    });

    addPdfPageFooters(doc,entreprise,"Fiche de remise de matériel");
    const datauri = doc.output("datauristring");
    return {
      base64: datauri.split(",").pop(),
      filename: "Fiche_remise_"+(employe?.nom||item.employe_nom||"materiel").replace(/\s+/g,"_")+"_"+(item.numero_inventaire||today())+".pdf"
    };
  };

  // ── Contrat de travail (généré à partir des données saisies par la direction) ──
  const generateContratTravailPdfBase64 = async (data, employe, entreprise, signatures={}) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const { ink, muted, dark } = PDF_BRAND;
    const entrepriseNom = enterpriseValue(entreprise,"entreprise_nom","Bralie Branding Food SARL");
    const isCDD = String(data.type_contrat||"").toUpperCase()==="CDD";
    const headerOptions = {
      title:"CONTRAT DE TRAVAIL — "+String(data.type_contrat||"CDI").toUpperCase(),
      reference: employe?.matricule || "",
      subtitle: "Édité le "+fmtDate(today())
    };
    const newPage = () => { doc.addPage(); return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset); };
    let y = drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);

    const nomComplet = [employe?.prenom, employe?.nom].filter(Boolean).join(" ").trim() || employe?.nom || "—";
    const coordonnees = [];
    if (employe?.date_naissance) coordonnees.push("né(e) le "+fmtDate(employe.date_naissance)+(employe?.lieu_naissance?" à "+employe.lieu_naissance:""));
    if (employe?.nationalite) coordonnees.push("de nationalité "+employe.nationalite);
    if (employe?.numero_cni) coordonnees.push("titulaire de la CNI n° "+employe.numero_cni);
    if (employe?.adresse) coordonnees.push("domicilié(e) à "+employe.adresse);
    if (employe?.tel) coordonnees.push("joignable au "+employe.tel);
    const coordonneesTxt = coordonnees.length ? " ("+coordonnees.join(", ")+")" : "";

    const writeArticle = (titre, texte) => {
      if (y>250) y=newPage();
      y = drawPdfSectionTitle(doc,titre,y+4);
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.2);
      const lines = doc.splitTextToSize(texte,182);
      let ly = y+3;
      lines.forEach(l=>{
        if (ly>280) { y=newPage(); ly=y+3; }
        doc.text(l,14,ly); ly+=4.6;
      });
      y = ly+3;
    };

    doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.2);
    const preambule = "Entre les soussignés : "+pdfSafe(entrepriseNom)+
      (enterpriseValue(entreprise,"entreprise_adresse","")?", dont le siège est situé à "+pdfSafe(enterpriseValue(entreprise,"entreprise_adresse","")):"")+
      ", ci-après dénommée « l'Employeur », d'une part, et "+pdfSafe(nomComplet)+pdfSafe(coordonneesTxt)+
      ", ci-après dénommé(e) « le Salarié », d'autre part, il a été convenu et arrêté ce qui suit, conformément au Code du travail camerounais et à la convention collective applicable.";
    const preambuleLines = doc.splitTextToSize(preambule,182);
    doc.text(preambuleLines,14,y+3);
    y += preambuleLines.length*4.6 + 6;

    writeArticle("Article 1 — Engagement et fonctions",
      "L'Employeur engage le Salarié en qualité de "+pdfSafe(data.poste||employe?.poste||"—")+
      (data.departement||employe?.departement?", au sein du département "+pdfSafe(data.departement||employe?.departement):"")+
      ". Le Salarié exercera ses fonctions sous l'autorité de la Direction, avec sérieux, assiduité et loyauté, et se conformera aux instructions et au règlement intérieur de l'entreprise.");

    writeArticle("Article 2 — Nature et durée du contrat",
      isCDD
        ? "Le présent contrat est conclu pour une durée déterminée prenant effet le "+pdfSafe(fmtDate(data.date_debut))+
          " et arrivant à échéance le "+pdfSafe(fmtDate(data.date_fin_contrat))+
          ". Il pourra être renouvelé dans les conditions prévues par la législation en vigueur."
        : "Le présent contrat est conclu pour une durée indéterminée et prend effet le "+pdfSafe(fmtDate(data.date_debut))+
          ". Il pourra être résilié par l'une ou l'autre des parties dans le respect des dispositions légales et conventionnelles applicables.");

    if (data.periode_essai_duree) {
      writeArticle("Article 3 — Période d'essai",
        "Le présent contrat ne deviendra définitif qu'à l'issue d'une période d'essai de "+pdfSafe(data.periode_essai_duree)+
        ", au cours de laquelle chacune des parties pourra rompre librement le contrat, sans préavis ni indemnité, sous réserve du respect des délais de prévenance légaux.");
    }

    writeArticle("Article 4 — Lieu et conditions de travail",
      "Le Salarié exercera ses fonctions à "+pdfSafe(data.ville||employe?.ville||"—")+
      ". Compte tenu des nécessités de service, il pourra être amené à effectuer des missions ponctuelles dans d'autres villes ou sites de l'entreprise. La durée du travail est fixée à "+
      pdfSafe(data.horaire_hebdo||"40 heures par semaine")+", réparties selon l'organisation en vigueur dans l'entreprise.");

    const remunerationTxt = "En contrepartie de son travail, le Salarié percevra une rémunération mensuelle brute de "+
      pdfFCFA(data.salaire_base)+
      (toFiniteNumber(data.prime_fixe)>0?", à laquelle s'ajoute une prime fixe de "+pdfFCFA(data.prime_fixe)+" par mois":"")+
      ". Cette rémunération sera versée mensuellement"+(data.mode_paiement||employe?.mode_paiement?" par "+pdfSafe(data.mode_paiement||employe?.mode_paiement):"")+
      ", sous déduction des cotisations sociales et fiscales applicables.";
    writeArticle("Article 5 — Rémunération", remunerationTxt);

    writeArticle("Article 6 — Congés et avantages",
      "Le Salarié bénéficiera des congés payés et des avantages sociaux prévus par le Code du travail camerounais et, le cas échéant, par la convention collective ou les usages en vigueur au sein de l'entreprise.");

    writeArticle("Article 7 — Confidentialité et loyauté",
      "Le Salarié s'engage à observer la plus stricte discrétion sur toutes les informations, données commerciales, techniques ou financières dont il pourrait avoir connaissance dans l'exercice de ses fonctions, pendant la durée du contrat et après sa cessation.");

    writeArticle("Article 8 — Résiliation du contrat",
      "Le présent contrat pourra être résilié dans les conditions prévues par le Code du travail camerounais, notamment en cas de démission, de licenciement pour motif légitime, ou, s'agissant d'un contrat à durée déterminée, à son terme.");

    if (data.clauses_particulieres) {
      writeArticle("Article 9 — Clauses particulières", pdfSafe(data.clauses_particulieres));
    }

    if (y>245) y=newPage();
    y += 4;
    doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.2);
    doc.text("Fait à "+pdfSafe(data.lieu_signature||"Dschang")+", le "+pdfSafe(fmtDate(data.date_signature||today()))+", en deux exemplaires originaux.",14,y);
    y += 16;

    const sigW = 84;
    [["L'Employeur",14,"Pour "+pdfSafe(entrepriseNom),"employeur"],["Le Salarié",112,pdfSafe(nomComplet),"salarie"]].forEach(([label,x,sub,sigKey])=>{
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(8.5);
      doc.text(label,x+sigW/2,y,{align:"center"});
      doc.setFont("helvetica","normal"); doc.setTextColor(...muted); doc.setFontSize(7.5);
      doc.text(sub,x+sigW/2,y+5,{align:"center",maxWidth:sigW});
      drawPdfSignatureBlock(doc,x,y+24,sigW,{caption:"Signature précédée de la mention « Lu et approuvé »",dataUrl:signatures?.[sigKey]});
    });

    addPdfPageFooters(doc,entreprise,"Contrat de travail");
    const datauri = doc.output("datauristring");
    return {
      base64: datauri.split(",").pop(),
      filename: "Contrat_travail_"+(employe?.nom||"employe").replace(/\s+/g,"_")+"_"+today()+".pdf"
    };
  };

  // ── Procuration temporaire accordée par la Direction à un collaborateur, pour un
  // objet et une période précis. Même mécanique de signature à l'écran / à distance
  // que le contrat de travail et la fiche de remise (voir runSignatureFlow).
  const generateProcurationPdfBase64 = async (data, employe, entreprise, signatures={}) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const { ink, muted, dark, line, pale } = PDF_BRAND;
    const entrepriseNom = enterpriseValue(entreprise,"entreprise_nom","Bralie Branding Food SARL");
    const headerOptions = {
      title:"PROCURATION",
      reference: employe?.matricule || "",
      subtitle: "Édité le "+fmtDate(today())
    };
    const newPage = () => { doc.addPage(); return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset); };
    let y = drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);

    const nomComplet = [employe?.prenom, employe?.nom].filter(Boolean).join(" ").trim() || employe?.nom || "—";
    const nomMandant = [session?.prenom, session?.nom].filter(Boolean).join(" ").trim() || session?.nom || "—";

    // Bloc d'identité de chaque partie — repris des mêmes champs que l'onglet « Identité »
    // de la fiche RH (y compris la validité de la CNI/passeport si renseignée), affiché en
    // encadré pour que l'essentiel se lise d'un coup d'œil plutôt que noyé dans un paragraphe.
    const identityRows = person => {
      const rows = [];
      if (person?.poste) rows.push(["Qualité", person.poste]);
      if (person?.date_naissance) rows.push(["Né(e) le", fmtDate(person.date_naissance)+(person?.lieu_naissance?" à "+person.lieu_naissance:"")]);
      if (person?.nationalite) rows.push(["Nationalité", person.nationalite]);
      if (person?.numero_cni) rows.push(["N° CNI / Passeport", person.numero_cni+(person?.date_validite_cni?" — valable jusqu'au "+fmtDate(person.date_validite_cni):"")]);
      if (person?.adresse) rows.push(["Domicile", person.adresse]);
      if (person?.tel) rows.push(["Téléphone", person.tel]);
      if (person?.email) rows.push(["Email", person.email]);
      return rows;
    };
    // Dessine un encadré d'identité en DEUX passes : on mesure d'abord combien de
    // lignes chaque morceau de texte va réellement occuper une fois retourné à la
    // ligne (le nom complet ou le sous-titre peuvent facilement dépasser une seule
    // ligne dans un encadré de ~8cm de large), puis on calcule la hauteur du cadre
    // et la position de chaque ligne à partir de ces mesures. Avant ce correctif, la
    // hauteur était devinée à l'avance sans tenir compte du retour à la ligne : le
    // texte qui débordait de sa ligne prévue se superposait à la ligne suivante, et
    // le trait du cadre (dessiné trop haut) traversait le texte du dessous.
    const drawIdentityBox = (x, yTop, w, title, name, rows, subtitle) => {
      const innerW = w-8;
      const nameLineH = 4.0, subtitleLineH = 3.3, rowLineH = 4.0;

      doc.setFont("helvetica","bold"); doc.setFontSize(9.2);
      const nameLines = doc.splitTextToSize(pdfSafe(name)||"—", innerW);

      let subtitleLines = [];
      if (subtitle) {
        doc.setFont("helvetica","italic"); doc.setFontSize(6.8);
        subtitleLines = doc.splitTextToSize(pdfSafe(subtitle), innerW);
      }

      doc.setFont("helvetica","normal"); doc.setFontSize(7.3);
      const rowsMeasured = rows.map(([lbl,val])=>({ lbl, lines: doc.splitTextToSize(pdfSafe(val), w-36) }));

      const nameH = nameLines.length*nameLineH;
      const subtitleH = subtitleLines.length ? subtitleLines.length*subtitleLineH + 2 : 0;
      const rowsH = rowsMeasured.reduce((s,r)=>s+Math.max(1,r.lines.length)*rowLineH, 0);
      const boxH = 12 + nameH + subtitleH + rowsH + 4;

      doc.setDrawColor(...line); doc.setFillColor(...pale);
      doc.rect(x, yTop, w, boxH, "FD");
      doc.setFont("helvetica","bold"); doc.setTextColor(...muted); doc.setFontSize(7);
      doc.text(title.toUpperCase(), x+4, yTop+6);

      let ly = yTop+11.5;
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(9.2);
      doc.text(nameLines, x+4, ly);
      ly += nameH + 1.5;

      if (subtitleLines.length) {
        doc.setFont("helvetica","italic"); doc.setTextColor(...muted); doc.setFontSize(6.8);
        doc.text(subtitleLines, x+4, ly);
        ly += subtitleLines.length*subtitleLineH + 2;
      }

      doc.setFontSize(7.3);
      rowsMeasured.forEach(({lbl,lines})=>{
        doc.setFont("helvetica","bold"); doc.setTextColor(...muted);
        doc.text(lbl+" :", x+4, ly);
        doc.setFont("helvetica","normal"); doc.setTextColor(...ink);
        doc.text(lines, x+32, ly);
        ly += Math.max(1,lines.length)*rowLineH;
      });
      return yTop+boxH;
    };

    const boxW = 86, gapW = 10;
    // Le Mandant signe en son nom propre mais AGIT au nom de la Direction de
    // l'entreprise (et non pour son seul compte personnel) — précisé ici pour
    // éviter toute confusion quand son nom de famille ressemble à celui du
    // Mandataire (ex. lien de parenté), comme signalé par un utilisateur.
    const yAfterMandant = drawIdentityBox(14, y, boxW, "Le Mandant", nomMandant, identityRows(session), "Agissant au nom et pour le compte de la Direction de "+entrepriseNom);
    const yAfterMandataire = drawIdentityBox(14+boxW+gapW, y, boxW, "Le Mandataire", nomComplet, identityRows(employe));
    y = Math.max(yAfterMandant, yAfterMandataire) + 8;

    const writeArticle = (titre, texte) => {
      if (y>250) y=newPage();
      y = drawPdfSectionTitle(doc,titre,y+4);
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.2);
      const lines = doc.splitTextToSize(texte,182);
      let ly = y+3;
      lines.forEach(l=>{
        if (ly>280) { y=newPage(); ly=y+3; }
        doc.text(l,14,ly); ly+=4.6;
      });
      y = ly+3;
    };

    doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.2);
    const preambule = "Je soussigné(e), identifié(e) ci-dessus en qualité de « Mandant », "+pdfSafe(session?.poste||"Directeur")+" de "+pdfSafe(entrepriseNom)+
      (enterpriseValue(entreprise,"entreprise_adresse","")?", dont le siège est situé à "+pdfSafe(enterpriseValue(entreprise,"entreprise_adresse","")):"")+
      ", agissant non pas en mon nom propre mais au nom et pour le compte de la Direction de "+pdfSafe(entrepriseNom)+
      ", donne par les présentes pouvoir à la personne identifiée ci-dessus en qualité de « Mandataire », à l'effet d'agir en son nom et pour son compte dans les conditions précisées ci-après.";
    const preambuleLines = doc.splitTextToSize(preambule,182);
    doc.text(preambuleLines,14,y+3);
    y += preambuleLines.length*4.6 + 6;

    writeArticle("Article 1 — Objet de la procuration", pdfSafe(data.objet||"—"));

    writeArticle("Article 2 — Durée de validité",
      "La présente procuration prend effet le "+pdfSafe(fmtDate(data.date_debut))+
      " et expire de plein droit le "+pdfSafe(fmtDate(data.date_fin))+
      ", sans qu'il soit besoin d'un acte supplémentaire. Elle peut être révoquée à tout moment par le Mandant avant son terme, sur simple notification écrite au Mandataire.");

    writeArticle("Article 3 — Étendue et limites",
      "Le Mandataire agit strictement dans le cadre de l'objet défini à l'article 1 et ne saurait engager le Mandant au-delà de ce cadre, ni au-delà de la période de validité fixée à l'article 2.");

    if (data.clauses_particulieres) {
      writeArticle("Article 4 — Clauses particulières", pdfSafe(data.clauses_particulieres));
    }

    if (y>245) y=newPage();
    y += 4;
    doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.2);
    doc.text("Fait à "+pdfSafe(data.lieu_signature||"Dschang")+", le "+pdfSafe(fmtDate(data.date_signature||today()))+", en deux exemplaires originaux.",14,y);
    y += 16;

    const sigW = 84;
    [["Le Mandant",14,"Pour la Direction de "+pdfSafe(entrepriseNom),"mandant"],["Le Mandataire",112,pdfSafe(nomComplet),"mandataire"]].forEach(([label,x,sub,sigKey])=>{
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(8.5);
      doc.text(label,x+sigW/2,y,{align:"center"});
      doc.setFont("helvetica","normal"); doc.setTextColor(...muted); doc.setFontSize(7.5);
      doc.text(sub,x+sigW/2,y+5,{align:"center",maxWidth:sigW});
      drawPdfSignatureBlock(doc,x,y+24,sigW,{caption:"Signature précédée de la mention « Bon pour pouvoir »",dataUrl:signatures?.[sigKey]});
    });

    addPdfPageFooters(doc,entreprise,"Procuration");
    const datauri = doc.output("datauristring");
    return {
      base64: datauri.split(",").pop(),
      filename: "Procuration_"+(employe?.nom||"employe").replace(/\s+/g,"_")+"_"+today()+".pdf"
    };
  };

  // ── Bon de versement à un employé (avance, prime…) — à faire signer par le bénéficiaire et la direction ──
  const nombreEnLettresFr = (nb) => {
    const U = ["zéro","un","deux","trois","quatre","cinq","six","sept","huit","neuf","dix","onze","douze","treize","quatorze","quinze","seize","dix-sept","dix-huit","dix-neuf"];
    const D = ["","","vingt","trente","quarante","cinquante","soixante"];
    const sous100 = (n, final) => {
      if (n<20) return U[n];
      const d = Math.floor(n/10), u = n%10;
      if (d===7 || d===9) { const base = d===7 ? "soixante" : "quatre-vingt"; const r = 10+u; return base+((d===7 && u===1)?" et ":"-")+U[r]; }
      if (d===8) return u===0 ? (final?"quatre-vingts":"quatre-vingt") : "quatre-vingt-"+U[u];
      if (u===0) return D[d];
      return D[d]+(u===1?" et un":"-"+U[u]);
    };
    const sous1000 = (n, final) => {
      const c = Math.floor(n/100), r = n%100;
      let s = "";
      if (c>0) { s = (c===1?"cent":U[c]+" cent"); if (r===0 && c>1 && final) s += "s"; }
      if (r>0) s += (s?" ":"")+sous100(r, final);
      return s;
    };
    const n = Math.max(0, Math.floor(Number(nb)||0));
    if (n===0) return "zéro";
    const mil = Math.floor(n/1000000), k = Math.floor((n%1000000)/1000), r = n%1000;
    const parts = [];
    if (mil>0) parts.push(sous1000(mil,true)+" million"+(mil>1?"s":""));
    if (k>0) parts.push(k===1?"mille":sous1000(k,false)+" mille");
    if (r>0) parts.push(sous1000(r,true));
    return parts.join(" ");
  };
  const generateBonVersementPdfBase64 = async (v, employe, entreprise, signatures={}) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const { ink, muted, dark, line, pale } = PDF_BRAND;
    const entrepriseNom = enterpriseValue(entreprise,"entreprise_nom","Bralie Branding Food SARL");
    const headerOptions = { title:"BON DE VERSEMENT", reference:String(v.numero||""), subtitle:"Édité le "+fmtDate(today()) };
    const newPage = () => { doc.addPage(); return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset); };
    let y = drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
    const montant = Math.round(Number(v.montant)||0);
    const nomEmp = [employe?.prenom, employe?.nom].filter(Boolean).join(" ").trim() || v.employe_nom || employe?.nom || "—";
    const nomDir = v.direction_nom || [session?.prenom, session?.nom].filter(Boolean).join(" ").trim() || session?.nom || "—";
    const enLettres = nombreEnLettresFr(montant);
    const enLettresMaj = enLettres.charAt(0).toUpperCase()+enLettres.slice(1);

    y = drawPdfSectionTitle(doc,"Bénéficiaire",y+4); y += 3;
    const info = (label, value) => {
      doc.setFontSize(9);
      const valLines = doc.splitTextToSize(pdfSafe(value||"—"),118);
      const rowH = Math.max(valLines.length,1)*4.6 + 1;
      if (y+rowH>270) y = newPage();
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.text(pdfSafe(label),14,y);
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.text(valLines,64,y);
      y += rowH;
    };
    info("Nom", nomEmp);
    if (employe?.matricule) info("Matricule", employe.matricule);
    if (employe?.poste) info("Poste", employe.poste);

    y = drawPdfSectionTitle(doc,"Versement",y+4); y += 3;
    info("Nature du versement", v.type_versement);
    info("Montant", FCFA(montant).replace(/ | /g," "));
    info("En toutes lettres", enLettresMaj+" francs CFA");
    info("Date du versement", fmtDate(v.date_versement||today()));
    info("Mode de paiement", v.moyen||"Espèces");
    if (v.motif) info("Motif", v.motif);

    const recup = !!v.recuperable;
    y = drawPdfSectionTitle(doc,"Engagement",y+4);
    doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.2);
    const texte = recup
      ? "Je soussigné(e) "+pdfSafe(nomEmp)+" reconnais avoir reçu de "+pdfSafe(entrepriseNom)+" la somme de "+pdfSafe(FCFA(montant).replace(/ | /g," "))+" ("+pdfSafe(enLettres)+" francs CFA), à titre d'"+pdfSafe(String(v.type_versement||"avance").toLowerCase())+
        ". Cette somme est une avance : elle sera retenue sur mes fiches de paie, par parts dont le montant est fixé par la direction à chaque fiche, jusqu'à remboursement complet. En cas de départ de l'entreprise avant remboursement complet, le solde restant dû sera retenu sur mon solde de tout compte."
      : "Je soussigné(e) "+pdfSafe(nomEmp)+" reconnais avoir reçu de "+pdfSafe(entrepriseNom)+" la somme de "+pdfSafe(FCFA(montant).replace(/ | /g," "))+" ("+pdfSafe(enLettres)+" francs CFA), au titre de : "+pdfSafe(String(v.type_versement||"versement").toLowerCase())+
        ". Cette somme ne donne lieu à aucune retenue ultérieure sur mes fiches de paie.";
    const lignes = doc.splitTextToSize(texte,182);
    let ly = y+3;
    lignes.forEach(l=>{ if (ly>270) { y=newPage(); ly=y+3; } doc.text(l,14,ly); ly+=4.7; });
    y = ly+6;

    if (y>225) y = newPage();
    doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.2);
    doc.text("Fait à "+pdfSafe(String(enterpriseValue(entreprise,"entreprise_ville","Dschang")||"Dschang").split("/")[0].trim())+", le "+pdfSafe(fmtDate(v.date_versement||today()))+", en deux exemplaires.",14,y);
    y += 14;
    const sigW = 84;
    [["Le bénéficiaire",14,pdfSafe(nomEmp),"employe","Signature précédée de la mention « Reçu la somme indiquée »"],["Pour la Direction",112,pdfSafe(nomDir),"direction","Signature et cachet"]].forEach(([label,x,sub,key,cap])=>{
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(8.5);
      doc.text(label,x+sigW/2,y,{align:"center"});
      doc.setFont("helvetica","normal"); doc.setTextColor(...muted); doc.setFontSize(7.5);
      doc.text(sub,x+sigW/2,y+5,{align:"center",maxWidth:sigW});
      drawPdfSignatureBlock(doc,x,y+24,sigW,{caption:cap,dataUrl:signatures?.[key]});
    });

    addPdfPageFooters(doc,entreprise,"Bon de versement");
    const datauri = doc.output("datauristring");
    return { base64: datauri.split(",").pop(), filename: "Bon_versement_"+String(v.numero||"")+"_"+String(nomEmp).replace(/[^A-Za-z0-9]+/g,"_")+".pdf" };
  };

  // ── Bon de commande fournisseur (PDF) — signé par la direction une fois validé ──
  const generateBonCommandePdfBase64 = async (c, fournisseur, entreprise, opts={}) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const { ink, muted, dark } = PDF_BRAND;
    const valide = c.validation==="validee" || !!c.signe_le;
    const headerOptions = { title:"BON DE COMMANDE", reference:String(c.numero||""), subtitle: valide ? "Émis le "+fmtDate(c.signe_le||c.date_commande||today()) : "PROJET — en attente de validation" };
    const newPage = () => { doc.addPage(); return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset); };
    let y = drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);
    const info = (label, value) => {
      doc.setFontSize(9);
      const valLines = doc.splitTextToSize(pdfSafe(value||"—"),118);
      const rowH = Math.max(valLines.length,1)*4.6 + 1;
      if (y+rowH>270) y = newPage();
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.text(pdfSafe(label),14,y);
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.text(valLines,64,y);
      y += rowH;
    };
    y = drawPdfSectionTitle(doc,"Fournisseur",y+4); y += 3;
    info("Nom", (fournisseur&&fournisseur.nom)||c.fournisseur_nom);
    if (fournisseur&&fournisseur.code) info("Code fournisseur", fournisseur.code);
    if (fournisseur&&fournisseur.contact) info("Contact", fournisseur.contact);
    if (fournisseur&&(fournisseur.ville||fournisseur.pays)) info("Localisation", [fournisseur.ville,fournisseur.pays].filter(Boolean).join(", "));

    y = drawPdfSectionTitle(doc,"Commande",y+4); y += 3;
    info("Date de la commande", fmtDate(c.date_commande||today()));
    info("Livraison à", c.ville ? "Dépôt de "+c.ville : "—");
    if (c.date_livraison_prevue) info("Livraison souhaitée avant le", fmtDate(c.date_livraison_prevue));

    y = drawPdfSectionTitle(doc,"Articles commandés",y+4);
    const cols=[
      {label:"CODE",width:20},
      {label:"DÉSIGNATION",width:76},
      {label:"QUANTITÉ",width:22,align:"right"},
      {label:"PRIX UNIT.",width:30,align:"right"},
      {label:"MONTANT",width:34,align:"right"}
    ];
    y=drawPdfTableHeader(doc,y,cols);
    const lignes = Array.isArray(c.lignes)?c.lignes:safeJsonParse(c.lignes,[]);
    let total = 0;
    lignes.forEach(l=>{
      const q = toFiniteNumber(l?.quantite), pu = toFiniteNumber(l?.prix_unitaire);
      const m = toFiniteNumber(l?.montant, q*pu);
      total += m;
      const cells=[
        {text:l?.code||"—",fontSize:8.6,bold:true},
        {text:l?.designation||"—",fontSize:8.6},
        {text:pdfNUM(q),align:"right",fontSize:8.6},
        {text:pu?pdfNUM(pu):"—",align:"right",fontSize:8.6},
        {text:pdfNUM(m),align:"right",fontSize:8.6,bold:true}
      ];
      const rowH=getPdfTableRowHeight(doc,cols,cells,{minHeight:9});
      if (y+rowH>262) { y=newPage()+4; y=drawPdfTableHeader(doc,y,cols); }
      y=drawPdfTableRow(doc,y,cols,cells,{height:rowH,minHeight:9});
    });
    if (!lignes.length) total = toFiniteNumber(c.total);
    y += 6;
    if (y>245) y = newPage();
    doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(11);
    doc.text("TOTAL : "+pdfSafe(FCFA(total).replace(/ | /g," ")),196,y,{align:"right"});
    y += 6;
    const enLettres = nombreEnLettresFr(Math.round(total));
    doc.setFont("helvetica","italic"); doc.setTextColor(...muted); doc.setFontSize(8.4);
    doc.text(doc.splitTextToSize("Arrêté le présent bon à la somme de : "+pdfSafe(enLettres.charAt(0).toUpperCase()+enLettres.slice(1))+" francs CFA.",182),14,y);
    y += 9;
    if (c.notes) {
      y = drawPdfSectionTitle(doc,"Notes",y);
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9);
      doc.splitTextToSize(pdfSafe(c.notes),182).forEach(l=>{ if (y>270) y=newPage(); doc.text(l,14,y); y+=4.6; });
      y += 4;
    }
    if (y>236) y = newPage();
    y = drawPdfSectionTitle(doc,"Conditions",y);
    doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(8.8);
    ["Merci de confirmer la prise en compte de ce bon et la date de livraison.",
     "La facture doit rappeler le numéro de ce bon de commande ("+pdfSafe(c.numero||"")+").",
     "Toute modification de quantité ou de prix doit être validée par écrit avant livraison."].forEach(t=>{ doc.text("• "+t,16,y); y+=4.8; });
    y += 5;
    if (y>236) y = newPage();
    const sigW = 84, x = 112;
    doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(8.5);
    doc.text("Pour la Direction",x+sigW/2,y,{align:"center"});
    doc.setFont("helvetica","normal"); doc.setTextColor(...muted); doc.setFontSize(7.5);
    doc.text(valide ? pdfSafe(c.signe_par||"La direction") : "En attente de validation",x+sigW/2,y+5,{align:"center",maxWidth:sigW});
    drawPdfSignatureBlock(doc,x,y+24,sigW,{caption:"Signature et cachet",dataUrl:valide?opts.direction:null,signedAt:c.signe_le});
    addPdfPageFooters(doc,entreprise,"Bon de commande");
    const datauri = doc.output("datauristring");
    return { base64: datauri.split(",").pop(), filename: "Bon_commande_"+String(c.numero||"")+"_"+String((c.fournisseur_nom||fournisseur?.nom||"")).replace(/[^A-Za-z0-9]+/g,"_")+".pdf" };
  };
  // ── Fiche de contrôle qualité par lot — déposée automatiquement dès qu'un lot
  // est clôturé avec la décision « Lot libéré » (voir qcEnregistrer).
  const generateFicheControleLotPdfBase64 = async (fiche, entreprise) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const { ink, muted, dark } = PDF_BRAND;
    // Titre court + référence courte (numéro de lot) : le produit, qui peut être un
    // nom long, est affiché dans le corps du document plutôt que dans le petit cadre
    // noir de l'en-tête, pour que celui-ci reste lisible sur une à deux lignes max.
    const headerOptions = {
      title:"FICHE DE CONTRÔLE QUALITÉ",
      reference: String(fiche.numero_lot||"—"),
      subtitle: "Saisie le "+fmtDate(fiche.updated_at||fiche.created_at||fiche.date_production||today())
    };
    const newPage = () => { doc.addPage(); return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset); };
    let y = drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);

    const infoRow = (label, value) => {
      doc.setFontSize(9);
      // Certains libellés (mesures avec cible entre parenthèses) sont longs : on les
      // fait passer sur plusieurs lignes plutôt que de les laisser chevaucher la colonne
      // de valeur, et la hauteur de la ligne s'adapte au plus long des deux blocs de texte.
      const labelLines = doc.splitTextToSize(pdfSafe(label),50);
      const valLines = doc.splitTextToSize(pdfSafe(value||"—"),118);
      const rowH = Math.max(labelLines.length,valLines.length,1)*4.6 + 1;
      if (y+rowH>280) y=newPage();
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(9);
      doc.text(labelLines,14,y);
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9);
      doc.text(valLines,72,y);
      y += rowH;
    };

    y = drawPdfSectionTitle(doc,"Informations générales",y+4); y+=3;
    infoRow("N° de lot", fiche.numero_lot);
    infoRow("Produit", fiche.produit);
    infoRow("Producteur", fiche.producteur_nom);
    infoRow("Ville / site", fiche.ville);
    infoRow("Date de production", fmtDate(fiche.date_production||today()));
    infoRow("Volume produit (L)", fiche.volume_litres!=null?String(fiche.volume_litres):"");
    infoRow("Nombre de bouteilles", fiche.nombre_bouteilles!=null?String(fiche.nombre_bouteilles):"");

    y = drawPdfSectionTitle(doc,"pH et pasteurisation (mesures)",y+4); y+=3;
    QC_MESURES.forEach(([k,label,cible])=>{
      const val = fiche[k];
      infoRow(label+(cible?" ("+cible+")":""), val!=null&&val!==""?String(val):"");
    });

    Object.keys(QC_CHECKS).forEach(section=>{
      y = drawPdfSectionTitle(doc,section,y+4); y+=3;
      QC_CHECKS[section].forEach(([k,label])=>{
        const v = (fiche.conformites||{})[k];
        const txt = v===true?"Oui":v===false?"Non":"—";
        const col = v===true?[46,125,50]:v===false?[183,28,28]:muted;
        doc.setFont("helvetica","normal"); doc.setFontSize(9);
        // Le libellé peut être long (plusieurs lignes) : on calcule la vraie hauteur
        // qu'il va occuper AVANT de dessiner, pour ne jamais chevaucher la ligne suivante.
        const labelLines = doc.splitTextToSize(pdfSafe(label),150);
        const rowH = Math.max(labelLines.length,1)*4.6 + 1.4;
        if (y+rowH>280) y=newPage();
        doc.setTextColor(...ink);
        doc.text(labelLines,14,y);
        doc.setFont("helvetica","bold"); doc.setTextColor(...col); doc.setFontSize(9);
        doc.text(txt,175,y);
        y += rowH;
      });
    });

    y = drawPdfSectionTitle(doc,"Décision et libération",y+4); y+=3;
    const decisionTxt = fiche.decision==="libere"?"LOT LIBÉRÉ":fiche.decision==="bloque"?"LOT BLOQUÉ":"—";
    const decisionCol = fiche.decision==="libere"?[46,125,50]:fiche.decision==="bloque"?[183,28,28]:muted;
    doc.setFont("helvetica","bold"); doc.setTextColor(...decisionCol); doc.setFontSize(11);
    doc.text(decisionTxt,14,y); y+=7;
    if (fiche.observations) {
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9);
      const lines = doc.splitTextToSize("Observations : "+fiche.observations,182);
      lines.forEach(l=>{ if (y>280){y=newPage();} doc.text(l,14,y); y+=4.6; });
      y+=2;
    }
    infoRow("Visa responsable", fiche.visa_responsable);

    addPdfPageFooters(doc,entreprise,"Fiche de contrôle qualité","Ouverte le "+new Date().toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric"})+" à "+new Date().toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"}));
    const datauri = doc.output("datauristring");
    return {
      base64: datauri.split(",").pop(),
      filename: "Fiche_controle_qualite_"+String(fiche.numero_lot||"lot").replace(/[^a-zA-Z0-9._-]/g,"_")+".pdf"
    };
  };

  // ── Feuille de transport (bordereau de livraison Dschang → ville de destination) ──
  const generateFeuilleTransportPdfBase64 = async (liv, entreprise, signatures={}) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const { ink, muted, line, dark } = PDF_BRAND;
    let y = drawPdfLetterhead(doc,entreprise,{
      title:"FEUILLE DE TRANSPORT",
      reference: liv.numero||"",
      subtitle: "Édité le "+fmtDate(today())
    },logoAsset);

    y = drawPdfSectionTitle(doc,"Trajet",y+4);
    doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.5);
    const trajetLines = [
      ["Origine","Siège de production — Dschang"],
      ["Destination", (liv.ville||"—")+(liv.depot_nom?" ("+liv.depot_nom+")":"")],
      ["Date de départ", fmtDate(liv.date_depart)],
      ["Date d'arrivée prévue", liv.date_arrivee?fmtDate(liv.date_arrivee):"—"]
    ];
    trajetLines.forEach(([label,val],i)=>{
      const colX = i%2===0 ? 17 : 108;
      const rowY = y + Math.floor(i/2)*7;
      doc.setFont("helvetica","bold"); doc.setTextColor(...muted); doc.setFontSize(7.5);
      doc.text(pdfSafe(label.toUpperCase()),colX,rowY);
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.5);
      doc.text(pdfSafe(val),colX,rowY+4.5,{maxWidth:85});
    });
    y += Math.ceil(trajetLines.length/2)*7 + 8;

    y = drawPdfSectionTitle(doc,"Transporteur",y);
    const transLines = [
      ["Chauffeur", liv.chauffeur||"—"],
      ["Véhicule", liv.vehicule||"—"],
      ["Agence de transport", liv.agence||"—"],
      ["Tél. agence", liv.agence_tel||"—"],
      ["Doit réceptionner", liv.receptionnaire_prevu||"—"]
    ];
    transLines.forEach(([label,val],i)=>{
      const colX = i%2===0 ? 17 : 108;
      const rowY = y + Math.floor(i/2)*7;
      doc.setFont("helvetica","bold"); doc.setTextColor(...muted); doc.setFontSize(7.5);
      doc.text(pdfSafe(label.toUpperCase()),colX,rowY);
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.5);
      doc.text(pdfSafe(val),colX,rowY+4.5,{maxWidth:85});
    });
    y += Math.ceil(transLines.length/2)*7 + (liv.agence_ref?7:0) + 8;
    if (liv.agence_ref) { doc.setFont("helvetica","italic"); doc.setFontSize(8.5); doc.setTextColor(...muted); doc.text("Réf. agence : "+pdfSafe(liv.agence_ref),17,y-8); }

    y = drawPdfSectionTitle(doc,"Produits transportés",y);
    const transportCols=[
      {label:"PRODUIT",width:140},
      {label:"QUANTITÉ",width:42,align:"right"}
    ];
    y=drawPdfTableHeader(doc,y,transportCols);
    const lignes = (Array.isArray(liv.lignes)&&liv.lignes.length) ? liv.lignes : [{produit:liv.produit,quantite:liv.quantite}];
    lignes.forEach(l=>{
      const cells=[
        {text:l.produit||"—",fontSize:8.8},
        {text:pdfNUM(l.quantite)+" btl",align:"right",fontSize:8.8,bold:true}
      ];
      const rowH=getPdfTableRowHeight(doc,transportCols,cells,{minHeight:9.5});
      y=drawPdfTableRow(doc,y,transportCols,cells,{height:rowH,minHeight:9.5});
    });
    y+=6;
    doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(10);
    doc.text("Total : "+pdfSafe(NUM(liv.quantite)+" bouteilles"),17,y);
    y += 14;

    if (liv.notes) {
      doc.setFont("helvetica","italic"); doc.setFontSize(8.7); doc.setTextColor(...muted);
      const noteLines = doc.splitTextToSize("Notes : "+liv.notes,182);
      doc.text(noteLines,14,y); y += noteLines.length*4.3+8;
    }

    const sigW = 84;
    [["Chauffeur / Transporteur",14,"chauffeur"],["Réceptionnaire à "+pdfSafe(liv.ville||""),112,"receptionnaire"]].forEach(([label,x,sigKey])=>{
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(8.5);
      doc.text(pdfSafe(label),x+sigW/2,y,{align:"center",maxWidth:sigW});
      drawPdfSignatureBlock(doc,x,y+22,sigW,{dataUrl:signatures?.[sigKey]});
    });

    addPdfPageFooters(doc,entreprise,"Feuille de transport");
    const datauri = doc.output("datauristring");
    return {
      base64: datauri.split(",").pop(),
      filename: "Feuille_transport_"+(liv.numero||today())+".pdf"
    };
  };



  // ⚠️ Calcul indicatif basé sur les barèmes publics couramment cités (CNPS 4,2% plafonné à 750 000,
  // abattement forfaitaire 30%, IRPP progressif 10/15/25/35%, CAC 10% de l'IRPP, CFC 1% plafonné).
  // À faire vérifier par un comptable ou la CNPS/DGI avant tout usage officiel.
  const calculerBulletinPaie = (
    salaireBase,
    primes,
    heuresSup,
    autresRetenues,
    calculerTaxes=true
  ) => {
    const brut =
      toFiniteNumber(salaireBase)
      + toFiniteNumber(primes)
      + toFiniteNumber(heuresSup);

    const taxesAppliquees = calculerTaxes !== false;
    let cnps = 0;
    let cfc = 0;
    let irpp = 0;
    let cac = 0;

    if (taxesAppliquees) {
      const baseCotisable = Math.min(brut, 750000);
      cnps = Math.round(baseCotisable * 0.042);
      cfc = Math.round(baseCotisable * 0.01);

      const revenuImposableAnnuel = Math.max(
        0,
        (brut - cnps) * 12 * 0.70
      );

      const bareme = [
        { max: 2000000, taux: 0.10 },
        { max: 3000000, taux: 0.15 },
        { max: 5000000, taux: 0.25 },
        { max: Infinity, taux: 0.35 },
      ];

      let irppAnnuel = 0;
      let reste = revenuImposableAnnuel;
      let prevMax = 0;

      for (const tranche of bareme) {
        if (reste <= 0) break;
        const montantTranche = Math.min(
          reste,
          tranche.max - prevMax
        );
        irppAnnuel += montantTranche * tranche.taux;
        reste -= montantTranche;
        prevMax = tranche.max;
      }

      irpp = Math.round(irppAnnuel / 12);
      cac = Math.round(irpp * 0.10);
    }

    const autres = Math.max(0, toFiniteNumber(autresRetenues));
    const totalRetenues = cnps + cfc + irpp + cac + autres;
    const net = brut - totalRetenues;

    return {
      brut,
      cnps,
      cfc,
      irpp,
      cac,
      taxesAppliquees,
      autresRetenues: autres,
      totalRetenues,
      net
    };
  };

  // ── Bulletins du mois précédent, pré-remplis pour ceux qui ont atteint leur objectif de cartons ──
  const moisPrecedent = () => { const [y,m]=today().slice(0,7).split("-").map(Number); const d=new Date(y,m-2,1); return d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0"); };
  const remunMois = (e, obj, moisCible) => {
    const ids = new Set(ventes.filter(v=>v.employe===e.nom&&v.facture_id).map(v=>String(v.facture_id)));
    const fixe = toFiniteNumber(e.salaire_base), primeFixe = toFiniteNumber(e.prime_fixe), pct = toFiniteNumber(obj?.commission_pct);
    const encaisse = paiements.filter(p=>isPaiementValide(p)
      && String(p.date_versement||p.date_validation||"").slice(0,7)===moisCible
      && (p.facture_id ? ids.has(String(p.facture_id)) : factures.some(f=>ids.has(String(f.id))&&f.numero===p.facture_numero))
    ).reduce((sum,p)=>sum+toFiniteNumber(p.montant),0);
    const btl = ventes.filter(v=>v.employe===e.nom&&v.date&&String(v.date).slice(0,7)===moisCible).reduce((sum,v)=>sum+toFiniteNumber(v.qte),0);
    const objBtl = toFiniteNumber(obj?.bouteilles)>0 ? toFiniteNumber(obj.bouteilles) : 480;
    return { fixe, primeFixe, pct, encaisse, comm:Math.round(encaisse*pct/100), btl, objBtl, atteint: btl>=objBtl };
  };
  const bulletinsAConfirmerListe = () => {
    if (!isDirecteur()) return { mois:"", a:[], non:[] };
    const m = moisPrecedent();
    const a = [], non = [];
    employes.filter(e=>{ const p=safeJsonParse(e.permissions,[]); return e.actif && !estRoleDirecteur(e.role,e.poste) && Array.isArray(p) && p.includes("ventes"); }).forEach(e=>{
      const obj = objectifs[e.id]||{};
      if (obj.sans_objectif) return;
      if (bulletinsPaie.some(b=>String(b.employe_id)===String(e.id)&&String(b.mois).slice(0,7)===m)) return;
      const r = remunMois(e,obj,m);
      if (r.atteint) a.push({e,r,mois:m});
      else if (toFiniteNumber(obj.bouteilles)>0) non.push({e,r,mois:m});
    });
    return { mois:m, a, non };
  };
  const confirmerBulletinAuto = async (item) => {
    const { e, r, mois } = item;
    const primes = r.primeFixe + r.comm;
    const calc = calculerBulletinPaie(r.fixe, primes, 0, 0, true);
    if (!window.confirm("Confirmer le bulletin de "+e.nom+" pour "+mois+" ?\nBrut : "+FCFA(calc.brut)+" — Net à payer : "+FCFA(calc.net))) return;
    await saveBulletinPaie(e, calc, { mois, salaireBase:String(r.fixe), primes:String(primes), heuresSup:"0", calculerTaxes:true, autresRetenues:"0", autresRetenuesLibelle:"" });
  };
  const modifierBulletinAuto = (item) => {
    const { e, r, mois } = item;
    setBulletinForm({ employeId:String(e.id), mois, salaireBase:String(r.fixe), primes:String(r.primeFixe+r.comm), heuresSup:"", calculerTaxes:true, autresRetenues:"", autresRetenuesLibelle:"" });
    try { document.querySelector("main")?.scrollTo({top:0,behavior:"smooth"}); } catch(_) {}
    showToast("Bulletin chargé dans le formulaire : modifiez puis « Générer et enregistrer »");
  };

  const generateBonLivraisonPdfBase64 = async (facture, client, entreprise, signatures={}) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const { ink, muted, dark } = PDF_BRAND;
    const headerOptions = { title:"BON DE LIVRAISON", reference:"BL-"+(facture.numero||""), subtitle:"Édité le "+fmtDate(today()) };
    const newPage = () => { doc.addPage(); return drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset); };
    let y = drawPdfLetterhead(doc,entreprise,headerOptions,logoAsset);

    y = drawPdfSectionTitle(doc,"Livraison",y+4);
    const infos = [
      ["Client", facture.client_nom||client?.nom||"—"],
      ["Code client", client?.code||"—"],
      ["Facture n°", facture.numero||"—"],
      ["Date de la facture", fmtDate(facture.date_emission)],
      ["Adresse de livraison", [client?.adresse, facture.ville||client?.ville].filter(Boolean).join(", ")||"—"],
      ["Téléphone du client", client?.contact||client?.contact_tel||"—"]
    ];
    infos.forEach(([label,val],i)=>{
      const colX = i%2===0 ? 17 : 108;
      const rowY = y + Math.floor(i/2)*11;
      doc.setFont("helvetica","bold"); doc.setTextColor(...muted); doc.setFontSize(7.5);
      doc.text(pdfSafe(label.toUpperCase()),colX,rowY);
      doc.setFont("helvetica","normal"); doc.setTextColor(...ink); doc.setFontSize(9.5);
      doc.text(pdfSafe(val),colX,rowY+4.5,{maxWidth:85});
    });
    y += Math.ceil(infos.length/2)*11 + 5;

    y = drawPdfSectionTitle(doc,"Produits livrés",y);
    const cols=[
      {label:"CODE",width:22},
      {label:"DÉSIGNATION",width:90},
      {label:"BOUTEILLES",width:35,align:"right"},
      {label:"CARTONS",width:35,align:"right"}
    ];
    y=drawPdfTableHeader(doc,y,cols);
    const lignes = Array.isArray(facture.lignes)?facture.lignes:safeJsonParse(facture.lignes,[]);
    let totalBtl = 0;
    lignes.forEach(l=>{
      const q = toFiniteNumber(l?.qte);
      totalBtl += q;
      const cells=[
        {text:codeProduitDe(l?.designation||l?.produit)||"—",fontSize:8.8,bold:true},
        {text:l?.designation||l?.produit||"—",fontSize:8.8},
        {text:pdfNUM(q),align:"right",fontSize:8.8,bold:true},
        {text:pdfNUM(Math.round(q/24*10)/10),align:"right",fontSize:8.8}
      ];
      const rowH=getPdfTableRowHeight(doc,cols,cells,{minHeight:9.5});
      if (y+rowH>270) { y=newPage()+4; y=drawPdfTableHeader(doc,y,cols); }
      y=drawPdfTableRow(doc,y,cols,cells,{height:rowH,minHeight:9.5});
    });
    y+=6;
    doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(10);
    doc.text("Total : "+pdfSafe(NUM(totalBtl)+" bouteilles  ("+NUM(Math.round(totalBtl/24*10)/10)+(Math.round(totalBtl/24*10)/10>1?" cartons":" carton")+")"),17,y);
    y += 10;

    // Réserves / observations (à remplir à la main à la réception)
    if (y>205) y=newPage()+4;
    doc.setFont("helvetica","bold"); doc.setTextColor(...muted); doc.setFontSize(7.5);
    doc.text("RÉSERVES / OBSERVATIONS À LA RÉCEPTION",17,y);
    doc.setDrawColor(190,200,194); doc.setLineWidth(0.25);
    doc.rect(14,y+2,182,22);
    y += 34;

    if (y>235) y=newPage()+4;
    const sigW = 84;
    [["Livreur / Chauffeur",14,"livreur"],["Réceptionnaire — "+pdfSafe(facture.client_nom||""),112,"client"]].forEach(([label,x,sigKey])=>{
      doc.setFont("helvetica","bold"); doc.setTextColor(...dark); doc.setFontSize(8.5);
      doc.text(pdfSafe(label),x+sigW/2,y,{align:"center",maxWidth:sigW});
      drawPdfSignatureBlock(doc,x,y+22,sigW,{dataUrl:signatures?.[sigKey]});
    });
    y += 52;
    doc.setFont("helvetica","normal"); doc.setTextColor(...muted); doc.setFontSize(8.2);
    doc.text("Nom : ______________________   Date : ____ / ____ / ______",14,y);
    doc.text("Nom : ______________________   Date : ____ / ____ / ______",112,y);

    addPdfPageFooters(doc,entreprise,"Bon de livraison");
    const datauri = doc.output("datauristring");
    return {
      base64: datauri.split(",").pop(),
      filename: "Bon_livraison_"+(facture.numero||today())+".pdf"
    };
  };

  const imprimerBonLivraison = async (f, cli) => {
    setReportGenerating(true);
    try {
      const signatures = await openSignatureFlow("Bon de livraison — "+(f.numero||""),[{key:"livreur",label:"Livreur / Chauffeur"},{key:"client",label:"Réceptionnaire — "+(f.client_nom||"")}]);
      const pdf = await generateBonLivraisonPdfBase64(f,cli,entreprise,signatures);
      await openPdf(pdf,"Bon de livraison");
    } catch(e) { showToast("Erreur : "+e.message,false); }
    setReportGenerating(false);
  };

  const generateBulletinPaiePdfBase64 = async (bulletin, employe, entreprise, signatures={}) => {
    const doc = new jsPDF({unit:"mm",format:"a4"});
    const logoAsset = await getPdfLogoAsset();
    const moisRaw = new Date(
      bulletin.mois+"-01T12:00:00"
    ).toLocaleDateString("fr-FR",{
      month:"long",
      year:"numeric"
    });
    const moisLabel =
      moisRaw.charAt(0).toUpperCase()+moisRaw.slice(1);

    const taxesAppliquees = bulletin.calculer_taxes !== false;
    const totalRetenues = toFiniteNumber(
      bulletin.total_retenues,
      toFiniteNumber(bulletin.cnps)
        + toFiniteNumber(bulletin.cfc)
        + toFiniteNumber(bulletin.irpp)
        + toFiniteNumber(bulletin.cac)
        + toFiniteNumber(bulletin.autres_retenues)
    );

    const headerOptions = {
      title:"BULLETIN DE PAIE",
      reference:moisLabel,
      subtitle:taxesAppliquees
        ? "Document individuel et confidentiel"
        : "Document individuel — taxes non calculées"
    };

    let y = drawPdfLetterhead(
      doc,
      entreprise,
      headerOptions,
      logoAsset
    );
    const {ink,muted,pale,gold,dark,line} = PDF_BRAND;

    doc.setFillColor(...pale);
    doc.roundedRect(14,y,86,28,2,2,"F");
    doc.roundedRect(110,y,86,28,2,2,"F");
    doc.setTextColor(...gold);
    doc.setFont("helvetica","bold");
    doc.setFontSize(7);
    doc.text("EMPLOYEUR",18,y+6);
    doc.text("EMPLOYÉ",114,y+6);
    doc.setTextColor(...ink);
    doc.setFontSize(9);
    doc.text(
      pdfSafe(enterpriseValue(entreprise,"entreprise_nom")),
      18,
      y+12,
      {maxWidth:76}
    );
    doc.text(
      pdfSafe(employe?.nom||bulletin.employe_nom),
      114,
      y+12,
      {maxWidth:76}
    );
    doc.setTextColor(...muted);
    doc.setFont("helvetica","normal");
    doc.setFontSize(7.2);
    doc.text(
      [
        pdfSafe(enterpriseValue(entreprise,"entreprise_adresse")),
        pdfSafe("NIU : "+enterpriseValue(entreprise,"entreprise_niu"))
      ],
      18,
      y+17,
      {lineHeightFactor:1.4}
    );
    doc.text(
      [
        pdfSafe("Matricule : "+(employe?.matricule||"—")),
        pdfSafe("Poste : "+(employe?.poste||"—")),
        pdfSafe("Service : "+(employe?.service||"—"))
      ],
      114,
      y+17,
      {lineHeightFactor:1.35}
    );
    y += 36;

    y = drawPdfSectionTitle(doc,"Éléments de rémunération",y);
    const payCols=[
      {label:"ÉLÉMENT",width:126},
      {label:"MONTANT",width:56,align:"right"}
    ];
    const gains = [
      ["Salaire de base",bulletin.salaire_base],
      ["Primes",bulletin.primes],
      ["Heures supplémentaires",bulletin.heures_sup]
    ];
    y=drawPdfTableHeader(doc,y,payCols);
    gains.forEach(([label,value])=>{
      y=drawPdfTableRow(doc,y,payCols,[
        {text:label,fontSize:8.3},
        {text:pdfFCFA(value||0),align:"right",fontSize:8.3}
      ],{minHeight:9});
    });
    y=drawPdfTableRow(doc,y,payCols,[
      {text:"SALAIRE BRUT",fontSize:8.5,bold:true},
      {text:pdfFCFA(bulletin.brut),align:"right",fontSize:8.5,bold:true}
    ],{minHeight:9.5,fill:PDF_BRAND.pale});
    y += 7;

    y = drawPdfSectionTitle(doc,"Retenues salariales",y);
    const deductions = [];

    if (taxesAppliquees) {
      deductions.push(
        ["CNPS salarié (4,2%)",bulletin.cnps],
        ["Crédit Foncier — CFC (1%)",bulletin.cfc],
        ["IRPP",bulletin.irpp],
        ["CAC (10% de l'IRPP)",bulletin.cac]
      );
    }

    if (toFiniteNumber(bulletin.autres_retenues)>0) {
      deductions.push([
        bulletin.autres_retenues_libelle||"Autres retenues",
        bulletin.autres_retenues
      ]);
    }

    y=drawPdfTableHeader(doc,y,payCols);
    if (deductions.length===0) {
      y=drawPdfTableRow(doc,y,payCols,[
        {text:taxesAppliquees ? "Aucune retenue applicable." : "Taxes et cotisations non calculées pour ce bulletin.",fontSize:8,italic:true,color:muted},
        {text:"—",align:"right",fontSize:8,color:muted}
      ],{minHeight:9});
    } else {
      deductions.forEach(([label,value])=>{
        y=drawPdfTableRow(doc,y,payCols,[
          {text:label,fontSize:8.3},
          {text:"-"+pdfFCFA(value||0),align:"right",fontSize:8.3}
        ],{minHeight:9});
      });
    }
    y=drawPdfTableRow(doc,y,payCols,[
      {text:"TOTAL RETENUES",fontSize:8.5,bold:true},
      {text:"-"+pdfFCFA(totalRetenues),align:"right",fontSize:8.5,bold:true}
    ],{minHeight:9.5,fill:PDF_BRAND.pale});
    y += 7;

    doc.setFillColor(...dark);
    doc.roundedRect(14,y,182,16,2,2,"F");
    doc.setTextColor(...gold);
    doc.setFont("helvetica","bold");
    doc.setFontSize(12);
    doc.text("NET À PAYER",20,y+10);
    doc.text(pdfFCFA(bulletin.net),190,y+10,{align:"right"});
    y += 24;

    doc.setFillColor(...pale);
    const disclaimerText = taxesAppliquees
      ? "Calcul indicatif basé sur les barèmes paramétrés dans l'application. Ce bulletin doit être contrôlé selon les règles sociales et fiscales applicables avant tout usage officiel."
      : "Les taxes et cotisations légales (CNPS, CFC, IRPP et CAC) n'ont pas été calculées pour ce bulletin. Seules les autres retenues saisies ont été déduites. Ce choix doit être vérifié par la direction ou un professionnel comptable avant tout usage officiel.";
    const disclaimer = doc.splitTextToSize(disclaimerText,172);
    const dh = disclaimer.length*4+9;
    doc.roundedRect(14,y,182,dh,2,2,"F");
    doc.setTextColor(...muted);
    doc.setFont("helvetica","normal");
    doc.setFontSize(7.2);
    doc.text(disclaimer,19,y+6,{lineHeightFactor:1.25});
    y += dh+10;

    doc.setTextColor(...ink);
    doc.setFontSize(8);
    doc.text("Signature employeur",37,y+18,{align:"center"});
    doc.text("Signature employé",173,y+18,{align:"center"});
    if (signatures?.employeur) { try { doc.addImage(signatures.employeur,"PNG",16,y-4,42,15); } catch(imgErr) { console.warn("Signature non insérée dans le PDF :", imgErr); } }
    if (signatures?.employe) { try { doc.addImage(signatures.employe,"PNG",152,y-4,42,15); } catch(imgErr) { console.warn("Signature non insérée dans le PDF :", imgErr); } }
    doc.setDrawColor(...gold);
    doc.line(14,y+12,60,y+12);
    doc.line(150,y+12,196,y+12);

    addPdfPageFooters(doc,entreprise,"Bulletin de paie");
    const datauri = doc.output("datauristring");
    return {
      base64:datauri.split(",").pop(),
      filename:
        "Bulletin_"
        +(employe?.nom||bulletin.employe_nom).replace(/\s+/g,"_")
        +"_"+bulletin.mois+".pdf"
    };
  };

  const printDocument = async () => {
    if (!docView) return;
    try {
      const pdf = await generateDocumentPdfBase64(docView, entreprise, session);
      await deliverPdfOnDevice({
        ...pdf,
        title:docView.type || "Document Azongbio",
        direct:true,
        onOpenInApp:(url,filename,title)=>setPdfPreview({url,filename,title})
      });
      setDocView(null);
    } catch(error) {
      showToast("Erreur PDF : "+(error?.message||"inconnue"), false);
    }
  };

  const confirmerReception = async () => {
    if (!receptionModal) return;
    setLoading(true);
    const receptionNotes =
      (receptionModal.notes||"") +
      (receptionModal.notes?" | ":"") +
      "Etat a reception : "+receptionForm.etat+
      (receptionForm.note?" — "+receptionForm.note:"");

    const { error } = await supabase.rpc("confirmer_reception_livraison_transaction", {
      p_livraison_id:String(receptionModal.id),
      p_recu_par:session.nom,
      p_notes:receptionNotes
    });
    if (error) {
      showToast("Réception impossible : "+error.message,false);
      setLoading(false);
      return;
    }
    await load();
    setLoading(false);
    setReceptionModal(null);
    setReceptionForm({etat:"Bon état",note:""});
    showToast("Réception confirmée : stock ajouté à "+receptionModal.ville+" ✓");
  };


  const saveDepot = async () => {
    if (!form.nom||!form.ville) return showToast("Nom et ville requis",false);
    setLoading(true);
    const { error } = form.id
      ? await supabase.from("depots").update({nom:form.nom,ville:form.ville,responsable:form.responsable||"",telephone:form.telephone||"",adresse:form.adresse||""}).eq("id",form.id)
      : await supabase.from("depots").insert({nom:form.nom,ville:form.ville,responsable:form.responsable||"",telephone:form.telephone||"",adresse:form.adresse||""});
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    showToast(form.id ? "Dépôt mis à jour ✓" : "Dépôt ajouté ✓");
    await load(); setLoading(false); closeModal();
  };

  const deleteDepot = async (id) => {
    if (!peutSupprimerDepot(id)) {
      showToast("Impossible : ce dépôt a déjà du stock ou des livraisons associées", false);
      return;
    }
    if (!window.confirm("Supprimer définitivement ce dépôt ?")) return;
    setLoading(true);
    const { error } = await supabase.from("depots").delete().eq("id",id);
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast("Dépôt supprimé");
  };

  const deleteLivraison = async (id) => {
    if (!window.confirm("Supprimer définitivement cette livraison ? Le stock déjà déduit ne sera pas restauré automatiquement.")) return;
    setLoading(true);
    const { error } = await supabase.from("livraisons").delete().eq("id",id);
    if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
    await load(); setLoading(false); showToast("Livraison supprimée");
  };

  // ── Journal des objectifs : l'objectif de chaque mois passé est figé (il ne change plus quand on modifie l'objectif) ──
  const objCibles = () => employes.filter(e=>{ const p=safeJsonParse(e.permissions,[]); return e.actif && !estRoleDirecteur(e.role,e.poste) && Array.isArray(p) && p.includes("ventes"); });
  const objMoisListe = (e) => {
    const ms = ventes.filter(v=>v.employe===e.nom&&v.date).map(v=>String(v.date).slice(0,7)).sort();
    if (!ms.length) return [];
    const fin = today().slice(0,7); const out = [];
    let [y,m] = ms[0].split("-").map(Number);
    for (let i=0;i<240;i++) { const k = y+"-"+String(m).padStart(2,"0"); out.push(k); if (k>=fin) break; m++; if (m>12) { m=1; y++; } }
    return out;
  };
  const figerObjectifs = async (e, src) => {
    const o = src || objectifs[e.id];
    if (!o || !objHistPret) return;
    const fin = today().slice(0,7);
    const manquants = objMoisListe(e).filter(m=>m<fin && !objHist[e.id+"|"+m]);
    if (!manquants.length) return;
    const val = JSON.stringify({ bouteilles:toFiniteNumber(o.bouteilles), commission_pct:toFiniteNumber(o.commission_pct), sans_objectif:!!o.sans_objectif });
    const { error } = await supabase.from("parametres").insert(manquants.map(m=>({ cle:"journal_obj_"+e.id+"_"+m, valeur:val })));
    if (!error) setObjHist(h=>{ const n={...h}; manquants.forEach(m=>{ n[e.id+"|"+m]=JSON.parse(val); }); return n; });
  };
  const objFigeFait = useRef(false);
  useEffect(()=>{
    if (objFigeFait.current || !session || !isDirecteur() || !objHistPret || !ventes.length || !employes.length || !Object.keys(objectifs).length) return;
    objFigeFait.current = true;
    (async()=>{ for (const e of objCibles()) { try { await figerObjectifs(e); } catch(_) {} } })();
    // eslint-disable-next-line
  },[session?.id, objHistPret, ventes.length, employes.length, Object.keys(objectifs).length]);
  const saveObjectif = async (empId, data) => {
    { const emp = employes.find(x=>String(x.id)===String(empId)); if (emp && objectifs[empId]) { try { await figerObjectifs(emp, objectifs[empId]); } catch(_) {} } }
    const cle = "objectif_"+empId;
    const val = JSON.stringify(data);
    const {data:ex} = await supabase.from("parametres").select("id").eq("cle",cle).single();
    const { error } = ex
      ? await supabase.from("parametres").update({valeur:val}).eq("cle",cle)
      : await supabase.from("parametres").insert({cle, valeur:val});
    if (error) { showToast("Erreur : "+error.message, false); return; }
    setObjectifs(o=>({...o,[empId]:data}));
    setEditObjectif(null); showToast("Objectif mis à jour ✓");
  };

  const getEncouragementMsg = (pct, nom) => {
    if (pct >= 100) return "Félicitations "+nom+" ! Tu as atteint ton objectif ce mois-ci !";
    if (pct >= 80)  return "Excellent "+nom+" ! Tu es à "+pct+"% de ton objectif. Plus que quelques efforts !";
    if (pct >= 60)  return "Bien joué "+nom+" ! "+pct+"% de l'objectif atteint. Continue !";
    if (pct >= 40)  return nom+", tu es à "+pct+"% de ton objectif. Continue !";
    if (pct >= 20)  return "⚡ "+nom+", seulement "+pct+"% de l'objectif. Il faut accélérer !";
    return nom+", l'objectif du mois est à risque. Parlons ensemble !";
  };

  if (loggingOut) return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,background:"#F5F7F6",color:"#0F1F17",fontSize:14}}>
      <img src={LOGO_URI} alt="Azongbio" style={{height:48}} />
      <div>Déconnexion…</div>
    </div>
  );
  if (!session) return <LoginScreen onLogin={login} />;

  const fHT=facForm.lignes.reduce((s,l)=>s+l.montant,0);
  const fRem=facForm.remisePct?Math.round(fHT*facForm.remisePct/100):0;
  const fHN=fHT-fRem;
  const fTVA=facForm.appliquerTVA?Math.round(fHN*tvaTaux):0;
  const fTTC=fHN+fTVA;
  const pfHT=proformaForm.lignes.reduce((s,l)=>s+l.montant,0);
  const pfRem=proformaForm.remisePct?Math.round(pfHT*proformaForm.remisePct/100):0;
  const pfHN=pfHT-pfRem;
  const pfTVA=proformaForm.appliquerTVA?Math.round(pfHN*tvaTaux):0;
  const pfTTC=pfHN+pfTVA;

  // Icônes des modules de navigation (lucide-react — fiable, ne se corrompent jamais lors des copier-coller)
  const goToNav = (id) => {
    if(id==="production"&&!canSeeAllCities()&&!isDschangUser()){
      showToast("La production est centralisée à Dschang et n'est pas accessible depuis votre ville.",false);
      return;
    }
    if (id === "messagerie") {
      try { localStorage.setItem("azongbio_last_mail_check", String(Date.now())); } catch(e) {}
      setMailReminder(false);
      window.open("https://accounts.zoho.eu/signin?servicename=ZohoMail&serviceurl=https%3A%2F%2Fmail.zoho.eu%2Fzm", "_blank", "noopener,noreferrer");
      return;
    }
    setPage(id);
  };

  const renderNavIcon = (id) => {
    const sz = 17;
    switch(id) {
      case "tableau": return <LayoutDashboard size={sz}/>;
      case "mon_activite": return <Activity size={sz}/>;
      case "ventes": return <ShoppingCart size={sz}/>;
      case "factures": return <Receipt size={sz}/>;
      case "proformas": return <FileText size={sz}/>;
      case "clients": return <Users size={sz}/>;
      case "objectifs": return <Target size={sz}/>;
      case "paiements": return <CreditCard size={sz}/>;
      case "achats": return <Briefcase size={sz}/>;
      case "stock": return <Boxes size={sz}/>;
      case "production": return <Layers size={sz}/>;
      case "controle_qualite": return <ShieldCheck size={sz}/>;
      case "fournisseurs": return <Truck size={sz}/>;
      case "livraisons": return <PackageCheck size={sz}/>;
      case "stock-villes": return <MapPin size={sz}/>;
      case "avaries": return <AlertOctagon size={sz}/>;
      case "prospection": return <Compass size={sz}/>;
      case "comptabilite": return <Calculator size={sz}/>;
      case "caisse": return <Wallet size={sz}/>;
      case "rentabilite": return <TrendingUp size={sz}/>;
      case "rapports": return <BarChart3 size={sz}/>;
      case "documents": return <FileEdit size={sz}/>;
      case "annonces": return <Bell size={sz}/>;
      case "messagerie": return <Mail size={sz}/>;
      case "employes": return <User size={sz}/>;
      case "immobilisations": return <Landmark size={sz}/>;
      case "procurations": return <FileEdit size={sz}/>;
      case "salaires": return <Wallet size={sz}/>;
      case "parametres": return <Settings size={sz}/>;
      case "journal_activite": return <Eye size={sz}/>;
      case "a_cocher": return <ClipboardCheck size={sz}/>;
      case "demandes_depense": return <Banknote size={sz}/>;
      case "produits_admin": return <Package size={sz}/>;
      case "depots": return <Warehouse size={sz}/>;
      case "chat": return <MessageCircle size={sz}/>;
      default: return <ChevronRight size={sz}/>;
    }
  };

  return (
    <div style={{fontFamily:"var(--az-font)",background:"#F5F7F6",minHeight:"100vh",color:"#0F1F17",display:"flex",flexDirection:"column"}}>
      <style>{`
        *{box-sizing:border-box}
        :root{
          --az-font:"Inter","Inter Display",system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
          --az-night:#F5F7F6;
          --az-surface:#FFFFFF;
          --az-surface-2:#F1F5F3;
          --az-border:#E3E9E6;
          --az-gold:#2F9E3F;
          --az-gold-soft:#0F1F17;
          --az-text:#0F1F17;
          --az-muted:#5B6B62;
        }
        html,body,#root{margin:0;min-height:100%;overflow-x:clip;max-width:100vw;background:var(--az-night)}
        body{overscroll-behavior-x:none}
        button,[role="button"],a,input,textarea{touch-action:manipulation}
        select{touch-action:auto}
        button:disabled{cursor:not-allowed;opacity:.58}
        button:focus-visible,a:focus-visible,input:focus-visible,select:focus-visible,textarea:focus-visible{
          outline:2px solid var(--az-gold);outline-offset:2px
        }
        .card{background:#FFFFFF;border:1px solid #E3E9E6;border-radius:8px;padding:18px}
        .btn{min-height:36px;padding:7px 16px;border-radius:7px;border:none;cursor:pointer;font-family:inherit;font-size:13px;font-weight:600;transition:transform .12s ease,background .15s ease,border-color .15s ease,color .15s ease,opacity .15s ease;user-select:none}.btn:active:not(:disabled){transform:translateY(1px)}
        .btn-gold{background:#2F9E3F;color:#FFFFFF;font-weight:700}.btn-gold:hover{background:#DDF0E0}
        .btn-ghost{background:transparent;border:1px solid #CBD5D0;color:#5B6B62}.btn-ghost:hover{border-color:#2F9E3F;color:#1E8A32}
        .btn-red{background:#D6453D;color:#FFFFFF;border:none}.btn-red:hover{background:#D6453D}
        .btn-purple{background:#7A4FD6;color:#FFFFFF;border:none;font-weight:700}.btn-purple:hover{background:#c4a0ff}
        .btn-sm{padding:4px 10px;font-size:12px}
        .btn-icon{display:inline-flex;align-items:center;justify-content:center;gap:6px}
        .inp{background:#F1F5F3;border:1px solid #E3E9E6;color:#0F1F17;padding:8px 12px;border-radius:4px;font-size:13px;width:100%;font-family:inherit;box-sizing:border-box}
        .page-head{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:18px;flex-wrap:wrap}.page-title{font-size:24px;color:#0F1F17}.page-subtitle{font-size:12px;color:#5B6B62;margin-top:3px}.achat-stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-bottom:14px}.achat-stat{padding:15px}.achat-stat-label{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#5B6B62}.achat-stat-value{font-size:22px;font-weight:800;color:#0F1F17;margin:5px 0}.achat-stat-detail{font-size:11px;color:#2F6FDE}.module-tabs{display:flex;gap:6px;overflow:auto;padding-bottom:8px;margin-bottom:10px}.module-tabs button{white-space:nowrap;background:#FFFFFF;color:#5B6B62;border:1px solid #E3E9E6;border-radius:999px;padding:8px 13px;cursor:pointer}.module-tabs button.active{background:#2F9E3F;color:#FFFFFF;border-color:#2F9E3F;font-weight:700}.card-title{font-size:16px;font-weight:700;color:#0F1F17;margin-bottom:12px}.responsive-form-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}.achat-line-builder{display:grid;grid-template-columns:1fr 1fr 1.5fr .6fr .8fr auto;gap:8px;margin:14px 0}.achat-lines>div,.compact-row,.action-list>div{display:flex;align-items:center;justify-content:space-between;gap:10px;border-bottom:1px solid #EDF1EF;padding:10px 0}.achat-lines button{border:0;background:transparent;color:#D6453D;font-size:20px;cursor:pointer}.responsive-card-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.purchase-card{display:flex;flex-direction:column;gap:7px}.purchase-card>div:first-child{display:flex;justify-content:space-between;gap:8px}.purchase-card h3,.purchase-card p{margin:0}.purchase-card small,.compact-row small{display:block;color:#5B6B62;margin-top:3px}.form-actions,.row-actions{display:flex;gap:8px;flex-wrap:wrap;margin-top:12px}.filter-bar{display:flex;align-items:center;gap:8px;margin-bottom:12px}.mobile-cards{display:none}
        @media(max-width:640px){
          .achat-stats{grid-template-columns:1fr 1fr}.responsive-form-grid,.responsive-card-grid{grid-template-columns:1fr}.achat-line-builder{grid-template-columns:1fr}.desktop-table{display:none}.mobile-cards{display:grid;grid-template-columns:1fr;gap:10px}.page-head .btn{width:100%}.achat-stat-value{font-size:18px}.achat-form{padding:13px}.module-tabs{margin-left:-4px;margin-right:-4px}.row-actions .btn,.form-actions .btn{flex:1;min-width:120px}
          .inp, select, input, textarea { font-size:16px!important; }
          input[type="date"], input[type="number"], input[type="text"], input[type="email"], input[type="password"] { font-size:16px!important; }
        }
        html{-webkit-text-size-adjust:100%;text-size-adjust:100%}
        img{max-width:100%}
        * { -webkit-tap-highlight-color: transparent; }
        .inp:focus{outline:none;border-color:#2F9E3F} select.inp option{background:#F1F5F3}
        .lbl{font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#5B6B62;margin-bottom:4px;display:block}
        .fld{margin-bottom:13px}
        .table-wrap{border-radius:7px;overflow:hidden;overflow-x:auto}
        .tbl{width:100%;border-collapse:collapse}
        .tbl th{text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#5B6B62;padding:11px;background:rgba(47,158,63,0.040);border-bottom:1px solid #E3E9E6;font-weight:600}
        .tbl td{padding:11px;border-bottom:1px solid #EDF1EF;font-size:13px;vertical-align:top}
        .tbl tbody tr:last-child td{border-bottom:none}
        .tbl tbody tr:nth-child(even) td{background:rgba(15,31,23,0.014)}
        .tbl tr:hover td{background:rgba(47,158,63,0.056)}
        .badge{display:inline-flex;align-items:center;justify-content:center;text-align:center;line-height:1.3;padding:2px 9px;border-radius:20px;font-size:11px}
        .bg{background:rgba(47,158,63,0.240);color:#1E7A2D;border:1px solid #BFE3C6}
        .bg2{background:rgba(47,158,63,0.120);color:#1E8A32;border:1px solid rgba(47,158,63,0.240)}
        .br{background:rgba(192,57,43,.2);color:#D6453D;border:1px solid rgba(192,57,43,.4)}
        .bpurple{background:rgba(168,128,238,.15);color:#7A4FD6;border:1px solid rgba(168,128,238,.3)}
        .overlay{position:fixed;inset:0;background:rgba(0,0,0,.78);z-index:100;display:flex;align-items:center;justify-content:center;padding:20px;overflow-y:auto}
        .modal{background:#FFFFFF;border:1px solid #2F9E3F;border-radius:8px;padding:26px;width:100%;max-width:520px;max-height:90vh;overflow-y:auto}
        .toast{position:fixed;bottom:22px;right:22px;padding:11px 18px;border-radius:6px;font-size:13px;z-index:400}
        .tok{background:#E3F3E6;border:1px solid #4a8c61;color:#1E7A2D}
        .terr{background:#FDECEA;border:1px solid #8c4a4a;color:#B3372F}
        .ni{padding:9px 13px;border-radius:6px;cursor:pointer;display:flex;align-items:center;gap:9px;font-size:12px;color:#5B6B62;white-space:nowrap}
        .ni:hover{color:#0F1F17;background:#F1F5F3}
        .ni.active{color:#1E8A32;background:rgba(47,158,63,0.064);border-left:2px solid #2F9E3F}@media(max-width:768px){.ni.active{border-left:none;border-top:2px solid #2F9E3F;background:rgba(47,158,63,0.096)}}
        .scr{overflow-x:auto}.kpi{background:#FFFFFF;border-radius:8px;padding:16px 18px;border:1px solid #E3E9E6}
        .lr{display:flex;align-items:center;gap:7px;padding:6px 9px;background:rgba(47,158,63,0.040);border-radius:4px;margin-bottom:4px;font-size:12px}
        .divider{border:none;border-top:1px solid #E3E9E6;margin:14px 0}
        .chk{display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:4px;cursor:pointer;font-size:13px}
        .chk:hover{background:#F1F5F3}
        .rp{background:rgba(47,158,63,0.060)}.ra{background:rgba(192,57,43,.06)}
        @media(max-width:768px){
          .hide-mobile{display:none!important}
          .mob-full{width:100%!important;grid-template-columns:1fr!important}
          .mob-pad{padding:12px!important}
          .mob-text{font-size:11px!important}
          .mob-stack{flex-direction:column!important;gap:8px!important}
          .tbl td,.tbl th{padding:7px 8px!important;font-size:11px!important}
          .modal{padding:16px!important;max-width:calc(100vw - 18px)!important;margin:9px!important;max-height:calc(100dvh - 18px)!important}
          .btn-mob{padding:10px 14px!important;font-size:14px!important}
          .btn{min-height:42px}
          .overlay{padding:0;align-items:flex-start}
          .card,.kpi{border-radius:10px}
          .scr{-webkit-overflow-scrolling:touch}
        }
        @font-face{font-family:"Inter";font-style:normal;font-weight:100 900;font-display:swap;src:url(/fonts/inter-latin-ext-wght-normal.woff2) format("woff2");unicode-range:U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF}
        @font-face{font-family:"Inter";font-style:normal;font-weight:100 900;font-display:swap;src:url(/fonts/inter-latin-wght-normal.woff2) format("woff2");unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD}
        /* ===== Nouveau style clair (v3) ===== */
        body,input,button,select,textarea{font-family:var(--az-font)}
        body{-webkit-font-smoothing:antialiased;font-feature-settings:"tnum","cv11"}
        .card{border-color:#E6EBE8;border-radius:16px;box-shadow:0 1px 2px rgba(15,31,23,.04),0 4px 14px rgba(15,31,23,.05)}
        .kpi{border-color:#E6EBE8;border-radius:16px;box-shadow:0 1px 2px rgba(15,31,23,.04),0 4px 14px rgba(15,31,23,.05)}
        .btn{border-radius:10px;min-height:38px}
        .btn-gold{background:#2F9E3F;color:#fff;box-shadow:0 2px 6px rgba(47,158,63,.28)}.btn-gold:hover{background:#278a36}
        .btn-ghost{background:#fff;border:1px solid #D5DDD9;color:#2B3A32}.btn-ghost:hover{border-color:#2F9E3F;color:#1E7A2D}
        .inp{border-radius:10px;background:#fff;border:1px solid #D5DDD9;padding:9px 12px;color:#0F1F17}
        .inp:focus{border-color:#2F9E3F;box-shadow:0 0 0 3px rgba(47,158,63,.15)}
        select.inp option{background:#fff;color:#0F1F17}
        .lbl{font-size:11.5px;text-transform:none;letter-spacing:0;font-weight:600;color:#4B5A52}
        .tbl th{font-size:11px;letter-spacing:.06em;background:#F7F9F8;color:#6B7B72}
        .tbl td{font-size:13.5px}
        .tbl tbody tr:nth-child(even) td{background:transparent}
        .tbl tr:hover td{background:#F5F9F6}
        .table-wrap{border-radius:12px}
        .badge{font-weight:600;border-radius:999px;padding:3px 10px}
        .modal{border-radius:18px;border:1px solid #E6EBE8;box-shadow:0 24px 60px rgba(15,31,23,.25)}
        .overlay{background:rgba(15,31,23,.45);backdrop-filter:blur(2px)}
        .ni{border-radius:10px;font-size:13.5px;padding:9px 12px;font-weight:500;color:#4B5A52;border-left:none}
        .ni:hover{background:#F1F5F3;color:#0F1F17}
        .ni.active{background:#E8F5EA;color:#1E7A2D;font-weight:600;border-left:none}
        @media(max-width:768px){.ni.active{border-top:none;background:#E8F5EA}}
        .page-title{font-size:26px;letter-spacing:-.02em;font-weight:700;color:#0F1F17}
        .toast{border-radius:12px;box-shadow:0 10px 30px rgba(15,31,23,.2)}
        .tok{background:#E3F3E6;border-color:#BFE3C6;color:#1E7A2D}
        .terr{background:#FDECEA;border-color:#F3B9B4;color:#B3372F}
        .btn-red{background:#FDECEA;color:#D6453D;border:1px solid #F3B9B4}.btn-red:hover{background:#D6453D;color:#fff}
        .btn-purple{background:#F0EBFB;color:#7A4FD6;border:1px solid #D9CBF6}.btn-purple:hover{background:#7A4FD6;color:#fff}
        .ra{background:rgba(214,69,61,.035)}.rp{background:rgba(47,158,63,.04)}
        .dash-top{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;margin-bottom:16px}
        .dash-mini{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:12px}
        .dash-two{display:grid;grid-template-columns:minmax(0,1.5fr) minmax(0,1fr);gap:16px;margin-top:16px}
        .dk{padding:18px 20px}
        .dk-hero{background:linear-gradient(135deg,#2F9E3F,#1E7A2D);border:none;color:#fff;box-shadow:0 8px 20px rgba(47,158,63,.25)}
        @media(max-width:1100px){.dash-top{grid-template-columns:repeat(2,minmax(0,1fr))}.dash-two{grid-template-columns:minmax(0,1fr)}}
        @media(max-width:640px){.dash-top{gap:10px}.dash-top .dk-hero{grid-column:1/-1}.dk{padding:14px}.dash-mini{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}}
        @media(max-width:640px){.dash-top>.dk:last-child:nth-child(4){grid-column:1/-1}}

        .dash-top>*,.dash-mini>*,.dash-two>*{min-width:0}
        .dash-mini .dk{overflow:hidden}
        .tbl.az-mcards,.tbl.az-mcards tbody{display:block;width:100%}
        .tbl.az-mcards thead{display:none}
        .tbl.az-mcards tr{display:grid;grid-template-columns:1fr auto;gap:5px 12px;align-items:center;padding:14px 14px 12px;margin:0 0 12px;border:1px solid #E3E9E6;border-radius:16px;background:#fff;box-shadow:0 1px 2px rgba(15,31,23,.04)}
        .tbl.az-mcards tr.rp{background:#F6FBF7}
        .tbl.az-mcards td,.tbl.az-mcards tr:hover td,.tbl.az-mcards tbody tr:nth-child(even) td{display:block;padding:0!important;border:none!important;background:transparent!important;font-size:13px!important;min-width:0}
        .tbl.az-mcards td:nth-child(1){grid-column:1;grid-row:1;font-size:12px!important}
        .tbl.az-mcards td:nth-child(5){grid-column:2;grid-row:1;text-align:right;font-size:17px!important;font-weight:800!important;color:#0F1F17;white-space:nowrap}
        .tbl.az-mcards td:nth-child(2){grid-column:1;grid-row:2;font-size:15px!important;font-weight:650;color:#0F1F17}
        .tbl.az-mcards td:nth-child(7){grid-column:2;grid-row:2;text-align:right}
        .tbl.az-mcards td:nth-child(4){grid-column:1/-1;grid-row:3;color:#5B6B62}
        .tbl.az-mcards td:nth-child(4)::before{content:"Échéance : ";color:#8A978F}
        .tbl.az-mcards td:nth-child(3),.tbl.az-mcards td:nth-child(6){display:none!important}
        .tbl.az-mcards td:nth-child(8){grid-column:1/-1;grid-row:4;margin-top:8px;padding-top:10px!important;border-top:1px solid #EEF2EF!important}
        .tbl.az-mcards td:nth-child(8)>div{flex-wrap:wrap;gap:8px!important}
        .tbl.az-mcards td:nth-child(8) .btn{min-height:42px;min-width:44px;padding:8px 12px;display:inline-flex;align-items:center;justify-content:center}
        html,body{overscroll-behavior:none;-webkit-text-size-adjust:100%;text-size-adjust:100%}
        @media(max-width:768px){
          #root>div{min-height:100vh;min-height:100dvh}
          input:not([type=checkbox]):not([type=radio]):not([type=range]):not([type=file]):not([type=color]),select,textarea,.inp{font-size:16px!important}
          body::before{content:"";position:fixed;top:0;left:0;right:0;height:env(safe-area-inset-top,0px);background:#fff;z-index:95;pointer-events:none}
          #root>div>header{padding-top:calc(env(safe-area-inset-top,0px) + 8px)!important}
        }
.tbl.az-mcards tr.grp{display:block!important;padding:0!important;margin:0 0 8px!important;background:#EAF5EC!important;border:1px solid #BFE3C6!important}
.tbl.az-mcards tr.grp td,.tbl.az-mcards tr.grp td:nth-child(1){display:block!important;grid-column:auto!important;font-size:13px!important;padding:0!important}
.tbl tr.grp:hover td{background:#EAF5EC!important}
        .liv-long{display:none}
        .tbl.az-livcards,.tbl.az-livcards tbody{display:block;width:100%}
        .tbl.az-livcards thead{display:none}
        .tbl.az-livcards tr{display:grid;grid-template-columns:1fr auto;gap:6px 12px;align-items:center;padding:14px 14px 12px;margin:0 0 12px;border:1px solid #E3E9E6;border-radius:16px;background:#fff;box-shadow:0 1px 2px rgba(15,31,23,.04)}
        .tbl.az-livcards td,.tbl.az-livcards tr:hover td,.tbl.az-livcards tbody tr:nth-child(even) td{display:block;padding:0!important;border:none!important;background:transparent!important;font-size:13px!important;min-width:0}
        .tbl.az-livcards td:nth-child(1){grid-column:1;grid-row:1;font-size:12px!important}
        .tbl.az-livcards td:nth-child(6){grid-column:2;grid-row:1;text-align:right;font-size:17px!important;font-weight:800!important;color:#0F1F17;white-space:nowrap}
        .tbl.az-livcards td:nth-child(3){grid-column:1/-1;grid-row:2;font-size:15px!important;font-weight:650;color:#0F1F17}
        .tbl.az-livcards td:nth-child(5){grid-column:1/-1;grid-row:3;color:#5B6B62}
        .tbl.az-livcards td:nth-child(5)::before{content:"Départ : ";color:#8A978F}
        .tbl.az-livcards td:nth-child(2){grid-column:1/-1;grid-row:4}
        .tbl.az-livcards td:nth-child(2)::before{content:"Origine : ";color:#8A978F;font-size:13px}
        .tbl.az-livcards td:nth-child(4),.tbl.az-livcards td:nth-child(7),.tbl.az-livcards td:nth-child(8){display:none!important}
        .tbl.az-livcards td:nth-child(9){grid-column:1/-1;grid-row:5;margin-top:6px;padding-top:10px!important;border-top:1px solid #EEF2EF!important}
        .tbl.az-livcards td:nth-child(9) .btn{width:100%;min-height:48px;font-size:15px;display:flex;align-items:center;justify-content:center}
        .tbl.az-livcards td:nth-child(10){grid-column:1/-1;grid-row:6;justify-content:flex-end}
        .tbl.az-livcards td:nth-child(10) .btn{min-height:42px;min-width:44px}
        .az-livcards .liv-long{display:inline}
        .liv-recep{font-size:11px;color:#5B6B62;margin-top:6px}
        @keyframes azflash{0%,70%{background:rgba(255,214,10,.50);outline:2px solid #E0A800;outline-offset:-2px}100%{background:transparent;outline:2px solid transparent}}
        @keyframes azflashbg{0%,70%{background:rgba(255,214,10,.50)}100%{background:transparent}}
        .az-flash{animation:azflash 3.4s ease-out}
        tr.az-flash>td{animation:azflashbg 3.4s ease-out}
        @keyframes azb-spin{
          from{transform:rotate(0deg)}
          to{transform:rotate(360deg)}
        }
        @supports(padding:max(0px)){
          header{padding-top:max(8px,env(safe-area-inset-top))}
          .toast{bottom:max(22px,calc(env(safe-area-inset-bottom) + 10px))}
        }
      `}</style>

      {/* HEADER */}
      <header style={{position:"sticky",top:0,zIndex:90,background:"#FFFFFF",borderBottom:"1px solid #E3E9E6",boxShadow:"0 1px 0 rgba(15,31,23,.02)",padding:isMobile?"8px 10px":"10px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:isMobile?8:12,minWidth:0,flex:isMobile?"1 1 auto":"0 1 auto",overflow:"hidden"}}>
          {isMobile&&(
            <button onClick={()=>setMobileMenuOpen(true)} style={{background:"none",border:"1px solid #E3E9E6",borderRadius:6,color:"#1E8A32",padding:"7px 8px",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center"}} title="Menu">
              <Menu size={18}/>
            </button>
          )}
          <img src={LOGO_UI} alt="Azongbio" style={{height:isMobile?26:34,flexShrink:0}} />
          {!isMobile&&(
            <div style={{fontSize:9,color:"#5B6B62",letterSpacing:2,textTransform:"uppercase",lineHeight:1.6,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {entreprise.entreprise_nom||"Bralie Branding Food SARL"}<br/>
              <span style={{color:"#1E8A32"}}>Système de Gestion</span>
            </div>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:isMobile?6:16,flexShrink:0}}>
          {!isMobile&&(
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:13,color:"#0F1F17"}}>{session.nom}</div>
              <div style={{fontSize:10,color:"#1E8A32",textTransform:"uppercase",letterSpacing:1}}>{session.poste} · {session.matricule}</div>
              <div style={{fontSize:9,color:"#2F6FDE",marginTop:3}}>📍 {accessScopeLabel()}</div>
            </div>
          )}
          {maPhotoRond(isMobile?30:40)}
          {session&&(
            <button onClick={()=>setNotifOuvert(o=>!o)} style={{position:"relative",padding:isMobile?"7px 9px":"4px 10px",flexShrink:0,background:"transparent",border:"1px solid #CBD5D0",borderRadius:4,color:notifNonLues>0?"#0F1F17":"#5B6B62",cursor:"pointer",display:"inline-flex",alignItems:"center"}} title="Notifications" aria-label="Notifications">
              <Bell size={14}/>
              {notifNonLues>0&&(
                <span style={{position:"absolute",top:-8,right:-8,minWidth:18,height:18,padding:"0 4px",borderRadius:9,background:"#D6453D",color:"#FFFFFF",border:"2px solid #E3E9E6",fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {notifNonLues>99?"99+":notifNonLues}
                </span>
              )}
            </button>
          )}
          {can("chat")&&(
            <button onClick={openChat} style={{position:"relative",padding:isMobile?"7px 9px":"4px 10px",flexShrink:0,background:"transparent",border:"1px solid #CBD5D0",borderRadius:4,color:"#5B6B62",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}} title="Chat d'équipe">
              <MessageCircle size={14}/>
              {chatUnread&&(
                <span style={{position:"absolute",top:-8,right:-8,minWidth:18,height:18,padding:"0 4px",borderRadius:9,background:"#D6453D",color:"#FFFFFF",border:"2px solid #E3E9E6",fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {chatUnreadTotal>99?"99+":chatUnreadTotal}
                </span>
              )}
            </button>
          )}
          {mesDemandesSignature.length>0&&(
            <button onClick={()=>setSignatureRequestsView(true)} style={{position:"relative",padding:isMobile?"7px 9px":"4px 10px",flexShrink:0,background:"transparent",border:"1px solid #CBD5D0",borderRadius:4,color:"#5B6B62",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}} title="Documents à signer">
              <FileEdit size={14}/>
              <span style={{position:"absolute",top:-8,right:-8,minWidth:18,height:18,padding:"0 4px",borderRadius:9,background:"#2F9E3F",color:"#FFFFFF",border:"2px solid #E3E9E6",fontSize:9,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {mesDemandesSignature.length>99?"99+":mesDemandesSignature.length}
              </span>
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-icon"
            onClick={resetApplication}
            disabled={resettingApp}
            style={{padding:isMobile?"7px 9px":"4px 10px",flexShrink:0}}
            title="Réinitialiser et actualiser sans se déconnecter"
          >
            <RefreshCw size={14} style={{animation:resettingApp?"azb-spin .8s linear infinite":"none"}}/>
            {!isMobile&&(resettingApp?" Actualisation…":" Réinitialiser")}
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>setShowProfile(true)} style={{padding:isMobile?"7px 9px":"4px 10px",flexShrink:0}} title="Profil"><User size={14}/>{!isMobile&&" Profil"}</button>
          <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={logout} style={{padding:isMobile?"7px 9px":"4px 10px",flexShrink:0}} title="Déconnexion"><LogOut size={14}/>{!isMobile&&" Déconnexion"}</button>
        </div>
      </header>

      {/* BARRE D'ONGLETS EN BAS — téléphone uniquement */}
      {isMobile&&page!=="chat"&&(()=>{
        const dispo = navItemsAvecActivite.map(n=>n.id);
        const prefer = ["tableau","ventes","factures","stock","clients","mon_activite","proformas","paiements"];
        const tabs = prefer.filter(id=>dispo.includes(id)).slice(0,4);
        const labelOf = (id)=>({tableau:"Accueil",ventes:"Ventes",factures:"Factures",stock:"Stock",clients:"Clients",mon_activite:"Activité",proformas:"Proformas",paiements:"Preuves"}[id]||id);
        const nbRetard = factures.filter(f=>{ if(resteFacture(f)<=0) return false; const e=endOfLocalDay(f.date_echeance); return e && e<new Date(); }).length;
        const isOn = (id)=> page===id || (id==="factures"&&page==="nouvelle-facture") || (id==="proformas"&&page==="nouvelle-proforma");
        const tabsOn = tabs.some(isOn);
        return (
          <nav className="az-tabbar" aria-label="Navigation rapide">
            <style>{`.toast{bottom:calc(env(safe-area-inset-bottom) + 104px)!important}
.az-tabbar{position:fixed;left:12px;right:12px;bottom:calc(10px + env(safe-area-inset-bottom));z-index:90;display:flex;align-items:center;padding:7px 8px;border-radius:36px;background:rgba(58,66,62,.72);-webkit-backdrop-filter:blur(22px) saturate(1.8);backdrop-filter:blur(22px) saturate(1.8);border:1px solid rgba(255,255,255,.24);box-shadow:0 12px 32px rgba(15,31,23,.30),inset 0 1px 0 rgba(255,255,255,.28)}
@supports not ((backdrop-filter:blur(1px)) or (-webkit-backdrop-filter:blur(1px))){.az-tabbar{background:rgba(52,60,56,.95)}}
.az-tab{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;font-family:inherit;font-size:10.5px;font-weight:600;color:rgba(255,255,255,.92);padding:6px 2px 5px;background:none;border:none;border-radius:28px;cursor:pointer;position:relative;min-width:0;-webkit-tap-highlight-color:transparent;transition:background .15s}
.az-ti{width:34px;height:28px;display:flex;align-items:center;justify-content:center}
.az-ti svg{width:22px;height:22px;stroke-width:1.9}
.az-tab.on{background:rgba(255,255,255,.22)}
.az-tab.mid{flex:0 0 auto;padding:0 6px}
.az-tab.mid.on{background:none}
.az-tab.mid .az-ti{width:46px;height:46px;border-radius:50%;background:#2F9E3F;box-shadow:0 4px 12px rgba(30,122,45,.45)}
.az-tab.mid.on .az-ti{box-shadow:0 0 0 3px rgba(255,255,255,.55),0 4px 12px rgba(30,122,45,.45)}
.az-tab.mid .az-ti svg{width:24px;height:24px}
.az-tb{position:absolute;top:1px;left:calc(50% + 6px);min-width:17px;height:17px;border-radius:9px;background:#E5484D;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 4px;box-sizing:border-box;border:1.5px solid rgba(255,255,255,.9)}
.az-tab.mid .az-tb{top:-2px;left:calc(50% + 12px)}`}</style>
            {tabs.map((id,i)=>(
              <button key={id} type="button" className={"az-tab"+(isOn(id)?" on":"")+((tabs.length===4&&i===2)?" mid":"")} onClick={()=>goToNav(id)}>
                <span className="az-ti">{renderNavIcon(id)}</span>
                <span>{labelOf(id)}</span>
                {id==="factures"&&nbRetard>0&&<span className="az-tb" title={nbRetard+" facture(s) en retard"}>{nbRetard>99?"99+":nbRetard}</span>}
              </button>
            ))}
            <button type="button" className={"az-tab"+(!tabsOn?" on":"")} onClick={()=>setMobileMenuOpen(true)}>
              <span className="az-ti"><Menu size={17}/></span>
              <span>Menu</span>
            </button>
          </nav>
        );
      })()}

      {/* MENU MOBILE — tiroir latéral */}
      {isMobile&&mobileMenuOpen&&(
        <div onClick={()=>setMobileMenuOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.65)",zIndex:199}}>
          <nav onClick={e=>e.stopPropagation()} style={{position:"fixed",top:0,left:0,bottom:0,width:"78%",maxWidth:290,background:"#FFFFFF",borderRight:"1px solid #E3E9E6",zIndex:200,display:"flex",flexDirection:"column",padding:"14px 10px",overflowY:"auto",boxShadow:"4px 0 24px rgba(0,0,0,.5)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 8px 16px 8px",borderBottom:"1px solid #E3E9E6",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <img src={LOGO_UI} alt="Azongbio" style={{height:26}} />
                {maPhotoRond(34)}
                <div style={{fontSize:12,color:"#0F1F17"}}>{session.nom.split(" ")[0]}</div>
              </div>
              <button onClick={()=>setMobileMenuOpen(false)} style={{background:"none",border:"none",color:"#5B6B62",cursor:"pointer",padding:6}}><X size={20}/></button>
            </div>
            {groupNavItems(navItemsAvecActivite).map(g=>(
              <div key={g.cat}>
                {CAT_LABELS[g.cat]&&<div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#8A978F",padding:"14px 12px 6px"}}>{CAT_LABELS[g.cat]}</div>}
                {g.items.map(n=>(
                  <div key={n.id}
                    className={"ni"+((page===n.id||(n.id==="factures"&&(page==="nouvelle-facture"))||(n.id==="proformas"&&page==="nouvelle-proforma"))?" active":"")}
                    onClick={()=>{ goToNav(n.id); setMobileMenuOpen(false); }}
                    style={{padding:"13px 12px",fontSize:14,gap:12}}>
                    <span style={{width:20,display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>
                      {renderNavIcon(n.id)}
                      {n.id==="messagerie"&&mailReminder&&<span style={{position:"absolute",top:-2,right:-2,width:8,height:8,borderRadius:"50%",background:"#D6453D",border:"1px solid #E3E9E6"}}/>}
                      {n.id==="salaires"&&bulletinsAConfirmerListe().a.length>0&&<span title="Bulletins à confirmer" style={{position:"absolute",top:-2,right:-2,width:8,height:8,borderRadius:"50%",background:"#D6453D",border:"1px solid #E3E9E6"}}/>}
                      {n.id==="caisse"&&caisseMvts.some(m=>m.a_expliquer&&!m.explication)&&<span title="Écart de caisse à expliquer" style={{position:"absolute",top:-2,right:-2,width:8,height:8,borderRadius:"50%",background:"#E8A33D",border:"1px solid #E3E9E6"}}/>}
                      {n.id==="demandes_depense"&&demandeAlerte()&&<span title="Demande de dépense à traiter" style={{position:"absolute",top:-2,right:-2,width:8,height:8,borderRadius:"50%",background:"#D6453D",border:"1px solid #E3E9E6"}}/>}
                      {n.id==="annonces"&&annonceNonLue()&&<span title="Annonce à lire" style={{position:"absolute",top:-2,right:-2,width:8,height:8,borderRadius:"50%",background:"#D6453D",border:"1px solid #E3E9E6"}}/>}
                    </span>
                    <span>{n.label}</span>
                  </div>
                ))}
              </div>
            ))}
          </nav>
        </div>
      )}

      <div style={{display:"flex",flex:1,overflow:"clip"}}>
        {/* SIDEBAR — desktop & tablette uniquement (le mobile utilise le tiroir latéral) */}
        {!isMobile&&(
          <nav style={{width:210,background:"#FFFFFF",borderRight:"1px solid #E3E9E6",padding:"16px 8px",display:"flex",flexDirection:"column",gap:2,flexShrink:0,overflowY:"auto",position:"sticky",top:"var(--az-hh,63px)",alignSelf:"flex-start",height:"calc(100vh - var(--az-hh,63px))",boxSizing:"border-box"}}>
            {groupNavItems(navItemsAvecActivite).map(g=>(
              <div key={g.cat}>
                {CAT_LABELS[g.cat]&&<div style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.5,color:"#8A978F",padding:"14px 13px 5px"}}>{CAT_LABELS[g.cat]}</div>}
                {g.items.map(n=>(
                  <div key={n.id} className={"ni"+((page===n.id||(n.id==="factures"&&(page==="nouvelle-facture"))||(n.id==="proformas"&&page==="nouvelle-proforma"))?" active":"")}
                    onClick={()=>goToNav(n.id)}
                    style={{padding:"9px 13px",gap:9,fontSize:12,alignItems:"center"}}>
                    <span style={{width:18,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,position:"relative"}}>
                      {renderNavIcon(n.id)}
                      {n.id==="messagerie"&&mailReminder&&<span style={{position:"absolute",top:-2,right:-2,width:7,height:7,borderRadius:"50%",background:"#D6453D",border:"1px solid #E3E9E6"}}/>}
                      {n.id==="salaires"&&bulletinsAConfirmerListe().a.length>0&&<span title="Bulletins à confirmer" style={{position:"absolute",top:-2,right:-2,width:7,height:7,borderRadius:"50%",background:"#D6453D",border:"1px solid #E3E9E6"}}/>}
                      {n.id==="caisse"&&caisseMvts.some(m=>m.a_expliquer&&!m.explication)&&<span title="Écart de caisse à expliquer" style={{position:"absolute",top:-2,right:-2,width:7,height:7,borderRadius:"50%",background:"#E8A33D",border:"1px solid #E3E9E6"}}/>}
                      {n.id==="demandes_depense"&&demandeAlerte()&&<span title="Demande de dépense à traiter" style={{position:"absolute",top:-2,right:-2,width:7,height:7,borderRadius:"50%",background:"#D6453D",border:"1px solid #E3E9E6"}}/>}
                      {n.id==="annonces"&&annonceNonLue()&&<span title="Annonce à lire" style={{position:"absolute",top:-2,right:-2,width:7,height:7,borderRadius:"50%",background:"#D6453D",border:"1px solid #E3E9E6"}}/>}
                    </span>
                    <span style={{fontSize:12,lineHeight:1.2}}>{n.label}</span>
                  </div>
                ))}
              </div>
            ))}
          </nav>
        )}

        <main style={{flex:1,overflowY:"auto",padding:isMobile?(page==="chat"?"14px 12px 24px 12px":"14px 12px 120px 12px"):"22px",minWidth:0}}>

          {/* TABLEAU DE BORD */}
          {page==="tableau"&&(()=>{
            const nf=(n)=>new Intl.NumberFormat("fr-FR").format(Math.round(n||0));
            const now=new Date();
            const monthKey=(d)=>d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0");
            const mois6=Array.from({length:6},(_,i)=>{const d=new Date(now.getFullYear(),now.getMonth()-(5-i),1);return {key:monthKey(d),label:d.toLocaleDateString("fr-FR",{month:"short"}).replace(".",""),total:0};});
            ventes.forEach(v=>{ if(!v.date) return; const m=mois6.find(x=>x.key===String(v.date).slice(0,7)); if(m) m.total+=Number(v.montant)||0; });
            const moisCourant=mois6[5].total, moisPrec=mois6[4].total;
            const variation=moisPrec>0?Math.round(((moisCourant-moisPrec)/moisPrec)*100):null;
            const maxMois=Math.max(...mois6.map(m=>m.total),1);
            const spark=mois6.map((m,i)=>(i*(300/5))+","+(34-(m.total/maxMois)*30)).join(" ");
            const alertesStock = can("stock") ? produits.filter(p=>stockCentral(p)<=p.seuil_alerte) : [];
            const alertesRetard = can("factures") ? facturesEnRetard : [];
            const alertesObjectifs = can("objectifs") ? objectifsEnDanger : [];
            const totalAlertes = alertesStock.length + alertesRetard.length + alertesObjectifs.length;
            const tile=(Icon,color,tint,size=34)=>(<span style={{width:size,height:size,borderRadius:10,background:tint,color,display:"grid",placeItems:"center",flexShrink:0}}><Icon size={Math.round(size*0.5)}/></span>);
            const metric=(label,value,unit,color,Icon,tint,sub,title)=>(
              <div className="card dk" title={title}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                  <span style={{fontSize:12.5,fontWeight:500,color:"#4B5A52"}}>{label}</span>
                  {tile(Icon,color,tint)}
                </div>
                <div style={{fontSize:isMobile?20:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",margin:"12px 0 4px",lineHeight:1.1,whiteSpace:"nowrap"}}>{value}{unit&&<span style={{fontSize:12,fontWeight:600,color:"#8A978F",marginLeft:5}}>{unit}</span>}</div>
                {sub&&<div style={{fontSize:12,fontWeight:600,color}}>{sub}</div>}
              </div>
            );
            const mini=(label,value,unit,color,Icon,tint,title)=>(
              <div className="card" title={title} style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px"}}>
                {tile(Icon,color,tint,38)}
                <div style={{minWidth:0}}>
                  <div style={{fontSize:12.5,fontWeight:500,color:"#4B5A52"}}>{label}</div>
                  <div style={{fontSize:isMobile?16:19,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginTop:2,whiteSpace:"nowrap"}}>{value}{unit&&<span style={{fontSize:11.5,fontWeight:600,color:"#8A978F",marginLeft:4}}>{unit}</span>}</div>
                </div>
              </div>
            );
            const alertRow=(key,Icon,color,tint,titre,detail,onClick)=>(
              <div key={key} onClick={onClick} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 0",borderTop:"1px solid #EDF1EF",cursor:onClick?"pointer":"default"}}>
                {tile(Icon,color,tint)}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13.5,fontWeight:600,color:"#0F1F17",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{titre}</div>
                  <div style={{fontSize:12,color:"#5B6B62"}}>{detail}</div>
                </div>
              </div>
            );
            return (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap",marginBottom:20}}>
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  {maPhotoRond(isMobile?44:52)}
                  <div>
                    <div style={{fontSize:isMobile?22:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>{salutCourante}, {maCivilite?maCivilite+" ":""}{session.nom.split(" ")[0]} 👋</div>
                    <div style={{fontSize:13,color:"#5B6B62",marginTop:2}}>{now.toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
                  </div>
                </div>
                <div style={{padding:"7px 14px",borderRadius:999,background:"#E8F5EA",fontSize:12.5,fontWeight:600,color:"#1E7A2D"}}>
                  📍 Vue : {accessScopeLabel()}
                </div>
              </div>
              {!canSeeAllCities()&&!sessionVille()&&(
                <div className="card" style={{marginBottom:18,borderColor:"#E8C37A",background:"#FFF4DD",color:"#8A5A00"}}>
                  Votre ville n'est pas encore attribuée. Demandez au Directeur de renseigner votre ville dans Employés afin d'afficher vos données locales.
                </div>
              )}

            {(isDirecteur()||can("livraisons"))&&livraisons.some(l=>l.statut==="En transit")&&(()=>{
              const att = livraisons.filter(l=>l.statut==="En transit");
              return (
                <div className="card" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",marginBottom:14,borderColor:"#BFE3C6",background:"#F6FBF7"}}>
                  <div style={{fontSize:13,color:"#0F1F17",minWidth:0}}>
                    <strong>🚚 {att.length} livraison{att.length>1?"s":""} à réceptionner</strong>
                    <div style={{fontSize:11,color:"#5B6B62",marginTop:3}}>{att.slice(0,3).map(l=>l.numero+" → "+(l.depot_nom||l.ville)+(l.receptionnaire_prevu?" (par "+l.receptionnaire_prevu+")":"")).join(" · ")}{att.length>3?" …":""}</div>
                  </div>
                  <button className="btn btn-gold btn-sm" onClick={()=>setPage("livraisons")}>Voir et confirmer</button>
                </div>
              );
            })()}
            <div className="dash-top">
                {can("ventes")&&(
                  <div className="card dk dk-hero">
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{fontSize:12.5,fontWeight:500,opacity:.9}}>Chiffre d'affaires</span>
                      <span style={{width:34,height:34,borderRadius:10,background:"rgba(255,255,255,.2)",display:"grid",placeItems:"center"}}><TrendingUp size={17}/></span>
                    </div>
                    <div style={{fontSize:isMobile?26:28,fontWeight:700,letterSpacing:"-.02em",margin:"12px 0 4px",lineHeight:1.1,whiteSpace:"nowrap"}}>{nf(totalVentes)}<span style={{fontSize:12,fontWeight:600,opacity:.75,marginLeft:5}}>FCFA</span></div>
                    <div style={{fontSize:12,fontWeight:600,opacity:.92}}>Ce mois : {nf(moisCourant)} FCFA{variation!==null&&<span> · {variation>=0?"▲":"▼"} {Math.abs(variation)} %</span>}</div>
                    <svg viewBox="0 0 300 38" preserveAspectRatio="none" style={{width:"100%",height:34,marginTop:10,opacity:.9}}><polyline points={spark} fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                )}
                {can("factures")&&metric("Encaissements réels",nf(encaissements),"FCFA","#1E7A2D",Wallet,"#E8F5EA","Paiements validés")}
                {can("factures")&&creances>0&&metric("Créances clients",nf(creances),"FCFA","#D6453D",Clock,"#FDECEA",facturesEnRetard.length>0?facturesEnRetard.length+" facture(s) en retard":null)}
                {can("comptabilite")&&can("factures")&&metric("Trésorerie réelle",nf(tresorerie),"FCFA",tresorerie>=0?"#2F6FDE":"#D6453D",CreditCard,tresorerie>=0?"#E9F0FD":"#FDECEA","Encaissé moins dépenses","Ce qui est vraiment encaissé, moins les dépenses — différent du bénéfice comptable qui inclut les factures pas encore payées")}
              </div>

              <div className="dash-mini" style={{marginTop:0}}>
                {can("comptabilite")&&mini("Dépenses",nf(totalDepenses),"FCFA","#D6453D",Wallet,"#FDECEA")}
                {can("comptabilite")&&mini("Bénéfice d'exploitation",nf(benefice),"FCFA",benefice>=0?"#1E7A2D":"#D6453D",TrendingUp,benefice>=0?"#E8F5EA":"#FDECEA","Ventes moins dépenses de fonctionnement, avant amortissement des immobilisations")}
                {can("rentabilite")&&mini("Marge globale estimée",margeGlobalePct,"%","#1E7A2D",BarChart3,"#E8F5EA")}
                {can("stock")&&mini("Stock total",nf(stockTotalVisible),"btl","#2F6FDE",Package,"#E9F0FD")}
                {can("factures")&&mini("Factures payées",facPayees+" / "+factures.length,"","#7A4FD6",FileText,"#F0EBFB")}
              </div>

              <div className="dash-two">
                {can("ventes")&&(
                  <div className="card" style={{padding:"20px 22px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                      <div>
                        <div style={{fontSize:16,fontWeight:700,letterSpacing:"-.01em"}}>Ventes des 6 derniers mois</div>
                        <div style={{fontSize:12.5,color:"#5B6B62",marginTop:2}}>En FCFA</div>
                      </div>
                      {variation!==null&&<span style={{padding:"4px 10px",borderRadius:999,fontSize:12,fontWeight:600,background:variation>=0?"#E8F5EA":"#FDECEA",color:variation>=0?"#1E7A2D":"#D6453D"}}>{variation>=0?"▲":"▼"} {Math.abs(variation)} % vs mois dernier</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"flex-end",gap:isMobile?8:18,height:190,marginTop:18,padding:"0 4px",borderBottom:"1px solid #E6EBE8"}}>
                      {mois6.map((m,i)=>{
                        const h=Math.max(Math.round((m.total/maxMois)*140),m.total>0?6:2);
                        const cur=i===5;
                        return (
                          <div key={m.key} title={m.label+" : "+nf(m.total)+" FCFA"} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",height:"100%",minWidth:0}}>
                            <div style={{fontSize:11,fontWeight:600,color:cur?"#1E7A2D":"#8A978F",marginBottom:5,whiteSpace:"nowrap"}}>{m.total>0?(m.total>=1000000?(m.total/1000000).toFixed(1).replace(".",",")+" M":Math.round(m.total/1000)+" k"):"—"}</div>
                            <div style={{width:"100%",maxWidth:56,height:h,borderRadius:"8px 8px 4px 4px",background:cur?"#2F9E3F":"#CFE9D3"}}/>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{display:"flex",gap:isMobile?8:18,padding:"8px 4px 0"}}>
                      {mois6.map((m,i)=><div key={m.key} style={{flex:1,textAlign:"center",fontSize:12,color:i===5?"#1E7A2D":"#5B6B62",fontWeight:i===5?700:500}}>{m.label}</div>)}
                    </div>
                  </div>
                )}

                <div className="card" style={{padding:"20px 22px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <div style={{fontSize:16,fontWeight:700,letterSpacing:"-.01em"}}>À traiter</div>
                    <span style={{padding:"4px 10px",borderRadius:999,fontSize:12,fontWeight:600,background:totalAlertes>0?"#FDECEA":"#E8F5EA",color:totalAlertes>0?"#D6453D":"#1E7A2D"}}>{totalAlertes>0?totalAlertes+" alerte(s)":"Tout est à jour"}</span>
                  </div>
                  {totalAlertes===0&&<div style={{fontSize:13,color:"#5B6B62",padding:"14px 0"}}>Aucune facture en retard, aucun stock faible, aucun objectif en retard.</div>}
                  {alertesRetard.slice(0,5).map(f=>{
                    const jours = Math.floor((Date.now()-(endOfLocalDay(f.date_echeance)?.getTime()||Date.now()))/(1000*60*60*24));
                    return alertRow("r"+f.id,Clock,"#D6453D","#FDECEA","Facture "+f.numero+" en retard",f.client_nom+" · "+jours+" jour(s)",()=>setPage("factures"));
                  })}
                  {alertesRetard.length>5&&<div style={{fontSize:12,color:"#5B6B62",padding:"8px 0",borderTop:"1px solid #EDF1EF"}}>+ {alertesRetard.length-5} autre(s) facture(s) en retard</div>}
                  {alertesStock.map(p=>alertRow("s"+p.id,Package,"#C7861A","#FFF4DD","Stock faible : "+p.nom,stockCentral(p)+" btl · seuil "+p.seuil_alerte,()=>setPage("stock")))}
                  {alertesObjectifs.map(e=>alertRow("o"+e.id,Target,"#2F6FDE","#E9F0FD","Objectif à vérifier",e.nom,()=>setPage("objectifs")))}
                </div>
              </div>

              {can("ventes")&&(
                <div className="card" style={{marginTop:16,padding:"20px 22px"}}>
                  <div style={{fontSize:16,fontWeight:700,letterSpacing:"-.01em",marginBottom:6}}>Dernières ventes</div>
                  {ventes.slice(0,5).map(v=>(
                    <div key={v.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,padding:"11px 0",borderTop:"1px solid #EDF1EF"}}>
                      <div style={{minWidth:0}}><div style={{fontSize:13.5,fontWeight:600}}>{v.client}</div><div style={{fontSize:12,color:"#5B6B62"}}>{v.produit} · {v.qte} btl · {v.employe}</div></div>
                      <div style={{fontSize:13.5,color:"#1E7A2D",fontWeight:700,whiteSpace:"nowrap"}}>+{FCFA(v.montant)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })()}

          
{/* VENTES */}
          {page==="ventes"&&(isDirecteur()||can("ventes"))&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Ventes</div>
              </div>
              <div style={{fontSize:12,color:"#5B6B62",marginBottom:16}}>Consultation uniquement — les ventes sont enregistrées automatiquement à la création d'une facture.</div>
              <div className="card scr">
                <table className="tbl">
                  <thead><tr><th>Date</th><th>Ville</th><th>Client</th><th>Produit</th><th>Qté</th><th>Montant</th><th>Employé</th></tr></thead>
                  <tbody>{ventes.map(v=>(
                    <tr key={v.id}>
                      <td style={{color:"#5B6B62"}}>{new Date(v.date).toLocaleDateString("fr-FR")}</td>
                      <td><span className="badge bg">{v.ville||"Non définie"}</span></td>
                      <td>{v.client}</td><td><span className="badge bg2">{v.produit}</span></td>
                      <td>{v.qte} btl</td><td style={{color:"#1E7A2D",fontWeight:600}}>{FCFA(v.montant)}</td>
                      <td style={{color:"#5B6B62",fontSize:12}}>{v.employe||"—"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div style={{marginTop:12,textAlign:"right",color:"#5B6B62",fontSize:13}}>Total : <span style={{color:"#1E8A32",fontSize:17,fontWeight:700}}>{FCFA(totalVentes)}</span></div>
            </div>
          )}

          {/* STOCK */}
          {page==="stock"&&(isDirecteur()||can("stock")||can("produits_admin"))&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
                <div><div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Stock & Produits</div>
                <div style={{fontSize:11,color:"#8A978F",marginTop:3}}>Stock du magasin central (Dschang). Le stock de chaque ville se voit dans Dépôts et livraisons.</div></div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {canEdit()&&<button className="btn btn-ghost" onClick={()=>setShowIngredientsPanel(v=>!v)}>{showIngredientsPanel?"Masquer les ingrédients":"Ingrédients"}</button>}
                  <button className="btn btn-gold" onClick={()=>openModal("produit",{})}>+ Nouveau produit</button>
                </div>
              </div>

              {showIngredientsPanel&&(canEdit())&&(
                <div className="card scr" style={{marginBottom:20}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
                    <div>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62"}}>Ingrédients — prix d'achat de référence</div>
                      <div style={{fontSize:11,color:"#8A978F",marginTop:2}}>Sert au calcul automatique du coût de revient via la recette de chaque produit (bouton « Recette »).</div>
                    </div>
                    <button className="btn btn-gold btn-sm" onClick={()=>openModal("ingredient",{unite:"kg"})}>+ Ajouter</button>
                  </div>
                  <table className="tbl">
                    <thead><tr><th>Nom</th><th>Unité</th><th>Prix unitaire</th><th>Actions</th></tr></thead>
                    <tbody>{ingredients.map(i=>(
                      <tr key={i.id}>
                        <td style={{fontWeight:600}}>{i.nom}</td>
                        <td style={{color:"#5B6B62"}}>{i.unite}</td>
                        <td>{FCFA(i.prix_unitaire)} / {i.unite}</td>
                        <td style={{display:"flex",gap:5}}>
                          <button className="btn btn-ghost btn-sm" onClick={()=>openModal("ingredient",i)}>✏</button>
                          {isDirecteur()&&<button className="btn btn-red btn-sm" onClick={()=>deleteIngredient(i.id)}><Trash2 size={13}/></button>}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                  {ingredients.length===0&&<div style={{color:"#5B6B62",padding:12}}>Aucun ingrédient enregistré.</div>}
                </div>
              )}

              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit,minmax(270px,1fr))",gap:isMobile?10:16}}>
                {produits.map(p=>{
                  const stk=stockCentral(p);
                  const pct=Math.min((stk/Math.max(stk,p.seuil_alerte*3))*100,100);
                  const col=stk>p.seuil_alerte?"#1E7A2D":stk>0?"#1E8A32":"#D6453D";
                  const statut=stk>p.seuil_alerte?"✓ Suffisant":stk>0?"⚠ Faible":"✗ Épuisé";
                  const coutEff = coutRevientEffectif(p);
                  const aRecette = recettes.some(r=>String(r.produit_id)===String(p.id));
                  const marge = p.prix - coutEff;
                  return (
                    <div key={p.id} className="card" style={{borderColor:stk<=p.seuil_alerte?"rgba(192,57,43,.3)":"#E3E9E6"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:12,alignItems:"flex-start"}}>
                        <div>
                          <div style={{fontSize:14,fontWeight:700,color:"#0F1F17"}}>{p.nom}{p.code&&<span style={{marginLeft:8,fontSize:11,fontWeight:700,color:"#5B6B62",background:"#EEF2EF",borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap"}}>{p.code}</span>}</div>
                        </div>
                        <span className="badge" style={{background:col+"22",color:col,border:"1px solid "+col}}>{statut}</span>
                      </div>
                      <div style={{fontSize:36,color:col,fontWeight:700,marginBottom:4}}>{NUM(stk)}</div>
                      <div style={{fontSize:11,color:"#5B6B62",marginBottom:10}}>bouteilles au magasin central · Seuil : {p.seuil_alerte}</div>
                      <div style={{height:8,background:"#E3E9E6",borderRadius:4,marginBottom:12,overflow:"hidden"}}>
                        <div style={{height:"100%",width:pct+"%",background:col,borderRadius:4}} />
                      </div>
                      <div style={{fontSize:11,color:"#5B6B62",marginBottom:10}}>
                        Prix : <strong style={{color:"#1E8A32"}}>{FCFA(p.prix)}</strong> · Valeur stock : <strong style={{color:"#2F6FDE"}}>{FCFA(stk*p.prix)}</strong>
                        {coutEff>0&&<><br/>Coût : <strong>{FCFA(coutEff)}</strong>{aRecette&&<span className="badge bg2" style={{marginLeft:6,fontSize:9}}>recette</span>} · Marge : <strong style={{color:marge>=0?"#1E7A2D":"#D6453D"}}>{FCFA(marge)}</strong></>}
                      </div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {(canEdit())&&<button className="btn btn-ghost btn-sm" onClick={()=>openModal("produit",p)}>✏ Modifier</button>}
                        {(canEdit())&&<button className="btn btn-ghost btn-sm" onClick={()=>{
                          const initial={};
                          recettes.filter(r=>String(r.produit_id)===String(p.id)).forEach(r=>{ initial[r.ingredient_id]=String(r.quantite_par_bouteille); });
                          setRecetteQtes(initial);
                          setRecetteEditProduit(p);
                        }}>🧪 Recette</button>}
                        {(canEdit())&&<button className="btn btn-gold btn-sm btn-icon" onClick={()=>{ setForm({produitNom:p.nom, qte:"", note:""}); setModal("ajout_stock"); }}>+ Stock</button>}
                        {(canEdit())&&<button className="btn btn-ghost btn-sm btn-icon" style={{borderColor:"rgba(233,162,157,0.40)",color:"#D6453D"}} onClick={()=>{ setForm({produitNom:p.nom, lotId:"", qte:"", clientNom:"", factureId:"", raison:""}); setModal("avarie"); }}><AlertTriangle size={12}/> Avarie</button>}
                        {isDirecteur()&&<button className="btn btn-red btn-sm" onClick={()=>deleteProduit(p.id)}><Trash2 size={13}/></button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* PRODUCTION */}
          {page==="production"&&(isDirecteur()||can("production"))&&(()=>{
            const startDate=new Date((productionDates.debut||today())+"T00:00:00");
            const endDate=new Date((productionDates.fin||today())+"T23:59:59");
            const filteredLots=lots.filter(l=>{
              const d=parseDateSafe(l.date);
              const periodOk=d&&d>=startDate&&d<=endDate;
              const q=productionSearch.trim().toLowerCase();
              const searchOk=!q||[
                l.numero,l.produit,l.operateur,l.statut,l.notes
              ].filter(Boolean).join(" ").toLowerCase().includes(q);
              return periodOk&&searchOk;
            });

            const totalProduit=filteredLots.reduce((s,l)=>s+toFiniteNumber(l.quantite),0);
            const totalLots=filteredLots.length;
            const moyenneLot=totalLots?Math.round(totalProduit/totalLots):0;
            const joursActifs=new Set(filteredLots.map(l=>String(l.date||"").slice(0,10))).size;
            const moyenneJour=joursActifs?Math.round(totalProduit/joursActifs):0;

            const byProduct={};
            filteredLots.forEach(l=>{
              const key=l.produit||"Produit non défini";
              byProduct[key]=(byProduct[key]||0)+toFiniteNumber(l.quantite);
            });
            const productRows=Object.entries(byProduct).sort((a,b)=>b[1]-a[1]);
            const maxProduct=Math.max(1,...productRows.map(([,v])=>v));

            const byStatus={};
            filteredLots.forEach(l=>{
              const key=l.statut||"Non défini";
              byStatus[key]=(byStatus[key]||0)+toFiniteNumber(l.quantite);
            });
            const statusRows=Object.entries(byStatus).sort((a,b)=>b[1]-a[1]);
            const statusPalette=["#1E8A32","#1E7A2D","#5B7A8A","#D6453D","#7A4FD6","#C7861A"];
            let statusAcc=0;
            const statusGradient=statusRows.length&&totalProduit>0
              ? `conic-gradient(${statusRows.map(([label,value],index)=>{
                  const begin=statusAcc;
                  statusAcc+=value/totalProduit*100;
                  return `${statusPalette[index%statusPalette.length]} ${begin}% ${statusAcc}%`;
                }).join(",")})`
              : "#8A978F";

            const monthMap={};
            filteredLots.forEach(l=>{
              const d=parseDateSafe(l.date);
              if(!d)return;
              const key=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
              monthMap[key]=(monthMap[key]||0)+toFiniteNumber(l.quantite);
            });
            const monthRows=Object.entries(monthMap).sort((a,b)=>a[0].localeCompare(b[0]));
            const maxMonth=Math.max(1,...monthRows.map(([,v])=>v));

            const setPreset=type=>{
              const now=new Date();
              const yy=now.getFullYear(),mm=now.getMonth();
              let debut,fin;
              if(type==="mois"){
                debut=new Date(yy,mm,1);fin=now;
              }else if(type==="mois_prec"){
                debut=new Date(yy,mm-1,1);fin=new Date(yy,mm,0);
              }else if(type==="trimestre"){
                const q=Math.floor(mm/3);
                debut=new Date(yy,q*3,1);fin=now;
              }else if(type==="annee"){
                debut=new Date(yy,0,1);fin=now;
              }else{
                debut=new Date(yy-1,0,1);fin=new Date(yy-1,11,31);
              }
              setProductionDates({debut:localISODate(debut),fin:localISODate(fin)});
            };

            return (
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:18,flexWrap:"wrap"}}>
                  <div>
                    <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Pilotage de la production</div>
                    <div style={{fontSize:12,color:"#5B6B62",marginTop:4}}>
                      Analyse professionnelle des volumes produits sur la période sélectionnée.
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button className="btn btn-ghost" onClick={()=>openModal("receptionBouteille",{date:today()})}>+ Réception bouteilles</button>
                    <button className="btn btn-gold" onClick={()=>openModal("lot")}>+ Nouveau lot</button>
                  </div>
                </div>

                <div className="card" style={{marginBottom:18}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:12}}>
                    Contrôle bouteilles vides — entrées usine vs utilisées en production
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
                    {(()=>{ const dispo=stockBouteillesVides(); return (
                      <div style={{padding:"10px 12px",background:"#FFFFFF",border:"1px solid #E3E9E6",borderRadius:6}}>
                        <div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Toutes saveurs confondues</div>
                        <div style={{fontSize:17,fontWeight:700,color:dispo<0?"#D6453D":"#0F1F17"}}>{NUM(dispo)} btl</div>
                        <div style={{fontSize:9,color:"#2F6FDE",marginTop:2}}>bouteilles vides disponibles</div>
                      </div>
                    ); })()}
                  </div>
                </div>

                <div className="card scr" style={{marginBottom:18}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:4}}>Bilan mensuel des bouteilles vides (avec report)</div>
                  <div style={{fontSize:11,color:"#8A978F",marginBottom:10}}>Le reste à la fin d'un mois devient la disponibilité de début du mois suivant. Entrées = réceptions saisies dans l'appli ; sorties = bouteilles utilisées en production.</div>
                  <table className="tbl">
                    <thead><tr><th>Mois</th><th>Disponible début</th><th>Entrées</th><th>Sorties</th><th>Reste fin de mois</th></tr></thead>
                    <tbody>{bilanBouteillesParMois().slice().reverse().map(r=>(
                      <tr key={r.mois}>
                        <td>{new Date(r.mois+"-01").toLocaleDateString("fr-FR",{month:"long",year:"numeric"})}</td>
                        <td>{NUM(r.debut)}</td>
                        <td style={{color:"#1E7A2D"}}>+{NUM(r.entrees)}</td>
                        <td style={{color:"#D6453D"}}>-{NUM(r.sorties)}</td>
                        <td style={{fontWeight:700,color:r.fin<0?"#D6453D":"#0F1F17"}}>{NUM(r.fin)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>

                <div className="card" style={{marginBottom:18}}>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
                    {[
                      ["Ce mois","mois"],["Mois précédent","mois_prec"],
                      ["Ce trimestre","trimestre"],["Cette année","annee"],
                      ["Année précédente","annee_prec"]
                    ].map(([label,type])=>(
                      <button key={type} type="button" className="btn btn-ghost btn-sm" onClick={()=>setPreset(type)}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1.4fr",gap:12,alignItems:"end"}}>
                    <div>
                      <label className="lbl">Date de début</label>
                      <input type="date" className="inp" value={productionDates.debut}
                        onChange={e=>setProductionDates(d=>({...d,debut:e.target.value}))}/>
                    </div>
                    <div>
                      <label className="lbl">Date de fin</label>
                      <input type="date" className="inp" value={productionDates.fin}
                        onChange={e=>setProductionDates(d=>({...d,fin:e.target.value}))}/>
                    </div>
                    <div style={{position:"relative"}}>
                      <label className="lbl">Rechercher un lot</label>
                      <Search size={16} style={{position:"absolute",left:12,bottom:11,color:"#1E8A32",pointerEvents:"none"}}/>
                      <input className="inp" value={productionSearch}
                        onChange={e=>setProductionSearch(e.target.value)}
                        placeholder="N° lot, produit, opérateur, statut…"
                        style={{paddingLeft:38,paddingRight:productionSearch?38:12}}/>
                      {productionSearch&&(
                        <button type="button" onClick={()=>setProductionSearch("")}
                          style={{position:"absolute",right:8,bottom:7,width:28,height:28,borderRadius:"50%",border:"none",background:"rgba(47,158,63,0.120)",color:"#0F1F17",cursor:"pointer"}}>×</button>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:12,marginBottom:18}}>
                  {[
                    ["Total produit",NUM(totalProduit)+" btl","#0F1F17"],
                    ["Nombre de lots",NUM(totalLots),"#1E8A32"],
                    ["Moyenne par lot",NUM(moyenneLot)+" btl","#1E7A2D"],
                    ["Moyenne par jour actif",NUM(moyenneJour)+" btl","#5B7A8A"]
                  ].map(([label,value,color])=>(
                    <div key={label} className="card" style={{padding:15,borderColor:color+"44"}}>
                      <div style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.2,color:"#5B6B62",marginBottom:7}}>{label}</div>
                      <div style={{fontSize:isMobile?15:19,fontWeight:800,color}}>{value}</div>
                    </div>
                  ))}
                </div>

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1.25fr .75fr",gap:18,marginBottom:18}}>
                  <div className="card">
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:16}}>
                      Production par produit
                    </div>
                    {productRows.length===0&&<div style={{color:"#5B6B62",fontSize:12,textAlign:"center",padding:28}}>Aucune production sur cette période.</div>}
                    {productRows.map(([label,value])=>(
                      <div key={label} style={{marginBottom:13}}>
                        <div style={{display:"flex",justifyContent:"space-between",gap:12,fontSize:12,marginBottom:5}}>
                          <span style={{color:"#2B3A32"}}>{label}</span>
                          <strong style={{color:"#0F1F17"}}>{NUM(value)} btl</strong>
                        </div>
                        <div style={{height:9,borderRadius:20,background:"#EEF3F0",overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${Math.max(2,value/maxProduct*100)}%`,background:"linear-gradient(90deg,#2F9E3F,#DDF0E0)",borderRadius:20}}/>
                        </div>
                        <div style={{fontSize:9,color:"#8A978F",marginTop:3}}>{totalProduit?Math.round(value/totalProduit*100):0}% du volume total</div>
                      </div>
                    ))}
                  </div>

                  <div className="card">
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:16}}>
                      Répartition par statut
                    </div>
                    <div style={{display:"flex",justifyContent:"center",marginBottom:18}}>
                      <div style={{width:190,height:190,borderRadius:"50%",background:statusGradient,display:"grid",placeItems:"center"}}>
                        <div style={{width:112,height:112,borderRadius:"50%",background:"#FFFFFF",border:"1px solid #E3E9E6",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",textAlign:"center",padding:8}}>
                          <div style={{fontSize:9,color:"#5B6B62",textTransform:"uppercase"}}>Production totale</div>
                          <div style={{fontSize:15,color:"#0F1F17",fontWeight:800,marginTop:5}}>{NUM(totalProduit)}</div>
                          <div style={{fontSize:9,color:"#5B6B62"}}>bouteilles</div>
                        </div>
                      </div>
                    </div>
                    {statusRows.map(([label,value],index)=>(
                      <div key={label} style={{display:"grid",gridTemplateColumns:"10px 1fr auto auto",gap:8,alignItems:"center",padding:"6px 0",borderBottom:"1px solid #EDF1EF",fontSize:11}}>
                        <span style={{width:9,height:9,borderRadius:"50%",background:statusPalette[index%statusPalette.length]}}/>
                        <span style={{color:"#2B3A32"}}>{label}</span>
                        <span style={{color:"#5B6B62"}}>{totalProduit?Math.round(value/totalProduit*100):0}%</span>
                        <strong style={{color:"#0F1F17"}}>{NUM(value)}</strong>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card" style={{marginBottom:18}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:16}}>
                    Évolution mensuelle de la production
                  </div>
                  {monthRows.length===0&&<div style={{color:"#5B6B62",fontSize:12,textAlign:"center",padding:22}}>Aucune donnée mensuelle disponible.</div>}
                  <div style={{display:"flex",alignItems:"flex-end",gap:10,minHeight:180,overflowX:"auto",padding:"8px 4px 0"}}>
                    {monthRows.map(([key,value])=>{
                      const [yy,mm]=key.split("-").map(Number);
                      const label=new Date(yy,mm-1,1).toLocaleDateString("fr-FR",{month:"short",year:"2-digit"}).replace(".","");
                      const height=Math.max(12,value/maxMonth*135);
                      return (
                        <div key={key} style={{minWidth:58,flex:1,textAlign:"center"}}>
                          <div style={{fontSize:9,color:"#0F1F17",fontWeight:700,marginBottom:5}}>{NUM(value)}</div>
                          <div title={NUM(value)+" bouteilles"} style={{height,width:"72%",maxWidth:44,margin:"0 auto",borderRadius:"7px 7px 2px 2px",background:"linear-gradient(180deg,#DDF0E0,#2F9E3F)",boxShadow:"0 4px 14px rgba(47,158,63,0.128)"}}/>
                          <div style={{fontSize:9,color:"#5B6B62",marginTop:7,textTransform:"capitalize"}}>{label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="card scr">
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,marginBottom:12}}>
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62"}}>
                      Détail des lots de la période
                    </div>
                    <div style={{fontSize:11,color:"#1E8A32"}}>{NUM(filteredLots.length)} lot(s)</div>
                  </div>
                  <table className="tbl">
                    <thead><tr><th>N° Lot</th><th>Produit</th><th>Date</th><th>Quantité</th><th>Écart bouteilles</th><th>Opérateur</th><th>Statut</th><th>Notes</th><th></th></tr></thead>
                    <tbody>{filteredLots.map(l=>{
                      // Écart de contrôle : bouteilles vides utilisées − quantité produite reconstituée
                      // (quantité actuelle du lot + avaries déjà déclarées dessus, puisque quantite
                      // diminue au fil des avaries — voir enregistrer_avarie_transaction côté base).
                      const avariesLot=avaries.filter(a=>String(a.lot_id)===String(l.id)).reduce((s,a)=>s+toFiniteNumber(a.quantite),0);
                      const quantiteInitiale=toFiniteNumber(l.quantite)+avariesLot;
                      const ecart=l.bouteilles_utilisees!=null ? toFiniteNumber(l.bouteilles_utilisees)-quantiteInitiale : null;
                      return (
                      <tr key={l.id}>
                        <td style={{fontFamily:"monospace",fontSize:11,color:"#1E8A32"}}>{l.numero}</td>
                        <td>{l.produit}</td>
                        <td style={{color:"#5B6B62"}}>{new Date(l.date).toLocaleDateString("fr-FR")}</td>
                        <td style={{fontWeight:700,color:"#0F1F17"}}>{NUM(l.quantite)} btl</td>
                        <td>{ecart==null
                          ? <span style={{color:"#8A978F",fontSize:11}}>Non suivi</span>
                          : ecart===0
                            ? <span className="badge bg">OK</span>
                            : ecart>0
                              ? <span className="badge br" title="Bouteilles vides entrées non retrouvées en production">-{NUM(ecart)} inexpliqué(s)</span>
                              : <span className="badge" style={{color:"#1E8A32",background:"rgba(47,158,63,0.096)"}} title="Plus de bouteilles produites+avariées que de bouteilles vides déclarées entrées — à vérifier">Anomalie (+{NUM(-ecart)})</span>
                        }</td>
                        <td style={{color:"#5B6B62",fontSize:12}}>{l.operateur}</td>
                        <td><span className={"badge "+(l.statut==="En stock"?"bg":l.statut==="Distribué"?"bg2":"br")}>{l.statut}</span></td>
                        <td style={{color:"#5B6B62",fontSize:11,maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.notes}</td>
                        <td style={{display:"flex",gap:5}}>
                          {(canEdit())&&<button className="btn btn-ghost btn-sm" onClick={()=>openModal("lot",l)}>✏</button>}
                          <button className="btn btn-ghost btn-sm" onClick={()=>setLotPrint(l)}><Printer size={13}/></button>
                          <button className="btn btn-gold btn-sm" onClick={()=>setLotEtiquette(l)}><Tag size={13}/></button>
                          {canEdit()?<button className="btn btn-red btn-sm" onClick={()=>deleteLot(l)}><Trash2 size={13}/></button>:null}
                        </td>
                      </tr>
                      );
                    })}</tbody>
                  </table>
                  {filteredLots.length===0&&(
                    <div style={{padding:24,textAlign:"center",color:"#5B6B62",fontSize:12}}>
                      Aucun lot ne correspond à la période ou à la recherche sélectionnée.
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* CONTRÔLE QUALITÉ PAR LOT */}
          {page==="controle_qualite"&&(can("production")||isDirecteur())&&(()=>{
            const qcOpenLots = fichesQualite.filter(f=>f.statut==="ouvert").sort((a,b)=>new Date(b.date_production)-new Date(a.date_production));
            const qcHistorique = [...fichesQualite].sort((a,b)=>new Date(b.date_production)-new Date(a.date_production));
            const qcToggle = (k,v) => setQcConformites(c=>({...c,[k]:v}));
            const qcSetField = (k,v) => setQcForm(f=>({...f,[k]:v}));
            return (
              <div>
                <div style={{marginBottom:18}}>
                  <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Contrôle qualité par lot</div>
                  <div style={{fontSize:12,color:"#5B6B62",marginTop:4}}>
                    Ouvre un lot en début de production, puis clôture-le en fin de production avec le relevé du compteur machine. Un lot libéré dépose automatiquement sa fiche PDF.
                  </div>
                </div>

                {qcPhase==="start"&&(<>
                  {qcOpenLots.length>0&&(
                    <div className="card" style={{marginBottom:18}}>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Reprendre un lot resté ouvert</div>
                      <select className="inp" defaultValue="" onChange={e=>e.target.value&&qcReprendreLot(e.target.value)}>
                        <option value="" disabled>— choisir un lot ouvert —</option>
                        {qcOpenLots.map(l=><option key={l.id} value={l.id}>{l.numero_lot} · {l.produit} · {fmtDate(l.date_production)} · {l.producteur_nom}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="card" style={{marginBottom:18}}>
                    <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>Ouvrir un nouveau lot</div>
                    <div className="fld"><label className="lbl">N° de lot (imprimé par la machine) *</label>
                      <input className="inp" value={qcNumeroLot} onChange={e=>setQcNumeroLot(e.target.value)} placeholder="ex. AZB-2026-018" />
                    </div>
                    <div className="fld"><label className="lbl">Produit *</label>
                      <select className="inp" value={qcProduit} onChange={e=>setQcProduit(e.target.value)}>
                        <option value="">Choisir...</option>{produits.map(p=><option key={p.id} value={p.nom}>{p.code?p.code+" · ":""}{p.nom}</option>)}
                      </select>
                    </div>
                    {qcMessage&&<div style={{margin:"10px 0",padding:"10px 12px",borderRadius:6,background:qcMessage.type==="error"?"#FDECEA":"#E3F3E6",color:qcMessage.type==="error"?"#D6453D":"#1E7A2D",fontSize:13}}>{qcMessage.text}</div>}
                    <button className="btn btn-gold" disabled={qcSaving} onClick={qcOuvrirLot} style={{marginTop:8}}>{qcSaving?"Ouverture…":"Ouvrir le lot"}</button>
                  </div>
                </>)}

                {qcPhase==="complete"&&(<>
                  <div className="card" style={{marginBottom:18}}>
                    <div style={{display:"flex",gap:24,flexWrap:"wrap"}}>
                      <div><div style={{fontSize:11,color:"#5B6B62"}}>N° de lot</div><b style={{color:"#0F1F17"}}>{qcNumeroLot}</b></div>
                      <div><div style={{fontSize:11,color:"#5B6B62"}}>Produit</div><b style={{color:"#0F1F17"}}>{qcProduit}</b></div>
                      <div><div style={{fontSize:11,color:"#5B6B62"}}>Producteur</div><b style={{color:"#0F1F17"}}>{session.nom}</b></div>
                      {session.ville&&<div><div style={{fontSize:11,color:"#5B6B62"}}>Ville</div><b style={{color:"#0F1F17"}}>{session.ville}</b></div>}
                    </div>
                  </div>

                  <div className="card" style={{marginBottom:18,borderColor:"rgba(47,158,63,0.40)"}}>
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:8}}>Nombre de bouteilles (compteur machine)</div>
                    <input className="inp" type="number" inputMode="numeric" style={{fontSize:20,fontWeight:700}} value={qcForm.nombre_bouteilles} onChange={e=>qcSetField("nombre_bouteilles",e.target.value)} placeholder="relevé du compteur" />
                    <div className="fld" style={{marginTop:12,maxWidth:220}}><label className="lbl">Volume produit (L)</label>
                      <input className="inp" type="number" value={qcForm.volume_litres} onChange={e=>qcSetField("volume_litres",e.target.value)} />
                    </div>
                  </div>

                  {Object.keys(QC_CHECKS).slice(0,1).map(section=>(
                    <div className="card" key={section} style={{marginBottom:18}}>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>{section}</div>
                      {QC_CHECKS[section].map(([k,label])=>(
                        <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid rgba(227,233,230,0.33)"}}>
                          <span style={{fontSize:13,color:"#2B3A32"}}>{label}</span>
                          <span>
                            <button type="button" className="btn btn-sm" style={{marginLeft:6,background:qcConformites[k]===true?"#1E7A2D":"transparent",border:"1px solid "+(qcConformites[k]===true?"#2E7D32":"#E3E9E6"),color:qcConformites[k]===true?"#FFFFFF":"#5B6B62"}} onClick={()=>qcToggle(k,true)}>Oui</button>
                            <button type="button" className="btn btn-sm" style={{marginLeft:6,background:qcConformites[k]===false?"#B3372F":"transparent",border:"1px solid "+(qcConformites[k]===false?"#B71C1C":"#E3E9E6"),color:qcConformites[k]===false?"#FFFFFF":"#5B6B62"}} onClick={()=>qcToggle(k,false)}>Non</button>
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}

                  <div className="card" style={{marginBottom:18}}>
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>pH et pasteurisation (mesures)</div>
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
                      {QC_MESURES.map(([k,label,cible,isNumber])=>(
                        <div key={k} className="fld">
                          <label className="lbl">{label}{cible?" — "+cible:""}</label>
                          <input className="inp" type={isNumber?"number":"text"} step="0.1" value={qcForm[k]} onChange={e=>qcSetField(k,e.target.value)} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {Object.keys(QC_CHECKS).slice(1).map(section=>(
                    <div className="card" key={section} style={{marginBottom:18}}>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>{section}</div>
                      {QC_CHECKS[section].map(([k,label])=>(
                        <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid rgba(227,233,230,0.33)"}}>
                          <span style={{fontSize:13,color:"#2B3A32"}}>{label}</span>
                          <span>
                            <button type="button" className="btn btn-sm" style={{marginLeft:6,background:qcConformites[k]===true?"#1E7A2D":"transparent",border:"1px solid "+(qcConformites[k]===true?"#2E7D32":"#E3E9E6"),color:qcConformites[k]===true?"#FFFFFF":"#5B6B62"}} onClick={()=>qcToggle(k,true)}>Oui</button>
                            <button type="button" className="btn btn-sm" style={{marginLeft:6,background:qcConformites[k]===false?"#B3372F":"transparent",border:"1px solid "+(qcConformites[k]===false?"#B71C1C":"#E3E9E6"),color:qcConformites[k]===false?"#FFFFFF":"#5B6B62"}} onClick={()=>qcToggle(k,false)}>Non</button>
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}

                  <div className="card" style={{marginBottom:18}}>
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Décision et libération</div>
                    <div style={{marginBottom:12}}>
                      <button type="button" className="btn" style={{background:qcForm.decision==="libere"?"#1E7A2D":"transparent",border:"1px solid "+(qcForm.decision==="libere"?"#2E7D32":"#E3E9E6"),color:qcForm.decision==="libere"?"#FFFFFF":"#5B6B62",marginRight:8}} onClick={()=>qcSetField("decision","libere")}>Lot libéré</button>
                      <button type="button" className="btn" style={{background:qcForm.decision==="bloque"?"#B3372F":"transparent",border:"1px solid "+(qcForm.decision==="bloque"?"#B71C1C":"#E3E9E6"),color:qcForm.decision==="bloque"?"#FFFFFF":"#5B6B62"}} onClick={()=>qcSetField("decision","bloque")}>Lot bloqué</button>
                    </div>
                    <div className="fld"><label className="lbl">Observations / actions correctives</label>
                      <textarea className="inp" style={{minHeight:70}} value={qcForm.observations} onChange={e=>qcSetField("observations",e.target.value)} /></div>
                    <div className="fld" style={{marginTop:12}}><label className="lbl">Visa responsable</label>
                      <input className="inp" value={qcForm.visa_responsable} onChange={e=>qcSetField("visa_responsable",e.target.value)} /></div>
                  </div>

                  {qcMessage&&<div style={{margin:"10px 0",padding:"10px 12px",borderRadius:6,background:qcMessage.type==="error"?"#FDECEA":"#E3F3E6",color:qcMessage.type==="error"?"#D6453D":"#1E7A2D",fontSize:13}}>{qcMessage.text}</div>}
                  <div style={{display:"flex",gap:9,flexWrap:"wrap",marginBottom:24}}>
                    <button className="btn btn-ghost" disabled={qcSaving} onClick={()=>qcEnregistrer(false)}>Enregistrer (garder ouvert)</button>
                    <button className="btn btn-gold" disabled={qcSaving||qcGeneratingPdf} onClick={()=>qcEnregistrer(true)}>{qcSaving?"Enregistrement…":qcGeneratingPdf?"Dépôt du PDF…":"Enregistrer et clôturer"}</button>
                    <button className="btn btn-ghost" onClick={qcAnnulerSaisie}>Annuler la saisie</button>
                  </div>
                </>)}

                <div className="card" style={{marginBottom:18}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:12}}>Historique des lots contrôlés</div>
                  <div style={{overflowX:"auto"}}>
                    <table className="tbl">
                      <thead><tr><th>Lot</th><th>Produit</th><th>Producteur</th><th>Date</th><th>Statut</th><th>Décision</th><th>N° stock</th><th>PDF</th></tr></thead>
                      <tbody>
                        {qcHistorique.map(f=>{
                          const stCol = f.statut==="cloture"?"#1E7A2D":"#1E8A32";
                          const decCol = f.decision==="libere"?"#1E7A2D":f.decision==="bloque"?"#D6453D":"#5B6B62";
                          return (
                            <tr key={f.id}>
                              <td>{f.numero_lot}</td>
                              <td>{f.produit}</td>
                              <td>{f.producteur_nom}</td>
                              <td>{fmtDate(f.date_production)}</td>
                              <td><span className="badge" style={{background:stCol+"22",color:stCol,border:"1px solid "+stCol}}>{f.statut==="cloture"?"Clôturé":"Ouvert"}</span></td>
                              <td><span style={{color:decCol,fontSize:12}}>{f.decision==="libere"?"Libéré":f.decision==="bloque"?"Bloqué":"—"}</span></td>
                              <td>{f.lot_stock_numero||"—"}</td>
                              <td>{(f.pdf_path||f.statut==="cloture")?<button className="btn btn-ghost btn-sm" onClick={()=>qcOuvrirPdf(f)}>Voir</button>:"—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {qcHistorique.length===0&&<div style={{padding:24,textAlign:"center",color:"#5B6B62",fontSize:12}}>Aucune fiche de contrôle qualité pour l'instant.</div>}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* CAISSE */}
          {page==="caisse"&&isDirecteur()&&(()=>{ const C=caisseCalc; const ecartLive = String(caisseCompte).trim()===""?null:(Math.round(toFiniteNumber(caisseCompte))-Math.round(C.solde)); return (
            <div>
              <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:6}}>Caisse</div>
              <div style={{fontSize:12,color:"#5B6B62",marginBottom:16}}>Les espèces : ce qui est entré, ce qui est sorti, et ce qui doit rester. Depuis le {fmtDate(C.deb)}, solde de départ {FCFA(caisseParam.depart)}. <button type="button" className="btn btn-ghost btn-sm" onClick={()=>setCaisseCfg({debut:C.deb,depart:String(caisseParam.depart)})}>Modifier</button></div>
              {caisseMvts.filter(m=>m.a_expliquer&&!m.explication).map(m=>(
                <div key={m.id} className="card" style={{padding:14,marginBottom:14,borderColor:"#E8A33D",background:"#FFF8EC"}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#8A5A00",marginBottom:4}}>⚠ Écart de caisse à expliquer : {toFiniteNumber(m.montant)>0?"+":""}{FCFA(toFiniteNumber(m.montant))} <span style={{fontWeight:400,fontSize:12}}>({toFiniteNumber(m.montant)<0?"il manquait":"il y avait en trop"} lors du comptage du {fmtDate(m.date)})</span></div>
                  <div style={{fontSize:12,color:"#5B6B62",marginBottom:8}}>Cet écart reste affiché tant que vous ne l'avez pas expliqué. Quand vous avez compris d'où il vient, écrivez-le ici pour le clore.</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <input type="text" className="inp" style={{flex:1,minWidth:200}} placeholder="Explication (ex : dépense de transport non saisie)" value={caisseExpl[m.id]||""} onChange={e=>setCaisseExpl(x=>({...x,[m.id]:e.target.value}))}/>
                    <button className="btn btn-gold btn-sm" onClick={()=>expliquerEcartCaisse(m)}>Expliquer et clore</button>
                  </div>
                </div>
              ))}
              {caisseCfg&&<div className="card" style={{padding:14,marginBottom:14}}>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr auto auto",gap:10,alignItems:"end"}}>
                  <div><label className="lbl">La caisse compte à partir du</label><input type="date" className="inp" value={caisseCfg.debut} onChange={e=>setCaisseCfg({...caisseCfg,debut:e.target.value})}/></div>
                  <div><label className="lbl">Espèces en caisse à cette date (FCFA)</label><input type="number" className="inp" value={caisseCfg.depart} onChange={e=>setCaisseCfg({...caisseCfg,depart:e.target.value})}/></div>
                  <button className="btn btn-gold" onClick={sauverCaisseCfg}>Enregistrer</button>
                  <button className="btn btn-ghost" onClick={()=>setCaisseCfg(null)}>Annuler</button>
                </div>
              </div>}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:12,marginBottom:14}}>
                <div className="kpi" style={{gridColumn:isMobile?"1 / -1":undefined,borderColor:(C.solde>=0?"#1E8A32":"#D6453D")+"66"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:6}}>Solde attendu en caisse</div><div style={{fontSize:28,fontWeight:700,color:C.solde>=0?"#1E8A32":"#D6453D"}}>{FCFA(C.solde)}</div><div style={{fontSize:10,color:"#5B6B62",marginTop:4}}>Ce qui devrait être en espèces, d'après l'ERP</div></div>
                <div className="kpi"><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:6}}>Espèces reçues</div><div style={{fontSize:18,fontWeight:700,color:"#2F6FDE"}}>{FCFA(C.recu+C.entreeAutre)}</div></div>
                <div className="kpi"><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:6}}>Dépenses en espèces</div><div style={{fontSize:18,fontWeight:700,color:"#D6453D"}}>{FCFA(C.dep+C.ach+C.sortieAutre)}</div></div>
                <div className="kpi"><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:6}}>Versé en banque</div><div style={{fontSize:18,fontWeight:700,color:"#0F1F17"}}>{FCFA(C.vers)}</div></div>
              </div>
              <div className="card" style={{padding:14,marginBottom:14}}>
                <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:8}}>Compter la caisse</div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
                  <input type="number" className="inp" style={{maxWidth:220}} placeholder="Montant compté (FCFA)" value={caisseCompte} onChange={e=>setCaisseCompte(e.target.value)}/>
                  {ecartLive!==null&&<span style={{fontSize:13,fontWeight:600,color:ecartLive===0?"#1E8A32":"#D6453D"}}>{ecartLive===0?"La caisse est juste ✓":"Écart : "+(ecartLive>0?"+":"")+FCFA(ecartLive)+(ecartLive<0?" (il manque)":" (en trop)")}</span>}
                  {ecartLive!==null&&ecartLive!==0&&<button className="btn btn-ghost btn-sm" onClick={enregistrerEcartCaisse}>Enregistrer l'écart</button>}
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
                <button className="btn btn-gold" onClick={()=>setCaisseForm({type:"versement_banque",date:today(),montant:"",libelle:""})}>+ Versement en banque</button>
                <button className="btn btn-ghost" onClick={()=>setCaisseForm({type:"sortie_autre",date:today(),montant:"",libelle:""})}>+ Autre sortie</button>
                <button className="btn btn-ghost" onClick={()=>setCaisseForm({type:"entree_autre",date:today(),montant:"",libelle:""})}>+ Autre entrée</button>
              </div>
              {caisseForm&&<div className="card" style={{padding:14,marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:700,color:"#1E8A32",marginBottom:10}}>{({versement_banque:"Versement en banque",sortie_autre:"Autre sortie d'espèces",entree_autre:"Autre entrée d'espèces"})[caisseForm.type]}</div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr 2fr auto auto",gap:10,alignItems:"end"}}>
                  <div><label className="lbl">Date</label><input type="date" className="inp" value={caisseForm.date} onChange={e=>setCaisseForm({...caisseForm,date:e.target.value})}/></div>
                  <div><label className="lbl">Montant (FCFA)</label><input type="number" className="inp" value={caisseForm.montant} onChange={e=>setCaisseForm({...caisseForm,montant:e.target.value})}/></div>
                  <div><label className="lbl">{caisseForm.type==="versement_banque"?"Banque / référence (facultatif)":"Motif"}</label><input type="text" className="inp" value={caisseForm.libelle} onChange={e=>setCaisseForm({...caisseForm,libelle:e.target.value})}/></div>
                  <button className="btn btn-gold" onClick={sauverCaisseMvt}>Enregistrer</button>
                  <button className="btn btn-ghost" onClick={()=>setCaisseForm(null)}>Annuler</button>
                </div>
              </div>}
              <div className="card" style={{padding:0}}>
                <div className="table-wrap"><table className="tbl">
                  <thead><tr><th>Date</th><th>Type</th><th>Détail</th><th style={{textAlign:"right"}}>Entrée</th><th style={{textAlign:"right"}}>Sortie</th><th></th></tr></thead>
                  <tbody>
                    {C.journal.length===0&&<tr><td colSpan={6} style={{textAlign:"center",color:"#5B6B62",padding:20}}>Aucun mouvement depuis le {fmtDate(C.deb)}.</td></tr>}
                    {C.journal.map(r=>(
                      <tr key={r.k} style={r.barre?{opacity:.55}:undefined}>
                        <td style={{whiteSpace:"nowrap",color:"#5B6B62"}}>{fmtDate(r.date)}</td>
                        <td style={{fontSize:12,fontWeight:600}}>{r.type}</td>
                        <td style={{fontSize:12}}>
                          <div>{r.label}</div>
                          {r.detail&&<div style={{fontSize:11,color:"#5B6B62"}}>{r.detail}</div>}
                          {r.dep&&<select className="inp" style={{marginTop:4,padding:"3px 6px",fontSize:11,maxWidth:150}} value={r.dep.moyen||"Espèces"} onChange={e=>changerMoyenDepense(r.dep,e.target.value)}>{["Espèces","Virement","Mobile Money","Autre"].map(m=><option key={m} value={m}>{m}</option>)}</select>}
                        </td>
                        <td style={{textAlign:"right",fontWeight:600,color:"#1E8A32"}}>{r.entree?FCFA(r.entree):""}</td>
                        <td style={{textAlign:"right",fontWeight:600,color:"#D6453D"}}>{r.sortie?FCFA(r.sortie):""}</td>
                        <td>{r.mvt&&<button className="btn btn-ghost btn-sm" title="Supprimer" onClick={()=>supprimerCaisseMvt(r.mvt)}><X size={13}/></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
              <div style={{fontSize:11,color:"#5B6B62",marginTop:10}}>Les dépenses sont comptées « payées en espèces » tant que vous n'avez pas choisi un autre moyen dans la liste de la ligne. Les paiements reçus par virement ou Mobile Money n'entrent pas en caisse.</div>
            </div>
          ); })()}

          {/* COMPTABILITE */}
          {page==="comptabilite"&&(isDirecteur()||can("comptabilite"))&&(
            <div>
              <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:6}}>Comptabilité</div>
              <div style={{fontSize:12,color:"#5B6B62",marginBottom:20}}>Pour saisir un achat (matières premières, ou toute autre dépense), va dans le module Achats — tout y est regroupé au même endroit.</div>
              {isDirecteur()&&<div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",marginBottom:16}}>
                <button type="button" className="btn btn-gold btn-sm" onClick={()=>vrOuvrir()}>+ Versement à un employé (avance, prime…)</button>
                {(()=>{ const t = vrListe.filter(v=>v.recuperable&&v.statut==="signe").reduce((s,v)=>s+(Number(v.solde_restant)||0),0); return t>0?<span style={{fontSize:12,color:"#8A6A12"}}>Avances aux employés encore à retenir : <b>{FCFA(t)}</b></span>:null; })()}
              </div>}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(3,1fr)",gap:12,marginBottom:12}}>
                {[{label:"Total ventes",val:FCFA(totalVentes),col:"#1E8A32"},{label:"Dépenses de fonctionnement",val:FCFA(totalDepenses),col:"#D6453D"},{label:"Bénéfice d'exploitation",val:FCFA(benefice),col:benefice>=0?"#1E7A2D":"#D6453D"}].map((k,i)=>(
                  <div key={i} className="kpi" style={{borderColor:k.col+"44"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:6}}>{k.label}</div><div style={{fontSize:18,color:k.col,fontWeight:700}}>{k.val}</div></div>
                ))}
              </div>
              <div className="card" style={{marginBottom:20,padding:14}}>
                <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Détail des dépenses de fonctionnement</div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:12}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}><span style={{fontSize:12,color:"#5B6B62"}}>Achats fournisseurs</span><strong style={{color:"#0F1F17"}}>{FCFA(totalAchatsFournisseurs)}</strong></div>
                  <div style={{display:"flex",justifyContent:"space-between"}}><span style={{fontSize:12,color:"#5B6B62"}}>Salaires versés</span><strong style={{color:"#0F1F17"}}>{FCFA(totalSalaires)}</strong></div>
                  <div style={{display:"flex",justifyContent:"space-between"}}><span style={{fontSize:12,color:"#5B6B62"}}>Autres dépenses (électricité, etc.)</span><strong style={{color:"#0F1F17"}}>{FCFA(totalDepensesManuelles)}</strong></div>
                </div>
              </div>
              <div className="card" style={{marginBottom:20,padding:14}}>
                {(()=>{
                  const mois=codesMois; const parCode={}; let sansCode=0, nbSans=0; const retenues={};
                  depenses.filter(d=>String(d.date||"").slice(0,7)===mois).forEach(d=>{ const m=toFiniteNumber(d.montant); if(d.code){ parCode[d.code]=(parCode[d.code]||0)+m; } else { sansCode+=m; nbSans++; } });
                  bulletinsPaie.filter(b=>String(b.mois||"").slice(0,7)===mois).forEach(b=>{
                    parCode["6001"]=(parCode["6001"]||0)+toFiniteNumber(b.salaire_base);
                    parCode["6002"]=(parCode["6002"]||0)+toFiniteNumber(b.primes);
                    parCode["6003"]=(parCode["6003"]||0)+toFiniteNumber(b.heures_sup);
                    retenues["6004"]=(retenues["6004"]||0)+toFiniteNumber(b.cnps)+toFiniteNumber(b.irpp)+toFiniteNumber(b.cac)+toFiniteNumber(b.cfc);
                    retenues["6005"]=(retenues["6005"]||0)+toFiniteNumber(b.autres_retenues);
                  });
                  const lignes=Object.keys(parCode).filter(c=>parCode[c]>0).sort();
                  const total=lignes.reduce((t,c)=>t+parCode[c],0)+sansCode;
                  return <>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:10}}>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62"}}>Dépenses par code</div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <input type="month" className="inp" style={{width:160}} value={codesMois} onChange={e=>setCodesMois(e.target.value||today().slice(0,7))} />
                        {isDirecteur()&&<button className="btn btn-ghost btn-sm" onClick={()=>setModal("codes")}>Gérer les codes</button>}
                      </div>
                    </div>
                    {lignes.length===0&&sansCode===0
                      ? <div style={{fontSize:12,color:"#8A978F"}}>Aucune dépense ni salaire ce mois-ci.</div>
                      : <>
                        {lignes.map(c=>(
                          <div key={c} style={{display:"flex",justifyContent:"space-between",gap:10,padding:"6px 0",borderTop:"1px solid #EEF2F0",fontSize:13}}>
                            <span><b style={{marginRight:8}}>{c}</b>{(codeInfo(c)||{}).libelle||"—"}</span>
                            <span style={{whiteSpace:"nowrap"}}><b>{FCFA(parCode[c])}</b> <span style={{color:"#8A978F",fontSize:11}}>{total>0?Math.round(parCode[c]*100/total):0} %</span></span>
                          </div>
                        ))}
                        {sansCode>0&&<div style={{display:"flex",justifyContent:"space-between",gap:10,padding:"6px 0",borderTop:"1px solid #EEF2F0",fontSize:13,color:"#A8680A"}}><span>Sans code — à classer ({nbSans})</span><b>{FCFA(sansCode)}</b></div>}
                        <div style={{display:"flex",justifyContent:"space-between",borderTop:"2px solid #0F1F17",marginTop:6,paddingTop:8,fontWeight:700}}><span>Total</span><span>{FCFA(total)}</span></div>
                        {(retenues["6004"]>0||retenues["6005"]>0)&&<div style={{fontSize:11,color:"#8A978F",marginTop:8}}>Salaires pris en compte : montants bruts des fiches de paie. Retenues (déjà comprises, non ajoutées au total) : 6004 {FCFA(retenues["6004"]||0)} · 6005 {FCFA(retenues["6005"]||0)}</div>}
                      </>}
                  </>;
                })()}
              </div>
              <div className="card" style={{marginBottom:20,padding:14}}>
                <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:4}}>Immobilisations (hors dépenses)</div>
                <div style={{fontSize:11,color:"#8A978F",marginBottom:10}}>Le prix d'achat du matériel n'est pas une dépense de fonctionnement : il est étalé dans le temps (amortissement).</div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(2,1fr)",gap:12}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}><span style={{fontSize:12,color:"#5B6B62"}}>Amortissement cumulé à ce jour</span><strong style={{color:"#0F1F17"}}>{FCFA(totalAmortissementCumule)}</strong></div>
                  <div style={{display:"flex",justifyContent:"space-between"}}><span style={{fontSize:12,color:"#5B6B62"}}>Bénéfice après amortissement</span><strong style={{color:beneficeApresAmort>=0?"#1E7A2D":"#D6453D"}}>{FCFA(beneficeApresAmort)}</strong></div>
                </div>
              </div>
              <div className="card scr">
                <table className="tbl">
                  <thead><tr><th>Date</th><th>Code</th><th>Libellé</th><th>Catégorie</th><th>Fournisseur</th><th>Montant</th>{isDirecteur()&&<th>Actions</th>}</tr></thead>
                  <tbody>{depenses.map(d=>(
                    <tr key={d.id}>
                      <td style={{color:"#5B6B62"}}>{new Date(d.date).toLocaleDateString("fr-FR")}</td>
                      <td>{d.code?<span className="badge" title={codeLabel(d.code)}>{d.code}</span>:<span style={{color:"#8A978F"}}>—</span>}</td>
                      <td>{d.libelle}</td><td><span className="badge bg2">{d.categorie}</span></td>
                      <td style={{color:"#5B6B62",fontSize:12}}>{d.fournisseur||"—"}</td>
                      <td style={{color:"#D6453D",fontWeight:600}}>-{FCFA(d.montant)}</td>
                      {isDirecteur()&&<td><div style={{display:"flex",gap:6}}>
                        <button className="btn btn-ghost btn-sm" onClick={()=>{setForm({id:d.id,date:d.date,libelle:d.libelle,montant:String(d.montant),categorie:d.categorie,fournisseur:d.fournisseur,code:d.code||"",moyen:d.moyen||"Espèces"});setModal("depense");}}>✏</button>
                        <button className="btn btn-red btn-sm" onClick={()=>deleteDepense(d)}><Trash2 size={13}/></button>
                      </div></td>}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* RENTABILITÉ */}
          {page==="rentabilite"&&(isDirecteur()||can("rentabilite"))&&(()=>{
            const mois = rentabiliteMois;
            const dansLeMois = (dateStr) => dateStr && dateStr.slice(0,7)===mois;

            const ventesMois = ventes.filter(v=>dansLeMois(v.date));
            const depensesMois = depenses.filter(d=>dansLeMois(d.date));
            const lotsMois = lots.filter(l=>dansLeMois(l.date));

            // Charges du mois = dépenses diverses (électricité, transport/logistique, etc.)
            // + achats fournisseurs (matières premières, emballages...) + salaires versés
            // (bulletins de paie) + amortissement du mois — les mêmes composantes que le total
            // de Comptabilité (l'amortissement en plus, manquait aussi à l'appel ici), sinon
            // la "Marge nette (charges incluses)" ci-dessous restait, elle aussi, incomplète.
            const achatsFacturesMois = achatsFactures.filter(f=>dansLeMois(f.date_facture));
            const bulletinsMois = bulletinsPaie.filter(b=>dansLeMois(b.mois));
            // Voir le correctif du 02/10/2026 plus haut (estDepensePersonnelHorsFichePaie) : le
            // "Personnel (hors fiche de paie)" compte ici aussi comme du salaire, pas une dépense.
            const totalDepensesDiversesMois = depensesMois.filter(d=>!estDepensePersonnelHorsFichePaie(d)).reduce((s,d)=>s+toFiniteNumber(d.montant),0);
            const totalAchatsFournisseursMois = achatsFacturesMois.reduce((s,f)=>s+toFiniteNumber(f.montant_ttc),0);
            const [rentabAnnee,rentabMoisIdx] = mois.split("-").map(Number);
            const rentabY0 = new Date(rentabAnnee,(rentabMoisIdx||1)-1,1);
            const rentabY1 = new Date(rentabAnnee,(rentabMoisIdx||1),0);
            const totalAmortissementMois = immobilisations.reduce((s,item)=>s+amortissementSurPeriode(item,rentabY0,rentabY1),0);
            const totalSalairesMois = bulletinsMois.reduce((s,b)=>s+toFiniteNumber(b.net),0)
              + depensesMois.filter(estDepensePersonnelHorsFichePaie).reduce((s,d)=>s+toFiniteNumber(d.montant),0);
            // Correctif du 05/10/2026 : les charges du mois ne comptent plus l'amortissement (présenté à part).
            const totalDepensesMois = totalDepensesDiversesMois + totalAchatsFournisseursMois + totalSalairesMois;
            const bouteillesProduitesMois = lotsMois.reduce((s,l)=>s+l.quantite,0);
            const coutIndirectParBouteille = bouteillesProduitesMois>0 ? totalDepensesMois/bouteillesProduitesMois : 0;

            const produitsStats = produits.map(p=>{
              const ventesP = ventesMois.filter(v=>v.produit===p.nom);
              const qteVendue = ventesP.reduce((s,v)=>s+v.qte,0);
              const caProduit = ventesP.reduce((s,v)=>s+v.montant,0);
              const cout = coutRevientEffectif(p);
              const coutDepuisRecette = recettes.some(r=>String(r.produit_id)===String(p.id));
              const margeUnitaire = p.prix - cout;
              const margePct = p.prix>0 ? Math.round((margeUnitaire/p.prix)*100) : 0;
              const margeTotale = margeUnitaire * qteVendue;
              const margeNetteUnitaire = margeUnitaire - coutIndirectParBouteille;
              const margeNetteTotale = margeNetteUnitaire * qteVendue;
              return { ...p, qteVendue, caProduit, cout_effectif:cout, coutDepuisRecette, margeUnitaire, margePct, margeTotale, margeNetteUnitaire, margeNetteTotale, coutRenseigne: cout>0 };
            }).sort((a,b)=>b.margeNetteTotale-a.margeNetteTotale);

            const margeTotaleGlobale = produitsStats.reduce((s,p)=>s+p.margeTotale,0);
            const margeNetteGlobale = produitsStats.reduce((s,p)=>s+p.margeNetteTotale,0);
            const sansCoutRenseigne = produits.filter(p=>!coutRevientEffectif(p));

            // Pertes de production (réservé au directeur) : dépenses du mois rapportées aux
            // bouteilles VENDUES (pas produites) vs le coût de revient théorique — signale
            // une perte si le coût réel dépasse ce qui était prévu.
            const bouteillesVenduesMois = ventesMois.reduce((s,v)=>s+toFiniteNumber(v.qte),0);
            const coutRevientPondereTotal = produitsStats.reduce((s,p)=>s+p.cout_effectif*p.qteVendue,0);
            const coutRevientTheoriqueMoyen = bouteillesVenduesMois>0 ? coutRevientPondereTotal/bouteillesVenduesMois : 0;
            const coutReelParBouteilleVendue = bouteillesVenduesMois>0 ? totalDepensesMois/bouteillesVenduesMois : 0;
            const perteEstimeeMois = bouteillesVenduesMois>0 ? (coutReelParBouteilleVendue-coutRevientTheoriqueMoyen)*bouteillesVenduesMois : 0;

            // Double contrôle bouteilles (entrées usine vs utilisées en production) sur le mois.
            const receptionsMois = receptionsBouteilles.filter(r=>dansLeMois(r.date));
            const bouteillesEntreesMois = receptionsMois.reduce((s,r)=>s+toFiniteNumber(r.quantite),0);
            const lotsMoisAvecControle = lotsMois.filter(l=>l.bouteilles_utilisees!=null);
            const bouteillesUtiliseesMois = lotsMoisAvecControle.reduce((s,l)=>s+toFiniteNumber(l.bouteilles_utilisees),0);
            const ecartBouteillesMois = lotsMoisAvecControle.reduce((s,l)=>{
              const avariesLot=avaries.filter(a=>String(a.lot_id)===String(l.id)).reduce((ss,a)=>ss+toFiniteNumber(a.quantite),0);
              const quantiteInitiale=toFiniteNumber(l.quantite)+avariesLot;
              return s+(toFiniteNumber(l.bouteilles_utilisees)-quantiteInitiale);
            },0);

            const clientsStats = clients.map(c=>{
              const ventesC = ventesMois.filter(v=>rattacherVente(v,c));
              const ca = ventesC.reduce((s,v)=>s+v.montant,0);
              let margeEst = 0, margeNetteEst = 0;
              ventesC.forEach(v=>{
                const prod = produits.find(p=>p.nom===v.produit);
                if (prod) {
                  const coutProd = coutRevientEffectif(prod);
                  margeEst += (prod.prix - coutProd) * v.qte;
                  margeNetteEst += (prod.prix - coutProd - coutIndirectParBouteille) * v.qte;
                }
              });
              return { ...c, ca, margeEst, margeNetteEst, nbCommandes: ventesC.length };
            }).filter(c=>c.ca>0).sort((a,b)=>b.margeNetteEst-a.margeNetteEst);

            const moisLabel = new Date(mois+"-01").toLocaleDateString("fr-FR",{month:"long",year:"numeric"});

            return (
              <div>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:6}}>Rentabilité</div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:16}}>Quels produits et clients rapportent vraiment, charges du mois comprises — {moisLabel.charAt(0).toUpperCase()+moisLabel.slice(1)}</div>

                <div className="fld" style={{maxWidth:220,marginBottom:20}}>
                  <label className="lbl">Mois analysé</label>
                  <input type="month" className="inp" value={rentabiliteMois} onChange={e=>setRentabiliteMois(e.target.value)} />
                </div>

                {sansCoutRenseigne.length>0&&(
                  <div style={{background:"rgba(47,158,63,0.064)",border:"1px solid rgba(47,158,63,0.160)",borderRadius:6,padding:"12px 16px",marginBottom:14,fontSize:12,color:"#1E8A32",display:"flex",alignItems:"center",gap:8}}>
                    <AlertTriangle size={15}/>
                    {sansCoutRenseigne.length} produit(s) sans coût de revient ({sansCoutRenseigne.map(p=>p.nom).join(", ")}) — la marge calculée pour eux est sous-estimée. Renseigne une recette (bouton « Recette » dans Stock & Produits) ou, à défaut, le coût de revient manuel.
                  </div>
                )}

                {isDirecteur()&&(
                  <div className="card" style={{marginBottom:20,borderColor:(perteEstimeeMois>0?"#E9A29D":"#BFE3C6")+"88",background:perteEstimeeMois>0?"rgba(192,57,43,.06)":"rgba(47,158,63,0.036)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62"}}>Pertes de production — réservé à la direction</div>
                      <span className="badge" style={{color:perteEstimeeMois>0?"#D6453D":"#1E7A2D",background:perteEstimeeMois>0?"rgba(192,57,43,.15)":"rgba(47,158,63,0.090)",fontWeight:700}}>
                        {perteEstimeeMois>0?"⚠ Perte probable en production":"✓ Pas de perte apparente"}
                      </span>
                    </div>
                    <div style={{fontSize:11,color:"#8A978F",marginBottom:12}}>Dépenses du mois rapportées aux bouteilles vendues, comparées au coût de revient théorique.</div>
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:14,fontSize:13,marginBottom:14}}>
                      <div><span style={{color:"#5B6B62"}}>Bouteilles vendues</span><br/><strong>{NUM(bouteillesVenduesMois)} btl</strong></div>
                      <div><span style={{color:"#5B6B62"}}>Coût réel / bouteille vendue</span><br/><strong>{FCFA(coutReelParBouteilleVendue)}</strong></div>
                      <div><span style={{color:"#5B6B62"}}>Coût de revient théorique</span><br/><strong>{FCFA(coutRevientTheoriqueMoyen)}</strong></div>
                      <div><span style={{color:"#5B6B62"}}>Perte estimée du mois</span><br/><strong style={{color:perteEstimeeMois>0?"#D6453D":"#1E7A2D"}}>{FCFA(Math.abs(perteEstimeeMois))}{perteEstimeeMois<=0?" (marge intacte)":""}</strong></div>
                    </div>
                    <div style={{borderTop:"1px solid #E3E9E6",paddingTop:12}}>
                      <div style={{fontSize:11,color:"#8A978F",marginBottom:10}}>Bouteilles vides (usine) — bilan du mois avec report : le reste de fin de mois devient la disponibilité du mois suivant :</div>
                      {(()=>{ const b=bilanBouteillesParMois().find(r=>r.mois===mois)||{debut:0,entrees:0,sorties:0,fin:0}; return (
                        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:14,fontSize:13,marginBottom:12}}>
                          <div><span style={{color:"#5B6B62"}}>Disponible en début de mois</span><br/><strong>{NUM(b.debut)} btl</strong></div>
                          <div><span style={{color:"#5B6B62"}}>Entrées du mois (réceptions)</span><br/><strong style={{color:"#1E7A2D"}}>+{NUM(b.entrees)} btl</strong></div>
                          <div><span style={{color:"#5B6B62"}}>Sorties du mois (production)</span><br/><strong style={{color:"#D6453D"}}>-{NUM(b.sorties)} btl</strong></div>
                          <div><span style={{color:"#5B6B62"}}>Reste en fin de mois</span><br/><strong style={{color:b.fin<0?"#D6453D":"#0F1F17"}}>{NUM(b.fin)} btl</strong></div>
                        </div>
                      ); })()}
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(3,1fr)",gap:14,fontSize:13}}>
                        <div><span style={{color:"#5B6B62"}}>Bouteilles vides entrées</span><br/><strong>{NUM(bouteillesEntreesMois)} btl</strong></div>
                        <div><span style={{color:"#5B6B62"}}>Utilisées en production (suivies)</span><br/><strong>{NUM(bouteillesUtiliseesMois)} btl</strong></div>
                        <div><span style={{color:"#5B6B62"}}>Écart double contrôle</span><br/><strong style={{color:ecartBouteillesMois>0?"#D6453D":"#1E7A2D"}}>{ecartBouteillesMois>0?"-":""}{NUM(Math.abs(ecartBouteillesMois))} btl{ecartBouteillesMois>0?" inexpliqué(s)":""}</strong></div>
                      </div>
                      {lotsMoisAvecControle.length<lotsMois.length&&<div style={{fontSize:10,color:"#8A978F",marginTop:10}}>{lotsMois.length-lotsMoisAvecControle.length} lot(s) du mois sans saisie de bouteilles utilisées — écart non calculable pour eux.</div>}
                    </div>
                  </div>
                )}

                <div className="card" style={{marginBottom:20,background:"rgba(47,158,63,0.040)"}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Répartition des charges du mois (comme un comptable)</div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:14,fontSize:13,marginBottom:14}}>
                    <div><span style={{color:"#5B6B62"}}>Achats fournisseurs</span><br/><strong style={{fontSize:15}}>{FCFA(totalAchatsFournisseursMois)}</strong></div>
                    <div><span style={{color:"#5B6B62"}}>Salaires versés</span><br/><strong style={{fontSize:15}}>{FCFA(totalSalairesMois)}</strong></div>
                    <div><span style={{color:"#5B6B62"}}>Autres dépenses</span><br/><strong style={{fontSize:15}}>{FCFA(totalDepensesDiversesMois)}</strong></div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:14,fontSize:13,borderTop:"1px solid #E3E9E6",paddingTop:14}}>
                    <div><span style={{color:"#5B6B62"}}>Total charges du mois</span><br/><strong style={{color:"#D6453D",fontSize:16}}>{FCFA(totalDepensesMois)}</strong></div>
                    <div><span style={{color:"#5B6B62"}}>Bouteilles produites</span><br/><strong style={{fontSize:16}}>{NUM(bouteillesProduitesMois)} btl</strong></div>
                    <div><span style={{color:"#5B6B62"}}>Charge indirecte / bouteille</span><br/><strong style={{color:"#1E8A32",fontSize:16}}>{FCFA(coutIndirectParBouteille)}</strong></div>
                  </div>
                  <div style={{fontSize:11,color:"#8A978F",marginTop:10}}>Amortissement des immobilisations du mois (présenté à part, non inclus dans les charges ci-dessus) : <strong style={{color:"#5B6B62"}}>{FCFA(totalAmortissementMois)}</strong></div>
                  {bouteillesProduitesMois===0&&<div style={{fontSize:11,color:"#5B6B62",marginTop:10}}>Aucun lot produit ce mois — la charge indirecte ne peut pas être répartie (division par zéro évitée).</div>}
                </div>

                {estProprietaire()&&(()=>{
                  // ── Seuil de rentabilité du mois (ajouté le 05/10/2026) ──
                  // Réservé au propriétaire (compte Nouboubo) à sa demande.
                  // Charges fixes = salaires + autres dépenses du mois (+ amortissement si coché).
                  // Les achats de matières ne sont PAS comptés ici : ils sont déjà dans le coût de
                  // revient de chaque bouteille (recette ou coût saisi sur la fiche produit).
                  const chargesFixes = totalSalairesMois + totalDepensesDiversesMois + (seuilInclureAmort ? totalAmortissementMois : 0);
                  const caMois = ventesMois.reduce((s,v)=>s+toFiniteNumber(v.montant),0);
                  const vendues = bouteillesVenduesMois;
                  const prixMoyen = vendues>0 ? caMois/vendues : 0;
                  const margeUnit = prixMoyen - coutRevientTheoriqueMoyen;
                  const coutOk = vendues>0 && coutRevientTheoriqueMoyen>0;
                  const seuil = (coutOk && margeUnit>0) ? Math.ceil(chargesFixes/margeUnit) : null;
                  const resultat = coutOk ? margeUnit*vendues - chargesFixes : null;
                  const reste = seuil!==null ? Math.max(0,seuil-vendues) : null;
                  const pct = seuil ? Math.min(100,Math.round(vendues/seuil*100)) : 0;
                  const ok = resultat!==null && resultat>=0;
                  return (
                    <div className="card" style={{marginBottom:20,borderColor:resultat===null?"#E3E9E6":(ok?"rgba(143,208,155,0.40)":"rgba(233,162,157,0.40)")}}>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:4}}>Seuil de rentabilité du mois</div>
                      <div style={{fontSize:11,color:"#8A978F",marginBottom:12}}>Combien de bouteilles vendre pour couvrir les salaires et les dépenses, et à partir de quand on gagne de l'argent.</div>
                      {!coutOk ? (
                        <div style={{fontSize:12,color:"#5B6B62"}}>{vendues===0?"Aucune bouteille vendue ce mois-ci : le seuil ne peut pas encore être calculé.":"Le coût de revient des produits vendus n'est pas renseigné (fiche produit ou recette) : le seuil ne peut pas être calculé."}</div>
                      ) : margeUnit<=0 ? (
                        <div style={{fontSize:12,color:"#D6453D"}}>Chaque bouteille est vendue en moyenne à {FCFA(prixMoyen)} pour un coût de revient de {FCFA(coutRevientTheoriqueMoyen)} : aucune marge, le seuil n'existe pas. Vérifie les prix et les coûts.</div>
                      ) : (
                        <div>
                          <div style={{fontSize:16,fontWeight:700,color:ok?"#1E7A2D":"#D6453D",marginBottom:10}}>
                            {ok ? "✓ Tu es dans le vert ce mois-ci : bénéfice de "+FCFA(resultat) : "✗ Tu es encore dans le rouge ce mois-ci : il manque "+FCFA(-resultat)}
                          </div>
                          <div style={{height:10,borderRadius:6,background:"#F1F5F3",overflow:"hidden",marginBottom:6}}>
                            <div style={{width:pct+"%",height:"100%",background:ok?"#2F9E3F":"#2F9E3F"}}></div>
                          </div>
                          <div style={{fontSize:11,color:"#5B6B62",marginBottom:14}}>{NUM(vendues)} bouteille(s) vendue(s) sur {NUM(seuil)} nécessaires ({pct}%)</div>
                          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:14,fontSize:13,marginBottom:12}}>
                            <div><span style={{color:"#5B6B62"}}>Bouteilles à vendre pour couvrir les dépenses</span><br/><strong style={{fontSize:16,color:"#1E8A32"}}>{NUM(seuil)} btl</strong></div>
                            <div><span style={{color:"#5B6B62"}}>{ok?"Bénéfice à partir de la bouteille n°":"Il reste à vendre"}</span><br/><strong style={{fontSize:16,color:ok?"#1E7A2D":"#D6453D"}}>{ok?NUM(seuil+1):NUM(reste)+" btl"}</strong></div>
                            <div><span style={{color:"#5B6B62"}}>Gain par bouteille au-delà du seuil</span><br/><strong style={{fontSize:16}}>{FCFA(margeUnit)}</strong></div>
                          </div>
                          <div style={{fontSize:11,color:"#8A978F",lineHeight:1.6}}>
                            Prix moyen réel : {FCFA(prixMoyen)} · coût de revient moyen : {FCFA(coutRevientTheoriqueMoyen)} · charges à couvrir : {FCFA(chargesFixes)} (salaires {FCFA(totalSalairesMois)} + autres dépenses {FCFA(totalDepensesDiversesMois)}{seuilInclureAmort?" + amortissement "+FCFA(totalAmortissementMois):""}).
                            Basé sur les charges déjà saisies pour ce mois.
                          </div>
                        </div>
                      )}
                      <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#5B6B62",marginTop:12,cursor:"pointer"}}>
                        <input type="checkbox" checked={seuilInclureAmort} onChange={e=>setSeuilInclureAmort(e.target.checked)} /> Compter aussi l'amortissement des immobilisations dans les charges
                      </label>
                    </div>
                  );
                })()}

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit,minmax(200px,1fr))",gap:12,marginBottom:24}}>
                  <div className="kpi" style={{borderColor:"rgba(143,208,155,0.27)"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:6}}>Marge brute (produit)</div><div style={{fontSize:18,color:"#1E7A2D",fontWeight:700}}>{FCFA(margeTotaleGlobale)}</div></div>
                  <div className="kpi" style={{borderColor:margeNetteGlobale>=0?"rgba(143,208,155,0.27)":"rgba(233,162,157,0.27)"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:6}}>Marge nette (charges incluses)</div><div style={{fontSize:18,color:margeNetteGlobale>=0?"#1E7A2D":"#D6453D",fontWeight:700}}>{FCFA(margeNetteGlobale)}</div></div>
                  <div className="kpi" style={{borderColor:"rgba(157,188,240,0.27)"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:6}}>Client le + rentable</div><div style={{fontSize:15,color:"#2F6FDE",fontWeight:700}}>{clientsStats[0]?.nom||"—"}</div></div>
                </div>

                <div className="card scr" style={{marginBottom:20}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Rentabilité par produit — {moisLabel}</div>
                  <table className="tbl">
                    <thead><tr><th>Produit</th><th>Prix vente</th><th>Coût direct</th><th>Marge brute/u.</th><th>Charge indir./u.</th><th>Marge nette/u.</th><th>Qté vendue</th><th>Marge nette totale</th></tr></thead>
                    <tbody>{produitsStats.map(p=>(
                      <tr key={p.id}>
                        <td style={{fontWeight:600}}>{p.nom}</td>
                        <td>{FCFA(p.prix)}</td>
                        <td style={{color:p.coutRenseigne?"#0F1F17":"#5B6B62",fontStyle:p.coutRenseigne?"normal":"italic"}}>{p.coutRenseigne?<>{FCFA(p.cout_effectif)}{p.coutDepuisRecette&&<span className="badge bg2" style={{marginLeft:6,fontSize:9}}>recette</span>}</>:"Non renseigné"}</td>
                        <td style={{color:p.margeUnitaire>=0?"#1E7A2D":"#D6453D"}}>{FCFA(p.margeUnitaire)}</td>
                        <td style={{color:"#D6453D"}}>− {FCFA(coutIndirectParBouteille)}</td>
                        <td style={{color:p.margeNetteUnitaire>=0?"#1E7A2D":"#D6453D",fontWeight:600}}>{FCFA(p.margeNetteUnitaire)}</td>
                        <td>{NUM(p.qteVendue)} btl</td>
                        <td style={{color:p.margeNetteTotale>=0?"#1E7A2D":"#D6453D",fontWeight:700}}>{FCFA(p.margeNetteTotale)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>

                <div className="card scr">
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Top clients par rentabilité nette — {moisLabel}</div>
                  <table className="tbl">
                    <thead><tr><th>Client</th><th>Commandes</th><th>Chiffre d'affaires</th><th>Marge nette estimée</th></tr></thead>
                    <tbody>{clientsStats.slice(0,15).map(c=>(
                      <tr key={c.id}>
                        <td style={{fontWeight:600}}>{c.nom}</td>
                        <td style={{color:"#5B6B62"}}>{c.nbCommandes}</td>
                        <td>{FCFA(c.ca)}</td>
                        <td style={{color:c.margeNetteEst>=0?"#1E7A2D":"#D6453D",fontWeight:700}}>{FCFA(c.margeNetteEst)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* PREUVES DE PAIEMENT */}
          {page==="demandes_depense"&&(()=>{
            const dir = isDirecteur();
            const filtre = demFiltre || (dir?"traiter":"encours");
            const groupes = dir
              ? { traiter:demandes.filter(d=>d.statut==="en_attente"), attente:demandes.filter(d=>d.statut==="a_clarifier"), acceptees:demandes.filter(d=>d.statut==="acceptee"), historique:demandes.filter(d=>["utilisee","refusee","annulee"].includes(d.statut)) }
              : { encours:demandes.filter(d=>["en_attente","a_clarifier","acceptee"].includes(d.statut)), historique:demandes.filter(d=>["utilisee","refusee","annulee"].includes(d.statut)) };
            const onglets = dir
              ? [["traiter","À décider"],["attente","En attente de réponse"],["acceptees","Acceptées"],["historique","Historique"]]
              : [["encours","En cours"],["historique","Historique"]];
            const liste = groupes[filtre] || [];
            const puce = (st) => { const x = DEM_STATUT[st]||["","","#5B6B62","#EEF3F0"]; return <span style={{fontSize:11.5,fontWeight:700,color:x[2],background:x[3],borderRadius:999,padding:"3px 10px",whiteSpace:"nowrap"}}>{dir?x[0]:x[1]}</span>; };
            return (
              <div>
                <div className="page-head">
                  <div><div className="page-title">Demandes de dépense</div><div className="page-subtitle">{dir?"Acceptez, refusez ou demandez des précisions avant que l'argent soit dépensé":"Demandez l'accord du directeur avant de dépenser"}</div></div>
                  {!dir&&<button className="btn btn-gold" onClick={()=>openModal("depense",{date:today()})}>+ Nouvelle demande</button>}
                </div>
                <div className="module-tabs">
                  {onglets.map(([k,l])=><button key={k} className={filtre===k?"active":""} onClick={()=>setDemFiltre(k)}>{l} ({(groupes[k]||[]).length})</button>)}
                </div>
                {!liste.length&&<div className="card" style={{color:"#5B6B62",fontSize:13}}>{filtre==="traiter"?"Aucune demande à décider pour le moment ✓":filtre==="encours"?"Aucune demande en cours. Pour une dépense soumise à accord, utilisez « Nouvelle demande ».":"Rien à afficher ici."}</div>}
                <div style={{display:"grid",gap:12}}>
                  {liste.map(d=>{
                    const msgs = demMsgs.filter(m=>String(m.demande_id)===String(d.id));
                    const ouvert = demAction && String(demAction.d.id)===String(d.id) ? demAction : null;
                    const ouverte = d.statut==="en_attente"||d.statut==="a_clarifier";
                    return (
                      <div className="card" key={d.id}>
                        <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",alignItems:"flex-start"}}>
                          <div style={{minWidth:0,flex:"1 1 240px"}}>
                            <div style={{fontWeight:700,fontSize:15,color:"#0F1F17",overflowWrap:"anywhere"}}>{d.libelle}</div>
                            <div style={{fontSize:12,color:"#5B6B62",marginTop:3}}>{codeLabel(d.code)}{d.fournisseur?" · "+d.fournisseur:""}{d.moyen?" · "+d.moyen:""} · pour le {fmtDate(d.date_depense)}</div>
                            {dir&&<div style={{fontSize:12,color:"#5B6B62",marginTop:2}}>Demandé par <b>{d.demandeur_nom}</b>{d.ville?" ("+d.ville+")":""} le {sigHeure(d.created_at)}</div>}
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:20,fontWeight:800,color:"#0F1F17"}}>{FCFA(d.montant)}</div>
                            {d.montant_accorde&&Number(d.montant_accorde)!==Number(d.montant)&&<div style={{fontSize:12,color:"#1E7A2D",fontWeight:700}}>accordé : {FCFA(d.montant_accorde)}</div>}
                            <div style={{marginTop:4}}>{puce(d.statut)}</div>
                          </div>
                        </div>
                        {d.explication&&<div style={{marginTop:10,fontSize:13,background:"#F7F9F8",border:"1px solid #E3E9E6",borderRadius:10,padding:"8px 11px",whiteSpace:"pre-wrap",overflowWrap:"anywhere"}}><span style={{color:"#5B6B62",fontSize:11.5}}>Explication de {dir?d.demandeur_nom:"votre part"} : </span>{d.explication}</div>}
                        {msgs.length>0&&(
                          <div style={{marginTop:10,display:"grid",gap:6}}>
                            {msgs.map(m=>(
                              <div key={m.id} style={{justifySelf:m.auteur_role==="directeur"?"start":"end",maxWidth:"92%",background:m.auteur_role==="directeur"?"#E9F0FD":"#E8F5EA",borderRadius:12,padding:"7px 11px",fontSize:13,whiteSpace:"pre-wrap",overflowWrap:"anywhere"}}>
                                <div style={{fontSize:10.5,color:"#5B6B62",marginBottom:2}}>{m.auteur_nom||(m.auteur_role==="directeur"?"Directeur":"Demandeur")} · {sigHeure(m.created_at)}</div>{m.texte}
                              </div>
                            ))}
                          </div>
                        )}
                        {d.statut==="refusee"&&d.motif&&!msgs.some(m=>m.texte===d.motif)&&<div style={{marginTop:8,fontSize:13,color:"#B3372F"}}>Motif : {d.motif}</div>}
                        {ouvert&&(
                          <div style={{marginTop:12,background:"#F7F9F8",border:"1px solid #E3E9E6",borderRadius:12,padding:12}}>
                            <div style={{fontWeight:700,fontSize:13,marginBottom:8}}>{ouvert.type==="acceptee"?"Accepter la dépense":ouvert.type==="refusee"?"Refuser la dépense":ouvert.type==="a_clarifier"?"Demander des précisions à "+d.demandeur_nom:"Répondre au directeur"}</div>
                            {ouvert.type==="acceptee"&&<div className="fld"><label className="lbl">Montant accordé (FCFA, au plus {FCFA(d.montant)})</label><input type="number" className="inp" value={ouvert.montant} onChange={e=>setDemAction({...ouvert,montant:e.target.value})} /></div>}
                            <div className="fld"><label className="lbl">{ouvert.type==="acceptee"?"Message (facultatif)":ouvert.type==="refusee"?"Motif du refus (obligatoire)":ouvert.type==="a_clarifier"?"Votre question (obligatoire)":"Votre message"}</label>
                              <textarea className="inp" rows={3} value={ouvert.texte} onChange={e=>setDemAction({...ouvert,texte:e.target.value})} style={{width:"100%",resize:"vertical"}} /></div>
                            <div style={{display:"flex",gap:8}}>
                              <button className="btn btn-gold" style={{flex:1}} disabled={demEnvoi} onClick={demEnvoyerAction}>{demEnvoi?"…":ouvert.type==="acceptee"?"Confirmer l'accord":ouvert.type==="refusee"?"Confirmer le refus":"Envoyer"}</button>
                              <button className="btn btn-ghost" onClick={()=>setDemAction(null)}>Annuler</button>
                            </div>
                          </div>
                        )}
                        {!ouvert&&(
                          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:12}}>
                            {dir&&ouverte&&<>
                              <button className="btn btn-gold btn-sm" onClick={()=>setDemAction({d,type:"acceptee",texte:"",montant:String(Math.round(toFiniteNumber(d.montant)))})}>✓ Accepter</button>
                              <button className="btn btn-ghost btn-sm" onClick={()=>setDemAction({d,type:"a_clarifier",texte:""})}>? Demander des précisions</button>
                              <button className="btn btn-ghost btn-sm" style={{color:"#B3372F"}} onClick={()=>setDemAction({d,type:"refusee",texte:""})}>✕ Refuser</button>
                            </>}
                            {!dir&&ouverte&&<>
                              <button className="btn btn-gold btn-sm" onClick={()=>setDemAction({d,type:"repondre",texte:""})}>{d.statut==="a_clarifier"?"Répondre à la question":"Ajouter une précision"}</button>
                              <button className="btn btn-ghost btn-sm" onClick={()=>demAnnuler(d)}>Annuler la demande</button>
                            </>}
                            {!dir&&d.statut==="acceptee"&&<button className="btn btn-gold btn-sm" onClick={()=>demEnregistrerDepense(d)}>Enregistrer la dépense ({FCFA(d.montant_accorde||d.montant)} maximum)</button>}
                            {dir&&d.statut==="acceptee"&&<span style={{fontSize:12,color:"#5B6B62"}}>En attente que {d.demandeur_nom} enregistre la dépense.</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {dir&&(
                  <div className="card" style={{marginTop:18}}>
                    <button type="button" onClick={()=>setDemCodesOuvert(v=>!v)} style={{background:"none",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:14,color:"#0F1F17",display:"flex",justifyContent:"space-between",width:"100%"}}>
                      <span>Dépenses sans demande (achats de production)</span><span>{demCodesOuvert?"▲":"▼"}</span>
                    </button>
                    {demCodesOuvert&&(<>
                      <div style={{fontSize:12,color:"#5B6B62",margin:"8px 0 10px"}}>Cochez les codes que vos employés peuvent dépenser sans demander l'accord (par défaut : les matières premières de production). Vos propres dépenses ne demandent jamais d'accord.</div>
                      {groupesCodes(codesPourSaisie).map(g=>(
                        <div key={g.famille} style={{marginBottom:10}}>
                          <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1,color:"#5B6B62",marginBottom:4}}>{g.famille}</div>
                          {g.items.map(c=>(
                            <label key={c.code} style={{display:"flex",alignItems:"center",gap:10,padding:"5px 0",fontSize:13.5,cursor:"pointer"}}>
                              <input type="checkbox" checked={!!c.sans_demande} onChange={()=>demBasculerCode(c)} style={{width:18,height:18,accentColor:"#2F9E3F"}} />
                              <span>{c.code} · {c.libelle}</span>
                            </label>
                          ))}
                        </div>
                      ))}
                    </>)}
                  </div>
                )}
              </div>
            );
          })()}

          {page==="annonces"&&(()=>{
            const dir = isDirecteur();
            const cibles = annonceCibles();
            const liste = [...annonces].reverse();
            const dateCourte = (d) => new Date(d).toLocaleString("fr-FR",{timeZone:"Africa/Douala",day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
            return (
              <div>
                <div className="page-head">
                  <div><div className="page-title">Annonces</div><div className="page-subtitle">{dir?"Partagez une nouveauté ou une information avec l'équipe et voyez qui l'a lue":"Les informations de la direction"}</div></div>
                  {dir&&<button className="btn btn-gold" onClick={()=>setAnnNouveau(v=>!v)}>{annNouveau?"Fermer":"+ Nouvelle annonce"}</button>}
                </div>
                {dir&&annNouveau&&<div className="card" style={{marginBottom:14}}>
                  <div className="fld"><label className="lbl">Titre</label><input type="text" className="inp" maxLength={150} placeholder="ex. Nouveau : les badges des employés" value={annTitre} onChange={e=>setAnnTitre(e.target.value)} /></div>
                  <div className="fld"><label className="lbl">Message</label><textarea className="inp" rows={6} maxLength={2000} placeholder="Écrivez l'information ou expliquez la nouveauté…" value={annMsg} onChange={e=>setAnnMsg(e.target.value)} /></div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:annIA.err||annIA.prop?8:14}}>
                    <button className="btn btn-ghost btn-sm" disabled={!!annIA.busy||annBusy} onClick={()=>annonceIA("corriger")}>{annIA.busy==="corriger"?"L'IA corrige…":"✨ Corriger les fautes"}</button>
                    <button className="btn btn-ghost btn-sm" disabled={!!annIA.busy||annBusy} onClick={()=>annonceIA("reformuler")}>{annIA.busy==="reformuler"?"L'IA reformule…":"✨ Reformuler"}</button>
                    <span style={{fontSize:11.5,color:"#5B6B62"}}>L'IA propose, vous décidez : rien n'est changé sans votre accord.</span>
                  </div>
                  {annIA.err&&<div style={{background:"#FDECEA",border:"1px solid #F3C6C1",color:"#8A2A22",borderRadius:10,padding:"9px 12px",fontSize:12.5,marginBottom:14}}>{annIA.err}</div>}
                  {annIA.prop&&<div style={{background:"#F1FAF2",border:"1px solid #BFE3C6",borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                    <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",color:"#1E7A2D",textTransform:"uppercase",marginBottom:6}}>Proposition de l'IA</div>
                    {annIA.prop.titre&&<div style={{fontWeight:700,fontSize:14,color:"#0F1F17",marginBottom:6}}>{annIA.prop.titre}</div>}
                    <div style={{fontSize:13.5,color:"#2B3A32",lineHeight:1.55,whiteSpace:"pre-line",marginBottom:10}}>{annIA.prop.message}</div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      <button className="btn btn-gold btn-sm" onClick={()=>{setAnnTitre(annIA.prop.titre);setAnnMsg(annIA.prop.message);setAnnIA({busy:"",prop:null,err:""});}}>Utiliser cette version</button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>setAnnIA({busy:"",prop:null,err:""})}>Garder la mienne</button>
                    </div>
                  </div>}
                  <div className="fld"><label className="lbl">Page à ouvrir depuis l'annonce (facultatif)</label>
                    <select className="inp" value={annPage} onChange={e=>setAnnPage(e.target.value)}>
                      <option value="">Aucune</option>
                      {navItemsAvecActivite.filter(n=>n.cat!=="permission_only"&&n.id!=="annonces").map(n=><option key={n.id} value={n.id}>{n.label}</option>)}
                    </select></div>
                  <div style={{fontSize:12,color:"#5B6B62",marginBottom:10}}>Envoyée à {cibles.length} employé(s) actif(s) : fenêtre à l'ouverture de l'application + notification (application et téléphone). Vous êtes prévenu à chaque lecture.</div>
                  <button className="btn btn-gold" disabled={annBusy||!annTitre.trim()||!annMsg.trim()} onClick={annoncePublier}>{annBusy?"...":"Envoyer à l'équipe"}</button>
                </div>}
                {!liste.length&&<div className="card" style={{color:"#5B6B62",fontSize:13}}>Aucune annonce pour le moment.</div>}
                <div style={{display:"grid",gap:10}}>
                  {liste.map(a=>{
                    const lus = cibles.filter(e=>annonceLuPar(a,e));
                    const nonLus = cibles.filter(e=>!annonceLuPar(a,e));
                    const maLecture = !dir && annLectures.find(l=>String(l.annonce_id)===String(a.id));
                    return (
                      <details key={a.id} className="card" open={dir?nonLus.length>0:!maLecture} style={{padding:"12px 14px"}}>
                        <summary style={{cursor:"pointer",display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",alignItems:"center"}}>
                          <span><span style={{fontWeight:700,fontSize:14,color:"#0F1F17"}}>📢 {a.titre}</span><span style={{fontSize:11,color:"#5B6B62",marginLeft:8}}>{dateCourte(a.created_at)}</span></span>
                          {dir
                            ? <span style={{fontSize:11.5,fontWeight:700,color:nonLus.length?"#A8680A":"#1E7A2D",background:nonLus.length?"#FFF4DD":"#E8F5EA",borderRadius:999,padding:"3px 10px"}}>{lus.length} / {cibles.length} ont lu</span>
                            : <span style={{fontSize:11.5,fontWeight:700,color:maLecture?"#1E7A2D":"#A8680A",background:maLecture?"#E8F5EA":"#FFF4DD",borderRadius:999,padding:"3px 10px"}}>{maLecture?"Lu":"À lire"}</span>}
                        </summary>
                        <div style={{fontSize:13.5,color:"#2B3A32",lineHeight:1.55,whiteSpace:"pre-line",margin:"12px 0"}}>{a.message}</div>
                        {a.page&&<button className="btn btn-ghost btn-sm" onClick={()=>setPage(a.page)}>Ouvrir la page</button>}
                        {!dir&&!maLecture&&<button className="btn btn-gold btn-sm" style={{marginLeft:a.page?8:0}} disabled={annBusy} onClick={()=>annonceLire(a,false)}>J'ai lu et compris</button>}
                        {dir&&<>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(230px,1fr))",gap:6,marginTop:12}}>
                            {cibles.map(e=>{ const l=annonceLuPar(a,e); return (
                              <div key={e.id} style={{fontSize:12.5,display:"flex",justifyContent:"space-between",gap:8,padding:"6px 9px",borderRadius:8,background:l?"#F1FAF2":"#FFF8EA"}}>
                                <span style={{fontWeight:600,color:"#0F1F17"}}>{[e.prenom,e.nom].filter(Boolean).join(" ")}</span>
                                <span style={{color:l?"#1E7A2D":"#A8680A",whiteSpace:"nowrap"}}>{l?"✓ "+new Date(l.lu_le).toLocaleString("fr-FR",{timeZone:"Africa/Douala",day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}):"pas encore lu"}</span>
                              </div>); })}
                          </div>
                          {nonLus.length>0&&<button className="btn btn-ghost btn-sm" style={{marginTop:12}} disabled={annBusy} onClick={()=>annonceRappeler(a)}>Rappeler ceux qui n'ont pas lu ({nonLus.length})</button>}
                        </>}
                      </details>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {page==="a_cocher"&&(()=>{
            const tout = acListe||[];
            const aCocher = tout.filter(f=>!f.regul_statut && Number(f.reste)>0);
            const traitees = tout.filter(f=>!!f.regul_statut);
            const aValider = traitees.filter(f=>f.regul_statut==="À valider");
            const q = acRecherche.trim().toLowerCase();
            const liste = (acVue==="a_cocher"?aCocher:traitees)
              .filter(f=>!q || [f.numero,f.client_nom].filter(Boolean).join(" ").toLowerCase().includes(q));
            const resteTotal = aCocher.reduce((s,f)=>s+Number(f.reste||0),0);
            const groupes = {};
            liste.forEach(f=>{ const nom=(f.client_nom||"").trim()||"Client non défini"; (groupes[nom]=groupes[nom]||[]).push(f); });
            const noms = Object.keys(groupes).sort((a,b)=>a.localeCompare(b,"fr"));
            const peutValider = canValidatePayments();
            const defMoyen = (f) => {
              if (Object.prototype.hasOwnProperty.call(AC_PRESET,f.numero)) return AC_PRESET[f.numero];
              const p = paiements.find(x=>isPaiementValide(x)&&x.client_nom===f.client_nom&&x.moyen);
              return p ? p.moyen : "Espèces";
            };
            const COUL = {oui:["#2F9E3F","#FFFFFF"], non:["#D6453D","#FFFFFF"], part:["#B7791F","#FFFFFF"]};
            const bouton = (f,id,label) => {
              const sel = acDraft[f.numero]&&acDraft[f.numero].choix===id;
              return <button type="button" className="ac-b" disabled={!!acBusy}
                style={{background:sel?COUL[id][0]:"#FFFFFF",color:sel?COUL[id][1]:"#0F1F17",borderColor:sel?COUL[id][0]:"#CBD5D0"}}
                onClick={()=>acChoisir(f.numero,id,defMoyen(f))}>{label}</button>;
            };
            const badgeTraite = (s) => s==="Payée"?["#E3F3E6","#1E7A2D","Payée"]:s==="Partiel"?["#FFF3DC","#8A5A00","Partiel"]:s==="À valider"?["#E8EEFB","#2F4F9E","En attente de validation"]:["#FBE4E2","#B5332C","Non payée"];
            return (
              <div>
                <style>{`.ac-b{flex:1;min-height:42px;border:1.5px solid #CBD5D0;border-radius:10px;font-weight:700;font-size:14px;cursor:pointer;font-family:inherit}
.ac-row{padding:12px 14px;margin-bottom:10px}
.ac-sel{width:100%;min-height:42px;font-size:16px}`}</style>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:isMobile?22:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Factures à cocher</div>
                  <div style={{fontSize:12,color:"#5B6B62",marginTop:4}}>Anciennes factures d'avant l'ERP : cochez Oui / Non / Partiel, puis appuyez sur « Enregistrer » sous le client.{peutValider?" Vos « Oui » sont validés tout de suite.":" Vos paiements sont envoyés à la direction pour validation."}</div>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12,alignItems:"center"}}>
                  <button className={"btn "+(acVue==="a_cocher"?"btn-gold":"btn-ghost")} onClick={()=>setAcVue("a_cocher")}>À cocher ({aCocher.length})</button>
                  <button className={"btn "+(acVue==="traitees"?"btn-gold":"btn-ghost")} onClick={()=>setAcVue("traitees")}>Déjà traitées ({traitees.length})</button>
                  <button className="btn btn-ghost btn-sm" disabled={!!acBusy} onClick={acCharger}>↻ Actualiser</button>
                </div>
                {peutValider&&aValider.length>0&&<div className="card" style={{padding:"10px 14px",marginBottom:12,border:"1.5px solid #2F4F9E",background:"#F3F6FD",fontSize:13,color:"#2F4F9E"}}>
                  <strong>{aValider.length} paiement(s) saisi(s) par l'équipe attendent votre validation.</strong>
                  <button className="btn btn-sm btn-gold" style={{marginLeft:10}} onClick={()=>setAcVue("traitees")}>Voir</button>
                </div>}
                {acVue==="a_cocher"&&<div className="card" style={{padding:"10px 14px",marginBottom:12,fontSize:13,color:"#5B6B62"}}>
                  Reste à encaisser sur cette liste : <strong style={{color:"#0F1F17"}}>{FCFA(resteTotal)}</strong> · {aCocher.length} facture(s)
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginTop:8}}>
                    <span>Date enregistrée pour les paiements :</span>
                    <button className={"btn btn-sm "+(acDate==="facture"?"btn-gold":"btn-ghost")} onClick={()=>setAcDate("facture")}>Date de la facture</button>
                    <button className={"btn btn-sm "+(acDate==="jour"?"btn-gold":"btn-ghost")} onClick={()=>setAcDate("jour")}>Aujourd'hui</button>
                  </div>
                </div>}
                <input className="inp" aria-label="Rechercher" placeholder="Rechercher un client ou un numéro…" value={acRecherche} onChange={e=>setAcRecherche(e.target.value)} style={{marginBottom:12,fontSize:16}}/>
                {acErreurs.length>0&&<div className="card" style={{padding:"10px 14px",marginBottom:12,border:"1px solid #D6453D",color:"#B5332C",fontSize:13}}>
                  <strong>Problème :</strong>
                  {acErreurs.map((e,i)=><div key={i}>• {e}</div>)}
                </div>}
                {acListe===null&&<div className="card" style={{padding:20,textAlign:"center",color:"#5B6B62"}}>Chargement…</div>}
                {acListe!==null&&noms.length===0&&<div className="card" style={{padding:20,textAlign:"center",color:"#5B6B62"}}>{acVue==="a_cocher"?"Plus aucune facture à cocher 🎉":"Aucune facture traitée pour le moment."}</div>}
                {noms.map(nom=>{
                  const fs = groupes[nom];
                  const resteG = fs.reduce((s,f)=>s+Number(f.reste||0),0);
                  const nbDraft = fs.filter(f=>acDraft[f.numero]).length;
                  return (
                    <div key={nom} style={{marginBottom:18}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap",margin:"4px 2px 8px"}}>
                        <div style={{fontWeight:700,color:"#0F1F17",fontSize:15}}>{nom} <span style={{fontWeight:400,color:"#5B6B62",fontSize:12}}>· {fs.length} facture(s){acVue==="a_cocher"?" · reste "+FCFA(resteG):""}</span></div>
                        {acVue==="a_cocher"&&fs.length>1&&<button className="btn btn-ghost btn-sm" disabled={!!acBusy} onClick={()=>setAcDraft(d=>{const n={...d}; fs.forEach(f=>{ n[f.numero]={choix:"oui",montant:"",moyen:(d[f.numero]&&d[f.numero].moyen)||defMoyen(f)}; }); return n;})}>Tout « Oui »</button>}
                      </div>
                      {fs.map(f=>{
                        const reste = Math.round(Number(f.reste)||0);
                        const ttc = Math.round(Number(f.ttc)||0);
                        const d = acDraft[f.numero];
                        if (acVue==="traitees") {
                          const [bg,fg,txt] = badgeTraite(f.regul_statut);
                          const attente = f.regul_statut==="À valider";
                          return <div key={f.numero} className="card ac-row" style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                            <div><strong>{f.numero}</strong> <span style={{fontSize:12,color:"#5B6B62"}}>· {fmtDate(f.date_emission)} · {FCFA(ttc)}</span>
                              <div style={{fontSize:11,color:"#5B6B62",marginTop:2}}>
                                {attente?"Paiement saisi : "+FCFA(f.attente_montant)+" · "+(f.attente_moyen||"—")+" · ":""}
                                {f.regul_par?"par "+f.regul_par:""}{f.regul_statut==="Partiel"&&reste>0?" · reste "+FCFA(reste):""}
                              </div></div>
                            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                              <span style={{padding:"4px 10px",borderRadius:20,background:bg,color:fg,fontSize:12,fontWeight:700}}>{txt}</span>
                              {attente&&peutValider&&<button className="btn btn-gold btn-sm" disabled={!!acBusy} onClick={()=>acValider(f)}>✓ Valider</button>}
                              {(f.regul_statut==="Non payée"||attente)&&<button className="btn btn-ghost btn-sm" disabled={!!acBusy} onClick={()=>acRemettre(f)}>{attente?"Annuler":"Remettre à cocher"}</button>}
                            </div>
                          </div>;
                        }
                        return (
                          <div key={f.numero} className="card ac-row" style={d?{borderColor:COUL[d.choix][0],borderWidth:2}:undefined}>
                            <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:10}}>
                              <div><strong style={{fontSize:15}}>{f.numero}</strong> <span style={{fontSize:12,color:"#5B6B62"}}>· {fmtDate(f.date_emission)}</span></div>
                              <div style={{textAlign:"right",fontSize:13}}><span style={{color:"#5B6B62"}}>Reste : </span><strong style={{color:"#1E8A32"}}>{FCFA(reste)}</strong>{reste<ttc&&<span style={{color:"#5B6B62",fontSize:11}}> (sur {FCFA(ttc)})</span>}</div>
                            </div>
                            <div style={{display:"flex",gap:8}}>{bouton(f,"oui","Oui, payée")}{bouton(f,"non","Non")}{bouton(f,"part","Partiel")}</div>
                            {d&&d.choix!=="non"&&<div style={{display:"grid",gridTemplateColumns:d.choix==="part"?"1fr 1fr":"1fr",gap:8,marginTop:10}}>
                              {d.choix==="part"&&<div><label className="lbl">Montant reçu (FCFA)</label><input type="number" inputMode="numeric" className="inp" style={{fontSize:16}} value={d.montant} placeholder={"max "+(reste-1)} onChange={e=>acModif(f.numero,"montant",e.target.value)}/></div>}
                              <div><label className="lbl">Mode de paiement{!d.moyen&&<span style={{color:"#D6453D"}}> (à choisir)</span>}</label>
                                <select className="inp ac-sel" value={d.moyen||""} onChange={e=>acModif(f.numero,"moyen",e.target.value)}>
                                  <option value="">Choisir…</option>{MOYENS_REGLEMENT.filter(Boolean).map(m=><option key={m} value={m}>{m}</option>)}
                                </select></div>
                            </div>}
                          </div>
                        );
                      })}
                      {acVue==="a_cocher"&&<button type="button" className="btn btn-gold" style={{width:"100%",minHeight:46,fontSize:15,opacity:nbDraft?1:.45}} disabled={!!acBusy||!nbDraft} onClick={()=>acEnregistrerClient(nom,fs)}>
                        {acBusy===nom?"Enregistrement…":(nbDraft?"Enregistrer "+nom+" ("+nbDraft+")":"Enregistrer "+nom)}
                      </button>}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {page==="paiements"&&(isDirecteur()||can("paiements"))&&(()=>{
            const filtered = paiements.filter(p=>{
              if (paiementFilter==="tous") return true;
              if (paiementFilter==="attente") return String(p.statut||"").toLowerCase().includes("attente");
              if (paiementFilter==="valides") return String(p.statut||"").toLowerCase().startsWith("valid");
              return String(p.statut||"").toLowerCase().startsWith("refus");
            });
            const nbAttente=paiements.filter(p=>String(p.statut||"").toLowerCase().includes("attente")).length;
            const nbValides=paiements.filter(p=>String(p.statut||"").toLowerCase().startsWith("valid")).length;
            const nbRefuses=paiements.filter(p=>String(p.statut||"").toLowerCase().startsWith("refus")).length;
            const badge=(statut)=>{
              const st=String(statut||"").toLowerCase();
              if(st.startsWith("valid")) return {bg:"rgba(47,158,63,0.168)",bd:"#1E7A2D",fg:"#1E7A2D",txt:"Validé"};
              if(st.startsWith("refus")) return {bg:"rgba(192,57,43,.18)",bd:"#D6453D",fg:"#D6453D",txt:"Refusé"};
              return {bg:"rgba(47,158,63,0.104)",bd:"#8A6D35",fg:"#1E8A32",txt:"En attente"};
            };
            return <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:16}}>
                <div>
                  <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Preuves de paiement</div>
                  <div style={{fontSize:12,color:"#5B6B62",marginTop:4}}>Dépôt des justificatifs et validation par la direction</div>
                </div>
                <button className="btn btn-gold btn-icon" onClick={openPaiementModal}><CreditCard size={15}/> Déposer une preuve</button>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
                {[["tous","Tous",paiements.length],["attente","En attente",nbAttente],["valides","Validés",nbValides],["refuses","Refusés",nbRefuses]].map(([id,label,n])=>(
                  <button key={id} className={"btn "+(paiementFilter===id?"btn-gold":"btn-ghost")} onClick={()=>setPaiementFilter(id)}>{label} ({n})</button>
                ))}
              </div>
              <div className="card" style={{padding:0,overflowX:"auto"}}>
                <table className="tbl" style={{minWidth:1100,tableLayout:"fixed"}}>
                  <colgroup>
                    <col style={{width:135}} />
                    <col style={{width:170}} />
                    <col style={{width:135}} />
                    <col style={{width:190}} />
                    <col style={{width:190}} />
                    <col style={{width:140}} />
                    <col style={{width:240}} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Facture</th>
                      <th>Client</th>
                      <th>Versement</th>
                      <th>Moyen / Référence</th>
                      <th>Soumis par</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length===0&&<tr><td colSpan="7" style={{textAlign:"center",padding:30,color:"#5B6B62"}}>Aucune preuve de paiement</td></tr>}
                    {filtered.map(p=>{const b=badge(p.statut);return <tr key={p.id}>
                      <td><button className="btn-linklike" style={{fontWeight:700,color:"#0F1F17",background:"none",border:"none",padding:0,cursor:"pointer",textDecoration:"underline",fontSize:"inherit"}} onClick={()=>{const f=factures.find(x=>x.numero===p.facture_numero);if(f) setFacView({facture:f,client:clients.find(c=>c.nom===f.client_nom)}); else showToast("Facture introuvable",false);}}>{p.facture_numero}</button><div style={{fontSize:11,color:"#5B6B62"}}>{p.date_versement?fmtDate(p.date_versement):""}</div></td>
                      <td>{p.client_nom||"—"}</td>
                      <td style={{fontWeight:700,color:"#1E8A32"}}>{FCFA(p.montant)}</td>
                      <td><div>{p.moyen||"—"}</div>{p.reference_virement&&<div style={{fontSize:11,color:"#5B6B62"}}>{p.reference_virement}</div>}</td>
                      <td><div>{p.soumis_par||"—"}</div><div style={{fontSize:11,color:"#5B6B62"}}>{p.date_soumission?new Date(p.date_soumission).toLocaleString("fr-FR"):""}</div>{p.soumis_par==="Client (lien de paiement)"&&String(p.statut||"").toLowerCase().includes("attente")&&<div style={{fontSize:11,color:"#7A4B00",background:"#FFF4DD",borderRadius:8,padding:"4px 8px",marginTop:5,maxWidth:210}}>Envoyé par le client — vérifiez sur le relevé avant de valider</div>}</td>
                      <td><span style={{display:"inline-block",padding:"4px 9px",borderRadius:20,background:b.bg,border:"1px solid "+b.bd,color:b.fg,fontSize:11}}>{b.txt}</span>{p.commentaire&&<div title={p.commentaire} style={{fontSize:11,color:"#5B6B62",marginTop:5,maxWidth:180,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.commentaire}</div>}</td>
                      <td><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>openPaiementProof(p.preuve_url)}><Eye size={13}/> Voir</button>
                        {canValidatePayments()&&String(p.statut||"").toLowerCase().includes("attente")&&<>
                          <button className="btn btn-sm" style={{background:"#E3F3E6",color:"#FFFFFF"}} disabled={loading} onClick={()=>{ if (p.soumis_par==="Client (lien de paiement)" && !window.confirm("Ce paiement a été envoyé par le CLIENT depuis le lien de paiement.\n\nL'avez-vous vérifié sur le relevé MTN / Orange / bancaire ?\n\nValidez seulement si l'argent est bien reçu.")) return; decidePaiement(p,"Validé"); }}>✓ Valider</button>
                          <button className="btn btn-red btn-sm" disabled={loading} onClick={()=>decidePaiement(p,"Refusé")}>✕ Refuser</button>
                        </>}
                        {isDirecteur()&&/refus/i.test(String(p.statut||""))&&<>
                          <button className="btn btn-gold btn-sm" disabled={loading} onClick={()=>ouvrirModifPaiement(p)}>✏ Corriger</button>
                          <button className="btn btn-red btn-sm" disabled={loading} onClick={()=>supprimerPaiementRefuse(p)}>Supprimer</button>
                        </>}
                      </div></td>
                    </tr>})}
                  </tbody>
                </table>
              </div>
            </div>;
          })()}

          {/* FACTURES */}
          {page==="factures"&&(isDirecteur()||can("factures"))&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Factures</div>
                <div style={{display:"flex",gap:8}}>
                  {can("proformas")&&<button className="btn btn-purple btn-sm" onClick={()=>setPage("nouvelle-proforma")}>+ Proforma</button>}
                  <button className="btn btn-gold" onClick={()=>{setFacForm(f=>({...f,ville:f.ville||(isDirecteur()?"":(session.ville||""))}));setPage("nouvelle-facture");}}>+ Nouvelle facture</button>
                </div>
              </div>
              <div style={{position:"relative",marginBottom:14,maxWidth:520}}>
                <Search size={17} style={{
                  position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",
                  color:"#1E8A32",pointerEvents:"none"
                }}/>
                <input
                  className="inp"
                  value={factureSearch}
                  onChange={e=>setFactureSearch(e.target.value)}
                  placeholder="Rechercher par numéro, client, date ou règlement…"
                  aria-label="Rechercher une facture"
                  style={{
                    width:"100%",paddingLeft:42,paddingRight:factureSearch?42:14,
                    borderColor:factureSearch?"#2F9E3F":"#E3E9E6"
                  }}
                />
                {factureSearch&&(
                  <button
                    type="button"
                    onClick={()=>setFactureSearch("")}
                    aria-label="Effacer la recherche"
                    style={{
                      position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",
                      width:28,height:28,borderRadius:"50%",border:"none",
                      background:"rgba(47,158,63,0.128)",color:"#0F1F17",
                      cursor:"pointer",display:"grid",placeItems:"center",fontSize:16
                    }}
                  >×</button>
                )}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:14}}>
                <div style={{display:"inline-flex",border:"1px solid #CBD5D0",borderRadius:999,overflow:"hidden",background:"#fff"}}>
                  <button type="button" onClick={()=>changerFactureVue("client")} style={{border:"none",padding:"8px 16px",cursor:"pointer",fontSize:13,fontWeight:700,background:factureVue==="client"?"#2F9E3F":"transparent",color:factureVue==="client"?"#fff":"#5B6B62"}}>Par client</button>
                  <button type="button" onClick={()=>changerFactureVue("liste")} style={{border:"none",padding:"8px 16px",cursor:"pointer",fontSize:13,fontWeight:700,background:factureVue==="liste"?"#2F9E3F":"transparent",color:factureVue==="liste"?"#fff":"#5B6B62"}}>Liste complète</button>
                </div>
                {factureVue==="client"&&!factureSearch.trim()&&(
                  <>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={()=>{const o={};factures.forEach(f=>{o[(f.client_nom||"").trim()||"Client non défini"]=true;});setFacOuverts(o);}}>Tout ouvrir</button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={()=>setFacOuverts({})}>Tout fermer</button>
                  </>
                )}
              </div>
              <div style={{display:"flex",gap:16,marginBottom:16,flexWrap:"wrap"}}>
                <div onClick={()=>setFactureStatutFilter(f=>f==="payee"?"tous":"payee")}
                  style={{background:"rgba(47,158,63,0.120)",border:"1px solid "+(factureStatutFilter==="payee"?"#8FD09B":"#BFE3C6"),borderRadius:6,padding:"8px 16px",fontSize:13,cursor:"pointer"}}>
                  <span style={{color:"#1E7A2D",fontWeight:700}}>{facPayees}</span> <span style={{color:"#5B6B62"}}>payée(s)</span>
                </div>
                <div onClick={()=>setFactureStatutFilter(f=>f==="attente"?"tous":"attente")}
                  style={{background:"rgba(192,57,43,.1)",border:"1px solid "+(factureStatutFilter==="attente"?"#E9A29D":"rgba(192,57,43,.3)"),borderRadius:6,padding:"8px 16px",fontSize:13,cursor:"pointer"}}>
                  <span style={{color:"#D6453D",fontWeight:700}}>{facAttente}</span> <span style={{color:"#5B6B62"}}>en attente</span>
                </div>
                {factureStatutFilter!=="tous"&&<button className="btn btn-ghost btn-sm" onClick={()=>setFactureStatutFilter("tous")}>✕ Effacer le filtre</button>}
                <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>setShowCreancesClients(v=>!v)}>
                  <User size={13}/> {showCreancesClients?"Masquer":"Voir"} les clients avec impayés
                </button>
                <button className="btn btn-gold btn-sm btn-icon" disabled={reportGenerating} onClick={async()=>{
                  setReportGenerating(true);
                  try {
                    const pdf = await generateCreancesPdfBase64(entreprise, clients, factures, paiements);
                      await openPdf(pdf,"Document Azongbio");
                  } catch(e) { showToast("Erreur : "+e.message, false); }
                  setReportGenerating(false);
                }}><FileText size={13}/> {reportGenerating?"...":"Liste des créances (PDF)"}</button>
                <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>{
                  setDocSendModal({kind:"creances", data:{}});
                  setDocSendForm({to:"", toName:session.nom, subject:"Liste des créances clients — "+entreprise.entreprise_nom, message:"Veuillez trouver ci-joint la liste des clients avec factures impayées."});
                }}><Mail size={13}/> Envoyer</button>
                <div style={{fontSize:12,color:"#1E8A32",display:"flex",alignItems:"center"}}>← Cliquer sur une pastille pour filtrer, ou sur le statut d'une ligne pour valider</div>
              </div>
              {showCreancesClients&&(()=>{
                const parClient={};
                factures.filter(f=>resteFacture(f)>0).forEach(f=>{
                  const nom=f.client_nom||"Client non défini";
                  parClient[nom]=parClient[nom]||{nb:0,montant:0};
                  parClient[nom].nb+=1; parClient[nom].montant+=resteFacture(f);
                });
                const liste=Object.entries(parClient).sort((a,b)=>b[1].montant-a[1].montant);
                return (
                  <div className="card" style={{marginBottom:16}}>
                    <div style={{fontSize:13,color:"#1E8A32",marginBottom:12,fontWeight:600}}>Clients avec factures impayées ({liste.length})</div>
                    {liste.length===0?(
                      <div style={{color:"#5B6B62",fontSize:13,textAlign:"center",padding:16}}>Aucun client débiteur — tout est réglé ✓</div>
                    ):(
                      <table className="tbl">
                        <thead><tr><th>Client</th><th>Factures impayées</th><th>Montant dû</th></tr></thead>
                        <tbody>{liste.map(([nom,info])=>(
                          <tr key={nom}>
                            <td style={{fontWeight:600}}>{nom}</td>
                            <td style={{color:"#5B6B62"}}>{info.nb}</td>
                            <td style={{color:"#D6453D",fontWeight:700}}>{FCFA(info.montant)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    )}
                  </div>
                );
              })()}
              {factures.length===0?(
                <div className="card" style={{textAlign:"center",padding:50,color:"#5B6B62"}}>
                  <div style={{display:"flex",justifyContent:"center",marginBottom:12,color:"#5B6B62"}}><Receipt size={40}/></div>
                  <button className="btn btn-gold" onClick={()=>{setFacForm(f=>({...f,ville:f.ville||(isDirecteur()?"":(session.ville||""))}));setPage("nouvelle-facture");}}>Créer une facture</button>
                </div>
              ):(
                <div className="card scr">
                  {factureSearch&&factures.filter(f=>{
                    const statusOk = factureStatutFilter==="tous" ||
                      (factureStatutFilter==="payee" ? resteFacture(f)<=0 : resteFacture(f)>0);
                    const query = factureSearch.trim().toLowerCase();
                    if (!statusOk) return false;
                    const searchable = [
                      f.numero,f.client_nom,f.date_emission,f.date_echeance,f.reglement,f.statut
                    ].filter(Boolean).join(" ").toLowerCase();
                    return searchable.includes(query);
                  }).length===0&&(
                    <div style={{padding:"20px 16px",textAlign:"center",color:"#5B6B62",fontSize:13}}>
                      Aucune facture ne correspond à « {factureSearch} ».
                    </div>
                  )}
                  <table className={"tbl"+(isMobile?" az-mcards":"")}>
                    <thead><tr><th>N° Facture</th><th>Client</th><th>Date</th><th>Échéance</th><th>Total TTC</th><th>Règlement</th><th>Statut</th><th>Actions</th></tr></thead>
                    <tbody>{facItems.map(f=>{
                      if (f.__groupe) return (
                        <tr key={f.id} className="grp">
                          <td colSpan={8} style={{padding:0,background:"#EAF5EC"}}>
                            <div onClick={()=>setFacOuverts(o=>({...o,[f.nom]:!f.ouvert}))} role="button" tabIndex={0}
                              style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",padding:"12px 14px",cursor:"pointer"}}>
                              <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                                <span style={{fontSize:14,color:"#1E8A32",width:14}}>{f.ouvert?"▾":"▸"}</span>
                                <span style={{fontSize:15,fontWeight:700,color:"#0F1F17"}}>{f.nom}</span>
                                <span style={{fontSize:12,color:"#5B6B62"}}>· {f.nb} facture{f.nb>1?"s":""}</span>
                              </div>
                              <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:12,color:"#5B6B62"}}>
                                <span>Total <strong style={{color:"#0F1F17"}}>{FCFA(f.total)}</strong></span>
                                <span>Payé <strong style={{color:"#1E7A2D"}}>{FCFA(f.paye)}</strong></span>
                                <span>Reste <strong style={{color:f.reste>0?"#D6453D":"#1E7A2D"}}>{f.reste>0?FCFA(f.reste):"soldé ✓"}</strong></span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                      const {ttc}=calculateInvoiceTotals(f,entreprise);
                      const paid=totalPayeValide(f);
                      const remaining=Math.max(0,ttc-paid);
                      const pay=remaining<=0||f.statut==="Payée";
                      const partial=!pay&&paid>0;
                      const cli=clients.find(c=>c.nom===f.client_nom);
                      return (
                        <tr key={f.id} className={pay?"rp":"ra"}>
                          <td style={{fontFamily:"monospace",fontSize:11,color:"#1E8A32"}}>{f.numero}{(()=>{ const n=avaries.filter(a=>String(a.facture_id)===String(f.id)); return n.length?<div title={n.map(a=>a.quantite+" "+a.produit+" — "+(a.remplacement||"")).join("\n")} style={{fontFamily:"inherit",fontSize:10,color:"#D6453D",marginTop:2}}>⚠ {n.length} avarie{n.length>1?"s":""}</div>:null; })()}</td>
                          <td>{f.client_nom}</td>
                          <td style={{color:"#5B6B62"}}>{fmtDate(f.date_emission)}</td>
                          <td style={{color:"#5B6B62"}}>{fmtDate(f.date_echeance)}</td>
                          <td style={{fontWeight:600}}>{FCFA(ttc)}</td>
                          <td style={{fontSize:11,color:"#5B6B62"}}>{f.reglement||"—"}</td>
                          <td>
                            <span className={"badge "+(pay?"bg":partial?"bg2":"br")} title={partial?"Reste dû : "+FCFA(remaining):"Statut piloté par les paiements validés"}>
                              {pay?"✓ Payée":partial?"◐ Partiellement payée":"● En attente"}
                            </span>
                          </td>
                          <td>
                            <div style={{display:"flex",gap:5}}>
                              <button className="btn btn-ghost btn-sm" onClick={()=>setFacView({facture:f,client:cli})}><Eye size={13}/></button>
                              <button className="btn btn-ghost btn-sm" title="Bon de livraison" disabled={reportGenerating} onClick={()=>imprimerBonLivraison(f,cli)}><Truck size={13}/></button>
                              {!pay&&<button className="btn btn-ghost btn-sm" style={{color:"#1E7A2D",borderColor:"#BFE3C6"}} title="Relancer par WhatsApp" onClick={()=>{ if(!openRelanceWhatsApp(f,cli,entreprise,remaining,ttc)) showToast("Téléphone du client manquant : ajoutez-le dans la fiche client", false); }}><MessageCircle size={13}/></button>}
                              {paid>0&&<button className="btn btn-ghost btn-sm" title="Historique des paiements" onClick={()=>setPaiementsFactureView(f)}>💰 {partial?FCFA(remaining):""}</button>}
                              {(canEdit())&&<button className="btn btn-ghost btn-sm" onClick={()=>setEditFac({...f,lignes:Array.isArray(f.lignes)?f.lignes:safeJsonParse(f.lignes,[])})}>✏</button>}
                              {cli?.email&&<button className="btn btn-ghost btn-sm" onClick={()=>{
                                const {ttc}=calculateInvoiceTotals(f,entreprise);
                                setEmailForm({
                                  to:cli.email, subject:"Facture "+f.numero+" — "+entreprise.entreprise_nom,
                                  facture_numero:f.numero, client_nom:f.client_nom,
                                  date_emission:fmtDate(f.date_emission),
                                  date_echeance:fmtDate(f.date_echeance),
                                  montant_ttc:FCFA(ttc),
                                  lignes:f.lignes.map(l=>l.designation+" x"+l.qte+" = "+FCFA(l.montant)).join(", "),
                                  message:"Veuillez trouver ci-dessous votre facture."
                                });
                                setEmailModal({facture:f, client:cli});
                              }}><Mail size={13}/></button>}
                              {canDeleteInvoices()&&<button className="btn btn-red btn-sm" onClick={()=>deleteFacture(f.id)} title="Supprimer la facture"><Trash2 size={13}/></button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* NOUVELLE FACTURE */}
          {page==="nouvelle-facture"&&(isDirecteur()||can("factures"))&&(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPage("factures")}>← Retour</button>
                <div>
                  <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Nouvelle facture</div>
                  <div style={{fontSize:12,color:"#1E7A2D",marginTop:4}}>✓ Les ventes et le stock seront mis à jour automatiquement</div>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1.4fr",gap:18,marginBottom:18}}>
                <div className="card">
                  <div className="fld"><label className="lbl">Client *</label>
                    <select className="inp" value={facForm.clientCode||""} onChange={e=>{const code=e.target.value;const cli=clients.find(c=>c.code===code);const vc=(cli?.ville||"").trim();const aDepot=!!vc&&depots.some(d=>(d.ville||"").trim()===vc);setFacForm(f=>({...f,clientNom:cli?cli.nom:"",clientCode:code,ville:aDepot?vc:f.ville}));}}>
                      <option value="">Choisir...</option>
                      {clients.map(c=><option key={c.id} value={c.code}>{etiquetteClient(c)}</option>)}
                    </select>
                  </div>
                  {(()=>{
                    const cli=trouverClient(facForm.clientNom,facForm.clientCode);
                    const vc=(cli?.ville||"").trim();
                    const auto=!!vc&&depots.some(d=>(d.ville||"").trim()===vc);
                    if(auto) return (
                      <div className="fld"><label className="lbl">Ville (stock déduit ici)</label>
                        <input className="inp" readOnly value={vc} style={{background:"#F1F5F9",cursor:"not-allowed",fontWeight:700}} />
                        <div style={{fontSize:10,color:"#5B6B62",marginTop:4}}>Automatique : le stock est déduit du dépôt de la ville du client.</div>
                      </div>
                    );
                    return (
                      <div className="fld"><label className="lbl">Ville (stock déduit ici) *</label>
                        {facForm.clientNom && canSeeAllCities() ? (
                          <select className="inp" value={facForm.ville} onChange={e=>setFacForm(f=>({...f,ville:e.target.value}))}>
                            <option value="">Choisir...</option>
                            {[...new Set(depots.map(d=>d.ville).filter(Boolean))].sort().map(v=><option key={v}>{v}</option>)}
                          </select>
                        ) : (
                          <input className="inp" readOnly value={facForm.clientNom?(facForm.ville||""):""} placeholder="Choisissez d'abord un client" style={{background:"#F1F5F9",cursor:"not-allowed"}} />
                        )}
                        <div style={{fontSize:10,color:"#5B6B62",marginTop:4}}>{facForm.clientNom?"Aucun dépôt dans la ville de ce client (ou ville non renseignée sur sa fiche).":"La ville se remplit automatiquement avec celle du client."}</div>
                      </div>
                    );
                  })()}
                  <div className="fld"><label className="lbl">Date émission</label><input type="date" className="inp" value={facForm.dateEmission} onChange={e=>setFacForm(f=>({...f,dateEmission:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Date échéance</label><input type="date" className="inp" value={facForm.dateEcheance} onChange={e=>setFacForm(f=>({...f,dateEcheance:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Remise (%)</label><input type="number" className="inp" min="0" max="100" value={facForm.remisePct||""} onChange={e=>setFacForm(f=>({...f,remisePct:parseFloat(e.target.value)||0}))} /></div>
                  <div className="fld"><label className="lbl">Statut</label>
                    <select className="inp" value={facForm.statut} onChange={e=>setFacForm(f=>({...f,statut:e.target.value}))}>
                      <option>En attente</option><option>Payée</option>
                    </select>
                  </div>
                  {/* MOYEN DE RÈGLEMENT */}
                  <div className="fld">
                    <label className="lbl">Moyen de règlement</label>
                    <SelectReglement value={facForm.reglement} onChange={e=>setFacForm(f=>({...f,reglement:e.target.value}))} />
                  </div>
                  <div className="fld"><label className="lbl">Note</label><input type="text" className="inp" value={facForm.note} onChange={e=>setFacForm(f=>({...f,note:e.target.value}))} /></div>
                  <div className="fld">
                    <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>setFacForm(f=>({...f,appliquerTVA:!f.appliquerTVA}))}>
                      <div style={{width:20,height:20,borderRadius:3,border:"2px solid "+(facForm.appliquerTVA?"#2F9E3F":"#CBD5D0"),background:facForm.appliquerTVA?"#2F9E3F":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {facForm.appliquerTVA&&<span style={{color:"#FFFFFF",fontSize:13,fontWeight:700}}>✓</span>}
                      </div>
                      <span style={{fontSize:13,color:facForm.appliquerTVA?"#0F1F17":"#5B6B62"}}>TVA ({entreprise.tva_taux||"19.25"}%)</span>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Lignes</div>
                  {facForm.lignes.map((l,i)=>(
                    <div key={i} className="lr">
                      <div style={{flex:2}}>{libelleLigne(l.designation)}</div>
                      <div style={{color:"#5B6B62"}}>{NUM(l.qte)} btl ×</div>
                      <input type="number" className="inp" min="0" title="Prix unitaire (modifiable)" style={{width:84,padding:"4px 6px"}} value={l.prixUnit} onChange={e=>setFacForm(f=>({...f,lignes:majPrixLigne(f.lignes,i,e.target.value)}))} />
                      <div style={{color:"#1E8A32",fontWeight:600}}>{NUM(l.montant)} F</div>
                      <button onClick={()=>setFacForm(f=>({...f,lignes:f.lignes.filter((_,idx)=>idx!==i)}))} style={{background:"none",border:"none",color:"#D6453D",cursor:"pointer"}}>✕</button>
                    </div>
                  ))}
                  <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr auto",gap:6,marginTop:10,alignItems:"end"}}>
                    <div><label className="lbl">Produit</label>
                      <select className="inp" value={newLigne.produit} onChange={e=>{const p=produits.find(pr=>pr.nom===e.target.value);setNewLigne(n=>({...n,produit:e.target.value,prixUnit:p?p.prix:""}));}}>
                        <option value="">...</option>{produits.map(p=><option key={p.id} value={p.nom}>{p.code?p.code+" · ":""}{p.nom}</option>)}
                      </select>
                    </div>
                    <div><label className="lbl">Qté</label><input type="number" className="inp" value={newLigne.qte} onChange={e=>setNewLigne(n=>({...n,qte:e.target.value}))} /></div>
                    <div><label className="lbl">Prix</label><input type="number" className="inp" value={newLigne.prixUnit} onChange={e=>setNewLigne(n=>({...n,prixUnit:e.target.value}))} /></div>
                    <button className="btn btn-gold btn-sm" style={{alignSelf:"flex-end"}} onClick={ajouterLigne}>+</button>
                  </div>
                  {facForm.lignes.length>0&&(
                    <div style={{marginTop:18,borderTop:"1px solid #E3E9E6",paddingTop:14}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#5B6B62",marginBottom:4}}><span>Sous-total HT</span><span>{FCFA(fHT)}</span></div>
                      {fRem>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#D6453D",marginBottom:4}}><span>Remise {facForm.remisePct}%</span><span>- {FCFA(fRem)}</span></div>}
                      {facForm.appliquerTVA&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#5B6B62",marginBottom:4}}><span>TVA {entreprise.tva_taux||"19.25"}%</span><span>{FCFA(fTVA)}</span></div>}
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:19,color:"#1E8A32",fontWeight:700,marginTop:6}}><span>Total TTC</span><span>{FCFA(fTTC)}</span></div>
                    </div>
                  )}
                </div>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button className="btn btn-ghost" onClick={()=>setPage("factures")}>Annuler</button>
                <button className="btn btn-gold" onClick={creerFacture} disabled={loading}>{loading?"...":"Créer la facture"}</button>
              </div>
            </div>
          )}

          {/* PROFORMAS */}
          {page==="proformas"&&(isDirecteur()||can("proformas"))&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Proformas</div>
                <button className="btn btn-purple" onClick={()=>setPage("nouvelle-proforma")}>+ Nouvelle proforma</button>
              </div>
              {proformas.length===0?(
                <div className="card" style={{textAlign:"center",padding:50,color:"#5B6B62"}}>
                  <div style={{display:"flex",justifyContent:"center",marginBottom:12,color:"#5B6B62"}}><FileText size={40}/></div>
                  <button className="btn btn-purple" onClick={()=>setPage("nouvelle-proforma")}>Créer une proforma</button>
                </div>
              ):(
                <div className="card scr">
                  <table className="tbl">
                    <thead><tr><th>N° Proforma</th><th>Client</th><th>Date</th><th>Validité</th><th>Total TTC</th><th>TVA</th><th>Actions</th></tr></thead>
                    <tbody>{proformas.map(pf=>{
                      const {ttc}=calculateInvoiceTotals(pf,entreprise);
                      const cli=clients.find(c=>c.nom===pf.client_nom);
                      return (
                        <tr key={pf.id}>
                          <td style={{fontFamily:"monospace",fontSize:11,color:"#7A4FD6"}}>{pf.numero}</td>
                          <td>{pf.client_nom}</td>
                          <td style={{color:"#5B6B62"}}>{fmtDate(pf.date_emission)}</td>
                          <td style={{color:"#5B6B62"}}>{fmtDate(pf.date_echeance)}</td>
                          <td style={{fontWeight:600}}>{FCFA(ttc)}</td>
                          <td><span className={"badge "+(pf.appliquer_tva!==false?"bg2":"br")}>{pf.appliquer_tva!==false?"Avec TVA":"Sans TVA"}</span></td>
                          <td>
                            <div style={{display:"flex",gap:5}}>
                              <button className="btn btn-ghost btn-sm" onClick={()=>setProformaView({facture:pf,client:cli})}><Eye size={13}/></button>
                              {cli?.email&&<button className="btn btn-ghost btn-sm" onClick={()=>{
                                setDocSendModal({kind:"proforma", data:pf, client:cli});
                                setDocSendForm({to:cli.email, toName:pf.client_nom, subject:"Proforma "+pf.numero+" — "+entreprise.entreprise_nom, message:"Veuillez trouver ci-joint votre proforma."});
                              }}><Mail size={13}/></button>}
                              <button className="btn btn-gold btn-sm" disabled={!!pf.convertie_en_facture_id} onClick={()=>convertirProformaEnFacture(pf)}>{pf.convertie_en_facture_id?"✓ Convertie":"→ Facture"}</button>
                              {(canEdit())&&<button className="btn btn-red btn-sm" onClick={()=>deleteProforma(pf.id)}><Trash2 size={13}/></button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* NOUVELLE PROFORMA */}
          {page==="nouvelle-proforma"&&(isDirecteur()||can("proformas"))&&(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPage("proformas")}>← Retour</button>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Nouvelle proforma</div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1.4fr",gap:18,marginBottom:18}}>
                <div className="card">
                  <div className="fld"><label className="lbl">Client *</label>
                    <select className="inp" value={proformaForm.clientCode||""} onChange={e=>{const code=e.target.value;const cli=clients.find(c=>c.code===code);setProformaForm(f=>({...f,clientNom:cli?cli.nom:"",clientCode:code}));}}>
                      <option value="">Choisir...</option>
                      {clients.map(c=><option key={c.id} value={c.code}>{etiquetteClient(c)}</option>)}
                    </select>
                  </div>
                  <div className="fld"><label className="lbl">Date émission</label><input type="date" className="inp" value={proformaForm.dateEmission} onChange={e=>setProformaForm(f=>({...f,dateEmission:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Date validité</label><input type="date" className="inp" value={proformaForm.dateEcheance} onChange={e=>setProformaForm(f=>({...f,dateEcheance:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Remise (%)</label><input type="number" className="inp" min="0" max="100" value={proformaForm.remisePct||""} onChange={e=>setProformaForm(f=>({...f,remisePct:parseFloat(e.target.value)||0}))} /></div>
                  <div className="fld">
                    <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>setProformaForm(f=>({...f,appliquerTVA:!f.appliquerTVA}))}>
                      <div style={{width:20,height:20,borderRadius:3,border:"2px solid "+(proformaForm.appliquerTVA?"#2F9E3F":"#CBD5D0"),background:proformaForm.appliquerTVA?"#2F9E3F":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {proformaForm.appliquerTVA&&<span style={{color:"#FFFFFF",fontSize:13,fontWeight:700}}>✓</span>}
                      </div>
                      <span style={{fontSize:13,color:proformaForm.appliquerTVA?"#0F1F17":"#5B6B62"}}>TVA ({entreprise.tva_taux||"19.25"}%)</span>
                    </div>
                  </div>
                  <div className="fld"><label className="lbl">Note</label><input type="text" className="inp" value={proformaForm.note} onChange={e=>setProformaForm(f=>({...f,note:e.target.value}))} /></div>
                </div>
                <div className="card">
                  <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Lignes</div>
                  {proformaForm.lignes.map((l,i)=>(
                    <div key={i} className="lr">
                      <div style={{flex:2}}>{libelleLigne(l.designation)}</div>
                      <div style={{color:"#5B6B62"}}>{NUM(l.qte)} btl ×</div>
                      <input type="number" className="inp" min="0" title="Prix unitaire (modifiable)" style={{width:84,padding:"4px 6px"}} value={l.prixUnit} onChange={e=>setProformaForm(f=>({...f,lignes:majPrixLigne(f.lignes,i,e.target.value)}))} />
                      <div style={{color:"#7A4FD6",fontWeight:600}}>{NUM(l.montant)} F</div>
                      <button onClick={()=>setProformaForm(f=>({...f,lignes:f.lignes.filter((_,idx)=>idx!==i)}))} style={{background:"none",border:"none",color:"#D6453D",cursor:"pointer"}}>✕</button>
                    </div>
                  ))}
                  <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr auto",gap:6,marginTop:10,alignItems:"end"}}>
                    <div><label className="lbl">Produit</label>
                      <select className="inp" value={newProformaLigne.produit} onChange={e=>{const p=produits.find(pr=>pr.nom===e.target.value);setNewProformaLigne(n=>({...n,produit:e.target.value,prixUnit:p?p.prix:""}));}}>
                        <option value="">...</option>{produits.map(p=><option key={p.id} value={p.nom}>{p.code?p.code+" · ":""}{p.nom}</option>)}
                      </select>
                    </div>
                    <div><label className="lbl">Qté</label><input type="number" className="inp" value={newProformaLigne.qte} onChange={e=>setNewProformaLigne(n=>({...n,qte:e.target.value}))} /></div>
                    <div><label className="lbl">Prix</label><input type="number" className="inp" value={newProformaLigne.prixUnit} onChange={e=>setNewProformaLigne(n=>({...n,prixUnit:e.target.value}))} /></div>
                    <button className="btn btn-purple btn-sm" style={{alignSelf:"flex-end"}} onClick={ajouterLigneProforma}>+</button>
                  </div>
                  {proformaForm.lignes.length>0&&(
                    <div style={{marginTop:18,borderTop:"1px solid #E3E9E6",paddingTop:14}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#5B6B62",marginBottom:4}}><span>Sous-total HT</span><span>{FCFA(pfHT)}</span></div>
                      {pfRem>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#D6453D",marginBottom:4}}><span>Remise {proformaForm.remisePct}%</span><span>- {FCFA(pfRem)}</span></div>}
                      {proformaForm.appliquerTVA&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#5B6B62",marginBottom:4}}><span>TVA</span><span>{FCFA(pfTVA)}</span></div>}
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:19,color:"#7A4FD6",fontWeight:700,marginTop:6}}><span>Total</span><span>{FCFA(pfTTC)}</span></div>
                    </div>
                  )}
                </div>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button className="btn btn-ghost" onClick={()=>setPage("proformas")}>Annuler</button>
                <button className="btn btn-purple" onClick={creerProforma} disabled={loading}>{loading?"...":"Créer la proforma"}</button>
              </div>
            </div>
          )}

          {/* CLIENTS */}
          {/* MON ACTIVITÉ — chaque employé ne voit que SES chiffres */}
          {page==="journal_activite"&&isDirecteur()&&(()=>{
            const ACT = {
              creation:{label:"Création",couleur:"#1E7A2D"},
              modification:{label:"Modification",couleur:"#0F1F17"},
              suppression:{label:"Suppression",couleur:"#D6453D"},
              consultation:{label:"Consultation",couleur:"#5B6B62"},
              connexion:{label:"Connexion",couleur:"#2F6FDE"}
            };
            const OBJETS = ["Page","Connexion","Document PDF","Document","Facture","Proforma","Paiement","Paiements d'une facture","Vente","Client","Produit","Stock par dépôt","Dépôt","Livraison","Transfert de stock","Avarie","Réception de bouteilles","Lot de production","Contrôle qualité","Commande fournisseur","Facture fournisseur","Paiement fournisseur","Réception achat","Demande d'achat","Fournisseur","Dépense","Déclaration de ventes","Prospection","Congé","Solde de congés","Employé","Bulletin de paie","Contrat","Évaluation","Immobilisation","Mouvement d'immobilisation","Objectif","Paramètre","Département","Document employé","Demande de signature"];
            const quand = d => new Date(d).toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit",second:"2-digit"});
            const kpi = (label,val,couleur) => <div className="kpi" style={{minWidth:120,flex:"1 1 120px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.2,color:"#5B6B62",marginBottom:5}}>{label}</div><div style={{fontSize:17,fontWeight:700,color:couleur||"#0F1F17"}}>{val}</div></div>;
            const tot = journalResume.reduce((t,r)=>({n:t.n+Number(r.nb_total),c:t.c+Number(r.nb_creations),m:t.m+Number(r.nb_modifications),s:t.s+Number(r.nb_suppressions),v:t.v+Number(r.nb_consultations)}),{n:0,c:0,m:0,s:0,v:0});
            const champsDe = r => { const ks=[...new Set([...Object.keys(r.anciennes||{}),...Object.keys(r.nouvelles||{})])]; return ks; };
            const valTxt = v => v===undefined||v===null||v===""?"vide":(typeof v==="object"?JSON.stringify(v):String(v));
            return (
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,flexWrap:"wrap",gap:10}}>
                  <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Journal d'activité</div>
                  <div style={{display:"flex",gap:8}}>
                    <button className="btn btn-ghost btn-sm" disabled={journalBusy} onClick={chargerJournal}>{journalBusy?"Actualisation…":"Actualiser"}</button>
                    <button className="btn btn-gold" disabled={reportGenerating||journalBusy} onClick={imprimerJournal}><FileText size={14}/> {reportGenerating?"...":"Imprimer (PDF)"}</button>
                  </div>
                </div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:12}}>Trace de tout ce que font les employés dans l'ERP : connexions, pages et fiches consultées, créations, modifications (ancienne et nouvelle valeur) et suppressions. Réservé à la direction. Personne ne peut modifier ni effacer ce journal.</div>
                {journalErr&&<div className="card" style={{marginBottom:12,borderColor:"rgba(233,162,157,0.53)",color:"#D6453D",fontSize:13}}>Impossible de charger le journal : {journalErr}</div>}
                <div className="card" style={{marginBottom:14}}>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(auto-fit,minmax(150px,1fr))",gap:10,alignItems:"end"}}>
                    <div><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Du</div><input type="date" className="inp" value={journalDu} onChange={e=>{setJournalLimite(100);setJournalDu(e.target.value);}}/></div>
                    <div><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Au</div><input type="date" className="inp" value={journalAu} onChange={e=>{setJournalLimite(100);setJournalAu(e.target.value);}}/></div>
                    <div><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Employé</div>
                      <select className="inp" value={journalEmp} onChange={e=>{setJournalLimite(100);setJournalEmp(e.target.value);}}>
                        <option value="">Tous</option>
                        {[...new Set(journalResume.map(r=>r.employe_nom).filter(Boolean))].sort((a,b)=>a.localeCompare(b)).map(n=><option key={n} value={n}>{n}</option>)}
                      </select></div>
                    <div><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Action</div>
                      <select className="inp" value={journalAction} onChange={e=>{setJournalLimite(100);setJournalAction(e.target.value);}}>
                        <option value="">Toutes</option>
                        {Object.keys(ACT).map(k=><option key={k} value={k}>{ACT[k].label}</option>)}
                      </select></div>
                    <div><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Sur quoi</div>
                      <select className="inp" value={journalObjet} onChange={e=>{setJournalLimite(100);setJournalObjet(e.target.value);}}>
                        <option value="">Tout</option>
                        {OBJETS.map(o=><option key={o} value={o}>{o}</option>)}
                      </select></div>
                    <div style={{gridColumn:isMobile?"1 / span 2":"auto"}}><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Recherche (numéro, client, mot…)</div><input type="text" className="inp" placeholder="ex. AZB-2026-126 ou SANTA" value={journalQ} onChange={e=>{setJournalLimite(100);setJournalQ(e.target.value);}}/></div>
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:10}}>
                    {[["Aujourd'hui",0],["7 jours",6],["30 jours",29]].map(([lib,j])=><button key={lib} type="button" className="btn btn-ghost btn-sm" onClick={()=>{setJournalLimite(100);setJournalDu(localISODate(new Date(Date.now()-j*86400000)));setJournalAu(localISODate(new Date()));}}>{lib}</button>)}
                    <button type="button" className="btn btn-ghost btn-sm" onClick={()=>{setJournalLimite(100);setJournalDu("");setJournalAu("");}}>Tout l'historique</button>
                    {(journalEmp||journalAction||journalObjet||journalQ)&&<button type="button" className="btn btn-ghost btn-sm" onClick={()=>{setJournalEmp("");setJournalAction("");setJournalObjet("");setJournalQ("");}}>Effacer les filtres</button>}
                  </div>
                </div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14}}>
                  {kpi("Actions (période)",tot.n)}
                  {kpi("Créations",tot.c,"#1E7A2D")}
                  {kpi("Modifications",tot.m)}
                  {kpi("Suppressions",tot.s,tot.s>0?"#D6453D":"#5B6B62")}
                  {kpi("Consultations",tot.v,"#5B6B62")}
                </div>
                <div style={{fontSize:15,color:"#0F1F17",margin:"6px 0 8px"}}>Par employé (touche une ligne pour filtrer)</div>
                <div className="card" style={{padding:0,marginBottom:16}}><div style={{overflowX:"auto"}}>
                  <table className="tbl"><thead><tr><th>Employé</th><th style={{textAlign:"right"}}>Total</th><th style={{textAlign:"right"}}>Créations</th><th style={{textAlign:"right"}}>Modifs</th><th style={{textAlign:"right"}}>Suppr.</th><th style={{textAlign:"right"}}>Consult.</th><th style={{textAlign:"right"}}>Connexions</th><th>Dernière action</th></tr></thead>
                    <tbody>
                      {journalResume.map(r=>(
                        <tr key={r.employe_nom} style={{cursor:"pointer",background:journalEmp===r.employe_nom?"rgba(47,158,63,0.112)":"transparent"}} onClick={()=>{setJournalLimite(100);setJournalEmp(journalEmp===r.employe_nom?"":r.employe_nom);}}>
                          <td style={{fontWeight:600}}>{r.employe_nom}</td>
                          <td style={{textAlign:"right",fontWeight:700}}>{r.nb_total}</td>
                          <td style={{textAlign:"right"}}>{r.nb_creations}</td>
                          <td style={{textAlign:"right"}}>{r.nb_modifications}</td>
                          <td style={{textAlign:"right",color:Number(r.nb_suppressions)>0?"#D6453D":"inherit"}}>{r.nb_suppressions}</td>
                          <td style={{textAlign:"right"}}>{r.nb_consultations}</td>
                          <td style={{textAlign:"right"}}>{r.nb_connexions}</td>
                          <td style={{fontSize:12,color:"#5B6B62"}}>{r.derniere_action?quand(r.derniere_action):"Aucune action"}</td>
                        </tr>
                      ))}
                      {!journalResume.length&&<tr><td colSpan={8} style={{color:"#5B6B62",padding:14,fontSize:13}}>{journalBusy?"Chargement…":"Aucune action enregistrée pour cette période."}</td></tr>}
                    </tbody>
                  </table>
                </div></div>
                <div style={{fontSize:15,color:"#0F1F17",margin:"6px 0 8px"}}>Détail des actions <span style={{fontSize:12,color:"#5B6B62",fontWeight:400}}>— {journalTotal} au total pour ces filtres, {journalRows.length} affichée{journalRows.length>1?"s":""}</span></div>
                <div className="card" style={{padding:0}}><div style={{overflowX:"auto"}}>
                  <table className="tbl"><thead><tr><th>Date et heure</th><th>Employé</th><th>Action</th><th>Sur quoi</th><th>Détail</th></tr></thead>
                    <tbody>
                      {journalRows.map(r=>{
                        const a = ACT[r.action]||{label:r.action,couleur:"#5B6B62"};
                        const ouvrable = !!(r.anciennes||r.nouvelles);
                        return (
                          <Fragment key={r.id}>
                            <tr style={{cursor:ouvrable?"pointer":"default",background:r.action==="suppression"?"rgba(224,112,96,.10)":"transparent"}} onClick={()=>ouvrable&&setJournalOuvert(journalOuvert===r.id?null:r.id)}>
                              <td style={{whiteSpace:"nowrap",fontSize:12}}>{quand(r.created_at)}</td>
                              <td style={{fontWeight:600}}>{r.employe_nom}{r.ville?<div style={{fontSize:10,color:"#8A978F",fontWeight:400}}>{r.ville}</div>:null}</td>
                              <td><span style={{color:a.couleur,fontWeight:700,fontSize:12}}>{a.label}</span></td>
                              <td>{r.objet}{r.reference?<div style={{fontFamily:"monospace",fontSize:11,color:"#1E8A32"}}>{r.reference}</div>:null}</td>
                              <td style={{fontSize:12,color:"#2B3A32"}}>{r.detail}{ouvrable&&<span style={{color:"#8A978F",fontSize:11}}>{journalOuvert===r.id?"  ▲ masquer":"  ▼ voir le détail"}</span>}</td>
                            </tr>
                            {journalOuvert===r.id&&ouvrable&&(
                              <tr><td colSpan={5} style={{background:"rgba(15,31,23,0.04)",padding:"10px 16px"}}>
                                <table style={{fontSize:12,borderCollapse:"collapse",width:"100%"}}><thead><tr style={{color:"#5B6B62",textAlign:"left"}}><th style={{padding:"3px 8px"}}>Champ</th><th style={{padding:"3px 8px"}}>Avant</th><th style={{padding:"3px 8px"}}>Après</th></tr></thead>
                                  <tbody>{champsDe(r).map(k=>(
                                    <tr key={k}><td style={{padding:"3px 8px",fontFamily:"monospace",color:"#1E8A32"}}>{k}</td><td style={{padding:"3px 8px",color:"#B3372F",wordBreak:"break-word"}}>{r.anciennes&&k in r.anciennes?valTxt(r.anciennes[k]):"—"}</td><td style={{padding:"3px 8px",color:"#1E7A2D",wordBreak:"break-word"}}>{r.nouvelles&&k in r.nouvelles?valTxt(r.nouvelles[k]):"—"}</td></tr>
                                  ))}</tbody>
                                </table>
                              </td></tr>
                            )}
                          </Fragment>
                        );
                      })}
                      {!journalRows.length&&<tr><td colSpan={5} style={{color:"#5B6B62",padding:16,fontSize:13}}>{journalBusy?"Chargement…":"Aucune action pour ces filtres."}</td></tr>}
                    </tbody>
                  </table>
                  {journalRows.length<journalTotal&&<div style={{padding:12,textAlign:"center"}}><button className="btn btn-ghost" disabled={journalBusy} onClick={()=>setJournalLimite(l=>l+200)}>Afficher 200 de plus</button></div>}
                </div></div>
              </div>
            );
          })()}
          {page==="mon_activite"&&(()=>{
            const r = calculerMonActivite();
            const periodeLibelle = (monDu||monAu) ? ((monDu?"du "+fmtDate(monDu)+" ":"")+(monAu?"au "+fmtDate(monAu):"")).trim() : "depuis le début";
            const kpi = (label,val,couleur,note) => <div className="kpi" style={{minWidth:150,flex:"1 1 150px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.2,color:"#5B6B62",marginBottom:5}}>{label}</div><div style={{fontSize:18,fontWeight:700,color:couleur||"#0F1F17"}}>{val}</div>{note&&<div style={{fontSize:10,color:"#5B6B62",marginTop:3}}>{note}</div>}</div>;
            return (
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
                  <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Mon activité</div>
                  <div style={{display:"flex",gap:8}}>
                    <button className="btn btn-ghost btn-sm" disabled={monActiviteBusy} onClick={chargerMonActivite}>{monActiviteBusy?"Actualisation…":"Actualiser"}</button>
                    <button className="btn btn-gold" disabled={reportGenerating||monActiviteBusy||!monActivite} onClick={async()=>{
                      setReportGenerating(true);
                      try {
                        const pdf = await generateMonActivitePdfBase64(entreprise, (monActivite&&monActivite.nom)||session.nom, r, periodeLibelle);
                        await openPdf(pdf,"Document Azongbio");
                      } catch(e) { showToast("Erreur : "+e.message, false); }
                      setReportGenerating(false);
                    }}><FileText size={14}/> {reportGenerating?"...":"Imprimer mes créances"}</button>
                  </div>
                </div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:12}}>Tes chiffres personnels : les ventes et les factures que tu as émises. Les autres employés ne voient pas ces données, et tu ne vois pas les leurs.</div>
                {monActiviteErr&&<div className="card" style={{marginBottom:12,borderColor:"rgba(233,162,157,0.53)",color:"#D6453D",fontSize:13}}>Impossible de charger tes données : {monActiviteErr}</div>}
                <div className="card" style={{marginBottom:14}}>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(auto-fit,minmax(170px,220px))",gap:10,alignItems:"end"}}>
                    <div><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Période : du</div><input type="date" className="inp" value={monDu} onChange={e=>setMonDu(e.target.value)}/></div>
                    <div><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>au</div><input type="date" className="inp" value={monAu} onChange={e=>setMonAu(e.target.value)}/></div>
                    {(monDu||monAu)&&<button type="button" className="btn btn-ghost btn-sm" onClick={()=>{setMonDu("");setMonAu("");}}>Effacer les dates</button>}
                  </div>
                </div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14}}>
                  {kpi("Mon chiffre d'affaires",FCFA(r.ca),"#1E8A32","Période : "+periodeLibelle)}
                  {kpi("Facturé (TTC)",FCFA(r.facture),null,"Période : "+periodeLibelle)}
                  {kpi("Encaissé",FCFA(r.encaisse),"#1E7A2D","Sur ces factures")}
                  {kpi("Reste à réclamer",FCFA(r.creances),"#D6453D","Total à ce jour, toutes dates")}
                  {kpi("Dont en retard",FCFA(r.enRetard),r.enRetard>0?"#D6453D":"#5B6B62","Échéance dépassée")}
                </div>
                <div style={{fontSize:15,color:"#0F1F17",margin:"6px 0 8px"}}>Mes créances à réclamer</div>
                <div className="card" style={{padding:0}}><div style={{overflowX:"auto"}}>
                  <table className="tbl"><thead><tr><th>Client / facture</th><th>Date</th><th>Échéance</th><th style={{textAlign:"right"}}>Retard</th><th style={{textAlign:"right"}}>Reste à payer</th></tr></thead>
                    <tbody>
                      {r.clientsDus.map(c=>(
                        <Fragment key={c.client}>
                          <tr><td colSpan={4} style={{background:"rgba(47,158,63,0.096)",color:"#0F1F17",fontWeight:700}}>{c.client} <span style={{fontWeight:400,fontSize:11,color:"#5B6B62"}}>— {c.factures.length} facture{c.factures.length>1?"s":""}</span></td><td style={{background:"rgba(47,158,63,0.096)",textAlign:"right",fontWeight:700,color:"#D6453D"}}>{FCFA(c.reste)}</td></tr>
                          {c.factures.map(f=>(
                            <tr key={f.numero}><td style={{paddingLeft:22,fontFamily:"monospace",fontSize:12}}>{f.numero}</td><td>{fmtDate(f.date)}</td><td>{f.echeance?fmtDate(f.echeance):"—"}</td>
                              <td style={{textAlign:"right",color:f.retard>0?"#D6453D":"#5B6B62",fontWeight:f.retard>0?700:400}}>{f.retard>0?f.retard+" j":"—"}</td>
                              <td style={{textAlign:"right"}}>{FCFA(f.reste)}</td></tr>
                          ))}
                        </Fragment>
                      ))}
                      {r.clientsDus.length>0&&<tr style={{fontWeight:800,background:"rgba(47,158,63,0.144)"}}><td colSpan={4}>TOTAL À RÉCLAMER ({r.nbFacturesDues} facture{r.nbFacturesDues>1?"s":""})</td><td style={{textAlign:"right",color:"#D6453D"}}>{FCFA(r.creances)}</td></tr>}
                    </tbody>
                  </table>
                  {!monActiviteBusy&&monActivite&&r.clientsDus.length===0&&<div style={{color:"#1E7A2D",padding:16,fontSize:13}}>Aucune créance à réclamer. Tout est payé.</div>}
                  {monActiviteBusy&&!monActivite&&<div style={{color:"#5B6B62",padding:16,fontSize:13}}>Chargement…</div>}
                </div></div>
              </div>
            );
          })()}

          {page==="clients"&&(isDirecteur()||can("clients"))&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Clients</div>
                {(isDirecteur()||can("factures"))&&<div style={{display:"flex",gap:0,border:"1px solid #E3E9E6",borderRadius:8,overflow:"hidden"}}>
                  <button type="button" className={"btn btn-sm "+(clientVue==="fiches"?"btn-gold":"btn-ghost")} style={{borderRadius:0}} onClick={()=>setClientVue("fiches")}>Fiches</button>
                  <button type="button" className={"btn btn-sm "+(clientVue==="suivi"?"btn-gold":"btn-ghost")} style={{borderRadius:0}} onClick={()=>setClientVue("suivi")}>Suivi & créances</button>
                </div>}
                <div style={{display:"flex",gap:8}}>
                  <button className="btn btn-ghost btn-icon" disabled={reportGenerating} onClick={async()=>{
                    setReportGenerating(true);
                    try {
                      const pdf = await generateClientsListPdfBase64(entreprise, clients, ventes, rattacherVente);
                      await openPdf(pdf,"Document Azongbio");
                    } catch(e) { showToast("Erreur : "+e.message, false); }
                    setReportGenerating(false);
                  }}><FileText size={14}/> Exporter (PDF)</button>
                  <button className="btn btn-gold" onClick={()=>openModal("client")}>+ Nouveau client</button>
                </div>
              </div>
              {clientVue==="suivi"&&(isDirecteur()||can("factures"))&&(()=>{
                const suivi = calculerSuiviClients();
                const catsDispo = SUIVI_ORDRE_CATS.slice(0,5);
                const villesDispo = [...new Set(factures.map(f=>String(f.ville||"").trim()||"Non précisée"))].sort((a,b)=>a.localeCompare(b));
                const bascule = c => setSuiviCats(l=>l.includes(c)?l.filter(x=>x!==c):[...l,c]);
                const basculeVille = v => setSuiviVilles(l=>l.includes(v)?l.filter(x=>x!==v):[...l,v]);
                const kpi = (label,val,couleur) => <div className="kpi" style={{minWidth:130,flex:"1 1 130px"}}><div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.2,color:"#5B6B62",marginBottom:5}}>{label}</div><div style={{fontSize:16,fontWeight:700,color:couleur||"#0F1F17"}}>{val}</div></div>;
                return (
                  <div>
                    <div className="card" style={{marginBottom:14}}>
                      <div style={{fontSize:11,color:"#5B6B62",marginBottom:6}}>CATÉGORIES (touche pour choisir, aucune = toutes)</div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                        <button type="button" className={"btn btn-sm "+(suiviCats.length===0?"btn-gold":"btn-ghost")} onClick={()=>setSuiviCats([])}>Toutes</button>
                        {catsDispo.map(c=><button key={c} type="button" className={"btn btn-sm "+(suiviCats.includes(c)?"btn-gold":"btn-ghost")} onClick={()=>bascule(c)}>{c}</button>)}
                      </div>
                      <div style={{fontSize:11,color:"#5B6B62",marginBottom:6}}>VILLES (touche pour choisir, aucune = toutes)</div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                        <button type="button" className={"btn btn-sm "+(suiviVilles.length===0?"btn-gold":"btn-ghost")} onClick={()=>setSuiviVilles([])}>Toutes</button>
                        {villesDispo.map(v=><button key={v} type="button" className={"btn btn-sm "+(suiviVilles.includes(v)?"btn-gold":"btn-ghost")} onClick={()=>basculeVille(v)}>{v}</button>)}
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fit,minmax(170px,1fr))",gap:10,alignItems:"end"}}>
                        <div style={{gridColumn:isMobile?"auto":"span 2"}}><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Client (nom)</div>
                          <input type="text" className="inp" list="suivi-clients-noms" placeholder="Tape un nom, ex. SANTA LUCIA" value={suiviNom} onChange={e=>setSuiviNom(e.target.value)}/>
                          <datalist id="suivi-clients-noms">{[...new Set(clients.map(c=>c.nom))].sort((a,b)=>a.localeCompare(b)).map(n=><option key={n} value={n}/>)}</datalist></div>
                        <div><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Du</div><input type="date" className="inp" value={suiviDu} onChange={e=>setSuiviDu(e.target.value)}/></div>
                        <div><div style={{fontSize:11,color:"#5B6B62",marginBottom:4}}>Au</div><input type="date" className="inp" value={suiviAu} onChange={e=>setSuiviAu(e.target.value)}/></div>
                        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#2B3A32",cursor:"pointer"}}><input type="checkbox" checked={suiviSeulementDus} onChange={e=>setSuiviSeulementDus(e.target.checked)}/> Seulement ceux qui doivent de l'argent</label>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14}}>
                      {kpi("Lignes client",suivi.total.clients)}
                      {kpi("Facturé",FCFA(suivi.total.facture))}
                      {kpi("Encaissé",FCFA(suivi.total.paye),"#1E7A2D")}
                      {kpi("Reste à payer",FCFA(suivi.total.reste),"#D6453D")}
                    </div>
                    <div style={{display:"flex",justifyContent:"flex-end",marginBottom:10}}>
                      <button className="btn btn-gold" disabled={reportGenerating||suivi.groupes.length===0} onClick={async()=>{
                        setReportGenerating(true);
                        try {
                          const pdf = await generateSuiviClientsPdfBase64(entreprise, suivi, libelleFiltresSuivi());
                          await openPdf(pdf,"Document Azongbio");
                        } catch(e) { showToast("Erreur : "+e.message, false); }
                        setReportGenerating(false);
                      }}><FileText size={14}/> {reportGenerating?"...":"Imprimer / PDF"}</button>
                    </div>
                    <div className="card" style={{padding:0}}><div style={{overflowX:"auto"}}>
                      <table className="tbl"><thead><tr><th>Client</th><th>Ville</th><th style={{textAlign:"right"}}>Fact.</th><th style={{textAlign:"right"}}>Facturé</th><th style={{textAlign:"right"}}>Encaissé</th><th style={{textAlign:"right"}}>Reste à payer</th></tr></thead>
                        <tbody>
                          {suivi.groupes.map(g=>(
                            <Fragment key={g.cat}>
                              <tr><td colSpan={6} style={{background:"rgba(47,158,63,0.096)",color:"#0F1F17",fontWeight:700,fontSize:12}}>{g.cat} — {g.lignes.length} client{g.lignes.length>1?"s":""}</td></tr>
                              {g.lignes.map(r=>(
                                <tr key={r.client+"|"+r.ville}>
                                  <td>{r.client}</td><td>{r.ville}</td><td style={{textAlign:"right"}}>{r.nb}</td>
                                  <td style={{textAlign:"right"}}>{FCFA(r.facture)}</td><td style={{textAlign:"right"}}>{FCFA(r.paye)}</td>
                                  <td style={{textAlign:"right",fontWeight:r.reste>0?700:400,color:r.reste>0?"#D6453D":"#5B6B62"}}>{FCFA(r.reste)}</td>
                                </tr>
                              ))}
                              <tr style={{fontWeight:700}}><td colSpan={2}>Sous-total {g.cat}</td><td style={{textAlign:"right"}}>{g.nb}</td><td style={{textAlign:"right"}}>{FCFA(g.facture)}</td><td style={{textAlign:"right"}}>{FCFA(g.paye)}</td><td style={{textAlign:"right",color:"#D6453D"}}>{FCFA(g.reste)}</td></tr>
                            </Fragment>
                          ))}
                          {suivi.groupes.length>0&&<tr style={{fontWeight:800,background:"rgba(47,158,63,0.144)"}}><td colSpan={2}>TOTAL GÉNÉRAL</td><td style={{textAlign:"right"}}>{suivi.total.nb}</td><td style={{textAlign:"right"}}>{FCFA(suivi.total.facture)}</td><td style={{textAlign:"right"}}>{FCFA(suivi.total.paye)}</td><td style={{textAlign:"right",color:"#D6453D"}}>{FCFA(suivi.total.reste)}</td></tr>}
                        </tbody>
                      </table>
                    </div>
                    {suivi.groupes.length===0&&<div style={{color:"#5B6B62",padding:16,fontSize:13}}>Aucun client pour ces filtres.</div>}</div>
                  </div>
                );
              })()}
              {clientVue==="fiches"&&<>
              <div style={{position:"relative",marginBottom:20,maxWidth:400}}>
                <Search size={15} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#5B6B62"}}/>
                <input type="text" className="inp" style={{paddingLeft:36}} placeholder="Rechercher un client (nom, code, contact, ville...)"
                  value={clientSearch} onChange={e=>setClientSearch(e.target.value)} />
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(270px,1fr))",gap:14}}>
                {clients.filter(c=>{
                  const q = clientSearch.trim().toLowerCase();
                  if (!q) return true;
                  return [c.nom,c.code,c.contact,c.ville,c.email,c.niu].filter(Boolean).some(v=>String(v).toLowerCase().includes(q));
                }).map(c=>{
                  const vc=ventes.filter(v=>rattacherVente(v,c));
                  const tc=vc.reduce((s,v)=>s+v.montant,0);
                  return (
                    <div key={c.id} className="card">
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                        <div>
                          <div style={{fontSize:15,fontWeight:700}}>{c.nom}</div>
                          <div style={{fontSize:10,color:"#1E8A32",fontFamily:"monospace",marginTop:2}}>{c.code}</div>
                        </div>
                        <span className="badge bg2">{c.type}</span>
                      </div>
                      {c.niu&&<div style={{fontSize:11,color:"#5B6B62"}}>NIU : {c.niu}</div>}
                      {c.adresse?<div style={{fontSize:12,color:"#5B6B62",display:"flex",alignItems:"center",gap:5}}><MapPin size={12}/> {c.adresse}, {c.ville}{c.pays&&c.pays!=="Cameroun"?" ("+c.pays+")":""}</div>:<div style={{fontSize:12,color:"#D6453D"}}>⚠ Adresse manquante{nomsDoubles[normNomClient(c.nom)]?" — nécessaire pour distinguer ce client":""}</div>}
                      {nomsDoubles[normNomClient(c.nom)]&&<div style={{fontSize:11,color:"#0F1F17"}}>⚠ Nom en double — distingué par le code et l'adresse</div>}
                      <div style={{fontSize:12,color:"#5B6B62",display:"flex",alignItems:"center",gap:5}}><Phone size={12}/> {c.contact}</div>
                      {c.email&&<div style={{fontSize:12,color:"#5B6B62"}}>✉ {c.email}</div>}
                      <hr className="divider"/>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={{fontSize:11,color:"#5B6B62"}}>{vc.length} commande(s)</div>
                          <div style={{fontSize:13,color:"#1E8A32",fontWeight:600}}>{FCFA(tc)}</div>
                        </div>
                        <div style={{display:"flex",gap:5}}>
                          {(canEdit())&&<button className="btn btn-ghost btn-sm" onClick={()=>openModal("client",c)}>✏</button>}
                          {(canEdit())&&<button className="btn btn-red btn-sm" onClick={()=>deleteClient(c.id)}><Trash2 size={13}/></button>}
                          <button className="btn btn-gold btn-sm" onClick={()=>{setFacForm(f=>({...f,clientNom:c.nom,clientCode:c.code,lignes:[],ville:f.ville||(isDirecteur()?"":(session.ville||""))}));setPage("nouvelle-facture");}}><Receipt size={13}/></button>
                          <button className="btn btn-purple btn-sm" onClick={()=>{setProformaForm(f=>({...f,clientNom:c.nom,clientCode:c.code,lignes:[]}));setPage("nouvelle-proforma");}}><FileText size={13}/></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>}
            </div>
          )}

          {/* ACHATS — V2.5 */}
          {page==="achats"&&(can("achats")||isDirecteur())&&(()=>{
            const commandes=achatsCommandes.filter(x=>!achatSearch.trim()||[x.numero,x.fournisseur_nom,x.ville,x.statut].some(v=>String(v||"").toLowerCase().includes(achatSearch.toLowerCase())));
            const totalMois=achatsFactures.filter(x=>String(x.date_facture||"").slice(0,7)===today().slice(0,7)).reduce((a,x)=>a+toFiniteNumber(x.montant_ttc),0);
            const totalPaye=achatsPaiements.reduce((a,x)=>a+toFiniteNumber(x.montant),0);
            const dette=achatsFactures.reduce((a,f)=>a+Math.max(0,toFiniteNumber(f.montant_ttc)-achatsPaiements.filter(p=>String(p.facture_id)===String(f.id)).reduce((s,p)=>s+toFiniteNumber(p.montant),0)),0);
            const stat=(label,value,detail)=><div className="card achat-stat"><div className="achat-stat-label">{label}</div><div className="achat-stat-value">{value}</div><div className="achat-stat-detail">{detail}</div></div>;
            return <div className="achat-page">
              <div className="page-head"><div><div className="page-title">Achats</div><div className="page-subtitle">Commandes, réceptions, factures et règlements fournisseurs</div></div><div style={{display:"flex",gap:8,flexWrap:"wrap"}}><button className="btn btn-ghost" onClick={()=>openModal("depense")}>+ Autre dépense (électricité, loyer, marketing...)</button><button className="btn btn-gold" onClick={()=>{resetAchatForm();setAchatFormOpen(true);setAchatSection("commandes");}}>+ Bon de commande</button></div></div>
              <div className="achat-stats">{stat("Commandes",commandes.length,"dans votre périmètre")}{stat("Achats du mois",FCFA(totalMois),today().slice(0,7))}{stat("Dette fournisseurs",FCFA(dette),"reste à payer")}{stat("Paiements",FCFA(totalPaye),"historique cumulé")}</div>
              {/* Regroupé ici le 02/10/2026 : cette saisie rapide des matières premières vivait */}
              {/* jusqu'ici dans Comptabilité, séparée des commandes/factures fournisseurs — deux */}
              {/* endroits pour une même notion d'achat. Tout passe maintenant par cette page. */}
              <div className="card achat-form" style={{marginBottom:12}}>
                <div className="card-title">Achat de matières premières — saisie rapide</div>
                <div className="fld" style={{maxWidth:200,marginBottom:14}}>
                  <label className="lbl">Date de l'achat</label>
                  <input type="date" className="inp" value={ingredientDate} onChange={e=>setIngredientDate(e.target.value)} />
                </div>
                {ingredientRows.map((row,i)=>(
                  <div key={row.id} style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:10,alignItems:"center"}}>
                    <input type="text" className="inp" style={{flex:"2 1 150px"}} placeholder="Ingrédient" value={row.nom} onChange={e=>setIngredientRows(rows=>rows.map(r=>r.id===row.id?{...r,nom:e.target.value}:r))} />
                    <select className="inp" style={{flex:"1 1 150px"}} value={row.code||""} aria-label="Code" onChange={e=>{const c=codeInfo(e.target.value);setIngredientRows(rows=>rows.map(r=>r.id===row.id?{...r,code:e.target.value,nom:(!r.nom.trim()&&c)?c.libelle:r.nom}:r));}}><option value="">Code…</option>{codesAchat.map(c=><option key={c.code} value={c.code}>{c.code} · {c.libelle}</option>)}</select>
                    <input type="number" className="inp" style={{flex:"1 1 120px"}} placeholder="Montant FCFA" value={row.montant} onChange={e=>setIngredientRows(rows=>rows.map(r=>r.id===row.id?{...r,montant:e.target.value}:r))} />
                    <button onClick={()=>setIngredientRows(rows=>rows.filter(r=>r.id!==row.id))} style={{background:"none",border:"none",color:"#D6453D",cursor:"pointer",padding:"6px 8px"}}>✕</button>
                  </div>
                ))}
                <div style={{display:"flex",gap:9,marginTop:12}}>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setIngredientRows(rows=>[...rows,{id:Date.now(),nom:"",montant:"",code:""}])}>+ Ajouter une ligne</button>
                  <button className="btn btn-gold btn-sm" style={{marginLeft:"auto"}} disabled={ingredientSaving} onClick={saveIngredientRows}>{ingredientSaving?"...":"Enregistrer les achats"}</button>
                </div>
              </div>
              <div className="module-tabs">{[["dashboard","Synthèse"],["commandes","Commandes"],["factures","Factures"],["paiements","Paiements"],["fournisseurs","Fournisseurs"]].map(([id,label])=><button key={id} className={achatSection===id?"active":""} onClick={()=>setAchatSection(id)}>{label}</button>)}</div>
              {achatFormOpen&&<div className="card achat-form"><div className="card-title">Nouveau bon de commande</div><div className="responsive-form-grid">
                <div><label>Fournisseur *</label><select className="inp" value={achatForm.fournisseur_id} onChange={e=>{const f=fournisseurs.find(x=>String(x.id)===String(e.target.value));setAchatForm(a=>({...a,fournisseur_id:e.target.value,fournisseur_nom:f?.nom||""}));}}><option value="">Choisir</option>{fournisseurs.map(f=><option key={f.id} value={f.id}>{f.nom}</option>)}</select></div>
                <div><label>Ville *</label><select className="inp" value={achatForm.ville} onChange={e=>setAchatForm(a=>({...a,ville:e.target.value}))} disabled={!isDirecteur()&&!!session.ville}><option value="">Choisir...</option>{[...new Set(depots.filter(d=>d.ville!=="Magasin central").map(d=>d.ville).filter(Boolean))].sort().map(v=><option key={v}>{v}</option>)}</select></div><div><label>Date commande</label><input type="date" className="inp" value={achatForm.date_commande} onChange={e=>setAchatForm(a=>({...a,date_commande:e.target.value}))}/></div><div><label>Livraison prévue</label><input type="date" className="inp" value={achatForm.date_livraison_prevue} onChange={e=>setAchatForm(a=>({...a,date_livraison_prevue:e.target.value}))}/></div>
              </div><div className="achat-line-builder"><select className="inp" value={achatLigne.produit_id} onChange={e=>{const p=produits.find(x=>String(x.id)===String(e.target.value));setAchatLigne(l=>({...l,produit_id:e.target.value,designation:p?.nom||l.designation}));}}><option value="">Article libre / produit</option>{produits.map(p=><option key={p.id} value={p.id}>{p.nom}</option>)}</select><select className="inp" value={achatLigne.code||""} aria-label="Code article" onChange={e=>{const c=codeInfo(e.target.value);const dernier=dernierPrixCode(e.target.value);setAchatLigne(l=>({...l,code:e.target.value,designation:(!l.designation.trim()||l.designation===((codeInfo(l.code)||{}).libelle))?(c?c.libelle:l.designation):l.designation,prix_unitaire:(!String(l.prix_unitaire||"")&&dernier)?String(dernier):l.prix_unitaire}));}}><option value="">Code article…</option>{codesAchat.map(c=><option key={c.code} value={c.code}>{c.code} · {c.libelle}</option>)}</select><input className="inp" placeholder="Désignation" value={achatLigne.designation} onChange={e=>setAchatLigne(l=>({...l,designation:e.target.value}))}/><input type="number" className="inp" placeholder="Qté" value={achatLigne.quantite} onChange={e=>setAchatLigne(l=>({...l,quantite:e.target.value}))}/><input type="number" className="inp" placeholder="Prix unitaire" value={achatLigne.prix_unitaire} onChange={e=>setAchatLigne(l=>({...l,prix_unitaire:e.target.value}))}/><button className="btn btn-ghost" onClick={addAchatLigne}>Ajouter</button></div>
              <div className="achat-lines">{achatForm.lignes.map((l,i)=><div key={i}><span>{l.code?l.code+" · ":""}{l.designation} · {NUM(l.quantite)} × {FCFA(l.prix_unitaire)}</span><b>{FCFA(l.montant)}</b><button onClick={()=>setAchatForm(f=>({...f,lignes:f.lignes.filter((_,j)=>j!==i)}))}>×</button></div>)}</div><textarea className="inp" rows="2" placeholder="Notes" value={achatForm.notes} onChange={e=>setAchatForm(a=>({...a,notes:e.target.value}))}/><div className="form-actions"><button className="btn btn-gold" disabled={achatSaving} onClick={saveAchatCommande}>{achatSaving?"Enregistrement…":"Enregistrer la commande"}</button><button className="btn btn-ghost" onClick={()=>setAchatFormOpen(false)}>Annuler</button></div></div>}
              {achatSection==="dashboard"&&<div className="responsive-card-grid"><div className="card"><div className="card-title">À traiter</div><div className="action-list"><div><span>Bons à valider</span><b>{achatsCommandes.filter(x=>x.validation==="a_valider").length}</b></div><div><span>Commandes non reçues</span><b>{achatsCommandes.filter(x=>x.statut!=="Reçue"&&x.validation!=="a_valider"&&x.validation!=="refusee").length}</b></div><div><span>Factures à payer</span><b>{achatsFactures.filter(x=>x.statut!=="Payée").length}</b></div><div><span>Réceptions enregistrées</span><b>{achatsReceptions.length}</b></div></div></div><div className="card"><div className="card-title">Dernières commandes</div>{commandes.slice(0,5).map(x=><div className="compact-row" key={x.id}><div><b>{x.numero}</b><small>{x.fournisseur_nom} · {x.ville}</small></div><span>{FCFA(x.total)}</span></div>)}</div></div>}
              {achatSection==="commandes"&&<><div className="card filter-bar"><Search size={15}/><input className="inp" placeholder="Rechercher une commande…" value={achatSearch} onChange={e=>setAchatSearch(e.target.value)}/></div><div className="desktop-table card" style={{padding:0}}><div className="table-wrap"><table className="tbl"><thead><tr><th>N°</th><th>Fournisseur</th><th>Ville</th><th>Date</th><th>Total</th><th>Statut</th><th>Actions</th></tr></thead><tbody>{commandes.map(x=><tr key={x.id}><td><b>{x.numero}</b></td><td>{x.fournisseur_nom}</td><td>{x.ville}</td><td>{fmtDate(x.date_commande)}</td><td>{FCFA(x.total)}</td><td><span className="badge">{x.statut}</span>{bcChip(x)}</td><td><div className="row-actions" style={{flexWrap:"wrap"}}>{bcActions(x)}</div></td></tr>)}</tbody></table></div></div><div className="mobile-cards">{commandes.map(x=><div className="card purchase-card" key={x.id}><div><b>{x.numero}</b><span className="badge">{x.statut}</span></div><div style={{margin:"4px 0"}}>{bcChip(x)}</div><h3>{x.fournisseur_nom}</h3><p>{x.ville} · {fmtDate(x.date_commande)}</p><strong>{FCFA(x.total)}</strong><div className="form-actions" style={{flexWrap:"wrap"}}>{bcActions(x)}</div></div>)}</div></>}
              {achatSection==="factures"&&<div className="responsive-card-grid">{achatsFactures.map(f=>{const paid=achatsPaiements.filter(p=>String(p.facture_id)===String(f.id)).reduce((a,p)=>a+toFiniteNumber(p.montant),0);return <div className="card purchase-card" key={f.id}><div><b>{f.numero}</b><span className="badge">{f.statut}</span></div><h3>{f.fournisseur_nom}</h3><p>Échéance : {fmtDate(f.date_echeance)}</p><strong>{FCFA(f.montant_ttc)}</strong><small>Reste : {FCFA(Math.max(0,toFiniteNumber(f.montant_ttc)-paid))}</small>{f.statut!=="Payée"&&<button className="btn btn-gold btn-sm" onClick={()=>payFactureAchat(f)}>Enregistrer paiement</button>}</div>})}</div>}
              {achatSection==="paiements"&&<div className="responsive-card-grid">{achatsPaiements.map(p=><div className="card compact-row" key={p.id}><div><b>{p.facture_numero}</b><small>{p.fournisseur_nom} · {fmtDate(p.date_paiement)}</small></div><strong>{FCFA(p.montant)}</strong></div>)}</div>}
              {achatSection==="fournisseurs"&&<div className="responsive-card-grid">{fournisseurs.map(f=><div className="card purchase-card" key={f.id}><div><b>{f.code||"Fournisseur"}</b></div><h3>{f.nom}</h3><p>{f.categorie||"—"} · {f.ville||"—"}</p><small>{f.contact||"Aucun contact"}</small></div>)}</div>}
            </div>;
          })()}

          {/* FOURNISSEURS */}
          {page==="fournisseurs"&&(isDirecteur()||can("fournisseurs"))&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Fournisseurs</div>
                <button className="btn btn-gold" onClick={()=>openModal("fournisseur")}>+ Nouveau fournisseur</button>
              </div>
              <div className="card scr">
                <table className="tbl">
                  <thead><tr><th>Code</th><th>Nom</th><th>Catégorie</th><th>Ville</th><th>Contact</th><th></th></tr></thead>
                  <tbody>{fournisseurs.map(f=>(
                    <tr key={f.id}>
                      <td style={{fontFamily:"monospace",fontSize:11,color:"#1E8A32"}}>{f.code}</td>
                      <td style={{fontWeight:600}}>{f.nom}</td><td><span className="badge bg2">{f.categorie}</span></td>
                      <td style={{color:"#5B6B62"}}>{f.ville}{f.pays&&f.pays!=="Cameroun"?" ("+f.pays+")":""}</td><td style={{color:"#5B6B62"}}>{f.contact}</td>
                      <td style={{display:"flex",gap:5}}>
                        {(canEdit())&&<button className="btn btn-ghost btn-sm" onClick={()=>openModal("fournisseur",f)}>✏</button>}
                        {(canEdit())&&<button className="btn btn-red btn-sm" onClick={()=>deleteFournisseur(f.id)}><Trash2 size={13}/></button>}
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* IMMOBILISATIONS — V2.4 — réservé à la Direction (voir navItems) */}
          {page==="immobilisations"&&(isDirecteur()||can("immobilisations"))&&(()=>{
            const villes=[...new Set(immobilisations.map(x=>x.ville).filter(Boolean))].sort();
            const categories=[...new Set(immobilisations.map(x=>x.categorie).filter(Boolean))].sort();
            const filtered=immobilisations.filter(x=>{const q=immoSearch.trim().toLowerCase();return (!q||[x.numero_inventaire,x.designation,x.categorie,x.marque,x.modele,x.numero_serie,x.ville,x.localisation,x.employe_nom].some(v=>String(v||"").toLowerCase().includes(q)))&&(immoVilleFilter==="toutes"||x.ville===immoVilleFilter)&&(immoCategorieFilter==="toutes"||x.categorie===immoCategorieFilter);});
            const valeur=filtered.reduce((a,x)=>a+toFiniteNumber(x.valeur_acquisition),0);
            const maintenance=filtered.filter(x=>x.prochaine_maintenance&&x.prochaine_maintenance<=addDaysISO(today(),30)).length;
            const k=(l,v,d)=><div className="card" style={{padding:16}}><div style={{fontSize:11,textTransform:"uppercase",color:"#5B6B62"}}>{l}</div><div style={{fontSize:25,fontWeight:800,color:"#0F1F17",marginTop:5}}>{v}</div><div style={{fontSize:11,color:"#2F6FDE"}}>{d}</div></div>;
            return <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,flexWrap:"wrap",gap:10}}><div><div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Immobilisations</div><div style={{fontSize:12,color:"#5B6B62"}}>Matériel, équipements, affectations et maintenance{!isDirecteur()&&" — lecture seule"}</div></div>{isDirecteur()&&<button className="btn btn-gold" onClick={()=>{resetImmoForm();setImmoShowForm(true);}}>+ Nouvelle immobilisation</button>}</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(5,1fr)",gap:12,marginBottom:16}}>{k("Équipements",filtered.length,"dans la vue")}{isDirecteur()&&k("Valeur d'acquisition",FCFA(valeur),"valeur historique")}{isDirecteur()&&k("Valeur nette (VNC)",FCFA(filtered.reduce((a,x)=>a+valeurNetteComptable(x),0)),"après amortissement")}{k("Villes",villes.length,accessScopeLabel())}{k("Maintenance",maintenance,"sous 30 jours / en retard")}</div>
              {isDirecteur()&&immoShowForm&&<div className="card" style={{marginBottom:16,borderColor:"rgba(47,158,63,0.360)"}}><div style={{fontSize:16,fontWeight:700,color:"#0F1F17",marginBottom:12}}>{immoForm.id?"Modifier":"Ajouter"} une immobilisation</div><div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:10}}>
                <div><label>Désignation *</label><input className="inp" value={immoForm.designation} onChange={e=>setImmoForm(f=>({...f,designation:e.target.value}))}/></div><div><label>Catégorie</label><select className="inp" value={immoForm.categorie} onChange={e=>setImmoForm(f=>({...f,categorie:e.target.value}))}>{["Ordinateur","Téléphone","Véhicule","Machine","Imprimante","Mobilier","Groupe électrogène","Équipement réseau","Bâtiment","Autre"].map(x=><option key={x}>{x}</option>)}</select></div><div><label>N° inventaire</label><input className="inp" placeholder="Automatique si vide" value={immoForm.numero_inventaire} onChange={e=>setImmoForm(f=>({...f,numero_inventaire:e.target.value}))}/></div>
                <div><label>Marque</label><input className="inp" value={immoForm.marque} onChange={e=>setImmoForm(f=>({...f,marque:e.target.value}))}/></div><div><label>Modèle</label><input className="inp" value={immoForm.modele} onChange={e=>setImmoForm(f=>({...f,modele:e.target.value}))}/></div><div><label>N° série / immatriculation</label><input className="inp" value={immoForm.numero_serie} onChange={e=>setImmoForm(f=>({...f,numero_serie:e.target.value}))}/></div>
                <div><label>Pays</label><select className="inp" value={immoForm.pays||"Cameroun"} onChange={e=>setImmoForm(f=>({...f,pays:e.target.value,ville:""}))}>{paysDisponibles.map(p=><option key={p}>{p}</option>)}</select></div><div><label>Ville *</label><select className="inp" value={immoForm.ville} onChange={e=>setImmoForm(f=>({...f,ville:e.target.value}))}><option value="">Choisir...</option>{villesPourPays(immoForm.pays||"Cameroun").map(v=><option key={v}>{v}</option>)}</select></div><div><label>Localisation</label><input className="inp" value={immoForm.localisation} onChange={e=>setImmoForm(f=>({...f,localisation:e.target.value}))}/></div><div><label>Responsable</label><select className="inp" value={immoForm.employe_id} onChange={e=>setImmoForm(f=>({...f,employe_id:e.target.value}))}><option value="">Non affecté</option>{employes.map(e=><option key={e.id} value={e.id}>{e.nom} — {e.ville}</option>)}</select></div>
                <div><label>Date d'acquisition</label><input type="date" className="inp" value={immoForm.date_acquisition||""} onChange={e=>setImmoForm(f=>({...f,date_acquisition:e.target.value}))}/></div><div><label>Valeur d'acquisition (FCFA)</label><input type="number" className="inp" value={immoForm.valeur_acquisition} onChange={e=>setImmoForm(f=>({...f,valeur_acquisition:e.target.value}))}/></div><div><label>Durée d'amortissement (années)</label><input type="number" min="1" className="inp" placeholder={"Auto : "+dureeAmortissementDefaut(immoForm.categorie)+" ans"} value={immoForm.duree_amortissement} onChange={e=>setImmoForm(f=>({...f,duree_amortissement:e.target.value}))}/></div><div><label>Fournisseur</label><input className="inp" value={immoForm.fournisseur} onChange={e=>setImmoForm(f=>({...f,fournisseur:e.target.value}))}/></div>
                <div><label>État</label><select className="inp" value={immoForm.etat} onChange={e=>setImmoForm(f=>({...f,etat:e.target.value}))}>{["Neuf","Bon","Moyen","À réparer","Hors service"].map(x=><option key={x}>{x}</option>)}</select></div><div><label>Statut</label><select className="inp" value={immoForm.statut} onChange={e=>setImmoForm(f=>({...f,statut:e.target.value}))}>{["En service","En stock","En maintenance","Réformé","Cédé"].map(x=><option key={x}>{x}</option>)}</select></div><div><label>Prochaine maintenance</label><input type="date" className="inp" value={immoForm.prochaine_maintenance||""} onChange={e=>setImmoForm(f=>({...f,prochaine_maintenance:e.target.value}))}/></div>
              </div><div style={{display:"flex",gap:8,marginTop:12}}><button className="btn btn-gold" disabled={immoSaving} onClick={saveImmobilisation}>{immoSaving?"Enregistrement…":"Enregistrer"}</button><button className="btn btn-ghost" onClick={()=>setImmoShowForm(false)}>Annuler</button></div></div>}
              <div className="card" style={{padding:12,marginBottom:14}}><div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"2fr 1fr 1fr",gap:8}}><div style={{position:"relative"}}><Search size={15} style={{position:"absolute",left:11,top:11,color:"#5B6B62"}}/><input className="inp" style={{paddingLeft:34}} placeholder="Rechercher équipement, série, ville…" value={immoSearch} onChange={e=>setImmoSearch(e.target.value)}/></div><select className="inp" value={immoVilleFilter} onChange={e=>setImmoVilleFilter(e.target.value)}><option value="toutes">Toutes les villes</option>{villes.map(v=><option key={v}>{v}</option>)}</select><select className="inp" value={immoCategorieFilter} onChange={e=>setImmoCategorieFilter(e.target.value)}><option value="toutes">Toutes les catégories</option>{categories.map(v=><option key={v}>{v}</option>)}</select></div></div>
              <div className="card" style={{padding:0}}><div className="table-wrap"><table className="tbl"><thead><tr><th>Inventaire</th><th>Désignation</th><th>Ville / lieu</th><th>Responsable</th><th>État</th>{isDirecteur()&&<th>Valeur d'origine</th>}{isDirecteur()&&<th>Valeur nette (VNC)</th>}<th>Actions</th></tr></thead><tbody>{filtered.map(x=><tr key={x.id}><td><b>{x.numero_inventaire||"—"}</b><div style={{fontSize:10,color:"#5B6B62"}}>{x.categorie}</div></td><td>{x.designation}<div style={{fontSize:10,color:"#5B6B62"}}>{[x.marque,x.modele,x.numero_serie].filter(Boolean).join(" · ")}</div></td><td>{x.ville}{x.pays&&x.pays!=="Cameroun"?" ("+x.pays+")":""}<div style={{fontSize:10,color:"#5B6B62"}}>{x.localisation}</div></td><td>{x.employe_nom||"Non affecté"}</td><td><span className="badge">{x.etat}</span><div style={{fontSize:10,color:"#5B6B62"}}>{x.statut}</div></td>{isDirecteur()&&<td>{FCFA(x.valeur_acquisition)}</td>}{isDirecteur()&&<td>{FCFA(valeurNetteComptable(x))}<div style={{fontSize:10,color:"#5B6B62"}}>Amorti sur {toFiniteNumber(x.duree_amortissement)||dureeAmortissementDefaut(x.categorie)} ans</div></td>}<td><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{isDirecteur()&&<button className="btn btn-ghost btn-sm" onClick={()=>{setImmoForm({...x,valeur_acquisition:String(x.valeur_acquisition||"")});setImmoShowForm(true);}}>Modifier</button>}{x.employe_id&&<button className="btn btn-ghost btn-sm" disabled={reportGenerating} title="Générer la fiche de remise préremplie" onClick={async()=>{setReportGenerating(true);try{const emp=employes.find(e=>String(e.id)===String(x.employe_id));const pdf=await generateFicheRemisePdfBase64(x,emp,entreprise);await openPdf(pdf,"Fiche de remise");}catch(e){showToast("Erreur : "+e.message,false);}setReportGenerating(false);}}><FileText size={13}/> Fiche de remise</button>}{isDirecteur()&&<button className="btn btn-red btn-sm" onClick={()=>deleteImmobilisation(x)}><Trash2 size={13}/></button>}</div></td></tr>)}</tbody></table>{!filtered.length&&<div style={{padding:26,textAlign:"center",color:"#5B6B62"}}>Aucune immobilisation trouvée.</div>}</div></div>
            </div>;
          })()}

          {/* PROCURATIONS — pouvoirs temporaires accordés par la Direction */}
          {page==="procurations"&&isDirecteur()&&(()=>{
            const procs = documentsEmployes.filter(d=>d.type_document==="Procuration").sort((a,b)=>String(b.date_document||"").localeCompare(String(a.date_document||"")));
            const enAttente = demandesSignature.filter(d=>d.type_document==="procuration"&&d.statut!=="signe").sort((a,b)=>String(b.created_at||"").localeCompare(String(a.created_at||"")));
            const estActive = d => d.date_expiration && d.date_expiration>=today();
            return (
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,flexWrap:"wrap",gap:10}}>
                  <div><div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Procurations</div><div style={{fontSize:12,color:"#5B6B62"}}>Pouvoirs temporaires accordés par la Direction à des collaborateurs</div></div>
                  <button className="btn btn-gold" onClick={()=>openModal("procuration",{
                    employe_id:"", procurationType:"encaissement", clientIds:[], objet:"",
                    date_debut:today(), date_fin:"", lieu_signature:"Dschang", date_signature:today(), clauses_particulieres:""
                  })}>+ Nouvelle procuration</button>
                </div>
                {enAttente.length>0&&(
                  <div className="card" style={{padding:0,marginBottom:16,borderColor:"rgba(47,158,63,0.360)"}}>
                    <div style={{padding:"10px 14px",fontSize:12,color:"#1E8A32",fontWeight:700}}>⏳ En attente de signature du Mandataire ({enAttente.length})</div>
                    <div className="table-wrap">
                      <table className="tbl">
                        <thead><tr><th>Mandataire</th><th>Objet</th><th>Demandée le</th><th>Actions</th></tr></thead>
                        <tbody>
                          {enAttente.map(d=>{
                            const s=(d.signataires||[])[0];
                            return (
                              <tr key={d.id}>
                                <td>{s?.label?.replace(/^Le Mandataire \(|\)$/g,"")||"—"}</td>
                                <td style={{maxWidth:320}}>{(d.payload?.data?.objet||"").slice(0,80)}</td>
                                <td>{d.created_at?new Date(d.created_at).toLocaleDateString("fr-FR"):"—"}</td>
                                <td><button className="btn btn-ghost btn-sm" onClick={()=>deleteProcurationDemande(d)} style={{color:"#D6453D"}}><Trash2 size={13}/> Annuler</button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className="card" style={{padding:0}}>
                  <div className="table-wrap">
                    <table className="tbl">
                      <thead><tr><th>Mandataire</th><th>Objet</th><th>Période</th><th>Statut</th><th>Actions</th></tr></thead>
                      <tbody>
                        {procs.map(d=>(
                          <tr key={d.id}>
                            <td>{d.employe_nom}</td>
                            <td style={{maxWidth:320}}>{(d.titre||"").replace(/^Procuration — /,"")}</td>
                            <td>{fmtDate(d.date_document)} → {d.date_expiration?fmtDate(d.date_expiration):"—"}</td>
                            <td><span className={"badge "+(estActive(d)?"bg":"br")}>{estActive(d)?"Active":"Expirée"}</span></td>
                            <td style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                              <button className="btn btn-ghost btn-sm" onClick={()=>openMyDocument(d)}><Eye size={13}/> Voir le PDF</button>
                              <button className="btn btn-ghost btn-sm" onClick={()=>deleteProcurationDocument(d)} style={{color:"#D6453D"}}><Trash2 size={13}/> Supprimer</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {!procs.length&&<div style={{padding:26,textAlign:"center",color:"#5B6B62"}}>Aucune procuration générée pour l'instant.</div>}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* EMPLOYES — V2.3 RH */}
          {page==="employes"&&(isDirecteur()||can("employes"))&&(()=>{
            const villesRH=[...new Set(employes.map(e=>e.ville).filter(Boolean))].sort();
            const now=new Date();
            const dans90Jours=new Date(now); dans90Jours.setDate(now.getDate()+90);
            const contratsBientot=employes.filter(e=>{
              const d=parseDateSafe(e.date_fin_contrat); return d&&d>=now&&d<=dans90Jours;
            });
            const congesAttente=conges.filter(c=>String(c.statut||"").toLowerCase().includes("attente"));
            const actifs=employes.filter(e=>e.actif!==false&&String(e.statut_rh||"Actif").toLowerCase()!=="inactif");
            const filtered=employes.filter(e=>{
              const q=rhSearch.trim().toLowerCase();
              const matchQ=!q||[e.nom,e.prenom,e.matricule,e.poste,e.service,e.departement,e.ville,e.tel,e.email]
                .some(v=>String(v||"").toLowerCase().includes(q));
              const matchVille=rhVilleFilter==="toutes"||e.ville===rhVilleFilter;
              const estActif=e.actif!==false&&String(e.statut_rh||"Actif").toLowerCase()!=="inactif";
              const matchStatut=rhStatutFilter==="tous"||(rhStatutFilter==="actifs"?estActif:!estActif);
              return matchQ&&matchVille&&matchStatut;
            });
            const kpi=(label,value,detail)=><div className="card" style={{padding:16}}><div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.2,color:"#5B6B62"}}>{label}</div><div style={{fontSize:27,fontWeight:800,color:"#0F1F17",marginTop:5}}>{value}</div><div style={{fontSize:11,color:"#2F6FDE",marginTop:3}}>{detail}</div></div>;
            return <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
                <div><div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Ressources humaines</div><div style={{fontSize:12,color:"#5B6B62",marginTop:4}}>Fiches professionnelles, contrats, congés et historique du personnel</div></div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {[['personnel','Personnel'],['conges','Congés'],['feries','Jours fériés'],['documents','Documents'],['evaluations','Évaluations'],['historique','Historique']].map(([id,label])=><button key={id} className={"btn "+(rhSection===id?"btn-gold":"btn-ghost")} onClick={()=>setRhSection(id)}>{label}</button>)}
                  <button className="btn btn-ghost btn-icon" disabled={reportGenerating} onClick={async()=>{
                    setReportGenerating(true); try { const pdf=await generateEmployeesListPdfBase64(entreprise,filtered); await openPdf(pdf,"Registre du personnel"); }
                    catch(e){showToast("Erreur : "+e.message,false);} setReportGenerating(false);
                  }}><FileText size={14}/> Exporter le registre</button>
                  {isDirecteur()&&<button className="btn btn-ghost btn-icon" disabled={badgeBusy} title="Imprimante de cartes PVC : 1 carte par page (recto puis verso), format 54 x 85,6 mm" onClick={()=>genererBadges(filtered,"carte")}><ShieldCheck size={14}/> {badgeBusy?"Préparation…":"Badges cartes ("+filtered.filter(employeActifRH).length+")"}</button>}
                  {isDirecteur()&&<button className="btn btn-ghost btn-icon" disabled={badgeBusy} title="Feuilles A4 : 9 badges par feuille à découper" onClick={()=>genererBadges(filtered,"a4")}>Feuilles A4</button>}
                  <button className="btn btn-gold" onClick={()=>openModal("employe",{_rhTab:"identite",actif:true,statut_rh:"Actif",permissions:[],fonctions:["employe"],role:"employe"})}>+ Nouvel employé</button>
                </div>
              </div>
              {rhSection==="personnel"&&<>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:12,marginBottom:18}}>
                {kpi("Effectif",employes.length,actifs.length+" actif(s)")}
                {kpi("Villes",villesRH.length,accessScopeLabel())}
                {kpi("Congés à valider",congesAttente.length,"Demandes en attente")}
                {kpi("Contrats à surveiller",contratsBientot.length,"Échéance sous 90 jours")}
              </div>
              {(congesAttente.length>0||contratsBientot.length>0)&&<div className="card" style={{marginBottom:16,borderColor:"rgba(47,158,63,0.336)"}}>
                <div style={{fontSize:12,fontWeight:700,color:"#0F1F17",marginBottom:8}}>Alertes RH</div>
                {congesAttente.length>0&&<div style={{fontSize:12,color:"#5B6B62",marginBottom:4}}>• {congesAttente.length} demande(s) de congé attendent une décision.</div>}
                {contratsBientot.length>0&&<div style={{fontSize:12,color:"#5B6B62"}}>• {contratsBientot.length} contrat(s) arrivent à échéance dans les 90 prochains jours.</div>}
              </div>}
              <div className="card" style={{marginBottom:16,padding:12}}>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"2fr 1fr 1fr",gap:8}}>
                  <div style={{position:"relative"}}><Search size={15} style={{position:"absolute",left:11,top:11,color:"#5B6B62"}}/><input className="inp" style={{paddingLeft:34}} placeholder="Rechercher nom, matricule, poste, ville…" value={rhSearch} onChange={e=>setRhSearch(e.target.value)}/></div>
                  <select className="inp" value={rhVilleFilter} onChange={e=>setRhVilleFilter(e.target.value)}><option value="toutes">Toutes les villes</option>{villesRH.map(v=><option key={v}>{v}</option>)}</select>
                  <select className="inp" value={rhStatutFilter} onChange={e=>setRhStatutFilter(e.target.value)}><option value="tous">Tous les statuts</option><option value="actifs">Actifs</option><option value="inactifs">Inactifs</option></select>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(315px,1fr))",gap:14,marginBottom:24}}>
                {filtered.map(e=>{
                  const permsRaw=safeJsonParse(e.permissions,[]); const perms=Array.isArray(permsRaw)?permsRaw:[];
                  const empConges=conges.filter(c=>String(c.employe_id)===String(e.id));
                  const empDocs=documentsEmployes.filter(d=>String(d.employe_id)===String(e.id));
                  const manager=employes.find(m=>String(m.id)===String(e.manager_id));
                  const estActif=e.actif!==false&&String(e.statut_rh||"Actif").toLowerCase()!=="inactif";
                  return <div key={e.id} className="card" style={{borderColor:estActif?"#E3E9E6":"rgba(192,57,43,.3)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",gap:10,marginBottom:10}}>
                      <div style={{display:"flex",gap:10,alignItems:"center"}}>
                        <div style={{width:52,height:52,borderRadius:"50%",background:"#F1F5F3",border:"1px solid #E3E9E6",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>{e.photo_url?<img alt="" src={e.photo_url} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}}/>:<User size={22}/>}</div>
                        <div><div style={{fontSize:15,fontWeight:700}}>{[e.prenom,e.nom].filter(Boolean).join(" ")}</div><div style={{fontSize:10,color:"#1E8A32",fontFamily:"monospace",marginTop:2}}>{e.matricule||"Matricule en attente"} · @{e.username}</div></div>
                      </div>
                      <span className={"badge "+(estActif?"bg":"br")} style={{alignSelf:"center",whiteSpace:"nowrap",minWidth:58,padding:"4px 12px"}}>{employeStatutLabel(e)}</span>
                    </div>
                    <div style={{fontSize:12,color:"#5B6B62",marginBottom:3}}><Briefcase size={12} style={{verticalAlign:"middle",marginRight:5}}/>{e.poste||"Poste non renseigné"} · {e.departement||e.service||"—"}</div>
                    <div style={{fontSize:12,color:"#2F6FDE",marginBottom:3}}>📍 {e.ville||"Ville non attribuée"}</div>
                    <div style={{fontSize:12,color:"#5B6B62",marginBottom:3}}><Phone size={12} style={{verticalAlign:"middle",marginRight:5}}/>{e.tel||"Téléphone non renseigné"}</div>
                    {manager&&<div style={{fontSize:11,color:"#5B6B62",marginTop:6}}>Responsable : {manager.nom}</div>}
                    <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}><span className="badge bg2">{empConges.length} congé(s)</span><span className="badge bg2">{empDocs.length} document(s)</span>{e.type_contrat&&<span className="badge bg2">{e.type_contrat}</span>}</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:12}}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>{openModal("employe",{...e,permissions:perms,fonctions:fonctionsDepuisEmploye(e),_rhTab:"identite"});recalculerSoldeConges(e.id);}}>Ouvrir la fiche RH</button>
                      {isDirecteur()&&estActif&&<button className="btn btn-ghost btn-sm" disabled={badgeBusy} onClick={()=>genererBadges([e])}><ShieldCheck size={13}/> Badge</button>}
                      {isDirecteur()&&estActif&&<button className="btn btn-ghost btn-sm" disabled={badgeBusy} title="Badge perdu : l'ancien QR code ne fonctionnera plus" onClick={()=>renouvelerBadge(e)}>Badge perdu</button>}
                      {isDirecteur()&&<button className="btn btn-ghost btn-sm" onClick={()=>openModal("contratTravail",{
                        employe_id:e.id, type_contrat:e.type_contrat||"CDI", poste:e.poste||"", departement:e.departement||e.service||"",
                        ville:e.ville||"", date_debut:e.date_embauche||today(), date_fin_contrat:e.date_fin_contrat||"",
                        periode_essai_duree: e.periode_essai_fin?"":"3 mois", periode_essai_fin:e.periode_essai_fin||"",
                        salaire_base:e.salaire_base||"", prime_fixe:e.prime_fixe||"", mode_paiement:e.mode_paiement||"",
                        horaire_hebdo:"40 heures par semaine", lieu_signature:"Dschang", date_signature:today(), clauses_particulieres:""
                      })}><FileText size={13}/> Générer le contrat</button>}
                    </div>
                  </div>;
                })}
                {filtered.length===0&&<div className="card" style={{color:"#5B6B62"}}>Aucun employé ne correspond aux filtres.</div>}
              </div>
              </>}
              {rhSection==="conges"&&<div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><div style={{fontSize:16,color:"#0F1F17"}}>Demandes de congés</div><button className="btn btn-gold" onClick={()=>openModal("congeRH",{statut:"En attente",type_conge:"Congé annuel",date_debut:today(),date_fin:today()})}>+ Nouvelle demande</button></div>
                <div className="card"><div style={{overflowX:"auto"}}><table className="tbl"><thead><tr><th>Employé</th><th>Type</th><th>Période</th><th>Jours</th><th>Statut</th><th>Décision</th></tr></thead><tbody>{conges.map(c=><tr key={c.id}><td>{c.employe_nom||rhEmployeeLabel(c.employe_id)}</td><td>{c.type_conge}</td><td>{c.date_debut} → {c.date_fin}</td><td>{c.nb_jours}</td><td><span className="badge bg2">{c.statut}</span></td><td style={{display:"flex",gap:5,flexWrap:"wrap"}}><button className="btn btn-ghost btn-sm" onClick={()=>openModal("congeRH",c)}>✏</button>{String(c.statut).toLowerCase().includes("attente")&&<><button className="btn btn-gold btn-sm" onClick={()=>decideCongeRH(c,"Approuvé")}>Approuver</button><button className="btn btn-red btn-sm" onClick={()=>decideCongeRH(c,"Refusé")}>Refuser</button></>}<button className="btn btn-red btn-sm" onClick={()=>deleteRhRecord("conges",c.id,"cette demande")}>✕</button></td></tr>)}</tbody></table></div>{conges.length===0&&<div style={{color:"#5B6B62",padding:12}}>Aucune demande de congé.</div>}</div>
              </div>}
              {rhSection==="feries"&&<div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div><div style={{fontSize:16,color:"#0F1F17"}}>Jours fériés</div><div style={{fontSize:11,color:"#5B6B62",marginTop:2}}>Exclus automatiquement du décompte des congés (avec les week-ends).</div></div>
                  {isDirecteur()&&<button className="btn btn-gold" onClick={()=>openModal("jourFerie",{date:today(),mobile:false})}>+ Ajouter</button>}
                </div>
                <div className="card"><div style={{overflowX:"auto"}}><table className="tbl"><thead><tr><th>Date</th><th>Libellé</th><th>Type</th>{isDirecteur()&&<th>Actions</th>}</tr></thead><tbody>{joursFeries.map(j=><tr key={j.id}><td>{j.date}</td><td>{j.libelle}</td><td>{j.mobile?<span className="badge" style={{color:"#1E8A32",background:"rgba(47,158,63,0.096)"}}>Mobile — à confirmer</span>:<span className="badge bg2">Fixe</span>}</td>{isDirecteur()&&<td style={{display:"flex",gap:5}}><button className="btn btn-ghost btn-sm" onClick={()=>openModal("jourFerie",j)}>✏</button><button className="btn btn-red btn-sm" onClick={()=>deleteRhRecord("jours_feries",j.id,"ce jour férié")}>✕</button></td>}</tr>)}</tbody></table></div>{joursFeries.length===0&&<div style={{color:"#5B6B62",padding:12}}>Aucun jour férié déclaré.</div>}</div>
              </div>}
              {rhSection==="documents"&&<div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><div style={{fontSize:16,color:"#0F1F17"}}>Documents RH</div><button className="btn btn-gold" onClick={()=>openModal("documentRH",{type_document:"Contrat",confidentiel:true})}>+ Ajouter un document</button></div>
                <div className="card"><div style={{overflowX:"auto"}}><table className="tbl"><thead><tr><th>Employé</th><th>Type</th><th>Titre</th><th>Statut</th><th>Expiration</th><th>Fichier</th><th>Actions</th></tr></thead><tbody>{documentsEmployes.map(d=><tr key={d.id}><td>{d.employe_nom||rhEmployeeLabel(d.employe_id)}</td><td>{d.type_document}</td><td>{d.titre}</td><td>{d.statut==="En attente"?<span className="badge" style={{color:"#1E8A32",background:"rgba(47,158,63,0.096)"}}>En attente</span>:<span className="badge" style={{color:"#1E7A2D",background:"rgba(47,158,63,0.12)"}}>Validé</span>}</td><td>{d.date_expiration||"—"}</td><td>{d.fichier_url?<button className="btn btn-ghost btn-sm" onClick={()=>openMyDocument(d)}>Ouvrir</button>:"—"}</td><td style={{display:"flex",gap:5}}>{d.statut==="En attente"&&<button className="btn btn-gold btn-sm" onClick={()=>validerMonDocument(d)}>✓ Valider</button>}<button className="btn btn-ghost btn-sm" onClick={()=>openModal("documentRH",d)}>✏</button><button className="btn btn-red btn-sm" onClick={()=>deleteRhRecord("documents_employes",d.id,"ce document")}>✕</button></td></tr>)}</tbody></table></div>{documentsEmployes.length===0&&<div style={{color:"#5B6B62",padding:12}}>Aucun document RH.</div>}</div>
              </div>}
              {rhSection==="evaluations"&&<div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><div style={{fontSize:16,color:"#0F1F17"}}>Évaluations</div><button className="btn btn-gold" onClick={()=>openModal("evaluationRH",{type_evaluation:"Évaluation périodique",statut:"Brouillon",note:""})}>+ Nouvelle évaluation</button></div>
                <div className="card"><div style={{overflowX:"auto"}}><table className="tbl"><thead><tr><th>Employé</th><th>Période</th><th>Type</th><th>Note</th><th>Statut</th><th>Actions</th></tr></thead><tbody>{evaluationsEmployes.map(ev=><tr key={ev.id}><td>{ev.employe_nom||rhEmployeeLabel(ev.employe_id)}</td><td>{ev.periode_debut||"—"} → {ev.periode_fin||"—"}</td><td>{ev.type_evaluation}</td><td>{ev.note==null?"—":ev.note+"/100"}</td><td><span className="badge bg2">{ev.statut}</span></td><td style={{display:"flex",gap:5}}><button className="btn btn-ghost btn-sm" onClick={()=>openModal("evaluationRH",ev)}>✏</button><button className="btn btn-red btn-sm" onClick={()=>deleteRhRecord("evaluations_employes",ev.id,"cette évaluation")}>✕</button></td></tr>)}</tbody></table></div>{evaluationsEmployes.length===0&&<div style={{color:"#5B6B62",padding:12}}>Aucune évaluation.</div>}</div>
              </div>}
              {rhSection==="historique"&&<div className="card"><div style={{fontSize:16,color:"#0F1F17",marginBottom:12}}>Historique RH détaillé</div><div style={{overflowX:"auto"}}><table className="tbl"><thead><tr><th>Date</th><th>Employé</th><th>Catégorie</th><th>Champ</th><th>Ancienne valeur</th><th>Nouvelle valeur</th><th>Auteur</th></tr></thead><tbody>{historiqueEmployes.map(h=><tr key={h.id}><td>{new Date(h.created_at).toLocaleString("fr-FR")}</td><td>{h.employe_nom||rhEmployeeLabel(h.employe_id)}</td><td>{h.categorie}</td><td>{h.champ}</td><td style={{color:"#5B6B62"}}>{h.ancienne_valeur||"—"}</td><td>{h.nouvelle_valeur||"—"}</td><td>{h.effectue_par_nom||"—"}</td></tr>)}</tbody></table></div></div>}
              <div className="card"><div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Historique connexions</div><table className="tbl"><thead><tr><th>Date</th><th>Employé</th><th>Matricule</th></tr></thead><tbody>{connexions.slice(0,10).map((c,i)=><tr key={i}><td style={{color:"#5B6B62"}}>{new Date(c.date).toLocaleString("fr-FR")}</td><td>{c.employe_nom}</td><td style={{fontFamily:"monospace",fontSize:11,color:"#1E8A32"}}>{c.employe_matricule}</td></tr>)}</tbody></table></div>
            </div>;
          })()}


          {/* FICHES DE SALAIRE */}
          {page==="salaires"&&(isDirecteur()||can("salaires"))&&(()=>{
            const empSel = employes.find(e=>String(e.id)===String(bulletinForm.employeId));
            const preview = calculerBulletinPaie(
              parseInt(bulletinForm.salaireBase||0),
              parseInt(bulletinForm.primes||0),
              parseInt(bulletinForm.heuresSup||0),
              parseInt(bulletinForm.autresRetenues||0),
              bulletinForm.calculerTaxes !== false
            );
            return (
              <div>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:6}}>Fiches de salaire</div>
                {isDirecteur()&&bulletinsPaie.length>0&&(()=>{
                  const mois = [...new Set(bulletinsPaie.map(b=>String(b.mois||"").slice(0,7)))].filter(Boolean).sort().reverse();
                  const m = ficheMois && mois.includes(ficheMois) ? ficheMois : mois[0];
                  const liste = bulletinsPaie.filter(b=>String(b.mois||"").slice(0,7)===m);
                  const sig = liste.filter(b=>ficheEtat(b)==="signee");
                  const att = liste.filter(b=>ficheEtat(b)==="a_signer");
                  const non = liste.filter(b=>ficheEtat(b)==="non_envoyee");
                  return (
                    <div className="card scr" style={{marginBottom:14,borderTop:"3px solid #D4A84B"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:10}}>
                        <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62"}}>Signature des fiches de paie</div>
                        <select className="inp" style={{width:"auto",minWidth:160}} value={m} onChange={e=>setFicheMois(e.target.value)}>{mois.map(x=><option key={x} value={x}>{ficheMoisLbl(x)}</option>)}</select>
                      </div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
                        <span style={{padding:"5px 10px",borderRadius:14,fontSize:12,fontWeight:600,color:"#1E7A2D",background:"rgba(47,158,63,.12)"}}>{sig.length} signée{sig.length>1?"s":""}</span>
                        <span style={{padding:"5px 10px",borderRadius:14,fontSize:12,fontWeight:600,color:"#8A6A12",background:"rgba(212,168,75,.2)"}}>{att.length} en attente</span>
                        {non.length>0&&<span style={{padding:"5px 10px",borderRadius:14,fontSize:12,fontWeight:600,color:"#5B6B62",background:"#EDF1EF"}}>{non.length} non envoyée{non.length>1?"s":""}</span>}
                      </div>
                      {att.length>0&&<div style={{fontSize:12,color:"#5B6B62",marginBottom:10}}>Pas encore signé : {att.map(b=>b.employe_nom).join(", ")}</div>}
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {att.length>0&&<button className="btn btn-gold btn-sm" onClick={()=>ficheRelancerMois(m)}>Relancer les {att.length} non-signataire{att.length>1?"s":""}</button>}
                        {non.length>0&&<button className="btn btn-ghost btn-sm" onClick={()=>{ if (window.confirm("Envoyer "+non.length+" fiche(s) de "+ficheMoisLbl(m)+" pour signature ?")) ficheEnvoyerMois(m); }}>Envoyer les {non.length} non envoyée{non.length>1?"s":""}</button>}
                      </div>
                      <div style={{fontSize:11,color:"#8A978F",marginTop:10}}>Chaque nouvelle fiche saisie part automatiquement à l'employé. Rappel automatique tous les 3 jours (3 fois au maximum).</div>
                    </div>
                  );
                })()}
                {isDirecteur()&&(()=>{
                  const L = bulletinsAConfirmerListe();
                  if (!L.a.length && !L.non.length) return null;
                  const dm = new Date(L.mois+"-01T12:00:00").toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                  const cart = (b)=>NUM(Math.round(b/24*10)/10);
                  return (
                    <div className="card" style={{marginBottom:18,borderColor:"rgba(47,158,63,0.36)"}}>
                      <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#1E8A32",marginBottom:6}}>Bulletins à confirmer — {dm}</div>
                      <div style={{fontSize:12,color:"#5B6B62",marginBottom:12}}>Pré-remplis pour ceux qui ont atteint leur objectif de cartons (factures du mois). Vérifiez, modifiez si besoin, puis confirmez : le bulletin est alors enregistré et visible par l'employé.</div>
                      {L.a.length===0&&<div style={{fontSize:12,color:"#5B6B62"}}>Personne n'a atteint son objectif ce mois-là.</div>}
                      {L.a.map(it=>{
                        const primes = it.r.primeFixe+it.r.comm;
                        const calc = calculerBulletinPaie(it.r.fixe, primes, 0, 0, true);
                        return (
                          <div key={it.e.id} style={{display:"flex",flexWrap:"wrap",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 0",borderTop:"1px solid #EDF1EF"}}>
                            <div style={{minWidth:200,flex:1}}>
                              <div style={{fontWeight:700,color:"#0F1F17"}}>{it.e.nom} <span style={{fontWeight:400,fontSize:11,color:"#1E7A2D"}}>✓ {cart(it.r.btl)} / {cart(it.r.objBtl)} cartons</span></div>
                              <div style={{fontSize:11,color:"#5B6B62",lineHeight:1.6}}>Fixe {FCFA(it.r.fixe)}{it.r.primeFixe>0?" + prime fixe "+FCFA(it.r.primeFixe):""} + commission {FCFA(it.r.comm)} ({it.r.pct} % de {FCFA(it.r.encaisse)} encaissés) · Net estimé <strong style={{color:"#1E7A2D"}}>{FCFA(calc.net)}</strong></div>
                            </div>
                            <div style={{display:"flex",gap:8}}>
                              <button className="btn btn-ghost btn-sm" onClick={()=>modifierBulletinAuto(it)}>Modifier</button>
                              <button className="btn btn-gold btn-sm" disabled={bulletinSaving||calc.brut<=0} onClick={()=>confirmerBulletinAuto(it)}>Confirmer</button>
                            </div>
                          </div>
                        );
                      })}
                      {L.non.length>0&&<div style={{fontSize:11,color:"#5B6B62",marginTop:10,borderTop:"1px solid #EDF1EF",paddingTop:8}}>Objectif non atteint (pas de bulletin automatique) : {L.non.map(it=>it.e.nom+" ("+cart(it.r.btl)+" / "+cart(it.r.objBtl)+")").join(" · ")}</div>}
                    </div>
                  );
                })()}
                {isDirecteur()&&<div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1.2fr",gap:18,marginBottom:24}}>
                  <div className="card">
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Nouveau bulletin</div>
                    <div className="fld"><label className="lbl">Employé *</label>
                      <select className="inp" value={bulletinForm.employeId} onChange={e=>{
                        const id=e.target.value;
                        const chosen=employes.find(emp=>String(emp.id)===String(id));
                        // Reprend directement le salaire de base (et la prime fixe) de la fiche
                        // employé, pour éviter de le ressaisir à chaque bulletin. Reste modifiable
                        // ensuite si le montant du mois diffère exceptionnellement.
                        setBulletinForm(f=>({
                          ...f,
                          employeId:id,
                          salaireBase: chosen?.salaire_base!=null && chosen.salaire_base!=="" ? String(chosen.salaire_base) : f.salaireBase,
                          primes: chosen?.prime_fixe!=null && chosen.prime_fixe!=="" ? String(chosen.prime_fixe) : f.primes
                        }));
                      }}>
                        <option value="">Choisir...</option>
                        {employes.filter(e=>e.actif).map(e=><option key={e.id} value={e.id}>{e.nom} — {e.poste}</option>)}
                      </select>
                    </div>
                    <div className="fld"><label className="lbl">Mois</label><input type="month" className="inp" value={bulletinForm.mois} onChange={e=>setBulletinForm(f=>({...f,mois:e.target.value}))} /></div>
                    <div className="fld"><label className="lbl">Salaire de base (FCFA) *</label><input type="number" className="inp" value={bulletinForm.salaireBase} onChange={e=>setBulletinForm(f=>({...f,salaireBase:e.target.value}))} />
                      {empSel&&<div style={{fontSize:10,color:"#2F6FDE",marginTop:3}}>Repris de la fiche employé — modifiable si besoin ce mois-ci.</div>}
                    </div>
                    <div className="fld"><label className="lbl">Primes (FCFA)</label><input type="number" className="inp" value={bulletinForm.primes} onChange={e=>setBulletinForm(f=>({...f,primes:e.target.value}))} /></div>
                    <div className="fld"><label className="lbl">Heures supplémentaires (FCFA)</label><input type="number" className="inp" value={bulletinForm.heuresSup} onChange={e=>setBulletinForm(f=>({...f,heuresSup:e.target.value}))} /></div>
                    <div style={{background:"#F5F7F6",border:"1px solid #E3E9E6",borderRadius:6,padding:"12px 14px",marginBottom:14}}>
                      <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
                        <input
                          type="checkbox"
                          checked={bulletinForm.calculerTaxes !== false}
                          onChange={e=>setBulletinForm(f=>({...f,calculerTaxes:e.target.checked}))}
                          style={{marginTop:3,accentColor:"#1E8A32"}}
                        />
                        <span>
                          <span style={{display:"block",color:"#0F1F17",fontSize:13,fontWeight:600}}>
                            Calculer les taxes et cotisations
                          </span>
                          <span style={{display:"block",color:"#5B6B62",fontSize:11,lineHeight:1.5,marginTop:3}}>
                            CNPS, CFC, IRPP et CAC. Décoche cette option pour ne déduire que les autres retenues saisies.
                          </span>
                        </span>
                      </label>
                    </div>
                    {(()=>{
                      if (!bulletinForm.employeId) return null;
                      const ouv = vrOuvertes(bulletinForm.employeId);
                      const nonSig = vrListe.filter(v=>v.recuperable && v.statut==="a_signer" && String(v.employe_id)===String(bulletinForm.employeId));
                      if (!ouv.length && !nonSig.length) return null;
                      return (
                        <div style={{background:"#FFF9EC",border:"1px solid #F0D9A0",borderRadius:8,padding:"12px 14px",marginBottom:14}}>
                          <div style={{fontSize:12.5,fontWeight:700,color:"#7A4B00",marginBottom:6}}>Avances de {empSel?empSel.nom:"cet employé"} à retenir (vous décidez du montant)</div>
                          {ouv.map(v=>(
                            <div key={v.id} style={{display:"flex",gap:8,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",padding:"6px 0",borderTop:"1px solid #F3E4BC"}}>
                              <div style={{fontSize:12.5,color:"#2B3A32",flex:"1 1 180px"}}>{v.type_versement} du {vrDateCourte(v.date_versement)}<br/><span style={{color:"#5B6B62"}}>reste {FCFA(v.solde_restant)} sur {FCFA(v.montant)}</span></div>
                              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                                <input type="number" inputMode="numeric" className="inp" style={{width:110}} placeholder="Retenir…" value={vrRet[v.id]||""} onChange={e=>vrSetRet(v,e.target.value)} />
                                <button type="button" className="btn btn-ghost btn-sm" onClick={()=>vrSetRet(v,v.solde_restant)}>Tout</button>
                              </div>
                            </div>
                          ))}
                          {nonSig.map(v=><div key={v.id} style={{fontSize:11.5,color:"#A3231C",marginTop:6}}>{v.type_versement} de {FCFA(v.montant)} ({v.numero}) : bon pas encore signé — il ne peut pas être retenu avant.</div>)}
                          <div style={{fontSize:11,color:"#5B6B62",marginTop:6}}>Le montant retenu s'ajoute aux « autres retenues » ci-dessous et vient en déduction du net à payer.</div>
                        </div>
                      );
                    })()}
                    <div className="fld"><label className="lbl">Autres retenues — libellé</label><input type="text" className="inp" placeholder="ex: avance sur salaire" value={bulletinForm.autresRetenuesLibelle} onChange={e=>setBulletinForm(f=>({...f,autresRetenuesLibelle:e.target.value}))} /></div>
                    <div className="fld"><label className="lbl">Autres retenues — montant (FCFA)</label><input type="number" className="inp" value={bulletinForm.autresRetenues} onChange={e=>setBulletinForm(f=>({...f,autresRetenues:e.target.value}))} /></div>
                    <button className="btn btn-gold" style={{width:"100%",marginTop:6}} disabled={!bulletinForm.employeId||!bulletinForm.salaireBase||bulletinSaving} onClick={async()=>{
                      const saved = await saveBulletinPaie(empSel, preview, undefined, vrItemsRet());
                      if (saved) setVrRet({});
                      if (saved) setBulletinForm({ employeId:"", mois:today().slice(0,7), salaireBase:"", primes:"", heuresSup:"", calculerTaxes:true, autresRetenues:"", autresRetenuesLibelle:"" });
                    }}>{bulletinSaving?"...":"Générer et enregistrer"}</button>
                  </div>
                  <div className="card">
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Aperçu du calcul</div>
                    {bulletinForm.salaireBase?(
                      <>
                        <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #EDF1EF",fontSize:13}}><span style={{color:"#5B6B62"}}>Salaire brut</span><span style={{fontWeight:700}}>{FCFA(preview.brut)}</span></div>
                        {bulletinForm.calculerTaxes !== false ? (
                          <>
                            <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #EDF1EF",fontSize:13}}><span style={{color:"#5B6B62"}}>CNPS salarié (4,2%)</span><span style={{color:"#D6453D"}}>− {FCFA(preview.cnps)}</span></div>
                            <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #EDF1EF",fontSize:13}}><span style={{color:"#5B6B62"}}>Crédit Foncier — CFC (1%)</span><span style={{color:"#D6453D"}}>− {FCFA(preview.cfc)}</span></div>
                            <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #EDF1EF",fontSize:13}}><span style={{color:"#5B6B62"}}>IRPP</span><span style={{color:"#D6453D"}}>− {FCFA(preview.irpp)}</span></div>
                            <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #EDF1EF",fontSize:13}}><span style={{color:"#5B6B62"}}>CAC (10% de l'IRPP)</span><span style={{color:"#D6453D"}}>− {FCFA(preview.cac)}</span></div>
                          </>
                        ) : (
                          <div style={{padding:"9px 10px",margin:"8px 0",borderRadius:5,background:"rgba(47,158,63,0.080)",border:"1px solid rgba(47,158,63,0.200)",color:"#1E8A32",fontSize:12}}>
                            Taxes et cotisations non calculées.
                          </div>
                        )}
                        {preview.autresRetenues>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #EDF1EF",fontSize:13}}><span style={{color:"#5B6B62"}}>{bulletinForm.autresRetenuesLibelle||"Autres retenues"}</span><span style={{color:"#D6453D"}}>− {FCFA(preview.autresRetenues)}</span></div>}
                        <div style={{display:"flex",justifyContent:"space-between",padding:"12px 0",marginTop:8,fontSize:17}}><span style={{color:"#1E8A32",fontWeight:700}}>Net à payer</span><span style={{color:"#1E7A2D",fontWeight:700}}>{FCFA(preview.net)}</span></div>
                      </>
                    ):<div style={{color:"#5B6B62",fontSize:13}}>Renseigne le salaire de base pour voir le calcul.</div>}
                  </div>
                </div>}

                {isDirecteur()&&(()=>{
                  const ouverts = vrListe.filter(v=>v.recuperable && v.statut==="signe" && Number(v.solde_restant)>0);
                  const totalOuvert = ouverts.reduce((s,v)=>s+(Number(v.solde_restant)||0),0);
                  return (
                    <div className="card" style={{marginBottom:24}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:12}}>
                        <div>
                          <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62"}}>Versements aux employés</div>
                          <div style={{fontSize:12,color:"#5B6B62",marginTop:3}}>Avances, primes… avec un bon à signer. {totalOuvert>0?<>Avances encore à retenir : <b style={{color:"#8A6A12"}}>{FCFA(totalOuvert)}</b></>:"Aucune avance en attente de retenue."}</div>
                        </div>
                        <button type="button" className="btn btn-gold btn-sm" onClick={()=>vrOuvrir()}>+ Nouveau versement</button>
                      </div>
                      {vrEtat==="erreur"&&<div style={{fontSize:12.5,color:"#A3231C"}}>Impossible de charger les versements. <button className="btn btn-ghost btn-sm" onClick={vrCharger}>Réessayer</button></div>}
                      {vrEtat==="ok"&&!vrListe.length&&<div style={{fontSize:13,color:"#5B6B62"}}>Aucun versement enregistré pour le moment.</div>}
                      <div style={{display:"grid",gap:8}}>
                        {vrListe.slice(0,8).map(v=>(
                          <div key={v.id} style={{display:"flex",gap:10,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",padding:"9px 12px",border:"1px solid #E3E9E6",borderRadius:10}}>
                            <div style={{minWidth:0,flex:"1 1 230px"}}>
                              <div style={{fontWeight:700,fontSize:13.5,color:"#0F1F17",overflowWrap:"anywhere"}}>{v.type_versement} · {v.employe_nom}</div>
                              <div style={{fontSize:12,color:"#5B6B62",marginTop:2}}>{v.numero} · {vrDateCourte(v.date_versement)} · <b>{FCFA(v.montant)}</b>{v.recuperable&&v.statut==="signe"?<> · reste à retenir <b>{FCFA(v.solde_restant)}</b></>:null}</div>
                              <div style={{marginTop:4}}>{vrChip(v)}</div>
                            </div>
                            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                              {v.statut==="a_signer"&&<button type="button" className="btn btn-gold btn-sm" disabled={vrBusy} onClick={()=>vrSigner(v)}>Signer tout de suite</button>}
                              {v.statut==="a_signer"&&!v.envoye_le&&<button type="button" className="btn btn-ghost btn-sm btn-icon" disabled={vrBusy} onClick={()=>vrEnvoyer(v)}><Send size={13}/> Envoyer pour signature</button>}
                              {v.statut==="a_signer"&&v.envoye_le&&<button type="button" className="btn btn-ghost btn-sm" disabled={vrBusy} onClick={()=>vrRelancer(v)}>Relancer</button>}
                              <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={()=>vrOuvrirBon(v)}><Eye size={14}/> Bon</button>
                              {v.statut==="a_signer"&&<button type="button" className="btn btn-ghost btn-sm" title="Annuler ce versement" aria-label={"Annuler le versement "+v.numero} onClick={()=>vrAnnuler(v)}><Trash2 size={13}/></button>}
                            </div>
                          </div>
                        ))}
                      </div>
                      {vrListe.length>8&&<div style={{fontSize:11.5,color:"#5B6B62",marginTop:8}}>Les {vrListe.length-8} plus anciens sont dans Documents → Documents signés.</div>}
                    </div>
                  );
                })()}
                {!isDirecteur()&&vrMes.length>0&&(
                  <div className="card" style={{marginBottom:24,borderLeft:"4px solid #D4A84B"}}>
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Bons de versement à signer</div>
                    <div style={{display:"grid",gap:8}}>
                      {vrMes.map(m=>(
                        <div key={m.id} style={{display:"flex",gap:10,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",padding:"9px 12px",border:"1px solid #E3E9E6",borderRadius:10}}>
                          <div style={{minWidth:0,flex:"1 1 200px"}}>
                            <div style={{fontWeight:700,fontSize:13.5,color:"#0F1F17"}}>{m.type_versement}</div>
                            <div style={{fontSize:12,color:"#5B6B62",marginTop:2}}>{m.numero} · {vrDateCourte(m.date_versement)} · <b>{FCFA(m.montant)}</b></div>
                          </div>
                          <button type="button" className="btn btn-gold btn-sm" disabled={vrMesBusy} onClick={()=>{ setVrMesReporte(r=>({...r,[m.id]:false})); setVrMesReview(String(m.id)); }}>Ouvrir et signer</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="card scr">
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>{isDirecteur()?"Bulletins déjà générés":"Mes fiches de paie"}</div>
                  {bulletinsPaie.length===0?(
                    <div style={{color:"#5B6B62",fontSize:13,textAlign:"center",padding:20}}>{isDirecteur()?"Aucun bulletin généré pour le moment.":"Vous n'avez pas encore de fiche de paie enregistrée."}</div>
                  ):(
                    <table className="tbl">
                      <thead><tr><th>Employé</th><th>Mois</th><th>Taxes</th><th>Brut</th><th>Net</th><th>Signature</th><th>Actions</th></tr></thead>
                      <tbody>{bulletinsPaie.map(b=>{
                        const emp = employes.find(e=>e.id===b.employe_id);
                        const moisLbl = new Date(b.mois+"-01").toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                        return (
                          <tr key={b.id}>
                            <td style={{fontWeight:600}}>{b.employe_nom}</td>
                            <td style={{color:"#5B6B62"}}>{moisLbl.charAt(0).toUpperCase()+moisLbl.slice(1)}</td>
                            <td>
                              <span style={{
                                display:"inline-block",
                                padding:"3px 8px",
                                borderRadius:12,
                                fontSize:10,
                                color:b.calculer_taxes===false?"#1E8A32":"#1E7A2D",
                                background:b.calculer_taxes===false?"rgba(47,158,63,0.096)":"rgba(47,158,63,0.12)"
                              }}>
                                {b.calculer_taxes===false?"Non":"Oui"}
                              </span>
                            </td>
                            <td>{FCFA(b.brut)}</td>
                            <td style={{color:"#1E7A2D",fontWeight:700}}>{FCFA(b.net)}</td>
                            <td>{ficheChip(b)}</td>
                            <td>
                              <div style={{display:"flex",gap:5}}>
                                <button type="button" className="btn btn-ghost btn-sm"
                                  aria-label={"Ouvrir le bulletin de "+b.employe_nom}
                                  onClick={async()=>{
                                    try {
                                      const pdf = await generateBulletinPaiePdfBase64(b, emp, entreprise);
                                      await deliverPdfOnDevice({
                                        ...pdf,
                                        title:"Bulletin de paie — "+b.employe_nom+" — "+moisLbl,
                                        onOpenInApp:(url,filename,title)=>setPdfPreview({url,filename,title})
                                      });
                                    } catch(error) {
                                      console.error("Bulletin PDF :",error);
                                      showToast("Impossible d'ouvrir le bulletin : "+(error?.message||"erreur inconnue"),false);
                                    }
                                  }}><FileText size={13}/></button>
                                {b.signe_le&&<button type="button" className="btn btn-ghost btn-sm" title="Fiche signée" aria-label={"Fiche signée de "+b.employe_nom} onClick={()=>ficheOuvrirPdf(b,true)}><ShieldCheck size={13}/></button>}
                                {!isDirecteur()&&ficheEtat(b)==="a_signer"&&String(b.employe_id)===String(session?.id)&&<button type="button" className="btn btn-gold btn-sm" disabled={ficheBusy} onClick={()=>ficheSigner(b)}>Signer</button>}
                                {isDirecteur()&&ficheEtat(b)==="non_envoyee"&&<button type="button" className="btn btn-ghost btn-sm" title="Envoyer pour signature" aria-label={"Envoyer la fiche de "+b.employe_nom+" pour signature"} onClick={()=>ficheEnvoyer(b)}><Send size={13}/></button>}
                                {emp?.email&&<button className="btn btn-ghost btn-sm" onClick={()=>{
                                  setDocSendModal({kind:"bulletin", data:b, client:emp});
                                  setDocSendForm({to:emp.email, toName:emp.nom, subject:"Bulletin de paie — "+moisLbl+" — "+entreprise.entreprise_nom, message:"Veuillez trouver ci-joint votre bulletin de paie."});
                                }}><Mail size={13}/></button>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })()}

          {/* RAPPORTS */}
          {page==="rapports"&&(isDirecteur()||can("rapports"))&&(()=>{
            const hasPeriod=rapportDates.debut&&rapportDates.fin;
            const rangeStart=hasPeriod?new Date(rapportDates.debut+"T00:00:00"):new Date(new Date().getFullYear(),0,1);
            const rangeEnd=hasPeriod?new Date(rapportDates.fin+"T23:59:59"):new Date();
            const inRange=value=>{
              const d=parseDateSafe(value);
              return d&&d>=rangeStart&&d<=rangeEnd;
            };
            const bulletinInRange=b=>{
              const key=String(b?.mois||"").slice(0,7);
              if(!/^\d{4}-\d{2}$/.test(key))return false;
              const [yy,mm]=key.split("-").map(Number);
              const d=new Date(yy,mm-1,15);
              return d>=rangeStart&&d<=rangeEnd;
            };
            const selectedDepenses=depenses.filter(d=>inRange(d.date));
            // Voir le correctif du 02/10/2026 (estDepensePersonnelHorsFichePaie) : ces lignes
            // comptent comme des salaires versés, pas comme des dépenses diverses.
            const selectedDepensesHorsPersonnel=selectedDepenses.filter(d=>!estDepensePersonnelHorsFichePaie(d));
            const selectedDepensesPersonnel=selectedDepenses.filter(estDepensePersonnelHorsFichePaie);
            const selectedLots=lots.filter(l=>inRange(l.date));
            const selectedSalaires=bulletinsPaie.filter(bulletinInRange);
            const depTotal=selectedDepensesHorsPersonnel.reduce((s,d)=>s+toFiniteNumber(d.montant),0);
            const salTotal=selectedSalaires.reduce((s,b)=>s+toFiniteNumber(b.net,b.brut),0)
              + selectedDepensesPersonnel.reduce((s,d)=>s+toFiniteNumber(d.montant),0);
            const sortiesTotal=depTotal+salTotal;
            const bottlesTotal=selectedLots.reduce((s,l)=>s+toFiniteNumber(l.quantite),0);

            const monthKeys=[];
            const cursor=new Date(rangeStart.getFullYear(),rangeStart.getMonth(),1);
            const last=new Date(rangeEnd.getFullYear(),rangeEnd.getMonth(),1);
            while(cursor<=last){
              monthKeys.push(`${cursor.getFullYear()}-${String(cursor.getMonth()+1).padStart(2,"0")}`);
              cursor.setMonth(cursor.getMonth()+1);
            }
            const monthKey=value=>{
              const d=parseDateSafe(value);
              return d?`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`:"";
            };
            const monthly=monthKeys.map(key=>{
              const [yy,mm]=key.split("-").map(Number);
              const label=new Date(yy,mm-1,1).toLocaleDateString("fr-FR",{month:"short",year:"numeric"}).replace(".","");
              const dep=selectedDepensesHorsPersonnel.filter(d=>monthKey(d.date)===key).reduce((s,d)=>s+toFiniteNumber(d.montant),0);
              const sal=selectedSalaires.filter(b=>String(b.mois||"").slice(0,7)===key).reduce((s,b)=>s+toFiniteNumber(b.net,b.brut),0)
                + selectedDepensesPersonnel.filter(d=>monthKey(d.date)===key).reduce((s,d)=>s+toFiniteNumber(d.montant),0);
              const bottles=selectedLots.filter(l=>monthKey(l.date)===key).reduce((s,l)=>s+toFiniteNumber(l.quantite),0);
              return {key,label,dep,sal,total:dep+sal,bottles};
            });

            const categoryMap={};
            selectedDepensesHorsPersonnel.forEach(d=>{
              const cat=String(d.categorie||"Autres dépenses").trim()||"Autres dépenses";
              categoryMap[cat]=(categoryMap[cat]||0)+toFiniteNumber(d.montant);
            });
            if(salTotal>0)categoryMap["Salaires nets"]=salTotal;
            const categories=Object.entries(categoryMap).sort((a,b)=>b[1]-a[1]);
            const palette=["#1E8A32","#D6453D","#5B7A8A","#1E7A2D","#7A4FD6","#C7861A","#8A978F","#A24D80"];
            let acc=0;
            const gradient=categories.length&&sortiesTotal>0
              ? `conic-gradient(${categories.map(([label,value],index)=>{
                  const start=acc;
                  acc+=value/sortiesTotal*100;
                  return `${palette[index%palette.length]} ${start}% ${acc}%`;
                }).join(",")})`
              : "#8A978F";

            return (
              <div>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:6}}>Rapport opérationnel</div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:20}}>
                  Production, ventes, dépenses détaillées, avaries, statistiques par ville et performances par collaborateur.
                </div>

                <div className="card" style={{marginBottom:20}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Rapport mensuel (PDF)</div>
                  <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:14,padding:"10px 12px",border:"1px solid #E3E9E6",borderRadius:10,background:"rgba(13,39,66,.45)"}}>
                    <input
                      id="report-creditors-option"
                      type="checkbox"
                      checked={reportIncludeCreances}
                      onChange={e=>setReportIncludeCreances(e.target.checked)}
                      style={{width:17,height:17,accentColor:"#1E8A32"}}
                    />
                    <label htmlFor="report-creditors-option" style={{fontSize:12,color:"#2B3A32",cursor:"pointer"}}>
                      Ajouter exceptionnellement la liste des créanciers en annexe du rapport
                    </label>
                  </div>
                  <div style={{fontSize:11,color:"#5B6B62",marginBottom:12}}>
                    Par défaut, aucun nom de créancier n'apparaît. La liste reste disponible séparément ou via cette option facultative.
                  </div>
                  <div style={{display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
                    <div><label className="lbl">Mois</label><input type="month" className="inp" value={reportMonth} onChange={e=>setReportMonth(e.target.value)} /></div>
                    <button className="btn btn-gold btn-icon" disabled={reportGenerating} onClick={async()=>{
                      setReportGenerating(true);
                      try{
                        const [yy,mm]=reportMonth.split("-").map(Number);
                        const monthDate=new Date(yy,mm-1,1);
                        const pdf=await generateMonthlyReportPdfBase64(
                          monthDate,entreprise,depenses,lots,ventes,avaries,livraisons,
                          factures,paiements,reportIncludeCreances,immobilisations
                        );
                        await openPdf(pdf,"Rapport opérationnel");
                      }catch(e){showToast("Erreur : "+e.message,false);}
                      setReportGenerating(false);
                    }}><FileText size={14}/> {reportGenerating?"Génération...":"Télécharger le PDF"}</button>
                    <button className="btn btn-ghost btn-icon" disabled={reportGenerating} onClick={()=>{
                      const [yy,mm]=reportMonth.split("-").map(Number);
                      const monthDate=new Date(yy,mm-1,1);
                      setDocSendModal({kind:"rapport",data:{monthDate,includeCreances:reportIncludeCreances}});
                      setDocSendForm({
                        to:"",
                        toName:session.nom,
                        subject:"Rapport opérationnel — "+monthDate.toLocaleDateString("fr-FR",{month:"long",year:"numeric"})+" — "+entreprise.entreprise_nom,
                        message:"Veuillez trouver ci-joint le rapport mensuel des dépenses, salaires et bouteilles produites."
                      });
                    }}><Mail size={14}/> Envoyer par email</button>
                  </div>
                </div>

                <div className="card" style={{marginBottom:20}}>
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Période d'analyse</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
                    {[
                      ["Ce trimestre","trimestre"],["Trimestre précédent","trimestre_prec"],
                      ["Ce semestre","semestre"],["Semestre précédent","semestre_prec"],
                      ["Cette année","annee"],["Année précédente","annee_prec"],
                    ].map(([label,type])=>(
                      <button key={type} className="btn btn-ghost btn-sm" onClick={()=>{
                        const now=new Date(),yy=now.getFullYear(),m=now.getMonth();
                        const fmt=d=>localISODate(d);
                        let start,end;
                        if(type==="trimestre"){const q=Math.floor(m/3);start=new Date(yy,q*3,1);end=new Date(yy,q*3+3,0);}
                        else if(type==="trimestre_prec"){let q=Math.floor(m/3)-1,y2=yy;if(q<0){q=3;y2--;}start=new Date(y2,q*3,1);end=new Date(y2,q*3+3,0);}
                        else if(type==="semestre"){const s=m<6?0:6;start=new Date(yy,s,1);end=new Date(yy,s+6,0);}
                        else if(type==="semestre_prec"){if(m<6){start=new Date(yy-1,6,1);end=new Date(yy-1,12,0);}else{start=new Date(yy,0,1);end=new Date(yy,6,0);}}
                        else if(type==="annee"){start=new Date(yy,0,1);end=new Date(yy,11,31);}
                        else{start=new Date(yy-1,0,1);end=new Date(yy-1,11,31);}
                        setRapportDates({debut:fmt(start),fin:fmt(end)});
                      }}>{label}</button>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:16,alignItems:"flex-end",flexWrap:"wrap"}}>
                    <div style={{flex:1,minWidth:140}}><label className="lbl">Date début</label><input type="date" className="inp" value={rapportDates.debut} onChange={e=>setRapportDates(r=>({...r,debut:e.target.value}))}/></div>
                    <div style={{flex:1,minWidth:140}}><label className="lbl">Date fin</label><input type="date" className="inp" value={rapportDates.fin} onChange={e=>setRapportDates(r=>({...r,fin:e.target.value}))}/></div>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setRapportDates({debut:"",fin:""})}>Cette année</button>
                    <button className="btn btn-gold btn-sm btn-icon" disabled={!hasPeriod||reportGenerating} onClick={async()=>{
                      setReportGenerating(true);
                      try{
                        const pdf=await generateCustomPeriodReportPdfBase64(
                          rapportDates.debut,rapportDates.fin,entreprise,depenses,lots,ventes,
                          avaries,livraisons,factures,paiements,reportIncludeCreances,immobilisations
                        );
                        await openPdf(pdf,"Rapport opérationnel");
                      }catch(e){showToast("Erreur : "+e.message,false);}
                      setReportGenerating(false);
                    }}><FileText size={13}/> {reportGenerating?"...":"PDF de cette période"}</button>
                    <button className="btn btn-ghost btn-sm btn-icon" disabled={!hasPeriod} onClick={()=>{
                      setDocSendModal({kind:"rapport_periode",data:{debut:rapportDates.debut,fin:rapportDates.fin,includeCreances:reportIncludeCreances}});
                      setDocSendForm({
                        to:"",
                        toName:session.nom,
                        subject:"Rapport opérationnel "+fmtDate(rapportDates.debut)+" au "+fmtDate(rapportDates.fin)+" — "+entreprise.entreprise_nom,
                        message:"Veuillez trouver ci-joint le rapport des dépenses, salaires et bouteilles produites pour cette période."
                      });
                    }}><Mail size={13}/> Envoyer</button>
                  </div>
                </div>

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:12,marginBottom:18}}>
                  {[
                    ["Dépenses enregistrées",FCFA(depTotal),"#D6453D"],
                    ["Salaires nets",FCFA(salTotal),"#1E8A32"],
                    ["Sorties totales",FCFA(sortiesTotal),"#0F1F17"],
                    ["Bouteilles produites",NUM(bottlesTotal),"#1E7A2D"]
                  ].map(([label,value,color])=>(
                    <div key={label} className="card" style={{padding:14}}>
                      <div style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.1,color:"#5B6B62",marginBottom:7}}>{label}</div>
                      <div style={{fontSize:isMobile?14:18,fontWeight:800,color}}>{value}</div>
                    </div>
                  ))}
                </div>

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1.35fr .85fr",gap:18}}>
                  <div className="card scr">
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Détail mois par mois</div>
                    <table className="tbl">
                      <thead><tr><th>Mois</th><th>Dépenses</th><th>Salaires</th><th>Total</th><th>Bouteilles</th></tr></thead>
                      <tbody>{monthly.map(row=>(
                        <tr key={row.key}>
                          <td style={{textTransform:"capitalize"}}>{row.label}</td>
                          <td style={{color:"#D6453D"}}>{FCFA(row.dep)}</td>
                          <td style={{color:"#1E8A32"}}>{FCFA(row.sal)}</td>
                          <td style={{fontWeight:800,color:"#0F1F17"}}>{FCFA(row.total)}</td>
                          <td style={{color:"#1E7A2D",fontWeight:700}}>{NUM(row.bottles)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>

                  <div className="card">
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:16}}>Répartition des sorties</div>
                    <div style={{display:"flex",justifyContent:"center",marginBottom:18}}>
                      <div style={{
                        width:190,height:190,borderRadius:"50%",background:gradient,
                        display:"grid",placeItems:"center",boxShadow:"inset 0 0 0 1px rgba(15,31,23,0.08)"
                      }}>
                        <div style={{
                          width:112,height:112,borderRadius:"50%",background:"#FFFFFF",
                          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                          border:"1px solid #E3E9E6",textAlign:"center",padding:8
                        }}>
                          <div style={{fontSize:9,color:"#5B6B62",textTransform:"uppercase"}}>Total sorties</div>
                          <div style={{fontSize:13,color:"#0F1F17",fontWeight:800,marginTop:5}}>{FCFA(sortiesTotal)}</div>
                        </div>
                      </div>
                    </div>
                    {categories.length===0&&<div style={{fontSize:12,color:"#5B6B62",textAlign:"center"}}>Aucune dépense pour cette période.</div>}
                    {categories.map(([label,value],index)=>(
                      <div key={label} style={{display:"grid",gridTemplateColumns:"10px 1fr auto auto",gap:8,alignItems:"center",padding:"6px 0",borderBottom:"1px solid #EDF1EF",fontSize:11}}>
                        <span style={{width:9,height:9,borderRadius:"50%",background:palette[index%palette.length]}}/>
                        <span style={{color:"#2B3A32",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</span>
                        <span style={{color:"#5B6B62"}}>{sortiesTotal?Math.round(value/sortiesTotal*100):0}%</span>
                        <span style={{color:"#0F1F17",fontWeight:700}}>{FCFA(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* PARAMETRES */}
          {page==="parametres"&&(isDirecteur()||can("parametres"))&&(
            <div>
              <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:20}}>Paramètres entreprise</div>
              <div className="card" style={{maxWidth:640,marginBottom:16}}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>Modèle de facture</div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:12}}>S'applique à l'aperçu et au PDF de toutes les factures. Un clic suffit pour changer.</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10}}>
                  {[
                    {id:"moderne",nom:"Moderne",desc:"Aéré, touches de vert"},
                    {id:"classique",nom:"Classique",desc:"Traditionnel, tableau à en-tête foncé"},
                    {id:"sobre",nom:"Sobre",desc:"Épuré, noir et blanc"}
                  ].map(m=>{
                    const on=(entreprise.facture_modele||"moderne")===m.id;
                    return (
                      <button key={m.id} type="button" disabled={loading} onClick={async()=>{
                        if(on) return;
                        setLoading(true);
                        const {data:ex}=await supabase.from("parametres").select("id").eq("cle","facture_modele").maybeSingle();
                        const {error}=ex
                          ? await supabase.from("parametres").update({valeur:m.id}).eq("cle","facture_modele")
                          : await supabase.from("parametres").insert({cle:"facture_modele",valeur:m.id});
                        setLoading(false);
                        if(error){ showToast("Erreur : "+error.message,false); return; }
                        setEntreprise(prev=>({...prev,facture_modele:m.id}));
                        showToast("Modèle « "+m.nom+" » enregistré ✓");
                      }} style={{textAlign:"left",cursor:"pointer",fontFamily:"inherit",padding:10,borderRadius:14,background:"#fff",border:on?"2px solid #2F9E3F":"1px solid #D5DDD9",boxShadow:on?"0 0 0 3px rgba(47,158,63,.14)":"none"}}>
                        <div style={{height:84,borderRadius:8,border:"1px solid #E3E9E6",background:"#fff",padding:8,marginBottom:8,overflow:"hidden",borderTop:m.id==="moderne"?"5px solid #2F9E3F":"1px solid #E3E9E6"}}>
                          <div style={{height:6,width:"40%",background:m.id==="moderne"?"#1E7A2D":"#0F1F17",borderRadius:3,marginBottom:6}}/>
                          {m.id==="sobre"
                            ? <div style={{height:14,borderTop:"1px solid #0F1F17",borderBottom:"1px solid #0F1F17",marginBottom:6}}/>
                            : <div style={{height:14,background:m.id==="classique"?"#F3F4F3":"#EAF5EC",borderLeft:m.id==="classique"?"4px solid #2F6FDE":"none",borderRadius:m.id==="moderne"?5:0,marginBottom:6}}/>}
                          <div style={{height:7,background:m.id==="classique"?"#0F1F17":m.id==="moderne"?"#EAF5EC":"#fff",borderBottom:m.id==="sobre"?"1px solid #0F1F17":"none",marginBottom:4}}/>
                          <div style={{height:3,background:"#E3E9E6",marginBottom:3}}/>
                          <div style={{height:3,background:"#E3E9E6",width:"70%"}}/>
                        </div>
                        <div style={{fontSize:14,fontWeight:700,color:"#0F1F17"}}>{m.nom}{on?" ✓":""}</div>
                        <div style={{fontSize:11.5,color:"#5B6B62",marginTop:2,lineHeight:1.4}}>{m.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              
<div className="card" style={{maxWidth:640}}>
                {[
                  {k:"entreprise_nom",label:"Nom de l'entreprise"},
                  {k:"entreprise_adresse",label:"Adresse"},
                  {k:"entreprise_ville",label:"Ville / Région"},
                  {k:"entreprise_tel",label:"Téléphone"},
                  {k:"entreprise_email",label:"Email"},
                  {k:"entreprise_site",label:"Site web"},
                  {k:"entreprise_niu",label:"NIU"},
                  {k:"entreprise_rc",label:"Numéro RC"},
                  {k:"entreprise_rccm",label:"RCCM"},
                  {k:"entreprise_contribuable",label:"N° Contribuable"},
                  {k:"paiement_momo_numero",label:"MTN MoMo : numéro"},
                  {k:"paiement_momo_nom",label:"MTN MoMo : nom du titulaire"},
                  {k:"paiement_banque",label:"Banque"},
                  {k:"paiement_compte",label:"N° de compte / RIB"},
                  {k:"paiement_titulaire",label:"Titulaire du compte"},
                  {k:"tva_taux",label:"Taux TVA (%)"},
                ].map(({k,label})=>(
                  <div key={k} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:"1px solid #EDF1EF"}}>
                    <div style={{width:180,fontSize:11,textTransform:"uppercase",letterSpacing:1,color:"#5B6B62",flexShrink:0}}>{label}</div>
                    {form[k]!==undefined?(
                      <div style={{display:"flex",flex:1,gap:8}}>
                        <input type="text" className="inp" style={{flex:1}} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} autoFocus />
                        <button className="btn btn-gold btn-sm" disabled={loading} onClick={async()=>{
                          setLoading(true);
                          const val=form[k];
                          const {data:ex}=await supabase.from("parametres").select("id").eq("cle",k).single();
                          const { error } = ex
                            ? await supabase.from("parametres").update({valeur:val}).eq("cle",k)
                            : await supabase.from("parametres").insert({cle:k,valeur:val});
                          if (error) { showToast("Erreur : "+error.message, false); setLoading(false); return; }
                          setEntreprise(prev=>({...prev,[k]:val}));
                          await load(); setLoading(false);
                          setForm(f=>{const n={...f};delete n[k];return n;});
                          showToast(label+" mis à jour ✓");
                        }}>{loading?"...":"✓"}</button>
                        <button className="btn btn-ghost btn-sm" onClick={()=>setForm(f=>{const n={...f};delete n[k];return n;})}>✕</button>
                      </div>
                    ):(
                      <div style={{display:"flex",flex:1,alignItems:"center",justifyContent:"space-between"}}>
                        <span style={{fontSize:13,color:"#0F1F17"}}>{entreprise[k]||<span style={{color:"#5B6B62",fontStyle:"italic"}}>Non défini</span>}</span>
                        <button className="btn btn-ghost btn-sm" onClick={()=>setForm(f=>({...f,[k]:entreprise[k]||""}))}>✏ Modifier</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Villes disponibles (02/10/2026) — gérée ici par la direction, au lieu d'une
                  liste figée dans le code, pour les fiches client/fournisseur/immobilisation. */}
              <div className="card" style={{maxWidth:640,marginTop:16}}>
                <div style={{fontSize:14,fontWeight:700,color:"#1E8A32",marginBottom:4}}>Villes disponibles</div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:14}}>
                  Utilisées sur les fiches client, fournisseur et immobilisation. (La ville d'une commande d'achat et la « Ville d'affectation » d'un employé viennent des dépôts existants, pas de cette liste — pour celles-là, ajoutez la ville ici si besoin, puis créez un dépôt dans cette ville ci-dessous.)
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
                  {villesDisponibles.map(v=>(
                    <span key={v} className="badge bg2" style={{display:"inline-flex",alignItems:"center",gap:6}}>
                      {v}
                      <button type="button" title={"Retirer "+v} onClick={()=>retirerVille(v)} style={{background:"none",border:"none",color:"#D6453D",cursor:"pointer",padding:0,fontSize:13,lineHeight:1}}>✕</button>
                    </span>
                  ))}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <input type="text" className="inp" style={{flex:1}} placeholder="Nouvelle ville..." value={form.nouvelleVille||""} onChange={e=>setForm(f=>({...f,nouvelleVille:e.target.value}))} onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); ajouterVille(form.nouvelleVille); setForm(f=>({...f,nouvelleVille:""})); } }} />
                  <button className="btn btn-gold btn-sm" onClick={()=>{ ajouterVille(form.nouvelleVille); setForm(f=>({...f,nouvelleVille:""})); }}>+ Ajouter</button>
                </div>
              </div>

              {/* Pays disponibles (02/10/2026) — même principe que les villes disponibles. */}
              <div className="card" style={{maxWidth:640,marginTop:16}}>
                <div style={{fontSize:14,fontWeight:700,color:"#1E8A32",marginBottom:4}}>Pays disponibles</div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:14}}>
                  Utilisés sur les fiches client, fournisseur et immobilisation. « Cameroun » reste toujours disponible par défaut. Les 54 pays d'Afrique sont déjà proposés ci-dessous ; retire ceux dont tu n'as pas besoin, ou ajoute-en d'autres (hors Afrique par exemple).
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
                  {paysDisponibles.map(p=>(
                    <span key={p} className="badge bg2" style={{display:"inline-flex",alignItems:"center",gap:6}}>
                      {p}
                      {p!=="Cameroun"&&<button type="button" title={"Retirer "+p} onClick={()=>retirerPays(p)} style={{background:"none",border:"none",color:"#D6453D",cursor:"pointer",padding:0,fontSize:13,lineHeight:1}}>✕</button>}
                    </span>
                  ))}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <input type="text" className="inp" style={{flex:1}} placeholder="Nouveau pays..." value={form.nouveauPays||""} onChange={e=>setForm(f=>({...f,nouveauPays:e.target.value}))} onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); ajouterPays(form.nouveauPays); setForm(f=>({...f,nouveauPays:""})); } }} />
                  <button className="btn btn-gold btn-sm" onClick={()=>{ ajouterPays(form.nouveauPays); setForm(f=>({...f,nouveauPays:""})); }}>+ Ajouter</button>
                </div>
              </div>

              {/* Villes par pays (05/10/2026) — une fois un pays choisi sur une fiche, seules les
                  villes de ce pays sont proposées. Capitale + grandes villes déjà pré-remplies
                  pour les 54 pays d'Afrique (VILLES_PAR_PAYS_BASE) ; modifiable ici pays par pays.
                  Le Cameroun n'apparaît pas dans ce sélecteur : il a sa propre carte ci-dessus. */}
              <div className="card" style={{maxWidth:640,marginTop:16}}>
                <div style={{fontSize:14,fontWeight:700,color:"#1E8A32",marginBottom:4}}>Villes par pays</div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:14}}>
                  Choisis un pays (hors Cameroun) pour voir et modifier ses villes proposées sur les fiches client, fournisseur et immobilisation.
                </div>
                <div className="fld" style={{marginBottom:12}}>
                  <select className="inp" value={form.paysPourVilles||""} onChange={e=>setForm(f=>({...f,paysPourVilles:e.target.value,nouvelleVillePourPays:""}))}>
                    <option value="">Choisir un pays...</option>
                    {paysDisponibles.filter(p=>p!=="Cameroun").map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                {form.paysPourVilles&&(<>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
                    {villesPourPays(form.paysPourVilles).length===0?(
                      <div style={{fontSize:12,color:"#5B6B62",fontStyle:"italic"}}>Aucune ville pour l'instant.</div>
                    ):villesPourPays(form.paysPourVilles).map(v=>(
                      <span key={v} className="badge bg2" style={{display:"inline-flex",alignItems:"center",gap:6}}>
                        {v}
                        <button type="button" title={"Retirer "+v} onClick={()=>retirerVillePourPays(form.paysPourVilles,v)} style={{background:"none",border:"none",color:"#D6453D",cursor:"pointer",padding:0,fontSize:13,lineHeight:1}}>✕</button>
                      </span>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <input type="text" className="inp" style={{flex:1}} placeholder={"Nouvelle ville pour "+form.paysPourVilles+"..."} value={form.nouvelleVillePourPays||""} onChange={e=>setForm(f=>({...f,nouvelleVillePourPays:e.target.value}))} onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); ajouterVillePourPays(form.paysPourVilles,form.nouvelleVillePourPays); setForm(f=>({...f,nouvelleVillePourPays:""})); } }} />
                    <button className="btn btn-gold btn-sm" onClick={()=>{ ajouterVillePourPays(form.paysPourVilles,form.nouvelleVillePourPays); setForm(f=>({...f,nouvelleVillePourPays:""})); }}>+ Ajouter</button>
                  </div>
                </>)}
              </div>

              {/* Dépôts (02/10/2026) — gérer soi-même les dépôts, dont dépendent la ville d'une
                  commande d'achat et la "Ville d'affectation" d'un employé. "Magasin central"
                  n'apparaît pas ici : c'est le site de production, pas un dépôt de ville. */}
              <div className="card" style={{maxWidth:640,marginTop:16}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <div style={{fontSize:14,fontWeight:700,color:"#1E8A32"}}>Dépôts</div>
                  <button className="btn btn-gold btn-sm" onClick={()=>openModal("depot")}>+ Nouveau dépôt</button>
                </div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:14}}>
                  La ville d'un dépôt se choisit dans la liste « Villes disponibles » ci-dessus (ajoutez-la d'abord si elle n'y est pas). Créer le dépôt la rend aussitôt disponible pour une commande d'achat et pour la « Ville d'affectation » d'un employé.
                </div>
                {depots.filter(d=>d.ville!=="Magasin central").length===0?(
                  <div style={{fontSize:12,color:"#5B6B62",fontStyle:"italic"}}>Aucun dépôt pour l'instant.</div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {depots.filter(d=>d.ville!=="Magasin central").map(d=>(
                      <div key={d.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:4,padding:"8px 10px"}}>
                        <div>
                          <b style={{color:"#0F1F17"}}>{d.nom}</b> <span style={{color:"#5B6B62"}}>— {d.ville}</span>
                          {d.responsable&&<span style={{color:"#2F6FDE"}}> · {d.responsable}</span>}
                        </div>
                        <div style={{display:"flex",gap:6}}>
                          <button className="btn btn-ghost btn-sm" onClick={()=>openModal("depot",d)}>✏</button>
                          <button className="btn btn-red btn-sm" onClick={()=>deleteDepot(d.id)}><Trash2 size={13}/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GESTION PRODUITS */}
          {/* OBJECTIFS */}
          {page==="objectifs"&&(isDirecteur()||can("objectifs"))&&(
            <div>
              <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:6}}>Objectifs commerciaux</div>
              <div style={{fontSize:12,color:"#5B6B62",marginBottom:20}}>{new Date().toLocaleDateString("fr-FR",{month:"long",year:"numeric"})} — suivi de performance simple, sans impact sur le stock.</div>
              {isDirecteur()&&<div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
                <button className={"btn "+(objVue==="mois"?"btn-gold":"btn-ghost")} onClick={()=>setObjVue("mois")}>Ce mois-ci</button>
                <button className={"btn "+(objVue==="journal"?"btn-gold":"btn-ghost")} onClick={()=>setObjVue("journal")}>Journal des mois</button>
              </div>}
              {isDirecteur()&&objVue==="journal"&&(()=>{
                const cibles = objCibles();
                const moisNow = today().slice(0,7);
                const nomMois = (m) => { const [y,mm]=m.split("-").map(Number); const t=new Date(y,mm-1,1).toLocaleDateString("fr-FR",{month:"long",year:"numeric"}); return t.charAt(0).toUpperCase()+t.slice(1); };
                const cart = (b) => NUM(Math.round(toFiniteNumber(b)/24*10)/10);
                const ligneMois = (e,m) => {
                  const fige = objHist[e.id+"|"+m];
                  const obj = fige || objectifs[e.id] || {};
                  const r = remunMois(e,obj,m);
                  const objBtl = toFiniteNumber(obj.bouteilles);
                  const pct = objBtl>0 ? Math.round(r.btl/objBtl*100) : null;
                  return { m, obj, r, objBtl, pct, estime: !fige && m<moisNow, enCours: m===moisNow, sans: !!obj.sans_objectif };
                };
                const couleurPct = (p) => p==null?"#5B6B62":p>=100?"#1E7A2D":p>=60?"#1E8A32":p>=30?"#C7861A":"#D6453D";
                const choisi = cibles.find(e=>String(e.id)===String(objJournalEmp));
                const tousMois = [...new Set(cibles.flatMap(e=>objMoisListe(e)))].sort().reverse();
                return (
                  <div>
                    <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
                      <select className="inp" style={{maxWidth:320}} value={objJournalEmp} onChange={e=>setObjJournalEmp(e.target.value)}>
                        <option value="">Vue d'ensemble (tous)</option>
                        {cibles.map(e=><option key={e.id} value={e.id}>{e.nom}</option>)}
                      </select>
                      <span style={{fontSize:11.5,color:"#5B6B62"}}>L'objectif de chaque mois passé est figé : le modifier n'a plus d'effet sur l'historique.</span>
                    </div>
                    {!choisi&&<div className="card" style={{padding:12}}>
                      <div style={{overflowX:"auto"}}><table className="tbl">
                        <thead><tr><th>Mois</th>{cibles.map(e=><th key={e.id}>{e.nom}</th>)}</tr></thead>
                        <tbody>{tousMois.map(m=><tr key={m}>
                          <td style={{whiteSpace:"nowrap",fontWeight:600}}>{nomMois(m)}{m===moisNow?" (en cours)":""}</td>
                          {cibles.map(e=>{ if(!objMoisListe(e).includes(m)) return <td key={e.id} style={{color:"#9AA7A0"}}>—</td>; const l=ligneMois(e,m); return <td key={e.id} style={{whiteSpace:"nowrap"}}>{l.sans?<span style={{color:"#5B6B62"}}>fixe</span>:l.pct==null?<span style={{color:"#9AA7A0"}}>sans objectif</span>:<span style={{fontWeight:700,color:couleurPct(l.pct)}}>{l.pct} %</span>}{!l.sans&&<span style={{fontSize:10.5,color:"#5B6B62"}}> · {cart(l.r.btl)} c.</span>}</td>; })}
                        </tr>)}</tbody>
                      </table></div>
                      {!tousMois.length&&<div style={{color:"#5B6B62",fontSize:13}}>Aucune vente enregistrée pour le moment.</div>}
                      <div style={{fontSize:11,color:"#5B6B62",marginTop:10}}>Pourcentage de l'objectif en cartons (c. = cartons facturés dans le mois). Choisissez une personne pour voir le détail mois par mois.</div>
                    </div>}
                    {choisi&&<div className="card" style={{padding:12}}>
                      <div style={{fontSize:15,fontWeight:700,color:"#0F1F17",marginBottom:2}}>{choisi.nom}</div>
                      <div style={{fontSize:11.5,color:"#5B6B62",marginBottom:12}}>{choisi.poste}</div>
                      <div style={{overflowX:"auto"}}><table className="tbl">
                        <thead><tr><th>Mois</th><th>Objectif (cartons)</th><th>Facturé (cartons)</th><th>Atteint</th><th>Encaissé</th><th>Commission</th></tr></thead>
                        <tbody>{[...objMoisListe(choisi)].reverse().map(m=>{ const l=ligneMois(choisi,m); return <tr key={m}>
                          <td style={{whiteSpace:"nowrap",fontWeight:600}}>{nomMois(m)}{l.enCours?<span style={{fontSize:10.5,color:"#2F6FDE"}}> (en cours)</span>:null}</td>
                          <td>{l.sans?"Salaire fixe":l.objBtl>0?cart(l.objBtl):"—"}{l.estime&&!l.sans?<span title="Mois passé avant l'enregistrement de l'historique : objectif actuel utilisé" style={{color:"#A8680A"}}> ~</span>:null}</td>
                          <td>{cart(l.r.btl)}</td>
                          <td>{l.sans||l.pct==null?"—":<span style={{fontWeight:700,color:couleurPct(l.pct)}}>{l.pct} %{l.pct>=100?" ✓":""}</span>}</td>
                          <td>{FCFA(l.r.encaisse)}</td>
                          <td>{l.sans?"—":<span>{FCFA(l.r.comm)} <span style={{fontSize:10.5,color:"#5B6B62"}}>({l.r.pct} %)</span></span>}</td>
                        </tr>; })}</tbody>
                      </table></div>
                      {!objMoisListe(choisi).length&&<div style={{color:"#5B6B62",fontSize:13}}>Aucune vente enregistrée pour cette personne.</div>}
                      <div style={{fontSize:11,color:"#5B6B62",marginTop:10}}>« Encaissé » = paiements validés versés dans le mois sur ses ventes, même pour une facture d'un mois précédent. Le signe ~ indique un mois antérieur à ce journal : l'objectif affiché est l'objectif actuel.</div>
                    </div>}
                  </div>
                );
              })()}

              {!isDirecteur()&&(()=>{
                const mois = today().slice(0,7);
                const mesDecl = declarationsVentes.filter(d=>d.employe_id===String(session.id)&&d.date&&d.date.slice(0,7)===mois).sort((a,b)=>new Date(b.date)-new Date(a.date));
                const total = mesDecl.reduce((s,d)=>s+d.quantite,0);
                return (
                  <div className="card" style={{marginBottom:20,borderColor:"rgba(47,158,63,0.360)"}}>
                    <div style={{fontSize:14,color:"#1E8A32",fontWeight:700,marginBottom:4}}>Ma saisie du jour</div>
                    <div style={{fontSize:12,color:"#5B6B62",marginBottom:12}}>Ce mois-ci : <strong style={{color:"#0F1F17"}}>{NUM(total)} bouteilles vendues</strong> ({mesDecl.length} jour{mesDecl.length>1?"s":""} saisi{mesDecl.length>1?"s":""})</div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:mesDecl.length?14:0}}>
                      <input type="number" min="1" className="inp" style={{maxWidth:140}} placeholder="Nb bouteilles" value={declForm.quantite} onChange={e=>setDeclForm(f=>({...f,quantite:e.target.value}))} />
                      <input type="text" className="inp" style={{flex:1,minWidth:140}} placeholder="Note (optionnel)" value={declForm.note} onChange={e=>setDeclForm(f=>({...f,note:e.target.value}))} />
                      <button className="btn btn-gold" disabled={declSaving} onClick={saveDeclaration}>{declSaving?"...":"Ajouter"}</button>
                    </div>
                    {mesDecl.length>0&&(
                      <div style={{display:"flex",flexDirection:"column",gap:5,maxHeight:200,overflowY:"auto"}}>
                        {mesDecl.map(d=>(
                          <div key={d.id} style={{display:"flex",justifyContent:"space-between",fontSize:12,background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:4,padding:"6px 10px"}}>
                            <span style={{color:"#5B6B62"}}>{fmtDate(d.date)}{d.note?" — "+d.note:""}</span>
                            <strong style={{color:"#1E8A32"}}>{NUM(d.quantite)} btl</strong>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{display:(isDirecteur()&&objVue==="journal")?"none":"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(340px,1fr))",gap:16}}>
                {employes.filter(e=>{const p=safeJsonParse(e.permissions,[]);return e.actif&&!estRoleDirecteur(e.role,e.poste)&&Array.isArray(p)&&p.includes("ventes");}).map(e=>{
                  const mois = today().slice(0,7);
                  const declEmp = declarationsVentes.filter(d=>d.employe_id===String(e.id) && d.date&&d.date.slice(0,7)===mois);
                  const declBtl = declEmp.reduce((s,d)=>s+toFiniteNumber(d.quantite),0);
                  const realBtl = ventes.filter(v=>v.employe===e.nom&&v.date&&String(v.date).slice(0,7)===mois).reduce((s,v)=>s+toFiniteNumber(v.qte),0);
                  const obj = objectifs[e.id]||{bouteilles:0,montant:0};
                  if (obj.sans_objectif) {
                    if (!isDirecteur()) return null;
                    return (
                      <div key={e.id} className="card">
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:15,fontWeight:700,color:"#0F1F17"}}>{e.nom}</div>
                          <div style={{fontSize:11,color:"#5B6B62"}}>{e.poste}</div>
                        </div>
                        <div style={{background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:6,padding:"10px 12px",fontSize:12,lineHeight:1.7}}>
                          <div style={{color:"#5B6B62"}}>Pas d'objectif : salaire mensuel fixe</div>
                          <div style={{fontSize:18,fontWeight:700,color:"#0F1F17"}}>{FCFA(toFiniteNumber(e.salaire_base))}</div>
                        </div>
                      </div>
                    );
                  }
                  const pctBtl = obj.bouteilles>0?Math.min(100,Math.round((realBtl/obj.bouteilles)*100)):0;
                  const couleur = pctBtl>=100?"#1E7A2D":pctBtl>=60?"#1E8A32":pctBtl>=30?"#C7861A":"#D6453D";
                  const msg = getEncouragementMsg(pctBtl, e.nom.split(" ")[0]);
                  return (
                    <div key={e.id} className="card" style={{borderColor:couleur+"44"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                        <div>
                          <div style={{fontSize:15,fontWeight:700,color:"#0F1F17"}}>{e.nom}</div>
                          <div style={{fontSize:11,color:"#5B6B62"}}>{e.poste}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:22,fontWeight:700,color:couleur}}>{pctBtl}%</div>
                          <div style={{fontSize:10,color:"#5B6B62"}}>de l'objectif</div>
                        </div>
                      </div>
                      <div style={{marginBottom:14}}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:5}}>
                          <span style={{color:"#5B6B62"}}>Bouteilles facturées{declBtl>0?" (+"+NUM(declBtl)+" déclarées, non comptées)":""}</span>
                          <span style={{color:"#1E8A32",fontWeight:600}}>{NUM(realBtl)} / {NUM(obj.bouteilles)} btl ({NUM(Math.round(obj.bouteilles/24*10)/10)} cartons)</span>
                        </div>
                        <div style={{height:8,background:"#E3E9E6",borderRadius:4,overflow:"hidden"}}>
                          <div style={{height:"100%",width:pctBtl+"%",background:couleur,borderRadius:4}} />
                        </div>
                      </div>
                      <div style={{background:"rgba(47,158,63,0.064)",border:"1px solid rgba(47,158,63,0.120)",borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#0F1F17",lineHeight:1.6}}>{msg}</div>
                      {isDirecteur()&&(()=>{
                        const ids = new Set(ventes.filter(v=>v.employe===e.nom&&v.facture_id).map(v=>String(v.facture_id)));
                        const fixe = toFiniteNumber(e.salaire_base), pct = toFiniteNumber(obj.commission_pct);
                        const encaisse = paiements.filter(p=>isPaiementValide(p)
                          && String(p.date_versement||p.date_validation||"").slice(0,7)===mois
                          && (p.facture_id ? ids.has(String(p.facture_id)) : factures.some(f=>ids.has(String(f.id))&&f.numero===p.facture_numero))
                        ).reduce((s,p)=>s+toFiniteNumber(p.montant),0);
                        const comm = Math.round(encaisse*pct/100);
                        const btlFact = ventes.filter(v=>v.employe===e.nom&&v.date&&String(v.date).slice(0,7)===mois).reduce((s,v)=>s+toFiniteNumber(v.qte),0);
                        const ligne = (a,b,gras) => (<div style={{display:"flex",justifyContent:"space-between",gap:8,fontWeight:gras?700:400,color:gras?"#0F1F17":"#5B6B62"}}><span>{a}</span><span style={{color:"#0F1F17"}}>{b}</span></div>);
                        return (
                          <div style={{background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:12,lineHeight:1.8}}>
                            <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.2,color:"#5B6B62",marginBottom:4}}>Rémunération du mois (estimation)</div>
                            {ligne("Cartons facturés ce mois", NUM(Math.round(btlFact/24*10)/10)+" / "+NUM(Math.round(obj.bouteilles/24*10)/10))}
                            {ligne("Salaire fixe", FCFA(fixe))}
                            {ligne("Argent encaissé ce mois", FCFA(encaisse))}
                            {ligne("Commission ("+pct+" %)", FCFA(comm))}
                            {ligne("Total à payer", FCFA(fixe+comm), true)}
                          </div>
                        );
                      })()}
                      <div style={{display:"flex",gap:8}}>
                        {isDirecteur()&&<button className="btn btn-ghost btn-sm btn-icon" style={{flex:1}} onClick={()=>{setEditObjectif(e);setObjForm({...obj,bouteilles:obj.bouteilles||0,montant:obj.montant||0,commission_pct:obj.commission_pct||0});}}><Target size={13}/> Définir objectif</button>}
                        {e.tel&&<button className="btn btn-gold btn-sm btn-icon" onClick={()=>{window.open("https://wa.me/"+e.tel.replace(/[^0-9]/g,"")+"?text="+encodeURIComponent(msg),"_blank");}}><MessageCircle size={13}/> WhatsApp</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* DOCUMENTS */}
          {page==="documents"&&(isDirecteur()||can("documents"))&&(
            <div>
              <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:20}}>Documents</div>
              {isDirecteur()&&(
                <div style={{display:"flex",gap:8,marginBottom:16}}>
                  {[["rediger","Rédiger"],["signes","Documents signés"]].map(([k,l])=>(
                    <button key={k} className={"btn "+(docOnglet===k?"btn-gold":"btn-ghost")} onClick={()=>setDocOnglet(k)}>{l}</button>
                  ))}
                </div>
              )}
              {docOnglet==="signes"&&isDirecteur()?(()=>{
                const q = sigRech.trim().toLowerCase();
                const types = Array.from(new Set(sigListe.map(r=>r.type_document).filter(Boolean)));
                const vus = sigListe.filter(r=>(!sigTypeFiltre||r.type_document===sigTypeFiltre)&&(!q||[r.type_document,r.destinataire,r.objet,r.nom_signe,r.fonction_signee].join(" ").toLowerCase().includes(q)));
                return (
                  <div>
                    <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:12}}>
                      <input className="inp" style={{flex:"1 1 220px"}} placeholder="Rechercher (client, objet, signataire…)" value={sigRech} onChange={e=>setSigRech(e.target.value)} />
                      <select className="inp" style={{flex:"0 1 220px"}} value={sigTypeFiltre} onChange={e=>setSigTypeFiltre(e.target.value)}>
                        <option value="">Tous les types</option>
                        {types.map(t=><option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {sigArchEnCours>0&&<div style={{marginBottom:10,fontSize:12.5,color:"#7A4B00",background:"#FFF4DD",borderRadius:10,padding:"8px 12px"}}>Rangement des PDF en cours…</div>}
                    {sigListeEtat==="chargement"&&<div className="card" style={{color:"#5B6B62",fontSize:13}}>Chargement…</div>}
                    {sigListeEtat==="erreur"&&<div className="card" style={{color:"#A3231C",fontSize:13}}>Impossible de charger les documents signés. <button className="btn btn-ghost btn-sm" onClick={sigCharger}>Réessayer</button></div>}
                    {sigListeEtat==="ok"&&!vus.length&&<div className="card" style={{color:"#5B6B62",fontSize:13}}>{sigListe.length?"Aucun document ne correspond à la recherche.":"Aucun document signé pour le moment. Dans « Rédiger », utilisez « Signer par téléphone (QR code) »."}</div>}
                    <div style={{display:"grid",gap:10}}>
                      {vus.map(r=>(
                        <div className="card" key={r.id} style={{display:"flex",gap:12,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap"}}>
                          <div style={{minWidth:0,flex:"1 1 260px"}}>
                            <div style={{fontWeight:700,fontSize:14.5,color:"#0F1F17",overflowWrap:"anywhere"}}>{r.type_document}{r.destinataire?" · "+r.destinataire:""}</div>
                            {r.objet&&<div style={{fontSize:12.5,color:"#2B3A32",marginTop:2,overflowWrap:"anywhere"}}>{r.objet}</div>}
                            <div style={{fontSize:12,color:"#5B6B62",marginTop:3}}>Signé par <b>{r.nom_signe}</b>{r.fonction_signee?" ("+r.fonction_signee+")":""} · {sigHeure(r.signe_le)}</div>
                            <div style={{fontSize:11.5,marginTop:4,color:r.pdf_path?"#1E7A2D":"#A8680A"}}>{r.pdf_path?"✓ PDF archivé":"PDF pas encore rangé"}{!r.pdf_path&&<> · <button type="button" onClick={()=>sigArchiverMaintenant(r)} style={{background:"none",border:"none",color:"#1E7A2D",fontWeight:700,cursor:"pointer",padding:0,fontFamily:"inherit",fontSize:"inherit"}}>Ranger maintenant</button></>}</div>
                          </div>
                          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                            <button className="btn btn-gold btn-sm btn-icon" onClick={()=>sigOuvrirArchive(r)}><Eye size={14}/> Ouvrir</button>
                            <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>sigEnvoyerArchive(r)}><Mail size={14}/> Envoyer</button>
                            <a className="btn btn-ghost btn-sm" style={{textDecoration:"none"}} href={sigVerifUrl(r.token)} target="_blank" rel="noopener noreferrer">Vérifier ↗</a>
                          </div>
                        </div>
                      ))}
                    </div>
                    {(()=>{
                      const q = sigRech.trim().toLowerCase();
                      const bb = achatsCommandes.filter(c=>c.signe_le && (!q||[c.numero,c.fournisseur_nom,c.ville].join(" ").toLowerCase().includes(q))).sort((a,b)=>String(b.signe_le).localeCompare(String(a.signe_le)));
                      if (!bb.length) return null;
                      return (
                        <div style={{marginTop:20}}>
                          <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Bons de commande fournisseurs signés</div>
                          <div style={{display:"grid",gap:10}}>
                            {bb.map(c=>(
                              <div className="card" key={c.id} style={{display:"flex",gap:12,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap"}}>
                                <div style={{minWidth:0,flex:"1 1 260px"}}>
                                  <div style={{fontWeight:700,fontSize:14.5,color:"#0F1F17",overflowWrap:"anywhere"}}>{c.numero} · {c.fournisseur_nom}</div>
                                  <div style={{fontSize:12.5,color:"#2B3A32",marginTop:2}}>{c.ville} · {bcDate(c.date_commande)} · <b>{FCFA(c.total)}</b></div>
                                  <div style={{marginTop:4}}>{bcChip(c)}</div>
                                </div>
                                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                                  <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={()=>bcVoir(c)}><Eye size={14}/> Ouvrir</button>
                                  <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={()=>bcEnvoyerMail(c)}><Mail size={13}/> E-mail</button>
                                  <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={()=>bcPartager(c)}><Send size={13}/> WhatsApp</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    {(()=>{
                      const q = sigRech.trim().toLowerCase();
                      const vv = vrListe.filter(v=>(!q||[v.type_versement,v.employe_nom,v.numero,v.motif].join(" ").toLowerCase().includes(q)));
                      if (!vrListe.length && vrEtat!=="erreur") return null;
                      return (
                        <div style={{marginTop:20}}>
                          <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Bons de versement aux employés</div>
                          {vrEtat==="erreur"&&<div className="card" style={{color:"#A3231C",fontSize:13}}>Impossible de charger les bons de versement. <button className="btn btn-ghost btn-sm" onClick={vrCharger}>Réessayer</button></div>}
                          {!vv.length&&vrListe.length>0&&<div className="card" style={{color:"#5B6B62",fontSize:13}}>Aucun bon ne correspond à la recherche.</div>}
                          <div style={{display:"grid",gap:10}}>
                            {vv.map(v=>(
                              <div className="card" key={v.id} style={{display:"flex",gap:12,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap"}}>
                                <div style={{minWidth:0,flex:"1 1 260px"}}>
                                  <div style={{fontWeight:700,fontSize:14.5,color:"#0F1F17",overflowWrap:"anywhere"}}>{v.type_versement} · {v.employe_nom}</div>
                                  <div style={{fontSize:12.5,color:"#2B3A32",marginTop:2}}>{v.numero} · {vrDateCourte(v.date_versement)} · <b>{FCFA(v.montant)}</b>{v.motif?" · "+v.motif:""}</div>
                                  <div style={{marginTop:4,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>{vrChip(v)}{v.recuperable&&v.statut==="signe"&&<span style={{fontSize:11.5,color:Number(v.solde_restant)>0?"#8A6A12":"#1E7A2D"}}>{Number(v.solde_restant)>0?"reste à retenir : "+FCFA(v.solde_restant):"entièrement retenu"}</span>}</div>
                                </div>
                                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                                  {v.statut==="a_signer"&&<button type="button" className="btn btn-gold btn-sm" disabled={vrBusy} onClick={()=>vrSigner(v)}>Signer tout de suite</button>}
                                  {v.statut==="a_signer"&&!v.envoye_le&&<button type="button" className="btn btn-ghost btn-sm btn-icon" disabled={vrBusy} onClick={()=>vrEnvoyer(v)}><Send size={13}/> Envoyer pour signature</button>}
                                  {v.statut==="a_signer"&&v.envoye_le&&<button type="button" className="btn btn-ghost btn-sm" disabled={vrBusy} onClick={()=>vrRelancer(v)}>Relancer</button>}
                                  <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={()=>vrOuvrirBon(v)}><Eye size={14}/> Ouvrir</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })():(
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1.4fr",gap:18}}>
                <div className="card">
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Paramètres du document</div>
                  <div className="fld">
                    <label className="lbl">Type de document</label>
                    <select className="inp" value={docForm.type} onChange={e=>{
                      const type = e.target.value;
                      setDocForm(f=>{ const r=docRelancePour(type,f.clientNom,f.destKey,relDesel); return {...f, type, corps:r.corps, ...(r.objet?{objet:r.objet}:{})}; });
                    }}>
                      {["Lettre commerciale","Offre de prix","Bon de commande","Mise en demeure","Lettre de relance"].map(t=><option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="fld">
                    <label className="lbl">Destinataire (Client)</label>
                    <select className="inp" value={docForm.destKey||""} onChange={e=>{
                      const v = e.target.value;
                      let clientNom = "";
                      if (v.startsWith("G:")) { const g=groupesClients().find(x=>x.key===v.slice(2)); clientNom=g?g.label:""; }
                      else if (v.startsWith("C:")) { const c=clients.find(x=>String(x.id)===v.slice(2)); clientNom=c?c.nom:""; }
                      setRelDesel({});
                      setDocForm(f=>{ const r=docRelancePour(f.type,clientNom,v,{}); return {...f, clientNom, destKey:v, corps:r.corps, ...(r.objet?{objet:r.objet}:{})}; });
                    }}>
                      <option value="">Choisir un client...</option>
                      <optgroup label="Groupes (plusieurs points de vente)">
                        {groupesClients().map(g=><option key={g.key} value={"G:"+g.key}>{g.label} — {g.membres.length} points</option>)}
                      </optgroup>
                      <optgroup label="Clients">
                        {clients.map(c=><option key={c.id} value={"C:"+c.id}>{c.nom}</option>)}
                      </optgroup>
                    </select>
                    <input type="text" className="inp" style={{marginTop:8}} placeholder="… ou autre destinataire (ex. : James, équipe commerciale)" value={docForm.destKey?"":(docForm.clientNom||"")} onChange={e=>{ const v=e.target.value; setRelDesel({}); setDocForm(f=>({...f, destKey:"", clientNom:v})); }} />
                  </div>
                  {(docForm.type==="Lettre de relance"||docForm.type==="Mise en demeure")&&docForm.destKey&&(()=>{
                    const lignes = facturesImpayees(fichesDest(docForm.destKey));
                    const plusieurs = docForm.destKey.startsWith("G:");
                    const sel = lignes.filter(x=>!relDesel[String(x.f.id)]);
                    const totalSel = sel.reduce((s,x)=>s+x.reste,0);
                    const fichesGrp = fichesDest(docForm.destKey);
                    const points = fichesGrp.map(c=>({c,items:lignes.filter(x=>x.c.id===c.id)}))
                      .sort((a,b)=>(a.items.length?0:1)-(b.items.length?0:1)||String(a.c.nom).localeCompare(String(b.c.nom),"fr"));
                    const nbAJour = points.filter(p=>!p.items.length).length;
                    const cocher = (cocheTout)=>{ const d={}; if(!cocheTout) lignes.forEach(x=>{ d[String(x.f.id)]=true; }); majRelance(d); };
                    return (
                      <div style={{margin:"4px 0 14px",border:"1px solid #E3E9E6",borderRadius:8,background:"#FBFCFB"}}>
                        <div style={{padding:"10px 12px",borderBottom:"1px solid #E3E9E6",display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                          <div style={{fontSize:12,fontWeight:700,color:"#0F1F17"}}>{plusieurs?"Structures à relancer":"Factures non réglées"} {lignes.length>0&&<span style={{fontWeight:400,color:"#5B6B62"}}>— {plusieurs?points.filter(p=>p.items.some(x=>!relDesel[String(x.f.id)])).length+" structure(s), ":""}{sel.length} facture{sel.length>1?"s":""} sur {lignes.length}</span>}</div>
                          {lignes.length>0&&<div style={{display:"flex",gap:6}}><button type="button" className="btn btn-ghost btn-sm" onClick={()=>cocher(true)}>Tout cocher</button><button type="button" className="btn btn-ghost btn-sm" onClick={()=>cocher(false)}>Tout décocher</button></div>}
                        </div>
                        {lignes.length===0&&<div style={{padding:12,fontSize:12,color:"#5B6B62"}}>Aucune facture impayée pour {plusieurs?"ce groupe":"ce client"}.</div>}
                        <div style={{maxHeight:460,overflowY:"auto"}}>
                          {points.map(p=>{
                            const tousCoches = p.items.length>0 && p.items.every(x=>!relDesel[String(x.f.id)]);
                            const aJour = p.items.length===0;
                            return (
                              <div key={p.c.id} style={{borderBottom:"1px solid #EDF1EF"}}>
                                {plusieurs&&<div style={{display:"flex",alignItems:"stretch",background:aJour?"#FBFCFB":(tousCoches?"#E8F5EB":"#F1F5F3")}}>
                                  <label style={{flex:1,display:"flex",alignItems:"center",gap:10,padding:"10px 12px",cursor:aJour?"default":"pointer",fontSize:13,fontWeight:700,color:aJour?"#8A978F":"#0F1F17"}}>
                                    <input type="checkbox" disabled={aJour} checked={tousCoches} onChange={()=>{ const d={...relDesel}; p.items.forEach(x=>{ if(tousCoches) d[String(x.f.id)]=true; else delete d[String(x.f.id)]; }); majRelance(d); }} style={{accentColor:"#1E8A32",width:22,height:22,flexShrink:0}}/>
                                    <span style={{flex:1}}>{etiquetteClient(p.c)}</span>
                                    {aJour
                                      ? <span style={{fontWeight:600,fontSize:11,color:"#1E7A2D"}}>à jour ✓</span>
                                      : <span style={{fontWeight:600,color:"#D6453D",textAlign:"right"}}>{FCFA(p.items.reduce((s,x)=>s+x.reste,0))}<span style={{display:"block",fontWeight:400,fontSize:10,color:"#5B6B62"}}>{p.items.length} facture{p.items.length>1?"s":""}</span></span>}
                                  </label>
                                  {!aJour&&<button type="button" title="Voir / masquer les factures" onClick={()=>setRelOuvert(o=>({...o,[p.c.id]:!o[p.c.id]}))} style={{border:"none",background:"transparent",cursor:"pointer",padding:"0 12px",fontSize:12,color:"#5B6B62"}}>{relOuvert[p.c.id]?"▲":"▼"}</button>}
                                </div>}
                                {(!plusieurs||relOuvert[p.c.id])&&p.items.map(x=>(
                                  <label key={x.f.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px "+"7px "+(plusieurs?"26px":"12px"),cursor:"pointer",fontSize:12}}>
                                    <input type="checkbox" checked={!relDesel[String(x.f.id)]} onChange={()=>{ const d={...relDesel}; if(d[String(x.f.id)]) delete d[String(x.f.id)]; else d[String(x.f.id)]=true; majRelance(d); }} style={{accentColor:"#1E8A32"}}/>
                                    <span style={{flex:1}}><strong>{x.f.numero}</strong> <span style={{color:"#5B6B62"}}>· {fmtDate(x.f.date_emission)}{x.retard?" · ":""}</span>{x.retard&&<span style={{color:"#D6453D",fontWeight:600}}>en retard</span>}</span>
                                    <span style={{fontWeight:600}}>{FCFA(x.reste)}</span>
                                  </label>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                        {plusieurs&&nbAJour>0&&<div style={{padding:"6px 12px",fontSize:11,color:"#5B6B62",borderTop:"1px solid #EDF1EF"}}>{nbAJour} structure{nbAJour>1?"s":""} à jour (aucune facture impayée) : rien à relancer.</div>}
                        {lignes.length>0&&<div style={{padding:"10px 12px",display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:700,color:"#1E8A32"}}><span>Total à relancer</span><span>{FCFA(totalSel)}</span></div>}
                        <div style={{padding:"0 12px 10px",fontSize:10,color:"#5B6B62"}}>Cocher ou décocher une facture met à jour la liste dans la lettre (le texte est alors régénéré : faites vos retouches de texte en dernier).</div>
                      </div>
                    );
                  })()}
                  <div className="fld"><label className="lbl">Date</label><input type="date" className="inp" value={docForm.date} onChange={e=>setDocForm(f=>({...f,date:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Objet</label><input type="text" className="inp" value={docForm.objet} onChange={e=>setDocForm(f=>({...f,objet:e.target.value}))} /></div>
                  <button className="btn btn-ghost btn-icon" style={{width:"100%",marginTop:8}} onClick={sigOuvrir}>✍ {sigValide(docForm)?"Refaire la signature par téléphone":"Signer par téléphone (QR code)"}</button>
                  {docForm.signature&&(sigValide(docForm)?(
                    <div style={{marginTop:8,background:"#F1FAF2",border:"1px solid #BFE3C6",borderRadius:10,padding:"9px 12px",fontSize:12.5,color:"#1E5A28",display:"flex",gap:10,alignItems:"center",justifyContent:"space-between"}}>
                      <span>✓ Signé par <b>{docForm.signature.nom}</b> le {sigHeure(docForm.signature.signe_le)}</span>
                      <button className="btn btn-ghost btn-sm" onClick={sigRetirer}>Retirer</button>
                    </div>
                  ):(
                    <div style={{marginTop:8,background:"#FFF4DD",border:"1px solid #F0D9A0",borderRadius:10,padding:"9px 12px",fontSize:12.5,color:"#7A4B00",display:"flex",gap:10,alignItems:"center",justifyContent:"space-between"}}>
                      <span>Le texte a changé depuis la signature de {docForm.signature.nom} : elle n'apparaît plus dans le document. Refaites la signature (ou remettez le texte d'origine).</span>
                      <button className="btn btn-ghost btn-sm" onClick={sigRetirer}>Retirer</button>
                    </div>
                  ))}
                  <button className="btn btn-gold btn-icon" style={{width:"100%",marginTop:8}} onClick={()=>setDocView(docForm)}><Eye size={14}/> Aperçu et impression</button>
                  <button className="btn btn-ghost btn-icon" style={{width:"100%",marginTop:8}} onClick={()=>{
                    const cli = emailDestDoc();
                    setDocSendModal({kind:"document", data:{...docForm}});
                    setDocSendForm({to:cli?.email||"", toName:docForm.clientNom, subject:docForm.type+" — "+entreprise.entreprise_nom, message:"Veuillez trouver ci-joint le document."});
                  }}><Mail size={14}/> Envoyer par email</button>
                </div>
                <div className="card">
                  <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Corps du document</div>
                  <textarea className="inp" rows={18} value={docForm.corps} onChange={e=>setDocForm(f=>({...f,corps:e.target.value}))}
                    style={{resize:"vertical",fontFamily:"var(--az-font)",fontSize:13,lineHeight:1.8,width:"100%"}} />
                </div>
              </div>
              )}
            </div>
          )}

          {/* CHAT D'ÉQUIPE — interface mobile type WhatsApp */}
          {page==="chat_supervision"&&isDirecteur()&&(()=>{
            const moi = String(session.matricule);
            const nomDe = (mat) => employes.find(e=>String(e.matricule)===String(mat))?.nom || String(mat);
            const paires = {};
            supMsgs.forEach(m=>{
              const k = [String(m.sender_matricule),String(m.destinataire_matricule)].sort().join("|");
              (paires[k]=paires[k]||[]).push(m);
            });
            const liste = Object.entries(paires)
              .filter(([k])=>!k.split("|").includes(moi))
              .map(([k,ms])=>({k,ms,a:k.split("|")[0],b:k.split("|")[1],last:ms[ms.length-1]}))
              .sort((x,y)=>new Date(y.last.created_at)-new Date(x.last.created_at));
            const sel = liste.find(x=>x.k===supPair) || null;
            const heure = (d) => new Date(d).toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"});
            const showList = !isMobile || !sel;
            const showThread = !isMobile || !!sel;
            return (
              <div>
                <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:6}}>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setPage("chat")}>← Retour au chat</button>
                  <div style={{fontSize:isMobile?20:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Discussions de l'équipe</div>
                  <button className="btn btn-ghost btn-sm" onClick={()=>loadSupervision()}>Actualiser</button>
                </div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:14}}>Vue réservée à la direction, en lecture seule. Les personnes concernées ne sont pas prévenues quand vous ouvrez une discussion.</div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"300px minmax(0,1fr)",gap:isMobile?0:16,minHeight:0}}>
                  {showList&&(
                    <div className="card" style={{padding:0,overflow:"hidden"}}>
                      {liste.length===0&&<div style={{padding:18,fontSize:12,color:"#5B6B62"}}>Aucune discussion privée entre collègues pour le moment.</div>}
                      {liste.map(p=>(
                        <button key={p.k} type="button" onClick={()=>setSupPair(p.k)}
                          style={{width:"100%",border:"none",borderBottom:"1px solid rgba(15,31,23,0.066)",background:sel&&sel.k===p.k?"#F1F5F3":"transparent",color:"#0F1F17",padding:"12px 14px",cursor:"pointer",textAlign:"left",display:"block"}}>
                          <div style={{fontSize:13,fontWeight:700}}>{nomDe(p.a)} ⇄ {nomDe(p.b)}</div>
                          <div style={{fontSize:11,color:"#5B6B62",marginTop:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.last.contenu}</div>
                          <div style={{fontSize:10,color:"#8A978F",marginTop:2}}>{heure(p.last.created_at)} · {p.ms.length} message{p.ms.length>1?"s":""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showThread&&(
                    <div className="card" style={{padding:0,overflow:"hidden",display:"flex",flexDirection:"column",minHeight:isMobile?"60vh":"60vh"}}>
                      {!sel&&<div style={{margin:"auto",padding:24,fontSize:13,color:"#5B6B62",textAlign:"center"}}>Choisissez une discussion à gauche pour la lire.</div>}
                      {sel&&(
                        <>
                          <div style={{padding:"12px 16px",borderBottom:"1px solid #E3E9E6",display:"flex",alignItems:"center",gap:10}}>
                            {isMobile&&<button type="button" aria-label="Retour" onClick={()=>setSupPair(null)} style={{border:"none",background:"transparent",fontSize:25,lineHeight:1,cursor:"pointer",color:"#0F1F17"}}>‹</button>}
                            <div style={{fontSize:14,fontWeight:700}}>{nomDe(sel.a)} ⇄ {nomDe(sel.b)}</div>
                            <div style={{marginLeft:"auto",fontSize:10,color:"#8A978F"}}>Lecture seule</div>
                          </div>
                          <div style={{flex:1,overflowY:"auto",padding:"14px 16px",display:"flex",flexDirection:"column",gap:7}}>
                            {sel.ms.map(m=>{
                              const deA = String(m.sender_matricule)===sel.a;
                              return (
                                <div key={m.id} style={{alignSelf:deA?"flex-start":"flex-end",maxWidth:isMobile?"88%":"76%"}}>
                                  <div style={{fontSize:11,fontWeight:700,color:"#0F1F17",margin:"0 5px 3px"}}>{nomDe(m.sender_matricule)}</div>
                                  <div style={{background:deA?"#F1F5F3":"#DDF0E0",color:"#0F1F17",padding:"8px 10px 6px",borderRadius:deA?"13px 13px 13px 3px":"13px 13px 3px 13px",fontSize:13,lineHeight:1.45,overflowWrap:"anywhere"}}>
                                    <div>{m.contenu}</div>
                                    <div style={{fontSize:9,opacity:.6,marginTop:4,textAlign:"right"}}>{heure(m.created_at)}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {page==="chat"&&(isDirecteur()||can("chat"))&&(()=>{
            const collegues = employes.filter(e=>e.actif && e.matricule!==session.matricule);
            const activeColleague = activeChatThread===null
              ? null
              : collegues.find(c=>String(c.matricule)===String(activeChatThread));
            const activeName = activeChatThread===null
              ? "Salon général"
              : (activeColleague?.nom || "Collègue");
            const activeSubtitle = activeChatThread===null
              ? "Tous les membres de l'équipe"
              : [activeColleague?.poste,activeColleague?.service].filter(Boolean).join(" · ");
            const showConversation = !isMobile || mobileChatConversationOpen;
            const openConversation = (thread) => {
              setActiveChatThread(thread);
              setMobileChatConversationOpen(true);
            };

            return (
              <div style={{height:isMobile?"calc(100dvh - 76px)":"auto",minHeight:0}}>
                {!isMobile&&<div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:16}}>Chat d'équipe</div>}

                <div style={{
                  display:"grid",
                  gridTemplateColumns:isMobile
                    ? (showConversation?"1fr":"1fr")
                    : "260px minmax(0,1fr)",
                  gap:isMobile?0:16,
                  height:isMobile?"100%":"70vh",
                  minHeight:0
                }}>
                  {/* Liste des conversations : plein écran sur téléphone */}
                  {(!isMobile || !showConversation)&&(
                    <div className="card" style={{
                      padding:0,
                      overflow:"hidden",
                      display:"flex",
                      flexDirection:"column",
                      minHeight:0,
                      borderRadius:isMobile?0:10
                    }}>
                      <div style={{
                        padding:"14px 16px",
                        borderBottom:"1px solid #E3E9E6",
                        background:"#FFFFFF",
                        color:"#0F1F17",
                        fontWeight:700,
                        fontSize:17,
                        flexShrink:0
                      }}>
                        Discussions
                      </div>
                      {isDirecteur()&&(
                        <button type="button" onClick={()=>{setSupPair(null);setPage("chat_supervision");}}
                          style={{width:"100%",border:"none",borderBottom:"1px solid rgba(15,31,23,0.066)",background:"#F1F5F3",color:"#1E8A32",padding:"10px 14px",cursor:"pointer",textAlign:"left",fontWeight:700,fontSize:12,flexShrink:0}}>
                          👁 Voir toutes les discussions de l'équipe
                        </button>
                      )}

                      <div style={{overflowY:"auto",WebkitOverflowScrolling:"touch",flex:1}}>
                        <button
                          type="button"
                          onClick={()=>openConversation(null)}
                          style={{
                            width:"100%",
                            border:"none",
                            borderBottom:"1px solid rgba(15,31,23,0.066)",
                            background:"transparent",
                            color:"#0F1F17",
                            padding:"12px 14px",
                            cursor:"pointer",
                            display:"flex",
                            alignItems:"center",
                            gap:12,
                            textAlign:"left"
                          }}
                        >
                          <div style={{
                            width:44,height:44,borderRadius:"50%",
                            background:"#2F9E3F",color:"#FFFFFF",
                            display:"flex",alignItems:"center",justifyContent:"center",
                            flexShrink:0,fontWeight:800
                          }}>
                            <Users size={20}/>
                          </div>
                          <div style={{minWidth:0,flex:1}}>
                            <div style={{fontSize:14,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              Salon général
                            </div>
                            <div style={{fontSize:11,color:"#5B6B62",marginTop:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              Tous les membres de l'équipe
                            </div>
                          </div>
                          {(chatUnreadCounts[GENERAL_CHAT_KEY]||0)>0&&(
                            <span style={{
                              minWidth:22,height:22,padding:"0 6px",borderRadius:11,
                              background:"#2F9E3F",color:"#FFFFFF",fontSize:10,
                              fontWeight:800,display:"inline-flex",alignItems:"center",justifyContent:"center"
                            }}>
                              {chatUnreadCounts[GENERAL_CHAT_KEY]>99?"99+":chatUnreadCounts[GENERAL_CHAT_KEY]}
                            </span>
                          )}
                        </button>

                        {collegues.map(c=>{
                          const unreadCount=chatUnreadCounts[c.matricule]||0;
                          const initials=String(c.nom||"?")
                            .split(/\s+/).filter(Boolean).slice(0,2)
                            .map(part=>part[0]).join("").toUpperCase();
                          return (
                            <button
                              type="button"
                              key={c.id}
                              onClick={()=>openConversation(c.matricule)}
                              style={{
                                width:"100%",
                                border:"none",
                                borderBottom:"1px solid rgba(15,31,23,0.066)",
                                background:"transparent",
                                color:"#0F1F17",
                                padding:"12px 14px",
                                cursor:"pointer",
                                display:"flex",
                                alignItems:"center",
                                gap:12,
                                textAlign:"left"
                              }}
                            >
                              <div style={{
                                width:44,height:44,borderRadius:"50%",
                                background:"#F1F5F3",color:"#0F1F17",
                                border:"1px solid #E3E9E6",
                                display:"flex",alignItems:"center",justifyContent:"center",
                                flexShrink:0,fontWeight:800,fontSize:13,overflow:"hidden"
                              }}>
                                {c.photo_url
                                  ? <img alt="" src={c.photo_url} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}}/>
                                  : (initials||<User size={19}/>)}
                              </div>
                              <div style={{minWidth:0,flex:1}}>
                                <div style={{
                                  fontSize:14,fontWeight:700,
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
                                }}>
                                  {c.nom}
                                </div>
                                <div style={{
                                  fontSize:11,color:"#5B6B62",marginTop:3,
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
                                }}>
                                  {[c.poste,c.service].filter(Boolean).join(" · ") || c.matricule}
                                </div>
                              </div>
                              {unreadCount>0&&(
                                <span style={{
                                  minWidth:22,height:22,padding:"0 6px",borderRadius:11,
                                  background:"#2F9E3F",color:"#FFFFFF",fontSize:10,
                                  fontWeight:800,display:"inline-flex",alignItems:"center",justifyContent:"center"
                                }}>
                                  {unreadCount>99?"99+":unreadCount}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Conversation : plein écran sur téléphone */}
                  {showConversation&&(
                    <div className="card" style={{
                      display:"flex",
                      flexDirection:"column",
                      padding:0,
                      overflow:"hidden",
                      minHeight:0,
                      height:"100%",
                      borderRadius:isMobile?0:10
                    }}>
                      <div style={{
                        minHeight:58,
                        padding:isMobile?"8px 10px":"10px 16px",
                        borderBottom:"1px solid #E3E9E6",
                        background:"#FFFFFF",
                        display:"flex",
                        alignItems:"center",
                        gap:10,
                        flexShrink:0
                      }}>
                        {isMobile&&(
                          <button
                            type="button"
                            aria-label="Retour aux conversations"
                            onClick={()=>setMobileChatConversationOpen(false)}
                            style={{
                              width:40,height:40,borderRadius:"50%",
                              border:"none",background:"transparent",
                              color:"#0F1F17",cursor:"pointer",
                              fontSize:25,lineHeight:1,display:"flex",
                              alignItems:"center",justifyContent:"center",flexShrink:0
                            }}
                          >
                            ‹
                          </button>
                        )}
                        <div style={{
                          width:40,height:40,borderRadius:"50%",
                          background:activeChatThread===null?"#2F9E3F":"#F1F5F3",
                          color:activeChatThread===null?"#FFFFFF":"#0F1F17",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          flexShrink:0,fontWeight:800,overflow:"hidden"
                        }}>
                          {activeChatThread===null
                            ? <Users size={19}/>
                            : activeColleague?.photo_url
                            ? <img alt="" src={activeColleague.photo_url} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}}/>
                            : String(activeName).split(/\s+/).filter(Boolean).slice(0,2).map(p=>p[0]).join("").toUpperCase()}
                        </div>
                        <div style={{minWidth:0,flex:1}}>
                          <div style={{
                            fontSize:14,color:"#0F1F17",fontWeight:700,
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
                          }}>
                            {activeName}
                          </div>
                          <div style={{
                            fontSize:10,color:"#5B6B62",marginTop:2,
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
                          }}>
                            {activeSubtitle||"Discussion privée"}
                          </div>
                        </div>
                      </div>

                      <div style={{
                        flex:1,
                        minHeight:0,
                        overflowY:"auto",
                        WebkitOverflowScrolling:"touch",
                        padding:isMobile?"12px 10px":"14px 16px",
                        display:"flex",
                        flexDirection:"column",
                        gap:7,
                        background:"linear-gradient(rgba(255,255,255,0.96),rgba(255,255,255,0.96))"
                      }}>
                        {chatMessages.length===0&&(
                          <div style={{
                            color:"#5B6B62",fontSize:12,textAlign:"center",
                            margin:"auto",padding:18,borderRadius:12,
                            background:"rgba(13,39,66,.8)"
                          }}>
                            Aucun message pour le moment.<br/>Commencez la discussion.
                          </div>
                        )}

                        {chatMessages.map(m=>{
                          const mine = String(m.sender_matricule)===String(session.matricule);
                          const senderDisplay = m.sender_nom
                            || employes.find(e=>String(e.matricule)===String(m.sender_matricule))?.nom
                            || "Utilisateur";
                          return (
                            <div key={m.id} style={{
                              alignSelf:mine?"flex-end":"flex-start",
                              maxWidth:isMobile?"88%":"76%",
                              minWidth:0
                            }}>
                              {/* Le nom reste visible sur téléphone, surtout dans le salon général. */}
                              {!mine&&(
                                <div style={{
                                  fontSize:11,color:"#0F1F17",fontWeight:700,
                                  marginBottom:3,marginLeft:5,
                                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"
                                }}>
                                  {(()=>{
                                    const ph=employes.find(e=>String(e.matricule)===String(m.sender_matricule))?.photo_url;
                                    return ph?<img alt="" src={ph} style={{width:20,height:20,borderRadius:"50%",objectFit:"cover",objectPosition:"center top",verticalAlign:"middle",marginRight:6}}/>:null;
                                  })()}
                                  {senderDisplay}
                                </div>
                              )}
                              <div style={{
                                background:mine?"#2F9E3F":"#F1F5F3",
                                color:mine?"#FFFFFF":"#0F1F17",
                                padding:"8px 10px 6px",
                                borderRadius:mine?"13px 13px 3px 13px":"13px 13px 13px 3px",
                                fontSize:13,lineHeight:1.45,
                                overflowWrap:"anywhere",
                                boxShadow:"0 2px 5px rgba(0,0,0,.2)"
                              }}>
                                <div>{m.contenu}</div>
                                <div style={{
                                  fontSize:9,opacity:.72,marginTop:4,
                                  textAlign:"right",whiteSpace:"nowrap"
                                }}>
                                  {new Date(m.created_at).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"})}
                                  {mine&&(()=>{
                                    const lu = activeChatThread!==null && chatLuParAutre >= new Date(m.created_at).getTime();
                                    return <span style={{marginLeft:5,fontWeight:lu?700:400,color:lu?"#FFFFFF":"inherit",opacity:lu?1:undefined}}>{lu?"✓✓ Lu":"✓"}</span>;
                                  })()}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={chatEndRef}/>
                      </div>

                      <div style={{
                        display:"flex",
                        alignItems:"flex-end",
                        gap:8,
                        padding:isMobile
                          ?"8px 8px max(8px, env(safe-area-inset-bottom))"
                          :"10px 12px",
                        borderTop:"1px solid #E3E9E6",
                        background:"#FFFFFF",
                        flexShrink:0
                      }}>
                        <textarea
                          className="inp"
                          rows={1}
                          aria-label="Écrire un message"
                          placeholder="Message"
                          value={chatInput}
                          onChange={e=>setChatInput(e.target.value)}
                          onKeyDown={e=>{
                            if(e.key==="Enter"&&!e.shiftKey){
                              e.preventDefault();
                              sendChatMessage();
                            }
                          }}
                          style={{
                            resize:"none",
                            minHeight:42,
                            maxHeight:110,
                            borderRadius:22,
                            padding:"10px 14px",
                            lineHeight:1.4,
                            flex:1
                          }}
                        />
                        <button
                          type="button"
                          aria-label="Envoyer le message"
                          disabled={chatSending||!chatInput.trim()}
                          onClick={sendChatMessage}
                          style={{
                            width:44,height:44,borderRadius:"50%",
                            border:"none",background:"#2F9E3F",color:"#FFFFFF",
                            cursor:"pointer",display:"flex",alignItems:"center",
                            justifyContent:"center",flexShrink:0,
                            opacity:(chatSending||!chatInput.trim())?0.55:1
                          }}
                        >
                          <Send size={18}/>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* LIVRAISONS */}
          {page==="livraisons"&&(isDirecteur()||can("livraisons"))&&(
            <div>
              <button className="btn btn-ghost btn-sm" style={{marginBottom:10}} onClick={()=>setPage("stock-villes")}>← Parcours du stock</button>
              <div className="module-tabs" style={{marginBottom:14}}>
                <button onClick={()=>setPage("stock-villes")}>Vue d'ensemble</button>
                <button className="active">Livraisons</button>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Livraisons vers dépôts</div>
                <button className="btn btn-gold" onClick={()=>setPage("nouvelle-livraison")}>+ Nouvelle livraison</button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:10,marginBottom:20}}>
                {depots.map(dep=>{
                  const livDep=livraisons.filter(l=>l.depot_id===dep.id);
                  const totalBtl=livDep.reduce((s,l)=>s+l.quantite,0);
                  return (
                    <div key={dep.id} className="kpi" style={{borderColor:"rgba(47,158,63,0.27)"}}>
                      <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1,color:"#5B6B62",marginBottom:4}}>{dep.ville}</div>
                      <div style={{fontSize:20,color:"#1E8A32",fontWeight:700}}>{NUM(totalBtl)}</div>
                      <div style={{fontSize:10,color:"#5B6B62"}}>btl envoyées</div>
                    </div>
                  );
                })}
              </div>
              {livraisons.length===0?(
                <div className="card" style={{textAlign:"center",padding:40,color:"#5B6B62"}}>
                  <div style={{display:"flex",justifyContent:"center",marginBottom:12,color:"#5B6B62"}}><Truck size={40}/></div>
                  <button className="btn btn-gold" onClick={()=>setPage("nouvelle-livraison")}>Créer une livraison</button>
                </div>
              ):(
                <div className="card scr">
                  <table className={"tbl"+(isMobile?" az-livcards":"")}>
                    <thead><tr><th>N°</th><th>Origine</th><th>Dépôt</th><th>Ville</th><th>Date départ</th><th>Qté</th><th>Chauffeur</th><th>Agence</th><th>Statut</th><th></th></tr></thead>
                    <tbody>{livraisons.map(l=>(
                      <tr key={l.id}>
                        <td style={{fontFamily:"monospace",fontSize:11,color:"#1E8A32"}}>{l.numero}</td>
                        <td>{l.ville_origine?(
                          <span className="badge br" style={{whiteSpace:"nowrap"}}>⇄ {l.ville_origine}</span>
                        ):(
                          <span style={{fontSize:11,color:"#5B6B62"}}>Magasin central</span>
                        )}</td>
                        <td>{l.depot_nom}</td>
                        <td><span className="badge bg2">{l.ville}</span></td>
                        <td style={{color:"#5B6B62"}}>{new Date(l.date_depart).toLocaleDateString("fr-FR")}</td>
                        <td style={{fontWeight:600}}>{NUM(l.quantite)} btl</td>
                        <td style={{color:"#5B6B62",fontSize:12}}>{l.chauffeur||"—"}</td>
                        <td style={{color:"#5B6B62",fontSize:12}}>{l.agence||"—"}</td>
                        <td>
                          {l.statut==="En transit"&&l.receptionnaire_prevu&&<div className="liv-recep" style={{marginBottom:6,order:-1}}>Doit réceptionner : <strong>{l.receptionnaire_prevu}</strong></div>}
                          {l.statut==="En transit"?(
                            <button className="btn btn-gold btn-sm" onClick={()=>{setReceptionModal(l);setReceptionForm({etat:"Bon état",note:""});}}>✓ Confirmer<span className="liv-long">&nbsp;la réception</span></button>
                          ):l.statut==="Reçu au dépôt"?(
                            <div>
                              <span className="badge bg" style={{display:"block",marginBottom:3}}>✓ Reçu</span>
                              {l.recu_par&&<div style={{fontSize:10,color:"#5B6B62"}}>par {l.recu_par}</div>}
                            </div>
                          ):<span className="badge br">Retourné</span>}
                        </td>
                        <td style={{display:"flex",gap:5}}>
                          <button className="btn btn-ghost btn-sm" onClick={()=>setLivraisonView(l)}><Eye size={13}/></button>
                          {(canEdit())&&<button className="btn btn-red btn-sm" onClick={()=>deleteLivraison(l.id)}><Trash2 size={13}/></button>}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* NOUVELLE LIVRAISON */}
          {page==="nouvelle-livraison"&&(isDirecteur()||can("livraisons"))&&(
            <div>
              <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPage("livraisons")}>← Retour</button>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17"}}>Nouvelle livraison</div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1.4fr",gap:18,marginBottom:18}}>
                <div className="card">
                  {canTransfertVilles()&&(
                  <div className="fld"><label className="lbl">Origine du transfert *</label>
                    <select className="inp" value={livraisonForm.origineType} onChange={e=>setLivraisonForm(f=>({...f,origineType:e.target.value,villeOrigine:""}))}>
                      <option value="central">Magasin central (Dschang)</option>
                      <option value="ville">Une ville (dépannage vers une autre ville)</option>
                    </select>
                  </div>
                  )}
                  {livraisonForm.origineType==="ville"&&canTransfertVilles()&&(
                    <div className="fld"><label className="lbl">Ville d'origine *</label>
                      <select className="inp" value={livraisonForm.villeOrigine} onChange={e=>setLivraisonForm(f=>({...f,villeOrigine:e.target.value}))}>
                        <option value="">Choisir...</option>
                        {[...new Set(depotStocks.filter(s=>s.ville!=="Magasin central"&&s.ville!==livraisonForm.ville).map(s=>s.ville))].sort().map(v=><option key={v} value={v}>{v}</option>)}
                      </select>
                      <div style={{fontSize:10,color:"#5B6B62",marginTop:4}}>Le stock de cette ville sera réduit ; le magasin central n'est pas concerné.</div>
                    </div>
                  )}
                  <div className="fld"><label className="lbl">Dépôt destinataire *</label>
                    <select className="inp" value={livraisonForm.depotId} onChange={e=>{const d=depots.find(x=>x.id===e.target.value);setLivraisonForm(f=>({...f,depotId:e.target.value,depotNom:d?.nom||"",ville:d?.ville||""}));}}>
                      <option value="">Choisir un dépôt...</option>
                      {/* Le magasin central n'est jamais une destination de livraison — il n'est alimenté que par la production. */}
                      {depots.filter(d=>d.ville!=="Magasin central"&&!(livraisonForm.origineType==="ville"&&d.ville===livraisonForm.villeOrigine)).map(d=><option key={d.id} value={d.id}>{d.nom} — {d.ville}</option>)}
                    </select>
                  </div>
                  <div className="fld"><label className="lbl">Date de départ</label><input type="date" className="inp" value={livraisonForm.dateDepart} onChange={e=>setLivraisonForm(f=>({...f,dateDepart:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Date d'arrivée prévue</label><input type="date" className="inp" value={livraisonForm.dateArrivee} onChange={e=>setLivraisonForm(f=>({...f,dateArrivee:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Chauffeur</label>
                    <input type="text" className="inp" placeholder="Nom du chauffeur" value={livraisonForm.chauffeur} onChange={e=>setLivraisonForm(f=>({...f,chauffeur:e.target.value}))} />
                  </div>
                  <div className="fld"><label className="lbl">Véhicule</label><input type="text" className="inp" placeholder="ex: LT 1234 A" value={livraisonForm.vehicule} onChange={e=>setLivraisonForm(f=>({...f,vehicule:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Agence de transport</label><input type="text" className="inp" value={livraisonForm.agence} onChange={e=>setLivraisonForm(f=>({...f,agence:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Tél. agence</label><input type="text" className="inp" value={livraisonForm.agenceTel} onChange={e=>setLivraisonForm(f=>({...f,agenceTel:e.target.value}))} /></div>
                  <div className="fld"><label className="lbl">Collaborateur qui doit réceptionner</label>
                    <select className="inp" value={livraisonForm.receptionnairePrevu} onChange={e=>setLivraisonForm(f=>({...f,receptionnairePrevu:e.target.value}))}>
                      <option value="">Choisir...</option>
                      {employes.filter(e=>e.actif&&(!livraisonForm.ville||e.ville===livraisonForm.ville)).map(e=><option key={e.id} value={e.nom}>{e.nom} — {e.ville}</option>)}
                    </select>
                    <div style={{fontSize:10,color:"#5B6B62",marginTop:4}}>La personne qui devra confirmer la réception à l'arrivée.</div>
                  </div>
                  <div className="fld"><label className="lbl">Notes</label><input type="text" className="inp" value={livraisonForm.notes} onChange={e=>setLivraisonForm(f=>({...f,notes:e.target.value}))} /></div>
                </div>
                <div className="card">
                  <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:14}}>Produits à livrer</div>
                  {livraisonForm.lignes.map((l,i)=>(
                    <div key={i} className="lr">
                      <div style={{flex:2}}>{l.produit}</div>
                      <div style={{color:"#1E8A32",fontWeight:600}}>{NUM(l.quantite)} btl</div>
                      <button onClick={()=>setLivraisonForm(f=>({...f,lignes:f.lignes.filter((_,idx)=>idx!==i)}))} style={{background:"none",border:"none",color:"#D6453D",cursor:"pointer"}}>✕</button>
                    </div>
                  ))}
                  <div style={{display:"grid",gridTemplateColumns:"2fr 1fr auto",gap:6,marginTop:10,alignItems:"end"}}>
                    <div><label className="lbl">Produit</label>
                      <select className="inp" value={newLivraisonLigne.produit} onChange={e=>setNewLivraisonLigne(n=>({...n,produit:e.target.value}))}>
                        <option value="">...</option>
                        {produits.map(p=>{
                          const dispo = livraisonForm.origineType==="ville"
                            ? (depotStocks.find(s=>s.ville===livraisonForm.villeOrigine&&s.produit_nom===p.nom)?.quantite||0)
                            : stockCentral(p);
                          return <option key={p.id} value={p.nom}>{p.code?p.code+" · ":""}{p.nom} (stock: {dispo})</option>;
                        })}
                      </select>
                    </div>
                    <div><label className="lbl">Quantité</label><input type="number" className="inp" value={newLivraisonLigne.quantite} onChange={e=>setNewLivraisonLigne(n=>({...n,quantite:e.target.value}))} /></div>
                    <button className="btn btn-gold btn-sm" style={{alignSelf:"flex-end"}} onClick={()=>{
                      if (!newLivraisonLigne.produit||!newLivraisonLigne.quantite) return;
                      setLivraisonForm(f=>({...f,lignes:[...f.lignes,{produit:newLivraisonLigne.produit,quantite:parseInt(newLivraisonLigne.quantite)}]}));
                      setNewLivraisonLigne({produit:"",quantite:""});
                    }}>+</button>
                  </div>
                  {livraisonForm.origineType==="ville"&&livraisonForm.villeOrigine&&newLivraisonLigne.produit&&(()=>{
                    const dispo=depotStocks.find(s=>s.ville===livraisonForm.villeOrigine&&s.produit_nom===newLivraisonLigne.produit)?.quantite||0;
                    return <div style={{fontSize:11,color:"#5B6B62",marginTop:6}}>Disponible à {livraisonForm.villeOrigine} : <strong style={{color:"#0F1F17"}}>{NUM(dispo)} btl</strong></div>;
                  })()}
                  {livraisonForm.lignes.length>0&&(
                    <div style={{marginTop:18,borderTop:"1px solid #E3E9E6",paddingTop:14,display:"flex",justifyContent:"space-between",fontSize:16,color:"#1E8A32",fontWeight:700}}>
                      <span>Total bouteilles</span>
                      <span>{NUM(livraisonForm.lignes.reduce((s,l)=>s+parseInt(l.quantite),0))} btl</span>
                    </div>
                  )}
                </div>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button className="btn btn-ghost" onClick={()=>setPage("livraisons")}>Annuler</button>
                <button className="btn btn-gold" onClick={creerLivraison} disabled={loading}>{loading?"...":"Créer la livraison"}</button>
              </div>
            </div>
          )}

          {/* DEPOTS */}
          {page==="avaries"&&(isDirecteur()||can("avaries")||can("production"))&&(()=>{
            const total=avaries.reduce((s,a)=>s+toFiniteNumber(a.quantite),0);
            const parVille={};
            avaries.forEach(a=>{ const v=a.ville||"Non précisé"; parVille[v]=(parVille[v]||0)+toFiniteNumber(a.quantite); });
            return (
              <div>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:6}}>Avaries</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap",marginBottom:20}}>
                  <div style={{fontSize:12,color:"#5B6B62",maxWidth:560}}>Produits perdus, cassés ou retournés. Saisis l'avarie ici : le stock de la ville baisse, et si tu choisis une facture, le lieu, l'adresse et la ville s'affichent sur cette facture. (Une avarie sur un lot de production se signale toujours dans Production.)</div>
                  <button className="btn btn-red" onClick={ouvrirAvarieVille}><AlertTriangle size={14}/> Déclarer une avarie</button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:12,marginBottom:18}}>
                  <div className="card" style={{padding:16}}><div style={{fontSize:11,textTransform:"uppercase",color:"#5B6B62"}}>Total avaries</div><div style={{fontSize:25,fontWeight:800,color:"#D6453D",marginTop:5}}>{NUM(total)} btl</div></div>
                  <div className="card" style={{padding:16}}><div style={{fontSize:11,textTransform:"uppercase",color:"#5B6B62"}}>À remplacer</div><div style={{fontSize:25,fontWeight:800,color:"#0F1F17",marginTop:5}}>{NUM(avaries.filter(a=>a.remplacement==="À remplacer").reduce((t,a)=>t+toFiniteNumber(a.quantite),0))} btl</div></div>
                  {Object.entries(parVille).sort((a,b)=>b[1]-a[1]).slice(0,2).map(([v,q])=>(
                    <div key={v} className="card" style={{padding:16}}><div style={{fontSize:11,textTransform:"uppercase",color:"#5B6B62"}}>{v}</div><div style={{fontSize:25,fontWeight:800,color:"#0F1F17",marginTop:5}}>{NUM(q)} btl</div></div>
                  ))}
                </div>
                <div className="card" style={{padding:0}}><div className="table-wrap"><table className="tbl"><thead><tr><th>Date</th><th>Produit</th><th>Qté</th><th>Ville</th><th>Lieu / adresse</th><th>Client / facture</th><th>Raison</th><th>Remplacement</th><th>Signalé par</th></tr></thead><tbody>
                  {avaries.map(a=>(
                    <tr key={a.id}>
                      <td>{fmtDate(a.date)}</td>
                      <td>{a.produit}</td>
                      <td style={{color:"#D6453D",fontWeight:700}}>{NUM(a.quantite)}</td>
                      <td>{a.ville||"—"}</td>
                      <td style={{fontSize:12}}>{[a.lieu,a.adresse,a.lieu_ville&&a.lieu_ville!==a.ville?a.lieu_ville:""].filter(Boolean).join(" — ")||"—"}{a.lot_numero?<div style={{fontSize:10,color:"#8A978F"}}>Lot {a.lot_numero}</div>:null}</td>
                      <td>{a.client_nom||"—"}{(()=>{ const fa=a.facture_id?factures.find(f=>String(f.id)===String(a.facture_id)):null; return fa?<div style={{fontFamily:"monospace",fontSize:11,color:"#1E8A32"}}>{fa.numero}</div>:null; })()}</td>
                      <td>{a.raison||"—"}</td>
                      <td style={{fontSize:12}}>
                        <span style={{color:a.remplacement==="Remplacée"?"#1E7A2D":a.remplacement==="À remplacer"?"#D6453D":"#5B6B62",fontWeight:700}}>{a.remplacement||"—"}</span>
                        {a.remplacement==="Remplacée"&&a.remplace_le&&<div style={{fontSize:10,color:"#8A978F"}}>le {fmtDate(a.remplace_le)}{a.remplace_par?" par "+a.remplace_par:""}</div>}
                        {a.remplacement==="À remplacer"&&<div><button className="btn btn-ghost btn-sm" style={{marginTop:4}} onClick={()=>marquerAvarieRemplacee(a.id,"Remplacée")}>✓ Marquer remplacée</button></div>}
                      </td>
                      <td>{a.acteur||"—"}</td>
                    </tr>
                  ))}
                </tbody></table>{!avaries.length&&<div style={{padding:26,textAlign:"center",color:"#5B6B62"}}>Aucune avarie enregistrée.</div>}</div></div>
              </div>
            );
          })()}

          {page==="prospection"&&(isDirecteur()||can("prospection")||can("ventes")||can("objectifs"))&&(()=>{
            const total=prospections.reduce((s,p)=>s+toFiniteNumber(p.quantite),0);
            const coutTotal=prospections.reduce((s,p)=>s+toFiniteNumber(p.cout_total),0);
            const villesDispo=[...new Set(depotStocks.map(s=>s.ville))].sort();
            const stockDispo = prospectionForm.ville && prospectionForm.produit
              ? depotStocks.find(s=>s.ville===prospectionForm.ville&&s.produit_nom===prospectionForm.produit)?.quantite||0
              : null;
            return (
              <div>
                <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:6}}>Prospection</div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:20}}>Bouteilles données en échantillon à des prospects — sort du stock de la ville, compte comme dépense marketing, ne compte pas comme une vente.</div>

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(2,1fr)",gap:12,marginBottom:20}}>
                  <div className="card" style={{padding:16}}><div style={{fontSize:11,textTransform:"uppercase",color:"#5B6B62"}}>Bouteilles données</div><div style={{fontSize:25,fontWeight:800,color:"#1E8A32",marginTop:5}}>{NUM(total)} btl</div></div>
                  <div className="card" style={{padding:16}}><div style={{fontSize:11,textTransform:"uppercase",color:"#5B6B62"}}>Coût total (compté en dépense)</div><div style={{fontSize:25,fontWeight:800,color:"#D6453D",marginTop:5}}>{FCFA(coutTotal)}</div></div>
                </div>

                <div className="card" style={{marginBottom:20}}>
                  <div style={{fontSize:14,color:"#1E8A32",fontWeight:700,marginBottom:12}}>Nouvelle prospection</div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:10}}>
                    <div className="fld"><label className="lbl">Ville *</label>
                      <select className="inp" value={prospectionForm.ville} onChange={e=>setProspectionForm(f=>({...f,ville:e.target.value}))} disabled={!isDirecteur()&&!!session.ville}>
                        <option value="">Choisir...</option>
                        {villesDispo.map(v=><option key={v}>{v}</option>)}
                      </select>
                    </div>
                    <div className="fld"><label className="lbl">Produit *</label>
                      <select className="inp" value={prospectionForm.produit} onChange={e=>setProspectionForm(f=>({...f,produit:e.target.value}))}>
                        <option value="">Choisir...</option>
                        {produits.map(p=><option key={p.id} value={p.nom}>{p.code?p.code+" · ":""}{p.nom}</option>)}
                      </select>
                    </div>
                    <div className="fld"><label className="lbl">Quantité *</label><input type="number" min="1" className="inp" value={prospectionForm.quantite} onChange={e=>setProspectionForm(f=>({...f,quantite:e.target.value}))} /></div>
                    <div className="fld"><label className="lbl">Prospect / client potentiel</label><input type="text" className="inp" value={prospectionForm.prospectNom} onChange={e=>setProspectionForm(f=>({...f,prospectNom:e.target.value}))} /></div>
                    <div className="fld" style={{gridColumn:isMobile?"1":"span 2"}}><label className="lbl">Notes</label><input type="text" className="inp" value={prospectionForm.notes} onChange={e=>setProspectionForm(f=>({...f,notes:e.target.value}))} /></div>
                  </div>
                  {stockDispo!==null&&<div style={{fontSize:11,color:"#5B6B62",marginTop:8}}>Disponible à {prospectionForm.ville} : <strong style={{color:"#0F1F17"}}>{NUM(stockDispo)} btl</strong></div>}
                  <button className="btn btn-gold" style={{marginTop:14}} disabled={prospectionSaving} onClick={saveProspection}>{prospectionSaving?"...":"Enregistrer"}</button>
                </div>

                <div className="card" style={{padding:0}}><div className="table-wrap"><table className="tbl"><thead><tr><th>Date</th><th>Ville</th><th>Produit</th><th>Qté</th><th>Prospect</th><th>Coût</th><th>Par</th></tr></thead><tbody>
                  {prospections.map(p=>(
                    <tr key={p.id}>
                      <td>{fmtDate(p.date)}</td>
                      <td>{p.ville}</td>
                      <td>{p.produit_nom}</td>
                      <td style={{color:"#1E8A32",fontWeight:700}}>{NUM(p.quantite)}</td>
                      <td>{p.prospect_nom||"—"}</td>
                      <td style={{color:"#D6453D"}}>{FCFA(p.cout_total)}</td>
                      <td>{p.employe||"—"}</td>
                    </tr>
                  ))}
                </tbody></table>{!prospections.length&&<div style={{padding:26,textAlign:"center",color:"#5B6B62"}}>Aucune prospection enregistrée.</div>}</div></div>
              </div>
            );
          })()}

          {page==="stock-villes"&&(isDirecteur()||can("stock")||can("livraisons")||can("stock-villes"))&&(
            <div>
              <div style={{fontSize:26,fontWeight:700,letterSpacing:"-.02em",color:"#0F1F17",marginBottom:6}}>Parcours du stock</div>
              {/* Fusion du 02/10/2026 : "Livraisons vers dépôts" n'est plus une entrée séparée du */}
              {/* menu — on y accède d'ici, comme un second onglet de ce même écran. */}
            {(isDirecteur()||can("livraisons"))&&livraisons.some(l=>l.statut==="En transit")&&(()=>{
              const att = livraisons.filter(l=>l.statut==="En transit");
              return (
                <div className="card" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",marginBottom:14,borderColor:"#BFE3C6",background:"#F6FBF7"}}>
                  <div style={{fontSize:13,color:"#0F1F17",minWidth:0}}>
                    <strong>🚚 {att.length} livraison{att.length>1?"s":""} à réceptionner</strong>
                    <div style={{fontSize:11,color:"#5B6B62",marginTop:3}}>{att.slice(0,3).map(l=>l.numero+" → "+(l.depot_nom||l.ville)+(l.receptionnaire_prevu?" (par "+l.receptionnaire_prevu+")":"")).join(" · ")}{att.length>3?" …":""}</div>
                  </div>
                  <button className="btn btn-gold btn-sm" onClick={()=>setPage("livraisons")}>Voir et confirmer</button>
                </div>
              );
            })()}
            <div className="module-tabs" style={{marginBottom:14}}>
                <button className="active">Vue d'ensemble</button>
                <button onClick={()=>setPage("livraisons")}>Livraisons</button>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10,marginBottom:20}}>
                <div style={{fontSize:12,color:"#5B6B62"}}>Du magasin central de Dschang jusqu'aux magasins de chaque ville.</div>
                {canTransfertVilles()&&<button className="btn btn-gold btn-sm" onClick={()=>{setLivraisonForm(f=>({...f,origineType:"ville",villeOrigine:""}));setPage("nouvelle-livraison");}}>⇄ Transférer entre villes (dépannage)</button>}
              </div>

              {/* PRODUCTION TOTALE HISTORIQUE */}
              {(() => {
                const totalProduction = lots.reduce((s,l)=>s+toFiniteNumber(l.quantite),0);
                const parProduit = {};
                lots.forEach(l=>{ parProduit[l.produit] = (parProduit[l.produit]||0) + toFiniteNumber(l.quantite); });
                return (
                  <div className="card" style={{marginBottom:22,background:"linear-gradient(135deg,rgba(47,158,63,0.064),rgba(47,158,63,0.016))"}}>
                    <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:4}}>Production totale historique — Dschang</div>
                    <div style={{fontSize:28,color:"#1E8A32",fontWeight:700,marginBottom:10}}>{NUM(totalProduction)} <span style={{fontSize:13,color:"#5B6B62",fontWeight:400}}>bouteilles produites depuis le début ({NUM(lots.length)} lots)</span></div>
                    <div style={{display:"flex",gap:18,flexWrap:"wrap"}}>
                      {Object.entries(parProduit).map(([nom,qte])=>(
                        <div key={nom} style={{fontSize:12,color:"#5B6B62"}}>{nom} : <strong style={{color:"#0F1F17"}}>{NUM(qte)}</strong></div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ÉTAPE 1 : MAGASIN CENTRAL DSCHANG */}
              <div style={{fontSize:13,color:"#1E8A32",fontWeight:700,marginBottom:8,display:"flex",alignItems:"center",gap:6}}><Warehouse size={15}/> 1. Magasin central — Dschang</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(220px,1fr))",gap:12,marginBottom:22}}>
                {produits.map(p=>{
                  // Correctif du 30/09/2026 (audit — vague 7) : si la ligne de dépôt "Dschang"
                  // n'a jamais été créée pour ce produit, on retombe sur le stock central
                  // (produits.stock) plutôt que d'afficher 0 — cet écran affichait alors une
                  // valeur différente de "Stock & Produits" pour le même produit. La migration
                  // SQL de cette vague crée désormais cette ligne dès la prochaine opération de
                  // stock (et a rattrapé celles déjà manquantes) ; ce filet reste par sécurité.
                  // Correctif du 02/10/2026 (fusion partie 2) : le magasin central a sa propre
                  // valeur de ville ("Magasin central"), distincte du dépôt Dschang — qui est
                  // désormais un dépôt de ville comme un autre (voir étape 3 ci-dessous).
                  const ligneDschang = depotStocks.find(s=>s.ville==="Magasin central"&&s.produit_nom===p.nom);
                  const stockDschang = ligneDschang ? (ligneDschang.quantite||0) : (p.stock||0);
                  return (
                  <div key={p.id} className="card" style={{padding:14}}>
                    <div style={{fontSize:12,color:"#0F1F17",marginBottom:4}}>{p.nom}{p.code&&<span style={{marginLeft:8,fontSize:11,fontWeight:700,color:"#5B6B62",background:"#EEF2EF",borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap"}}>{p.code}</span>}</div>
                    {/* Correctif du 02/10/2026 : le bouton de modification manuelle du stock */}
                    {/* central a été retiré — c'est précisément ce qui avait permis au stock */}
                    {/* central de diverger silencieusement de la réalité (incident du 02/10). */}
                    {/* Le magasin central ne doit plus bouger que par la production (lots) et */}
                    {/* les livraisons qui en partent — jamais par une saisie manuelle directe. */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                      <div style={{fontSize:19,color:"#1E8A32",fontWeight:700}}>{NUM(stockDschang)} <span style={{fontSize:11,color:"#5B6B62",fontWeight:400}}>btl</span></div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* ÉTAPE 2 : EN TRANSIT */}
              {(() => {
                const enTransit = livraisons.filter(l=>l.statut==="En transit");
                const parVilleProduit = {};
                enTransit.forEach(l=>{
                  const lignes = (Array.isArray(l.lignes)&&l.lignes.length) ? l.lignes : [{produit:l.produit,quantite:l.quantite}];
                  lignes.forEach(lg=>{
                    const key = l.ville+"__"+lg.produit;
                    parVilleProduit[key] = parVilleProduit[key] || {ville:l.ville, produit:lg.produit, qte:0};
                    parVilleProduit[key].qte += toFiniteNumber(lg.quantite);
                  });
                });
                const rows = Object.values(parVilleProduit);
                return (
                  <>
                    <div style={{fontSize:13,color:"#1E8A32",fontWeight:700,marginBottom:8,display:"flex",alignItems:"center",gap:6}}><Truck size={15}/> 2. En transit</div>
                    {rows.length===0?(
                      <div className="card" style={{textAlign:"center",padding:20,color:"#5B6B62",fontSize:12,marginBottom:22}}>Aucune livraison en cours de route actuellement.</div>
                    ):(
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(220px,1fr))",gap:12,marginBottom:22}}>
                        {rows.map(r=>(
                          <div key={r.ville+r.produit} className="card" style={{padding:14,borderColor:"rgba(233,162,157,0.27)"}}>
                            <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1,color:"#D6453D",marginBottom:3}}>→ {r.ville}</div>
                            <div style={{fontSize:12,color:"#0F1F17",marginBottom:4}}>{r.produit}</div>
                            <div style={{fontSize:17,color:"#D6453D",fontWeight:700}}>{NUM(r.qte)} <span style={{fontSize:11,color:"#5B6B62",fontWeight:400}}>btl</span></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ÉTAPE 3 : MAGASIN DE LA VILLE */}
              {/* Dschang apparaît maintenant ici comme une ville normale (fusion partie 2, 02/10/2026) : */}
              {/* seul le magasin central ("Magasin central") reste à part, affiché à l'étape 1. */}
              <div style={{fontSize:13,color:"#1E8A32",fontWeight:700,marginBottom:8,display:"flex",alignItems:"center",gap:6}}><MapPin size={15}/> 3. Magasin de la ville (stock actuel + total déjà reçu)</div>
              {depotStocks.filter(s=>s.ville!=="Magasin central").length===0?(
                <div className="card" style={{textAlign:"center",padding:40,color:"#5B6B62"}}>Aucun stock reçu et confirmé pour l'instant dans une ville.</div>
              ):(
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
                  {[...new Set(depotStocks.filter(s=>s.ville!=="Magasin central").map(s=>s.ville))].sort().map(ville=>{
                    const lignesVille = depotStocks.filter(s=>s.ville===ville);
                    const total = lignesVille.reduce((s,l)=>s+toFiniteNumber(l.quantite),0);
                    const totalRecuVille = mouvementsStockLog.filter(m=>m.ville===ville&&m.delta>0).reduce((s,m)=>s+m.delta,0);
                    return (
                      <div key={ville} className="card">
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <div style={{fontSize:15,fontWeight:700,color:"#0F1F17"}}>{ville}</div>
                          <span className="badge bg2">{NUM(total)} btl en stock</span>
                        </div>
                        <div style={{fontSize:10,color:"#5B6B62",marginBottom:8}}>Total déjà reçu depuis le début : <strong style={{color:"#2F6FDE"}}>{NUM(totalRecuVille)} btl</strong></div>
                        <hr className="divider"/>
                        {lignesVille.map(l=>{
                          const recuProduit = mouvementsStockLog.filter(m=>m.ville===ville&&m.produit_nom===l.produit_nom&&m.delta>0).reduce((s,m)=>s+m.delta,0);
                          return (
                            <div key={l.id||l.produit_nom} style={{fontSize:12,color:"#5B6B62",display:"flex",justifyContent:"space-between",padding:"4px 0"}}>
                              <span>{l.produit_nom}</span>
                              <span><strong style={{color:"#0F1F17"}}>{NUM(l.quantite)}</strong> <span style={{fontSize:10,color:"#2F6FDE"}}>(reçu {NUM(recuProduit)})</span></span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* VENTES PAR MOIS ET PAR VILLE */}
              {(() => {
                const mk = d => { const dt=new Date(d); return Number.isNaN(dt.getTime())?"?":dt.getFullYear()+"-"+String(dt.getMonth()+1).padStart(2,"0"); };
                const villesVente = [...new Set(ventes.map(v=>v.ville).filter(Boolean))].sort();
                const mois = [...new Set(ventes.map(v=>mk(v.date)))].sort().reverse();
                const cell = {};
                ventes.forEach(v=>{
                  const key = mk(v.date)+"__"+(v.ville||"");
                  cell[key] = cell[key] || {qte:0,montant:0};
                  cell[key].qte += toFiniteNumber(v.qte);
                  cell[key].montant += toFiniteNumber(v.montant);
                });
                const moisLabel = m => { if(m==="?") return "Date inconnue"; const [an,mo]=m.split("-"); return ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"][parseInt(mo)-1]+" "+an; };
                return (
                  <>
                    <div style={{fontSize:13,color:"#1E8A32",fontWeight:700,margin:"26px 0 8px",display:"flex",alignItems:"center",gap:6}}><TrendingUp size={15}/> Ventes par mois et par ville</div>
                    {mois.length===0?(
                      <div className="card" style={{textAlign:"center",padding:20,color:"#5B6B62",fontSize:12}}>Aucune vente enregistrée pour l'instant.</div>
                    ):(
                      <div className="card scr">
                        <table className="tbl">
                          <thead><tr>
                            <th>Mois</th>
                            {villesVente.map(v=><th key={v}>{v}</th>)}
                            <th>Total</th>
                          </tr></thead>
                          <tbody>
                            {mois.map(m=>{
                              const totalMoisMontant = villesVente.reduce((s,v)=>s+(cell[m+"__"+v]?.montant||0),0);
                              const totalMoisQte = villesVente.reduce((s,v)=>s+(cell[m+"__"+v]?.qte||0),0);
                              return (
                                <tr key={m}>
                                  <td style={{fontWeight:600}}>{moisLabel(m)}</td>
                                  {villesVente.map(v=>{
                                    const c = cell[m+"__"+v];
                                    return <td key={v} style={{color:"#5B6B62"}}>{c?<>{NUM(c.qte)} btl<div style={{fontSize:10,color:"#1E8A32"}}>{FCFA(c.montant)}</div></>:"—"}</td>;
                                  })}
                                  <td style={{fontWeight:700,color:"#0F1F17"}}>{NUM(totalMoisQte)} btl<div style={{fontSize:10,color:"#1E8A32"}}>{FCFA(totalMoisMontant)}</div></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Journal des mouvements de stock : qui a fait quoi, quand et d'où ça vient */}
              {(isDirecteur()||can("stock"))&&(()=>{
                const tous = [...mouvementsStockLog].sort((a,b)=>String(b.created_at).localeCompare(String(a.created_at)));
                const villesMvt = [...new Set(mouvementsStockLog.map(m=>m.ville).filter(Boolean))].sort();
                const filtres = tous.filter(m=>(!mvtFiltre.ville||m.ville===mvtFiltre.ville)&&(!mvtFiltre.sens||(mvtFiltre.sens==="sorties"?m.delta<0:m.delta>0)));
                const quand = d => { try { return new Date(d).toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"2-digit",hour:"2-digit",minute:"2-digit"}); } catch(e) { return String(d||""); } };
                return (
                  <div style={{marginTop:26}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:8}}>
                      <div style={{fontSize:13,color:"#1E8A32",fontWeight:700,display:"flex",alignItems:"center",gap:6}}><FileText size={15}/> Journal des mouvements de stock</div>
                      <button className="btn btn-ghost btn-sm" onClick={()=>setShowMvtJournal(v=>!v)}>{showMvtJournal?"Masquer":"Afficher ("+NUM(tous.length)+" mouvements)"}</button>
                    </div>
                    {showMvtJournal&&(
                      <>
                        <div style={{fontSize:11,color:"#5B6B62",marginBottom:8}}>Chaque entrée ou sortie de stock, avec la personne qui l'a faite. Les mouvements d'avant la mise en place de cette trace n'ont pas toujours d'auteur (—).</div>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
                          <select className="inp" style={{maxWidth:200}} value={mvtFiltre.ville} onChange={e=>setMvtFiltre(f=>({...f,ville:e.target.value}))}>
                            <option value="">Tous les lieux</option>
                            {villesMvt.map(v=><option key={v} value={v}>{v}</option>)}
                          </select>
                          <select className="inp" style={{maxWidth:200}} value={mvtFiltre.sens} onChange={e=>setMvtFiltre(f=>({...f,sens:e.target.value}))}>
                            <option value="">Entrées et sorties</option>
                            <option value="sorties">Sorties seulement</option>
                            <option value="entrees">Entrées seulement</option>
                          </select>
                        </div>
                        <div className="card scr">
                          <table className="tbl">
                            <thead><tr><th>Date</th><th>Lieu</th><th>Produit</th><th>Avant → après</th><th>Variation</th><th>Fait par</th><th>Origine</th></tr></thead>
                            <tbody>
                              {filtres.slice(0,300).map(m=>(
                                <tr key={m.id}>
                                  <td style={{whiteSpace:"nowrap",color:"#5B6B62"}}>{quand(m.created_at)}</td>
                                  <td>{m.ville}</td>
                                  <td>{m.produit_nom}</td>
                                  <td style={{whiteSpace:"nowrap"}}>{NUM(m.ancienne_quantite)} → {NUM(m.nouvelle_quantite)}</td>
                                  <td style={{fontWeight:700,color:m.delta<0?"#D6453D":"#1E7A2D"}}>{m.delta>0?"+":""}{NUM(m.delta)}</td>
                                  <td style={{fontWeight:600}}>{m.auteur||"—"}</td>
                                  <td style={{color:"#5B6B62",fontSize:12}}>{m.origine||"—"}</td>
                                </tr>
                              ))}
                              {filtres.length===0&&<tr><td colSpan={7} style={{textAlign:"center",color:"#5B6B62",padding:16}}>Aucun mouvement.</td></tr>}
                            </tbody>
                          </table>
                          {filtres.length>300&&<div style={{fontSize:11,color:"#5B6B62",padding:8}}>Les 300 plus récents sur {NUM(filtres.length)} sont affichés.</div>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

        </main>
      </div>

      {/* ══ MODALS ══ */}
      {modal&&(
        <div className="overlay" onClick={closeModal}>
          <div className="modal" onClick={e=>e.stopPropagation()}>

            {modal==="paiement"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:6}}>{form.editId?"Corriger la preuve refusée":"Déposer une preuve de paiement"}</div>
              <div style={{fontSize:12,color:"#5B6B62",marginBottom:18}}>Le directeur devra valider le justificatif avant que la facture passe à « Payée ».</div>
              {form.editId&&<div style={{background:"#FDECEA",border:"1px solid #F3C2BE",borderRadius:5,padding:10,marginBottom:14,fontSize:12,color:"#8A2A24"}}><strong>Motif du refus :</strong> {form.motif_refus||"(non précisé)"}<div style={{marginTop:4,color:"#5B6B62"}}>Corrigez les informations puis renvoyez : la preuve repart « En attente validation ».</div></div>}
              <div className="fld"><label className="lbl">Facture *</label>
                <select className="inp" disabled={!!form.editId} value={form.facture_numero||""} onChange={e=>selectPaiementFacture(e.target.value)}>
                  <option value="">Choisir une facture...</option>
                  {factures.filter(f=>resteFacture(f)>0).map(f=><option key={f.id} value={f.numero}>{f.numero} — {f.client_nom} — reste {FCFA(resteFacture(f))}</option>)}
                </select>
              </div>
              {form.facture_numero&&<div style={{background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:5,padding:10,marginBottom:14,fontSize:12}}>
                <div><span style={{color:"#5B6B62"}}>Client : </span>{form.client_nom||"—"}</div>
                <div><span style={{color:"#5B6B62"}}>Total facture : </span>{FCFA(form.montant_facture)}</div>
                <div><span style={{color:"#5B6B62"}}>Déjà validé : </span>{FCFA(form.deja_paye)}</div>
                <div><span style={{color:"#5B6B62"}}>Reste dû : </span><strong style={{color:"#1E8A32"}}>{FCFA(form.restant)}</strong></div>
              </div>}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div className="fld"><label className="lbl">Date du versement *</label><input type="date" className="inp" value={form.date_versement||today()} onChange={e=>setForm({...form,date_versement:e.target.value})}/></div>
                <div className="fld"><label className="lbl">Montant payé (FCFA) *</label><input type="number" min="1" max={form.restant||undefined} className="inp" value={form.montant||""} onChange={e=>setForm({...form,montant:e.target.value})}/></div>
              </div>
              <div className="fld"><label className="lbl">Moyen de paiement *</label>
                <select className="inp" value={form.moyen||""} onChange={e=>setForm({...form,moyen:e.target.value})}>
                  <option value="">Choisir...</option>{MOYENS_REGLEMENT.filter(Boolean).map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Référence du paiement (pré-remplie avec la facture, modifiable)</label><input className="inp" value={form.reference_virement||""} onChange={e=>setForm({...form,reference_virement:e.target.value})} placeholder="Ex. F00162 ou numéro de transaction"/></div>
              <div className="fld"><label className="lbl">{form.editId?"Scan ou photo de la preuve (facultatif : remplace l'actuelle)":"Scan ou photo de la preuve *"}</label>
                {form.editId&&!form.preuveFile&&form.preuve_actuelle&&<div style={{display:"flex",alignItems:"center",gap:10,background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:5,padding:10,marginBottom:8,fontSize:12}}><FileText size={16} color="#1E8A32"/><div style={{flex:1}}>Preuve actuelle conservée si vous n'en ajoutez pas une nouvelle.</div><button type="button" className="btn btn-ghost btn-sm" onClick={()=>openPaiementProof(form.preuve_actuelle)}>Voir</button></div>}
                {form.preuveFile ? (
                  <div style={{display:"flex",alignItems:"center",gap:10,background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:5,padding:10}}>
                    <FileText size={16} color="#1E8A32"/>
                    <div style={{flex:1,fontSize:12,color:"#0F1F17",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{form.preuveFile.name}</div>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={()=>setForm({...form,preuveFile:null})}>Changer</button>
                  </div>
                ) : (
                  <div style={{display:"flex",gap:8}}>
                    {estAppareilMobile()&&<button type="button" className="btn btn-gold" style={{flex:1}} onClick={()=>setAutoScanOpen(true)}><Eye size={14}/> Scanner le document</button>}
                    <label className="btn btn-ghost" style={{flex:1,textAlign:"center",cursor:"pointer",margin:0}}>
                      {estAppareilMobile()?"Photo classique":"Importer un fichier"}
                      <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" {...(estAppareilMobile()?{capture:"environment"}:{})} style={{display:"none"}} onChange={async e=>{
                        const f=e.target.files?.[0]||null;
                        e.target.value="";
                        if(!f) return;
                        try { const pdf=await imageFileToPdf(f); setForm(prev=>({...prev,preuveFile:pdf})); }
                        catch(err){ showToast("Fichier illisible : "+(err?.message||err),false); }
                      }}/>
                    </label>
                  </div>
                )}
                <div style={{fontSize:11,color:"#5B6B62",marginTop:5}}>{estAppareilMobile()
                  ? "« Scanner le document » ouvre la caméra : le document est repéré et recouvert en bleu, puis photographié automatiquement, redressé et enregistré en PDF. PDF ou image — maximum 10 Mo."
                  : "Le scan par caméra n'est possible que depuis un téléphone ou une tablette. Depuis cet ordinateur, importe un PDF ou une image existante (les images sont converties en PDF). Maximum 10 Mo."}</div>
              </div>
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={savePaiement} disabled={paiementSaving}>{paiementSaving?"Envoi...":(form.editId?"Renvoyer pour validation":"Envoyer pour validation")}</button>
                <button className="btn btn-ghost" onClick={closeModal} disabled={paiementSaving}>Annuler</button>
              </div>
            </>}

            {modal==="vente"&&(()=>{
              const lignesVente = form.lignesVente||[];
              const totalVente = lignesVente.reduce((s,l)=>{const p=produits.find(x=>x.nom===l.produit);return s+(p?l.qte*p.prix:0);},0);
              return <>
                <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>Nouvelle vente</div>
                <div className="fld"><label className="lbl">Date</label><input type="date" className="inp" value={form.date||today()} onChange={e=>setForm({...form,date:e.target.value})} /></div>
                <div className="fld"><label className="lbl">Client *</label>
                  <select className="inp" value={form.client||""} onChange={e=>{
                    const clientNom=e.target.value;
                    const autoDepot=getClientDepot(clientNom);
                    setForm(f=>({...f,client:clientNom,depotId:autoDepot?.id||""}));
                  }}>
                    <option value="">Choisir...</option>{clients.map(c=><option key={c.id} value={c.nom}>{c.nom}{c.ville?" — "+c.ville:""}</option>)}
                  </select>
                </div>
                <div className="fld"><label className="lbl">Ville / dépôt de vente *</label>
                  <select className="inp" value={form.depotId||""} onChange={e=>setForm(f=>({...f,depotId:e.target.value,_nvProd:"",_nvQte:""}))}>
                    <option value="">Choisir le stock à utiliser...</option>
                    {/* Le magasin central n'est pas un point de vente — on ne vend que depuis un dépôt de ville. */}
                    {depots.filter(d=>d.ville!=="Magasin central").map(d=><option key={d.id} value={d.id}>{d.nom} — {d.ville}</option>)}
                  </select>
                  {form.client&&getClientDepot(form.client)&&String(getClientDepot(form.client)?.id)===String(form.depotId)&&(
                    <div style={{fontSize:11,color:"#1E7A2D",marginTop:5}}>✓ Dépôt sélectionné automatiquement selon la ville du client.</div>
                  )}
                </div>
                <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:8,marginTop:4}}>Produits vendus</div>
                {lignesVente.map((l,i)=>{
                  const p=produits.find(x=>x.nom===l.produit);
                  return (
                    <div key={i} className="lr">
                      <div style={{flex:2,fontSize:12}}>{l.produit}</div>
                      <div style={{color:"#5B6B62"}}>{NUM(l.qte)} btl</div>
                      <div style={{color:"#1E8A32",fontWeight:600}}>{p?FCFA(l.qte*p.prix):"—"}</div>
                      <button onClick={()=>setForm(f=>({...f,lignesVente:f.lignesVente.filter((_,idx)=>idx!==i)}))} style={{background:"none",border:"none",color:"#D6453D",cursor:"pointer"}}>✕</button>
                    </div>
                  );
                })}
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr auto",gap:6,marginTop:8,alignItems:"end"}}>
                  <div><label className="lbl">Produit</label>
                    <select className="inp" value={form._nvProd||""} onChange={e=>setForm(f=>({...f,_nvProd:e.target.value}))}>
                      <option value="">Choisir...</option>{produits.map(p=>{
                        const cityStock=form.depotId?getDepotStock(form.depotId,p.nom):0;
                        return <option key={p.id} value={p.nom} disabled={!form.depotId||cityStock<=0}>{p.code?p.code+" · ":""}{p.nom} (stock ville: {cityStock})</option>;
                      })}
                    </select>
                  </div>
                  <div><label className="lbl">Quantité</label><input type="number" className="inp" value={form._nvQte||""} onChange={e=>setForm(f=>({...f,_nvQte:e.target.value}))} min="1" /></div>
                  <button className="btn btn-gold btn-sm" style={{alignSelf:"flex-end"}} onClick={()=>{
                    const nvProd=form._nvProd||"", nvQte=form._nvQte||"";
                    if (!nvProd||!nvQte) return;
                    const prod=produits.find(p=>p.nom===nvProd);
                    if (!form.depotId) return showToast("Choisissez d'abord la ville / le dépôt",false);
                    const cityStock=getDepotStock(form.depotId,nvProd);
                    if (parseInt(nvQte)>cityStock) return showToast("Stock ville insuffisant ("+cityStock+" btl)",false);
                    setForm(f=>({...f,lignesVente:[...(f.lignesVente||[]),{produit:nvProd,qte:parseInt(nvQte)}],_nvProd:"",_nvQte:""}));
                  }}>+</button>
                </div>
                {lignesVente.length>0&&(
                  <div style={{marginTop:14,borderTop:"1px solid #E3E9E6",paddingTop:10,display:"flex",justifyContent:"space-between",fontSize:16,color:"#1E8A32",fontWeight:700}}>
                    <span>Total</span><span>{FCFA(totalVente)}</span>
                  </div>
                )}
                <div style={{display:"flex",gap:9,marginTop:18}}>
                  <button className="btn btn-gold" style={{flex:1}} onClick={saveVente} disabled={loading}>{loading?"...":"Enregistrer"}</button>
                  <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
                </div>
              </>;
            })()}

            {modal==="codes"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:6}}>Codes comptables</div>
              <div style={{fontSize:11,color:"#5B6B62",marginBottom:14}}>Chaque dépense, achat et salaire porte un code à 4 chiffres. Vous pouvez ajouter un code ou renommer un libellé ; un code déjà utilisé ne se supprime pas, on le désactive.</div>
              <div style={{maxHeight:"46vh",overflowY:"auto",border:"1px solid #E3E9E6",borderRadius:12,marginBottom:14}}>
                {groupesCodes(codes).map(g=>(
                  <div key={g.famille}>
                    <div style={{background:"#F1F5F3",padding:"6px 12px",fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"#1E7A2D"}}>{g.famille}</div>
                    {g.items.map(c=>(
                      <div key={c.code} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",borderTop:"1px solid #EEF2F0",opacity:c.actif?1:.5}}>
                        <b style={{width:46}}>{c.code}</b><span style={{flex:1,minWidth:0}}>{c.libelle}</span>
                        <button className="btn btn-ghost btn-sm" onClick={()=>renommerCode(c)} aria-label={"Renommer le code "+c.code}>✏</button>
                        <button className="btn btn-ghost btn-sm" onClick={()=>basculerCode(c)}>{c.actif?"Désactiver":"Réactiver"}</button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div style={{fontSize:12,fontWeight:700,marginBottom:6}}>Ajouter un code</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <input className="inp" style={{width:90}} inputMode="numeric" maxLength={4} placeholder="Code" value={codeNouveau.code} onChange={e=>setCodeNouveau(v=>({...v,code:e.target.value.replace(/[^0-9]/g,"")}))} />
                <input className="inp" style={{flex:1,minWidth:160}} placeholder="Libellé" value={codeNouveau.libelle} onChange={e=>setCodeNouveau(v=>({...v,libelle:e.target.value}))} />
                <button className="btn btn-gold" onClick={ajouterCode}>Ajouter</button>
              </div>
              <div style={{fontSize:11,color:"#5B6B62",marginTop:6}}>Le premier chiffre donne la famille : 1 matières premières · 2 emballage · 3 transport · 4 équipement · 5 charges générales · 6 personnel · 7 immobilisations.</div>
              <div style={{display:"flex",gap:9,marginTop:16}}><button className="btn btn-ghost" style={{flex:1}} onClick={closeModal}>Fermer</button></div>
            </>}

            {modal==="depense"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:6}}>{form.id?"Modifier la dépense":form.demande_id?"Enregistrer la dépense acceptée":accordRequis(form.code)?"Demande de dépense":"Autre dépense"}</div>
              <div style={{fontSize:11,color:"#5B6B62",marginBottom:18}}>Pour tout ce qui n'est pas un achat de matière première (utilise le formulaire dédié ci-dessus). "Personnel (hors fiche de paie)" reste ici pratique à saisir, mais compte comme un salaire dans les totaux — pas une dépense diverse.</div>
              {form.demande_id&&<div style={{background:"#E8F5EA",border:"1px solid #BFE3C6",color:"#1E5A28",borderRadius:10,padding:"9px 12px",fontSize:12.5,marginBottom:12}}>✓ Le directeur a accepté cette dépense (jusqu'à {FCFA((demandes.find(x=>String(x.id)===String(form.demande_id))||{}).montant_accorde||form.montant)}). Saisissez le montant réellement payé.</div>}
              {!form.id&&!form.demande_id&&!isDirecteur()&&(accordRequis(form.code)
                ? <div style={{background:"#FFF4DD",border:"1px solid #F0D9A0",color:"#7A4B00",borderRadius:10,padding:"9px 12px",fontSize:12.5,marginBottom:12}}>Cette dépense demande l'accord du directeur <b>avant</b> d'être faite. En validant, une demande lui est envoyée ; vous serez prévenu de sa réponse.</div>
                : (form.code?<div style={{background:"#F1FAF2",border:"1px solid #BFE3C6",color:"#1E5A28",borderRadius:10,padding:"9px 12px",fontSize:12.5,marginBottom:12}}>Ce type de dépense ne demande pas d'accord : elle sera enregistrée directement.</div>
                           :<div style={{fontSize:11.5,color:"#5B6B62",marginBottom:10}}>Selon le code choisi, la dépense peut demander l'accord du directeur avant d'être enregistrée.</div>))}
              <div className="fld"><label className="lbl">Date</label><input type="date" className="inp" value={form.date||today()} onChange={e=>setForm({...form,date:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Libellé</label><input type="text" className="inp" value={form.libelle||""} onChange={e=>setForm({...form,libelle:e.target.value})} /></div>
              {codes.length>0&&<div className="fld"><label className="lbl">Code</label>
                <select className="inp" disabled={!!form.demande_id} value={form.code||""} onChange={e=>{const c=codeInfo(e.target.value);setForm({...form,code:e.target.value,categorie:c?c.categorie:form.categorie});}}>
                  <option value="">Choisir un code…</option>
                  {groupesCodes(codesPourSaisie).map(g=><optgroup key={g.famille} label={g.famille}>{g.items.map(c=><option key={c.code} value={c.code}>{c.code} · {c.libelle}</option>)}</optgroup>)}
                </select>
              </div>}
              <div className="fld" style={{display:codes.length>0?"none":undefined}}><label className="lbl">Catégorie</label>
                <select className="inp" value={form.categorie||""} onChange={e=>setForm({...form,categorie:e.target.value})}>
                  <option value="">Choisir...</option>{["Électricité / eau","Loyer","Emballage","Charges fixes","Marketing","Logistique","Personnel (hors fiche de paie)","Autre"].map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Fournisseur</label>
                <select className="inp" value={form.fournisseur||""} onChange={e=>setForm({...form,fournisseur:e.target.value})}>
                  <option value="">Aucun</option>{fournisseurs.map(f=><option key={f.id} value={f.nom}>{f.nom}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Montant (FCFA)</label><input type="number" className="inp" value={form.montant||""} onChange={e=>setForm({...form,montant:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Payé en</label>
                <select className="inp" value={form.moyen||"Espèces"} onChange={e=>setForm({...form,moyen:e.target.value})}>
                  {["Espèces","Virement","Mobile Money","Autre"].map(m=><option key={m} value={m}>{m}</option>)}
                </select>
                <div style={{fontSize:10,color:"#5B6B62",marginTop:4}}>Seules les dépenses payées en espèces sortent de la caisse.</div>
              </div>
              {!form.id&&!form.demande_id&&accordRequis(form.code)&&<div className="fld"><label className="lbl">Pourquoi cette dépense ? (explication pour le directeur)</label><textarea className="inp" rows={3} value={form.explication||""} onChange={e=>setForm({...form,explication:e.target.value})} style={{width:"100%",resize:"vertical"}} placeholder="Ex. : plein du véhicule pour la livraison à Bafoussam jeudi" /></div>}
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={saveDepense} disabled={loading}>{loading?"...":form.id?"Mettre à jour":(!form.demande_id&&accordRequis(form.code))?"Envoyer la demande au directeur":"Enregistrer"}</button>
                <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
              </div>
            </>}

            {modal==="client"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier client":"Nouveau client"}</div>
              {form.id&&form.code&&<div style={{fontSize:12,color:"#5B6B62",marginBottom:10}}>Code client : <strong style={{color:"#0F1F17"}}>{form.code}</strong> (ne change jamais, même si le nom change)</div>}
              <div className="fld"><label className="lbl">Nom *</label><input type="text" className="inp" value={form.nom||""} onChange={e=>setForm({...form,nom:e.target.value})} /></div>
              {(()=>{
                const k=normNomClient(form.nom);
                const memes=k?clients.filter(c=>String(c.id)!==String(form.id||"")&&normNomClient(c.nom)===k):[];
                if(memes.length===0) return null;
                return (
                  <div style={{background:"#FFF4DD",border:"1px solid #2F9E3F",borderRadius:8,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#0F1F17"}}>
                    <div style={{fontWeight:700,marginBottom:4}}>⚠ Ce nom existe déjà ({memes.length} fiche{memes.length>1?"s":""}) :</div>
                    {memes.map(c=><div key={c.id}>• <strong>{c.code}</strong> — {c.adresse||"adresse manquante"}{c.ville?", "+c.ville:""}</div>)}
                    <div style={{marginTop:6,color:"#0F1F17"}}>Si c'est le même client, fermez cette fenêtre et ouvrez sa fiche. Si c'est un autre client, indiquez bien son adresse pour le distinguer.</div>
                  </div>
                );
              })()}
              <div className="fld"><label className="lbl">Type</label>
                <select className="inp" value={form.type||"Détaillant"} onChange={e=>setForm({...form,type:e.target.value})}>
                  {["Détaillant","Grossiste","HoReCa","Institution","Autre"].map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">NIU</label><input type="text" className="inp" value={form.niu||""} onChange={e=>setForm({...form,niu:e.target.value})} /></div>
              <div className="fld"><label className="lbl">RC</label><input type="text" className="inp" value={form.rc||""} onChange={e=>setForm({...form,rc:e.target.value})} /></div>
              <div className="fld"><label className="lbl">RCCM</label><input type="text" className="inp" value={form.rccm||""} onChange={e=>setForm({...form,rccm:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Adresse * (quartier, rue, repère)</label><input type="text" className="inp" value={form.adresse||""} onChange={e=>setForm({...form,adresse:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Pays</label>
                <select className="inp" value={form.pays||"Cameroun"} onChange={e=>setForm({...form,pays:e.target.value,ville:""})}>
                  {paysDisponibles.map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Ville *</label>
                <select className="inp" value={form.ville||""} onChange={e=>setForm({...form,ville:e.target.value})}>
                  <option value="">Choisir...</option>
                  {villesPourPays(form.pays||"Cameroun").map(v=><option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Téléphone</label><input type="text" className="inp" value={form.contact||""} onChange={e=>setForm({...form,contact:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Email</label><input type="email" className="inp" value={form.email||""} onChange={e=>setForm({...form,email:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Contact personne</label><input type="text" className="inp" value={form.contact_nom||""} onChange={e=>setForm({...form,contact_nom:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Tél. contact</label><input type="text" className="inp" value={form.contact_tel||""} onChange={e=>setForm({...form,contact_tel:e.target.value})} /></div>
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={saveClient} disabled={loading}>{loading?"...":form.id?"Mettre à jour":"Ajouter"}</button>
                <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
              </div>
            </>}

            {modal==="fournisseur"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier":"Nouveau fournisseur"}</div>
              <div className="fld"><label className="lbl">Nom *</label><input type="text" className="inp" value={form.nom||""} onChange={e=>setForm({...form,nom:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Catégorie</label>
                <select className="inp" value={form.categorie||""} onChange={e=>setForm({...form,categorie:e.target.value})}>
                  {["Matières premières","Emballages","Équipements","Services","Autre"].map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Pays</label>
                <select className="inp" value={form.pays||"Cameroun"} onChange={e=>setForm({...form,pays:e.target.value,ville:""})}>
                  {paysDisponibles.map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Ville</label>
                <select className="inp" value={form.ville||""} onChange={e=>setForm({...form,ville:e.target.value})}>
                  <option value="">Choisir...</option>
                  {villesPourPays(form.pays||"Cameroun").map(v=><option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Contact</label><input type="text" className="inp" value={form.contact||""} onChange={e=>setForm({...form,contact:e.target.value})} /></div>
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={saveFournisseur} disabled={loading}>{loading?"...":"Sauvegarder"}</button>
                <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
              </div>
            </>}

            {modal==="depot"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier le dépôt":"Nouveau dépôt"}</div>
              <div style={{fontSize:11,color:"#5B6B62",marginBottom:14}}>La ville doit déjà figurer dans « Villes disponibles » (Paramètres) ; ajoutez-la là-bas d'abord si elle manque. Créer le dépôt la rend aussitôt disponible pour une commande d'achat et pour la « Ville d'affectation » d'un employé.</div>
              <div className="fld"><label className="lbl">Nom du dépôt *</label><input type="text" className="inp" placeholder="ex: Dépôt Garoua" value={form.nom||""} onChange={e=>setForm({...form,nom:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Ville *</label>
                <select className="inp" value={form.ville||""} onChange={e=>setForm({...form,ville:e.target.value})}>
                  <option value="">Choisir...</option>
                  {[...new Set([...villesDisponibles,...depots.map(d=>d.ville).filter(Boolean)])].sort((a,b)=>a.localeCompare(b,"fr")).map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Responsable</label><input type="text" className="inp" value={form.responsable||""} onChange={e=>setForm({...form,responsable:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Téléphone</label><input type="text" className="inp" value={form.telephone||""} onChange={e=>setForm({...form,telephone:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Adresse</label><input type="text" className="inp" value={form.adresse||""} onChange={e=>setForm({...form,adresse:e.target.value})} /></div>
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={saveDepot} disabled={loading}>{loading?"...":form.id?"Mettre à jour":"Créer"}</button>
                <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
              </div>
            </>}

            {modal==="produit"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier produit":"Nouveau produit"}</div>
              <div className="fld"><label className="lbl">Code produit * (4 chiffres)</label><input type="text" inputMode="numeric" maxLength={4} className="inp" value={codeProduitSaisi()} onChange={e=>setForm({...form,code:e.target.value.replace(/[^0-9]/g,"").slice(0,4)})} placeholder="ex. 9004" aria-label="Code produit" /></div>
              <div className="fld"><label className="lbl">Nom *</label><input type="text" className="inp" value={form.nom||""} onChange={e=>setForm({...form,nom:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Prix de vente (FCFA) *</label><input type="number" className="inp" value={form.prix||""} onChange={e=>setForm({...form,prix:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Coût de revient (FCFA)</label><input type="number" className="inp" value={form.cout_revient||""} onChange={e=>setForm({...form,cout_revient:e.target.value})} placeholder="Ingrédients, emballage, main d'œuvre..." /></div>
              <div style={{fontSize:11,color:"#8A978F",marginTop:-8,marginBottom:14}}>Utilisé seulement si aucune recette n'est définie pour ce produit (bouton « Recette » dans Stock & Produits) — la recette prend le dessus automatiquement dès qu'elle existe.</div>
              {form.prix&&form.cout_revient?(
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:14,marginTop:-6}}>
                  Marge : <strong style={{color:(form.prix-form.cout_revient)>=0?"#1E7A2D":"#D6453D"}}>{FCFA(form.prix-form.cout_revient)}</strong> par bouteille ({Math.round(((form.prix-form.cout_revient)/form.prix)*100)}%)
                </div>
              ):null}
              {!form.id&&<div style={{fontSize:11,color:"#8A978F",marginTop:-6,marginBottom:14}}>Le stock se gère après coup, depuis la carte du produit ("+ Stock") ou via une réception/livraison — un nouveau produit démarre toujours à 0.</div>}
              <div className="fld"><label className="lbl">Seuil alerte</label><input type="number" className="inp" value={form.seuil_alerte||60} onChange={e=>setForm({...form,seuil_alerte:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Couleur</label>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <input type="color" value={form.couleur||"#1E8A32"} onChange={e=>setForm({...form,couleur:e.target.value})} style={{width:40,height:36,border:"none",borderRadius:4,cursor:"pointer"}} />
                  <input type="text" className="inp" value={form.couleur||"#1E8A32"} onChange={e=>setForm({...form,couleur:e.target.value})} />
                </div>
              </div>
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={saveProduit} disabled={loading}>{loading?"...":form.id?"Mettre à jour":"Ajouter"}</button>
                <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
              </div>
            </>}

            {modal==="ingredient"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier l'ingrédient":"Nouvel ingrédient"}</div>
              <div className="fld"><label className="lbl">Nom *</label><input type="text" className="inp" value={form.nom||""} onChange={e=>setForm({...form,nom:e.target.value})} placeholder="ex: Thé noir, Sucre..." /></div>
              <div className="fld"><label className="lbl">Unité</label><input type="text" className="inp" value={form.unite||"kg"} onChange={e=>setForm({...form,unite:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Prix d'achat par unité (FCFA) *</label><input type="number" className="inp" value={form.prix_unitaire||""} onChange={e=>setForm({...form,prix_unitaire:e.target.value})} placeholder="ex: prix du kg" /></div>
              <div style={{fontSize:11,color:"#8A978F",marginTop:-8,marginBottom:14}}>Pour un sac de 50 kg par exemple, entre ici le prix divisé par 50 (prix au kg).</div>
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={saveIngredient} disabled={loading}>{loading?"...":form.id?"Mettre à jour":"Ajouter"}</button>
                <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
              </div>
            </>}

            {modal==="ajout_stock"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:6}}>+ Ajouter au stock</div>
              <div style={{fontSize:12,color:"#5B6B62",marginBottom:18}}>{form.produitNom}</div>
              <div className="fld"><label className="lbl">Quantité à ajouter (bouteilles) *</label><input type="number" className="inp" min="1" value={form.qte||""} onChange={e=>setForm({...form,qte:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Note (optionnel)</label><input type="text" className="inp" placeholder="ex: correction d'inventaire, retour..." value={form.note||""} onChange={e=>setForm({...form,note:e.target.value})} /></div>
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={saveAjoutStock} disabled={loading}>{loading?"...":"Ajouter au stock"}</button>
                <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
              </div>
            </>}

            {modal==="avarie_ville"&&(()=>{
              const villesStock=[...new Set(depotStocks.map(x=>x.ville).filter(Boolean))].sort();
              const dispo = form.ville&&form.produitNom ? (depotStocks.find(x=>x.ville===form.ville&&x.produit_nom===form.produitNom)?.quantite||0) : null;
              const facturesClient = form.clientNom ? factures.filter(f=>form.clientCode ? f.client_code===form.clientCode : f.client_nom===form.clientNom) : [];
              return <>
                <div style={{fontSize:19,color:"#D6453D",marginBottom:6,display:"flex",alignItems:"center",gap:8}}><AlertTriangle size={18}/> Déclarer une avarie</div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:16}}>Les bouteilles avariées sont retirées du stock de la ville choisie.</div>
                <div className="fld"><label className="lbl">Ville du stock concerné *</label>
                  <select className="inp" value={form.ville||""} onChange={e=>setForm({...form,ville:e.target.value})} disabled={!canSeeAllCities()&&!!sessionVille()}>
                    <option value="">Choisir...</option>
                    {villesStock.map(v=><option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div className="fld"><label className="lbl">Produit *</label>
                  <select className="inp" value={form.produitNom||""} onChange={e=>setForm({...form,produitNom:e.target.value})}>
                    <option value="">Choisir...</option>
                    {produits.map(pr=><option key={pr.id} value={pr.nom}>{pr.code?pr.code+" · ":""}{pr.nom}</option>)}
                  </select>
                  {dispo!==null&&<div style={{fontSize:11,color:"#5B6B62",marginTop:4}}>Disponible à {form.ville} : <strong style={{color:"#0F1F17"}}>{NUM(dispo)} btl</strong></div>}
                </div>
                <div className="fld"><label className="lbl">Quantité avariée (bouteilles) *</label><input type="number" className="inp" min="1" value={form.qte||""} onChange={e=>setForm({...form,qte:e.target.value})} /></div>
                <div className="fld"><label className="lbl">Raison</label><input type="text" className="inp" placeholder="ex: casse, fuite, périmé, retour client..." value={form.raison||""} onChange={e=>setForm({...form,raison:e.target.value})} /></div>
                <hr className="divider"/>
                <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Client et facture concernés (optionnel)</div>
                <div className="fld"><label className="lbl">Client</label>
                  <select className="inp" value={form.clientCode||""} onChange={e=>{ const code=e.target.value; const cl=clients.find(c=>c.code===code); setForm({...form,clientCode:code,clientNom:cl?cl.nom:"",factureId:"",adresse:(cl&&cl.adresse)||form.adresse||"",lieuVille:(cl&&cl.ville)||form.lieuVille||""}); }}>
                    <option value="">Aucun</option>
                    {clients.map(c=><option key={c.id} value={c.code}>{etiquetteClient(c)}</option>)}
                  </select>
                </div>
                {form.clientNom&&(
                  <div className="fld"><label className="lbl">Facture concernée (l'avarie s'affichera sur cette facture)</label>
                    <select className="inp" value={form.factureId||""} onChange={e=>setForm({...form,factureId:e.target.value})}>
                      <option value="">Aucune</option>
                      {facturesClient.map(f=><option key={f.id} value={f.id}>{f.numero} — {fmtDate(f.date_emission)} — {f.statut}</option>)}
                    </select>
                    {facturesClient.length===0&&<div style={{fontSize:11,color:"#5B6B62",marginTop:4}}>Ce client n'a aucune facture enregistrée.</div>}
                  </div>
                )}
                <hr className="divider"/>
                <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Où l'avarie s'est produite</div>
                <div className="fld"><label className="lbl">Lieu</label>
                  <select className="inp" value={form.lieu||""} onChange={e=>setForm({...form,lieu:e.target.value})}>
                    {["Chez le client","Dépôt / magasin","Pendant le transport","Point de vente","Autre"].map(l=><option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="fld"><label className="lbl">Adresse</label><input type="text" className="inp" placeholder="Quartier, rue, repère..." value={form.adresse||""} onChange={e=>setForm({...form,adresse:e.target.value})} /></div>
                <div className="fld"><label className="lbl">Ville</label><input type="text" className="inp" placeholder={form.ville||"Ville du lieu"} value={form.lieuVille||""} onChange={e=>setForm({...form,lieuVille:e.target.value})} /></div>
                <div className="fld"><label className="lbl">Remplacement</label>
                  <select className="inp" value={form.remplacement||"À remplacer"} onChange={e=>setForm({...form,remplacement:e.target.value})}>
                    {["À remplacer","Remplacée","Sans remplacement"].map(l=><option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div style={{display:"flex",gap:9,marginTop:18}}>
                  <button className="btn btn-red" style={{flex:1}} onClick={saveAvarieVille} disabled={loading}>{loading?"...":"Enregistrer l'avarie"}</button>
                  <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
                </div>
              </>;
            })()}

            {modal==="avarie"&&(()=>{
              const lotsProduit = lots.filter(l=>l.produit===form.produitNom && l.quantite>0);
              const facturesClient = form.clientNom ? factures.filter(f=>f.client_nom===form.clientNom) : [];
              return <>
                <div style={{fontSize:19,color:"#D6453D",marginBottom:6,display:"flex",alignItems:"center",gap:8}}><AlertTriangle size={18}/> Déclarer une avarie</div>
                <div style={{fontSize:12,color:"#5B6B62",marginBottom:18}}>{form.produitNom}</div>
                <div className="fld"><label className="lbl">Lot concerné *</label>
                  <select className="inp" value={form.lotId||""} onChange={e=>setForm({...form,lotId:e.target.value})}>
                    <option value="">Choisir...</option>
                    {lotsProduit.map(l=><option key={l.id} value={l.id}>{l.numero} — {NUM(l.quantite)} btl restant(s) — {new Date(l.date).toLocaleDateString("fr-FR")}</option>)}
                  </select>
                  {lotsProduit.length===0&&<div style={{fontSize:11,color:"#5B6B62",marginTop:4}}>Aucun lot avec du stock restant pour ce produit.</div>}
                </div>
                <div className="fld"><label className="lbl">Quantité avariée (bouteilles) *</label><input type="number" className="inp" min="1" value={form.qte||""} onChange={e=>setForm({...form,qte:e.target.value})} /></div>
                <div className="fld"><label className="lbl">Raison</label><input type="text" className="inp" placeholder="ex: casse, fuite, périmé..." value={form.raison||""} onChange={e=>setForm({...form,raison:e.target.value})} /></div>
                <hr className="divider"/>
                <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:10}}>Retirer ces unités d'une facture client (optionnel)</div>
                <div className="fld"><label className="lbl">Client concerné</label>
                  <select className="inp" value={form.clientNom||""} onChange={e=>setForm({...form,clientNom:e.target.value,factureId:""})}>
                    <option value="">Aucun</option>
                    {clients.map(c=><option key={c.id} value={c.nom}>{c.nom}</option>)}
                  </select>
                </div>
                {form.clientNom&&(
                  <div className="fld"><label className="lbl">Facture à ajuster</label>
                    <select className="inp" value={form.factureId||""} onChange={e=>setForm({...form,factureId:e.target.value})}>
                      <option value="">Choisir...</option>
                      {facturesClient.map(f=><option key={f.id} value={f.id}>{f.numero} — {f.statut}</option>)}
                    </select>
                    {facturesClient.length===0&&<div style={{fontSize:11,color:"#5B6B62",marginTop:4}}>Ce client n'a aucune facture enregistrée.</div>}
                  </div>
                )}
                <div style={{display:"flex",gap:9,marginTop:18}}>
                  <button className="btn btn-red" style={{flex:1}} onClick={saveAvarie} disabled={loading}>{loading?"...":"Enregistrer l'avarie"}</button>
                  <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
                </div>
              </>;
            })()}

            {modal==="contratTravail"&&(()=>{
              const emp=employeeById(form.employe_id);
              const isCDD=String(form.type_contrat||"").toUpperCase()==="CDD";
              const fld=(label,key,type="text",extra={})=><div className="fld"><label className="lbl">{label}</label><input type={type} className="inp" value={form[key]??""} onChange={e=>setForm({...form,[key]:e.target.value})} {...extra}/></div>;
              return <>
                <div style={{fontSize:19,color:"#1E8A32",marginBottom:4}}>Générer le contrat de travail</div>
                <div style={{fontSize:11,color:"#5B6B62",marginBottom:14}}>{emp?[emp.prenom,emp.nom].filter(Boolean).join(" "):"—"} — le PDF généré sera automatiquement déposé dans « Mes documents RH » du collaborateur.</div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:"0 10px"}}>
                  <div className="fld"><label className="lbl">Type de contrat *</label><select className="inp" value={form.type_contrat||""} onChange={e=>setForm({...form,type_contrat:e.target.value})}><option value="">Choisir...</option>{["CDI","CDD","Stage","Prestataire","Consultant","Journalier"].map(v=><option key={v}>{v}</option>)}</select></div>
                  {fld("Poste *","poste")}
                  {fld("Département","departement")}
                  {fld("Ville / lieu de travail","ville")}
                  {fld("Date de début *","date_debut","date")}
                  {isCDD&&fld("Date de fin (CDD) *","date_fin_contrat","date")}
                  {fld("Durée de la période d'essai","periode_essai_duree",undefined,{placeholder:"ex : 3 mois"})}
                  {fld("Salaire de base (FCFA) *","salaire_base","number",{min:"0"})}
                  {fld("Prime fixe (FCFA)","prime_fixe","number",{min:"0"})}
                  <div className="fld"><label className="lbl">Mode de paiement</label><select className="inp" value={form.mode_paiement||""} onChange={e=>setForm({...form,mode_paiement:e.target.value})}><option value="">Choisir...</option>{["Virement bancaire","Mobile Money","Espèces","Chèque"].map(v=><option key={v}>{v}</option>)}</select></div>
                  {fld("Horaire de travail","horaire_hebdo")}
                  {fld("Lieu de signature","lieu_signature")}
                  {fld("Date de signature","date_signature","date")}
                </div>
                <div className="fld"><label className="lbl">Clauses particulières (facultatif)</label><textarea className="inp" rows="3" placeholder="Toute clause spécifique à ajouter au contrat..." value={form.clauses_particulieres||""} onChange={e=>setForm({...form,clauses_particulieres:e.target.value})}/></div>
                <div style={{display:"flex",gap:9,marginTop:18}}>
                  <button className="btn btn-gold" style={{flex:1}} onClick={genererContratTravail} disabled={contratGenerating}><FileText size={14}/> {contratGenerating?"Génération...":"Générer et déposer dans le profil"}</button>
                  <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
                </div>
              </>;
            })()}

            {modal==="procuration"&&(()=>{
              const procType=form.procurationType||"encaissement";
              const selectedClients=Array.isArray(form.clientIds)?form.clientIds:[];
              const toggleClient=id=>setForm(f=>{
                const cur=Array.isArray(f.clientIds)?f.clientIds:[];
                return {...f, clientIds: cur.includes(id) ? cur.filter(x=>x!==id) : [...cur,id]};
              });
              // Une procuration se donne PAR un directeur À un collaborateur qui n'a pas déjà
              // les pleins pouvoirs — on retire donc les comptes Directeur/Administrateur de la
              // liste des mandataires possibles (leur choisir eux-mêmes n'aurait pas de sens).
              const isDirRow = row => estRoleDirecteur(row?.role, row?.poste);
              // Le Mandant (le directeur connecté) ne doit jamais pouvoir apparaître comme
              // son propre Mandataire — exclusion explicite en plus du filtre par rôle
              // ci-dessus, qui à elle seule couvre aussi les cas où le rôle stocké ne
              // contiendrait pas "directeur" pour une raison ou une autre.
              const mandataires = employes.filter(x=>x.actif!==false && !isDirRow(x) && String(x.id)!==String(session?.id));
              return <>
                <div style={{fontSize:19,color:"#1E8A32",marginBottom:4}}>Générer une procuration</div>
                <div style={{fontSize:11,color:"#5B6B62",marginBottom:14}}>Donne à un collaborateur le pouvoir d'agir au nom de la Direction, pour un objet et une durée précis. Le PDF généré sera automatiquement déposé dans « Mes documents RH » du collaborateur.</div>
                <div className="fld">
                  <label className="lbl">Collaborateur (le Mandataire) *</label>
                  <select className="inp" value={form.employe_id||""} onChange={e=>setForm({...form,employe_id:e.target.value})}>
                    <option value="">Choisir...</option>
                    {mandataires.map(x=><option key={x.id} value={x.id}>{[x.prenom,x.nom].filter(Boolean).join(" ")} — {x.poste||"Employé"}</option>)}
                  </select>
                  {mandataires.length===0&&<div style={{fontSize:11,color:"#1E8A32",marginTop:5}}>Aucun collaborateur disponible — les seuls comptes actuels sont des comptes Direction. Ajoute d'abord un employé (bouton « + Nouvel employé » dans Employés) avant de générer une procuration.</div>}
                </div>
                <div className="fld">
                  <label className="lbl">Type de procuration</label>
                  <select className="inp" value={procType} onChange={e=>setForm({...form,procurationType:e.target.value})}>
                    <option value="encaissement">Encaissement de paiements chez des clients</option>
                    <option value="autre">Autre (objet libre)</option>
                  </select>
                </div>
                {procType==="encaissement" ? (
                  <div className="fld">
                    <label className="lbl">Clients concernés * ({selectedClients.length} sélectionné{selectedClients.length>1?"s":""})</label>
                    <div style={{maxHeight:220,overflowY:"auto",border:"1px solid #E3E9E6",borderRadius:6,padding:"8px 10px",display:"flex",flexDirection:"column",gap:7,background:"#FFFFFF"}}>
                      {clients.length===0&&<div style={{fontSize:12,color:"#5B6B62"}}>Aucun client enregistré pour l'instant — ajoute-en dans le module « Clients » avant de générer cette procuration.</div>}
                      {clients.map(cl=>(
                        <label key={cl.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,color:"#0F1F17",cursor:"pointer"}}>
                          <input type="checkbox" checked={selectedClients.includes(cl.id)} onChange={()=>toggleClient(cl.id)}/>
                          {cl.nom}{cl.code?" ("+cl.code+")":""}
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="fld"><label className="lbl">Objet de la procuration *</label><textarea className="inp" rows="3" placeholder="Ex. : représenter l'entreprise et signer les documents nécessaires auprès de la banque X pendant mon absence." value={form.objet||""} onChange={e=>setForm({...form,objet:e.target.value})}/></div>
                )}
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:"0 10px"}}>
                  <div className="fld"><label className="lbl">Date de début *</label><input type="date" className="inp" value={form.date_debut||""} onChange={e=>setForm({...form,date_debut:e.target.value})}/></div>
                  <div className="fld"><label className="lbl">Date de fin *</label><input type="date" className="inp" value={form.date_fin||""} onChange={e=>setForm({...form,date_fin:e.target.value})}/></div>
                  <div className="fld"><label className="lbl">Lieu de signature</label><input type="text" className="inp" value={form.lieu_signature||""} onChange={e=>setForm({...form,lieu_signature:e.target.value})}/></div>
                  <div className="fld"><label className="lbl">Date de signature</label><input type="date" className="inp" value={form.date_signature||""} onChange={e=>setForm({...form,date_signature:e.target.value})}/></div>
                </div>
                <div className="fld"><label className="lbl">Clauses particulières (facultatif)</label><textarea className="inp" rows="3" placeholder="Toute précision ou limite supplémentaire à ajouter..." value={form.clauses_particulieres||""} onChange={e=>setForm({...form,clauses_particulieres:e.target.value})}/></div>
                <div style={{display:"flex",gap:9,marginTop:18}}>
                  <button className="btn btn-gold" style={{flex:1}} onClick={genererProcuration} disabled={procurationGenerating}><FileText size={14}/> {procurationGenerating?"Génération...":"Générer et déposer dans le profil"}</button>
                  <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
                </div>
              </>;
            })()}

            {modal==="employe"&&(()=>{
              const tab=form._rhTab||"identite";
              const setTab=t=>setForm(f=>({...f,_rhTab:t}));
              const tabs=[["identite","Identité"],["professionnel","Professionnel"],["contrat","Contrat & paie"],["documents","Documents"],["historique","Historique"],["acces","Accès ERP"]];
              const employeeDocs=documentsEmployes.filter(d=>String(d.employe_id)===String(form.id));
              const employeeHistory=historiqueEmployes.filter(h=>String(h.employe_id)===String(form.id));
              const employeeConges=conges.filter(c=>String(c.employe_id)===String(form.id));
              const fld=(label,key,type="text",extra={})=><div className="fld"><label className="lbl">{label}</label><input type={type} className="inp" value={form[key]??""} onChange={e=>setForm({...form,[key]:e.target.value})} {...extra}/></div>;
              return <>
                <div style={{fontSize:19,color:"#1E8A32",marginBottom:4}}>{form.id?"Fiche RH de "+form.nom:"Nouvel employé"}</div>
                <div style={{fontSize:11,color:"#5B6B62",marginBottom:14}}>{form.matricule||"Le matricule sera généré automatiquement"}</div>
                <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,marginBottom:14}}>{tabs.map(([id,label])=><button key={id} type="button" className={"btn btn-sm "+(tab===id?"btn-gold":"btn-ghost")} onClick={()=>setTab(id)}>{label}</button>)}</div>
                {tab==="identite"&&<>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:"0 10px"}}>
                    {fld("Nom *","nom")}{fld("Prénom","prenom")}{fld("Date de naissance","date_naissance","date")}
                    <div className="fld"><label className="lbl">Sexe</label><select className="inp" value={form.sexe||""} onChange={e=>setForm({...form,sexe:e.target.value})}><option value="">Non renseigné</option><option>Féminin</option><option>Masculin</option><option>Autre</option></select></div>
                    {fld("Lieu de naissance","lieu_naissance")}{fld("Nationalité","nationalite")}{fld("N° CNI / Passeport","numero_cni")}{fld("Validité de la CNI / Passeport","date_validite_cni","date")}{fld("Téléphone","tel","tel")}{fld("Email","email","email")}{fld("Adresse","adresse")}{fld("Contact d'urgence","contact_urgence_nom")}{fld("Téléphone d'urgence","contact_urgence_tel","tel")}<div className="fld" style={{gridColumn:isMobile?"auto":"1 / -1"}}><label className="lbl">Photo de profil</label>
                      <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                        <div style={{width:84,height:84,borderRadius:"50%",background:"#F1F5F3",border:"2px solid #E3E9E6",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>{form.photo_url?<img alt="" src={form.photo_url} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}}/>:<User size={34}/>}</div>
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          <label className="btn btn-gold btn-sm" style={{cursor:"pointer",textAlign:"center",display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{form.photo_url?"Changer la photo":(estAppareilMobile()?"Prendre ou choisir une photo":"Choisir une photo")}
                            <input type="file" accept="image/*" style={{display:"none"}} onChange={async ev=>{
                              const inputEl=ev.target; const file=inputEl.files&&inputEl.files[0];
                              if(!file) return;
                              setForm(f=>({...f,_photoMsg:{ok:true,txt:"Traitement de la photo…"}}));
                              try{
                                const data=await photoFileToDataUrl(file);
                                setForm(f=>({...f,photo_url:data,_photoMsg:{ok:true,txt:"✓ Photo prête ("+Math.round(data.length*0.75/1024)+" Ko) — clique sur Enregistrer en bas de la fiche."}}));
                              }catch(err){
                                setForm(f=>({...f,_photoMsg:{ok:false,txt:"✗ "+(err&&err.message||"Photo impossible à lire")}}));
                              }
                              try{ inputEl.value=""; }catch(_){}
                            }}/>
                          </label>
                          {form.photo_url&&<button type="button" className="btn btn-ghost btn-sm" onClick={()=>setForm(f=>({...f,photo_url:""}))}>Retirer la photo</button>}
                        </div>
                      </div>
                      {form._photoMsg&&<div style={{fontSize:12,fontWeight:700,marginTop:8,color:form._photoMsg.ok?"#1E7A2D":"#D6453D"}}>{form._photoMsg.txt}</div>}
                      <div style={{fontSize:10,color:"#5B6B62",marginTop:6}}>La photo est réduite automatiquement. Elle apparaît sur la fiche de l'employé. N'oublie pas d'enregistrer la fiche.</div>
                    </div>{fld("URL de la signature","signature_url","url")}
                  </div>
                </>}
                {tab==="professionnel"&&<>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:"0 10px"}}>
                    {fld("Poste","poste")}
                    <div className="fld"><label className="lbl">Département</label><select className="inp" value={form.departement||form.service||""} onChange={e=>setForm({...form,departement:e.target.value,service:e.target.value})}><option value="">Choisir...</option>{departements.map(d=><option key={d.id} value={d.nom}>{d.nom}</option>)}{departements.length===0&&["Direction","Production","Commercial","Finance et comptabilité","Logistique","Ressources humaines"].map(d=><option key={d}>{d}</option>)}</select></div>
                    <div className="fld"><label className="lbl">Ville d'affectation *</label><select className="inp" value={form.ville||""} onChange={e=>setForm({...form,ville:e.target.value})}><option value="">Choisir...</option>{[...new Set([...depots.filter(d=>d.ville!=="Magasin central").map(d=>d.ville).filter(Boolean),"Douala","Yaoundé","Bafoussam"])].map(v=><option key={v}>{v}</option>)}</select></div>
                    <div className="fld"><label className="lbl">Responsable hiérarchique</label><select className="inp" value={form.manager_id||""} onChange={e=>setForm({...form,manager_id:e.target.value})}><option value="">Aucun</option>{employes.filter(e=>String(e.id)!==String(form.id)).map(e=><option key={e.id} value={e.id}>{e.nom} — {e.poste||"Employé"}</option>)}</select></div>
                    {fld("Date d'embauche","date_embauche","date")}{fld("Date de départ","date_depart","date")}
                    <div className="fld"><label className="lbl">Statut RH</label><select className="inp" value={form.statut_rh||"Actif"} onChange={e=>setForm({...form,statut_rh:e.target.value,actif:e.target.value==="Actif"})}>{["Actif","Congé","Suspendu","Inactif","Démissionnaire","Licencié","Retraité"].map(v=><option key={v}>{v}</option>)}</select></div>
                  </div>
                  <div className="fld"><label className="lbl">Notes RH</label><textarea className="inp" rows="3" value={form.notes_rh||""} onChange={e=>setForm({...form,notes_rh:e.target.value})}/></div>
                </>}
                {tab==="contrat"&&<>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:"0 10px"}}>
                    <div className="fld"><label className="lbl">Type de contrat</label><select className="inp" value={form.type_contrat||""} onChange={e=>setForm({...form,type_contrat:e.target.value})}><option value="">Choisir...</option>{["CDI","CDD","Stage","Prestataire","Consultant","Journalier"].map(v=><option key={v}>{v}</option>)}</select></div>
                    {fld("Fin du contrat","date_fin_contrat","date")}{fld("Fin de période d'essai","periode_essai_fin","date")}{fld("Salaire de base (FCFA)","salaire_base","number",{min:"0"})}{fld("Prime fixe (FCFA)","prime_fixe","number",{min:"0"})}{fld("Commission","commission","number",{min:"0"})}
                    <div className="fld"><label className="lbl">Mode de paiement</label><select className="inp" value={form.mode_paiement||""} onChange={e=>setForm({...form,mode_paiement:e.target.value})}><option value="">Choisir...</option>{["Virement bancaire","Mobile Money","Espèces","Chèque"].map(v=><option key={v}>{v}</option>)}</select></div>
                    {fld("Banque / opérateur","banque")}{fld("N° compte / téléphone","numero_compte")}
                  </div>
                  {form.id&&(()=>{
                    const anneeEnCours=new Date().getFullYear();
                    const solde=soldesConges.find(s=>String(s.employe_id)===String(form.id)&&Number(s.annee)===anneeEnCours);
                    const soldeNet=solde?(Number(solde.acquis||0)+Number(solde.reporte||0)+Number(solde.ajuste||0)-Number(solde.pris||0)):null;
                    return (
                      <div className="card" style={{padding:12,marginTop:8}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                          <div style={{fontSize:12,color:"#0F1F17",fontWeight:700}}>Situation congés — {anneeEnCours}</div>
                          {isDirecteur()&&<button type="button" className="btn btn-ghost btn-sm" onClick={()=>recalculerSoldeConges(form.id)}>↻ Recalculer le solde</button>}
                        </div>
                        <div style={{fontSize:12,color:"#5B6B62",marginTop:6}}>{employeeConges.length} demande(s) enregistrée(s), dont {employeeConges.filter(c=>String(c.statut).toLowerCase().includes("attente")).length} en attente.</div>
                        {solde?(
                          <div style={{fontSize:12,color:"#5B6B62",marginTop:8,display:"flex",gap:16,flexWrap:"wrap"}}>
                            <div>Acquis : <strong style={{color:"#0F1F17"}}>{Number(solde.acquis||0).toFixed(1)} j</strong></div>
                            <div>Pris : <strong style={{color:"#0F1F17"}}>{Number(solde.pris||0).toFixed(1)} j</strong></div>
                            {Number(solde.reporte||0)!==0&&<div>Report : <strong style={{color:"#0F1F17"}}>{Number(solde.reporte).toFixed(1)} j</strong></div>}
                            {Number(solde.ajuste||0)!==0&&<div>Ajustement : <strong style={{color:"#0F1F17"}}>{Number(solde.ajuste).toFixed(1)} j</strong></div>}
                            <div>Solde : <strong style={{color:soldeNet>=0?"#1E7A2D":"#D6453D"}}>{soldeNet.toFixed(1)} j</strong></div>
                          </div>
                        ):(
                          <div style={{fontSize:11,color:"#8A978F",marginTop:8}}>Pas encore calculé{isDirecteur()?" — clique sur « Recalculer le solde »." :"."}</div>
                        )}
                      </div>
                    );
                  })()}
                </>}
                {tab==="documents"&&<>{!form.id?<div className="card" style={{color:"#5B6B62"}}>Enregistrez d'abord l'employé pour pouvoir rattacher des documents.</div>:<div><div style={{fontSize:12,color:"#5B6B62",marginBottom:10}}>{employeeDocs.length} document(s) rattaché(s).</div>{employeeDocs.map(d=><div key={d.id} className="card" style={{padding:10,marginBottom:7}}><div style={{fontWeight:700,fontSize:12}}>{d.titre||d.type_document}</div><div style={{fontSize:11,color:"#5B6B62"}}>{d.type_document} · {d.date_expiration?"Expire le "+fmtDate(d.date_expiration):"Sans expiration"}</div>{d.fichier_url&&<a href={d.fichier_url} target="_blank" rel="noreferrer" style={{fontSize:11,color:"#2F6FDE"}}>Ouvrir le document</a>}</div>)}{employeeDocs.length===0&&<div className="card" style={{color:"#5B6B62"}}>Aucun document RH enregistré.</div>}</div>}</>}
                {tab==="historique"&&<>{!form.id?<div className="card" style={{color:"#5B6B62"}}>L'historique sera créé après l'enregistrement.</div>:<div style={{maxHeight:380,overflowY:"auto"}}>{employeeHistory.map(h=><div key={h.id} style={{borderBottom:"1px solid #E3E9E6",padding:"9px 2px"}}><div style={{display:"flex",justifyContent:"space-between",gap:8}}><strong style={{fontSize:12,color:"#0F1F17"}}>{h.champ||h.categorie}</strong><span style={{fontSize:10,color:"#5B6B62"}}>{new Date(h.created_at).toLocaleString("fr-FR")}</span></div><div style={{fontSize:11,color:"#5B6B62",marginTop:3}}>{h.ancienne_valeur&&<span>{h.ancienne_valeur} → </span>}{h.nouvelle_valeur}</div><div style={{fontSize:10,color:"#2F6FDE",marginTop:2}}>Par {h.effectue_par_nom||"Système"}</div></div>)}{employeeHistory.length===0&&<div className="card" style={{color:"#5B6B62"}}>Aucun changement RH enregistré.</div>}</div>}</>}
                {tab==="acces"&&<>
                  {form.id&&!form.auth_user_id&&(
                    <div className="card" style={{marginBottom:16,borderColor:"rgba(47,158,63,0.360)"}}>
                      <div style={{fontSize:13,color:"#1E8A32",fontWeight:700,marginBottom:6}}>⚠ Aucun compte de connexion</div>
                      <div style={{fontSize:11,color:"#5B6B62",marginBottom:10}}>Cet employé ne peut ni se connecter ni enregistrer quoi que ce soit tant que son compte n'est pas créé.</div>
                      {!session.gestion_comptes_autorisee&&<div style={{fontSize:11,color:"#5B6B62",marginBottom:10}}>Seule une personne autorisée à gérer les accès peut créer ce compte — demande à la direction.</div>}
                      {session.gestion_comptes_autorisee&&<button className="btn btn-gold btn-sm" disabled={creatingAuthAccount} onClick={async()=>{
                        setCreatingAuthAccount(true);
                        // Mot de passe temporaire généré aléatoirement à chaque création de compte
                        // (auparavant une valeur fixe "Compta2026!" était codée en dur ici, visible
                        // par quiconque ouvrait les outils de développement du navigateur).
                        const tempPassword=(()=>{const chars="ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";const bytes=new Uint32Array(10);window.crypto.getRandomValues(bytes);let out="";for(let i=0;i<10;i++)out+=chars[bytes[i]%chars.length];return out;})();
                        const {data,error}=await supabase.rpc("creer_compte_auth_employe",{p_employe_id:form.id,p_mot_de_passe:tempPassword});
                        setCreatingAuthAccount(false);
                        if(error) return showToast("Erreur : "+error.message,false);
                        await load();
                        setForm(f=>({...f,username:data.username}));
                        showToast("Compte créé ✓");
                        // Fiche d'accès ERP (instructions, identifiants, QR code) générée automatiquement
                        // pour que la Direction puisse la remettre/imprimer/envoyer au nouvel employé.
                        try{
                          setAccesView({employe:{...form,username:data.username},credentials:{username:data.username,password:data.password}});
                        }catch(pdfErr){
                          console.warn("Fiche d'accès ERP non générée :",pdfErr);
                          showToast("Compte créé, mais la fiche PDF n'a pas pu être générée : "+pdfErr.message,false);
                        }
                      }}>{creatingAuthAccount?"Création...":"Créer le compte de connexion"}</button>}
                    </div>
                  )}
                  {form.id&&form.auth_user_id&&(
                    <div className="card" style={{marginBottom:16}}>
                      <div style={{fontSize:11,color:"#1E7A2D",marginBottom:10,display:"flex",alignItems:"center",gap:5}}>✓ Compte de connexion actif — @{form.username}</div>
                      <div style={{fontSize:11,color:"#5B6B62",marginBottom:10}}>Employé bloqué ou mot de passe oublié ? Génère-lui un nouveau mot de passe temporaire — l'ancien cessera immédiatement de fonctionner.</div>
                      {!session.gestion_comptes_autorisee&&<div style={{fontSize:11,color:"#5B6B62"}}>Seule une personne autorisée à gérer les accès peut réinitialiser ce mot de passe — demande à la direction.</div>}
                      {session.gestion_comptes_autorisee&&<button className="btn btn-ghost btn-sm" disabled={resettingPassword} onClick={async()=>{
                        if (!window.confirm(`Réinitialiser le mot de passe de ${form.nom} ? L'ancien mot de passe cessera immédiatement de fonctionner.`)) return;
                        setResettingPassword(true);
                        const tempPassword=(()=>{const chars="ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";const bytes=new Uint32Array(10);window.crypto.getRandomValues(bytes);let out="";for(let i=0;i<10;i++)out+=chars[bytes[i]%chars.length];return out;})();
                        try {
                          const { data: { session: authSession } } = await supabase.auth.getSession();
                          const res = await fetch("/api/reset-employee-password", {
                            method:"POST",
                            headers:{"Content-Type":"application/json", ...(authSession?.access_token?{"Authorization":"Bearer "+authSession.access_token}:{})},
                            body: JSON.stringify({ id: form.id, password: tempPassword })
                          });
                          const result = await res.json().catch(()=>({}));
                          setResettingPassword(false);
                          if (!res.ok || !result.ok) { showToast("Erreur : "+(result.error||"inconnue"), false); return; }
                          showToast("Mot de passe réinitialisé ✓");
                          try{
                            setAccesView({employe:form,credentials:{username:form.username,password:tempPassword}});
                          }catch(pdfErr){
                            console.warn("Fiche d'accès ERP non générée :",pdfErr);
                            showToast("Mot de passe réinitialisé, mais la fiche PDF n'a pas pu être générée : "+pdfErr.message,false);
                          }
                        } catch(e) { showToast("Erreur : "+e.message, false); setResettingPassword(false); }
                      }}>{resettingPassword?"Réinitialisation...":"Réinitialiser le mot de passe"}</button>}
                    </div>
                  )}
                  <div style={{marginBottom:14}}>
                    <label className="lbl">Fonction(s) ERP</label>
                    <div style={{fontSize:11,color:"#5B6B62",marginBottom:6}}>Un employé peut cumuler plusieurs fonctions (ex : Commercial et Comptable en même temps).</div>
                    <div style={{background:"#F1F5F3",borderRadius:6,padding:8}}>{FONCTIONS_OPTIONS.map(([v,l])=>{
                      const fonctions=Array.isArray(form.fonctions)?form.fonctions:[];
                      const checked=fonctions.includes(v);
                      return <div key={v} className="chk" onClick={()=>setForm(f=>{
                        const cur=Array.isArray(f.fonctions)?f.fonctions:[];
                        const next=checked?cur.filter(x=>x!==v):[...cur,v];
                        return {...f, fonctions:next, role:rolePrincipalDeFonctions(next)};
                      })}><div style={{width:18,height:18,borderRadius:3,border:"2px solid "+(checked?"#2F9E3F":"#CBD5D0"),background:checked?"#2F9E3F":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{checked&&<span style={{color:"#FFFFFF",fontSize:12,fontWeight:700}}>✓</span>}</div><span style={{color:checked?"#0F1F17":"#5B6B62"}}>{l}</span></div>;
                    })}</div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:"0 10px"}}>
                    {fld("Username","username")}{fld(form.id?"Nouveau mot de passe":"Mot de passe","password","text")}
                  </div>
                  {!form.id&&<div style={{fontSize:11,color:"#5B6B62",marginTop:-6,marginBottom:14}}>Laisse ces deux champs vides pour enregistrer l'employé sans compte de connexion — tu pourras générer ses accès plus tard depuis cet onglet, en rouvrant sa fiche.</div>}
                  <div style={{marginBottom:14}}><label className="lbl">Modules accessibles</label><div style={{background:"#F1F5F3",borderRadius:6,padding:8,maxHeight:230,overflowY:"auto"}}>{ALL_MODULES.map(m=>{const perms=form.permissions||[];const checked=perms.includes(m.id);return <div key={m.id} className="chk" onClick={()=>setForm(f=>({...f,permissions:checked?perms.filter(p=>p!==m.id):[...perms,m.id]}))}><div style={{width:18,height:18,borderRadius:3,border:"2px solid "+(checked?"#2F9E3F":"#CBD5D0"),background:checked?"#2F9E3F":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{checked&&<span style={{color:"#FFFFFF",fontSize:12,fontWeight:700}}>✓</span>}</div><span style={{color:checked?"#0F1F17":"#5B6B62"}}>{m.label}</span></div>})}</div></div>
                  {form.id&&<div className="fld"><label className="lbl">Compte applicatif</label><select className="inp" value={form.actif===false?"inactif":"actif"} onChange={e=>{
                    const val=e.target.value==="actif";
                    setForm(f=>({
                      ...f,
                      actif:val,
                      // Garde "Statut RH" synchronisé avec ce bouton tant qu'il n'a pas une valeur
                      // plus précise (Congé, Suspendu...) — sinon le badge pourrait rester rouge
                      // avec le mot "Actif" affiché, ou vert avec "Inactif".
                      statut_rh:(!f.statut_rh||["actif","inactif"].includes(String(f.statut_rh).toLowerCase()))?(val?"Actif":"Inactif"):f.statut_rh
                    }));
                  }}><option value="actif">Actif</option><option value="inactif">Inactif</option></select></div>}
                  {form.id&&session.gestion_comptes_autorisee&&(
                    <div className="card" style={{marginTop:18}}>
                      <div style={{fontSize:13,color:"#1E8A32",fontWeight:700,marginBottom:6}}>Gestion des accès</div>
                      <div style={{fontSize:11,color:"#5B6B62",marginBottom:10}}>Autorise cette personne à créer des comptes de connexion et réinitialiser des mots de passe pour les autres employés. Réserve ça aux personnes ayant une vraie fonction de direction.</div>
                      <div style={{display:"flex",alignItems:"center",gap:10,cursor:updatingGestionComptes?"default":"pointer",opacity:updatingGestionComptes?0.6:1}} onClick={async()=>{
                        if (updatingGestionComptes) return;
                        const nouvelleValeur = !form.gestion_comptes_autorisee;
                        setUpdatingGestionComptes(true);
                        const { error } = await supabase.rpc("definir_gestion_comptes", { p_employe_id: form.id, p_autorise: nouvelleValeur });
                        setUpdatingGestionComptes(false);
                        if (error) return showToast("Erreur : "+error.message, false);
                        setForm(f=>({...f,gestion_comptes_autorisee:nouvelleValeur}));
                        await load();
                        showToast(nouvelleValeur?"Autorisation accordée ✓":"Autorisation retirée ✓");
                      }}>
                        <div style={{width:20,height:20,borderRadius:3,border:"2px solid "+(form.gestion_comptes_autorisee?"#2F9E3F":"#CBD5D0"),background:form.gestion_comptes_autorisee?"#2F9E3F":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {form.gestion_comptes_autorisee&&<span style={{color:"#FFFFFF",fontSize:13,fontWeight:700}}>✓</span>}
                        </div>
                        <span style={{fontSize:13,color:form.gestion_comptes_autorisee?"#0F1F17":"#5B6B62"}}>Autorisé(e) à gérer les comptes de connexion</span>
                      </div>
                    </div>
                  )}
                  {form.id&&<div className="card" style={{marginTop:18,borderColor:"rgba(214,90,90,.45)"}}>
                    <div style={{fontSize:13,color:"#D6453D",fontWeight:700,marginBottom:6}}>Zone sensible</div>
                    <div style={{fontSize:11,color:"#5B6B62",marginBottom:10}}>Supprime définitivement cette fiche (historique, congés, évaluations, documents RH, compte de connexion). Pour un employé qui quitte réellement l'entreprise, préfère passer son statut sur "Inactif" ci-dessus plutôt que de le supprimer — la suppression est surtout utile pour corriger une fiche créée par erreur. Refusée automatiquement s'il y a de la paie ou une procuration signée enregistrée.</div>
                    <button className="btn btn-sm" style={{background:"transparent",border:"1px solid #D65A5A",color:"#D6453D"}} disabled={deletingEmploye} onClick={deleteEmploye}>{deletingEmploye?"Suppression...":"Supprimer définitivement l'employé"}</button>
                  </div>}
                </>}
                {form.id&&tab!=="historique"&&<div className="fld"><label className="lbl">Motif de la modification</label><input className="inp" value={form.motif_modification||""} onChange={e=>setForm({...form,motif_modification:e.target.value})} placeholder="Ex. promotion, mutation, révision annuelle…"/></div>}
                <div style={{display:"flex",gap:9,marginTop:18}}><button className="btn btn-gold" style={{flex:1}} onClick={saveEmploye} disabled={loading}>{loading?"...":form.id?"Enregistrer la fiche":"Créer l'employé"}</button><button className="btn btn-ghost" onClick={closeModal}>Fermer</button></div>
              </>;
            })()}


            {modal==="congeRH"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier la demande":"Nouvelle demande de congé"}</div>
              <div className="fld"><label className="lbl">Employé *</label><select className="inp" value={form.employe_id||""} onChange={e=>setForm({...form,employe_id:e.target.value})}><option value="">Choisir...</option>{employes.map(e=><option key={e.id} value={e.id}>{[e.prenom,e.nom].filter(Boolean).join(" ")} — {e.ville}</option>)}</select></div>
              <div className="fld"><label className="lbl">Type</label><select className="inp" value={form.type_conge||"Congé annuel"} onChange={e=>setForm({...form,type_conge:e.target.value})}>{["Congé annuel","Maladie","Maternité","Paternité","Permission","Sans solde","Autre"].map(x=><option key={x}>{x}</option>)}</select></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}><div className="fld"><label className="lbl">Début *</label><input type="date" className="inp" value={form.date_debut||today()} onChange={e=>setForm({...form,date_debut:e.target.value})}/></div><div className="fld"><label className="lbl">Fin *</label><input type="date" className="inp" value={form.date_fin||today()} onChange={e=>setForm({...form,date_fin:e.target.value})}/></div></div>
              <div className="fld"><label className="lbl">Motif</label><textarea className="inp" rows="3" value={form.motif||""} onChange={e=>setForm({...form,motif:e.target.value})}/></div>
              <div className="fld"><label className="lbl">Justificatif (URL)</label><input className="inp" value={form.justificatif_url||""} onChange={e=>setForm({...form,justificatif_url:e.target.value})}/></div>
              <div style={{display:"flex",gap:9,marginTop:18}}><button className="btn btn-gold" style={{flex:1}} onClick={saveCongeRH} disabled={rhSaving}>{rhSaving?"...":"Enregistrer"}</button><button className="btn btn-ghost" onClick={closeModal}>Fermer</button></div>
            </>}

            {modal==="jourFerie"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier le jour férié":"Ajouter un jour férié"}</div>
              <div className="fld"><label className="lbl">Date *</label><input type="date" className="inp" value={form.date||today()} onChange={e=>setForm({...form,date:e.target.value})}/></div>
              <div className="fld"><label className="lbl">Libellé *</label><input className="inp" placeholder="Ex. Fête du Travail" value={form.libelle||""} onChange={e=>setForm({...form,libelle:e.target.value})}/></div>
              <label style={{display:"flex",gap:8,alignItems:"center",fontSize:12,color:"#5B6B62"}}><input type="checkbox" checked={!!form.mobile} onChange={e=>setForm({...form,mobile:e.target.checked})}/> Fête mobile (date estimée, à reconfirmer chaque année)</label>
              <div style={{display:"flex",gap:9,marginTop:18}}><button className="btn btn-gold" style={{flex:1}} onClick={saveJourFerie} disabled={rhSaving}>{rhSaving?"...":"Enregistrer"}</button><button className="btn btn-ghost" onClick={closeModal}>Fermer</button></div>
            </>}

            {modal==="documentRH"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier le document":"Ajouter un document RH"}</div>
              <div className="fld"><label className="lbl">Employé *</label><select className="inp" value={form.employe_id||""} onChange={e=>setForm({...form,employe_id:e.target.value})}><option value="">Choisir...</option>{employes.map(e=><option key={e.id} value={e.id}>{[e.prenom,e.nom].filter(Boolean).join(" ")}</option>)}</select></div>
              <div className="fld"><label className="lbl">Type *</label><select className="inp" value={form.type_document||"Contrat"} onChange={e=>setForm({...form,type_document:e.target.value})}>{["Contrat","CNI","CV","Diplôme","Permis","Photo","Signature","Bulletin de paie","Autre"].map(x=><option key={x}>{x}</option>)}</select></div>
              <div className="fld"><label className="lbl">Titre</label><input className="inp" value={form.titre||""} onChange={e=>setForm({...form,titre:e.target.value})}/></div>
              <div className="fld"><label className="lbl">Lien du fichier *</label><input className="inp" placeholder="https://..." value={form.fichier_url||""} onChange={e=>setForm({...form,fichier_url:e.target.value})}/></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}><div className="fld"><label className="lbl">Date document</label><input type="date" className="inp" value={form.date_document||""} onChange={e=>setForm({...form,date_document:e.target.value})}/></div><div className="fld"><label className="lbl">Expiration</label><input type="date" className="inp" value={form.date_expiration||""} onChange={e=>setForm({...form,date_expiration:e.target.value})}/></div></div>
              <label style={{display:"flex",gap:8,alignItems:"center",fontSize:12,color:"#5B6B62"}}><input type="checkbox" checked={form.confidentiel!==false} onChange={e=>setForm({...form,confidentiel:e.target.checked})}/> Document confidentiel</label>
              <div className="fld" style={{marginTop:10}}><label className="lbl">Notes</label><textarea className="inp" rows="2" value={form.notes||""} onChange={e=>setForm({...form,notes:e.target.value})}/></div>
              <div style={{display:"flex",gap:9,marginTop:18}}><button className="btn btn-gold" style={{flex:1}} onClick={saveDocumentRH} disabled={rhSaving}>{rhSaving?"...":"Enregistrer"}</button><button className="btn btn-ghost" onClick={closeModal}>Fermer</button></div>
            </>}

            {modal==="evaluationRH"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier l'évaluation":"Nouvelle évaluation"}</div>
              <div className="fld"><label className="lbl">Employé *</label><select className="inp" value={form.employe_id||""} onChange={e=>setForm({...form,employe_id:e.target.value})}><option value="">Choisir...</option>{employes.map(e=><option key={e.id} value={e.id}>{[e.prenom,e.nom].filter(Boolean).join(" ")}</option>)}</select></div>
              <div className="fld"><label className="lbl">Type</label><input className="inp" value={form.type_evaluation||"Évaluation périodique"} onChange={e=>setForm({...form,type_evaluation:e.target.value})}/></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}><div className="fld"><label className="lbl">Période début</label><input type="date" className="inp" value={form.periode_debut||""} onChange={e=>setForm({...form,periode_debut:e.target.value})}/></div><div className="fld"><label className="lbl">Période fin</label><input type="date" className="inp" value={form.periode_fin||""} onChange={e=>setForm({...form,periode_fin:e.target.value})}/></div></div>
              <div className="fld"><label className="lbl">Objectifs</label><textarea className="inp" rows="2" value={form.objectifs||""} onChange={e=>setForm({...form,objectifs:e.target.value})}/></div>
              <div className="fld"><label className="lbl">Résultats</label><textarea className="inp" rows="2" value={form.resultats||""} onChange={e=>setForm({...form,resultats:e.target.value})}/></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}><div className="fld"><label className="lbl">Note /100</label><input type="number" min="0" max="100" className="inp" value={form.note??""} onChange={e=>setForm({...form,note:e.target.value})}/></div><div className="fld"><label className="lbl">Statut</label><select className="inp" value={form.statut||"Brouillon"} onChange={e=>setForm({...form,statut:e.target.value})}><option>Brouillon</option><option>En cours</option><option>Validée</option></select></div></div>
              <div className="fld"><label className="lbl">Points forts</label><textarea className="inp" rows="2" value={form.points_forts||""} onChange={e=>setForm({...form,points_forts:e.target.value})}/></div>
              <div className="fld"><label className="lbl">Axes d'amélioration</label><textarea className="inp" rows="2" value={form.axes_amelioration||""} onChange={e=>setForm({...form,axes_amelioration:e.target.value})}/></div>
              <div className="fld"><label className="lbl">Recommandations</label><textarea className="inp" rows="2" value={form.recommandations||""} onChange={e=>setForm({...form,recommandations:e.target.value})}/></div>
              <div style={{display:"flex",gap:9,marginTop:18}}><button className="btn btn-gold" style={{flex:1}} onClick={saveEvaluationRH} disabled={rhSaving}>{rhSaving?"...":"Enregistrer"}</button><button className="btn btn-ghost" onClick={closeModal}>Fermer</button></div>
            </>}


            {modal==="lot"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>{form.id?"Modifier lot":"Nouveau lot"}</div>
              <div className="fld"><label className="lbl">Produit *</label>
                <select className="inp" value={form.produit||""} onChange={e=>setForm({...form,produit:e.target.value})}>
                  <option value="">Choisir...</option>{produits.map(p=><option key={p.id} value={p.nom}>{p.code?p.code+" · ":""}{p.nom}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Date</label><input type="date" className="inp" value={form.date||today()} onChange={e=>setForm({...form,date:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Quantité (bouteilles produites)</label><input type="number" className="inp" value={form.quantite||""} onChange={e=>setForm({...form,quantite:e.target.value})} placeholder="Renseignable en fin de production"/></div>
              {form.produit&&<div className="fld">
                <label className="lbl">Bouteilles vides utilisées</label>
                <input type="number" className="inp" value={form.bouteilles_utilisees??""} onChange={e=>setForm({...form,bouteilles_utilisees:e.target.value})} placeholder="Optionnel — pour le contrôle double"/>
                <div style={{fontSize:10,color:"#2F6FDE",marginTop:3}}>Stock de bouteilles vides disponible (toutes saveurs) : {NUM(stockBouteillesVides()+(form.id?toFiniteNumber(lots.find(l=>String(l.id)===String(form.id))?.bouteilles_utilisees):0))} btl</div>
              </div>}
              <div className="fld"><label className="lbl">Statut</label>
                <select className="inp" value={form.statut||"En stock"} onChange={e=>setForm({...form,statut:e.target.value})}>
                  {["En stock","Distribué","Retour qualité"].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Notes</label><input type="text" className="inp" value={form.notes||""} onChange={e=>setForm({...form,notes:e.target.value})} /></div>
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={saveLot} disabled={loading}>{loading?"...":form.id?"Mettre à jour":"Créer"}</button>
                <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
              </div>
            </>}

            {modal==="receptionBouteille"&&<>
              <div style={{fontSize:19,color:"#1E8A32",marginBottom:18}}>Réception de bouteilles vides</div>
              <div style={{fontSize:11,color:"#2F6FDE",marginBottom:14}}>Les bouteilles vides forment un stock commun, partagé entre toutes les saveurs.</div>
              <div className="fld"><label className="lbl">Date</label><input type="date" className="inp" value={form.date||today()} onChange={e=>setForm({...form,date:e.target.value})}/></div>
              <div className="fld"><label className="lbl">Quantité reçue *</label><input type="number" className="inp" value={form.quantite||""} onChange={e=>setForm({...form,quantite:e.target.value})}/></div>
              <div className="fld"><label className="lbl">Fournisseur</label><input className="inp" value={form.fournisseur||""} onChange={e=>setForm({...form,fournisseur:e.target.value})}/></div>
              <div className="fld"><label className="lbl">Notes</label><input className="inp" value={form.notes||""} onChange={e=>setForm({...form,notes:e.target.value})}/></div>
              <div style={{display:"flex",gap:9,marginTop:18}}>
                <button className="btn btn-gold" style={{flex:1}} onClick={saveReceptionBouteille} disabled={loading}>{loading?"...":"Enregistrer"}</button>
                <button className="btn btn-ghost" onClick={closeModal}>Annuler</button>
              </div>
            </>}

          </div>
        </div>
      )}

      {/* BANDEAU : activer les alertes sur cet appareil */}
      {session&&!bandeauAlertesOff&&!notifOuvert&&(pushEtat==="a_activer"||(pushEtat==="non_supporte"&&estIOS()&&!estAppInstallee()))&&(alertesPref.messages||alertesPref.livraisons)&&(
        <div role="status" style={{position:"fixed",left:12,right:12,top:isMobile?64:72,zIndex:240,maxWidth:470,margin:"0 auto",background:"#FFFFFF",border:"1px solid #BFE3C6",borderRadius:14,boxShadow:"0 10px 30px rgba(15,31,23,.18)",padding:"12px 14px",display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <Bell size={18} style={{color:"#1E7A2D",flexShrink:0}}/>
          <div style={{flex:"1 1 190px",minWidth:0}}>
            <div style={{fontSize:13,fontWeight:700,color:"#0F1F17"}}>Activer les alertes</div>
            <div style={{fontSize:12,color:"#4B5A52",lineHeight:1.45}}>{pushEtat==="a_activer"
              ? "Reçois les messages et les livraisons qui te concernent, même l'application fermée."
              : "Sur iPhone : touche Partager dans Safari, puis « Sur l'écran d'accueil », et ouvre l'app depuis cette icône pour recevoir les alertes."}</div>
          </div>
          <div style={{display:"flex",gap:8,flexShrink:0}}>
            {pushEtat==="a_activer"&&<button type="button" className="btn btn-gold btn-sm" disabled={pushBusy} onClick={async()=>{ await activerPush(); setBandeauAlertesOff(true); }}>{pushBusy?"…":"Activer"}</button>}
            <button type="button" className="btn btn-ghost btn-sm" onClick={repousserBandeauAlertes}>Plus tard</button>
          </div>
        </div>
      )}

      {/* NOTIFICATIONS */}
      {notifOuvert&&session&&(
        <div className="overlay" onClick={()=>setNotifOuvert(false)} style={{alignItems:"flex-start",justifyContent:isMobile?"center":"flex-end",paddingTop:isMobile?56:62}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:430,padding:0,maxHeight:"78vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"14px 16px",borderBottom:"1px solid #E3E9E6",flexShrink:0}}>
              <div style={{fontSize:17,color:"#1E8A32",display:"flex",alignItems:"center",gap:8}}><Bell size={17}/> Notifications{notifNonLues>0&&<span style={{fontSize:11,background:"#D6453D",color:"#FFFFFF",borderRadius:9,padding:"1px 7px",fontWeight:700}}>{notifNonLues}</span>}</div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                {notifNonLues>0&&<button type="button" className="btn btn-ghost btn-sm" onClick={()=>marquerNotifsLues(notifs.filter(n=>!n.lue).map(n=>n.id))}>Tout marquer comme lu</button>}
                <button type="button" aria-label="Fermer" onClick={()=>setNotifOuvert(false)} style={{background:"none",border:"none",color:"#5B6B62",cursor:"pointer",padding:6}}><X size={18}/></button>
              </div>
            </div>
            <div style={{padding:"9px 16px",borderBottom:"1px solid #E3E9E6",flexShrink:0,fontSize:12,color:"#2B3A32",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
              {pushEtat==="verif"&&<span>Vérification de cet appareil…</span>}
              {pushEtat==="non_supporte"&&<span>{estIOS()&&!estAppInstallee()
                ? "Sur iPhone / iPad : touche le bouton Partager de Safari, puis « Sur l'écran d'accueil », et ouvre l'app depuis cette icône pour activer les notifications."
                : "Cet appareil ne permet pas les notifications hors de l'app."}</span>}
              {pushEtat==="refuse"&&<span>Notifications bloquées sur cet appareil : autorise-les dans les réglages du navigateur ou de l'app, puis reviens ici.</span>}
              {pushEtat==="a_activer"&&<><span>Reçois les alertes même app fermée.</span><button type="button" className="btn btn-gold btn-sm" disabled={pushBusy} onClick={activerPush}>{pushBusy?"Activation…":"Activer sur cet appareil"}</button></>}
              {pushEtat==="actif"&&<><span style={{color:"#1E7A2D"}}>✓ Notifications actives sur cet appareil</span><button type="button" className="btn btn-ghost btn-sm" disabled={pushBusy} onClick={desactiverPush}>Désactiver</button></>}
              {pushMsg&&<span style={{flexBasis:"100%",color:"#0F1F17"}}>{pushMsg}</span>}
            </div>
            <div style={{padding:"9px 16px",borderBottom:"1px solid #E3E9E6",flexShrink:0,fontSize:12,color:"#2B3A32"}}>
              <div style={{fontWeight:700,marginBottom:6}}>Mes alertes</div>
              <label style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,cursor:"pointer"}}><input type="checkbox" checked={alertesPref.messages} onChange={e=>changerAlerte("messages",e.target.checked)} style={{width:16,height:16}}/> Messages (privés et salon général)</label>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}><input type="checkbox" checked={alertesPref.livraisons} onChange={e=>changerAlerte("livraisons",e.target.checked)} style={{width:16,height:16}}/> Livraisons à réceptionner</label>
            </div>
            <div style={{overflowY:"auto",WebkitOverflowScrolling:"touch",flex:1}}>
              {notifs.length===0&&<div style={{padding:28,textAlign:"center",color:"#5B6B62",fontSize:13}}>Aucune notification pour le moment.<br/>{estProprietaire()?"Tu seras prévenu dès que quelqu'un ajoute quelque chose dans l'ERP.":"Tu seras prévenu ici des messages et des livraisons qui te concernent."}</div>}
              {notifs.map(n=>(
                <button key={n.id} type="button" onClick={()=>ouvrirNotif(n)}
                  style={{display:"block",width:"100%",textAlign:"left",border:"none",borderBottom:"1px solid rgba(15,31,23,0.066)",background:n.lue?"transparent":"rgba(47,158,63,0.080)",color:"#0F1F17",padding:"11px 16px",cursor:"pointer",fontFamily:"inherit"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    {!n.lue&&<span style={{width:8,height:8,borderRadius:"50%",background:"#D6453D",flexShrink:0}}/>}
                    <span style={{fontSize:13,fontWeight:700,color:n.lue?"#0F1F17":"#0F1F17"}}>{n.titre}</span>
                  </div>
                  <div style={{fontSize:12,color:"#2B3A32",marginTop:3,overflowWrap:"anywhere"}}>{n.message}</div>
                  <div style={{fontSize:10,color:"#5B6B62",marginTop:4}}>{n.acteur_nom}{n.ville?" · "+n.ville:""} · {notifIlYa(n.created_at)}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PROFIL */}
      {showProfile&&(
        <div className="overlay" onClick={()=>setShowProfile(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:19,color:"#1E8A32",marginBottom:6,display:"flex",alignItems:"center",gap:8}}><User size={18}/> Mon profil</div>
            <div style={{fontSize:12,color:"#5B6B62",marginBottom:14}}>@{session.username} · {session.matricule}</div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:18}}>
              <label style={{cursor:profilPhotoBusy?"wait":"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                <div style={{width:112,height:112,borderRadius:"50%",border:maPhotoUrl()?"3px solid #2F9E3F":"2px dashed #2F9E3F",background:"#F1F5F3",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",textAlign:"center"}}>
                  {maPhotoUrl()
                    ? <img alt="Ma photo" src={maPhotoUrl()} style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"center top"}}/>
                    : <div style={{padding:12,fontSize:12,lineHeight:1.35,color:"#0F1F17",fontWeight:600}}><User size={26} style={{display:"block",margin:"0 auto 4px"}}/>Charge une photo ici</div>}
                </div>
                <span style={{fontSize:12,color:"#1E8A32",fontWeight:700}}>{profilPhotoBusy?"Enregistrement…":(maPhotoUrl()?"Changer ma photo":"Touche le cadre pour choisir")}</span>
                <input type="file" accept="image/*" style={{display:"none"}} disabled={profilPhotoBusy} onChange={async ev=>{
                  const inputEl=ev.target; const file=inputEl.files&&inputEl.files[0];
                  if(!file) return;
                  setProfilPhotoBusy(true); setProfilPhotoMsg(null);
                  try{
                    const data=await photoFileToDataUrl(file);
                    const {error}=await supabase.rpc("maj_ma_photo",{p_photo:data});
                    if(error) throw new Error(error.message);
                    setEmployes(list=>list.map(x=>String(x.id)===String(session.id)?{...x,photo_url:data}:x));
                    setProfilPhotoMsg({ok:true,txt:"✓ Photo enregistrée"});
                  }catch(err){
                    setProfilPhotoMsg({ok:false,txt:"✗ "+(err&&err.message||"Photo impossible à enregistrer")});
                  }
                  setProfilPhotoBusy(false);
                  try{ inputEl.value=""; }catch(_){}
                }}/>
              </label>
              {maPhotoUrl()&&!profilPhotoBusy&&<button type="button" className="btn btn-ghost btn-sm" style={{marginTop:6}} onClick={async()=>{
                setProfilPhotoBusy(true); setProfilPhotoMsg(null);
                const {error}=await supabase.rpc("maj_ma_photo",{p_photo:""});
                if(error) setProfilPhotoMsg({ok:false,txt:"✗ "+error.message});
                else { setEmployes(list=>list.map(x=>String(x.id)===String(session.id)?{...x,photo_url:""}:x)); setProfilPhotoMsg({ok:true,txt:"Photo retirée"}); }
                setProfilPhotoBusy(false);
              }}>Retirer ma photo</button>}
              {profilPhotoMsg&&<div style={{fontSize:12,fontWeight:700,marginTop:8,color:profilPhotoMsg.ok?"#1E7A2D":"#D6453D",textAlign:"center"}}>{profilPhotoMsg.txt}</div>}
            </div>
            <div style={{background:"rgba(47,158,63,0.048)",border:"1px solid rgba(47,158,63,0.160)",borderRadius:6,padding:"12px 14px",marginBottom:20,fontSize:13,lineHeight:1.8}}>
              <div><strong style={{color:"#0F1F17"}}>{session.nom}</strong></div>
              <div style={{color:"#5B6B62"}}>{session.poste} · {session.service}</div>
              <div style={{color:"#5B6B62"}}>{session.email}</div>
            </div>
            <div style={{fontSize:13,color:"#1E8A32",marginBottom:14,fontWeight:600}}>Changer le mot de passe</div>
            <div className="fld"><label className="lbl">Ancien mot de passe</label><input type="password" className="inp" value={profileForm.ancienPwd} onChange={e=>setProfileForm(f=>({...f,ancienPwd:e.target.value}))} /></div>
            <div className="fld"><label className="lbl">Nouveau mot de passe</label><input type="password" className="inp" value={profileForm.nouveauPwd} onChange={e=>setProfileForm(f=>({...f,nouveauPwd:e.target.value}))} /></div>
            <div className="fld"><label className="lbl">Confirmer</label><input type="password" className="inp" value={profileForm.confirmPwd} onChange={e=>setProfileForm(f=>({...f,confirmPwd:e.target.value}))} /></div>
            <div style={{display:"flex",gap:9,marginTop:18,marginBottom:26}}>
              <button className="btn btn-gold" style={{flex:1}} onClick={changePassword} disabled={loading}>{loading?"...":"Changer"}</button>
            </div>

            <hr className="divider"/>
            <div style={{fontSize:13,color:"#1E8A32",margin:"18px 0 10px",fontWeight:600}}>Mes équipements</div>
            {(() => {
              const mesEquip = immobilisations.filter(i=>String(i.employe_id)===String(session.id));
              return mesEquip.length===0 ? (
                <div style={{color:"#5B6B62",fontSize:12,marginBottom:16}}>Aucun équipement affecté pour l'instant.</div>
              ) : (
                <div style={{marginBottom:16,display:"flex",flexDirection:"column",gap:6}}>
                  {mesEquip.map(i=>(
                    <div key={i.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:5,padding:"8px 10px"}}>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:12,color:"#0F1F17",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{i.designation}{i.marque?" — "+i.marque:""}{i.modele?" "+i.modele:""}</div>
                        <div style={{fontSize:10,color:"#5B6B62"}}>{i.numero_inventaire}</div>
                      </div>
                      <button className="btn btn-ghost btn-sm" style={{flexShrink:0}} onClick={async()=>{
                        try {
                          const signatures = await openSignatureFlow("Fiche de remise — "+(i.designation||"matériel"), [{key:"collaborateur",label:"Ma signature"}]);
                          const pdf = await generateFicheRemisePdfBase64(i, session, entreprise, signatures);
                          await openPdf(pdf,"Fiche de remise");
                        } catch(e) { showToast("Erreur : "+e.message,false); }
                      }}>Fiche de remise</button>
                    </div>
                  ))}
                </div>
              );
            })()}

            <hr className="divider"/>
            <div style={{fontSize:13,color:"#1E8A32",margin:"18px 0 10px",fontWeight:600}}>Mes fiches de paie</div>
            {(() => {
              const mesBulletins = bulletinsPaie.filter(b=>String(b.employe_id)===String(session.id));
              return mesBulletins.length===0 ? (
                <div style={{color:"#5B6B62",fontSize:12,marginBottom:16}}>Aucune fiche de paie enregistrée pour l'instant.</div>
              ) : (
                <div style={{marginBottom:16,display:"flex",flexDirection:"column",gap:6}}>
                  {mesBulletins.map(b=>{
                    const moisLbl = new Date(b.mois+"-01").toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                    return (
                      <div key={b.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:5,padding:"8px 10px"}}>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:12,color:"#0F1F17",textTransform:"capitalize"}}>{moisLbl}</div>
                          <div style={{fontSize:10,color:"#5B6B62",marginBottom:3}}>Net : {FCFA(b.net)}</div>
                          {ficheChip(b)}
                        </div>
                        <div style={{display:"flex",gap:6,flexShrink:0}}>
                          {ficheEtat(b)==="a_signer"&&<button className="btn btn-gold btn-sm" disabled={ficheBusy} onClick={()=>ficheSigner(b)}>Signer</button>}
                          <button className="btn btn-ghost btn-sm" onClick={()=>ficheOuvrirPdf(b,true)}>Voir</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            <hr className="divider"/>
            <div style={{fontSize:13,color:"#1E8A32",margin:"18px 0 10px",fontWeight:600}}>Mes documents RH</div>
            <div style={{fontSize:10,color:"#8A978F",marginTop:-6,marginBottom:10}}>Contrat de travail, pièce d'identité, diplômes... — dépose ici, ou consulte ce que la direction a déjà partagé.</div>
            {(() => {
              const mesDocs = documentsEmployes.filter(d=>String(d.employe_id)===String(session.id));
              return mesDocs.length===0 ? (
                <div style={{color:"#5B6B62",fontSize:12,marginBottom:16}}>Aucun document pour l'instant.</div>
              ) : (
                <div style={{marginBottom:16,display:"flex",flexDirection:"column",gap:6}}>
                  {mesDocs.map(d=>(
                    <div key={d.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:5,padding:"8px 10px"}}>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:12,color:"#0F1F17",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.titre}</div>
                        <div style={{fontSize:10,color:"#5B6B62"}}>{d.type_document}</div>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                        {d.statut==="En attente"
                          ? <span className="badge" style={{color:"#1E8A32",background:"rgba(47,158,63,0.096)"}}>En attente</span>
                          : <span className="badge" style={{color:"#1E7A2D",background:"rgba(47,158,63,0.12)"}}>Validé</span>}
                        {d.fichier_url&&<button className="btn btn-ghost btn-sm" onClick={()=>openMyDocument(d)}>Voir</button>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="fld"><label className="lbl">Type de document</label>
              <select className="inp" value={myDocForm.type_document} onChange={e=>setMyDocForm(f=>({...f,type_document:e.target.value}))}>
                <option value="">Choisir...</option>
                {["Contrat de travail","Pièce d'identité","Diplôme / Certification","Justificatif de domicile","Autre"].map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="fld"><label className="lbl">Titre</label><input type="text" className="inp" placeholder="ex: CNI recto-verso" value={myDocForm.titre} onChange={e=>setMyDocForm(f=>({...f,titre:e.target.value}))} /></div>
            <div className="fld"><label className="lbl">Fichier</label>
              <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="inp" onChange={e=>setMyDocForm(f=>({...f,file:e.target.files?.[0]||null}))} />
              <div style={{fontSize:10,color:"#5B6B62",marginTop:4}}>PDF, JPG, PNG ou WEBP — max 10 Mo. Le document sera visible par la direction pour validation.</div>
            </div>
            <button className="btn btn-gold" style={{width:"100%",marginBottom:18}} disabled={myDocSaving} onClick={saveMyDocument}>{myDocSaving?"Envoi...":"Envoyer pour validation"}</button>

            <div style={{display:"flex",gap:9}}>
              <button className="btn btn-ghost" style={{flex:1}} onClick={()=>setShowProfile(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODIFIER FACTURE */}
      {editFac&&(
        <div className="overlay" onClick={()=>setEditFac(null)}>
          <div className="modal" style={{maxWidth:600}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:19,color:"#1E8A32",marginBottom:6}}>✏ Modifier facture</div>
            <div style={{fontSize:11,color:"#1E8A32",fontFamily:"monospace",marginBottom:18}}>{editFac.numero}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div className="fld"><label className="lbl">Client</label>
                <select className="inp" value={editFac.client_nom} onChange={e=>setEditFac(f=>({...f,client_nom:e.target.value}))}>
                  {clients.map(c=><option key={c.id} value={c.nom}>{c.nom}</option>)}
                </select>
              </div>
              <div className="fld"><label className="lbl">Statut</label>
                <div className="inp" style={{background:"#F2F5F3",color:"#5B6B62"}}>{editFac.statut||"En attente"} <span style={{fontSize:10}}>(auto)</span></div>
              </div>
              <div className="fld"><label className="lbl">Date émission</label><input type="date" className="inp" value={editFac.date_emission} onChange={e=>setEditFac(f=>({...f,date_emission:e.target.value}))} /></div>
              <div className="fld"><label className="lbl">Date échéance</label><input type="date" className="inp" value={editFac.date_echeance} onChange={e=>setEditFac(f=>({...f,date_echeance:e.target.value}))} /></div>
              <div className="fld"><label className="lbl">Remise (%)</label><input type="number" className="inp" min="0" max="100" value={editFac.remise_pct||0} onChange={e=>setEditFac(f=>({...f,remise_pct:parseFloat(e.target.value)||0}))} /></div>
              <div className="fld" style={{display:"flex",alignItems:"center",paddingTop:20}}>
                <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>setEditFac(f=>({...f,appliquer_tva:!f.appliquer_tva}))}>
                  <div style={{width:20,height:20,borderRadius:3,border:"2px solid "+(editFac.appliquer_tva!==false?"#2F9E3F":"#CBD5D0"),background:editFac.appliquer_tva!==false?"#2F9E3F":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {editFac.appliquer_tva!==false&&<span style={{color:"#FFFFFF",fontSize:13,fontWeight:700}}>✓</span>}
                  </div>
                  <span style={{fontSize:13,color:editFac.appliquer_tva!==false?"#0F1F17":"#5B6B62"}}>TVA</span>
                </div>
              </div>
            </div>
            {/* MOYEN DE RÈGLEMENT dans modification */}
            <div className="fld">
              <label className="lbl">Moyen de règlement</label>
              <SelectReglement value={editFac.reglement||""} onChange={e=>setEditFac(f=>({...f,reglement:e.target.value}))} />
            </div>
            <div className="fld"><label className="lbl">Note</label><input type="text" className="inp" value={editFac.note||""} onChange={e=>setEditFac(f=>({...f,note:e.target.value}))} /></div>
            <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"#5B6B62",marginBottom:8}}>Lignes</div>
            {editFac.lignes.map((l,i)=>(
              <div key={i} className="lr">
                <div style={{flex:2,fontSize:12}}>{libelleLigne(l.designation)}</div>
                {isDirecteur()
                  ? <input type="number" className="inp" min="1" step="1" inputMode="numeric" title="Nombre de bouteilles (modifiable)" style={{width:70,padding:"4px 6px"}} value={l.qte} onChange={e=>setEditFac(f=>({...f,lignes:majQteLigne(f.lignes,i,e.target.value)}))} />
                  : <div style={{color:"#5B6B62"}}>{NUM(l.qte)}</div>}
                <div style={{color:"#5B6B62"}}>btl ×</div>
                <input type="number" className="inp" min="0" title="Prix unitaire (modifiable)" style={{width:84,padding:"4px 6px"}} value={l.prixUnit} onChange={e=>setEditFac(f=>({...f,lignes:majPrixLigne(f.lignes,i,e.target.value)}))} />
                <div style={{color:"#1E8A32",fontWeight:600}}>{NUM(l.montant)} F</div>
                <button onClick={()=>setEditFac(f=>({...f,lignes:f.lignes.filter((_,idx)=>idx!==i)}))} style={{background:"none",border:"none",color:"#D6453D",cursor:"pointer"}}>✕</button>
              </div>
            ))}
            {isDirecteur()&&(
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr auto",gap:6,marginTop:10,alignItems:"end"}}>
                <div><label className="lbl">Ajouter un produit</label>
                  <select className="inp" value={editNewLigne.produit} onChange={e=>{const p=produits.find(pr=>pr.nom===e.target.value);setEditNewLigne(n=>({...n,produit:e.target.value,prixUnit:p?p.prix:""}));}}>
                    <option value="">...</option>{produits.map(p=><option key={p.id} value={p.nom}>{p.code?p.code+" · ":""}{p.nom}</option>)}
                  </select>
                </div>
                <div><label className="lbl">Qté</label><input type="number" className="inp" min="1" value={editNewLigne.qte} onChange={e=>setEditNewLigne(n=>({...n,qte:e.target.value}))} /></div>
                <div><label className="lbl">Prix</label><input type="number" className="inp" min="0" value={editNewLigne.prixUnit} onChange={e=>setEditNewLigne(n=>({...n,prixUnit:e.target.value}))} /></div>
                <button className="btn btn-gold btn-sm" style={{alignSelf:"flex-end"}} onClick={ajouterLigneEdit}>+</button>
              </div>
            )}
            {(()=>{
              const {ttc}=calculateInvoiceTotals(editFac,entreprise);
              const ancienne=factures.find(x=>String(x.id)===String(editFac.id));
              const dejaPaye=ancienne?totalPayeValide(ancienne):0;
              return <div style={{marginTop:12,borderTop:"1px solid #E3E9E6",paddingTop:12}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:17,color:"#1E8A32",fontWeight:700}}><span>Total TTC</span><span>{FCFA(ttc)}</span></div>
                {dejaPaye>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#5B6B62",marginTop:4}}><span>Déjà payé (validé)</span><span>{FCFA(dejaPaye)}</span></div>}
                {dejaPaye>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:600,color:ttc>dejaPaye?"#D6453D":"#1E8A32",marginTop:2}}><span>{ttc>=dejaPaye?"Nouveau reste à payer":"Trop-perçu"}</span><span>{FCFA(Math.abs(ttc-dejaPaye))}</span></div>}
              </div>;
            })()}
            <div style={{display:"flex",gap:9,marginTop:18}}>
              <button className="btn btn-gold" style={{flex:1}} onClick={updateFacture} disabled={loading}>{loading?"...":"Sauvegarder"}</button>
              <button className="btn btn-ghost" onClick={()=>setEditFac(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* APERCU FACTURE */}
      {facView&&<FacturePrint facture={facView.facture} client={facView.client} entreprise={entreprise} paiements={paiements.filter(p=>isPaiementValide(p)&&paiementFactureMatch(p,facView.facture))} avaries={avaries.filter(a=>String(a.facture_id)===String(facView.facture.id))} onClose={()=>setFacView(null)} onToggleLien={isDirecteur()?toggleLienPaiement:undefined} />}

      {/* APERCU PROFORMA */}
      {proformaView&&<FacturePrint facture={proformaView.facture} client={proformaView.client} entreprise={entreprise} onClose={()=>setProformaView(null)} isProforma={true} />}

      {/* OBJECTIF MODAL */}
      {editObjectif&&(
        <div className="overlay" onClick={()=>setEditObjectif(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:19,color:"#1E8A32",marginBottom:6,display:"flex",alignItems:"center",gap:8}}><Target size={18}/> Objectif mensuel</div>
            <div style={{fontSize:12,color:"#5B6B62",marginBottom:20}}>{editObjectif.nom} · {new Date().toLocaleDateString("fr-FR",{month:"long",year:"numeric"})}</div>
            <div className="fld"><label className="lbl">Objectif bouteilles / mois</label><input type="number" className="inp" value={objForm.bouteilles} onChange={e=>setObjForm(f=>({...f,bouteilles:parseInt(e.target.value)||0}))} min="0" /></div>
            <div className="fld"><label className="lbl">Commission (% de l'argent encaissé)</label><input type="number" className="inp" value={objForm.commission_pct||0} onChange={e=>setObjForm(f=>({...f,commission_pct:parseFloat(e.target.value)||0}))} min="0" step="0.5" /></div>
            <div className="fld"><label className="lbl">Objectif chiffre d'affaires (FCFA)</label><input type="number" className="inp" value={objForm.montant} onChange={e=>setObjForm(f=>({...f,montant:parseInt(e.target.value)||0}))} min="0" /></div>
            <div style={{display:"flex",gap:9}}>
              <button className="btn btn-gold" style={{flex:1}} onClick={()=>saveObjectif(editObjectif.id,objForm)}>Enregistrer</button>
              <button className="btn btn-ghost" onClick={()=>setEditObjectif(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* EMAIL MODAL */}
      {emailModal&&(
        <div className="overlay" onClick={()=>setEmailModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:19,color:"#1E8A32",marginBottom:4,display:"flex",alignItems:"center",gap:8}}><Mail size={18}/> Envoyer la facture</div>
            <div style={{fontSize:12,color:"#5B6B62",marginBottom:16}}>{emailModal.facture.numero} · {emailForm.client_nom}</div>
            <div style={{background:"rgba(47,158,63,0.048)",border:"1px solid rgba(47,158,63,0.160)",borderRadius:8,padding:"14px 16px",marginBottom:16}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:13}}>
                <div><span style={{color:"#5B6B62"}}>À : </span><strong style={{color:"#0F1F17"}}>{emailForm.to}</strong></div>
                <div><span style={{color:"#5B6B62"}}>Montant : </span><strong style={{color:"#1E8A32"}}>{emailForm.montant_ttc}</strong></div>
                <div><span style={{color:"#5B6B62"}}>Émission : </span><span style={{color:"#0F1F17"}}>{emailForm.date_emission}</span></div>
                <div><span style={{color:"#5B6B62"}}>Échéance : </span><span style={{color:"#D6453D"}}>{emailForm.date_echeance}</span></div>
              </div>
            </div>
            <div className="fld"><label className="lbl">Message</label>
              <textarea className="inp" rows={2} value={emailForm.message} onChange={e=>setEmailForm(f=>({...f,message:e.target.value}))} style={{resize:"none",fontSize:12}} />
            </div>
            <button className="btn btn-gold btn-icon" style={{width:"100%",padding:"14px",fontSize:15,marginBottom:8}} disabled={emailLoading} onClick={async()=>{
              if (!emailForm.to) return showToast("Pas d'adresse email",false);
              setEmailLoading(true);
              try {
                const { base64, filename } = await generateInvoicePdfBase64(emailModal.facture, emailModal.client, entreprise, false, avaries.filter(a=>String(a.facture_id)===String(emailModal.facture.id)), paiements.filter(p=>isPaiementValide(p)&&paiementFactureMatch(p,emailModal.facture)));
                const htmlBody = `<div style="font-family:Georgia,serif;color:#1a1a1a">
                  <p>Bonjour ${emailForm.client_nom},</p>
                  <p>${(emailForm.message||"Veuillez trouver ci-joint votre facture.").replace(/\n/g,"<br/>")}</p>
                  <p><strong>Facture ${emailForm.facture_numero}</strong><br/>
                  Montant TTC : ${emailForm.montant_ttc}<br/>
                  Échéance : ${emailForm.date_echeance}</p>
                  <p>Cordialement,<br/>${entreprise.entreprise_nom}</p>
                </div>`;
                const { data: { session: authSession2 } } = await supabase.auth.getSession();
                const res = await fetch("/api/send-invoice", {
                  method:"POST",
                  headers:{"Content-Type":"application/json", ...(authSession2?.access_token?{"Authorization":"Bearer "+authSession2.access_token}:{})},
                  body: JSON.stringify({
                    to: emailForm.to,
                    toName: emailForm.client_nom,
                    subject: emailForm.subject,
                    htmlBody,
                    textBody: emailForm.message||"Veuillez trouver ci-joint votre facture.",
                    attachmentBase64: base64,
                    attachmentName: filename,
                  })
                });
                const data = await res.json().catch(()=>({}));
                setEmailLoading(false);
                if (res.ok && data.ok) { showToast("Email envoyé à "+emailForm.to+" ✓ (PDF joint)"); setEmailModal(null); }
                else showToast("Erreur envoi ("+res.status+") : "+JSON.stringify(data.error||data.message||"inconnue"),false);
              } catch(e) {
                setEmailLoading(false);
                showToast("Erreur : "+e.message,false);
              }
            }}>{emailLoading?"⏳ Envoi en cours...":<><Send size={15}/> Envoyer maintenant</>}</button>
            <button className="btn btn-ghost" style={{width:"100%"}} onClick={()=>setEmailModal(null)}>Annuler</button>
          </div>
        </div>
      )}

      {/* ENVOI GÉNÉRIQUE (Documents / Proformas) — depuis l'adresse personnelle de l'expéditeur */}
      {docSendModal&&(
        <div className="overlay" onClick={()=>setDocSendModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:19,color:"#1E8A32",marginBottom:4,display:"flex",alignItems:"center",gap:8}}>
              <Mail size={18}/> Envoyer {docSendModal.kind==="proforma"?"la proforma":(docSendModal.kind==="rapport"||docSendModal.kind==="rapport_periode")?"le rapport":docSendModal.kind==="creances"?"la liste des créances":docSendModal.kind==="bulletin"?"le bulletin de paie":docSendModal.kind==="bon_commande"?"le bon de commande":"le document"}
            </div>
            <div style={{fontSize:12,color:"#5B6B62",marginBottom:16}}>
              {docSendModal.kind==="proforma"?docSendModal.data.numero:(docSendModal.kind==="rapport"||docSendModal.kind==="rapport_periode")?"Rapport financier":docSendModal.kind==="creances"?"Liste des créances clients":docSendModal.kind==="bulletin"?"Bulletin de paie":docSendModal.kind==="bon_commande"?"Bon de commande "+docSendModal.data.numero:docSendModal.data.type} · Depuis : {getSenderAddress()}
            </div>
            <div className="fld"><label className="lbl">Destinataire (email) *</label>
              <input type="email" className="inp" value={docSendForm.to} onChange={e=>setDocSendForm(f=>({...f,to:e.target.value}))} placeholder="client@exemple.com" />
            </div>
            <div className="fld"><label className="lbl">Objet</label>
              <input type="text" className="inp" value={docSendForm.subject} onChange={e=>setDocSendForm(f=>({...f,subject:e.target.value}))} />
            </div>
            <div className="fld"><label className="lbl">Message</label>
              <textarea className="inp" rows={3} value={docSendForm.message} onChange={e=>setDocSendForm(f=>({...f,message:e.target.value}))} style={{resize:"none",fontSize:12}} />
            </div>
            <button className="btn btn-gold btn-icon" style={{width:"100%",padding:"14px",fontSize:15,marginBottom:8}} disabled={docSendLoading} onClick={sendDocSendModal}>
              {docSendLoading?"⏳ Envoi en cours...":<><Send size={15}/> Envoyer maintenant</>}
            </button>
            <button className="btn btn-ghost" style={{width:"100%"}} onClick={()=>setDocSendModal(null)}>Annuler</button>
          </div>
        </div>
      )}

      {/* APERÇU LIVRAISON */}
      {livraisonView&&(
        <div className="overlay" onClick={()=>setLivraisonView(null)}>
          <div className="modal" style={{maxWidth:560}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
              <div>
                <div style={{fontSize:19,color:"#1E8A32"}}>Livraison {livraisonView.numero}</div>
                <div style={{fontSize:12,color:"#5B6B62",marginTop:2}}>{livraisonView.depot_nom} · {livraisonView.ville}</div>
              </div>
              <span className={"badge "+(livraisonView.statut==="Reçu au dépôt"?"bg":livraisonView.statut==="Retourné"?"br":"bg2")}>{livraisonView.statut}</span>
            </div>
            <hr className="divider"/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div><div className="lbl">Date de départ</div><div style={{fontSize:13}}>{new Date(livraisonView.date_depart).toLocaleDateString("fr-FR")}</div></div>
              <div><div className="lbl">Date d'arrivée prévue</div><div style={{fontSize:13}}>{livraisonView.date_arrivee?new Date(livraisonView.date_arrivee).toLocaleDateString("fr-FR"):"—"}</div></div>
              <div><div className="lbl">Chauffeur</div><div style={{fontSize:13}}>{livraisonView.chauffeur||"—"}</div></div>
              <div><div className="lbl">Véhicule</div><div style={{fontSize:13}}>{livraisonView.vehicule||"—"}</div></div>
              <div><div className="lbl">Agence de transport</div><div style={{fontSize:13}}>{livraisonView.agence||"—"}</div></div>
              <div><div className="lbl">Tél. agence</div><div style={{fontSize:13}}>{livraisonView.agence_tel||"—"}</div></div>
              <div><div className="lbl">Doit réceptionner</div><div style={{fontSize:13}}>{livraisonView.receptionnaire_prevu||"—"}</div></div>
              {livraisonView.agence_ref&&<div style={{gridColumn:"1 / -1"}}><div className="lbl">Référence agence</div><div style={{fontSize:13}}>{livraisonView.agence_ref}</div></div>}
            </div>
            <div className="lbl">Produits livrés</div>
            {editLivraisonMode?(
              <div style={{marginBottom:14}}>
                {editLivraisonLignes.map((l,i)=>(
                  <div key={i} style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                    <div style={{flex:1,fontSize:13,color:"#0F1F17"}}>{l.produit}</div>
                    <input type="number" min="1" className="inp" style={{width:90,padding:"5px 8px"}} value={l.quantite} onChange={e=>setEditLivraisonLignes(ls=>ls.map((x,j)=>j===i?{...x,quantite:e.target.value}:x))} />
                  </div>
                ))}
                <div style={{display:"flex",gap:8,marginTop:10}}>
                  <button className="btn btn-gold btn-sm" disabled={editLivraisonSaving} onClick={async()=>{
                    setEditLivraisonSaving(true);
                    const lignesPayload = editLivraisonLignes.map(l=>({produit:l.produit,quantite:parseInt(l.quantite)||0}));
                    const {error}=await supabase.rpc("modifier_livraison_transit_transaction",{p_livraison_id:livraisonView.id,p_lignes:lignesPayload});
                    setEditLivraisonSaving(false);
                    if(error) return showToast("Erreur : "+error.message,false);
                    await load();
                    const updated = livraisons.find(l=>l.id===livraisonView.id);
                    setLivraisonView(updated||null); setEditLivraisonMode(false);
                    showToast("Livraison mise à jour ✓");
                  }}>{editLivraisonSaving?"...":"Enregistrer"}</button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setEditLivraisonMode(false)}>Annuler</button>
                </div>
              </div>
            ):(
              <>
                <div style={{fontSize:13,color:"#0F1F17",marginBottom:6}}>{livraisonView.produit}</div>
                <div style={{fontSize:15,color:"#1E8A32",fontWeight:700,marginBottom:14}}>Total : {NUM(livraisonView.quantite)} btl</div>
              </>
            )}
            {livraisonView.notes&&<div style={{background:"rgba(47,158,63,0.048)",border:"1px solid rgba(47,158,63,0.120)",borderRadius:6,padding:"10px 12px",fontSize:12,color:"#5B6B62",marginBottom:14}}>{livraisonView.notes}</div>}
            {livraisonView.recu_par&&<div style={{fontSize:12,color:"#1E7A2D",marginBottom:14}}>✓ Reçu par {livraisonView.recu_par}{livraisonView.date_reception?" le "+new Date(livraisonView.date_reception).toLocaleDateString("fr-FR"):""}</div>}
            <div style={{display:"flex",gap:9}}>
              {isDirecteur()&&livraisonView.statut==="En transit"&&!editLivraisonMode&&<button className="btn btn-ghost" onClick={()=>{
                const lignes = (Array.isArray(livraisonView.lignes)&&livraisonView.lignes.length) ? livraisonView.lignes : [{produit:livraisonView.produit,quantite:livraisonView.quantite}];
                setEditLivraisonLignes(lignes.map(l=>({produit:l.produit,quantite:l.quantite})));
                setEditLivraisonMode(true);
              }}>✏ Modifier</button>}
              <button className="btn btn-gold" style={{flex:1}} disabled={reportGenerating} onClick={async()=>{setReportGenerating(true);try{const signatures=await openSignatureFlow("Feuille de transport — "+(livraisonView.numero||""),[{key:"chauffeur",label:"Chauffeur / Transporteur"},{key:"receptionnaire",label:"Réceptionnaire à "+(livraisonView.ville||"")}]);const pdf=await generateFeuilleTransportPdfBase64(livraisonView,entreprise,signatures);await openPdf(pdf,"Feuille de transport");}catch(e){showToast("Erreur : "+e.message,false);}setReportGenerating(false);}}><FileText size={14}/> Feuille de transport</button>
              <button className="btn btn-ghost" style={{flex:1}} onClick={()=>{setLivraisonView(null);setEditLivraisonMode(false);}}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* SCANNER AUTOMATIQUE — preuve de paiement */}
      {autoScanOpen&&(
        <AutoScanCamera
          onCapture={(file)=>{ setForm(f=>({...f,preuveFile:file})); setAutoScanOpen(false); showToast("Document capturé ✓"); }}
          onCancel={()=>setAutoScanOpen(false)}
        />
      )}

      {/* SIGNATURE À L'ÉCRAN — avant génération d'un document (fiche de remise, contrat...) */}
      {signatureFlow&&(
        <SignatureFlow documentTitle={signatureFlow.documentTitle} signers={signatureFlow.signers} onComplete={signatureFlow.onComplete} />
      )}

      {/* MES DOCUMENTS À SIGNER — demandes de signature envoyées à distance et pas encore traitées */}
      {signatureRequestsView&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setSignatureRequestsView(false)}>
          <div className="card" style={{maxWidth:480,width:"100%",maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:16,color:"#1E8A32",marginBottom:4}}>Documents à signer</div>
            <div style={{fontSize:11,color:"#8A978F",marginBottom:14}}>Signez directement sur cet écran avec le doigt.</div>
            {mesDemandesSignature.length===0?(
              <div style={{color:"#5B6B62",fontSize:12,marginBottom:10}}>Rien à signer pour l'instant.</div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:10}}>
                {mesDemandesSignature.map(d=>(
                  <div key={d.id} style={{background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:6,padding:"10px 12px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:12,color:"#0F1F17",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.titre}</div>
                      <div style={{fontSize:10,color:"#5B6B62"}}>Demandé par {d.demande_par||"—"}{d.created_at?" le "+new Date(d.created_at).toLocaleDateString("fr-FR"):""}</div>
                    </div>
                    <button className="btn btn-gold btn-sm" style={{flexShrink:0}} onClick={()=>signerDemandeEnAttente(d)}>Signer</button>
                  </div>
                ))}
              </div>
            )}
            <button className="btn btn-ghost" style={{width:"100%"}} onClick={()=>setSignatureRequestsView(false)}>Fermer</button>
          </div>
        </div>
      )}

      {paiementsFactureView&&(()=>{
        const f=paiementsFactureView;
        const {ttc}=calculateInvoiceTotals(f,entreprise);
        const historique=paiements.filter(p=>paiementFactureMatch(p,f)).sort((a,b)=>new Date(a.date_versement||a.created_at)-new Date(b.date_versement||b.created_at));
        const totalValide=historique.filter(isPaiementValide).reduce((s,p)=>s+toFiniteNumber(p.montant),0);
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setPaiementsFactureView(null)}>
            <div className="card" style={{maxWidth:520,width:"100%",maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <div style={{fontSize:16,color:"#1E8A32",fontWeight:700}}>Paiements — {f.numero}</div>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPaiementsFactureView(null)}>✕</button>
              </div>
              <div style={{fontSize:12,color:"#5B6B62",marginBottom:14}}>{f.client_nom}</div>
              <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:110}}><div style={{fontSize:10,color:"#5B6B62",textTransform:"uppercase"}}>Total facture</div><div style={{fontSize:16,fontWeight:700,color:"#0F1F17"}}>{FCFA(ttc)}</div></div>
                <div style={{flex:1,minWidth:110}}><div style={{fontSize:10,color:"#5B6B62",textTransform:"uppercase"}}>Payé (validé)</div><div style={{fontSize:16,fontWeight:700,color:"#1E7A2D"}}>{FCFA(totalValide)}</div></div>
                <div style={{flex:1,minWidth:110}}><div style={{fontSize:10,color:"#5B6B62",textTransform:"uppercase"}}>Reste dû</div><div style={{fontSize:16,fontWeight:700,color:"#1E8A32"}}>{FCFA(Math.max(0,ttc-totalValide))}</div></div>
              </div>
              {historique.length===0?(
                <div style={{color:"#5B6B62",fontSize:13,textAlign:"center",padding:16}}>Aucun paiement enregistré pour l'instant.</div>
              ):(
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {historique.map(p=>(
                    <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:5,padding:"8px 10px"}}>
                      <div>
                        <div style={{fontSize:13,color:"#0F1F17",fontWeight:600}}>{FCFA(p.montant)}</div>
                        <div style={{fontSize:10,color:"#5B6B62"}}>{fmtDate(p.date_versement)} · {p.moyen||"—"}{p.reference_virement?" · "+p.reference_virement:""}</div>
                      </div>
                      <span className="badge" style={isPaiementValide(p)?{color:"#1E7A2D",background:"rgba(47,158,63,0.12)"}:{color:"#1E8A32",background:"rgba(47,158,63,0.096)"}}>
                        {isPaiementValide(p)?"Validé":p.statut||"En attente"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* APERÇU DOCUMENT */}
      {pdfPreview&&<PdfSheet preview={pdfPreview} onClose={()=>setPdfPreview(null)} />}
      {accesView&&<FicheAcces employe={accesView.employe} credentials={accesView.credentials} entreprise={entreprise}
        onClose={()=>setAccesView(null)}
        onPdf={async()=>{
          try{
            const pdf=await generateAccesErpPdfBase64(accesView.employe,entreprise,accesView.credentials);
            await deliverPdfOnDevice({...pdf,title:"Accès ERP",direct:true,onOpenInApp:(url,filename,title)=>setPdfPreview({url,filename,title})});
          }catch(pdfErr){
            console.warn("Fiche d'accès ERP non générée :",pdfErr);
            showToast("La fiche PDF n'a pas pu être générée : "+pdfErr.message,false);
          }
        }} />}

      {sigModal&&(()=>{
        const url = sigModal.token ? sigSignUrl(sigModal.token) : "";
        const qm = url ? makeQrMatrix(url) : null;
        const copierLien = async () => { try { await navigator.clipboard.writeText(url); setSigModal(m=>m?{...m,copie:true}:m); setTimeout(()=>setSigModal(m=>m?{...m,copie:false}:m),1800); } catch(_) {} };
        const msgWa = "Bonjour, merci de signer ce document ("+(docForm.type||"Document")+") depuis votre téléphone : "+url+" (lien valable 60 minutes)";
        const choix = (id, titre, sous) => (
          <button type="button" onClick={()=>setSigModal(m=>({...m,mode:id}))} style={{display:"block",width:"100%",textAlign:"left",border:"2px solid "+(sigModal.mode===id?"#2F9E3F":"#E3E9E6"),background:sigModal.mode===id?"#F1FAF2":"#fff",borderRadius:12,padding:"12px 14px",marginBottom:10,cursor:"pointer",fontFamily:"inherit"}}>
            <div style={{fontWeight:700,fontSize:14,color:"#0F1F17"}}>{titre}</div>
            <div style={{fontSize:12,color:"#5B6B62",marginTop:2}}>{sous}</div>
          </button>
        );
        return (
          <div className="overlay" onClick={()=>{ if (sigModal.etape!=="creation") sigFermer(); }}>
            <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
              <div style={{fontSize:19,fontWeight:700,color:"#0F1F17",marginBottom:4}}>✍ Signer par téléphone</div>
              <div style={{fontSize:12,color:"#5B6B62",marginBottom:14}}>{docForm.type}{docForm.clientNom?" · "+docForm.clientNom:""}</div>
              {(sigModal.etape==="choix"||sigModal.etape==="creation")&&(<>
                {sigModal.erreur&&<div style={{background:"#FDECEA",color:"#A3231C",borderRadius:10,padding:"9px 12px",fontSize:13,marginBottom:10}}>{sigModal.erreur}</div>}
                {choix("moi","Je signe moi-même","Le QR code s'ouvre sur votre téléphone : vous signez en tant que "+(session?.nom||"directeur")+".")}
                {choix("autre","Une autre personne signe","Elle scanne le QR code (ou reçoit le lien) et saisit son nom sur son téléphone.")}
                <div style={{display:"flex",gap:8,marginTop:6}}>
                  <button className="btn btn-ghost" style={{flex:1}} onClick={sigFermer}>Annuler</button>
                  <button className="btn btn-gold" style={{flex:2}} disabled={sigModal.etape==="creation"} onClick={()=>sigCreer(sigModal.mode)}>{sigModal.etape==="creation"?"Création…":"Générer le QR code"}</button>
                </div>
              </>)}
              {sigModal.etape==="attente"&&(<>
                <div style={{display:"flex",justifyContent:"center",margin:"4px 0 10px"}}>
                  {qm&&<svg viewBox={"0 0 "+(qm.n+4)+" "+(qm.n+4)} shapeRendering="crispEdges" role="img" aria-label="QR code de signature" style={{width:220,height:220,border:"1px solid #E3E9E6",borderRadius:10}}><rect width={qm.n+4} height={qm.n+4} fill="#fff"/><path d={qrSvgPath(qm)} fill="#0F1F17"/></svg>}
                </div>
                <div style={{textAlign:"center",fontSize:13,color:"#2B3A32",marginBottom:4}}>{sigModal.moi?"Scannez ce code avec votre téléphone, puis signez.":"Faites scanner ce code par la personne qui signe."}</div>
                <div style={{textAlign:"center",fontSize:12,color:"#5B6B62",marginBottom:12}}>⏳ En attente de la signature… valable jusqu'à {sigHeureCourte(sigModal.expire)}</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <a className="btn btn-ghost" style={{flex:"1 1 45%",textAlign:"center",textDecoration:"none"}} href={url} target="_blank" rel="noopener noreferrer">Ouvrir ici</a>
                  <button className="btn btn-ghost" style={{flex:"1 1 45%"}} onClick={copierLien}>{sigModal.copie?"Lien copié ✓":"Copier le lien"}</button>
                  <a className="btn btn-ghost" style={{flex:"1 1 100%",textAlign:"center",textDecoration:"none"}} href={"https://wa.me/?text="+encodeURIComponent(msgWa)} target="_blank" rel="noopener noreferrer">Envoyer le lien par WhatsApp</a>
                </div>
                <button className="btn btn-ghost" style={{width:"100%",marginTop:10}} onClick={sigFermer}>Annuler</button>
              </>)}
              {sigModal.etape==="fait"&&(<>
                <div style={{textAlign:"center",padding:"10px 0 14px"}}>
                  <div style={{width:56,height:56,borderRadius:"50%",background:"#2F9E3F",color:"#fff",display:"grid",placeItems:"center",fontSize:30,margin:"0 auto 10px"}}>✓</div>
                  <div style={{fontWeight:700,fontSize:16}}>Signé par {sigModal.nom}</div>
                  <div style={{fontSize:12.5,color:"#5B6B62",marginTop:4}}>La signature et un petit QR de vérification sont maintenant dans le document.</div>
                </div>
                <button className="btn btn-gold" style={{width:"100%"}} onClick={()=>setSigModal(null)}>Fermer</button>
              </>)}
              {sigModal.etape==="expire"&&(<>
                <div style={{background:"#FFF4DD",color:"#7A4B00",borderRadius:10,padding:"10px 12px",fontSize:13,marginBottom:12}}>Ce QR code a expiré (60 minutes). Générez-en un nouveau.</div>
                <div style={{display:"flex",gap:8}}>
                  <button className="btn btn-ghost" style={{flex:1}} onClick={()=>setSigModal(null)}>Fermer</button>
                  <button className="btn btn-gold" style={{flex:2}} onClick={()=>setSigModal({ etape:"choix", mode:sigModal.mode, erreur:"" })}>Nouveau QR code</button>
                </div>
              </>)}
            </div>
          </div>
        );
      })()}

      {docView&&(
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.9)", zIndex:300, display:"flex", flexDirection:"column", alignItems:"center", padding:10, overflowY:"auto" }}>
          <div style={{ width:"100%", maxWidth:860, display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, position:"sticky", top:0, zIndex:10, background:"rgba(0,0,0,0.88)", padding:"8px 12px", borderRadius:8 }}>
            <div style={{ color:"#1E8A32", fontSize:15 }}>{docView.type}</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={printDocument} style={{ padding:"9px 20px", background:"#2F9E3F", color:"#FFFFFF", border:"none", borderRadius:4, cursor:"pointer", fontWeight:700, fontSize:14, display:"inline-flex", alignItems:"center", gap:8 }}>
                <Printer size={15}/> Imprimer / PDF
              </button>
              <button onClick={()=>setDocView(null)} style={{ padding:"9px 18px", background:"#D6453D", color:"#FFFFFF", border:"none", borderRadius:4, cursor:"pointer", fontWeight:700, fontSize:16 }}>✕</button>
            </div>
          </div>
          <div style={{ background:"linear-gradient(160deg,#fdfaf3,#f7f0e0)", width:"100%", maxWidth:794, minHeight:600, borderRadius:4, overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.5)", fontFamily:"var(--az-font)", color:"#2C1F0A", padding:"48px 54px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", borderBottom:"2px solid #2F9E3F", paddingBottom:18, marginBottom:32 }}>
              <div>
                <img src={LOGO_URI} alt="Azongbio" style={{height:40}} />
                <div style={{fontSize:10,color:"#5B6B62",lineHeight:1.7,marginTop:8}}>
                  {entreprise.entreprise_nom}<br/>
                  {entreprise.entreprise_adresse}<br/>
                  {entreprise.entreprise_ville}<br/>
                  Tél : {entreprise.entreprise_tel} · {entreprise.entreprise_email}
                </div>
              </div>
              <div style={{textAlign:"right",fontSize:11,color:"#5B6B62"}}>{fmtDate(docView.date)}</div>
            </div>
            {docView.clientNom&&<div style={{marginBottom:20,fontSize:13}}><strong>À l'attention de :</strong> {docView.clientNom}</div>}
            {docView.objet&&<div style={{marginBottom:24,fontSize:14}}><strong>Objet : </strong>{docView.objet}</div>}
            <div style={{fontSize:13,lineHeight:2,whiteSpace:"pre-wrap"}}>{docView.corps}</div>
            <div style={{marginTop:48,display:"flex",justifyContent:sigValide(docView)?"space-between":"flex-end",alignItems:"flex-end",gap:16,flexWrap:"wrap"}}>
              {sigValide(docView)&&(()=>{
                const qv = makeQrMatrix(sigVerifUrl(docView.signature.token));
                return (
                  <div style={{display:"flex",gap:10,alignItems:"center",maxWidth:340}}>
                    {qv&&<svg viewBox={"0 0 "+(qv.n+4)+" "+(qv.n+4)} shapeRendering="crispEdges" role="img" aria-label="QR code de vérification de la signature" style={{width:84,height:84,flexShrink:0}}><rect width={qv.n+4} height={qv.n+4} fill="#fff"/><path d={qrSvgPath(qv)} fill="#0F1F17"/></svg>}
                    <div style={{fontSize:10,color:"#5B6B62",lineHeight:1.5}}>
                      <b style={{color:"#0F1F17"}}>Document signé électroniquement</b><br/>
                      Scannez le QR code pour vérifier la signature<br/>
                      Empreinte : <span style={{fontFamily:"monospace"}}>{docView.signature.empreinte}</span>
                    </div>
                  </div>
                );
              })()}
              <div style={{textAlign:"center",minWidth:180}}>
                {sigValide(docView)
                  ? <div style={{height:46,display:"flex",alignItems:"flex-end",justifyContent:"center"}}><img src={docView.signature.png} alt="Signature" style={{maxHeight:46,maxWidth:190}} /></div>
                  : <div style={{height:46}}></div>}
                <div style={{fontSize:13,fontWeight:700,borderTop:"1px solid #2F9E3F",paddingTop:6}}>{sigValide(docView)?docView.signature.nom:session.nom}</div>
                <div style={{fontSize:11,color:"#5B6B62",marginTop:2}}>{sigValide(docView)?(docView.signature.fonction||""):(session.poste || "Direction")}</div>
                {sigValide(docView)&&<div style={{fontSize:9.5,color:"#5B6B62",marginTop:3}}>Signé électroniquement le {sigHeure(docView.signature.signe_le)}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FICHE DE LOT (impression) */}
      {lotPrint&&(
        <div className="overlay" onClick={()=>setLotPrint(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:19,color:"#1E8A32",marginBottom:6}}>Fiche de lot</div>
            <div style={{fontSize:11,color:"#1E8A32",fontFamily:"monospace",marginBottom:18}}>{lotPrint.numero}</div>
            <div style={{fontSize:13,color:"#0F1F17",marginBottom:6}}><strong>Produit :</strong> {lotPrint.produit}</div>
            <div style={{fontSize:13,color:"#0F1F17",marginBottom:6}}><strong>Date :</strong> {new Date(lotPrint.date).toLocaleDateString("fr-FR")}</div>
            <div style={{fontSize:13,color:"#0F1F17",marginBottom:6}}><strong>Quantité :</strong> {NUM(lotPrint.quantite)} btl</div>
            <div style={{fontSize:13,color:"#0F1F17",marginBottom:6}}><strong>Opérateur :</strong> {lotPrint.operateur}</div>
            <div style={{fontSize:13,color:"#0F1F17",marginBottom:18}}><strong>Statut :</strong> {lotPrint.statut}</div>
            <div style={{display:"flex",gap:9}}>
              <button className="btn btn-gold btn-icon" style={{flex:1}} onClick={printLotRecap}><Printer size={14}/> Imprimer</button>
              <button className="btn btn-ghost" onClick={()=>setLotPrint(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ÉTIQUETTES (impression) */}
      {lotEtiquette&&(
        <div className="overlay" onClick={()=>setLotEtiquette(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:19,color:"#1E8A32",marginBottom:6}}>Étiquettes</div>
            <div style={{fontSize:11,color:"#1E8A32",fontFamily:"monospace",marginBottom:18}}>{lotEtiquette.numero} · {lotEtiquette.produit}</div>
            <div className="fld"><label className="lbl">Nombre d'étiquettes à imprimer</label>
              <input type="number" className="inp" min="1" value={nbEtiquettes} onChange={e=>setNbEtiquettes(e.target.value)} />
            </div>
            <div style={{display:"flex",gap:9,marginTop:9}}>
              <button className="btn btn-gold btn-icon" style={{flex:1}} onClick={printEtiquettes}><Tag size={14}/> Imprimer</button>
              <button className="btn btn-ghost" onClick={()=>setLotEtiquette(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL RECEPTION */}
      {receptionModal&&(
        <div className="overlay" onClick={()=>setReceptionModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:19,color:"#1E8A32",marginBottom:6}}>✓ Confirmer la réception</div>
            <div style={{fontSize:12,color:"#5B6B62",marginBottom:20}}>{receptionModal.numero} · {receptionModal.depot_nom} · {NUM(receptionModal.quantite)} btl</div>
            <div className="fld"><label className="lbl">État de la marchandise *</label>
              <select className="inp" value={receptionForm.etat} onChange={e=>setReceptionForm(f=>({...f,etat:e.target.value}))}>
                {["Bon état","Dommages mineurs","Dommages partiels","Dommages importants","Livraison incomplète"].map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="fld"><label className="lbl">Note de réception</label><input type="text" className="inp" value={receptionForm.note} onChange={e=>setReceptionForm(f=>({...f,note:e.target.value}))} /></div>
            <div style={{background:"rgba(47,158,63,0.048)",border:"1px solid rgba(47,158,63,0.160)",borderRadius:6,padding:"10px 14px",marginBottom:18,fontSize:12,color:"#5B6B62"}}>
              Réceptionné par : <strong style={{color:"#0F1F17"}}>{session.nom}</strong><br/>
              Date : <strong style={{color:"#0F1F17"}}>{new Date().toLocaleString("fr-FR")}</strong>
            </div>
            <div style={{display:"flex",gap:9}}>
              <button className="btn btn-gold" style={{flex:1}} onClick={confirmerReception} disabled={loading}>{loading?"...":"✓ Confirmer"}</button>
              <button className="btn btn-ghost" onClick={()=>setReceptionModal(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {recetteEditProduit&&(
        <div className="overlay" onClick={()=>setRecetteEditProduit(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:19,color:"#1E8A32",marginBottom:6}}>Recette — {recetteEditProduit.nom}</div>
            <div style={{fontSize:12,color:"#5B6B62",marginBottom:18}}>Quantité de chaque ingrédient nécessaire pour produire UNE bouteille. Le coût de revient sera recalculé automatiquement à partir de ces quantités et des prix d'achat.</div>
            {ingredients.length===0&&<div style={{color:"#5B6B62",marginBottom:14}}>Aucun ingrédient enregistré — ajoute-les d'abord depuis « Ingrédients » dans Stock & Produits.</div>}
            <div style={{maxHeight:320,overflowY:"auto",marginBottom:8}}>
              {ingredients.map(i=>(
                <div key={i.id} className="fld" style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <label className="lbl" style={{flex:1,marginBottom:0}}>{i.nom} <span style={{color:"#8A978F"}}>({FCFA(i.prix_unitaire)}/{i.unite})</span></label>
                  <input type="number" step="0.001" min="0" className="inp" style={{width:110}} placeholder={"0 "+i.unite}
                    value={recetteQtes[i.id]||""}
                    onChange={e=>setRecetteQtes(q=>({...q,[i.id]:e.target.value}))} />
                </div>
              ))}
            </div>
            {ingredients.length>0&&(()=>{
              const totalCalc = ingredients.reduce((s,i)=>s+toFiniteNumber(recetteQtes[i.id])*toFiniteNumber(i.prix_unitaire),0);
              return (
                <div style={{background:"rgba(47,158,63,0.048)",border:"1px solid rgba(47,158,63,0.160)",borderRadius:6,padding:"10px 14px",marginBottom:18,fontSize:13}}>
                  Coût ingrédients par bouteille : <strong style={{color:"#1E8A32"}}>{FCFA(totalCalc)}</strong>
                  {recetteEditProduit.prix>0&&<> · Marge résultante : <strong style={{color:(recetteEditProduit.prix-totalCalc)>=0?"#1E7A2D":"#D6453D"}}>{FCFA(recetteEditProduit.prix-totalCalc)}</strong></>}
                </div>
              );
            })()}
            <div style={{display:"flex",gap:9}}>
              <button className="btn btn-gold" style={{flex:1}} disabled={recetteSaving} onClick={async()=>{
                setRecetteSaving(true);
                const produitId = recetteEditProduit.id;
                await Promise.all(ingredients.map(i=>saveRecetteLigne(produitId, i.id, recetteQtes[i.id]||0)));
                await load();
                setRecetteSaving(false);
                setRecetteEditProduit(null);
                showToast("Recette enregistrée ✓");
              }}>{recetteSaving?"...":"Enregistrer"}</button>
              <button className="btn btn-ghost" onClick={()=>setRecetteEditProduit(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {(()=>{
        const a = annonceALire();
        if (!a) return null;
        return (
          <div role="dialog" aria-modal="true" aria-label="Annonce de la direction" style={{position:"fixed",inset:0,zIndex:9500,background:"rgba(15,31,23,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
            <div className="card" style={{maxWidth:520,width:"100%",maxHeight:"90vh",overflowY:"auto",padding:22,borderTop:"4px solid #1E8A32"}}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",color:"#1E8A32",textTransform:"uppercase",marginBottom:6}}>Annonce de la direction</div>
              <div style={{fontSize:19,fontWeight:700,color:"#0F1F17",marginBottom:12,lineHeight:1.25}}>{a.titre}</div>
              <div style={{fontSize:14,color:"#2B3A32",lineHeight:1.55,whiteSpace:"pre-line",marginBottom:18}}>{a.message}</div>
              <div style={{display:"flex",gap:9,flexWrap:"wrap"}}>
                <button className="btn btn-gold" style={{flex:"1 1 160px"}} disabled={annBusy} onClick={()=>annonceLire(a,false)}>{annBusy?"...":"J'ai lu et compris"}</button>
                {a.page&&<button className="btn btn-ghost" style={{flex:"1 1 160px"}} disabled={annBusy} onClick={()=>annonceLire(a,true)}>Lire puis ouvrir la page</button>}
              </div>
              <button className="btn btn-ghost btn-sm" style={{marginTop:10,width:"100%"}} onClick={()=>setAnnReporte(r=>({...r,[a.id]:true}))}>Plus tard</button>
              <div style={{fontSize:11,color:"#5B6B62",marginTop:10,textAlign:"center"}}>Le directeur est prévenu quand vous confirmez avoir lu.</div>
            </div>
          </div>
        );
      })()}
      {(()=>{
        const b = ficheAReviewer();
        if (!b) return null;
        const brut = Number(b.brut)||0, net = Number(b.net)||0;
        return (
          <div role="dialog" aria-modal="true" aria-label="Fiche de paie à signer" style={{position:"fixed",inset:0,zIndex:9400,background:"rgba(15,31,23,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
            <div className="card" style={{maxWidth:460,width:"100%",maxHeight:"90vh",overflowY:"auto",padding:22,borderTop:"4px solid #D4A84B"}}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",color:"#8A6A12",textTransform:"uppercase",marginBottom:6}}>Fiche de paie à signer</div>
              <div style={{fontSize:20,fontWeight:700,color:"#0F1F17",marginBottom:12}}>{ficheMoisLbl(b.mois)}</div>
              <div style={{background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0"}}><span style={{color:"#5B6B62"}}>Salaire brut</span><span>{FCFA(brut)}</span></div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0"}}><span style={{color:"#5B6B62"}}>Retenues</span><span>{FCFA(Number(b.total_retenues)||0)}</span></div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:15,fontWeight:700,padding:"6px 0 2px",borderTop:"1px solid #E3E9E6",marginTop:4}}><span>Net à payer</span><span style={{color:"#1E7A2D"}}>{FCFA(net)}</span></div>
              </div>
              <div style={{fontSize:12,color:"#5B6B62",lineHeight:1.5,marginBottom:14}}>En signant, vous confirmez avoir pris connaissance de votre fiche de paie. La direction est prévenue de votre signature.</div>
              <button className="btn btn-ghost" style={{width:"100%",marginBottom:8}} onClick={()=>ficheOuvrirPdf(b,false)}>Voir la fiche (PDF)</button>
              <button className="btn btn-gold" style={{width:"100%"}} disabled={ficheBusy} onClick={()=>ficheSigner(b)}>{ficheBusy?"...":"Signer maintenant"}</button>
              <button className="btn btn-ghost btn-sm" style={{marginTop:10,width:"100%"}} onClick={()=>{ setFicheReporte(r=>({...r,[b.id]:true})); setFicheReview(null); }}>Plus tard</button>
            </div>
          </div>
        );
      })()}
      {(()=>{
        const m = vrMesAReviewer();
        if (!m) return null;
        return (
          <div role="dialog" aria-modal="true" aria-label="Bon de versement à signer" style={{position:"fixed",inset:0,zIndex:9400,background:"rgba(15,31,23,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
            <div className="card" style={{maxWidth:460,width:"100%",maxHeight:"90vh",overflowY:"auto",padding:22,borderTop:"4px solid #D4A84B"}}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",color:"#8A6A12",textTransform:"uppercase",marginBottom:6}}>Bon de versement à signer</div>
              <div style={{fontSize:20,fontWeight:700,color:"#0F1F17",marginBottom:12}}>{m.type_versement}</div>
              <div style={{background:"#F1F5F3",border:"1px solid #E3E9E6",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0"}}><span style={{color:"#5B6B62"}}>Bon n°</span><span>{m.numero}</span></div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0"}}><span style={{color:"#5B6B62"}}>Date</span><span>{vrDateCourte(m.date_versement)}</span></div>
                {m.motif?<div style={{display:"flex",justifyContent:"space-between",gap:12,fontSize:13,padding:"4px 0"}}><span style={{color:"#5B6B62"}}>Motif</span><span style={{textAlign:"right"}}>{m.motif}</span></div>:null}
                <div style={{display:"flex",justifyContent:"space-between",fontSize:15,fontWeight:700,padding:"6px 0 2px",borderTop:"1px solid #E3E9E6",marginTop:4}}><span>Montant reçu</span><span style={{color:"#1E7A2D"}}>{FCFA(Number(m.montant)||0)}</span></div>
              </div>
              <div style={{fontSize:12,color:"#5B6B62",lineHeight:1.5,marginBottom:14}}>En signant, vous confirmez avoir reçu la somme indiquée. La direction est prévenue de votre signature.</div>
              <button className="btn btn-ghost" style={{width:"100%",marginBottom:8}} onClick={()=>vrMesPdf(m)}>Voir le bon (PDF)</button>
              <button className="btn btn-gold" style={{width:"100%"}} disabled={vrMesBusy} onClick={()=>vrMesSigner(m)}>{vrMesBusy?"...":"Signer maintenant"}</button>
              <button className="btn btn-ghost btn-sm" style={{marginTop:10,width:"100%"}} onClick={()=>{ setVrMesReporte(r=>({...r,[m.id]:true})); setVrMesReview(null); }}>Plus tard</button>
            </div>
          </div>
        );
      })()}
      {vrModal&&isDirecteur()&&(()=>{
        const f = vrModal, t = vrType(f.type);
        const emp = employes.find(e=>String(e.id)===String(f.employeId));
        const dejaOuvert = emp ? vrOuvertes(emp.id).reduce((s,v)=>s+(Number(v.solde_restant)||0),0) : 0;
        return (
          <div role="dialog" aria-modal="true" aria-label="Versement à un employé" style={{position:"fixed",inset:0,zIndex:9300,background:"rgba(15,31,23,.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={e=>{ if (e.target===e.currentTarget && !vrBusy) setVrModal(null); }}>
            <div className="card" style={{maxWidth:480,width:"100%",maxHeight:"92vh",overflowY:"auto",padding:22,borderTop:"4px solid #D4A84B"}}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",color:"#8A6A12",textTransform:"uppercase",marginBottom:6}}>Versement à un employé</div>
              <div style={{fontSize:20,fontWeight:700,color:"#0F1F17",marginBottom:14}}>Nouveau versement</div>
              <div className="fld"><label className="lbl">Nature du versement *</label>
                <select className="inp" value={f.type} onChange={e=>setVrModal({...f,type:e.target.value})}>
                  {VR_TYPES.map(x=><option key={x.k} value={x.k}>{x.k}</option>)}
                </select>
                <div style={{fontSize:11.5,color:"#5B6B62",marginTop:5,lineHeight:1.5}}>{t.aide}</div>
              </div>
              <div className="fld"><label className="lbl">Employé *</label>
                <select className="inp" value={f.employeId} onChange={e=>setVrModal({...f,employeId:e.target.value})}>
                  <option value="">Choisir…</option>
                  {employes.filter(e=>e.actif).map(e=><option key={e.id} value={e.id}>{e.nom}{e.poste?" — "+e.poste:""}</option>)}
                </select>
                {dejaOuvert>0&&<div style={{fontSize:11.5,color:"#7A4B00",background:"#FFF4DD",borderRadius:8,padding:"6px 10px",marginTop:6}}>{emp.nom} a déjà {FCFA(dejaOuvert)} d'avance(s) non encore retenue(s).</div>}
              </div>
              <div className="fld"><label className="lbl">Montant (FCFA) *</label><input type="number" inputMode="numeric" className="inp" value={f.montant} onChange={e=>setVrModal({...f,montant:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Date</label><input type="date" className="inp" value={f.date} onChange={e=>setVrModal({...f,date:e.target.value})} /></div>
              <div className="fld"><label className="lbl">Payé en</label>
                <select className="inp" value={f.moyen} onChange={e=>setVrModal({...f,moyen:e.target.value})}>
                  {["Espèces","Mobile Money","Virement","Autre"].map(m=><option key={m} value={m}>{m}</option>)}
                </select>
                <div style={{fontSize:10.5,color:"#5B6B62",marginTop:4}}>Seuls les versements en espèces sortent de la caisse.</div>
              </div>
              <div className="fld"><label className="lbl">Motif{f.type==="Autre versement"?" *":" (facultatif)"}</label><textarea className="inp" rows={2} value={f.motif} onChange={e=>setVrModal({...f,motif:e.target.value})} placeholder="ex : avance sur les commissions d'octobre" /></div>
              <div style={{fontSize:12,color:"#5B6B62",lineHeight:1.5,marginBottom:14}}>En validant, la dépense est enregistrée en comptabilité (code {t.code}). Choisissez ensuite comment faire signer le bon : <b>tout de suite</b> (l'employé est avec vous) ou <b>envoi pour signature</b> (vous signez d'abord, l'employé signe sur son téléphone). Le bon signé est rangé dans les documents de l'employé et dans « Documents signés » (visible de vous seul).</div>
              <button className="btn btn-gold" style={{width:"100%"}} disabled={vrBusy} onClick={()=>vrCreer(false)}>{vrBusy?"...":"Signer tout de suite (l'employé est là)"}</button>
              <button className="btn btn-ghost btn-icon" style={{width:"100%",marginTop:8,border:"1px solid #D4A84B",justifyContent:"center"}} disabled={vrBusy||!emp||!emp.matricule} onClick={()=>vrCreer(true)}><Send size={14}/> Envoyer pour signature à l'employé</button>
              {emp&&!emp.matricule&&<div style={{fontSize:11,color:"#A3231C",marginTop:5}}>Cet employé n'a pas de matricule : l'envoi n'est pas possible, faites-le signer tout de suite.</div>}
              <button className="btn btn-ghost btn-sm" style={{marginTop:10,width:"100%"}} disabled={vrBusy} onClick={()=>setVrModal(null)}>Annuler</button>
            </div>
          </div>
        );
      })()}
      {toast&&<div className={"toast "+(toast.ok?"tok":"terr")}>{toast.msg}</div>}
    </div>
  );
}